import { activeOffers } from '../data/activeOffers';
import { ownerAddresses } from '../data/ownerAddresses';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import Footer from '../components/Footer';
import PriceDisplay from '../components/common/PriceDisplay';
import { trendingCollections, lilpudgysPrices } from '../data/collections';
import { doodlesNfts } from '../data/doodles';
import { boredApeYachtClubNfts } from '../data/boredApeYachtClub';

// Helper function to generate random offers
const generateRandomOffers = (basePrice, count) => {
  const baseValue = parseFloat(basePrice);
  return Array.from({ length: count }, () => {
    // Generate price variation between -10% and +10% of base price
    const priceVariation = baseValue * (0.9 + Math.random() * 0.2);
    
    // Generate random wallet address
    const randomAddress = `0x${Math.random().toString(16).slice(2, 10)}...${Math.random().toString(16).slice(2, 6)}`;
    
    // Generate random expiration between 1-24 hours
    const hours = Math.floor(Math.random() * 24) + 1;
    
    return {
      price: priceVariation.toFixed(2),
      buyer: randomAddress,
      expiration: `${hours}h`
    };
  }).sort((a, b) => parseFloat(b.price) - parseFloat(a.price)); // Sort by price descending
};

function ItemPage() {
  const { collectionId, itemId } = useParams();
  const [nft, setNft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWalletMessage, setShowWalletMessage] = useState(false);

  const getPriceForId = (id) => {
    if (collectionId === 'pudgypenguins') {
      // Map specific IDs to fixed prices between 17.1 and 22.5 ETH
      const priceMap = {
        8422: '17.10',
        6457: '17.45',
        5496: '17.85',
        6190: '18.20',
        623: '18.55',
        919: '18.90',
        3248: '19.25',
        3753: '19.65',
        8663: '20.10',
        7651: '20.45',
        7934: '20.80',
        1441: '21.15',
        1792: '21.50',
        3362: '21.85',
        3581: '22.20',
        7962: '22.50'
      };
      return (priceMap[id] || '20.00') + ' ETH';
    } else if (collectionId === 'azuki') {
      // Use deterministic random number based on NFT ID for Azuki
      const seed = parseInt(id);
      const random = ((seed * 9301 + 49297) % 233280) / 233280; // Simple PRNG
      return (random * 5 + (trendingCollections.find(c => c.id === 'azuki')?.floorPrice || 12)).toFixed(2) + ' ETH';
    } else if (collectionId === 'lilpudgys') {
      // Use the fixed price from the price map
      return (lilpudgysPrices[id] || '1.75') + ' ETH';
    } else if (collectionId === 'doodles') {
      // Find the NFT in doodlesNfts array and use its exact price
      const nft = doodlesNfts.find(n => n.id === id);
      return nft?.price || '4.84 ETH';
                            
    } else if (collectionId === 'boredApeYachtClub') {
      // Find the NFT in miladyNfts array and use its exact price
      const nft = boredApeYachtClubNfts.find(n => n.id === id);
      return nft?.price || '14.19 ETH';
                            
    }
    // For other collections, use floor price
    const collection = trendingCollections.find(c => c.id === collectionId);
    return (collection?.floorPrice || 20).toFixed(2) + ' ETH';
  };

  const getCurrentPrice = () => {
    const collection = trendingCollections.find(c => c.id === collectionId);
    return (collection?.floorPrice || 20).toFixed(2) + ' ETH';
  };

  useEffect(() => {
    const loadNFT = () => {
      // Find the collection first
      const collection = trendingCollections.find(c => c.id === collectionId);
      if (!collection) {
        setLoading(false);
        return;
      }

      // Find the actual NFT data from the collection
      let actualNftData = null;
      
      if (collectionId === 'pudgypenguins') {
        const pudgyPenguinsNfts = [
          { 
            id: 8422, 
            name: 'Pudgy Penguins #8422', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OjX80bG9LqhlFhgw%252FWMdyaAv3SzDapn19Va0ddAsVt4to7FR%252BkHq3ndVb2VVTMwMqg%253D%253D.png'
          },
          { 
            id: 407, 
            name: 'Pudgy Penguins #407', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OnQyebHQ0D4s6Gvg8TAOPbja3zesB5MCVWofPB4Bl6ocvc2Xm%252FhyR5i4UqmnhN7hsA%253D%253D.png'
          },
          { 
            id: 3758, 
            name: 'Pudgy Penguins #3758', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OiNPRgfUposvhjsNCzn1ZSBRToY6kP2hEWWTjiXgKuHxUswO1hprMpbH%252FE7mbyv%252FDg%253D%253D.png'
          },
          { 
            id: 495, 
            name: 'Pudgy Penguins #495', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OnoCbEdhOLawR7%252F%252FHTx9doUzpxCmA3gpr%252FS4uTgv7XCdZ3Zp00GoYu44TQv9GtgpUQ%253D%253D.png'
          },
          { 
            id: 3934, 
            name: 'Pudgy Penguins #3934', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OklfLBahPJlF5mY0BmDhOCJ5dsv1WZXmzxmU%252BSf513FqrUQmKzsCypqDpsVGqh%252BaUA%253D%253D.png'
          },
          { 
            id: 222, 
            name: 'Pudgy Penguins #222', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OoK3A%252B1vSgwf88yp%252BeLnDqRKRhnhhbPOMe8HjIPYoL5rFNrA2EQMY%252B1GOz1S%252B%252BbKTw%253D%253D.png'
          },
          { 
            id: 702, 
            name: 'Pudgy Penguins #702', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OjX80bG9LqhlFhgw%252FWMdyaAv3SzDapn19Va0ddAsVt4to7FR%252BkHq3ndVb2VVTMwMqg%253D%253D.png'
          },
          { 
            id: 716, 
            name: 'Pudgy Penguins #716', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ok0ZqCG6hL7BEJTVkimkM4AnUlun2I2FBMiT7xDuqqYnKLfoqYW2Ed1vd2lxOajLTw%253D%253D.png'
          },
          { 
            id: 2640, 
            name: 'Pudgy Penguins #2640', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71On4Uu8RupEAOwyS2j9KKsArOJTm0sFRyu4x96YckNvtA9AAD6jx0yOta4uO%252BPHJ47g%253D%253D.png'
          },
          { 
            id: 7398, 
            name: 'Pudgy Penguins #7398', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oj3yB4QxiFHq8u7xMWHcHjm2qhy%252F9ljIrKdpbIJTpF%252BOpMr0V4QF6tb013gt4mjsVw%253D%253D.png'
          },
          { 
            id: 7457, 
            name: 'Pudgy Penguins #7457', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OnrSZ4llkk9Brun7SjwG42RJWdlGOD%252Bfh5wnVbr1CfH2807JWzNmLFdBjnPIM%252F%252FvJg%253D%253D.png'
          },
          { 
            id: 1735, 
            name: 'Pudgy Penguins #1735', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OrsQX9y3RuYDV7rdfN7jnOFKooi17qiEfLH4A8ZMoQBdeLUJR%252FVgKi9vq%252FYRHlkkaA%253D%253D.png'
          },
          { 
            id: 2378, 
            name: 'Pudgy Penguins #2378', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OjbtwZVm1sruv2L7yisoIWtu6KZHyb8p06UmpXmAmkchrimQ%252Fr%252FvM8FK6Pd0uPFivA%253D%253D.png'
          },
          { 
            id: 5332, 
            name: 'Pudgy Penguins #5332', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oq8r9%252BmdtR6CnaZ8cKOT%252B5dS55o4YAMWOWKa9mwY3su%252FAFZRuNEryiarf5mAG%252Bubfg%253D%253D.png'
          },
          { 
            id: 5792, 
            name: 'Pudgy Penguins #5792', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OnI9sCcatonWW%252BQzQwVnuvUYdqDqWje73TqdO2sbzDy59lca1r5qcnrP6mNPi9CyAQ%253D%253D.png'
          },
          { 
            id: 5368, 
            name: 'Pudgy Penguins #5368', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oo%252F1EswQoWnApV8ahqUE00Gpc3k%252FgQxBa30B84fvhwNVOxZOq1D2oa1zgMQgHQnKQA%253D%253D.png'
          },
          { 
            id: 2617, 
            name: 'Pudgy Penguins #2617', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OpHys919ieQ97htHQLWkBtpoow82pX8K3au6CKHZR502ebtmCj5gGnlBYTVCH4bouQ%253D%253D.png'
          },
          { 
            id: 4356, 
            name: 'Pudgy Penguins #4356', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OklfLBahPJlF5mY0BmDhOCJ5dsv1WZXmzxmU%252BSf513FqrUQmKzsCypqDpsVGqh%252BaUA%253D%253D.png'
          },
          { 
            id: 1630, 
            name: 'Pudgy Penguins #1630', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oq5irz0yJJa0p0qszMvYOXLX4f%252BcMn7WZ3H2pGx%252B4%252Ftxm646vSsDfQ6if7CgeLq2xQ%253D%253D.png'
          },
          { 
            id: 6457, 
            name: 'Pudgy Penguins #6457', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ol4viFMlwnI2g%252BifzWcC7PMkR9tob1U3Q1V5PT2YBLAZhsIqTLSb%252Fx8uUu7L1O6nHQ%253D%253D.png'
          },
          { 
            id: 5496, 
            name: 'Pudgy Penguins #5496', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OuPRnQk%252F9pLDNWmOtlrL4dDo3wBgTlvURbdfloxt3zAm%252FdEXOePeDU0%252FkimQXLhKXg%253D%253D.png'
          },
          { 
            id: 6190, 
            name: 'Pudgy Penguins #6190', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Om48zauV2Yy5geiHXDqqH3PZ4%252BTvoJ%252FKZ0B%252Bu4CFjRFyLhxh9Y%252BvVqo52C1QtmrU5A%253D%253D.png'
          },
          { 
            id: 623, 
            name: 'Pudgy Penguins #623', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OlkCM4gGnL2hqbR0d4h3gACqU5Befp7GcUMQs2NbvpnDOuj%252BumWExxcNfzAZ5sY70A%253D%253D.png'
          },
          { 
            id: 919, 
            name: 'Pudgy Penguins #919', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oou3mPC1mVVVG0SBTmUIqo4F3ryWpQR3tqcFMAdDVjRACGIkMDK0c23gr6m%252Ffxwfww%253D%253D.png'
          },
          { 
            id: 3248, 
            name: 'Pudgy Penguins #3248', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ove6K5ag8Irjt5bne4iv7jyoSoMk8%252Bmjs8L8fDNB%252BFJzDoFv7rTbDMRZo4rq1pCQpQ%253D%253D.png'
          },
          { 
            id: 3753, 
            name: 'Pudgy Penguins #3753', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OgFwAAAl73ga8y8zIcfCdSYElPN8MOh6Ynu5mYRZzfegu%252BabM8GgVHs8t8Vet5f0WQ%253D%253D.png'
          },
          { 
            id: 8663, 
            name: 'Pudgy Penguins #8663', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OmRxNPedD1QIgX8B6%252F3bEJW19r8VgYiy5v3VhZGRsFL31%252BIS7dJTiGP9iaYQmVKS5w%253D%253D.png'
          },
          { 
            id: 7651, 
            name: 'Pudgy Penguins #7651', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OofMbzqGcIWPnh4Ph98N3zbAh0%252FFEN%252FDbrq1dD4hvEc%252BYJBMIrpjbMQiJThyK9IiKQ%253D%253D.png'
          },
          { 
            id: 7934, 
            name: 'Pudgy Penguins #7934', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oq6ci5t%252FjYrFTghvv8nqwBk34dDUBJej%252F8kSkNnyFTRUEnCpycxEM0bJ4AplD59zEg%253D%253D.png'
          },
          { 
            id: 1441, 
            name: 'Pudgy Penguins #1441', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oq5gELZBv5Er2zYF2bMZgXKyYaVWFn9%252Bum%252FF0G8YxSBqHse7HMAv28ZYcNr2mJxIzQ%253D%253D.png'
          },
          { 
            id: 1792, 
            name: 'Pudgy Penguins #1792', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OpScmR7jeKdehz15z4GtwMjP3A%252ByBhNzp2BJdMs7FGJKCVJXSBhZnYBE3o7VftDOQw%253D%253D.png'
          },
          { 
            id: 3362, 
            name: 'Pudgy Penguins #3362', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ot1UA9CYxwDY6JES2yA5xFBlWZUZBWBbVgy7%252F%252B16vNPs77pwHqNIKrU7SP6KNeMU3A%253D%253D.png'
          },
          { 
            id: 3581, 
            name: 'Pudgy Penguins #3581', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OrBMHSWpHngPosWiqsF4%252BVnfyxiQjjCij3eultM8I1tQVfGAMDt2ytAtw6cKXMc3Ew%253D%253D.png'
          },
          { 
            id: 7962, 
            name: 'Pudgy Penguins #7962', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Or8Lq4k3sBcPKdnyAo6FGZej%252FFbeW670K8U36VLe4tGK1K1R3nRYKEjlOTsQYkuvbw%253D%253D.png'
          },
          { 
            id: 2847, 
            name: 'Pudgy Penguins #2847', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Otk0btk5GbdxTeDXCsxiYG6gS7uVlHk2cLXByf9ogzYpOdMqUust9aPQjfBHnCUiYA%253D%253D.png'
          },
          { 
            id: 4270, 
            name: 'Pudgy Penguins #4270', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oszg6%252F8xRmCzkl24EoMuNsqt8IsQCNOwLvRt2t82A0Tu2FyWMnFVbU%252F6zve4VKZtYg%253D%253D.png'
          },
          { 
            id: 8542, 
            name: 'Pudgy Penguins #8542', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ojos%252FywnMLDFUcFPq4LUOILcyZcTEeaWuMHN0SgZNsX4Igpge%252F3gV7dahKtGtgRCfw%253D%253D.png'
          },
          { 
            id: 5006, 
            name: 'Pudgy Penguins #5006', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Osagsk%252FvVMXFSS52dnqE%252FefFB4Df76Kgg9arW8tg241Wuj5ZUSFVnn6osOXuOtI1og%253D%253D.png'
          },
          { 
            id: 205, 
            name: 'Pudgy Penguins #205', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OpiksY0SPNTN5fnGjt688hYDujsVWjn1dMZRFJpguL9zG86FgR6IU1nG9Td7TbiaKw%253D%253D.png'
          },
          { 
            id: 5149, 
            name: 'Pudgy Penguins #5149', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OpnaKAHMmg8uEaCQ%252Fu5AGdriDBZY9R6W6xCTRQUxXXnHbTWONY%252B%252FVFG%252FSeTGCXNXBA%253D%253D.png'
          },
          { 
            id: 4759, 
            name: 'Pudgy Penguins #4759', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OnaSGYAmEz2s%252BCsZA%252FqnAPuPhrEDuHtLJW9ixjV8fH3zyOHipHMrqZT%252BnubS1p6fiw%253D%253D.png'
          },
          { 
            id: 3365, 
            name: 'Pudgy Penguins #3365', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oq4u2KqKiMKtT2%252B5hEMuJ5ulEU0sjAbXXTnA5v8NPikn.png'
          },
          { 
            id: 5015, 
            name: 'Pudgy Penguins #5015', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OlMOP6EGSBPSW0Z1he%252F2eYGZ2ViUv1ubtRcmNJu52v5WtCEO787N%252Bm9SdI%252BXE9xZbg%253D%253D.png'
          },
          { 
            id: 7480, 
            name: 'Pudgy Penguins #7480', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OoZ7SZ%252FiDHU1icwbO3nMyDx%252Be5Olo%252FfFQR4L8aKt0pliRIXN9qc5Lv475XD6zEmIkQ%253D%253D.png'
          },
          { 
            id: 4510, 
            name: 'Pudgy Penguins #4510', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Osg1sN%252BguBQs0TLozwsPbCDhw9RGlVmW5MkXMyshNS6znXiN5lGkYywjzid0sXlc8w%253D%253D.png'
          },
          { 
            id: 3314, 
            name: 'Pudgy Penguins #3314', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OqzMLCKb%252BnxWukfcP1aivy0RAowbPlZdiyvoltfzaZK47X3CEDSUNt1bd9aGSndmxg%253D%253D.png'
          },
            
          {
              id: 6074,
    name: 'Pudgy Penguins #6074',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OiI1Pf5yCfrQWq3oAtAxN%252FRs6Ov%252Fv6ZYUMOmGzz36IAhrbE6viEJZNTMWewIGk1GOw%253D%253D.png'
  },
  {
    id: 4682,
    name: 'Pudgy Penguins #4682',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OiUIApGvbXDq8hy9gk%252FkTxBGwv0HO%252F%252BxRpCxtkKelx%252FffJNJZyeSIdX810ZrZ37xyA%253D%253D.png'
  },
  {
    id: 6692,
    name: 'Pudgy Penguins #6692',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OqPuNBrqXLWy%252Bz9RSoptMFpYyZVSOkKdfYTUaaGvrrqDJKXAC0QxsbYbQ3jtj%252FUxgw%253D%253D.png'
  },
  {
    id: 4134,
    name: 'Pudgy Penguins #4134',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OjNOykfWkNOqlIlceNHCGTaHlbLdjXOtFYO0DA6dtYUwlwTafuIUha6QqdTegp22ag%253D%253D.png'
  },
  {
    id: 5392,
    name: 'Pudgy Penguins #5392',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Om3mjJVqpVGEEUjlK5%252FR1KvfvtLFBW8tXr4QAiQ%252Buofl%252BQYkW0LRpnSVAXDMDQ18fw%253D%253D.png'
  },
  {
    id: 761,
    name: 'Pudgy Penguins #761',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oor1PGcKpbSeU8WNxZd1m%252Fsnt6M0aTZkepflt%252BOMCRKcMsM9tFEK61OXheDqauuDBQ%253D%253D.png'
  },
  {
    id: 1228,
    name: 'Pudgy Penguins #1228',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Oor1PGcKpbSeU8WNxZd1m%252Fsnt6M0aTZkepflt%252BOMCRKcMsM9tFEK61OXheDqauuDBQ%253D%253D.png'
  },
  {
    id: 7210,
    name: 'Pudgy Penguins #7210',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OsDy1UVHihQ94JOh10UWFbFnUkjl75HiGOh%252Bg%252BamQQmTK0Vgev5rLrdqzEcHtZq3cA%253D%253D.png'
  },
  {
    id: 4995,
    name: 'Pudgy Penguins #4995',
    image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ogt7DZcrvXq1xYmYHtpM13AoNkPmP1ses%252BJ6LopqkgzwnWiD0ldL3YuTXwHpX2g3%252FA%253D%253D.png'
  },
  {
    id: 8300,
    name: 'Pudgy Penguins #8300',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OoC1Im%252Fz70OiuhzRSz%252FLE%252Bs714oJ021hT%252FpKKicI2Lu0A1NACUHz%252B8EHIVNMPrBtKw%253D%253D.png'
            
  },
  {
    id: 1809,
    name: 'Pudgy Penguins #1809',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OpcNB%252FNIgR05Lk1oQZuyeuxI1yzY4UGFHWVLcfDI5tQTwPgENCP08m4hk2Nih2%252Fycw%253D%253D.png'
  },
  {
    id: 3627,
    name: 'Pudgy Penguins #3627',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OkV7Nb3C2fxknlgsZKSqGqpzL7%252BAZzZQyEiQBjfpQZxDHLfG%252BJjnm1SiEzDaAL72Lw%253D%253D.png'
  },
  {
    id: 6018,
    name: 'Pudgy Penguins #6018',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OlJZaiPbjUL3sOPHmFaql9ptSM46D813HjxeQwMoP1eG5VjbMQq48Ty3TZycWodobQ%253D%253D.png'
  },
  {
    id: 7219,
    name: 'Pudgy Penguins #7219',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OlPlElxOjjnyq33mc67jjp47xM4mkWHgxKnHegIWmY27Eq2qCNLYXY4FgUvDoYenaQ%253D%253D.png'
  },
  {
    id: 2485,
    name: 'Pudgy Penguins #2485',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Or92Lemn%252Fv6HylKmK%252FsGhGAUOrcFgZf1F4crgrhplyL3HxBOKPjkHrCTeUkPQnGW8g%253D%253D.png'
  },
  {
    id: 1234,
    name: 'Pudgy Penguins #1234',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OoZdpCTHL%252FIm18IdYnxWcSpJonRAj7DaT9%252Fm0BSkJ4GAvjw0oVqmCqEFAPLbb66uVQ%253D%253D.png'
  },
  {
    id: 4319,
    name: 'Pudgy Penguins #4319',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Okn3He2CzoRXJs7L95j%252BSumfxRe%252FoI7bayIk0kPMh84szGx2sCAV3RfUkI%252Bqo3zNBw%253D%253D.png'
  },
  {
    id: 8361,
    name: 'Pudgy Penguins #8361',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OozzbwPHKLuwf5vty%252FupOAVKnbUISjb8hbIn6hj4XnM7Og3t0wTBVx25ZbAdshqeHw%253D%253D.png'
  },
  {
    id: 5822,
    name: 'Pudgy Penguins #5822',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OuSjdVbSJVbmWqLg9YpZjYjM%252B8kDfYiVy8p9XUHKZwaX6ab%252BA2YI7IaMcnSLwHcwIQ%253D%253D.png'
  },
  {
    id: 7465,
    name: 'Pudgy Penguins #7465',
    image: 'https://img-cdn.magiceden.dev/rs:fill:800:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OlY1fgORAJL8ui4n08Cyle1%252BdjhjNMHl%252Fvk19kxeUS7PFUHlMhRTypLv3lKd7DHbcA%253D%253D.png'
  },
            
            
            
            
          { 
            id: 7070, 
            name: 'Pudgy Penguins #7070', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OiwVUw1QugZOlSJsr1HguFGGfbEpTvIEZ7DWuF0HIpxPWfDfVMAYdwkKrwpaMMmPkA%253D%253D.png'
          },
          { 
            id: 8394, 
            name: 'Pudgy Penguins #8394', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71OinOMgJFYubEC5XmQL%252BHTFGqLkVx%252BcGeVVbsPCfwVyxOjc%252B5wRbA5Xd%252FI6p8IEbRYg%253D%253D.png'
          },
          { 
            id: 3463, 
            name: 'Pudgy Penguins #3463', 
            image: 'https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2Fi9YO%252F4yHXUdJsWcTqhqvf2sE%252BcsbkeVl%252B4W8jWCuUkuov0PzHyR9IJ1xIZwBbFfEA%252Bwbk8y%252FqPIB7%252Bl8cd71Ol%252BSRNP6Q4Ng4QK18HMsje1Vv6EL1fKleSzt2wdgtcjn46Egsjwobrl6%252FyDUB9Jpsg%253D%253D.png'
          }
        ];
        actualNftData = pudgyPenguinsNfts.find(nft => nft.id.toString() === itemId);
      } else if (collectionId === 'azuki') {
        const azukiNfts = [
          { id: 8116, name: 'Azuki #8116', image: 'https://i.seadn.io/s/raw/files/d690b608a3693bc2c8987c91d52922bb.png?auto=format&dpr=1&w=640' },
          { id: 9286, name: 'Azuki #9286', image: 'https://i.seadn.io/s/raw/files/69b0ffe8b4227a305810ffb8a632d9a2.png?auto=format&dpr=1&w=640' },
          { id: 327, name: 'Azuki #327', image: 'https://i.seadn.io/s/raw/files/f8c597d61abed047c3e4e2c0dbcf3594.png?auto=format&dpr=1&w=640' },
          { id: 1394, name: 'Azuki #1394', image: 'https://i.seadn.io/s/raw/files/a61e30c09621be8472590ffe081401f4.png?auto=format&dpr=1&w=640' },
          { id: 6798, name: 'Azuki #6798', image: 'https://i.seadn.io/s/raw/files/2525cc5c0946332f9e42a61640f766d3.png?auto=format&dpr=1&w=640' },
          { id: 8747, name: 'Azuki #8747', image: 'https://i.seadn.io/s/raw/files/14d7c47d24a7467aee48fb4d2bac11d9.png?auto=format&dpr=1&w=640' },
          { id: 3036, name: 'Azuki #3036', image: 'https://i.seadn.io/s/raw/files/1e44c22fe83691188304a4fcaf1093a6.png?auto=format&dpr=1&w=640' },
          { id: 1413, name: 'Azuki #1413', image: 'https://i.seadn.io/s/raw/files/c9b083b549a15aee5b5bf79fca80f710.png?auto=format&dpr=1&w=640' },
          { id: 1400, name: 'Azuki #1400', image: 'https://i.seadn.io/s/raw/files/3eaac906206cb7bacf851714d45762f9.png?auto=format&dpr=1&w=640' },
          { id: 3703, name: 'Azuki #3703', image: 'https://i.seadn.io/s/raw/files/ae57d8d41e7fe519c00f613fb38e4830.png?auto=format&dpr=1&w=640' },
          { id: 465, name: 'Azuki #465', image: 'https://i.seadn.io/s/raw/files/c5a670ebbe8081f8e42a046bdec1a230.png?auto=format&dpr=1&w=640' },
          { id: 3574, name: 'Azuki #3574', image: 'https://i.seadn.io/s/raw/files/ed884b8031b22298d6b453bfa9e9cf02.png?auto=format&dpr=1&w=640' },
          { id: 4345, name: 'Azuki #4345', image: 'https://i.seadn.io/s/raw/files/72323815859cb5b717223bbc8025af77.png?auto=format&dpr=1&w=640' },
          { id: 3238, name: 'Azuki #3238', image: 'https://i.seadn.io/s/raw/files/c04b4956ba7b2746afaa851365144cc8.png?auto=format&dpr=1&w=640' },
          { id: 7814, name: 'Azuki #7814', image: 'https://i.seadn.io/s/raw/files/3d331c6d8f50c391e438dbba737275d4.png?auto=format&dpr=1&w=640' },
          { id: 7584, name: 'Azuki #7584', image: 'https://i.seadn.io/s/raw/files/61200e110093e565e6da900d04fca628.png?auto=format&dpr=1&w=640' },
          { id: 9915, name: 'Azuki #9915', image: 'https://i.seadn.io/s/raw/files/6e0b8baf258a72b5097f9cb5d40122c8.png?auto=format&dpr=1&w=640' },
            
            { id: 301, name: 'Azuki #301', image: 'https://i.seadn.io/s/raw/files/4e9635bd0dcfbb3222d8682991e1b0b0.png?auto=format&dpr=1&w=640'},
    { id: 5442, name: 'Azuki #5442', image: 'https://i.seadn.io/s/raw/files/b7696b98d382d431231ec87752513c31.png?auto=format&dpr=1&w=640'},
    { id: 7102, name: 'Azuki #7102', image: 'https://i.seadn.io/s/raw/files/f7758ec34d559e46fa73664523cc8948.png?auto=format&dpr=1&w=640' },
    { id: 469, name: 'Azuki #469', image: 'https://i.seadn.io/s/raw/files/56f38736685804856dc34c382d3e8fe8.png?auto=format&dpr=1&w=640' },
    { id: 1565, name: 'Azuki #1565', image: 'https://i.seadn.io/s/raw/files/6226311161ba8023b8ce9d3355d9a2e0.png?auto=format&dpr=1&w=640'},
    { id: 9960, name: 'Azuki #9960', image: 'https://i.seadn.io/s/raw/files/6b83b469fe5c0353a0e8c4e1ba60bf86.png?auto=format&dpr=1&w=640' },
    { id: 5181, name: 'Azuki #5181', image: 'https://i.seadn.io/s/raw/files/28418ad1a8056f798e38aae0c6b96f6a.png?auto=format&dpr=1&w=640' },
    { id: 8305, name: 'Azuki #8305', image: 'https://i.seadn.io/s/raw/files/591aab45dc3ef44c19ab49a83d4d0c60.png?auto=format&dpr=1&w=640'},
    { id: 6992, name: 'Azuki #6992', image: 'https://i.seadn.io/s/raw/files/41e691e3de4153f38c3c9a925cc4b36a.png?auto=format&dpr=1&w=640'},
    { id: 4466, name: 'Azuki #4466', image: 'https://i.seadn.io/s/raw/files/c91f283153aaeb008ba0fb4e7218edf8.png?auto=format&dpr=1&w=640' },
    { id: 2917, name: 'Azuki #2917', image: 'https://i.seadn.io/s/raw/files/6d876c0893c9a21ae2255a52aa123094.png?auto=format&dpr=1&w=640' },
    { id: 7857, name: 'Azuki #7857', image: 'https://i.seadn.io/s/raw/files/c99f3f082a69fe9db9983d5859a1dcc8.png?auto=format&dpr=1&w=640'},
    { id: 1051, name: 'Azuki #1051', image: 'https://i.seadn.io/s/raw/files/2e26a0d4ca77941dd687412400fe9e81.png?auto=format&dpr=1&w=640'},
    { id: 7644, name: 'Azuki #7644', image: 'https://i.seadn.io/s/raw/files/42daba97c6a9dace35f115f46d6444d7.png?auto=format&dpr=1&w=640' },
    { id: 2952, name: 'Azuki #2952', image: 'https://i.seadn.io/s/raw/files/fef752000cb4977efc1f6845f50e0294.png?auto=format&dpr=1&w=640' },
    { id: 6744, name: 'Azuki #6744', image: 'https://i.seadn.io/s/raw/files/87dfc2b06cafd095a4769dc67b6a6797.png?auto=format&dpr=1&w=640' },
    { id: 6107, name: 'Azuki #6107', image: 'https://i.seadn.io/s/raw/files/7ea6b2c743c742daec292bcc7d75c463.png?auto=format&dpr=1&w=640'},
    { id: 6996, name: 'Azuki #6996', image: 'https://i.seadn.io/s/raw/files/0f224605eae077d7336b0184a179b62c.png?auto=format&dpr=1&w=640', },
    { id: 5626, name: 'Azuki #5626', image: 'https://i.seadn.io/s/raw/files/e3c7ce94a0002aa45a45fc1ac142b07d.png?auto=format&dpr=1&w=640'},

            
            { id: 3723, name: 'Azuki #3723', image: 'https://i.seadn.io/s/raw/files/9ca519258ccb2032a266886bf4e5d8e1.png?auto=format&dpr=1&w=640',},
{ id: 1994, name: 'Azuki #1994', image: 'https://i.seadn.io/s/raw/files/88eae6e47b8e14481782ae3c0b11ef75.png?auto=format&dpr=1&w=640' },
{ id: 4185, name: 'Azuki #4185', image: 'https://i.seadn.io/s/raw/files/96de5d23f0a30dde80cbe8db6e2144ae.png?auto=format&dpr=1&w=640'},
{ id: 3440, name: 'Azuki #3440', image: 'https://i.seadn.io/s/raw/files/41adabd14690092f5488ab8cbeb294d5.png?auto=format&dpr=1&w=640'},
{ id: 331, name: 'Azuki #331', image: 'https://i.seadn.io/s/raw/files/54a9792e74313fe11772761b8c836afb.png?auto=format&dpr=1&w=640' },
{ id: 7064, name: 'Azuki #7064', image: 'https://i.seadn.io/s/raw/files/fd1130ac64ffa39827d78c5d1f8dd7fd.png?auto=format&dpr=1&w=640'},
{ id: 5919, name: 'Azuki #5919', image: 'https://i.seadn.io/s/raw/files/0627c6c25a33f810add6499a9cafda63.png?auto=format&dpr=1&w=640' },
{ id: 6961, name: 'Azuki #6961', image: 'https://i.seadn.io/s/raw/files/4b56abee99d6bedd3183f1c7c04e96e6.png?auto=format&dpr=1&w=640'},
{ id: 3044, name: 'Azuki #3044', image: 'https://i.seadn.io/s/raw/files/6d0bacb5c4e525cf78a0c85cc9f7093f.png?auto=format&dpr=1&w=640'},
{ id: 1328, name: 'Azuki #1328', image: 'https://i.seadn.io/s/raw/files/e1bbfd6c74465d7d1283c5be208b399a.png?auto=format&dpr=1&w=640'},
{ id: 2705, name: 'Azuki #2705', image: 'https://i.seadn.io/s/raw/files/f5a65b8bcc3033ed50300f1979c956bb.png?auto=format&dpr=1&w=640'},
{ id: 9229, name: 'Azuki #9229', image: 'https://i.seadn.io/s/raw/files/41f85e728fe4eb204fa1577740cb8786.png?auto=format&dpr=1&w=640'},
{ id: 5630, name: 'Azuki #5630', image: 'https://i.seadn.io/s/raw/files/265989d6a5f8309e41a05323ec59b4cf.png?auto=format&dpr=1&w=640'},
{ id: 9006, name: 'Azuki #9006', image: 'https://i.seadn.io/s/raw/files/cd1e6423a3a7655e2a71c98ea9e70c38.png?auto=format&dpr=1&w=640' },
{ id: 6680, name: 'Azuki #6680', image: 'https://i.seadn.io/s/raw/files/3560e5cde52d1856abaef355d48fff1c.png?auto=format&dpr=1&w=640'},
{ id: 6048, name: 'Azuki #6048', image: 'https://i.seadn.io/s/raw/files/dc9ce6ed6c9fb7ce9c848a18104cf577.png?auto=format&dpr=1&w=640'},
{ id: 6045, name: 'Azuki #6045', image: 'https://i.seadn.io/s/raw/files/f1a20a30a665ba7746333f42ae18f69c.png?auto=format&dpr=1&w=640'},
{ id: 7920, name: 'Azuki #7920', image: 'https://i.seadn.io/s/raw/files/9ddf4260570c911535b3cf97f5295f73.png?auto=format&dpr=1&w=640'},
{ id: 2257, name: 'Azuki #2257', image: 'https://i.seadn.io/s/raw/files/085538b31b08df6f0ae29b97c8467ec0.png?auto=format&dpr=1&w=640'},
{ id: 1348, name: 'Azuki #1348', image: 'https://i.seadn.io/s/raw/files/15c56692fe749724469ad394672b1af1.png?auto=format&dpr=1&w=640' },

            { id: 8661, name: 'Azuki #8661', image: 'https://i.seadn.io/s/raw/files/3a8a30f022d3648ee0ce23ab2a9c73f8.png?auto=format&dpr=1&w=640' },
{ id: 6080, name: 'Azuki #6080', image: 'https://i.seadn.io/s/raw/files/d7948d0cfe90a5c923c65ff81975101a.png?auto=format&dpr=1&w=640'},
{ id: 989, name: 'Azuki #989', image: 'https://i.seadn.io/s/raw/files/926a70c48db754d60a2e1efeeab77b64.png?auto=format&dpr=1&w=640' },
{ id: 9632, name: 'Azuki #9632', image: 'https://i.seadn.io/s/raw/files/268e3108e3ea8f32f9dba8a12b3ed547.png?auto=format&dpr=1&w=640'},
{ id: 1311, name: 'Azuki #1311', image: 'https://i.seadn.io/s/raw/files/37e20b5a6e865cc9303a6bf89ec655f3.png?auto=format&dpr=1&w=640'},
{ id: 7113, name: 'Azuki #7113', image: 'https://i.seadn.io/s/raw/files/5cc56dbe2e37f9598e4ca7a57696d9ef.png?auto=format&dpr=1&w=640' },
{ id: 6971, name: 'Azuki #6971', image: 'https://i.seadn.io/s/raw/files/22e291b59705693c87807b8de8b55ca1.png?auto=format&dpr=1&w=640' },
{ id: 6598, name: 'Azuki #6598', image: 'https://i.seadn.io/s/raw/files/2c202053c4f0be27a8078a8dd9137696.png?auto=format&dpr=1&w=640' },
{ id: 4934, name: 'Azuki #4934', image: 'https://i.seadn.io/s/raw/files/7d0894f2602e561fc0417804c10455fa.png?auto=format&dpr=1&w=640' },
{ id: 9197, name: 'Azuki #9197', image: 'https://i.seadn.io/s/raw/files/d161914291b721af2eb84592de7e4f1c.png?auto=format&dpr=1&w=640' },
{ id: 1520, name: 'Azuki #1520', image: 'https://i.seadn.io/s/raw/files/ddae8c9bfc92ad399007b9f5333fd5dd.png?auto=format&dpr=1&w=640'},
{ id: 8323, name: 'Azuki #8323', image: 'https://i.seadn.io/s/raw/files/73b5f46525f542712684f72fba4928c6.png?auto=format&dpr=1&w=640'},
{ id: 8127, name: 'Azuki #8127', image: 'https://i.seadn.io/s/raw/files/cb59845bb7fcf0c75fdb63ec059153c3.png?auto=format&dpr=1&w=640' },
{ id: 7238, name: 'Azuki #7238', image: 'https://i.seadn.io/s/raw/files/d3f2fb26e10ba896b770f9956cd3d9e8.png?auto=format&dpr=1&w=640' },
{ id: 7048, name: 'Azuki #7048', image: 'https://i.seadn.io/s/raw/files/cf76f9c142bd5bc00f6507072d2162e5.png?auto=format&dpr=1&w=640' },
{ id: 8824, name: 'Azuki #8824', image: 'https://i.seadn.io/s/raw/files/05284f714f3fa0364084e8be46df75e8.png?auto=format&dpr=1&w=640'},
{ id: 3151, name: 'Azuki #3151', image: 'https://i.seadn.io/s/raw/files/1b9e4c3cff85ac9c37a7f3434da4694c.png?auto=format&dpr=1&w=640'},
{ id: 3373, name: 'Azuki #3373', image: 'https://i.seadn.io/s/raw/files/58fa48150d6398eaacbb77f129c331c8.png?auto=format&dpr=1&w=640' },
{ id: 7148, name: 'Azuki #7148', image: 'https://i.seadn.io/s/raw/files/6f9cdbb6ef7e0333835ba8566f4285ee.png?auto=format&dpr=1&w=640'},
{ id: 3467, name: 'Azuki #3467', image: 'https://i.seadn.io/s/raw/files/fc65094c6edf1057f8ff631d26c92fcd.png?auto=format&dpr=1&w=640' },
{ id: 3376, name: 'Azuki #3376', image: 'https://i.seadn.io/s/raw/files/74f0df3a2d1db5e5881e76acb2ed3ebc.png?auto=format&dpr=1&w=640' },
{ id: 1796, name: 'Azuki #1796', image: 'https://i.seadn.io/s/raw/files/df03116ccad9ba11bd10814f92181359.png?auto=format&dpr=1&w=640' }
            
        ];
        actualNftData = azukiNfts.find(nft => nft.id.toString() === itemId);
      } else if (collectionId === 'lilpudgys') {
        const lilpudgysNfts = [
          { id: 730, name: 'LilPudgys #730', image: 'https://i.seadn.io/gcs/files/5fe365d489e084030729eba2cc632ded.png?auto=format&dpr=1&w=384' },
          { id: 15097, name: 'LilPudgys #15097', image: 'https://i.seadn.io/gcs/files/a0f89aa997992b75036f7f1511da5b17.png?auto=format&dpr=1&w=384' },
          { id: 1768, name: 'LilPudgys #1768', image: 'https://i.seadn.io/gcs/files/b2ab8abc178043df6f6e36b5f4bd3ca6.png?auto=format&dpr=1&w=384' },
          { id: 16733, name: 'LilPudgys #16733', image: 'https://i.seadn.io/gcs/files/e7432828862b39c17a42231e17b97186.png?auto=format&dpr=1&w=384' },
            
            { id: 942, name: 'LilPudgys #942', image: 'https://i.seadn.io/s/raw/files/aa5b6ed177274c24698bf3e6fc80c9eb.png?auto=format&dpr=1&w=384' },
          { id: 2679, name: 'LilPudgys #2679', image: 'https://i.seadn.io/gcs/files/42baf33a7a31f9f5b37b2693c1cda2a7.png?auto=format&dpr=1&w=384' },
          { id: 1593, name: 'LilPudgys #1593', image: 'https://i.seadn.io/gcs/files/37852cb5d4c0a75114b242605e66fde0.png?auto=format&dpr=1&w=384' },
          { id: 15901, name: 'LilPudgys #15901', image: 'https://i.seadn.io/gcs/files/568dd4310cde56d9a67b2e7bd0b78060.png?auto=format&dpr=1&w=384' },
          { id: 12211, name: 'LilPudgys #12211', image: 'https://i.seadn.io/gcs/files/417bc92f240f298eca2fc580fd16087e.png?auto=format&dpr=1&w=384' },
            
            { id: 22083, name: 'LilPudgys #22083', image: 'https://i.seadn.io/gcs/files/3cc41ea1461c578734d4f4caad0480f7.png?auto=format&dpr=1&w=384' },
          { id: 3840, name: 'LilPudgys #3840', image: 'https://i.seadn.io/gcs/files/be26d5e3257ea172f18607406971d416.png?auto=format&dpr=1&w=384'},
          { id: 18766, name: 'LilPudgys #18766', image: 'https://i.seadn.io/gcs/files/361d0b0dcd1e199b1f44174a2aaeb25a.png?auto=format&dpr=1&w=384' },
          { id: 17286, name: 'LilPudgys #17286', image: 'https://i.seadn.io/gcs/files/266afacecb64939f0217a9c8b529a5d7.png?auto=format&dpr=1&w=1000' },
          { id: 17122, name: 'LilPudgys #17122', image: 'https://i.seadn.io/gcs/files/31067d622e725bd7d52bf12acced0788.png?auto=format&dpr=1&w=384' },
          { id: 11860, name: 'LilPudgys #11860', image: 'https://i.seadn.io/gcs/files/f0978cde39fad36b401b1215f8742028.png?auto=format&dpr=1&w=384' },
          { id: 4517, name: 'LilPudgys #4517', image: 'https://i.seadn.io/gcs/files/c48d549c26605e3f3ad0500ee03589ca.png?auto=format&dpr=1&w=384' },
          { id: 14163, name: 'LilPudgys #14163', image: 'https://i.seadn.io/gcs/files/416ad81de008f62b01aae5fd33a2b91e.png?auto=format&dpr=1&w=384' },
          { id: 14165, name: 'LilPudgys #14165', image: 'https://i.seadn.io/gcs/files/f879c553362f9ddb72c989000cf82e75.png?auto=format&dpr=1&w=384' },
          { id: 12227, name: 'LilPudgys #12227', image: 'https://i.seadn.io/gcs/files/40b9e90659645a9ec7c5e726c40b64a9.png?auto=format&dpr=1&w=384' },
          { id: 6494, name: 'LilPudgys #6494', image: 'https://i.seadn.io/gcs/files/646fa61dcd128c1e853c4337fdd099f7.png?auto=format&dpr=1&w=384' },
          { id: 7664, name: 'LilPudgys #7664', image: 'https://i.seadn.io/gcs/files/14997055dcb410d12ab0488fa7fddef8.png?auto=format&dpr=1&w=384' },
          { id: 4823, name: 'LilPudgys #4823', image: 'https://i.seadn.io/gcs/files/e731a2904aecce18933cb994375c7444.png?auto=format&dpr=1&w=384' },
          { id: 3618, name: 'LilPudgys #3618', image: 'https://i.seadn.io/gcs/files/a2d7f3ab8cc5d759dfaeb64e1c7a11ce.png?auto=format&dpr=1&w=384' },
          { id: 17100, name: 'LilPudgys #17100', image: 'https://i.seadn.io/gcs/files/9713f39b9163bdc898b7cdf48cda2ea9.png?auto=format&dpr=1&w=384' },
          { id: 21308, name: 'LilPudgys #21308', image: 'https://i.seadn.io/gcs/files/45603eef3867f5d5c1268c883a700e67.png?auto=format&dpr=1&w=384' },
          { id: 12809, name: 'LilPudgys #12809', image: 'https://i.seadn.io/gcs/files/cb0f654facc91dc28caac49c3427176e.png?auto=format&dpr=1&w=384' },
          { id: 10759, name: 'LilPudgys #10759', image: 'https://i.seadn.io/gcs/files/95d0b893d343f3867bf1ed8d5b8abf8f.png?auto=format&dpr=1&w=384' },
          { id: 13570, name: 'LilPudgys #13570', image: 'https://i.seadn.io/gcs/files/ac3d64887d557b3b2e0dd2579a0411ca.png?auto=format&dpr=1&w=384' },
          { id: 2655, name: 'LilPudgys #2655', image: 'https://i.seadn.io/gcs/files/688e8ee889dc3b02835b37f89b073118.png?auto=format&dpr=1&w=384' },
          { id: 17806, name: 'LilPudgys #17806', image: 'https://i.seadn.io/gcs/files/7959637d8beddc4328dd0779ad8a9922.png?auto=format&dpr=1&w=384' },
          { id: 4249, name: 'LilPudgys #4249', image: 'https://i.seadn.io/gcs/files/fe660570ef743ceea6a01c2c7e11dce1.png?auto=format&dpr=1&w=384' },
          { id: 6232, name: 'LilPudgys #6232', image: 'https://i.seadn.io/gcs/files/1241b439eb9a3d7d80af0c3e0a440e7c.png?auto=format&dpr=1&w=384' },
          { id: 5046, name: 'LilPudgys #5046', image: 'https://i.seadn.io/s/raw/files/3e185be386a8fee00db2f56090fde18b.png?auto=format&dpr=1&w=384' },
          { id: 19430, name: 'LilPudgys #19430', image: 'https://i.seadn.io/gcs/files/3cc41ea1461c578734d4f4caad0480f7.png?auto=format&dpr=1&w=384' },
          { id: 4126, name: 'LilPudgys #4126', image: 'https://i.seadn.io/gcs/files/085d73fa8db577e8c4d7c6dd44e774c0.png?auto=format&dpr=1&w=384' },
          { id: 15833, name: 'LilPudgys #15833', image: 'https://i.seadn.io/gcs/files/deed8ff91c01f718187f7c0752beb56a.png?auto=format&dpr=1&w=384' },
          { id: 1184, name: 'LilPudgys #1184', image: 'https://i.seadn.io/gcs/files/fb7f71509d0605c39956330505067870.png?auto=format&dpr=1&w=384' },
          { id: 10705, name: 'LilPudgys #10705', image: 'https://i.seadn.io/gcs/files/f9471aeac1b991d36e80b1d71f4c1300.png?auto=format&dpr=1&w=384' },
          { id: 7871, name: 'LilPudgys #7871', image: 'https://i.seadn.io/gcs/files/cf8fa4a2dffcd25e29d43765b2f5771c.png?auto=format&dpr=1&w=384' },
          { id: 17665, name: 'LilPudgys #17665', image: 'https://i.seadn.io/gcs/files/1b822dc4613c19cdb236858b365dd78f.png?auto=format&dpr=1&w=384' },
          { id: 5312, name: 'LilPudgys #5312', image: 'https://i.seadn.io/gcs/files/a69d514ef6b8ddcc694999781b18325c.png?auto=format&dpr=1&w=384' },
          { id: 17944, name: 'LilPudgys #17944', image: 'https://i.seadn.io/gcs/files/b9e75831fa11c2e13c56f4316200be1c.png?auto=format&dpr=1&w=384' },
          { id: 17383, name: 'LilPudgys #17383', image: 'https://i.seadn.io/gcs/files/dbb1535378835d7cb6b80dff27ddc1ea.png?auto=format&dpr=1&w=384' },
          { id: 13548, name: 'LilPudgys #13548', image: 'https://i.seadn.io/gcs/files/306b0a8117c7b124685176465c7b8557.png?auto=format&dpr=1&w=384' },
          { id: 16757, name: 'LilPudgys #16757', image: 'https://i.seadn.io/gcs/files/85553c737aac9d87a8616a9e25657452.png?auto=format&dpr=1&w=384' },
          { id: 8357, name: 'LilPudgys #8357', image: 'https://i.seadn.io/gcs/files/a625c7957942d3cecb0dc3b38073d659.png?auto=format&dpr=1&w=384' },
          { id: 20480, name: 'LilPudgys #20480', image: 'https://i.seadn.io/gcs/files/42dcd04e44877204cb3b53ace11b80c9.png?auto=format&dpr=1&w=384' },
          { id: 19365, name: 'LilPudgys #19365', image: 'https://i.seadn.io/gcs/files/2b5424af285867d0ce2cb23ce4309f60.png?auto=format&dpr=1&w=384' },
          { id: 2644, name: 'LilPudgys #2644', image: 'https://i.seadn.io/gcs/files/fe6422cf4556145cb4301d9c0a3d191a.png?auto=format&dpr=1&w=384' },
          { id: 4632, name: 'LilPudgys #4632', image: 'https://i.seadn.io/gcs/files/c4a1444ee07deedf14758d4e697b0538.png?auto=format&dpr=1&w=384' },
          { id: 18999, name: 'LilPudgys #18999', image: 'https://i.seadn.io/gcs/files/8b417f70e5b8b772757208c2ead9a70d.png?auto=format&dpr=1&w=384' },
          { id: 12527, name: 'LilPudgys #12527', image: 'https://i.seadn.io/gcs/files/8cea313fa5fe6ba35a0c989c45e03028.png?auto=format&dpr=1&w=384' },
          { id: 19237, name: 'LilPudgys #19237', image: 'https://i.seadn.io/gcs/files/dd2bee0e108d49d1f3847177305d957c.png?auto=format&dpr=1&w=384' },
            
        { id: 19237, name: 'LilPudgys #19237', image: 'https://i.seadn.io/gcs/files/dd2bee0e108d49d1f3847177305d957c.png?auto=format&dpr=1&w=384' },
          { id: 12922, name: 'LilPudgys #12922', image: 'https://i.seadn.io/gcs/files/4e79b779f4152a85828e9fe51541d657.png?auto=format&dpr=1&w=384' },
          { id: 7205, name: 'LilPudgys #7205', image: 'https://i.seadn.io/gcs/files/7e5113c74fe9b600296801458a23a61a.png?auto=format&dpr=1&w=384' },
          { id: 15383, name: 'LilPudgys #15383', image: 'https://i.seadn.io/gcs/files/2cebc98e4b10e9cc1ddb0e5783b55ff7.png?auto=format&dpr=1&w=384' },
          { id: 775, name: 'LilPudgys #775', image: 'https://i.seadn.io/gcs/files/2d493254f62e68003b6043dc5ba1fcb7.png?auto=format&dpr=1&w=384' },
          { id: 18527, name: 'LilPudgys #18527', image: 'https://i.seadn.io/gcs/files/48a1ca3584781dda4b93a331e53d5159.png?auto=format&dpr=1&w=384' },
          { id: 21691, name: 'LilPudgys #21691', image: 'https://i.seadn.io/gcs/files/362422abbf34d8c74af66e8a088eafc5.png?auto=format&dpr=1&w=384' },
          { id: 14290, name: 'LilPudgys #14290', image: 'https://i.seadn.io/gcs/files/b937fb44f56ceeb37f193288f29c4bee.png?auto=format&dpr=1&w=384' },
          { id: 1794, name: 'LilPudgys #1794', image: 'https://i.seadn.io/gcs/files/b90e19d1ff5e7a49e8d0c5d1d17adec0.png?auto=format&dpr=1&w=384' },
          { id: 9700, name: 'LilPudgys #9700', image: 'https://i.seadn.io/gcs/files/fc96c314bde191116e4c23bdcd31235b.png?auto=format&dpr=1&w=384' },
          { id: 2811, name: 'LilPudgys #2811', image: 'https://i.seadn.io/gcs/files/0934178995c9141b2b9d0054858ff911.png?auto=format&dpr=1&w=384' },
          { id: 12687, name: 'LilPudgys #12687', image: 'https://i.seadn.io/gcs/files/f91469c5a86f6fb51f78804b0f025dcd.png?auto=format&dpr=1&w=384' },
          { id: 3249, name: 'LilPudgys #3249', image: 'https://i.seadn.io/gcs/files/2b3766cdd24814aec1827d9c0a461e21.png?auto=format&dpr=1&w=384' },
          { id: 13537, name: 'LilPudgys #13537', image: 'https://i.seadn.io/gcs/files/9bf37918470befd50c428eaa27074bd3.png?auto=format&dpr=1&w=384' },
          { id: 11518, name: 'LilPudgys #11518', image: 'https://i.seadn.io/gcs/files/c44aea38b7dad82d002d07ac5a1486cb.png?auto=format&dpr=1&w=384' },
          { id: 11094, name: 'LilPudgys #11094', image: 'https://i.seadn.io/gcs/files/fbffe75b2d84f9ac392842c2940d3950.png?auto=format&dpr=1&w=384' },
          { id: 12309, name: 'LilPudgys #12309', image: 'https://i.seadn.io/gcs/files/f9af52c5529cc9104c91c86adaf4e576.png?auto=format&dpr=1&w=384' },
          { id: 15127, name: 'LilPudgys #15127', image: 'https://i.seadn.io/s/raw/files/d128fb786fd71e0ae48c93a1e01a9b33.png?auto=format&dpr=1&w=384' },
          { id: 12276, name: 'LilPudgys #12276', image: 'https://i.seadn.io/gcs/files/00c23f99fc4e6ae07472ace335dc2ad6.png?auto=format&dpr=1&w=384' },
          { id: 21556, name: 'LilPudgys #21556', image: 'https://i.seadn.io/gcs/files/35e838d0816e26ead775bb95dd6b39f6.png?auto=format&dpr=1&w=384' },
          { id: 20589, name: 'LilPudgys #20589', image: 'https://i.seadn.io/gcs/files/455168abed5cf0c396ad2fcede4a1b54.png?auto=format&dpr=1&w=384' },
          { id: 16885, name: 'LilPudgys #16885', image: 'https://i.seadn.io/gcs/files/761d3d26539c2d8f5577f915e231a664.png?auto=format&dpr=1&w=384' },
          { id: 13365, name: 'LilPudgys #13365', image: 'https://i.seadn.io/gcs/files/560c573e14fec06a67f3b7a615f37839.png?auto=format&dpr=1&w=384' },
          { id: 13189, name: 'LilPudgys #13189', image: 'https://i.seadn.io/gcs/files/24cb007cd91b12b7f87fbfe3a99dc8b5.png?auto=format&dpr=1&w=384' },
          { id: 10690, name: 'LilPudgys #10690', image: 'https://i.seadn.io/gcs/files/421f9217b71dd56ee27418bbe89809f3.png?auto=format&dpr=1&w=384' },
          { id: 16159, name: 'LilPudgys #16159', image: 'https://i.seadn.io/gcs/files/b1301bc6e375ff0a196f0ec572877105.png?auto=format&dpr=1&w=384' },
          { id: 18230, name: 'LilPudgys #18230', image: 'https://i.seadn.io/gcs/files/5bb4932c896c743cfc2fa580bea3bcc8.png?auto=format&dpr=1&w=384' }
    
            
        ];
        actualNftData = lilpudgysNfts.find(nft => nft.id.toString() === itemId);
          
          } else if (collectionId === 'doodles') {
        const doodlesNfts = [
       { id: "9994", name: "Doodle #9994", image: "https://ipfs.io/ipfs/QmYBYWMwGcyn1GoettFnn4yzpzRUwtaYrt3XscsxcyN6hD"},
       { id: "9954", name: "Doodle #9954", image: "https://ipfs.io/ipfs/QmQBCpSgpfxbyxSJQpYnXD5hU2afvEGrdV7Uyu4WYrvosc" },
       { id: "9972", name: "Doodle #9972", image: "https://ipfs.io/ipfs/QmWXmHP86nWnBNWx3NBAMnDeXppU69CpZqiaTMsdHwqpwH" },
       { id: "9984", name: "Doodle #9984", image: "https://ipfs.io/ipfs/Qmb5eyoo2KXamWDJh1deMmNq12eSQLrf7SEqwMxwHnAhTm" },
       { id: "9989", name: "Doodle #9989", image: "https://ipfs.io/ipfs/QmVQE36eU6mZsHjDVTP1vY1Hw9ix3XEwAhWtDRJ23NktxK" },
          {id: "9999", name: "Doodle #9999", image: "https://ipfs.io/ipfs/QmVdZcdjS5fAzqzH4nMeKrqEhZM6Kjf6EgRDhxZEemEjJM" },
            
  { id: "9998", name: "Doodle #9998", image: "https://ipfs.io/ipfs/QmZ1xD9zibPWqTWpTGSscWvqVDEyyRR6YBm1TmP5s1i22z" },
  { id: "9967", name: "Doodle #9967", image: "https://ipfs.io/ipfs/QmaP2tLRsVCFCzYctYf4uRLV7HkjHmd9aKtGM1ycBoAESs" },
  { id: "9944", name: "Doodle #9944", image: "https://ipfs.io/ipfs/QmNUHQTXXofjZKkHqkGcLiRoBm6wWXATtsY5TkjZzyFAL2" },
  { id: "9928", name: "Doodle #9928", image: "https://ipfs.io/ipfs/QmNxYNrimJBJfQwutGcfvHpxKsTYTaYupZXViivYY8uPnd" },
  { id: "9993", name: "Doodle #9993", image: "https://ipfs.io/ipfs/QmQ3xApuZrgZsqBzJmqzGVTarVfLc11e68pbEB6p83dc8t" },
  { id: "9949", name: "Doodle #9949", image: "https://ipfs.io/ipfs/QmQzZWcMmymnyWM68CyfzXhgu3XDbUiuur9Vdoio2duEgL" },
  { id: "9953", name: "Doodle #9953", image: "https://ipfs.io/ipfs/QmVm8ooGiSjgnKsbS6s1Mj7VqqjqzEW7gXNsJG5xXgrGsw" },
  { id: "9983", name: "Doodle #9983", image: "https://ipfs.io/ipfs/QmYxLXdf5cKGczmnSX8WKqZYqriXRM2qTynEPaUTPmnbdT" },
  { id: "9971", name: "Doodle #9971", image: "https://ipfs.io/ipfs/QmfYZdU7ARHdMtvc3ZdGNsbSRjxnYzFmedSjVzSpoXocKp" },
  { id: "9988", name: "Doodle #9988", image: "https://ipfs.io/ipfs/Qmf6hycZ6wKYJooCus99cEg5YZvqQg4qgJHa6HjHHNsRCq" },
  { id: "9934", name: "Doodle #9934", image: "https://ipfs.io/ipfs/QmUqPGnoDTVUX9hD1o8i9dXNxCVxytUse6JcHPvjHbSwCa" },
  { id: "9939", name: "Doodle #9939", image: "https://ipfs.io/ipfs/QmRCtioTMULv4YbKxQdPnC3tYpuEWFpA37a4qMwE6ErSsd" },
  { id: "9997", name: "Doodle #9997", image: "https://ipfs.io/ipfs/QmaP2tLRsVCFCzYctYf4uRLV7HkjHmd9aKtGM1ycBoAESs" },
  { id: "9966", name: "Doodle #9966", image: "https://ipfs.io/ipfs/QmaaR5YV581bF6NBwoNmf33xmjsZi77EcJzYR4obvkFCbj" },
  { id: "9927", name: "Doodle #9927", image: "https://ipfs.io/ipfs/Qmc8GtAEV1CR3S6j3jrYqJXuMB3j8VSLycnwZDxsNj2F6G" },
  { id: "9943", name: "Doodle #9943", image: "https://ipfs.io/ipfs/QmeEUqPUdfKRBy2aos9XoyyCdmbqC8g6GLjdLRjHy3xL9P" },
  { id: "9992", name: "Doodle #9992", image: "https://ipfs.io/ipfs/QmX7ik2DR1oPMTL33fTeGamk6E7MvyCiuyGjNDSSk9z11h" },
  { id: "9959", name: "Doodle #9959", image: "https://ipfs.io/ipfs/QmUX8AdGRKmUwXfeJu6qTPjCSequYNFSY94nFL7b2pHY4Z" },
  { id: "9977", name: "Doodle #9977", image: "https://ipfs.io/ipfs/QmZrSPPp2fceZg85rcsGF4har3QTboP88KNET4g8348nTT" },
  { id: "9948", name: "Doodle #9948", image: "https://ipfs.io/ipfs/QmWLZjAD39QWafssi7tz6dRC6JGUUf8MyRrDAcgUdqkFT1" },
  { id: "9982", name: "Doodle #9982", image: "https://ipfs.io/ipfs/QmXc3RPAzyyyDvtkAdmVFqtPcFff6e9vEsnWDp2vSj5SpC" },
  { id: "9952", name: "Doodle #9952", image: "https://ipfs.io/ipfs/QmeRsN6L9yVQtn67rdEC5Q49wtCLkP2WNRuhpDQTJcjPL7" },
  { id: "9962", name: "Doodle #9962", image: "https://ipfs.io/ipfs/QmXx1s5RkBvyWN2KQdABidSbhvqgAcRevH37dEyJ8BGCmX" },
  { id: "9987", name: "Doodle #9987", image: "https://ipfs.io/ipfs/QmbPkmv6Z2M3tH77pmqvv4RcJfnYY9fAkn8ATJLSDuKtH6" },
  { id: "9970", name: "Doodle #9970", image: "https://ipfs.io/ipfs/QmRubJcP6rDiaoYBagAAKc2ZK2a6hic6iCg1rYMmv7RaVj" },
  { id: "9933", name: "Doodle #9933", image: "https://ipfs.io/ipfs/QmRFWtgUbyMEp2abzR6eoeWks65wXYFgc3xHmGBtagp9PX" },
  { id: "9938", name: "Doodle #9938", image: "https://ipfs.io/ipfs/QmY4XmsYJJhKy4F9dhRVK4Rnx5ZgW6JsMQ7XvmBE7wXrpQ" },
  { id: "9996", name: "Doodle #9996", image: "https://ipfs.io/ipfs/QmQPYE5q4M5DQfUsxSGwRSmnZgM5q2tMCoLwecAhB1WAzY" },
  { id: "9926", name: "Doodle #9926", image: "https://ipfs.io/ipfs/Qmb1kMMXXhuwwdGdxtXyGru1g2bFRCY5WhqqdXU84HA4Kg" },
  { id: "9973", name: "Doodle #9973", image: "https://ipfs.io/ipfs/QmWhWfj2w5sUPM2w6DSEqFVfFgdr2iHkNYcGFQPXUY8ZJf" },
  { id: "9963", name: "Doodle #9963", image: "https://ipfs.io/ipfs/QmXY3GUCahwWj87sq7YvC1m7njXcBBk9qCjFe7bcUnMXfe" },
  { id: "9940", name: "Doodle #9940", image: "https://ipfs.io/ipfs/QmSH5oHaMJywfxteXppKhBQi3SGbVNbJmZd3AJbw7EScCo" },
  { id: "9921", name: "Doodle #9921", image: "https://ipfs.io/ipfs/QmcdqaEWKD48A28RVLnZLuPKj8waqqn3d5E2fP34j9LwFr" },
  { id: "9956", name: "Doodle #9956", image: "https://ipfs.io/ipfs/QmfVLnTyfTLgmBE6KiB4k8Rs1EzgvFUwa7NtKMdjuLxDKX" },
  { id: "9945", name: "Doodle #9945", image: "https://ipfs.io/ipfs/QmfD2LqJ4CoL96mtSD9AJYqbFwTBEGQ4ApvoFEbKYs4Y6J" },
  { id: "9929", name: "Doodle #9929", image: "https://ipfs.io/ipfs/QmXMEyhX6qbqgPWgMAZoarjULqtfyFk9xhnEj1aM8RRCrx" },
  { id: "9955", name: "Doodle #9955", image: "https://ipfs.io/ipfs/QmRrw9ixTA9nXQLTtmwe68nGE8GBTFGnxiiVMe5gUAjGKG" },
  { id: "9920", name: "Doodle #9920", image: "https://ipfs.io/ipfs/QmWknbNRSQiCt6U8UyngmFqnopU1qTpH2BqJJ6LUoWfd9m" },
  { id: "9930", name: "Doodle #9930", image: "https://ipfs.io/ipfs/QmXv7EujxVzeRYAFoSbGiSVH1TyDtdR8GLikmGQUFK7vBT" },
  { id: "9935", name: "Doodle #9935", image: "https://ipfs.io/ipfs/Qma1vKknhLbgDqN7TtPt4jBdUK3K4zTG5MxG85gKit6nw5" },
  { id: "9919", name: "Doodle #9919", image: "https://ipfs.io/ipfs/QmPGPv6M742VTpEPKQ9CtA4LU66vUE2T1MRqvRB6MZiKmA" },
  { id: "9903", name: "Doodle #9903", image: "https://ipfs.io/ipfs/QmdyKfZnGE8d47bY7Brz6MmcQSxrvWLeobsZH1yPHCdW8f" },
  { id: "9913", name: "Doodle #9913", image: "https://ipfs.io/ipfs/QmXZ1hqjhBmr1HwEJeUy2kkMnxiCdq1zoWrp9NxLKGYCDv" },
  { id: "9880", name: "Doodle #9880", image: "https://ipfs.io/ipfs/QmZjGoF375GrDXzKTBp8WtNijxsdsSZgHTvWxrtV6D5nSJ" },
  { id: "9898", name: "Doodle #9898", image: "https://ipfs.io/ipfs/QmQBFuUpfCayCcD4JpcBC5wDQBBKxdHW8GyxvL8VStq6uT" },
  { id: "9902", name: "Doodle #9902", image: "https://ipfs.io/ipfs/QmcmUAU5nvTKHnntKEUGqcZT5p1c4ensNmRkdmNk2YFm8F" },
  { id: "9912", name: "Doodle #9912", image: "https://ipfs.io/ipfs/Qmcqb8LLLsSHg8usmwWCG4tdLEDoL8UobtSvUU3KAgnLsE" },
  { id: "9875", name: "Doodle #9875", image: "https://ipfs.io/ipfs/QmVBLCyGo3sBoRYmiF2bQ4ok4GnehcT2nyMfqFxaBG5pfy" },
  { id: "9879", name: "Doodle #9879", image: "https://ipfs.io/ipfs/QmZ7ro6L8XJDae1mrKzmTDkS7myF87dg1FoLoNY5Y4jW7BkBhy" },
  { id: "9897", name: "Doodle #9897", image: "https://ipfs.io/ipfs/QmS5cftdhq34BywEsMFxXp8nDHbaYqovoT8BARt9VQThTf" },
  { id: "9901", name: "Doodle #9901", image: "https://ipfs.io/ipfs/QmZMqAAWzt5BVtrYnq16dz3u42gvJZJEVwhtfA7qe6oekJ" },
  { id: "9911", name: "Doodle #9911", image: "https://ipfs.io/ipfs/QmQ75Ui2Nqr1vFANXVak5CqECRpBYkx6A2fMxSZ7i8ALZQ" },
  { id: "9874", name: "Doodle #9874", image: "https://ipfs.io/ipfs/QmPjRxfWSmJeCSWRgWdazn5kxJaN7J2F2Ss3Xir2BWkCPE" },
  { id: "9878", name: "Doodle #9878", image: "https://ipfs.io/ipfs/QmXszouKdo8t5md5yhFqz3WvJVXCL7rJt1VPa9ErWbqYTc" },
  { id: "9837", name: "Doodle #9837", image: "https://ipfs.io/ipfs/QmV66Qr99n4TZBjS66W1H1ynjbtyeKKNyfeQ3ZFxHPA9J7" },
  { id: "9896", name: "Doodle #9896", image: "https://ipfs.io/ipfs/QmdkfQqP3Vvp5fboGJ7Zy6fj9nXFPkzc6RviQUwFoRhQB6" },
  { id: "9900", name: "Doodle #9900", image: "https://ipfs.io/ipfs/Qma6MZ1AiYuRt4pFYACCQEpvqERpTJzzo8SH76MVwG26sY" },
  { id: "9910", name: "Doodle #9910", image: "https://ipfs.io/ipfs/QmYusKXkg6c91pFY8P41y7SC1K3M8Fuju1NmbBiuhiyX35" },
  { id: "9890", name: "Doodle #9890", image: "https://ipfs.io/ipfs/QmXxks77TTdeHu76UveQ9RGnCHZPEqjRYZJHbuYu8BkBhy" },
  { id: "9873", name: "Doodle #9873", image: "https://ipfs.io/ipfs/QmbYbrL1suDCRW2PSdckS8juEiNCv6Qz5G6cFVyGnQwgEd" }
            
               ];
        actualNftData = doodlesNfts.find(nft => nft.id.toString() === itemId);
          
          } else if (collectionId === 'milady') {
        const miladyNfts = [
      
    {id: "9998", name: "Milady #9998",image: "https://miladymaker.net/milady/9998.png" },
    {id: "9997", name: "Milady #9997",image: "https://miladymaker.net/milady/9997.png" },
     {
  id: "9995",
  name: "Milady #9995",
  image: "https://miladymaker.net/milady/9995.png"
},
{
  id: "9994",
  name: "Milady #9994",
  image: "https://miladymaker.net/milady/9994.png"
},
{
  id: "9993",
  name: "Milady #9993",
  image: "https://miladymaker.net/milady/9993.png"
},
{
  id: "9992",
  name: "Milady #9992",
  image: "https://miladymaker.net/milady/9992.png"
},
{
  id: "9991",
  name: "Milady #9991",
  image: "https://miladymaker.net/milady/9991.png"
},
{
  id: "9990",
  name: "Milady #9990",
  image: "https://miladymaker.net/milady/9990.png"
},
{
  id: "9989",
  name: "Milady #9989",
  image: "https://miladymaker.net/milady/9989.png"
},
{
  id: "9988",
  name: "Milady #9988",
  image: "https://miladymaker.net/milady/9988.png"
},
{
  id: "9987",
  name: "Milady #9987",
  image: "https://miladymaker.net/milady/9987.png"
},
{
  id: "9986",
  name: "Milady #9986",
  image: "https://miladymaker.net/milady/9986.png"
},
{
  id: "9984",
  name: "Milady #9984",
  image: "https://miladymaker.net/milady/9984.png"
},
{
  id: "9983",
  name: "Milady #9983",
  image: "https://miladymaker.net/milady/9983.png"
},
{
  id: "9982",
  name: "Milady #9982",
  image: "https://miladymaker.net/milady/9982.png"
},
{
  id: "9981",
  name: "Milady #9981",
  image: "https://miladymaker.net/milady/9981.png"
},
{
  id: "9980",
  name: "Milady #9980",
  image: "https://miladymaker.net/milady/9980.png"
},
{
  id: "9979",
  name: "Milady #9979",
  image: "https://miladymaker.net/milady/9979.png"
},
{
  id: "9978",
  name: "Milady #9978",
  image: "https://miladymaker.net/milady/9978.png"
},
{
  id: "9977",
  name: "Milady #9977",
  image: "https://miladymaker.net/milady/9977.png"
},
{
  id: "9972",
  name: "Milady #9972",
  image: "https://miladymaker.net/milady/9972.png"
},
{
  id: "9970",
  name: "Milady #9970",
  image: "https://miladymaker.net/milady/9970.png"
},
{
  id: "9974",
  name: "Milady #9974",
  image: "https://miladymaker.net/milady/9974.png"
},
{
  id: "9968",
  name: "Milady #9968",
  image: "https://miladymaker.net/milady/9968.png"
},
{
  id: "9976",
  name: "Milady #9976",
  image: "https://miladymaker.net/milady/9976.png"
},
{
  id: "9966",
  name: "Milady #9966",
  image: "https://miladymaker.net/milady/9966.png"
},
{
  id: "9971",
  name: "Milady #9971",
  image: "https://miladymaker.net/milady/9971.png"
},
{
  id: "9973",
  name: "Milady #9973",
  image: "https://miladymaker.net/milady/9973.png"
},
{
  id: "9975",
  name: "Milady #9975",
  image: "https://miladymaker.net/milady/9975.png"
},
{
  id: "9969",
  name: "Milady #9969",
  image: "https://miladymaker.net/milady/9969.png"
},
{
  id: "9965",
  name: "Milady #9965",
  image: "https://miladymaker.net/milady/9965.png"
},
{
  id: "9967",
  name: "Milady #9967",
  image: "https://miladymaker.net/milady/9967.png"
},
{
  id: "9964",
  name: "Milady #9964",
  image: "https://miladymaker.net/milady/9964.png"
},
{
  id: "9963",
  name: "Milady #9963",
  image: "https://miladymaker.net/milady/9963.png"
},
{
  id: "9962",
  name: "Milady #9962",
  image: "https://miladymaker.net/milady/9962.png"
},
{
  id: "9961",
  name: "Milady #9961",
  image: "https://miladymaker.net/milady/9961.png"
},
{
  id: "9960",
  name: "Milady #9960",
  image: "https://miladymaker.net/milady/9960.png"
},
{
  id: "9959",
  name: "Milady #9959",
  image: "https://miladymaker.net/milady/9959.png"
},
            
            {
  id: "9958",
  name: "Milady #9958",
  image: "https://miladymaker.net/milady/9958.png"
},
{
  id: "9957",
  name: "Milady #9957",
  image: "https://miladymaker.net/milady/9957.png"
},
{
  id: "9956",
  name: "Milady #9956",
  image: "https://miladymaker.net/milady/9956.png"
},
{
  id: "9955",
  name: "Milady #9955",
  image: "https://miladymaker.net/milady/9955.png"
},
{
  id: "9946",
  name: "Milady #9946",
  image: "https://miladymaker.net/milady/9946.png"
},
{
  id: "9938",
  name: "Milady #9938",
  image: "https://miladymaker.net/milady/9938.png"
},
{
  id: "9954",
  name: "Milady #9954",
  image: "https://miladymaker.net/milady/9954.png"
},
{
  id: "9945",
  name: "Milady #9945",
  image: "https://miladymaker.net/milady/9945.png"
},
{
  id: "9937",
  name: "Milady #9937",
  image: "https://miladymaker.net/milady/9937.png"
},
{
  id: "9953",
  name: "Milady #9953",
  image: "https://miladymaker.net/milady/9953.png"
},
{
  id: "9948",
  name: "Milady #9948",
  image: "https://miladymaker.net/milady/9948.png"
},
{
  id: "9942",
  name: "Milady #9942",
  image: "https://miladymaker.net/milady/9942.png"
},
{
  id: "9950",
  name: "Milady #9950",
  image: "https://miladymaker.net/milady/9950.png"
},
{
  id: "9944",
  name: "Milady #9944",
  image: "https://miladymaker.net/milady/9944.png"
},
{
  id: "9952",
  name: "Milady #9952",
  image: "https://miladymaker.net/milady/9952.png"
},
{
  id: "9940",
  name: "Milady #9940",
  image: "https://miladymaker.net/milady/9940.png"
},
{
  id: "9941",
  name: "Milady #9941",
  image: "https://miladymaker.net/milady/9941.png"
},
{
  id: "9949",
  name: "Milady #9949",
  image: "https://miladymaker.net/milady/9949.png"
},
{
  id: "9943",
  name: "Milady #9943",
  image: "https://miladymaker.net/milady/9943.png"
},
{
  id: "9951",
  name: "Milady #9951",
  image: "https://miladymaker.net/milady/9951.png"
},
{
  id: "9947",
  name: "Milady #9947",
  image: "https://miladymaker.net/milady/9947.png"
},
{
  id: "9939",
  name: "Milady #9939",
  image: "https://miladymaker.net/milady/9939.png"
},
{
  id: "9936",
  name: "Milady #9936",
  image: "https://miladymaker.net/milady/9936.png"
},
{
  id: "9935",
  name: "Milady #9935",
  image: "https://miladymaker.net/milady/9935.png"
},
{
  id: "9934",
  name: "Milady #9934",
  image: "https://miladymaker.net/milady/9934.png"
},
{
  id: "9933",
  name: "Milady #9933",
  image: "https://miladymaker.net/milady/9933.png"
},
{
  id: "9932",
  name: "Milady #9932",
  image: "https://miladymaker.net/milady/9932.png"
},
{
  id: "9931",
  name: "Milady #9931",
  image: "https://miladymaker.net/milady/9931.png"
},
{
  id: "9930",
  name: "Milady #9930",
  image: "https://miladymaker.net/milady/9930.png"
},
{
  id: "9929",
  name: "Milady #9929",
  image: "https://miladymaker.net/milady/9929.png"
},
{
  id: "9928",
  name: "Milady #9928",
  image: "https://miladymaker.net/milady/9928.png"
},
{
  id: "9927",
  name: "Milady #9927",
  image: "https://miladymaker.net/milady/9927.png"
},
{
  id: "9926",
  name: "Milady #9926",
  image: "https://miladymaker.net/milady/9926.png"
},
{
  id: "9925",
  name: "Milady #9925",
  image: "https://miladymaker.net/milady/9925.png"
},
{
  id: "9922",
  name: "Milady #9922",
  image: "https://miladymaker.net/milady/9922.png"
},
{
  id: "9924",
  name: "Milady #9924",
  image: "https://miladymaker.net/milady/9924.png"
},
{
  id: "9921",
  name: "Milady #9921",
  image: "https://miladymaker.net/milady/9921.png"
},
{
  id: "9923",
  name: "Milady #9923",
  image: "https://miladymaker.net/milady/9923.png"
},
{
  id: "9918",
  name: "Milady #9918",
  image: "https://miladymaker.net/milady/9918.png"
},
{
  id: "9914",
  name: "Milady #9914",
  image: "https://miladymaker.net/milady/9914.png"
},
{
  id: "9912",
  name: "Milady #9912",
  image: "https://miladymaker.net/milady/9912.png"
},
{
  id: "9920",
  name: "Milady #9920",
  image: "https://miladymaker.net/milady/9920.png"
},
{
  id: "9916",
  name: "Milady #9916",
  image: "https://miladymaker.net/milady/9916.png"
},
{
  id: "9911",
  name: "Milady #9911",
  image: "https://miladymaker.net/milady/9911.png"
},
{
  id: "9913",
  name: "Milady #9913",
  image: "https://miladymaker.net/milady/9913.png"
},
{
  id: "9919",
  name: "Milady #9919",
  image: "https://miladymaker.net/milady/9919.png"
},
{
  id: "9915",
  name: "Milady #9915",
  image: "https://miladymaker.net/milady/9915.png"
},
{
  id: "9910",
  name: "Milady #9910",
  image: "https://miladymaker.net/milady/9910.png"
},
{
  id: "9909",
  name: "Milady #9909",
  image: "https://miladymaker.net/milady/9909.png"
},
{
  id: "9908",
  name: "Milady #9908",
  image: "https://miladymaker.net/milady/9908.png"
},
{
  id: "9906",
  name: "Milady #9906",
  image: "https://miladymaker.net/milady/9906.png"
},
{
  id: "9907",
  name: "Milady #9907",
  image: "https://miladymaker.net/milady/9907.png"
},
{
  id: "9905",
  name: "Milady #9905",
  image: "https://miladymaker.net/milady/9905.png"
},
{
  id: "9904",
  name: "Milady #9904",
  image: "https://miladymaker.net/milady/9904.png"
},
{
  id: "9903",
  name: "Milady #9903",
  image: "https://miladymaker.net/milady/9903.png"
},
{
  id: "9902",
  name: "Milady #9902",
  image: "https://miladymaker.net/milady/9902.png"
},
{
  id: "9900",
  name: "Milady #9900",
  image: "https://miladymaker.net/milady/9900.png"
},
{
  id: "9894",
  name: "Milady #9894",
  image: "https://miladymaker.net/milady/9894.png"
},
{
  id: "9901",
  name: "Milady #9901",
  image: "https://miladymaker.net/milady/9901.png"
},
{
  id: "9898",
  name: "Milady #9898",
  image: "https://miladymaker.net/milady/9898.png"
},
{
  id: "9996",
  name: "Milady #9996",
  image: "https://miladymaker.net/milady/9996.png"
} 
           ];
        actualNftData = miladyNfts.find(nft => nft.id.toString() === itemId);

    
      
      
      } else if (collectionId === 'azuki-elementals') {
        const azukielementalsNfts = [
            
         { id: "14706", name: "Azuki Elementals #14706", image: 'https://elementals-images.azuki.com/013dda25-9504-4ff9-90fe-2dccf819d41a.png' },
          { id: "17069", name: "Azuki Elementals #17069", image: 'https://elementals-images.azuki.com/15273654-466a-4bbb-94c0-5f368aaab56c.png' },
            {
  id: "2120",
  name: "Azuki Elementals #2120",
  image: "https://elementals-images.azuki.com/545b52e7-9801-4e28-ab7c-a0a33f2111cb.png"
},
{
  id: "16671",
  name: "Azuki Elementals #16671",
  image: "https://elementals-images.azuki.com/4bffa628-dd02-4a26-b628-4ff9941db174.png"
},
{
  id: "95",
  name: "Azuki Elementals #95",
  image: "https://elementals-images.azuki.com/8d8637da-632b-4ffa-8902-030286f77a53.png"
},
{
  id: "16748",
  name: "Azuki Elementals #16748",
  image: "https://elementals-images.azuki.com/ffedc9c3-7fda-44ca-b04f-d30e0dce7ac5.png"
},
{
  id: "11407",
  name: "Azuki Elementals #11407",
  image: "https://elementals-images.azuki.com/0f02b2c7-4c5c-4916-961c-5860c5ebf57b.png"
},
{
  id: "7017",
  name: "Azuki Elementals #7017",
  image: "https://elementals-images.azuki.com/8ad73596-bb3b-4890-a233-bb3c6d1d69f8.png"
},
{
  id: "6484",
  name: "Azuki Elementals #6484",
  image: "https://elementals-images.azuki.com/0e3712fb-7e3a-41e8-afd1-ad8e355e2772.png"
},
{
  id: "9519",
  name: "Azuki Elementals #9519",
  image: "https://elementals-images.azuki.com/f884f221-c567-4675-bb2a-0fcf80783140.png"
},
{
  id: "9270",
  name: "Azuki Elementals #9270",
  image: "https://elementals-images.azuki.com/6807a4a7-731c-4544-9ef7-ce25ef866b38.png"
},
{
  id: "3838",
  name: "Azuki Elementals #3838",
  image: "https://elementals-images.azuki.com/0cb15a34-3af2-4338-ad19-985019629d34.png"
},
{
  id: "11644",
  name: "Azuki Elementals #11644",
  image: "https://elementals-images.azuki.com/5d57b1aa-d79e-4f9f-b0d5-2a3c68a685d3.png"
},
{
  id: "957",
  name: "Azuki Elementals #957",
  image: "https://elementals-images.azuki.com/f89d4127-bd68-4d0e-be06-771cfac8386f.png"
},
{
  id: "19378",
  name: "Azuki Elementals #19378",
  image: "https://elementals-images.azuki.com/6cceea68-38a5-48da-9e5f-3560dfd1c115.png"
},
{
  id: "15690",
  name: "Azuki Elementals #15690",
  image: "https://elementals-images.azuki.com/d3dd34dc-40b5-4b39-a90c-74592bcd247f.png"
},
{
  id: "5797",
  name: "Azuki Elementals #5797",
  image: "https://elementals-images.azuki.com/1320fc68-4410-440f-92d2-2811ab143008.png"
},
{
  id: "11488",
  name: "Azuki Elementals #11488",
  image: "https://elementals-images.azuki.com/a7ce49a0-7cef-455a-a4a3-483bde990934.png"
},
{
  id: "8558",
  name: "Azuki Elementals #8558",
  image: "https://elementals-images.azuki.com/bdc93813-2744-4eee-8d30-8c3657284a7d.png"
},
{
  id: "17803",
  name: "Azuki Elementals #17803",
  image: "https://elementals-images.azuki.com/e1f40ba5-58f0-40ff-aaba-93fbf1643410.png"
},
{
  id: "4072",
  name: "Azuki Elementals #4072",
  image: "https://elementals-images.azuki.com/d95971f0-c70a-4b47-b296-b89a0090acb7.png"
},
{
  id: "4225",
  name: "Azuki Elementals #4225",
  image: "https://elementals-images.azuki.com/9d0ca596-c97b-4891-84b3-807a10f92750.png"
},
{
  id: "15306",
  name: "Azuki Elementals #15306",
  image: "https://elementals-images.azuki.com/bceca2b6-6462-4342-a012-30b4604a3002.png"
},
{
  id: "9839",
  name: "Azuki Elementals #9839",
  image: "https://elementals-images.azuki.com/fd0ce8af-71a6-4d1c-8b6b-5f9447eab15d.png"
},
{
  id: "19294",
  name: "Azuki Elementals #19294",
  image: "https://elementals-images.azuki.com/0304b973-266b-43cc-8e83-1e5523ffd9de.png"
},
{
  id: "19977",
  name: "Azuki Elementals #19977",
  image: "https://elementals-images.azuki.com/8082750f-515d-4ddf-b81f-98e2ddcd8b07.png"
},
{
  id: "7289",
  name: "Azuki Elementals #7289",
  image: "https://elementals-images.azuki.com/0622b45f-f7ea-4c80-884d-bf4fa13aff49.png"
},
{
  id: "16401",
  name: "Azuki Elementals #16401",
  image: "https://elementals-images.azuki.com/895f81f3-fb2b-4065-9c6f-2102cb6370de.png"
},
{
  id: "12745",
  name: "Azuki Elementals #12745",
  image: "https://elementals-images.azuki.com/073cc228-5a02-4297-82a2-324990a4a2ca.png"
},
{
  id: "8644",
  name: "Azuki Elementals #8644",
  image: "https://elementals-images.azuki.com/4cfb614d-0c44-45df-bd87-0d7ed0fe4836.png"
},
            {
  id: "5676",
  name: "Azuki Elementals #5676",
  image: "https://elementals-images.azuki.com/5f456fb0-b7e7-42ed-b5f0-48f9d1e90b42.png"
},
{
  id: "11718",
  name: "Azuki Elementals #11718",
  image: "https://elementals-images.azuki.com/c3b3069e-0e29-4912-b677-43796c56a3b4.png"
},
{
  id: "16094",
  name: "Azuki Elementals #16094",
  image: "https://elementals-images.azuki.com/3ef290ed-afcd-4e44-a004-9273e6fb6ff6.png"
},
{
  id: "4714",
  name: "Azuki Elementals #4714",
  image: "https://elementals-images.azuki.com/647868ae-54e7-42c7-8b15-7d7b9b5be4d6.png"
},
{
  id: "12604",
  name: "Azuki Elementals #12604",
  image: "https://elementals-images.azuki.com/b5c37951-7d31-4c40-b671-3d05d47c6842.png"
},
{
  id: "16543",
  name: "Azuki Elementals #16543",
  image: "https://elementals-images.azuki.com/a4f32b10-c6d0-4aaf-9793-60d2c43db7ea.png"
},
{
  id: "7006",
  name: "Azuki Elementals #7006",
  image: "https://elementals-images.azuki.com/24fdb1a9-8c97-4720-a75d-7d452c34d2bf.png"
},
{
  id: "4705",
  name: "Azuki Elementals #4705",
  image: "https://elementals-images.azuki.com/f208543f-1be3-4e1a-86bd-ca78d4b6ae2f.png"
},
{
  id: "6417",
  name: "Azuki Elementals #6417",
  image: "https://elementals-images.azuki.com/ea13169f-15de-4e61-aa67-f8d5c3bcc4cc.png"
},
{
  id: "9535",
  name: "Azuki Elementals #9535",
  image: "https://elementals-images.azuki.com/e56621ed-2c5f-431b-a690-0c722a06050c.png"
},
{
  id: "320",
  name: "Azuki Elementals #320",
  image: "https://elementals-images.azuki.com/4922a76f-48b4-478d-a141-4a85bc96bd90.png"
},
{
  id: "742",
  name: "Azuki Elementals #742",
  image: "https://elementals-images.azuki.com/de01f797-3587-446c-a3a7-915632b90edf.png"
},
{
  id: "19449",
  name: "Azuki Elementals #19449",
  image: "https://elementals-images.azuki.com/633848ee-66a0-4819-93d7-a23bb6a7c2a3.png"
},
{
  id: "11322",
  name: "Azuki Elementals #11322",
  image: "https://elementals-images.azuki.com/84baa0bb-0154-4fa4-aefd-d86f7f917dc8.png"
},
{
  id: "16106",
  name: "Azuki Elementals #16106",
  image: "https://elementals-images.azuki.com/b57cca1f-5a6b-41c8-a960-17bcc0d1fa21.png"
},
{
  id: "15215",
  name: "Azuki Elementals #15215",
  image: "https://elementals-images.azuki.com/0bb61c68-d59f-4273-ae48-c2ab82ef5fce.png"
},
{
  id: "8918",
  name: "Azuki Elementals #8918",
  image: "https://elementals-images.azuki.com/678379ff-7b54-4d4c-a730-862d448d8516.png"
},
{
  id: "16946",
  name: "Azuki Elementals #16946",
  image: "https://elementals-images.azuki.com/c9394ab3-aa12-4df1-9d4d-e1c2b7c7518f.png"
},
{
  id: "16170",
  name: "Azuki Elementals #16170",
  image: "https://elementals-images.azuki.com/77529fdb-c9ff-47e1-a927-4e4ce5bb5ecc.png"
},
{
  id: "9379",
  name: "Azuki Elementals #9379",
  image: "https://elementals-images.azuki.com/212aaee9-c17b-4d4c-9f64-ebd54a3d7a76.png"
},
{
  id: "6459",
  name: "Azuki Elementals #6459",
  image: "https://elementals-images.azuki.com/dc001314-46ba-4942-af67-c01736b8c404.png"
},
{
  id: "17621",
  name: "Azuki Elementals #17621",
  image: "https://elementals-images.azuki.com/43fc1dff-8738-4a90-b131-ae1602e72e53.png"
},
{
  id: "6346",
  name: "Azuki Elementals #6346",
  image: "https://elementals-images.azuki.com/dcf00193-8777-42ec-9fa5-e3a0f7cf56fa.png"
},
{
  id: "5878",
  name: "Azuki Elementals #5878",
  image: "https://elementals-images.azuki.com/66cc917c-ef4b-4912-bb40-2a02c6defc88.png"
},
{
  id: "18373",
  name: "Azuki Elementals #18373",
  image: "https://elementals-images.azuki.com/f5a2f95a-8fc0-48c9-9de4-30fd39d49a4f.png"
},
{
  id: "9517",
  name: "Azuki Elementals #9517",
  image: "https://elementals-images.azuki.com/d59bfebf-fed6-4900-8037-5dbd2121b332.png"
},
{
  id: "15202",
  name: "Azuki Elementals #15202",
  image: "https://elementals-images.azuki.com/f149678e-046c-4590-85e4-42c949885a91.png"
},
{
  id: "13688",
  name: "Azuki Elementals #13688",
  image: "https://elementals-images.azuki.com/ec31dcab-dd7e-4549-87e9-853674b33097.png"
},
{
  id: "4615",
  name: "Azuki Elementals #4615",
  image: "https://elementals-images.azuki.com/6237bbb3-6c06-43be-865c-7865c630730b.png"
},
{
  id: "3751",
  name: "Azuki Elementals #3751",
  image: "https://elementals-images.azuki.com/7d263292-dd5f-4c09-9fce-67394448efa1.png"
},
{
  id: "15673",
  name: "Azuki Elementals #15673",
  image: "https://elementals-images.azuki.com/934bcf77-8c54-4218-b58f-fbe4b2ab350a.png"
},
{
  id: "6956",
  name: "Azuki Elementals #6956",
  image: "https://elementals-images.azuki.com/408e71d8-a3a4-4045-b4a3-431231dd332a.png"
},
{
  id: "19548",
  name: "Azuki Elementals #19548",
  image: "https://elementals-images.azuki.com/97ddf9d5-af38-454d-9b10-27bb190f6b1d.png"
},
{
  id: "13560",
  name: "Azuki Elementals #13560",
  image: "https://elementals-images.azuki.com/6895a108-30ad-4fb5-a800-e3e0b2ce75a3.png"
},
{
  id: "16219",
  name: "Azuki Elementals #16219",
  image: "https://elementals-images.azuki.com/dc881e6a-1d59-464f-a841-536dbe38a8da.png"
}
            
          ];
        actualNftData = azukielementalsNfts.find(nft => nft.id.toString() === itemId);
        
      
      
       } else if (collectionId === 'mutant-ape-yacht-club') {
        const mutantApeYachtClubNfts = [
            
            {
  id: "25002",
  name: "MAYC #25002",
  image: "https://ipfs.io/ipfs/QmQhaRndyZWUqSam6JKFDKvDvAV3bnWncMVmRiZQsZGH4r"
},
{
  id: "25897",
  name: "MAYC #25897",
  image: "https://ipfs.io/ipfs/QmaZUve2DAaGtqyZXyyMHezqfLpN3jutUbL7nQ3yFZjh3r"
},
{
  id: "19446",
  name: "MAYC #19446",
  image: "https://ipfs.io/ipfs/QmQbsCD5qtqAyEoo4nSF9XGXZoU1uWTEcYb7FnZY7ojDqk"
},
{
  id: "21444",
  name: "MAYC #21444",
  image: "https://ipfs.io/ipfs/QmcaGhYt6jLY61Jk8jb4vaEChVT8gYbS8MkxqDMmHCHogx"
},
{
  id: "19276",
  name: "MAYC #19276",
  image: "https://ipfs.io/ipfs/QmYKeRWJ1Do4nr8YR1yfPHgckdUBRcPuXfCfz34UtCkkiR"
},
{
  id: "24720",
  name: "MAYC #24720",
  image: "https://ipfs.io/ipfs/QmbH21oPTfH7cHVocFAEMDh88LcEYmYuu6zC4D78hPuhGF"
},
{
  id: "23674",
  name: "MAYC #23674",
  image: "https://ipfs.io/ipfs/QmPPxue9QNAFVX9CGKVENQSzvBA2d2aRv5vmLpLafyo14y"
},
{
  id: "26344",
  name: "MAYC #26344",
  image: "https://ipfs.io/ipfs/QmccLPYCfjDuHjt2KjqowqLh9PGHe7KZm44FygBA3UAz18"
},
{
  id: "13460",
  name: "MAYC #13460",
  image: "https://ipfs.io/ipfs/QmdPkDRtwCjowmzp7FYNE7uFvYnCZBPW7oMFHGuDfJwfyB"
},
{
  id: "23176",
  name: "MAYC #23176",
  image: "https://ipfs.io/ipfs/QmQMfji3aKLsBEfCwCWFax6DLMpJnKM2dmsgJbLn2ApHnd"
},
{
  id: "15805",
  name: "MAYC #15805",
  image: "https://ipfs.io/ipfs/QmcPWNFeXD7gzioT1ZDL45ZsJMGHTPuW51gQqBbkopaGzt"
},
{
  id: "15804",
  name: "MAYC #15804",
  image: "https://ipfs.io/ipfs/QmcDDTgogJwU2SwoF65ZaMxgSrGM5A96h5WC4bpGLJ5TkU"
},
{
  id: "25765",
  name: "MAYC #25765",
  image: "https://ipfs.io/ipfs/QmbHtHRKKF6G1bdebrjYUZxsueeEX3u3DZBLw5SEccJTxv"
},
{
  id: "13122",
  name: "MAYC #13122",
  image: "https://ipfs.io/ipfs/Qmbuh8sqDMPXxZidsKecazkaVgBoQGxMfK14nJQHgmNDBJ"
},
{
  id: "26654",
  name: "MAYC #26654",
  image: "https://ipfs.io/ipfs/Qmbqos6hRScHb77CWGurZqDE8QL6tqbkx4YfRAe2Fo3SB6"
},
{
  id: "26386",
  name: "MAYC #26386",
  image: "https://ipfs.io/ipfs/Qmdq6Y5uhNACPQv7Sb1X4KmhupryA85ZVw6u5MbV9JvjhQ"
},
{
  id: "20274",
  name: "MAYC #20274",
  image: "https://ipfs.io/ipfs/QmdXYLFb6rRCTR7YG7f2JCeUt5kQCBicU1GpMRSF6QZbsF"
},
{
  id: "29406",
  name: "MAYC #29406",
  image: "https://ipfs.io/ipfs/QmcUi3kkpvYjZu8j1PEJ2Y1eYJnno4zi3RJHNEnHR1ux6o"
},
{
  id: "24738",
  name: "MAYC #24738",
  image: "https://ipfs.io/ipfs/QmTyvfkbEgDBwTThouaUjzo5Uu4CZSCtpPaqXCr9Wuibgo"
},
{
  id: "23198",
  name: "MAYC #23198",
  image: "https://ipfs.io/ipfs/QmfFd5pTb1y8Vgub3JmdVPzxRFPqiWYM2x3WHuhXWaZGfv"
},
{
  id: "22050",
  name: "MAYC #22050",
  image: "https://ipfs.io/ipfs/QmdtczMHZJAupTDU8BZH2rGfn5WLgSfj5UM3eXVJGy51Da"
},
{
  id: "20187",
  name: "MAYC #20187",
  image: "https://ipfs.io/ipfs/QmfC43Pc6Ey1wnktbQtaYqUikDnMXaKuNQcuy9sHwru7MY"
},
{
  id: "27330",
  name: "MAYC #27330",
  image: "https://ipfs.io/ipfs/QmZYE1GHkYa3fswcyKqT5gr7XxnpUxUCXtLKEN17ymrWRZ"
},
{
  id: "29628",
  name: "MAYC #29628",
  image: "https://ipfs.io/ipfs/QmP9iz8TF6VPgHG1Kg7bNqPCTZgWVUzF5nyvynVtF5kwBM"
},
{
  id: "14164",
  name: "MAYC #14164",
  image: "https://ipfs.io/ipfs/QmbE4wRFjPbpp6JUGfJFrR5NErkj8PxNaSPaUVQMQB4qcN"
},
{
  id: "22148",
  name: "MAYC #22148",
  image: "https://ipfs.io/ipfs/QmNVusrY233w9ACCdhjxA8L5FUTBmCTipn241qgCU3nteJ"
},
{
  id: "22394",
  name: "MAYC #22394",
  image: "https://ipfs.io/ipfs/QmYZvoe2RMyaPBewX7bdUX3itzujapXZqPsqvdrBaifYWs"
},
{
  id: "16802",
  name: "MAYC #16802",
  image: "https://ipfs.io/ipfs/QmYQ4ZCzU5pLdLvBrdFMpk3RLavH4YD9D6xs9JxZYcze3i"
},
{
  id: "14926",
  name: "MAYC #14926",
  image: "https://ipfs.io/ipfs/QmWqveELXrJoj3kTgf4NBJVUFLTsFnz3b3jPgAxoF1wqvz"
},
{
  id: "12964",
  name: "MAYC #12964",
  image: "https://ipfs.io/ipfs/QmVQuPJaAZa4dHxYgmv267Wqe4f7ewxEdnHdyX8inE9USE"
},
{
  id: "25240",
  name: "MAYC #25240",
  image: "https://ipfs.io/ipfs/QmTkWAEVt1fU8NtWjNhJycUhb84xqdQNjLbdkjujzoZ232"
},
{
  id: "29318",
  name: "MAYC #29318",
  image: "https://ipfs.io/ipfs/QmfKW9jUevraTTWVGmv65VCYEr135z8qhp8ECSASBnG7XK"
},
{
  id: "23008",
  name: "MAYC #23008",
  image: "https://ipfs.io/ipfs/QmRzKjTG9eNe4tMfupB4KS2jyeekY6wdi8tNSdfvD65DxR"
},
{
  id: "11822",
  name: "MAYC #11822",
  image: "https://ipfs.io/ipfs/QmesyD48u5WTN8yiM97rABMQPBdaXGfrncHytT77X2xfKt"
},
{
  id: "16195",
  name: "MAYC #16195",
  image: "https://ipfs.io/ipfs/QmU4JHiNXarKgnbKPbDBwbuyzKBHtfK5Rx7VtJdgxdhoFn"
},
{
  id: "25528",
  name: "MAYC #25528",
  image: "https://ipfs.io/ipfs/QmSvbCZ8dLWJTwxsvikQqL2GefJXUHJcnF4XV4wKgEy4oo"
},
{
  id: "11118",
  name: "MAYC #11118",
  image: "https://ipfs.io/ipfs/Qmba3NfKAChVGzNxynB913ArP6D5desSASPv4Pjf1ge1pL"
},
{
  id: "12136",
  name: "MAYC #12136",
  image: "https://ipfs.io/ipfs/QmdXjv6tsZAMku3zPW9eVX93wBDFenF5PQ2BM2LPboyJ9j"
},
{
  id: "15917",
  name: "MAYC #15917",
  image: "https://ipfs.io/ipfs/QmSgPJHkvGRkpM9oh2BwY9ArtBfcgy8a8xwmYvEMoXzP8t"
},
{
  id: "22821",
  name: "MAYC #22821",
  image: "https://ipfs.io/ipfs/QmQbUE9FP4DdhcgYti2DSHNy3PvW2eMZxiSZL9k6MAkKiu"
},
{
  id: "22820",
  name: "MAYC #22820",
  image: "https://ipfs.io/ipfs/QmcJH3HosMXqN8M1ZkBTZ4ADRREeZGqmHoXpGNEFAkoUyu"
},
{
  id: "18882",
  name: "MAYC #18882",
  image: "https://ipfs.io/ipfs/QmXYN1Ctz2ZNF3MKixjt9aiTR2ooZC8dbo798GG8vY67Js"
},
{
  id: "19006",
  name: "MAYC #19006",
  image: "https://ipfs.io/ipfs/QmXeKFWRX6C9s9nGncxUiYASYRC9i4z9CRidZkTfkQQJ1T"
},
{
  id: "25484",
  name: "MAYC #25484",
  image: "https://ipfs.io/ipfs/QmT8AwCLMk9AUDsRQEVeNp4akXiEToFAwDKSWBrt3XfSaX"
},
{
  id: "12896",
  name: "MAYC #12896",
  image: "https://ipfs.io/ipfs/QmXKMePr3BwkNRLiLVXG6ArTNx1qb54XrZi3SpMmzi37Sx"
},
{
  id: "14149",
  name: "MAYC #14149",
  image: "https://ipfs.io/ipfs/QmTusjZUFg3K7ouhMsJB1jtZaovGWZyyjF3N33sMqPWRiW"
},
{
  id: "11292",
  name: "MAYC #11292",
  image: "https://ipfs.io/ipfs/QmdEt1T9mWaDS1VXrVxZkgXLwaYnQHw1C8EWs2DqEW8Cm4"
},
{
  id: "11760",
  name: "MAYC #11760",
  image: "https://ipfs.io/ipfs/QmV2ZnnL6d3Fe6kttPtRuDvyPh6nAs3q8uU3qFoVxHDUB7"
},
{
  id: "16507",
  name: "MAYC #16507",
  image: "https://ipfs.io/ipfs/QmXeX6C91VnpWZ7qKiLDSjmqqGgQgsLv2JhszvykYz8wVZ"
},
{
  id: "26204",
  name: "MAYC #26204",
  image: "https://ipfs.io/ipfs/QmR8VV7z52jroDWAtiJk5RSXPohKUwUY7xbY8MjkCVrTb3"
},
{
  id: "11881",
  name: "MAYC #11881",
  image: "https://ipfs.io/ipfs/QmNr4FpN1Akf3BMk9Xo5weBLnEM6SLHV5eoCYaBasfxogF"
},
            
 {
  id: "29550",
  name: "MAYC #29550",
  image: "https://ipfs.io/ipfs/QmPtmm8Ex916LXMv1exEgMuxGBUaiz5k82zvoHWHS9EfLL"
},
{
  id: "16420",
  name: "MAYC #16420",
  image: "https://ipfs.io/ipfs/QmfU89wwK4XE3PmL591g3epKDFtEuUCLfw8wX1djGcFuRH"
},
{
  id: "27144",
  name: "MAYC #27144",
  image: "https://ipfs.io/ipfs/QmVfhYr2tu29xFPMrFfZZiCmfPVbSfh21oS5aR9CRMhar2"
},
{
  id: "28009",
  name: "MAYC #28009",
  image: "https://ipfs.io/ipfs/QmWJ9dHayfXyGoHGmBZGpRtXapA2MxEFNzoQp4TBwv7tdg"
},
{
  id: "12254",
  name: "MAYC #12254",
  image: "https://ipfs.io/ipfs/QmVz565Ahpsi8HHWqQo8zk7SRMA9p8n9eZfA4VkcdzNzxg"
},
{
  id: "20488",
  name: "MAYC #20488",
  image: "https://ipfs.io/ipfs/QmRwMLGzXSR1j4pqqzfnXsjjiAAxnwREhV2UtLVvZK7WSE"
},
{
  id: "16506",
  name: "MAYC #16506",
  image: "https://ipfs.io/ipfs/QmUU4WztddW4nqppQmzTTgw998Khf6KYkyFf7BFiCW8gBq"
},
{
  id: "20776",
  name: "MAYC #20776",
  image: "https://ipfs.io/ipfs/QmfZpQyrM8cbyJBtDz2HVUbvaYXzQT9ayC7MpzVr8MZNb9"
},
{
  id: "16340",
  name: "MAYC #16340",
  image: "https://ipfs.io/ipfs/QmWPeMEU6Ajr5fx3SXipYvGpJKAobAnSeUtxMR2RU17ca8"
},
{
  id: "25447",
  name: "MAYC #25447",
  image: "https://ipfs.io/ipfs/QmV46udn29rLXD6Tk6Ucgqp3gpAvfJwqYLC7tcWhNSqGTS"
},
{
  id: "19356",
  name: "MAYC #19356",
  image: "https://ipfs.io/ipfs/QmRh4yYDwn8YwLryaFzBvsFxoBeg81kKhWZYUPh6H6BY3q"
},
{
  id: "27910",
  name: "MAYC #27910",
  image: "https://ipfs.io/ipfs/QmVj7er326is4gJVW2PFEdn7XPWw8cezmQZgJFFqDWLMXT"
},
{
  id: "19984",
  name: "MAYC #19984",
  image: "https://ipfs.io/ipfs/QmTJL6qNFV6ibnmgstSkA3zvDc4uUzWcKBDbfFtGTyiEf5"
},
{
  id: "17025",
  name: "MAYC #17025",
  image: "https://ipfs.io/ipfs/QmXgAuVxUjDFAzNrRiSWQGZHvhDUojSKDELvruLJ76pasB"
},
{
  id: "28702",
  name: "MAYC #28702",
  image: "https://ipfs.io/ipfs/QmTtBYnmesiUJGHkAvrCQXt8BzgUBEEUUxreQEs8uRbpZa"
},
{
  id: "21218",
  name: "MAYC #21218",
  image: "https://ipfs.io/ipfs/QmTtt7xUeFtBBYgZrfbaCfwYzFmgDSgG8ewcnFGdpipxmt"
},
{
  id: "22370",
  name: "MAYC #22370",
  image: "https://ipfs.io/ipfs/QmREP4D7ek4ZvMiy4K2A1eFG1Vw1b6e1KL2UoajfWae3DN"
},
{
  id: "17008",
  name: "MAYC #17008",
  image: "https://ipfs.io/ipfs/QmUFVt73RRpmXSjx5Yof12KrjMHdET2Ee46AxXuRZnJ6h6"
},
{
  id: "18583",
  name: "MAYC #18583",
  image: "https://ipfs.io/ipfs/Qmcy3NTn74YDc1yTjmNbP1rUfS4Fywfd7DWDTmt9TcFb74"
},
{
  id: "23344",
  name: "MAYC #23344",
  image: "https://ipfs.io/ipfs/QmZN1cvGHTgpePLfzqVbMStDnYzYvKSHZekNcXyDJvfpjU"
},
{
  id: "12915",
  name: "MAYC #12915",
  image: "https://ipfs.io/ipfs/QmYCz72oxxSYMCuFXoheiFaGYfRJEcAUG5B1mXVLTyB2c1"
},
{
  id: "11232",
  name: "MAYC #11232",
  image: "https://ipfs.io/ipfs/QmdqYc1UxGMTRGLYYXqEQpLdsaXBow7PVCNbBEjPRvg5VX"
},
{
  id: "25678",
  name: "MAYC #25678",
  image: "https://ipfs.io/ipfs/QmS6vuXcU39igVHa9VJthrjbWVGejWqy8gkpxpSkKicm2s"
},
{
  id: "11566",
  name: "MAYC #11566",
  image: "https://ipfs.io/ipfs/QmRTTXzoVQsQYsut4EqtNUXmySZstbyYntGuahFBw3u1Wn"
},
{
  id: "12914",
  name: "MAYC #12914",
  image: "https://ipfs.io/ipfs/QmPTbzUBiJFkbbvdXhjnC4XBRLjaFuKAc47qRPiHM4Jg8U"
},
{
  id: "20464",
  name: "MAYC #20464",
  image: "https://ipfs.io/ipfs/QmeEyFq7D4XHT5cGArGyn8ku2edVV3Nbh65hQHpWaspMUm"
},
{
  id: "12714",
  name: "MAYC #12714",
  image: "https://ipfs.io/ipfs/Qme8DBPjzSgoJnpMFn1EWgYPuxGevPTvT7vMitizHVzTrB"
},
{
  id: "27016",
  name: "MAYC #27016",
  image: "https://ipfs.io/ipfs/Qmd2dh9U5Gdzu4FqpoBgLYKSreHpUuwvgYt6PS9CMME5np"
},
{
  id: "23442",
  name: "MAYC #23442",
  image: "https://ipfs.io/ipfs/QmYY2hDCiydAaLZjeef9AqZaqNr1GuJkMK1RJmNsi8AJME"
},
{
  id: "13068",
  name: "MAYC #13068",
  image: "https://ipfs.io/ipfs/QmNNgiwBph3W84T99zwNNEi4daubgffksAdgxeGR2qG7ej"
},
{
  id: "24189",
  name: "MAYC #24189",
  image: "https://ipfs.io/ipfs/QmVxkzRqsU4YeRWUXdpcaioFFKu8qjHWtdC7S4QcybCUcA"
},
{
  id: "24188",
  name: "MAYC #24188",
  image: "https://ipfs.io/ipfs/QmbsBK6VcMmRj7mSJkVmU57QHJ44q3MQ5JPfuuz4gGCeaX"
},
{
  id: "14235",
  name: "MAYC #14235",
  image: "https://ipfs.io/ipfs/QmaorVrCSu5VmUcuDFMT6GwqcTdqLvWLgu7NghmkWELmvp"
},
{
  id: "25438",
  name: "MAYC #25438",
  image: "https://ipfs.io/ipfs/QmQ7URuFKUsiqJLtVS2eMDqopDWXLRLB3GCrxNRg22mfuG"
},
{
  id: "15529",
  name: "MAYC #15529",
  image: "https://ipfs.io/ipfs/QmQyDyLLRPPVFCcwfKHZFeuHDysHW5a1JxYevX1aBn9dAK"
},
{
  id: "20174",
  name: "MAYC #20174",
  image: "https://ipfs.io/ipfs/QmZsJDtNYXD5VQshkZwX9DiNifPWYtyexvoo8fesXnkQrS"
},
{
  id: "21791",
  name: "MAYC #21791",
  image: "https://ipfs.io/ipfs/QmZBvc3dXciAJURPs4tGhBgdBdAy4wkM33VMQA1r9XzEpv"
},
{
  id: "21790",
  name: "MAYC #21790",
  image: "https://ipfs.io/ipfs/QmY9hqRK8dGxssezKnHGrgpExuFbQrqq2AnWYt7rGREdRj"
},
{
  id: "16324",
  name: "MAYC #16324",
  image: "https://ipfs.io/ipfs/QmVrKYamVb7vxuyULL2Q1d9eueKTxW5cNK2izLBE277z1r"
},
{
  id: "20668",
  name: "MAYC #20668",
  image: "https://ipfs.io/ipfs/QmUt5i25JZcKv5PUmLNG8KsuVPnntrH1DFSD2okv4Bdqr4"
},
{
  id: "26803",
  name: "MAYC #26803",
  image: "https://ipfs.io/ipfs/QmaFzY2NaDuYkqQhHLD7P4qwJ54W7Q9xnxyKR64n3npVRD"
},
{
  id: "14120",
  name: "MAYC #14120",
  image: "https://ipfs.io/ipfs/QmbuypGvnJVs6CUu91zc3qDoW6wLxate7axQJXKFUH4EJo"
},
{
  id: "13211",
  name: "MAYC #13211",
  image: "https://ipfs.io/ipfs/QmeuGFD8QFKmWySghQKGA2DLNh3mdhW45tkQCpHWHGD2cs"
},
{
  id: "13210",
  name: "MAYC #13210",
  image: "https://ipfs.io/ipfs/Qmb3xMsuWSWUGKg9iS8Fu9HEodaJuXEuxFUJPeirCCQg6p"
},
{
  id: "11517",
  name: "MAYC #11517",
  image: "https://ipfs.io/ipfs/QmXr46GufXoYT6iw5oTA1XWRKX63CpqRi3DsMtFJSqT1rh"
},
{
  id: "29350",
  name: "MAYC #29350",
  image: "https://ipfs.io/ipfs/QmYUtcMxiaqn1kSdyKdLmM9cebaLoY57F22Cd9TWTMpVL4"
},
{
  id: "24854",
  name: "MAYC #24854",
  image: "https://ipfs.io/ipfs/QmbPUThoCdch5rGhHVHFrtEBYqSUhANHjDXR9RuqHrvp9X"
},
{
  id: "26014",
  name: "MAYC #26014",
  image: "https://ipfs.io/ipfs/QmUp2wSUEqW9wnsm7LLZPXuGkTyj1eaLbPwcyknubFdiPC"
},
{
  id: "18051",
  name: "MAYC #18051",
  image: "https://ipfs.io/ipfs/QmdGf9vHjwduDU2UziErVd4mVWzoxUP1hEG85rWSkRCUAc"
}
            
             ];
        actualNftData = mutantApeYachtClubNfts.find(nft => nft.id.toString() === itemId);
           
       } else if (collectionId === 'kaito-genesis') {
        const kaitogenesisNfts = [
            
{
    id: "1789",
    name: "kaito genesis #1789",
    image: "https://i.seadn.io/s/raw/files/8a0d280b8d33fef2cf20b9d1080a1adc.png?auto=format&dpr=1&w=640",
    price: "9.63 ETH"
  },
            {
    id: "2081",
    name: "kaito genesis #2081",
    image: "https://i.seadn.io/s/raw/files/8a0d280b8d33fef2cf20b9d1080a1adc.png?auto=format&dpr=1&w=640",
    price: "9.77 ETH"
  },
             {
    id: "658",
    name: "kaito genesis #658",
    image: "https://i.seadn.io/s/raw/files/8a0d280b8d33fef2cf20b9d1080a1adc.png?auto=format&dpr=1&w=640",
    price: "9.77 ETH"
  }
            
            ];
        actualNftData = kaitogenesisNfts.find(nft => nft.id.toString() === itemId);
       
           } else if (collectionId === 'beanz') {
        const beanzNfts = [
            
            {
  id: "19949",
  name: "BEANZ #19949",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19949.png"
},
{
  id: "19948",
  name: "BEANZ #19948",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19948.png"
},
{
  id: "19947",
  name: "BEANZ #19947",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19947.png"
},
{
  id: "19946",
  name: "BEANZ #19946",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19946.png"
},
{
  id: "19945",
  name: "BEANZ #19945",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19945.png"
},
{
  id: "19944",
  name: "BEANZ #19944",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19944.png"
},
{
  id: "19943",
  name: "BEANZ #19943",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19943.png"
},
{
  id: "19942",
  name: "BEANZ #19942",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19942.png"
},
{
  id: "19941",
  name: "BEANZ #19941",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19941.png"
},
{
  id: "19940",
  name: "BEANZ #19940",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19940.png"
},
{
  id: "19939",
  name: "BEANZ #19939",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19939.png"
},
{
  id: "19938",
  name: "BEANZ #19938",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19938.png"
},
{
  id: "19937",
  name: "BEANZ #19937",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19937.png"
},
{
  id: "19936",
  name: "BEANZ #19936",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19936.png"
},
{
  id: "19935",
  name: "BEANZ #19935",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19935.png"
},
{
  id: "19934",
  name: "BEANZ #19934",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19934.png"
},
{
  id: "19933",
  name: "BEANZ #19933",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19933.png"
},
{
  id: "19932",
  name: "BEANZ #19932",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19932.png"
},
{
  id: "19931",
  name: "BEANZ #19931",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19931.png"
},
{
  id: "19930",
  name: "BEANZ #19930",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19930.png"
},
{
  id: "19929",
  name: "BEANZ #19929",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19929.png"
},
{
  id: "19928",
  name: "BEANZ #19928",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19928.png"
},
{
  id: "19927",
  name: "BEANZ #19927",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19927.png"
},
{
  id: "19926",
  name: "BEANZ #19926",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19926.png"
},
{
  id: "19925",
  name: "BEANZ #19925",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19925.png"
},
{
  id: "19924",
  name: "BEANZ #19924",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19924.png"
},
{
  id: "19923",
  name: "BEANZ #19923",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19923.png"
},
{
  id: "19922",
  name: "BEANZ #19922",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19922.png"
},
{
  id: "19921",
  name: "BEANZ #19921",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19921.png"
},
{
  id: "19920",
  name: "BEANZ #19920",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19920.png"
},
{
  id: "19919",
  name: "BEANZ #19919",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19919.png"
},
{
  id: "19918",
  name: "BEANZ #19918",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19918.png"
},
{
  id: "19917",
  name: "BEANZ #19917",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19917.png"
},
{
  id: "19916",
  name: "BEANZ #19916",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19916.png"
},
{
  id: "19915",
  name: "BEANZ #19915",
  image: "https://ipfs.io/ipfs/QmTRuWHr7bpqscUWFmhXndzf5AdQqkekhqwgbyJCqKMHrL/19915.png"
}
            
             ];
        actualNftData = beanzNfts.find(nft => nft.id.toString() === itemId);
            
           } else if (collectionId === 'boredapeyachtclub') {
        const boredapeyachtclubNfts = [
            
         {
  id: "9974",
  name: "BAYC #9974",
  image: "https://ipfs.io/ipfs/QmXHu7pFhp5sUJi1aajkmsgcFBuSK6y93mWz5czvY8A8hA"
},
{
  id: "9973",
  name: "BAYC #9973",
  image: "https://ipfs.io/ipfs/QmNs7bSj8NyfHED8YjgFtQdvDKVhh72YDhDaHHMpcSkwfH"
},
{
  id: "9949",
  name: "BAYC #9949",
  image: "https://ipfs.io/ipfs/QmVb1n7vGzuVcGDXojSu6vRmi9wgszz8i5CvFud8AkngCF"
},
{
  id: "9972",
  name: "BAYC #9972",
  image: "https://ipfs.io/ipfs/QmSGmMwXdBXhdot38pSNg83myPjBNeDxF4vHkvLznm9y2Z"
},
{
  id: "9948",
  name: "BAYC #9948",
  image: "https://ipfs.io/ipfs/QmUeAcuhFXkcXL6FVxAeTY4MguNuc282kpXQk4g2KxzmZk"
},
{
  id: "9929",
  name: "BAYC #9929",
  image: "https://ipfs.io/ipfs/QmSv58fhtgWYgSY9twKK2Sup1TTTzrPWSZ7S2Urp1o87Gr"
},
{
  id: "9998",
  name: "BAYC #9998",
  image: "https://ipfs.io/ipfs/QmZGe8geLZGaUA892xbVdEqw5NQmAQKkQegBhWSfxLaEQT"
},
{
  id: "9981",
  name: "BAYC #9981",
  image: "https://ipfs.io/ipfs/QmVu8aaZkLntMm1ZN32J64CMqMLtxJVV3GFJGWeCjo7XGQ"
},
{
  id: "9971",
  name: "BAYC #9971",
  image: "https://ipfs.io/ipfs/QmW44wbsGXSEvVYuCUzT7mhu9SACggQKcmMWo6Y1Mc1Qee"
},
{
  id: "9947",
  name: "BAYC #9947",
  image: "https://ipfs.io/ipfs/QmcTiFvfsC6QUnZk8ZFonjTh1eBDV2jYrtLLWC2JMBtUpm"
},
{
  id: "9928",
  name: "BAYC #9928",
  image: "https://ipfs.io/ipfs/QmNTY6VksfBbmJt18ebwvwYU3yQ8HjeRCFC3fpKEi5h8AC"
},
{
  id: "9887",
  name: "BAYC #9887",
  image: "https://ipfs.io/ipfs/QmYjXSJiRyZAwgFAddtNKFb1NoEEFa5SnzfFSJPVJDJ3gN"
},
{
  id: "9980",
  name: "BAYC #9980",
  image: "https://ipfs.io/ipfs/QmUV7G2UJmxfxxFfTXkaRExbkWAWgsUkycWqsE9fH58f9Y"
},
{
  id: "9997",
  name: "BAYC #9997",
  image: "https://ipfs.io/ipfs/QmZQpSUgutRJc7dMjtBwZb2naFJEwAaU8Rb4a5fnQMih3G"
},
{
  id: "9970",
  name: "BAYC #9970",
  image: "https://ipfs.io/ipfs/QmaQeTbdMGvHtJeDjix3NkwCqwQGqVRqir5ippwEeKvuvq"
},
{
  id: "9946",
  name: "BAYC #9946",
  image: "https://ipfs.io/ipfs/QmQn9BnEMHrbqApJ59wDnFqEjtoZE6pKy4LoxFhWA7MKPw"
},
{
  id: "9927",
  name: "BAYC #9927",
  image: "https://ipfs.io/ipfs/QmUebrurYa1jfZ3MHyQBVtSmCDCyH43c65c9DHCqnmSqq1"
},
{
  id: "9886",
  name: "BAYC #9886",
  image: "https://ipfs.io/ipfs/QmdG7c8b6NdZMCgjGgqPTB4xNVNsGjhFjN6KpJgpeJryBE"
},
{
  id: "9979",
  name: "BAYC #9979",
  image: "https://ipfs.io/ipfs/QmS4EcHmJyaHhrJ3LwPJcSjPzXQL5ssvSzSvgh7BjUx4zp"
},
{
  id: "9996",
  name: "BAYC #9996",
  image: "https://ipfs.io/ipfs/QmNMjRbzWxPf6eeLC6UiuFecWFmU6Nq18CZutHmD4Gapbm"
},
{
  id: "9969",
  name: "BAYC #9969",
  image: "https://ipfs.io/ipfs/QmNvEWSgmQP7qrpo9kxZyzGq95CZcq5sHDmSS8MD27mvF3"
},
{
  id: "9945",
  name: "BAYC #9945",
  image: "https://ipfs.io/ipfs/QmcynJqBshVioNc4amdPonuQv8MDiVdjjkSh5ypbeBsByh"
},
{
  id: "9926",
  name: "BAYC #9926",
  image: "https://ipfs.io/ipfs/QmPAjFkZiGSUfHkg7WCbkAZDpHa57QcEMwTe8fQzKemsAk"
},
{
  id: "9885",
  name: "BAYC #9885",
  image: "https://ipfs.io/ipfs/QmWbTdyC4bdvY95CrctzXLdfmFKuvYTtkeqno2AP9cS1j9"
},
{
  id: "9978",
  name: "BAYC #9978",
  image: "https://ipfs.io/ipfs/Qme1mgfFYATQhEwEMDLadU5jraWJbJRaE2gCGyZrmTgdZQ"
},
{
  id: "9995",
  name: "BAYC #9995",
  image: "https://ipfs.io/ipfs/QmcZD1z2MNqXfrDyN8fryvoJEJyvAMoYH5vYtMW9yHwYKk"
},
{
  id: "9968",
  name: "BAYC #9968",
  image: "https://ipfs.io/ipfs/QmcMTLxwK4SAJuYCaSUfEXQPEhAvsyQXp2X3CquKSmxDAg"
},
{
  id: "9992",
  name: "BAYC #9992",
  image: "https://ipfs.io/ipfs/QmPediLJS4nYpf3T1Rtjnge2iawVf58WKjgnfQzkTGCj8g"
},
{
  id: "9925",
  name: "BAYC #9925",
  image: "https://ipfs.io/ipfs/QmNq8R5rBtGKNJ5MVKiegWAcLkqDAEpukWfcKqBHzijK76"
},
{
  id: "9944",
  name: "BAYC #9944",
  image: "https://ipfs.io/ipfs/QmXRcauUaH4SGr54BrL1g7N32S4jpfDiU7kbYYjyxhwaPH"
},
{
  id: "9989",
  name: "BAYC #9989",
  image: "https://ipfs.io/ipfs/QmRixhzz7vuF7Lq1h9XrH8rCyL7kGBZRf3i79ArqDmk7eN"
},
{
  id: "9999",
  name: "BAYC #9999",
  image: "https://ipfs.io/ipfs/QmejYePrutbfWEV8wA3ogTHi2cf2j6hoaPhZSc6vNE43nH"
},
{
  id: "9884",
  name: "BAYC #9884",
  image: "https://ipfs.io/ipfs/QmaLZDb19J8RuXNP1ZJGnpmJxvuW9sU3vVoeiBRcL2ZyA4"
},
{
  id: "9994",
  name: "BAYC #9994",
  image: "https://ipfs.io/ipfs/QmNVmFNmNU96CuGxZacK3pqRwiKbg1BVZ8j7zVUxj9JYQY"
},
{
  id: "9977",
  name: "BAYC #9977",
  image: "https://ipfs.io/ipfs/QmRtEfaJPNXPrYsqMqj2YC8CjWhTiPgwiRYxCsKffYqX9R"
},
{
  id: "9967",
  name: "BAYC #9967",
  image: "https://ipfs.io/ipfs/QmVvXQekNfb8BuvCQyHUcpj3FQPxqhjtaZhJC8TBwETgx1"
},
{
  id: "9924",
  name: "BAYC #9924",
  image: "https://ipfs.io/ipfs/QmUtH4euX2VCBM3NE4MyeMtr94cuF6nFHmK2HV94NNhXsa"
},
{
  id: "9991",
  name: "BAYC #9991",
  image: "https://ipfs.io/ipfs/QmdtSF6sZTxqe1GKKrdKMyZPzZrm7zVL5f7F5cV4zZFAT8"
},
{
  id: "9943",
  name: "BAYC #9943",
  image: "https://ipfs.io/ipfs/Qmcifn4eycVY7fcRZoJcVxRFPVVjMEsuCPvHByskFYAyPi"
},
{
  id: "9986",
  name: "BAYC #9986",
  image: "https://ipfs.io/ipfs/QmTuNQooKPJ8aF6aMvQpkUx4kV21ngUMe9HptJQpmJfR8e"
},
{
  id: "9988",
  name: "BAYC #9988",
  image: "https://ipfs.io/ipfs/QmU7fZA7zVbSytGLWpMXDnCxF9s64VWVuKhuerzjtcX54X"
},
{
  id: "9883",
  name: "BAYC #9883",
  image: "https://ipfs.io/ipfs/QmZV7xUkr57yo9iM1JNkvPc2SGaEmXdNZ9FQpn7fdUqhbC"
},
{
  id: "9993",
  name: "BAYC #9993",
  image: "https://ipfs.io/ipfs/Qmc9a8HUENWiHTDov7SZ8Ncm4aFBSAwq4UgPkenHDvfTjY"
},
{
  id: "9976",
  name: "BAYC #9976",
  image: "https://ipfs.io/ipfs/QmayveerapVYVZvQ1Rnv5SmheGM6AnhL56kubjnXW2uZM1"
},
{
  id: "9966",
  name: "BAYC #9966",
  image: "https://ipfs.io/ipfs/QmbsHrhZF7x1mU2XLTXwwDvhn1DidR6wMXoExVRfeCqL1h"
},
{
  id: "9942",
  name: "BAYC #9942",
  image: "https://ipfs.io/ipfs/QmdZeGEJTwkJMSsXgqAbgoxwmBJ8edXAo3T3cs5dKsdHYJ"
},
{
  id: "9990",
  name: "BAYC #9990",
  image: "https://ipfs.io/ipfs/QmWLu7iWfQTNA3aY3PoJpxa9enTytwHAZ2AycT4dfWf2iz"
},
{
  id: "9923",
  name: "BAYC #9923",
  image: "https://ipfs.io/ipfs/QmUVisgmaRkHYjgB33D2vG57qTxWmnLDgLBe4k4MZDfAX4"
},
{
  id: "9987",
  name: "BAYC #9987",
  image: "https://ipfs.io/ipfs/QmW9mEm8XPw6heoMw8hYon81VaRC4F5m8D2fjJFtvwjCsJ"
},
{
  id: "9882",
  name: "BAYC #9882",
  image: "https://ipfs.io/ipfs/QmTwfbsLxP3827d6kXtNMHzCeC2iPMmoTRL4yuyHSbtrZt"
},
{
  id: "9985",
  name: "BAYC #9985",
  image: "https://ipfs.io/ipfs/QmUy2aSNBkEtRgiA8xCNDiyN6wALDRcbasia4jM7JLC1cv"
},
{
  id: "9982",
  name: "BAYC #9982",
  image: "https://ipfs.io/ipfs/QmdkD8KyfLjXYayoAxLwmNHGi2hA835VArQr3tiCyToayx"
},
{
  id: "9965",
  name: "BAYC #9965",
  image: "https://ipfs.io/ipfs/QmbQAiFTdw7jVraqht7Ljfeg51GssvjDp9iHrjA2sXaz57"
},
{
  id: "9975",
  name: "BAYC #9975",
  image: "https://ipfs.io/ipfs/QmSyVka1v6CwBtCuKv8TnuKbpVEDMMDbTWto2jtfxxuVMK"
},
{
  id: "9984",
  name: "BAYC #9984",
  image: "https://ipfs.io/ipfs/QmSQ5bUAZdxT3ny6u7kLWmbGUrvfB8u7R9Fz7vWS3YNunS"
},
{
  id: "9983",
  name: "BAYC #9983",
  image: "https://ipfs.io/ipfs/QmVwXXwoDGoJ1iqFg51SsvtQLXrMNFiKFy2GxRfPzn1Z91"
},
{
  id: "9941",
  name: "BAYC #9941",
  image: "https://ipfs.io/ipfs/QmXC6j6yZVm8Dva3QeoF1XwG9MbuVEUntXeZswmCNu9JgU"
},
{
  id: "9881",
  name: "BAYC #9881",
  image: "https://ipfs.io/ipfs/QmPg7LGbMwhigFda2DVWpHsDet4bVJexrHbNXfwfywHT7Y"
},
{
  id: "9922",
  name: "BAYC #9922",
  image: "https://ipfs.io/ipfs/QmZPfprye8VJPGkDFQCUSeDprqUFxyHaVfrpZtN64F729r"
},
{
  id: "9940",
  name: "BAYC #9940",
  image: "https://ipfs.io/ipfs/Qmd3bFT8j97C6cZM2ZdzVwrGJFW2DFuogaGBntdkWjn2Es"
},
{
  id: "9962",
  name: "BAYC #9962",
  image: "https://ipfs.io/ipfs/QmNMCR56431gTCd9Qo6yrMPRgwcf7CQKcZT1rNGS3Yqja6"
},
{
  id: "9921",
  name: "BAYC #9921",
  image: "https://ipfs.io/ipfs/QmWAGxnMEoGAdinkYYLpbgkfThWPscwNxV3SbrLgDmnrhq"
},
{
  id: "9880",
  name: "BAYC #9880",
  image: "https://ipfs.io/ipfs/QmQELr3pRoQra3FxFMHchb2qe2cLZ2QwagRdogWAEsezzG"
},
{
  id: "9939",
  name: "BAYC #9939",
  image: "https://ipfs.io/ipfs/QmZZgtDkziwxy1cyoo2Noh3vojcQHSqC1ox7G9EJThuZ2d"
},
{
  id: "9961",
  name: "BAYC #9961",
  image: "https://ipfs.io/ipfs/QmQ19CcBeJnfSP5iJJfgz1xJTEqBQu7C8eEXGyi7GQLDvu"
},
{
  id: "9920",
  name: "BAYC #9920",
  image: "https://ipfs.io/ipfs/QmZAfCQ16a2tTS3R9EDYAbg1SmbTMcPtk3YmF5b8nrJQVJ"
},
{
  id: "9879",
  name: "BAYC #9879",
  image: "https://ipfs.io/ipfs/QmchEjG6veQT6HDW4sGFNvpdSrzB4ffyd68iaKqhxsfbEd"
},
{
  id: "9938",
  name: "BAYC #9938",
  image: "https://ipfs.io/ipfs/QmY8JTCAXvr68iz5gVnCd5uGf8ymbXFYbASiWdbtV2iWEF"
},
{
  id: "9919",
  name: "BAYC #9919",
  image: "https://ipfs.io/ipfs/QmbET9nJhCuN31sRtXshcj6VJbBMckboPdnnvRzRvYbQZX"
},
{
  id: "9960",
  name: "BAYC #9960",
  image: "https://ipfs.io/ipfs/Qma1aZPn7iS1vxkfip6kjGjbA5EUPDaunsApJJ8mUt8pyT"
},
{
  id: "9878",
  name: "BAYC #9878",
  image: "https://ipfs.io/ipfs/QmNpLVqkRc88EcVgiYmeY5fHba7MEcDUrXxCcx87HRgEc3"
},
{
  id: "9937",
  name: "BAYC #9937",
  image: "https://ipfs.io/ipfs/QmYAM5FBvNt3xng9m5VBceQANcCozwF9yzG9rdHT4CzDzb"
},
{
  id: "9918",
  name: "BAYC #9918",
  image: "https://ipfs.io/ipfs/Qmaj8eHwVtVMDrBp7RrX8Y5sYv6AcU3MDiUxyxWSb75PpL"
}, 
             ];
        actualNftData = boredapeyachtclubNfts.find(nft => nft.id.toString() === itemId);
           
                  } else if (collectionId === 'jirasan') {
        const jirasanNfts = [
            
            {
  id: "7196",
  name: "Jirasan #10000",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5612.png"
},
{
  id: "9262",
  name: "Jirasan #9262",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7391.png"
},
{
  id: "3875",
  name: "Jirasan #3875",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F2897.png"
},
{
  id: "3939",
  name: "Jirasan #3939",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F2938.png"
},
{
  id: "9276",
  name: "Jirasan #9276",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7405.png"
},
{
  id: "5821",
  name: "Jirasan #5821",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4454.png"
},
{
  id: "3649",
  name: "Jirasan #3649",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F2948.png"
},
{
  id: "7145",
  name: "Jirasan #7145",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5561.png"
},
{
  id: "9157",
  name: "Jirasan #9157",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7286.png"
},
{
  id: "9279",
  name: "Jirasan #9279",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7408.png"
},
{
  id: "6634",
  name: "Jirasan #6634",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5135.png"
},
{
  id: "7015",
  name: "Jirasan #7015",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5469.png"
},
{
  id: "113",
  name: "Jirasan #113",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F113.png"
},
{
  id: "1955",
  name: "Jirasan #1955",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1379.png"
},
{
  id: "2226",
  name: "Jirasan #2226",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1583.png"
},
{
  id: "3177",
  name: "Jirasan #3177",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F2333.png"
},
{
  id: "7527",
  name: "Jirasan #7527",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5896.png"
},
{
  id: "7998",
  name: "Jirasan #7998",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6288.png"
},
{
  id: "8420",
  name: "Jirasan #8420",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6665.png"
},
{
  id: "7237",
  name: "Jirasan #7237",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5653.png"
},
{
  id: "1815",
  name: "Jirasan #1815",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1267.png"
},
{
  id: "9159",
  name: "Jirasan #9159",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7288.png"
},
{
  id: "5633",
  name: "Jirasan #5633",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4307.png"
},
{
  id: "48",
  name: "Jirasan #48",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F48.png"
},
{
  id: "7640",
  name: "Jirasan #7640",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5984.png"
},
{
  id: "5310",
  name: "Jirasan #5310",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4045.png"
},
{
  id: "5817",
  name: "Jirasan #5817",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4450.png"
},
{
  id: "1780",
  name: "Jirasan #1780",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1240.png"
},
{
  id: "168",
  name: "Jirasan #168",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F168.png"
},
{
  id: "4734",
  name: "Jirasan #4734",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F3575.png"
},
{
  id: "1432",
  name: "Jirasan #1432",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F962.png"
},
{
  id: "1939",
  name: "Jirasan #1939",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1366.png"
},
{
  id: "5757",
  name: "Jirasan #5757",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4404.png"
},
{
  id: "7018",
  name: "Jirasan #7018",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5472.png"
},
{
  id: "6192",
  name: "Jirasan #6192",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4749.png"
},
{
  id: "3274",
  name: "Jirasan #3274",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F2411.png"
},
{
  id: "4772",
  name: "Jirasan #4772",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F3606.png"
},
{
  id: "5112",
  name: "Jirasan #5112",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F3884.png"
},
{
  id: "9240",
  name: "Jirasan #9240",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7369.png"
},
            {
  id: "1333",
  name: "Jirasan #1333",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F877.png"
},
{
  id: "5232",
  name: "Jirasan #5232",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4055.png"
},
{
  id: "165",
  name: "Jirasan #165",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F164.png"
},
{
  id: "3281",
  name: "Jirasan #3281",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fimg.reservoir.tools%2Fimages%2Fv2%2Fmainnet%2F3UP35l03Au%252FTp0ADu%252BlTG1XbRyn%252BsYZB%252F5vpwLPkYPFDjCHxmzAvpyhJrYfnuYvj4Xv%252BAzu8onZHd3wXd94FyAHKIgikxI9qtb1hY0koZMM%253D.png"
},
{
  id: "8006",
  name: "Jirasan #8006",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6296.png"
},
{
  id: "9206",
  name: "Jirasan #9206",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7335.png"
},
{
  id: "95",
  name: "Jirasan #95",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F95.png"
},
{
  id: "8065",
  name: "Jirasan #8065",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6348.png"
},
{
  id: "6224",
  name: "Jirasan #6224",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4776.png"
},
{
  id: "7836",
  name: "Jirasan #7836",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6155.png"
},
{
  id: "7900",
  name: "Jirasan #7900",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6211.png"
},
{
  id: "7963",
  name: "Jirasan #7963",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6262.png"
},
{
  id: "7964",
  name: "Jirasan #7964",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6263.png"
},
{
  id: "1718",
  name: "Jirasan #1718",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1189.png"
},
{
  id: "7962",
  name: "Jirasan #7962",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6261.png"
},
{
  id: "7958",
  name: "Jirasan #7958",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6257.png"
},
{
  id: "5254",
  name: "Jirasan #5254",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4000.png"
},
{
  id: "7965",
  name: "Jirasan #7965",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6264.png"
},
{
  id: "8799",
  name: "Jirasan #8799",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6969.png"
},
{
  id: "9260",
  name: "Jirasan #9260",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F7389.png"
},
{
  id: "7224",
  name: "Jirasan #7224",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F5640.png"
},
{
  id: "7961",
  name: "Jirasan #7961",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F6260.png"
},
{
  id: "1237",
  name: "Jirasan #1237",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F801.png"
},
{
  id: "4304",
  name: "Jirasan #4304",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F3222.png"
},
{
  id: "5320",
  name: "Jirasan #5320",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4052.png"
},
{
  id: "1151",
  name: "Jirasan #1151",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F730.png"
},
{
  id: "6281",
  name: "Jirasan #6281",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FUncommon%2F902.png"
},
{
  id: "1582",
  name: "Jirasan #1582",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1078.png"
},
{
  id: "4403",
  name: "Jirasan #4403",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FUncommon%2F618.png"
},
{
  id: "848",
  name: "Jirasan #848",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FUncommon%2F12.png"
},
{
  id: "2028",
  name: "Jirasan #2028",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1438.png"
},
{
  id: "2246",
  name: "Jirasan #2246",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F1589.png"
},
{
  id: "4216",
  name: "Jirasan #4216",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F3163.png"
},
{
  id: "5409",
  name: "Jirasan #5409",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F4123.png"
},
{
  id: "1004",
  name: "Jirasan #1004",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FUncommon%2F51.png"
},
{
  id: "4185",
  name: "Jirasan #4185",
  image: "https://img-cdn.magiceden.dev/da:t/rs:fill:400:0:0/plain/https%3A%2F%2Fjirasanmeta.s3.eu-north-1.amazonaws.com%2FCommon%2F3133.png"
}
            
             ];
        actualNftData = jirasanNfts.find(nft => nft.id.toString() === itemId);
      
      
      } else if (collectionId === 'redacted-remilio-babies') {
        const remilioBabiesNFTs = [
            
{
  id: "9992",
  name: "Remilio #9992",
  image: "https://remilio.org/remilio/9992.png"
},
{
  id: "9982",
  name: "Remilio #9982",
  image: "https://remilio.org/remilio/9982.png"
},
{
  id: "9993",
  name: "Remilio #9993",
  image: "https://remilio.org/remilio/9993.png"
},
{
  id: "9983",
  name: "Remilio #9983",
  image: "https://remilio.org/remilio/9983.png"
},
{
  id: "9991",
  name: "Remilio #9991",
  image: "https://remilio.org/remilio/9991.png"
},
{
  id: "9994",
  name: "Remilio #9994",
  image: "https://remilio.org/remilio/9994.png"
},
{
  id: "9985",
  name: "Remilio #9985",
  image: "https://remilio.org/remilio/9985.png"
},
{
  id: "9986",
  name: "Remilio #9986",
  image: "https://remilio.org/remilio/9986.png"
},
{
  id: "9995",
  name: "Remilio #9995",
  image: "https://remilio.org/remilio/9995.png"
},
{
  id: "9989",
  name: "Remilio #9989",
  image: "https://remilio.org/remilio/9989.png"
},
{
  id: "9987",
  name: "Remilio #9987",
  image: "https://remilio.org/remilio/9987.png"
},
{
  id: "9997",
  name: "Remilio #9997",
  image: "https://remilio.org/remilio/9997.png"
},
{
  id: "9999",
  name: "Remilio #9999",
  image: "https://remilio.org/remilio/9999.png"
},
{
  id: "9988",
  name: "Remilio #9988",
  image: "https://remilio.org/remilio/9988.png"
},
{
  id: "9996",
  name: "Remilio #9996",
  image: "https://remilio.org/remilio/9996.png"
},
{
  id: "10000",
  name: "Remilio #10000",
  image: "https://remilio.org/remilio/10000.png"
},
{
  id: "9990",
  name: "Remilio #9990",
  image: "https://remilio.org/remilio/9990.png"
},
{
  id: "9998",
  name: "Remilio #9998",
  image: "https://remilio.org/remilio/9998.png"
},
{
  id: "9984",
  name: "Remilio #9984",
  image: "https://remilio.org/remilio/9984.png"
},
{
  id: "9945",
  name: "Remilio #9945",
  image: "https://remilio.org/remilio/9945.png"
},
{
  id: "9934",
  name: "Remilio #9934",
  image: "https://remilio.org/remilio/9934.png"
},
{
  id: "9929",
  name: "Remilio #9929",
  image: "https://remilio.org/remilio/9929.png"
},
{
  id: "9926",
  name: "Remilio #9926",
  image: "https://remilio.org/remilio/9926.png"
},
{
  id: "9937",
  name: "Remilio #9937",
  image: "https://remilio.org/remilio/9937.png"
},
{
  id: "9930",
  name: "Remilio #9930",
  image: "https://remilio.org/remilio/9930.png"
},
{
  id: "9923",
  name: "Remilio #9923",
  image: "https://remilio.org/remilio/9923.png"
},
{
  id: "9917",
  name: "Remilio #9917",
  image: "https://remilio.org/remilio/9917.png"
},
{
  id: "9938",
  name: "Remilio #9938",
  image: "https://remilio.org/remilio/9938.png"
},
{
  id: "9925",
  name: "Remilio #9925",
  image: "https://remilio.org/remilio/9925.png"
},
{
  id: "9939",
  name: "Remilio #9939",
  image: "https://remilio.org/remilio/9939.png"
},
{
  id: "9932",
  name: "Remilio #9932",
  image: "https://remilio.org/remilio/9932.png"
},
{
  id: "9920",
  name: "Remilio #9920",
  image: "https://remilio.org/remilio/9920.png"
},
{
  id: "9933",
  name: "Remilio #9933",
  image: "https://remilio.org/remilio/9933.png"
},
{
  id: "9927",
  name: "Remilio #9927",
  image: "https://remilio.org/remilio/9927.png"
},
{
  id: "9928",
  name: "Remilio #9928",
  image: "https://remilio.org/remilio/9928.png"
},
{
  id: "9922",
  name: "Remilio #9922",
  image: "https://remilio.org/remilio/9922.png"
},
{
  id: "9924",
  name: "Remilio #9924",
  image: "https://remilio.org/remilio/9924.png"
},
{
  id: "9931",
  name: "Remilio #9931",
  image: "https://remilio.org/remilio/9931.png"
},
{
  id: "9918",
  name: "Remilio #9918",
  image: "https://remilio.org/remilio/9918.png"
},
{
  id: "9921",
  name: "Remilio #9921",
  image: "https://remilio.org/remilio/9921.png"
},
{
  id: "9919",
  name: "Remilio #9919",
  image: "https://remilio.org/remilio/9919.png"
},
{
  id: "9916",
  name: "Remilio #9916",
  image: "https://remilio.org/remilio/9916.png"
},
{
  id: "9892",
  name: "Remilio #9892",
  image: "https://remilio.org/remilio/9892.png"
},
{
  id: "9887",
  name: "Remilio #9887",
  image: "https://remilio.org/remilio/9887.png"
},
{
  id: "9893",
  name: "Remilio #9893",
  image: "https://remilio.org/remilio/9893.png"
},
{
  id: "9891",
  name: "Remilio #9891",
  image: "https://remilio.org/remilio/9891.png"
},
{
  id: "9890",
  name: "Remilio #9890",
  image: "https://remilio.org/remilio/9890.png"
},
{
  id: "9885",
  name: "Remilio #9885",
  image: "https://remilio.org/remilio/9885.png"
},
{
  id: "9888",
  name: "Remilio #9888",
  image: "https://remilio.org/remilio/9888.png"
},
{
  id: "9884",
  name: "Remilio #9884",
  image: "https://remilio.org/remilio/9884.png"
},
{
  id: "9883",
  name: "Remilio #9883",
  image: "https://remilio.org/remilio/9883.png"
},
{
  id: "9886",
  name: "Remilio #9886",
  image: "https://remilio.org/remilio/9886.png"
},
{
  id: "9881",
  name: "Remilio #9881",
  image: "https://remilio.org/remilio/9881.png"
},
{
  id: "9882",
  name: "Remilio #9882",
  image: "https://remilio.org/remilio/9882.png"
},
{
  id: "9880",
  name: "Remilio #9880",
  image: "https://remilio.org/remilio/9880.png"
},
{
  id: "9879",
  name: "Remilio #9879",
  image: "https://remilio.org/remilio/9879.png"
},
{
  id: "9873",
  name: "Remilio #9873",
  image: "https://remilio.org/remilio/9873.png"
},
            {
  id: "9870",
  name: "Remilio #9870",
  image: "https://remilio.org/remilio/9870.png"
},
{
  id: "9877",
  name: "Remilio #9877",
  image: "https://remilio.org/remilio/9877.png"
},
{
  id: "9876",
  name: "Remilio #9876",
  image: "https://remilio.org/remilio/9876.png"
},
{
  id: "9871",
  name: "Remilio #9871",
  image: "https://remilio.org/remilio/9871.png"
},
{
  id: "9869",
  name: "Remilio #9869",
  image: "https://remilio.org/remilio/9869.png"
},
{
  id: "9868",
  name: "Remilio #9868",
  image: "https://remilio.org/remilio/9868.png"
},
{
  id: "9872",
  name: "Remilio #9872",
  image: "https://remilio.org/remilio/9872.png"
},
{
  id: "9867",
  name: "Remilio #9867",
  image: "https://remilio.org/remilio/9867.png"
},
{
  id: "9863",
  name: "Remilio #9863",
  image: "https://remilio.org/remilio/9863.png"
},
{
  id: "9866",
  name: "Remilio #9866",
  image: "https://remilio.org/remilio/9866.png"
},
{
  id: "9862",
  name: "Remilio #9862",
  image: "https://remilio.org/remilio/9862.png"
},
{
  id: "9857",
  name: "Remilio #9857",
  image: "https://remilio.org/remilio/9857.png"
},
{
  id: "9865",
  name: "Remilio #9865",
  image: "https://remilio.org/remilio/9865.png"
},
{
  id: "9864",
  name: "Remilio #9864",
  image: "https://remilio.org/remilio/9864.png"
},
{
  id: "9861",
  name: "Remilio #9861",
  image: "https://remilio.org/remilio/9861.png"
},
{
  id: "9859",
  name: "Remilio #9859",
  image: "https://remilio.org/remilio/9859.png"
},
{
  id: "9853",
  name: "Remilio #9853",
  image: "https://remilio.org/remilio/9853.png"
},
{
  id: "9860",
  name: "Remilio #9860",
  image: "https://remilio.org/remilio/9860.png"
},
{
  id: "9858",
  name: "Remilio #9858",
  image: "https://remilio.org/remilio/9858.png"
},
{
  id: "9848",
  name: "Remilio #9848",
  image: "https://remilio.org/remilio/9848.png"
},
{
  id: "9855",
  name: "Remilio #9855",
  image: "https://remilio.org/remilio/9855.png"
},
{
  id: "9856",
  name: "Remilio #9856",
  image: "https://remilio.org/remilio/9856.png"
},
{
  id: "9854",
  name: "Remilio #9854",
  image: "https://remilio.org/remilio/9854.png"
},
{
  id: "9852",
  name: "Remilio #9852",
  image: "https://remilio.org/remilio/9852.png"
},
{
  id: "9849",
  name: "Remilio #9849",
  image: "https://remilio.org/remilio/9849.png"
},
{
  id: "9843",
  name: "Remilio #9843",
  image: "https://remilio.org/remilio/9843.png"
},
{
  id: "9845",
  name: "Remilio #9845",
  image: "https://remilio.org/remilio/9845.png"
},
{
  id: "9851",
  name: "Remilio #9851",
  image: "https://remilio.org/remilio/9851.png"
},
{
  id: "9842",
  name: "Remilio #9842",
  image: "https://remilio.org/remilio/9842.png"
},
{
  id: "9839",
  name: "Remilio #9839",
  image: "https://remilio.org/remilio/9839.png"
},
{
  id: "9838",
  name: "Remilio #9838",
  image: "https://remilio.org/remilio/9838.png"
},
{
  id: "9850",
  name: "Remilio #9850",
  image: "https://remilio.org/remilio/9850.png"
},
{
  id: "9844",
  name: "Remilio #9844",
  image: "https://remilio.org/remilio/9844.png"
},
{
  id: "9847",
  name: "Remilio #9847",
  image: "https://remilio.org/remilio/9847.png"
},
{
  id: "9846",
  name: "Remilio #9846",
  image: "https://remilio.org/remilio/9846.png"
},
{
  id: "9836",
  name: "Remilio #9836",
  image: "https://remilio.org/remilio/9836.png"
},
{
  id: "9826",
  name: "Remilio #9826",
  image: "https://remilio.org/remilio/9826.png"
},
{
  id: "9828",
  name: "Remilio #9828",
  image: "https://remilio.org/remilio/9828.png"
},
{
  id: "9831",
  name: "Remilio #9831",
  image: "https://remilio.org/remilio/9831.png"
},
{
  id: "9825",
  name: "Remilio #9825",
  image: "https://remilio.org/remilio/9825.png"
},
{
  id: "9827",
  name: "Remilio #9827",
  image: "https://remilio.org/remilio/9827.png"
},
{
  id: "9823",
  name: "Remilio #9823",
  image: "https://remilio.org/remilio/9823.png"
},
{
  id: "9830",
  name: "Remilio #9830",
  image: "https://remilio.org/remilio/9830.png"
}
            ];
        actualNftData = remilioBabiesNFTs.find(nft => nft.id.toString() === itemId);
    
           } else if (collectionId === 'mocaverse') {
        const mocaverseNFTs = [
            
            {
  id: "8886",
  name: "Mocaverse #8886",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/146"
},
{
  id: "8887",
  name: "Mocaverse #8887",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/147"
},
{
  id: "8885",
  name: "Mocaverse #8885",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/145"
},
{
  id: "8884",
  name: "Mocaverse #8884",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/144"
},
{
  id: "8883",
  name: "Mocaverse #8883",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/143"
},
{
  id: "8881",
  name: "Mocaverse #8881",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/141"
},
{
  id: "8878",
  name: "Mocaverse #8878",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/138"
},
{
  id: "8882",
  name: "Mocaverse #8882",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/142"
},
{
  id: "8880",
  name: "Mocaverse #8880",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/140"
},
{
  id: "8879",
  name: "Mocaverse #8879",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/139"
},
{
  id: "8876",
  name: "Mocaverse #8876",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/136"
},
{
  id: "8877",
  name: "Mocaverse #8877",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/137"
},
{
  id: "8875",
  name: "Mocaverse #8875",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/135"
},
{
  id: "8872",
  name: "Mocaverse #8872",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/132"
},
{
  id: "8871",
  name: "Mocaverse #8871",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/131"
},
{
  id: "8869",
  name: "Mocaverse #8869",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/129"
},
{
  id: "8870",
  name: "Mocaverse #8870",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/130"
},
{
  id: "8868",
  name: "Mocaverse #8868",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/128"
},
{
  id: "8867",
  name: "Mocaverse #8867",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/127"
},
{
  id: "8866",
  name: "Mocaverse #8866",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/126"
},
{
  id: "8863",
  name: "Mocaverse #8863",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/123"
},
{
  id: "8865",
  name: "Mocaverse #8865",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/125"
},
{
  id: "8864",
  name: "Mocaverse #8864",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/124"
},
{
  id: "8862",
  name: "Mocaverse #8862",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/122"
},
{
  id: "8861",
  name: "Mocaverse #8861",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/121"
},
{
  id: "8860",
  name: "Mocaverse #8860",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/120"
},
{
  id: "8858",
  name: "Mocaverse #8858",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/118"
},
{
  id: "8859",
  name: "Mocaverse #8859",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/119"
},
{
  id: "8857",
  name: "Mocaverse #8857",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/117"
},
{
  id: "8856",
  name: "Mocaverse #8856",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/116"
},
{
  id: "8855",
  name: "Mocaverse #8855",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/115"
},
{
  id: "8854",
  name: "Mocaverse #8854",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/114"
},
{
  id: "8851",
  name: "Mocaverse #8851",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/111"
},
{
  id: "8852",
  name: "Mocaverse #8852",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/112"
},
{
  id: "8850",
  name: "Mocaverse #8850",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/110"
},
{
  id: "8849",
  name: "Mocaverse #8849",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/109"
},
{
  id: "8847",
  name: "Mocaverse #8847",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/107"
},
{
  id: "8848",
  name: "Mocaverse #8848",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/108"
},
{
  id: "8846",
  name: "Mocaverse #8846",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/106"
},
{
  id: "8845",
  name: "Mocaverse #8845",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/105"
},
{
  id: "8844",
  name: "Mocaverse #8844",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/104"
},
{
  id: "8843",
  name: "Mocaverse #8843",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/103"
},
{
  id: "8841",
  name: "Mocaverse #8841",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/101"
},
{
  id: "8842",
  name: "Mocaverse #8842",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/102"
},
{
  id: "8840",
  name: "Mocaverse #8840",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/100"
},
{
  id: "8839",
  name: "Mocaverse #8839",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/99"
},
{
  id: "8838",
  name: "Mocaverse #8838",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/98"
},
{
  id: "8837",
  name: "Mocaverse #8837",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/97"
},
{
  id: "8836",
  name: "Mocaverse #8836",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/96"
},
{
  id: "8835",
  name: "Mocaverse #8835",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/95"
},
{
  id: "8825",
  name: "Mocaverse #8825",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/85"
},
{
  id: "8834",
  name: "Mocaverse #8834",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/94"
},
{
  id: "8826",
  name: "Mocaverse #8826",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/86"
},
{
  id: "8828",
  name: "Mocaverse #8828",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/88"
},
{
  id: "8829",
  name: "Mocaverse #8829",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/89"
},
{
  id: "8827",
  name: "Mocaverse #8827",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/87"
},
{
  id: "8831",
  name: "Mocaverse #8831",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/91"
},
{
  id: "8833",
  name: "Mocaverse #8833",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/93"
},
{
  id: "8824",
  name: "Mocaverse #8824",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/84"
},
{
  id: "8823",
  name: "Mocaverse #8823",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/83"
},
{
  id: "8822",
  name: "Mocaverse #8822",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/82"
},
{
  id: "8821",
  name: "Mocaverse #8821",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/81"
},
{
  id: "8820",
  name: "Mocaverse #8820",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/80"
},
{
  id: "8819",
  name: "Mocaverse #8819",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/79"
},
{
  id: "8818",
  name: "Mocaverse #8818",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/78"
},
{
  id: "8817",
  name: "Mocaverse #8817",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/77"
},
{
  id: "8815",
  name: "Mocaverse #8815",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/75"
},
{
  id: "8814",
  name: "Mocaverse #8814",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/74"
},
{
  id: "8816",
  name: "Mocaverse #8816",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/76"
},
{
  id: "8813",
  name: "Mocaverse #8813",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/73"
},
{
  id: "8812",
  name: "Mocaverse #8812",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/72"
},
{
  id: "8811",
  name: "Mocaverse #8811",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/71"
},
{
  id: "8810",
  name: "Mocaverse #8810",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1543"
},
{
  id: "8809",
  name: "Mocaverse #8809",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1542"
},
{
  id: "8808",
  name: "Mocaverse #8808",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1541"
},
{
  id: "8807",
  name: "Mocaverse #8807",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1540"
},
{
  id: "8806",
  name: "Mocaverse #8806",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1539"
},
            {
  id: "8805",
  name: "Mocaverse #8805",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1538"
},
{
  id: "8803",
  name: "Mocaverse #8803",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1536"
},
{
  id: "8804",
  name: "Mocaverse #8804",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1537"
},
{
  id: "8802",
  name: "Mocaverse #8802",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1535"
},
{
  id: "8801",
  name: "Mocaverse #8801",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1534"
},
{
  id: "8800",
  name: "Mocaverse #8800",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1533"
},
{
  id: "8798",
  name: "Mocaverse #8798",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1531"
},
{
  id: "8799",
  name: "Mocaverse #8799",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1532"
},
{
  id: "8853",
  name: "Mocaverse #8853",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/113"
},
{
  id: "8874",
  name: "Mocaverse #8874",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/134"
},
{
  id: "8873",
  name: "Mocaverse #8873",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/133"
},
{
  id: "8832",
  name: "Mocaverse #8832",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/92"
},
{
  id: "8830",
  name: "Mocaverse #8830",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/90"
},
{
  id: "8797",
  name: "Mocaverse #8797",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/70"
},
{
  id: "8796",
  name: "Mocaverse #8796",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/69"
},
{
  id: "8794",
  name: "Mocaverse #8794",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/67"
},
{
  id: "8791",
  name: "Mocaverse #8791",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/64"
},
{
  id: "8790",
  name: "Mocaverse #8790",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/63"
},
{
  id: "8788",
  name: "Mocaverse #8788",
  image: "https://ipfs.io/ipfs/QmXZcT8b94a6jngckdSRP8vn279J7wHgDoS2f99B4PbL1W/1106"
},
{
  id: "8789",
  name: "Mocaverse #8789",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/62"
},
{
  id: "8795",
  name: "Mocaverse #8795",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/68"
},
{
  id: "8792",
  name: "Mocaverse #8792",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/65"
},
{
  id: "8793",
  name: "Mocaverse #8793",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/66"
},
{
  id: "8833",
  name: "Mocaverse #8833",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/93"
},
{
  id: "8824",
  name: "Mocaverse #8824",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/84"
},
{
  id: "8823",
  name: "Mocaverse #8823",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/83"
},
{
  id: "8822",
  name: "Mocaverse #8822",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/82"
},
{
  id: "8821",
  name: "Mocaverse #8821",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/81"
},
{
  id: "8820",
  name: "Mocaverse #8820",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/80"
},
{
  id: "8819",
  name: "Mocaverse #8819",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/79"
},
{
  id: "8818",
  name: "Mocaverse #8818",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/78"
},
{
  id: "8817",
  name: "Mocaverse #8817",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/77"
},
{
  id: "8815",
  name: "Mocaverse #8815",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/75"
},
{
  id: "8814",
  name: "Mocaverse #8814",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/74"
},
{
  id: "8816",
  name: "Mocaverse #8816",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/76"
},
{
  id: "8813",
  name: "Mocaverse #8813",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/73"
},
{
  id: "8812",
  name: "Mocaverse #8812",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/72"
},
{
  id: "8811",
  name: "Mocaverse #8811",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/71"
},
{
  id: "8810",
  name: "Mocaverse #8810",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1543"
},
{
  id: "8809",
  name: "Mocaverse #8809",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1542"
},
{
  id: "8808",
  name: "Mocaverse #8808",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1541"
},
{
  id: "8807",
  name: "Mocaverse #8807",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1540"
},
{
  id: "8806",
  name: "Mocaverse #8806",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1539"
},
{
  id: "8805",
  name: "Mocaverse #8805",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1538"
},
{
  id: "8803",
  name: "Mocaverse #8803",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1536"
},
{
  id: "8804",
  name: "Mocaverse #8804",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1537"
},
{
  id: "8802",
  name: "Mocaverse #8802",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1535"
},
{
  id: "8801",
  name: "Mocaverse #8801",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1534"
},
{
  id: "8800",
  name: "Mocaverse #8800",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1533"
},
{
  id: "8798",
  name: "Mocaverse #8798",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1531"
},
{
  id: "8799",
  name: "Mocaverse #8799",
  image: "https://ipfs.io/ipfs/Qme5CLY4hjfZ6kLzhZamPywRRCUYF4iqTqgQK7HxTtXatW/1532"
},
{
  id: "8853",
  name: "Mocaverse #8853",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/113"
},
{
  id: "8874",
  name: "Mocaverse #8874",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/134"
},
{
  id: "8873",
  name: "Mocaverse #8873",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/133"
},
{
  id: "8832",
  name: "Mocaverse #8832",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/92"
},
{
  id: "8830",
  name: "Mocaverse #8830",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/90"
},
{
  id: "8797",
  name: "Mocaverse #8797",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/70"
},
{
  id: "8796",
  name: "Mocaverse #8796",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/69"
},
{
  id: "8794",
  name: "Mocaverse #8794",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/67"
},
{
  id: "8791",
  name: "Mocaverse #8791",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/64"
},
{
  id: "8790",
  name: "Mocaverse #8790",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/63"
},
{
  id: "8788",
  name: "Mocaverse #8788",
  image: "https://ipfs.io/ipfs/QmXZcT8b94a6jngckdSRP8vn279J7wHgDoS2f99B4PbL1W/1106"
},
{
  id: "8789",
  name: "Mocaverse #8789",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/62"
},
{
  id: "8795",
  name: "Mocaverse #8795",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/68"
},
{
  id: "8792",
  name: "Mocaverse #8792",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/65"
},
{
  id: "8793",
  name: "Mocaverse #8793",
  image: "https://ipfs.io/ipfs/QmTSATKwnwD7LmGwmsT5jsxXvsw8azSLJbrrN4nwmipDCk/66"
}
             ];
        actualNftData = mocaverseNFTs.find(nft => nft.id.toString() === itemId);
           
                 } else if (collectionId === 'bit-bears') {
        const bitBearsNFTs = [
               
           {
  id: "400",
  name: "Bit Bears #400",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/400.gif"
},
{
  id: "178",
  name: "Bit Bears #178",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/178.gif"
},
{
  id: "1247",
  name: "Bit Bears #1247",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1247.gif"
},
{
  id: "1745",
  name: "Bit Bears #1745",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1745.gif"
},
{
  id: "676",
  name: "Bit Bears #676",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/676.gif"
},
{
  id: "551",
  name: "Bit Bears #551",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/551.gif"
},
{
  id: "2099",
  name: "Bit Bears #2099",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2099.gif"
},
{
  id: "2166",
  name: "Bit Bears #2166",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2166.gif"
},
{
  id: "2176",
  name: "Bit Bears #2176",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2176.gif"
},
{
  id: "2141",
  name: "Bit Bears #2141",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2141.gif"
},
{
  id: "1827",
  name: "Bit Bears #1827",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1827.gif"
},
{
  id: "1901",
  name: "Bit Bears #1901",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1901.gif"
},
{
  id: "953",
  name: "Bit Bears #953",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/953.gif"
},
{
  id: "654",
  name: "Bit Bears #654",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/654.gif"
},
{
  id: "455",
  name: "Bit Bears #455",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/455.gif"
},
{
  id: "2022",
  name: "Bit Bears #2022",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2022.gif"
},
{
  id: "1524",
  name: "Bit Bears #1524",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1524.gif"
},
{
  id: "156",
  name: "Bit Bears #156",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/156.gif"
},
{
  id: "1723",
  name: "Bit Bears #1723",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1723.gif"
},
{
  id: "1225",
  name: "Bit Bears #1225",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1225.gif"
},
{
  id: "951",
  name: "Bit Bears #951",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/951.gif"
},
{
  id: "1522",
  name: "Bit Bears #1522",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1522.gif"
},
{
  id: "453",
  name: "Bit Bears #453",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/453.gif"
},
{
  id: "2020",
  name: "Bit Bears #2020",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2020.gif"
},
{
  id: "1473",
  name: "Bit Bears #1473",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1473.gif"
},
{
  id: "2238",
  name: "Bit Bears #2238",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2238.gif"
},
{
  id: "1849",
  name: "Bit Bears #1849",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1849.gif"
},
{
  id: "2031",
  name: "Bit Bears #2031",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2031.gif"
},
{
  id: "1712",
  name: "Bit Bears #1712",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1712.gif"
},
{
  id: "1351",
  name: "Bit Bears #1351",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1351.gif"
},
{
  id: "1622",
  name: "Bit Bears #1622",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1622.gif"
},
{
  id: "1340",
  name: "Bit Bears #1340",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1340.gif"
},
{
  id: "1838",
  name: "Bit Bears #1838",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1838.gif"
},
{
  id: "1533",
  name: "Bit Bears #1533",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1533.gif"
},
{
  id: "2133",
  name: "Bit Bears #2133",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2133.gif"
},
{
  id: "1214",
  name: "Bit Bears #1214",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1214.gif"
},
{
  id: "962",
  name: "Bit Bears #962",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/962.gif"
},
{
  id: "769",
  name: "Bit Bears #769",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/769.gif"
},
{
  id: "643",
  name: "Bit Bears #643",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/643.gif"
},
{
  id: "780",
  name: "Bit Bears #780",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/780.gif"
},
{
  id: "1510",
  name: "Bit Bears #1510",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1510.gif"
},
{
  id: "1197",
  name: "Bit Bears #1197",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1197.gif"
},
{
  id: "1555",
  name: "Bit Bears #1555",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1555.gif"
},
{
  id: "1424",
  name: "Bit Bears #1424",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1424.gif"
},
{
  id: "1897",
  name: "Bit Bears #1897",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1897.gif"
},
{
  id: "1753",
  name: "Bit Bears #1753",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1753.gif"
},
{
  id: "911",
  name: "Bit Bears #911",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/911.gif"
},
{
  id: "1185",
  name: "Bit Bears #1185",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1185.gif"
},
{
  id: "1922",
  name: "Bit Bears #1922",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1922.gif"
},
{
  id: "853",
  name: "Bit Bears #853",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/853.gif"
},
{
  id: "777",
  name: "Bit Bears #777",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/777.gif"
},
{
  id: "2064",
  name: "Bit Bears #2064",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2064.gif"
},
{
  id: "994",
  name: "Bit Bears #994",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/994.gif"
},
{
  id: "497",
  name: "Bit Bears #497",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/497.gif"
},
{
  id: "2063",
  name: "Bit Bears #2063",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2063.gif"
},
{
  id: "1565",
  name: "Bit Bears #1565",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1565.gif"
},
            {
  id: "496",
  name: "Bit Bears #496",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/496.gif"
},
{
  id: "1566",
  name: "Bit Bears #1566",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1566.gif"
},
{
  id: "995",
  name: "Bit Bears #995",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/995.gif"
},
{
  id: "1066",
  name: "Bit Bears #1066",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1066.gif"
},
{
  id: "1846",
  name: "Bit Bears #1846",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1846.gif"
},
{
  id: "1869",
  name: "Bit Bears #1869",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1869.gif"
},
{
  id: "800",
  name: "Bit Bears #800",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/800.gif"
},
{
  id: "1075",
  name: "Bit Bears #1075",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1075.gif"
},
{
  id: "2136",
  name: "Bit Bears #2136",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2136.gif"
},
{
  id: "1067",
  name: "Bit Bears #1067",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1067.gif"
},
{
  id: "684",
  name: "Bit Bears #684",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/684.gif"
},
{
  id: "2058",
  name: "Bit Bears #2058",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2058.gif"
},
{
  id: "2057",
  name: "Bit Bears #2057",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2057.gif"
},
{
  id: "2056",
  name: "Bit Bears #2056",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2056.gif"
},
{
  id: "1806",
  name: "Bit Bears #1806",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1806.gif"
},
{
  id: "1744",
  name: "Bit Bears #1744",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1744.gif"
},
{
  id: "1690",
  name: "Bit Bears #1690",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1690.gif"
},
{
  id: "1680",
  name: "Bit Bears #1680",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1680.gif"
},
{
  id: "1643",
  name: "Bit Bears #1643",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1643.gif"
},
{
  id: "1560",
  name: "Bit Bears #1560",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1560.gif"
},
{
  id: "1559",
  name: "Bit Bears #1559",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1559.gif"
},
{
  id: "1558",
  name: "Bit Bears #1558",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1558.gif"
},
{
  id: "1557",
  name: "Bit Bears #1557",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1557.gif"
},
{
  id: "1246",
  name: "Bit Bears #1246",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1246.gif"
},
{
  id: "1372",
  name: "Bit Bears #1372",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1372.gif"
},
{
  id: "1308",
  name: "Bit Bears #1308",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1308.gif"
},
{
  id: "986",
  name: "Bit Bears #986",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/986.gif"
},
{
  id: "988",
  name: "Bit Bears #988",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/988.gif"
},
{
  id: "987",
  name: "Bit Bears #987",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/987.gif"
},
{
  id: "801",
  name: "Bit Bears #801",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/801.gif"
},
{
  id: "737",
  name: "Bit Bears #737",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/737.gif"
},
{
  id: "675",
  name: "Bit Bears #675",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/675.gif"
},
{
  id: "611",
  name: "Bit Bears #611",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/611.gif"
},
{
  id: "1870",
  name: "Bit Bears #1870",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1870.gif"
},
{
  id: "1182",
  name: "Bit Bears #1182",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1182.gif"
},
{
  id: "2055",
  name: "Bit Bears #2055",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2055.gif"
},
{
  id: "989",
  name: "Bit Bears #989",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/989.gif"
},
{
  id: "1039",
  name: "Bit Bears #1039",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1039.gif"
},
{
  id: "2108",
  name: "Bit Bears #2108",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2108.gif"
},
{
  id: "2045",
  name: "Bit Bears #2045",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/2045.gif"
},
{
  id: "1904",
  name: "Bit Bears #1904",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1904.gif"
},
{
  id: "1371",
  name: "Bit Bears #1371",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1371.gif"
},
{
  id: "1451",
  name: "Bit Bears #1451",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/1451.gif"
},
{
  id: "730",
  name: "Bit Bears #730",
  image: "https://ipfs.io/ipfs/Qmek1nCGxXmSGj6qq15eyxwkh8CpDN6vc9zrzQpQxAf2nm/730.gif"
}
             ];
        actualNftData = bitBearsNFTs.find(nft => nft.id.toString() === itemId);
                 
                 } else if (collectionId === 'the-captainz') {
        const captainzNFTs = [
            
         {
  id: "9924",
  name: "The Captainz #9924",
  image: "https://captainz-api.memeland.com/images/81a899e6-ca78-420d-a239-cc3cba572441/9924.png"
},
{
    id: '9945',
    name: 'The Captainz #9945',
    image: 'https://captainz-api.memeland.com/images/f6957d3e-516c-4e5a-8cef-36b15e38757c/9945.png'
},
{
    id: '9946',
    name: 'The Captainz #9946',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9952',
    name: 'The Captainz #9952',
    image: 'https://captainz-api.memeland.com/images/72078d5c-ada1-4edc-8230-c330f4015da2/9952.png'
},
{
    id: '9953',
    name: 'The Captainz #9953',
    image: 'https://captainz-api.memeland.com/images/3bf6dff7-4a22-4866-bc5f-dec3de297ff1/9953.png'
},
{
    id: '9954',
    name: 'The Captainz #9954',
    image: 'https://captainz-api.memeland.com/images/07722c56-6cc1-4da5-878b-05668e8b2756/9954.png'
},
{
    id: '9955',
    name: 'The Captainz #9955',
    image: 'https://captainz-api.memeland.com/images/89b30b18-a3dd-43f3-a3db-5a997e48352b/9955.png'
},
{
    id: '9956',
    name: 'The Captainz #9956',
    image: 'https://captainz-api.memeland.com/images/3ba5460e-d6b1-42f1-929e-d02bd761af6d/9956.png'
},
{
    id: '9958',
    name: 'The Captainz #9958',
    image: 'https://captainz-api.memeland.com/images/b3b63be2-e0eb-4fd2-a04a-8cc8afb0cd47/9958.png'
},
{
    id: '9959',
    name: 'The Captainz #9959',
    image: 'https://captainz-api.memeland.com/images/b530027e-1034-4db9-aed6-c91cd6b26555/9959.png'
},
{
    id: '9960',
    name: 'The Captainz #9960',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9961',
    name: 'The Captainz #9961',
    image: 'https://captainz-api.memeland.com/images/92907e89-44df-4e5a-8455-5eae7d61fe76/9961.png'
},
{
    id: '9962',
    name: 'The Captainz #9962',
    image: 'https://captainz-api.memeland.com/images/0fd7b782-097e-4f7e-8652-e3bff45a2b5a/9962.png'
},
{
    id: '9963',
    name: 'The Captainz #9963',
    image: 'https://captainz-api.memeland.com/images/dc8a9877-54b6-4e5c-beea-8b6c28c1c390/9963.png'
},
{
    id: '9964',
    name: 'The Captainz #9964',
    image: 'https://captainz-api.memeland.com/images/3d669621-014d-4e62-887e-3d0ce32dc9f9/9964.png'
},
{
    id: '9965',
    name: 'The Captainz #9965',
    image: 'https://captainz-api.memeland.com/images/1c978a21-c130-49ee-83d5-8026741868b1/9965.png'
},
{
    id: '9966',
    name: 'The Captainz #9966',
    image: 'https://captainz-api.memeland.com/images/48d84861-f069-4975-9e51-c82e1141742f/9966.png'
},
{
    id: '9967',
    name: 'The Captainz #9967',
    image: 'https://captainz-api.memeland.com/images/06e85e9c-8687-4037-8db5-11af4a1e691c/9967.png'
},
{
    id: '9968',
    name: 'The Captainz #9968',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9970',
    name: 'The Captainz #9970',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9971',
    name: 'The Captainz #9971',
    image: 'https://captainz-api.memeland.com/images/c5003a77-a346-488a-b8cd-5d2974dcf392/9971.png'
},
{
    id: '9972',
    name: 'The Captainz #9972',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9976',
    name: 'The Captainz #9976',
    image: 'https://captainz-api.memeland.com/images/55d703e8-b076-4815-9696-2fdd527180e5/9976.png'
},
{
    id: '9978',
    name: 'The Captainz #9978',
    image: 'https://captainz-api.memeland.com/images/f887a8ee-c26f-4892-90af-ee1ea47f250e/9978.png'
},
{
    id: '9980',
    name: 'The Captainz #9980',
    image: 'https://captainz-api.memeland.com/images/76b98d13-d298-4884-af6d-0eb8cb9b4f6b/9980.png'
},
{
    id: '9982',
    name: 'The Captainz #9982',
    image: 'https://captainz-api.memeland.com/images/bb0296c2-62cf-42ce-b418-9bf7075baf54/9982.png'
},
{
    id: '9983',
    name: 'The Captainz #9983',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9984',
    name: 'The Captainz #9984',
    image: 'https://captainz-api.memeland.com/images/0b7d5895-4f9f-45ce-9f67-4b97f849d39b/9984.png'
},
{
    id: '9985',
    name: 'The Captainz #9985',
    image: 'https://captainz-api.memeland.com/images/09142ae0-6a8e-4bfb-b0b0-0bf150ebca68/9985.png'
},
{
    id: '9986',
    name: 'The Captainz #9986',
    image: 'https://captainz-api.memeland.com/images/e2c5100f-e903-49f9-92c9-387bf9e51ad7/9986.png'
},
{
    id: '9987',
    name: 'The Captainz #9987',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9988',
    name: 'The Captainz #9988',
    image: 'https://captainz-api.memeland.com/images/a3e84c45-5750-4718-bf49-bbe774ccb963/9988.png'
},
           {
    id: '9990',
    name: 'The Captainz #9990',
    image: 'https://captainz-api.memeland.com/images/e1baf95b-5d95-46e4-8b5c-12c250846f63/9990.png'
},
{
    id: '9993',
    name: 'The Captainz #9993',
    image: 'https://captainz-api.memeland.com/images/30a9d102-bdc4-4eee-8af3-097ffbc37bd2/9993.png'
},
{
    id: '9995',
    name: 'The Captainz #9995',
    image: 'https://captainz-api.memeland.com/images/202fa0a4-7567-4e1d-9f7c-eeab95417977/9995.png'
},
{
    id: '9996',
    name: 'The Captainz #9996',
    image: 'https://captainz-api.memeland.com/images/d01e72ca-6b0e-4882-819f-b3c301f2774c/9996.png'
},
{
    id: '9997',
    name: 'The Captainz #9997',
    image: 'https://api.memeland.com/captainz/images/kraken.gif'
},
{
    id: '9998',
    name: 'The Captainz #9998',
    image: 'https://captainz-api.memeland.com/images/4d311bb4-4c2b-4ebf-80de-64632257c054/9998.png'
},
{
    id: '9924',
    name: 'The Captainz #9924',
    image: 'https://captainz-api.memeland.com/images/81a899e6-ca78-420d-a239-cc3cba572441/9924.png'
},
{
    id: '9945',
    name: 'The Captainz #9945',
    image: 'https://captainz-api.memeland.com/images/f6957d3e-516c-4e5a-8cef-36b15e38757c/9945.png'
},
{
    id: '9946',
    name: 'The Captainz #9946',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9952',
    name: 'The Captainz #9952',
    image: 'https://captainz-api.memeland.com/images/72078d5c-ada1-4edc-8230-c330f4015da2/9952.png'
},
{
    id: '9953',
    name: 'The Captainz #9953',
    image: 'https://captainz-api.memeland.com/images/3bf6dff7-4a22-4866-bc5f-dec3de297ff1/9953.png'
},
{
    id: '9954',
    name: 'The Captainz #9954',
    image: 'https://captainz-api.memeland.com/images/07722c56-6cc1-4da5-878b-05668e8b2756/9954.png'
},
{
    id: '9955',
    name: 'The Captainz #9955',
    image: 'https://captainz-api.memeland.com/images/89b30b18-a3dd-43f3-a3db-5a997e48352b/9955.png'
},
{
    id: '9956',
    name: 'The Captainz #9956',
    image: 'https://captainz-api.memeland.com/images/3ba5460e-d6b1-42f1-929e-d02bd761af6d/9956.png'
},
{
    id: '9958',
    name: 'The Captainz #9958',
    image: 'https://captainz-api.memeland.com/images/b3b63be2-e0eb-4fd2-a04a-8cc8afb0cd47/9958.png'
},
{
    id: '9959',
    name: 'The Captainz #9959',
    image: 'https://captainz-api.memeland.com/images/b530027e-1034-4db9-aed6-c91cd6b26555/9959.png'
},
{
    id: '9960',
    name: 'The Captainz #9960',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9961',
    name: 'The Captainz #9961',
    image: 'https://captainz-api.memeland.com/images/92907e89-44df-4e5a-8455-5eae7d61fe76/9961.png'
},
{
    id: '9962',
    name: 'The Captainz #9962',
    image: 'https://captainz-api.memeland.com/images/0fd7b782-097e-4f7e-8652-e3bff45a2b5a/9962.png'
},
{
    id: '9963',
    name: 'The Captainz #9963',
    image: 'https://captainz-api.memeland.com/images/dc8a9877-54b6-4e5c-beea-8b6c28c1c390/9963.png'
},
{
    id: '9964',
    name: 'The Captainz #9964',
    image: 'https://captainz-api.memeland.com/images/3d669621-014d-4e62-887e-3d0ce32dc9f9/9964.png'
},
{
    id: '9965',
    name: 'The Captainz #9965',
    image: 'https://captainz-api.memeland.com/images/1c978a21-c130-49ee-83d5-8026741868b1/9965.png'
},
{
    id: '9966',
    name: 'The Captainz #9966',
    image: 'https://captainz-api.memeland.com/images/48d84861-f069-4975-9e51-c82e1141742f/9966.png'
},
{
    id: '9967',
    name: 'The Captainz #9967',
    image: 'https://captainz-api.memeland.com/images/06e85e9c-8687-4037-8db5-11af4a1e691c/9967.png'
},
{
    id: '9968',
    name: 'The Captainz #9968',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9970',
    name: 'The Captainz #9970',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9971',
    name: 'The Captainz #9971',
    image: 'https://captainz-api.memeland.com/images/c5003a77-a346-488a-b8cd-5d2974dcf392/9971.png'
},
{
    id: '9972',
    name: 'The Captainz #9972',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9973',
    name: 'The Captainz #9973',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9974',
    name: 'The Captainz #9974',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9975',
    name: 'The Captainz #9975',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9976',
    name: 'The Captainz #9976',
    image: 'https://captainz-api.memeland.com/images/55d703e8-b076-4815-9696-2fdd527180e5/9976.png'
},
{
    id: '9977',
    name: 'The Captainz #9977',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9978',
    name: 'The Captainz #9978',
    image: 'https://captainz-api.memeland.com/images/f887a8ee-c26f-4892-90af-ee1ea47f250e/9978.png'
},
{
    id: '9979',
    name: 'The Captainz #9979',
    image: 'https://api.memeland.com/captainz/images/human.gif'
},
{
    id: '9980',
    name: 'The Captainz #9980',
    image: 'https://captainz-api.memeland.com/images/76b98d13-d298-4884-af6d-0eb8cb9b4f6b/9980.png'
},
{
    id: '9981',
    name: 'The Captainz #9981',
    image: 'https://api.memeland.com/captainz/images/human.gif'
} 
                      ];
        actualNftData = captainzNFTs.find(nft => nft.id.toString() === itemId);
                
       } else if (collectionId === 'kanpai-pandas') {
        const pandasNFTs = [
            
            {
  id: "2109",
  name: "Kanpai Panda #2109",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2109"
},
{
  id: "5830",
  name: "Kanpai Panda #5830",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5830"
},
{
  id: "7086",
  name: "Kanpai Panda #7086",
  image: "https://prodftpserver.blob.core.windows.net/images/default/7086"
},
{
  id: "3872",
  name: "Kanpai Panda #3872",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3872"
},
{
  id: "3299",
  name: "Kanpai Panda #3299",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3299"
},
{
  id: "2580",
  name: "Kanpai Panda #2580",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2580"
},
{
  id: "7730",
  name: "Kanpai Panda #7730",
  image: "https://prodftpserver.blob.core.windows.net/images/default/7730"
},
{
  id: "2071",
  name: "Kanpai Panda #2071",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2071"
},
{
  id: "2694",
  name: "Kanpai Panda #2694",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2694"
},
{
  id: "4131",
  name: "Kanpai Panda #4131",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4131"
},
{
  id: "3554",
  name: "Kanpai Panda #3554",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3554"
},
{
  id: "7845",
  name: "Kanpai Panda #7845",
  image: "https://prodftpserver.blob.core.windows.net/images/default/7845"
},
{
  id: "2492",
  name: "Kanpai Panda #2492",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2492"
},
{
  id: "2127",
  name: "Kanpai Panda #2127",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2127"
},
{
  id: "2126",
  name: "Kanpai Panda #2126",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2126"
},
{
  id: "5913",
  name: "Kanpai Panda #5913",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5913"
},
{
  id: "6001",
  name: "Kanpai Panda #6001",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6001"
},
{
  id: "6002",
  name: "Kanpai Panda #6002",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6002"
},
{
  id: "5739",
  name: "Kanpai Panda #5739",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5739"
},
{
  id: "6850",
  name: "Kanpai Panda #6850",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6850"
},
{
  id: "5601",
  name: "Kanpai Panda #5601",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5601"
},
{
  id: "5600",
  name: "Kanpai Panda #5600",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5600"
},
{
  id: "5984",
  name: "Kanpai Panda #5984",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5984"
},
{
  id: "5985",
  name: "Kanpai Panda #5985",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5985"
},
{
  id: "5983",
  name: "Kanpai Panda #5983",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5983"
},
{
  id: "5970",
  name: "Kanpai Panda #5970",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5970"
},
{
  id: "5982",
  name: "Kanpai Panda #5982",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5982"
},
{
  id: "5969",
  name: "Kanpai Panda #5969",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5969"
},
{
  id: "7051",
  name: "Kanpai Panda #7051",
  image: "https://prodftpserver.blob.core.windows.net/images/default/7051"
},
{
  id: "6511",
  name: "Kanpai Panda #6511",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6511"
},
{
  id: "3886",
  name: "Kanpai Panda #3886",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3886"
},
{
  id: "2522",
  name: "Kanpai Panda #2522",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2522"
},
{
  id: "2523",
  name: "Kanpai Panda #2523",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2523"
},
{
  id: "2524",
  name: "Kanpai Panda #2524",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2524"
},
{
  id: "8105",
  name: "Kanpai Panda #8105",
  image: "https://prodftpserver.blob.core.windows.net/images/default/8105"
},
{
  id: "3427",
  name: "Kanpai Panda #3427",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3427"
},
{
  id: "3428",
  name: "Kanpai Panda #3428",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3428"
},
{
  id: "3367",
  name: "Kanpai Panda #3367",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3367"
},
{
  id: "6026",
  name: "Kanpai Panda #6026",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6026"
},
{
  id: "5376",
  name: "Kanpai Panda #5376",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5376"
},
{
  id: "5377",
  name: "Kanpai Panda #5377",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5377"
},
{
  id: "4334",
  name: "Kanpai Panda #4334",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4334"
},
{
  id: "3170",
  name: "Kanpai Panda #3170",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3170"
},
{
  id: "3500",
  name: "Kanpai Panda #3500",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3500"
},
{
  id: "2620",
  name: "Kanpai Panda #2620",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2620"
},
{
  id: "2159",
  name: "Kanpai Panda #2159",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2159"
},
{
  id: "3920",
  name: "Kanpai Panda #3920",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3920"
},
{
  id: "3919",
  name: "Kanpai Panda #3919",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3919"
},
{
  id: "4693",
  name: "Kanpai Panda #4693",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4693"
},
{
  id: "4692",
  name: "Kanpai Panda #4692",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4692"
},
{
  id: "3635",
  name: "Kanpai Panda #3635",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3635"
},
{
  id: "3636",
  name: "Kanpai Panda #3636",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3636"
},
{
  id: "2731",
  name: "Kanpai Panda #2731",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2731"
},
{
  id: "2691",
  name: "Kanpai Panda #2691",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2691"
},
{
  id: "2455",
  name: "Kanpai Panda #2455",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2455"
},
{
  id: "2341",
  name: "Kanpai Panda #2341",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2341"
},
{
  id: "6859",
  name: "Kanpai Panda #6859",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6859"
},
            {
  id: "2062",
  name: "Kanpai Panda #2062",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2062"
},
{
  id: "2063",
  name: "Kanpai Panda #2063",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2063"
},
{
  id: "2064",
  name: "Kanpai Panda #2064",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2064"
},
{
  id: "2207",
  name: "Kanpai Panda #2207",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2207"
},
{
  id: "2741",
  name: "Kanpai Panda #2741",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2741"
},
{
  id: "3527",
  name: "Kanpai Panda #3527",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3527"
},
{
  id: "6868",
  name: "Kanpai Panda #6868",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6868"
},
{
  id: "2409",
  name: "Kanpai Panda #2409",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2409"
},
{
  id: "2392",
  name: "Kanpai Panda #2392",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2392"
},
{
  id: "4641",
  name: "Kanpai Panda #4641",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4641"
},
{
  id: "5905",
  name: "Kanpai Panda #5905",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5905"
},
{
  id: "8114",
  name: "Kanpai Panda #8114",
  image: "https://prodftpserver.blob.core.windows.net/images/default/8114"
},
{
  id: "2519",
  name: "Kanpai Panda #2519",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2519"
},
{
  id: "5155",
  name: "Kanpai Panda #5155",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5155"
},
{
  id: "3491",
  name: "Kanpai Panda #3491",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3491"
},
{
  id: "7884",
  name: "Kanpai Panda #7884",
  image: "https://prodftpserver.blob.core.windows.net/images/default/7884"
},
{
  id: "3372",
  name: "Kanpai Panda #3372",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3372"
},
{
  id: "4525",
  name: "Kanpai Panda #4525",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4525"
},
{
  id: "2474",
  name: "Kanpai Panda #2474",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2474"
},
{
  id: "4482",
  name: "Kanpai Panda #4482",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4482"
},
{
  id: "5910",
  name: "Kanpai Panda #5910",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5910"
},
{
  id: "2479",
  name: "Kanpai Panda #2479",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2479"
},
{
  id: "6079",
  name: "Kanpai Panda #6079",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6079"
},
{
  id: "2857",
  name: "Kanpai Panda #2857",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2857"
},
{
  id: "4529",
  name: "Kanpai Panda #4529",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4529"
},
{
  id: "4532",
  name: "Kanpai Panda #4532",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4532"
},
{
  id: "3560",
  name: "Kanpai Panda #3560",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3560"
},
{
  id: "3832",
  name: "Kanpai Panda #3832",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3832"
},
{
  id: "4449",
  name: "Kanpai Panda #4449",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4449"
},
{
  id: "3810",
  name: "Kanpai Panda #3810",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3810"
},
{
  id: "6564",
  name: "Kanpai Panda #6564",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6564"
},
{
  id: "2310",
  name: "Kanpai Panda #2310",
  image: "https://prodftpserver.blob.core.windows.net/images/default/2310"
},
{
  id: "7755",
  name: "Kanpai Panda #7755",
  image: "https://prodftpserver.blob.core.windows.net/images/default/7755"
},
{
  id: "4455",
  name: "Kanpai Panda #4455",
  image: "https://prodftpserver.blob.core.windows.net/images/default/4455"
},
{
  id: "3867",
  name: "Kanpai Panda #3867",
  image: "https://prodftpserver.blob.core.windows.net/images/default/3867"
},
{
  id: "6616",
  name: "Kanpai Panda #6616",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6616"
},
{
  id: "6617",
  name: "Kanpai Panda #6617",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6617"
},
{
  id: "6615",
  name: "Kanpai Panda #6615",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6615"
},
{
  id: "5712",
  name: "Kanpai Panda #5712",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5712"
},
{
  id: "5711",
  name: "Kanpai Panda #5711",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5711"
},
{
  id: "5710",
  name: "Kanpai Panda #5710",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5710"
},
{
  id: "6606",
  name: "Kanpai Panda #6606",
  image: "https://prodftpserver.blob.core.windows.net/images/default/6606"
},
{
  id: "5750",
  name: "Kanpai Panda #5750",
  image: "https://prodftpserver.blob.core.windows.net/images/default/5750"
}
            
            ];
        actualNftData = pandasNFTs.find(nft => nft.id.toString() === itemId);
       
            } else if (collectionId === 'sappy-seals') {
        const sappysealsNFTs = [
            
            {
  id: 'sappy-seals-9968',
  name: 'Sappy Seals #9968',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9968.png'
},
{
  id: 'sappy-seals-9967',
  name: 'Sappy Seals #9967',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9967.png'
},
{
  id: 'sappy-seals-9966',
  name: 'Sappy Seals #9966',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9966.png'
},
{
  id: 'sappy-seals-9965',
  name: 'Sappy Seals #9965',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9965.png'
},
{
  id: 'sappy-seals-9964',
  name: 'Sappy Seals #9964',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9964.png'
},
{
  id: 'sappy-seals-9963',
  name: 'Sappy Seals #9963',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9963.png'
},
{
  id: 'sappy-seals-9978',
  name: 'Sappy Seals #9978',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9978.png'
},
{
  id: 'sappy-seals-9962',
  name: 'Sappy Seals #9962',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9962.png'
},
{
  id: 'sappy-seals-9977',
  name: 'Sappy Seals #9977',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9977.png'
},
{
  id: 'sappy-seals-9971',
  name: 'Sappy Seals #9971',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9971.png'
},
{
  id: 'sappy-seals-9961',
  name: 'Sappy Seals #9961',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9961.png'
},
{
  id: 'sappy-seals-9981',
  name: 'Sappy Seals #9981',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9981.png'
},
{
  id: 'sappy-seals-9976',
  name: 'Sappy Seals #9976',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9976.png'
},
{
  id: 'sappy-seals-9970',
  name: 'Sappy Seals #9970',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9970.png'
},
{
  id: 'sappy-seals-9980',
  name: 'Sappy Seals #9980',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9980.png'
},
{
  id: 'sappy-seals-9960',
  name: 'Sappy Seals #9960',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9960.png'
},
{
  id: 'sappy-seals-9993',
  name: 'Sappy Seals #9993',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9993.png'
},
{
  id: 'sappy-seals-9979',
  name: 'Sappy Seals #9979',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9979.png'
},
{
  id: 'sappy-seals-9969',
  name: 'Sappy Seals #9969',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9969.png'
},
{
  id: 'sappy-seals-9959',
  name: 'Sappy Seals #9959',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9959.png'
},
{
  id: 'sappy-seals-9915',
  name: 'Sappy Seals #9915',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9915.png'
},
{
  id: 'sappy-seals-9973',
  name: 'Sappy Seals #9973',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9973.png'
},
{
  id: 'sappy-seals-9991',
  name: 'Sappy Seals #9991',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9991.png'
},
{
  id: 'sappy-seals-9958',
  name: 'Sappy Seals #9958',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9958.png'
},
{
  id: 'sappy-seals-9914',
  name: 'Sappy Seals #9914',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9914.png'
},
{
  id: 'sappy-seals-9975',
  name: 'Sappy Seals #9975',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9975.png'
},
{
  id: 'sappy-seals-9990',
  name: 'Sappy Seals #9990',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9990.png'
},
{
  id: 'sappy-seals-9972',
  name: 'Sappy Seals #9972',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9972.png'
},
{
  id: 'sappy-seals-9913',
  name: 'Sappy Seals #9913',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9913.png'
},
{
  id: 'sappy-seals-9989',
  name: 'Sappy Seals #9989',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9989.png'
},
{
  id: 'sappy-seals-9955',
  name: 'Sappy Seals #9955',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9955.png'
},
{
  id: 'sappy-seals-10000',
  name: 'Sappy Seals #10000',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/10000.png'
},
{
  id: 'sappy-seals-9954',
  name: 'Sappy Seals #9954',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9954.png'
},
{
  id: 'sappy-seals-9988',
  name: 'Sappy Seals #9988',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9988.png'
},
{
  id: 'sappy-seals-9996',
  name: 'Sappy Seals #9996',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9996.png'
},
{
  id: 'sappy-seals-9912',
  name: 'Sappy Seals #9912',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9912.png'
},
{
  id: 'sappy-seals-9987',
  name: 'Sappy Seals #9987',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9987.png'
},
{
  id: 'sappy-seals-9957',
  name: 'Sappy Seals #9957',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9957.png'
},
{
  id: 'sappy-seals-9953',
  name: 'Sappy Seals #9953',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9953.png'
},
{
  id: 'sappy-seals-9999',
  name: 'Sappy Seals #9999',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9999.png'
},
{
  id: 'sappy-seals-9911',
  name: 'Sappy Seals #9911',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9911.png'
},
{
  id: 'sappy-seals-9995',
  name: 'Sappy Seals #9995',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9995.png'
},
{
  id: 'sappy-seals-9986',
  name: 'Sappy Seals #9986',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9986.png'
},
{
  id: 'sappy-seals-9948',
  name: 'Sappy Seals #9948',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9948.png'
},
{
  id: 'sappy-seals-9956',
  name: 'Sappy Seals #9956',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9956.png'
},
{
  id: 'sappy-seals-9992',
  name: 'Sappy Seals #9992',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9992.png'
},
{
  id: 'sappy-seals-9985',
  name: 'Sappy Seals #9985',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9985.png'
},
{
  id: 'sappy-seals-9910',
  name: 'Sappy Seals #9910',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9910.png'
},
{
  id: 'sappy-seals-9947',
  name: 'Sappy Seals #9947',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9947.png'
},
{
  id: 'sappy-seals-9998',
  name: 'Sappy Seals #9998',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9998.png'
},
{
  id: 'sappy-seals-9952',
  name: 'Sappy Seals #9952',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9952.png'
},
{
  id: 'sappy-seals-9984',
  name: 'Sappy Seals #9984',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9984.png'
},
{
  id: 'sappy-seals-9909',
  name: 'Sappy Seals #9909',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9909.png'
},
{
  id: 'sappy-seals-9936',
  name: 'Sappy Seals #9936',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9936.png'
},
            {
  id: 'sappy-seals-9997',
  name: 'Sappy Seals #9997',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9997.png'
},
{
  id: 'sappy-seals-9983',
  name: 'Sappy Seals #9983',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9983.png'
},
{
  id: 'sappy-seals-9946',
  name: 'Sappy Seals #9946',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9946.png'
},
{
  id: 'sappy-seals-9951',
  name: 'Sappy Seals #9951',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9951.png'
},
{
  id: 'sappy-seals-9945',
  name: 'Sappy Seals #9945',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9945.png'
},
{
  id: 'sappy-seals-9942',
  name: 'Sappy Seals #9942',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9942.png'
},
{
  id: 'sappy-seals-9925',
  name: 'Sappy Seals #9925',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9925.png'
},
{
  id: 'sappy-seals-9908',
  name: 'Sappy Seals #9908',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9908.png'
},
{
  id: 'sappy-seals-9994',
  name: 'Sappy Seals #9994',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9994.png'
},
{
  id: 'sappy-seals-9918',
  name: 'Sappy Seals #9918',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9918.png'
},
{
  id: 'sappy-seals-9922',
  name: 'Sappy Seals #9922',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9922.png'
},
{
  id: 'sappy-seals-9982',
  name: 'Sappy Seals #9982',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9982.png'
},
{
  id: 'sappy-seals-9929',
  name: 'Sappy Seals #9929',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9929.png'
},
{
  id: 'sappy-seals-9935',
  name: 'Sappy Seals #9935',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9935.png'
},
{
  id: 'sappy-seals-9944',
  name: 'Sappy Seals #9944',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9944.png'
},
{
  id: 'sappy-seals-9950',
  name: 'Sappy Seals #9950',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9950.png'
},
{
  id: 'sappy-seals-9941',
  name: 'Sappy Seals #9941',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9941.png'
},
{
  id: 'sappy-seals-9931',
  name: 'Sappy Seals #9931',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9931.png'
},
{
  id: 'sappy-seals-9924',
  name: 'Sappy Seals #9924',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9924.png'
},
{
  id: 'sappy-seals-9939',
  name: 'Sappy Seals #9939',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9939.png'
},
{
  id: 'sappy-seals-9933',
  name: 'Sappy Seals #9933',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9933.png'
},
{
  id: 'sappy-seals-9917',
  name: 'Sappy Seals #9917',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9917.png'
},
{
  id: 'sappy-seals-9907',
  name: 'Sappy Seals #9907',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9907.png'
},
{
  id: 'sappy-seals-9921',
  name: 'Sappy Seals #9921',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9921.png'
},
{
  id: 'sappy-seals-9928',
  name: 'Sappy Seals #9928',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9928.png'
},
{
  id: 'sappy-seals-9943',
  name: 'Sappy Seals #9943',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9943.png'
},
{
  id: 'sappy-seals-9937',
  name: 'Sappy Seals #9937',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9937.png'
},
{
  id: 'sappy-seals-9974',
  name: 'Sappy Seals #9974',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9974.png'
},
{
  id: 'sappy-seals-9949',
  name: 'Sappy Seals #9949',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9949.png'
},
{
  id: 'sappy-seals-9930',
  name: 'Sappy Seals #9930',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9930.png'
},
{
  id: 'sappy-seals-9919',
  name: 'Sappy Seals #9919',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9919.png'
},
{
  id: 'sappy-seals-9938',
  name: 'Sappy Seals #9938',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9938.png'
},
{
  id: 'sappy-seals-9940',
  name: 'Sappy Seals #9940',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9940.png'
},
{
  id: 'sappy-seals-9923',
  name: 'Sappy Seals #9923',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9923.png'
},
{
  id: 'sappy-seals-9926',
  name: 'Sappy Seals #9926',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9926.png'
},
{
  id: 'sappy-seals-9932',
  name: 'Sappy Seals #9932',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9932.png'
},
{
  id: 'sappy-seals-9934',
  name: 'Sappy Seals #9934',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9934.png'
},
{
  id: 'sappy-seals-9906',
  name: 'Sappy Seals #9906',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9906.png'
},
{
  id: 'sappy-seals-9920',
  name: 'Sappy Seals #9920',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9920.png'
},
{
  id: 'sappy-seals-9916',
  name: 'Sappy Seals #9916',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9916.png'
},
{
  id: 'sappy-seals-9927',
  name: 'Sappy Seals #9927',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9927.png'
},
{
  id: 'sappy-seals-9879',
  name: 'Sappy Seals #9879',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9879.png'
},
{
  id: 'sappy-seals-9878',
  name: 'Sappy Seals #9878',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9878.png'
},
{
  id: 'sappy-seals-9877',
  name: 'Sappy Seals #9877',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9877.png'
},
{
  id: 'sappy-seals-9876',
  name: 'Sappy Seals #9876',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9876.png'
},
{
  id: 'sappy-seals-9875',
  name: 'Sappy Seals #9875',
  image: 'https://ipfs.io/ipfs/QmUs4WQP47QKGwzPLjVMmhqTbspJfAC344abDEE2UT52HF/9875.png'
}
              ];
        actualNftData = sappysealsNFTs.find(nft => nft.id.toString() === itemId);
            
                 } else if (collectionId === 'sproto-gremlins') {
        const sprotoNFTs = [
            
            {
  id: 'sproto-gremlins-3249',
  name: 'Sproto Gremlins #3249',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3249.png'
},
{
  id: 'sproto-gremlins-3248',
  name: 'Sproto Gremlins #3248',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3248.png'
},
{
  id: 'sproto-gremlins-3247',
  name: 'Sproto Gremlins #3247',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3247.png'
},
{
  id: 'sproto-gremlins-3245',
  name: 'Sproto Gremlins #3245',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3245.png'
},
{
  id: 'sproto-gremlins-3246',
  name: 'Sproto Gremlins #3246',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3246.png'
},
{
  id: 'sproto-gremlins-3244',
  name: 'Sproto Gremlins #3244',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3244.png'
},
{
  id: 'sproto-gremlins-3243',
  name: 'Sproto Gremlins #3243',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3243.png'
},
{
  id: 'sproto-gremlins-3242',
  name: 'Sproto Gremlins #3242',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3242.png'
},
{
  id: 'sproto-gremlins-3241',
  name: 'Sproto Gremlins #3241',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3241.png'
},
{
  id: 'sproto-gremlins-3240',
  name: 'Sproto Gremlins #3240',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3240.png'
},
{
  id: 'sproto-gremlins-3239',
  name: 'Sproto Gremlins #3239',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3239.png'
},
{
  id: 'sproto-gremlins-3238',
  name: 'Sproto Gremlins #3238',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3238.png'
},
{
  id: 'sproto-gremlins-3237',
  name: 'Sproto Gremlins #3237',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3237.png'
},
{
  id: 'sproto-gremlins-3236',
  name: 'Sproto Gremlins #3236',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3236.png'
},
{
  id: 'sproto-gremlins-3235',
  name: 'Sproto Gremlins #3235',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3235.png'
},
{
  id: 'sproto-gremlins-3234',
  name: 'Sproto Gremlins #3234',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3234.png'
},
{
  id: 'sproto-gremlins-3233',
  name: 'Sproto Gremlins #3233',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3233.png'
},
{
  id: 'sproto-gremlins-3231',
  name: 'Sproto Gremlins #3231',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3231.png'
},
{
  id: 'sproto-gremlins-3232',
  name: 'Sproto Gremlins #3232',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3232.png'
},
{
  id: 'sproto-gremlins-3230',
  name: 'Sproto Gremlins #3230',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3230.png'
},
{
  id: 'sproto-gremlins-3228',
  name: 'Sproto Gremlins #3228',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3228.png'
},
{
  id: 'sproto-gremlins-3229',
  name: 'Sproto Gremlins #3229',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3229.png'
},
{
  id: 'sproto-gremlins-3227',
  name: 'Sproto Gremlins #3227',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3227.png'
},
{
  id: 'sproto-gremlins-3226',
  name: 'Sproto Gremlins #3226',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3226.png'
},
{
  id: 'sproto-gremlins-3225',
  name: 'Sproto Gremlins #3225',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3225.png'
},
{
  id: 'sproto-gremlins-3224',
  name: 'Sproto Gremlins #3224',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3224.png'
},
{
  id: 'sproto-gremlins-3223',
  name: 'Sproto Gremlins #3223',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3223.png'
},
{
  id: 'sproto-gremlins-3221',
  name: 'Sproto Gremlins #3221',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3221.png'
},
{
  id: 'sproto-gremlins-3222',
  name: 'Sproto Gremlins #3222',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3222.png'
},
{
  id: 'sproto-gremlins-3220',
  name: 'Sproto Gremlins #3220',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3220.png'
},
{
  id: 'sproto-gremlins-3219',
  name: 'Sproto Gremlins #3219',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3219.png'
},
{
  id: 'sproto-gremlins-3218',
  name: 'Sproto Gremlins #3218',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3218.png'
},
{
  id: 'sproto-gremlins-3217',
  name: 'Sproto Gremlins #3217',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3217.png'
},
{
  id: 'sproto-gremlins-3216',
  name: 'Sproto Gremlins #3216',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3216.png'
},
{
  id: 'sproto-gremlins-3214',
  name: 'Sproto Gremlins #3214',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3214.png'
},
{
  id: 'sproto-gremlins-3213',
  name: 'Sproto Gremlins #3213',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3213.png'
},
{
  id: 'sproto-gremlins-3212',
  name: 'Sproto Gremlins #3212',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3212.png'
},
{
  id: 'sproto-gremlins-3211',
  name: 'Sproto Gremlins #3211',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3211.png'
},
{
  id: 'sproto-gremlins-3210',
  name: 'Sproto Gremlins #3210',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3210.png'
},
{
  id: 'sproto-gremlins-3209',
  name: 'Sproto Gremlins #3209',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3209.png'
},
{
  id: 'sproto-gremlins-3207',
  name: 'Sproto Gremlins #3207',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3207.png'
},
{
  id: 'sproto-gremlins-3208',
  name: 'Sproto Gremlins #3208',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3208.png'
},
{
  id: 'sproto-gremlins-3205',
  name: 'Sproto Gremlins #3205',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3205.png'
},
{
  id: 'sproto-gremlins-3206',
  name: 'Sproto Gremlins #3206',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3206.png'
},
{
  id: 'sproto-gremlins-3203',
  name: 'Sproto Gremlins #3203',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3203.png'
},
{
  id: 'sproto-gremlins-3202',
  name: 'Sproto Gremlins #3202',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3202.png'
},
{
  id: 'sproto-gremlins-3204',
  name: 'Sproto Gremlins #3204',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3204.png'
},
{
  id: 'sproto-gremlins-3200',
  name: 'Sproto Gremlins #3200',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3200.png'
},
{
  id: 'sproto-gremlins-3199',
  name: 'Sproto Gremlins #3199',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3199.png'
},
{
  id: 'sproto-gremlins-3198',
  name: 'Sproto Gremlins #3198',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3198.png'
},
{
  id: 'sproto-gremlins-3197',
  name: 'Sproto Gremlins #3197',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3197.png'
},
{
  id: 'sproto-gremlins-3196',
  name: 'Sproto Gremlins #3196',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3196.png'
},
{
  id: 'sproto-gremlins-3194',
  name: 'Sproto Gremlins #3194',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3194.png'
},
{
  id: 'sproto-gremlins-3193',
  name: 'Sproto Gremlins #3193',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3193.png'
},
{
  id: 'sproto-gremlins-3195',
  name: 'Sproto Gremlins #3195',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3195.png'
},
{
  id: 'sproto-gremlins-3191',
  name: 'Sproto Gremlins #3191',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3191.png'
},
{
  id: 'sproto-gremlins-3192',
  name: 'Sproto Gremlins #3192',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3192.png'
},
{
  id: 'sproto-gremlins-3190',
  name: 'Sproto Gremlins #3190',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3190.png'
},
{
  id: 'sproto-gremlins-3189',
  name: 'Sproto Gremlins #3189',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3189.png'
},
            {
  id: 'sproto-gremlins-3188',
  name: 'Sproto Gremlins #3188',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3188.png'
},
{
  id: 'sproto-gremlins-3187',
  name: 'Sproto Gremlins #3187',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3187.png'
},
{
  id: 'sproto-gremlins-3186',
  name: 'Sproto Gremlins #3186',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3186.png'
},
{
  id: 'sproto-gremlins-3185',
  name: 'Sproto Gremlins #3185',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3185.png'
},
{
  id: 'sproto-gremlins-3184',
  name: 'Sproto Gremlins #3184',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3184.png'
},
{
  id: 'sproto-gremlins-3183',
  name: 'Sproto Gremlins #3183',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3183.png'
},
{
  id: 'sproto-gremlins-3179',
  name: 'Sproto Gremlins #3179',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3179.png'
},
{
  id: 'sproto-gremlins-3182',
  name: 'Sproto Gremlins #3182',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3182.png'
},
{
  id: 'sproto-gremlins-3181',
  name: 'Sproto Gremlins #3181',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3181.png'
},
{
  id: 'sproto-gremlins-3180',
  name: 'Sproto Gremlins #3180',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3180.png'
},
{
  id: 'sproto-gremlins-3177',
  name: 'Sproto Gremlins #3177',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3177.png'
},
{
  id: 'sproto-gremlins-3178',
  name: 'Sproto Gremlins #3178',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3178.png'
},
{
  id: 'sproto-gremlins-3170',
  name: 'Sproto Gremlins #3170',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3170.png'
},
{
  id: 'sproto-gremlins-3176',
  name: 'Sproto Gremlins #3176',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3176.png'
},
{
  id: 'sproto-gremlins-3171',
  name: 'Sproto Gremlins #3171',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3171.png'
},
{
  id: 'sproto-gremlins-3173',
  name: 'Sproto Gremlins #3173',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3173.png'
},
{
  id: 'sproto-gremlins-3169',
  name: 'Sproto Gremlins #3169',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3169.png'
},
{
  id: 'sproto-gremlins-3175',
  name: 'Sproto Gremlins #3175',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3175.png'
},
{
  id: 'sproto-gremlins-3174',
  name: 'Sproto Gremlins #3174',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3174.png'
},
{
  id: 'sproto-gremlins-3172',
  name: 'Sproto Gremlins #3172',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3172.png'
},
{
  id: 'sproto-gremlins-3168',
  name: 'Sproto Gremlins #3168',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3168.png'
},
{
  id: 'sproto-gremlins-3166',
  name: 'Sproto Gremlins #3166',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3166.png'
},
{
  id: 'sproto-gremlins-3165',
  name: 'Sproto Gremlins #3165',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3165.png'
},
{
  id: 'sproto-gremlins-3167',
  name: 'Sproto Gremlins #3167',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3167.png'
},
{
  id: 'sproto-gremlins-3163',
  name: 'Sproto Gremlins #3163',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3163.png'
},
{
  id: 'sproto-gremlins-3164',
  name: 'Sproto Gremlins #3164',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3164.png'
},
{
  id: 'sproto-gremlins-3162',
  name: 'Sproto Gremlins #3162',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3162.png'
},
{
  id: 'sproto-gremlins-3161',
  name: 'Sproto Gremlins #3161',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3161.png'
},
{
  id: 'sproto-gremlins-3160',
  name: 'Sproto Gremlins #3160',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3160.png'
},
{
  id: 'sproto-gremlins-3159',
  name: 'Sproto Gremlins #3159',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3159.png'
},
{
  id: 'sproto-gremlins-3158',
  name: 'Sproto Gremlins #3158',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3158.png'
},
{
  id: 'sproto-gremlins-3157',
  name: 'Sproto Gremlins #3157',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3157.png'
},
{
  id: 'sproto-gremlins-3156',
  name: 'Sproto Gremlins #3156',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3156.png'
},
{
  id: 'sproto-gremlins-3155',
  name: 'Sproto Gremlins #3155',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3155.png'
},
{
  id: 'sproto-gremlins-3154',
  name: 'Sproto Gremlins #3154',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3154.png'
},
{
  id: 'sproto-gremlins-3152',
  name: 'Sproto Gremlins #3152',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3152.png'
},
{
  id: 'sproto-gremlins-3153',
  name: 'Sproto Gremlins #3153',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3153.png'
},
{
  id: 'sproto-gremlins-3151',
  name: 'Sproto Gremlins #3151',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3151.png'
},
{
  id: 'sproto-gremlins-3144',
  name: 'Sproto Gremlins #3144',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3144.png'
},
{
  id: 'sproto-gremlins-3149',
  name: 'Sproto Gremlins #3149',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3149.png'
},
{
  id: 'sproto-gremlins-3150',
  name: 'Sproto Gremlins #3150',
  image: 'https://ipfs.io/ipfs/bafybeiefzvptnmjns6mq37wgey3hhh5hth7vznnrdrv2km6nkhvs23kthq/3150.png'
}
                 ];
        actualNftData = sprotoNFTs.find(nft => nft.id.toString() === itemId);
                    
      
       } else if (collectionId === 'cryptodickbutts-s3') {
        const cryptodickbuttsNFTs = [
            
            {
  id: 'cryptodickbutts-5343',
  name: 'CryptoDickbutts #5343',
  image: 'https://ipfs.io/ipfs/QmeSuPoYFmVMYVRL2j6fnUjBXif56pWpkhPhsMbuXHxTro'
},
{
  id: 'cryptodickbutts-5351',
  name: 'CryptoDickbutts #5351',
  image: 'https://ipfs.io/ipfs/QmNcSmVaLrfyaL6zxKrUYyDqTMNtmKd1DcM8qFijyow8Ne'
},
{
  id: 'cryptodickbutts-5355',
  name: 'CryptoDickbutts #5355',
  image: 'https://ipfs.io/ipfs/QmcJEqitWhhqy1KWLUSC31pknD6t1Z2f8Hzzxw65DRypcs'
},
{
  id: 'cryptodickbutts-5354',
  name: 'CryptoDickbutts #5354',
  image: 'https://ipfs.io/ipfs/QmW7gT6qSKN6bKSUsFnfMsvJ2EKC8peqpreWz5A4gqnp46'
},
{
  id: 'cryptodickbutts-5342',
  name: 'CryptoDickbutts #5342',
  image: 'https://ipfs.io/ipfs/QmW5DFPf3qG7qeJe6viu1y3rktMtPauQUHSXzcveLMHG1X'
},
{
  id: 'cryptodickbutts-5353',
  name: 'CryptoDickbutts #5353',
  image: 'https://ipfs.io/ipfs/QmVJSWVqWMbN4EhhEpwh94NvtfTmmmXkybF3irfQvad3Fx'
},
{
  id: 'cryptodickbutts-5350',
  name: 'CryptoDickbutts #5350',
  image: 'https://ipfs.io/ipfs/QmSsAtRRi9YZjb25Nuu3epJVAJ1U5DcTytwWFfX9nzwZuR'
},
{
  id: 'cryptodickbutts-5341',
  name: 'CryptoDickbutts #5341',
  image: 'https://ipfs.io/ipfs/QmesVgB2BzD7Msf1ism3D9HfQ6H1am1JLLVMSdFYxVA6ZK'
},
{
  id: 'cryptodickbutts-5352',
  name: 'CryptoDickbutts #5352',
  image: 'https://ipfs.io/ipfs/QmaeZYH7papbtKXsAWcrPrGY8wmnK6NZexMjhfsfVtAZFx'
},
{
  id: 'cryptodickbutts-5347',
  name: 'CryptoDickbutts #5347',
  image: 'https://ipfs.io/ipfs/QmXiyYY5djSLpo1u6QvZrihbVd1GoZwTErxcRLzVYBwUDC'
},
{
  id: 'cryptodickbutts-5346',
  name: 'CryptoDickbutts #5346',
  image: 'https://ipfs.io/ipfs/QmZEmMFCEzrQJ6mYVddNhsGCk9y1WAYPywXoUeqagKYuNw'
},
{
  id: 'cryptodickbutts-5345',
  name: 'CryptoDickbutts #5345',
  image: 'https://ipfs.io/ipfs/QmNNHsUu64Rq7KArTbzsYoCjz2k8K473VebvuvKepasVnE'
},
{
  id: 'cryptodickbutts-5340',
  name: 'CryptoDickbutts #5340',
  image: 'https://ipfs.io/ipfs/QmQ4U3qX6epNTv49PchsdXe7wEyp6FXZRoDLKDKyHDxunH'
},
{
  id: 'cryptodickbutts-5349',
  name: 'CryptoDickbutts #5349',
  image: 'https://ipfs.io/ipfs/QmYQpW9dpVKLU492e73AhPjcX3AYGg9E8CFChf44xaVHqW'
},
{
  id: 'cryptodickbutts-5344',
  name: 'CryptoDickbutts #5344',
  image: 'https://ipfs.io/ipfs/QmVYCvStX2UNGQwoabY8JtQ83b8uum2ai38UeG1nXLqBLh'
},
{
  id: 'cryptodickbutts-5339',
  name: 'CryptoDickbutts #5339',
  image: 'https://ipfs.io/ipfs/QmexmqMQL8d7WSeryinjVwrW8mYZrjkNJC36indvqVPNpV'
},
{
  id: 'cryptodickbutts-5348',
  name: 'CryptoDickbutts #5348',
  image: 'https://ipfs.io/ipfs/QmXZDaMQ4z1kFaXWbHCqVP6fShakCi21muCQqYY5fyQJcY'
},
{
  id: 'cryptodickbutts-5338',
  name: 'CryptoDickbutts #5338',
  image: 'https://ipfs.io/ipfs/QmQ7ZHQGHe4CsY47LUfceVHGQhtzmWBz2h69UCk2xqcDPw'
},
{
  id: 'cryptodickbutts-5337',
  name: 'CryptoDickbutts #5337',
  image: 'https://ipfs.io/ipfs/QmcDrfbqD8vx9i6FsPH3dG2LMHmYdJDg7WJYYd5tearYdW'
},
{
  id: 'cryptodickbutts-5331',
  name: 'CryptoDickbutts #5331',
  image: 'https://ipfs.io/ipfs/QmRooAahbpKpMVWdBMV9zAHLvxiC7u2DytmVGWpNyUNw8p'
},
{
  id: 'cryptodickbutts-5330',
  name: 'CryptoDickbutts #5330',
  image: 'https://ipfs.io/ipfs/QmU9hRE7Nn4BkY5PiyJyZ7zwFQT76ykaQcMz2iye5ko8qe'
},
{
  id: 'cryptodickbutts-5336',
  name: 'CryptoDickbutts #5336',
  image: 'https://ipfs.io/ipfs/QmSYVAmyoWfbomaSSCH3RUZWRAnfxorXDUpdo7NYvd5H2x'
},
{
  id: 'cryptodickbutts-5329',
  name: 'CryptoDickbutts #5329',
  image: 'https://ipfs.io/ipfs/QmYeTi4h9itx353gkyspmEBqEJnn8AaazbTMEahux2LQyE'
},
{
  id: 'cryptodickbutts-5335',
  name: 'CryptoDickbutts #5335',
  image: 'https://ipfs.io/ipfs/QmepJd6ktM2a4TS8itizCipCKcpx6zA6oS6SnaGpkRhTpz'
},
{
  id: 'cryptodickbutts-5328',
  name: 'CryptoDickbutts #5328',
  image: 'https://ipfs.io/ipfs/QmVNyzU6y2apRFwkY4xJFx1dRSTsRmLtHdEPPLd8s8T2Lf'
},
{
  id: 'cryptodickbutts-5327',
  name: 'CryptoDickbutts #5327',
  image: 'https://ipfs.io/ipfs/QmbHoBeL2FcxREGacJiupeffF1FqxkoeZh3rsxu3NcxSwQ'
},
{
  id: 'cryptodickbutts-5334',
  name: 'CryptoDickbutts #5334',
  image: 'https://ipfs.io/ipfs/QmdHP7tD6yugvGp4AkVNEhWCbtpMsNHgM5UmYqm6upERSh'
},
{
  id: 'cryptodickbutts-5333',
  name: 'CryptoDickbutts #5333',
  image: 'https://ipfs.io/ipfs/QmbsDj39VRMiVesoXJp3eYnUUngvKspCyq4XQFiJXB2jJC'
},
{
  id: 'cryptodickbutts-5326',
  name: 'CryptoDickbutts #5326',
  image: 'https://ipfs.io/ipfs/QmbfiAyBZeyCuw5xR3hsX22DeRdqvbC6PRBkSokUCBzCoH'
},
{
  id: 'cryptodickbutts-5325',
  name: 'CryptoDickbutts #5325',
  image: 'https://ipfs.io/ipfs/QmTrD8pseG5PUHRiC5ztHhi2ofR7paeTdmdkdTXwxF7rCc'
},
{
  id: 'cryptodickbutts-5324',
  name: 'CryptoDickbutts #5324',
  image: 'https://ipfs.io/ipfs/Qmas4g6FqLr3DxCumceuZRcLAj7cc4CKLGodfVLcR96ooV'
},
{
  id: 'cryptodickbutts-5319',
  name: 'CryptoDickbutts #5319',
  image: 'https://ipfs.io/ipfs/QmZParoVPMWVWA4Gw2yGzcGJ4gbG7uAMjHMck8dHNHeVU9'
},
{
  id: 'cryptodickbutts-5323',
  name: 'CryptoDickbutts #5323',
  image: 'https://ipfs.io/ipfs/Qmc4xEG9jMiY6DCei5ocbeHqd63Tn22vE1m9FbWkDdjCsz'
},
{
  id: 'cryptodickbutts-5318',
  name: 'CryptoDickbutts #5318',
  image: 'https://ipfs.io/ipfs/QmV9JUUi62ufzcb3PH57WGiHqJWED7RpUEGpAus1YudB3r'
},
{
  id: 'cryptodickbutts-5332',
  name: 'CryptoDickbutts #5332',
  image: 'https://ipfs.io/ipfs/QmbDnMHJNN5LCWnumUFqV89M5geRiMKNCGBX8Mec2s7q5A'
},
{
  id: 'cryptodickbutts-5321',
  name: 'CryptoDickbutts #5321',
  image: 'https://ipfs.io/ipfs/QmPWwbFxxopvTFV6P6AtWFhkKM95Wdb8Rs7aDJSon9CzpL'
},
{
  id: 'cryptodickbutts-5317',
  name: 'CryptoDickbutts #5317',
  image: 'https://ipfs.io/ipfs/QmPtit3RyAfeXtWfM2gJE154JX8A9bFvTorE2H3Q5Qa2Ww'
},
{
  id: 'cryptodickbutts-5322',
  name: 'CryptoDickbutts #5322',
  image: 'https://ipfs.io/ipfs/QmcZTgHvyfQbBGF6UoKLTd5s1N7EnoYYdWgAfWqDn6fUeV'
},
{
  id: 'cryptodickbutts-5316',
  name: 'CryptoDickbutts #5316',
  image: 'https://ipfs.io/ipfs/QmeM9sANMYxCN8EMXyNdZuvgcUXTP24eBhE7wd5XHw4YpX'
},
{
  id: 'cryptodickbutts-5360',
  name: 'CryptoDickbutts #5360',
  image: 'https://ipfs.io/ipfs/QmNrQhmDZwKB2YzdeWBESzAowmQre4mqrPKsUCWgMrsXGD'
},
{
  id: 'cryptodickbutts-5359',
  name: 'CryptoDickbutts #5359',
  image: 'https://ipfs.io/ipfs/QmZePmEAajcUPcxUkoQ2qVmx8grD659q9rjevCMUsHyZoz'
},
{
  id: 'cryptodickbutts-5358',
  name: 'CryptoDickbutts #5358',
  image: 'https://ipfs.io/ipfs/QmTbpoS4CzEmRmpEtefyexikodxLKxA4oCSozqKskfpWA7'
},
{
  id: 'cryptodickbutts-5357',
  name: 'CryptoDickbutts #5357',
  image: 'https://ipfs.io/ipfs/QmNuQG6GuXE1dbUu95mEqQMJcxTLTX5KsaKThbMoe1Rt2A'
},
{
  id: 'cryptodickbutts-5356',
  name: 'CryptoDickbutts #5356',
  image: 'https://ipfs.io/ipfs/QmUn7XwxMcnoinBSyzdqfAQAPeGm2FRByLFGuZM82kyK9W'
},
{
  id: 'cryptodickbutts-5301',
  name: 'CryptoDickbutts #5301',
  image: 'https://ipfs.io/ipfs/QmSiBg7iezs4tqvV86q5hW1TDgLAu32Rei2qyYvpNMuX9Z'
},
{
  id: 'cryptodickbutts-5300',
  name: 'CryptoDickbutts #5300',
  image: 'https://ipfs.io/ipfs/QmXDv1vy88TBVEMew5kRwFW88sZ4FHWwKe3C2qhbbZuex3'
},
{
  id: 'cryptodickbutts-5299',
  name: 'CryptoDickbutts #5299',
  image: 'https://ipfs.io/ipfs/QmVPEDnrYyYL6a14vmhNXPZCZS72Uihf19Lb3aBUb99tKp'
},
{
  id: 'cryptodickbutts-5298',
  name: 'CryptoDickbutts #5298',
  image: 'https://ipfs.io/ipfs/QmUbjYG6r8gCqmBtBdiLxPo4A51mX11NNtXfYMDwYUunbr'
},
{
  id: 'cryptodickbutts-5293',
  name: 'CryptoDickbutts #5293',
  image: 'https://ipfs.io/ipfs/QmdrYWRFQn7CrjcBfGUhQdFm4wk6DRxoxLQaZfmvQHgi3d'
},
{
  id: 'cryptodickbutts-5297',
  name: 'CryptoDickbutts #5297',
  image: 'https://ipfs.io/ipfs/QmVcyyHsisYXBnaQbGese1ZzJEqiVbJiNdfQYCTA5brfVj'
},
{
  id: 'cryptodickbutts-5296',
  name: 'CryptoDickbutts #5296',
  image: 'https://ipfs.io/ipfs/QmcXAiEjwcNZc4d7sGvHVxuuY8LHRo5qytoUaVd22R2ifY'
},
{
  id: 'cryptodickbutts-5290',
  name: 'CryptoDickbutts #5290',
  image: 'https://ipfs.io/ipfs/QmSqaohMMij8KAQrAt5q8nzjrtJ6Lt6XeB91UXcmNB4fkz'
},
{
  id: 'cryptodickbutts-5289',
  name: 'CryptoDickbutts #5289',
  image: 'https://ipfs.io/ipfs/Qmeo9DD3YL9kyJ9PJMJzpg7vjR7D62CK7rxW43EoW2RKHx'
},
{
  id: 'cryptodickbutts-5292',
  name: 'CryptoDickbutts #5292',
  image: 'https://ipfs.io/ipfs/QmZmziHG5iGC5r7PxNVpNqgceXhH42gbLqbC6kacp4Cpr9'
},
{
  id: 'cryptodickbutts-5291',
  name: 'CryptoDickbutts #5291',
  image: 'https://ipfs.io/ipfs/QmPh31XuYzXMizBrZesFv8L5wYeD8UCqeVXQe5Y37jigyf'
},
{
  id: 'cryptodickbutts-5288',
  name: 'CryptoDickbutts #5288',
  image: 'https://ipfs.io/ipfs/QmfD51SatuHhzV1H7gmXkq61fuWs6UqV3Mq3WNsLSJ4WKQ'
},
{
  id: 'cryptodickbutts-5287',
  name: 'CryptoDickbutts #5287',
  image: 'https://ipfs.io/ipfs/QmXbnMpsWwcQZk8PZTTiumwpmSUU1fXbHrBhBzj7r4xjGh'
},
{
  id: 'cryptodickbutts-5286',
  name: 'CryptoDickbutts #5286',
  image: 'https://ipfs.io/ipfs/QmWwMiUxbv3W2Xs7CLgi3Gn2KcUyjVNsHCcsL43Y34ASC9'
},
{
  id: 'cryptodickbutts-5285',
  name: 'CryptoDickbutts #5285',
  image: 'https://ipfs.io/ipfs/QmQT2KMhoo6L1Q1o3dR5prjaymT7CyuE77ZoK1K8K3xqu5'
},
{
  id: 'cryptodickbutts-5284',
  name: 'CryptoDickbutts #5284',
  image: 'https://ipfs.io/ipfs/QmPcL667aGXJqjnNTJkStKkFUbpwRPt5Xt9FMoBemfTWbk'
},
{
  id: 'cryptodickbutts-5283',
  name: 'CryptoDickbutts #5283',
  image: 'https://ipfs.io/ipfs/QmTc9v3qW8LCSCW7U7TpzpWfVv4E3yKGe1HAiiM4NEmhZv'
},
{
  id: 'cryptodickbutts-5282',
  name: 'CryptoDickbutts #5282',
  image: 'https://ipfs.io/ipfs/QmQwYo1M8XTcfLy17zztm7fWZYuGYrS5rZUL8EWPiSFXpB'
},
{
  id: 'cryptodickbutts-5281',
  name: 'CryptoDickbutts #5281',
  image: 'https://ipfs.io/ipfs/QmPSdUkFRBX9QcyuZafQiz7PFUDdub1c7VHgQDvUj1Bfgp'
},
{
  id: 'cryptodickbutts-5280',
  name: 'CryptoDickbutts #5280',
  image: 'https://ipfs.io/ipfs/Qmb5hugUwSvxT61FvwqRr9WWpE9BgM24LFAdvVFPBjMcAh'
},
{
  id: 'cryptodickbutts-5279',
  name: 'CryptoDickbutts #5279',
  image: 'https://ipfs.io/ipfs/QmRsM6NqhPjknzbLqkkB1NupJBHuXCwHzf7YJANLRBqDHH'
},
{
  id: 'cryptodickbutts-5278',
  name: 'CryptoDickbutts #5278',
  image: 'https://ipfs.io/ipfs/QmSmx656X5y1HVqCYhKJpuaLrHbgtEcfUW6RwkhfBh8STc'
},
{
  id: 'cryptodickbutts-5277',
  name: 'CryptoDickbutts #5277',
  image: 'https://ipfs.io/ipfs/QmQk3d38DjGAkErbmA37p59xU8dps5E2Z1oNbg8KN9fdrs'
},
{
  id: 'cryptodickbutts-5276',
  name: 'CryptoDickbutts #5276',
  image: 'https://ipfs.io/ipfs/QmZkCqXHHrw31m2jmfcrbHk7KFyNcBD9gbZ7SFLhiKRC8w'
},
{
  id: 'cryptodickbutts-5275',
  name: 'CryptoDickbutts #5275',
  image: 'https://ipfs.io/ipfs/QmNsaTdxe2gy5WvrEaKpVX5agbX1DsBdgqFpAaSpzQVLZw'
},
{
  id: 'cryptodickbutts-5274',
  name: 'CryptoDickbutts #5274',
  image: 'https://ipfs.io/ipfs/QmXYG4KavtbXKtmSWbvuiXhJSFT3rRCkL2wkUG7Bmb8T7v'
},
{
  id: 'cryptodickbutts-5273',
  name: 'CryptoDickbutts #5273',
  image: 'https://ipfs.io/ipfs/QmeKLgbSKTiHBQrBkpD8eYtwWDQDEfr36YuW9ooGRBLMRD'
},
{
  id: 'cryptodickbutts-5272',
  name: 'CryptoDickbutts #5272',
  image: 'https://ipfs.io/ipfs/QmVpmZMiY5rAieUeBXrsaFh2xpNJR4Z1qiBoDzExe9Qbdu'
},
{
  id: 'cryptodickbutts-5271',
  name: 'CryptoDickbutts #5271',
  image: 'https://ipfs.io/ipfs/Qma7jMhAB8tfh95qiemRNpq9cDS36X8drfbXRktMUSVzMz'
},
{
  id: 'cryptodickbutts-5270',
  name: 'CryptoDickbutts #5270',
  image: 'https://ipfs.io/ipfs/QmP7o3w9W7Ru4P3NWrvao4jfdaFvMhtNnHDuDQeeMp8CVw'
},
{
  id: 'cryptodickbutts-5269',
  name: 'CryptoDickbutts #5269',
  image: 'https://ipfs.io/ipfs/QmXoPKfLuoDKkB6VjcS1gEpXD2SQDpeAa6M3ooDZjV2en3'
},
{
  id: 'cryptodickbutts-5268',
  name: 'CryptoDickbutts #5268',
  image: 'https://ipfs.io/ipfs/QmTfznaEukA3Nk77umGfvp6iXAWp6d9wFXSmUp8tGtbYDR'
},
{
  id: 'cryptodickbutts-5267',
  name: 'CryptoDickbutts #5267',
  image: 'https://ipfs.io/ipfs/QmfJv6Cd3oFucj3f2qwGmNU21EQYq9XAT2FqM3aV4sinrC'
},
{
  id: 'cryptodickbutts-5266',
  name: 'CryptoDickbutts #5266',
  image: 'https://ipfs.io/ipfs/QmcDGpb1NKdpmr4j2zxVAzA4pZ5wfByifGXwKK2yrgkwKD'
},
{
  id: 'cryptodickbutts-5265',
  name: 'CryptoDickbutts #5265',
  image: 'https://ipfs.io/ipfs/QmcBpDXs26654AfLCFN7aV9CMZ5TUuvXKtxb22VPKn4AU4'
},
{
  id: 'cryptodickbutts-5264',
  name: 'CryptoDickbutts #5264',
  image: 'https://ipfs.io/ipfs/QmdHFanm2StaiU5gBX1hUTbmW3hMGq9z8wLXFKNKMdWWMV'
},
{
  id: 'cryptodickbutts-5263',
  name: 'CryptoDickbutts #5263',
  image: 'https://ipfs.io/ipfs/QmfMWTY8iMZDF7JYNNgviooc65EpfSR4s3LoemwUqRahmY'
},
{
  id: 'cryptodickbutts-5262',
  name: 'CryptoDickbutts #5262',
  image: 'https://ipfs.io/ipfs/QmRndFJwDnctk3kB3uPJEGofBNnXVpR5XncHGeLth4myhq'
},
{
  id: 'cryptodickbutts-5261',
  name: 'CryptoDickbutts #5261',
  image: 'https://ipfs.io/ipfs/QmY1i4sFqAbKM4YH71SfMga4ooNvjTkifmDLNjhhL5rRmt'
}
             ];
        actualNftData = cryptodickbuttsNFTs.find(nft => nft.id.toString() === itemId);
       
            } else if (collectionId === 'creepz-by-overlord') {
        const CreepzbyOVERLORDNFTs = [
        
  {
    id: 'creepz-1262',
    name: 'Creepz #1262',
    image: 'https://ipfs.io/ipfs/bafybeiffcatsx4sulijwqkfyspgphg2nfq3k6gkx4w32ys672ksmxdue34/'
  },
  {
    id: 'creepz-2323',
    name: 'Creepz #2323',
    image: 'https://ipfs.io/ipfs/bafybeighshbwvb64xsom5tmmhzobczmfayyxbdloaba2sg34dwqcb5akju/'
  },
  {
    id: 'creepz-10309',
    name: 'Creepz #10309',
    image: 'https://ipfs.io/ipfs/bafybeifmydtib5wmg5ihdv4cjyclrx3k4lxddomjhefysuycfbas6pwuc4/'
  },
  {
    id: 'creepz-9707',
    name: 'Creepz #9707',
    image: 'https://ipfs.io/ipfs/bafybeieg56jle6dfd3djaxglx4azmnadcx5qht7umu3ii6v7xv7me5iaoy/'
  },
  {
    id: 'creepz-6587',
    name: 'Creepz #6587',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F6587.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F6587.png&w=1080&q=75'
  },
  {
    id: 'creepz-9070',
    name: 'Creepz #9070',
    image: 'https://ipfs.io/ipfs/bafybeibspjtbjktmecpc4cyvkmjcbcwhzf53xf5d5duiqzswa4nimh336y/'
  },
  {
    id: 'creepz-377',
    name: 'Creepz #377',
    image: 'https://ipfs.io/ipfs/bafybeibubtc7pulgkvkko7w5hglcwe57gnqg7rxhbhtr7nqm6x6t3rmmla/'
  },
  {
    id: 'creepz-8761',
    name: 'Creepz #8761',
    image: 'https://ipfs.io/ipfs/bafybeiebm5nc356flzqkk237wj6xmvhllxmwytwwvmyugboshqybexbtae/'
  },
  {
    id: 'creepz-5131',
    name: 'Creepz #5131',
    image: 'https://ipfs.io/ipfs/bafybeifqdcpdjqfmildfmt7mi4xj43ef3uh2giudqxq5xsns5f5sauer2m/'
  },
  {
    id: 'creepz-6357',
    name: 'Creepz #6357',
    image: 'https://ipfs.io/ipfs/bafybeihu5oe6ustreypqpyaqlold3xeyk5ax7arkzorw3sg7sgefsa3hje/'
  },
  {
    id: 'creepz-3475',
    name: 'Creepz #3475',
    image: 'https://ipfs.io/ipfs/bafybeigenkxfca3h2jw3oklf3noandckp7w3o25u3g26d23yqa7qupy2ru/'
  },
  {
    id: 'creepz-2698',
    name: 'Creepz #2698',
    image: 'https://ipfs.io/ipfs/bafybeihnglwfs4oxmto7kifasubyjsq2og4lzkt3cwxe7eqjqclqb366k4/'
  },
  {
    id: 'creepz-5578',
    name: 'Creepz #5578',
    image: 'https://ipfs.io/ipfs/bafybeiftima7bb6urmsdnaid7cyutzitmammfjlware2o3223j3cwsxk6m/'
  },
  {
    id: 'creepz-4113',
    name: 'Creepz #4113',
    image: 'https://ipfs.io/ipfs/bafybeig4zh66f6a6nkrs4wnkz7lljxyjweigjzq5xjue57nbfm6qwjpu3a/'
  },
  {
    id: 'creepz-5579',
    name: 'Creepz #5579',
    image: 'https://ipfs.io/ipfs/bafybeics2534r45gwxcx5hw2f5wmivzaitz44izpyuwlxjwlj245bpp2au/'
  },
  {
    id: 'creepz-32',
    name: 'Creepz #32',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F32.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F32.png&w=1080&q=75'
  },
  {
    id: 'creepz-7409',
    name: 'Creepz #7409',
    image: 'https://ipfs.io/ipfs/bafybeib4ox5lewqpf3nnxi4ynhla7v34ywhjgvzz7n6dgq3wsbragi7tre/'
  },
  {
    id: 'creepz-7011',
    name: 'Creepz #7011',
    image: 'https://ipfs.io/ipfs/bafybeibyhumeqamtiglrkal6gcwnuwpkzjeld7shiqyohdeqrgby6yfbba/'
  },
  {
    id: 'creepz-6273',
    name: 'Creepz #6273',
    image: 'https://ipfs.io/ipfs/bafybeihhgcjwgf6blyq5mz6ik2lbasdv2ecvxihvuwjleh7oa4jelfowsm/'
  },
  {
    id: 'creepz-4228',
    name: 'Creepz #4228',
    image: 'https://ipfs.io/ipfs/bafybeiba25kdr5mgy4bkx2bsfe5s53utlud2vxviq3czze6r7b4bprdxsq/'
  },
  {
    id: 'creepz-10985',
    name: 'Creepz #10985',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F10985.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F10985.png&w=1080&q=75'
  },
  {
    id: 'creepz-6873',
    name: 'Creepz #6873',
    image: 'https://ipfs.io/ipfs/bafybeicnc3ztq7oz6zwqxel47imhd6s5swqwdrdwllz6npawbpde22ubsu/'
  },
  {
    id: 'creepz-6299',
    name: 'Creepz #6299',
    image: 'https://ipfs.io/ipfs/bafybeibxlkahqv5h2fponm3tkcbfdxyh73jcbnsp5vgks6pbnqapjsutta/'
  },
  {
    id: 'creepz-9194',
    name: 'Creepz #9194',
    image: 'https://ipfs.io/ipfs/bafybeiaaxmo3to7opl2nciz6colkio7cl42t7plsl22jbruquutjhtoasu/'
  },
  {
    id: 'creepz-6919',
    name: 'Creepz #6919',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F6919.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F6919.png&w=1080&q=75'
  },
  {
    id: 'creepz-1904',
    name: 'Creepz #1904',
    image: 'https://ipfs.io/ipfs/bafybeiblgycamo42gfzbskgwckjkuwryynzpyufjv327nlnvzf4lurrijm/'
  },
  {
    id: 'creepz-10064',
    name: 'Creepz #10064',
    image: 'https://ipfs.io/ipfs/bafybeidl4va2ceqr6awcsswr6qeolvjf57y7whrakzqiviqm762v3mw4xy/'
  },
  {
    id: 'creepz-8659',
    name: 'Creepz #8659',
    image: 'https://ipfs.io/ipfs/bafybeihqdkjtgqebo52ptkt6bhezclex5ywcuixjb6xeiettvzdstenvxa/'
  },
  {
    id: 'creepz-10457',
    name: 'Creepz #10457',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F10457.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F10457.png&w=1080&q=75'
  },
  {
    id: 'creepz-305',
    name: 'Creepz #305',
    image: 'https://ipfs.io/ipfs/bafybeighscfzdobluc2ei2r6w27nl6tyyylf7lgd3lydflmgyvk5vdgeru/'
  },
  {
    id: 'creepz-907',
    name: 'Creepz #907',
    image: 'https://ipfs.io/ipfs/bafybeifqxfiwo7wdzngj4zoltouulzf4itlop35brjmmyhjbnzreoofbzu/'
  },
  {
    id: 'creepz-1425',
    name: 'Creepz #1425',
    image: 'https://ipfs.io/ipfs/bafybeife3ya6nevjqud2o7462cb3jwutsuosvkryygje7l5j4j53bmarcu/'
  },
  {
    id: 'creepz-2521',
    name: 'Creepz #2521',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F2521.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F2521.png&w=1080&q=75'
  },
  {
    id: 'creepz-411',
    name: 'Creepz #411',
    image: 'https://ipfs.io/ipfs/bafybeibxevjlk2o5f7hrnney2hbcuji7gpw27dywk7izbhxudvweqtlr6e/'
  },
  {
    id: 'creepz-2931',
    name: 'Creepz #2931',
    image: 'https://ipfs.io/ipfs/bafybeiakzjhd7ccijiyblr2yh5c6idtmqikax4rkc2mbyh73vpr7aln7fe/'
  },
  {
    id: 'creepz-2500',
    name: 'Creepz #2500',
    image: 'https://ipfs.io/ipfs/bafybeibajsmvvnddnradksrz53f4yjp56pqege3kc2x7guuflr747hevca/'
  },
  {
    id: 'creepz-7815',
    name: 'Creepz #7815',
    image: 'https://ipfs.io/ipfs/bafybeiesvtoa5phz3cisp6gqavpyydaxfiph7tfr45o3hgcsuzqfdp6krm/'
  },
  {
    id: 'creepz-4572',
    name: 'Creepz #4572',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F4572.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F4572.png&w=1080&q=75'
  },
  {
    id: 'creepz-4494',
    name: 'Creepz #4494',
    image: 'https://ipfs.io/ipfs/bafybeiabdue4yzocy3unf2eupmrehi7sfr6d3xabvkrehh7jq43r3y27km/'
  },
  {
    id: 'creepz-6235',
    name: 'Creepz #6235',
    image: 'https://ipfs.io/ipfs/bafybeifbezkwnorgcasiy7qcf2ejiqjwwlo4dqp2mot62a7ofxvrtlzfzm/'
  },
  {
    id: 'creepz-1756',
    name: 'Creepz #1756',
    image: 'https://ipfs.io/ipfs/bafybeiat3jjtnmadjjabzlb5aqlghfthcjisa3ih6c6fvt7at2627cgzfy/'
  },
  {
    id: 'creepz-8527',
    name: 'Creepz #8527',
    image: 'https://ipfs.io/ipfs/bafybeig7n5uxrnibwrliwa4b4ui53re7iv3l7pkxgwu6f5cusrazglupaa/'
  },
  {
    id: 'creepz-2316',
    name: 'Creepz #2316',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F2316.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F2316.png&w=1080&q=75'
  },
  {
    id: 'creepz-4505',
    name: 'Creepz #4505',
    image: 'https://ipfs.io/ipfs/bafybeig6hhbhpaqa3y6jrr5cie6mufjkm675hbvtpa5rcqxrzavy3d63bu/'
  },
  {
    id: 'creepz-4215',
    name: 'Creepz #4215',
    image: 'https://ipfs.io/ipfs/bafybeiayqugn6vqiquvv47w53uov5ryvyqfu2dzrb2mqkxzc7frsmcheaa/'
  },
  {
    id: 'creepz-2490',
    name: 'Creepz #2490',
    image: 'https://ipfs.io/ipfs/bafybeigzuil76nra3eviwuhypz3dnkxbk4vph6te4q5djgvejc26eghgaq/'
  },
  {
    id: 'creepz-4575',
    name: 'Creepz #4575',
    image: 'https://ipfs.io/ipfs/bafybeia72fb2hhoxobvjxqszebtrfswtscuwaqsjdyzsqatlahrrkjz3sy/'
  },
  {
    id: 'creepz-4755',
    name: 'Creepz #4755',
    image: 'https://ipfs.io/ipfs/bafybeif43ca62wmz7fhqnmslkqp4jssvxhiuqeg7fw3udonmvtn6c4g5be/'
  },
  {
    id: 'creepz-6653',
    name: 'Creepz #6653',
    image: 'https://ipfs.io/ipfs/bafybeibuv5mfszk6gcq5xmamh63fu47ql6mwsvpcfqwibgfaijnrhysa5q/'
  },
  {
    id: 'creepz-1303',
    name: 'Creepz #1303',
    image: 'https://ipfs.io/ipfs/bafybeickryh2zwhyvnc4xear5hqxfy5kbc4hkc7xukystfbwdhbnoa3yre/'
  },
  {
    id: 'creepz-7036',
    name: 'Creepz #7036',
    image: 'https://ipfs.io/ipfs/bafybeiajln22sflfprf4ugneynsyklc7mp46hiyb4xjsxyhsnloj7evgsi/'
  },
  {
    id: 'creepz-6020',
    name: 'Creepz #6020',
    image: 'https://ipfs.io/ipfs/bafybeidooygpwbqy2omtik7ezvms6cophnmc2cqy5klkknqfhmwufnowqa/'
  },
  {
    id: 'creepz-1555',
    name: 'Creepz #1555',
    image: 'https://ipfs.io/ipfs/bafybeiamusfaa5ubwk7ono6q6ptuulid5jaiuzqi6mgulwlw4cagsvnsuy/'
  },
  {
    id: 'creepz-6849',
    name: 'Creepz #6849',
    image: 'https://ipfs.io/ipfs/bafybeia2c3nbfsdjwpxiucls5pcg2r52cpozrqyunph7vmishblnfpmrou/'
  },
  {
    id: 'creepz-10411',
    name: 'Creepz #10411',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F10411.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F10411.png&w=1080&q=75'
  },
  {
    id: 'creepz-1553',
    name: 'Creepz #1553',
    image: 'https://ipfs.io/ipfs/bafybeicadojd5amgw2wgvxpy7baxhzlzvp7ascuvrvryyhwayixojbn6da/'
  },
  {
    id: 'creepz-868',
    name: 'Creepz #868',
    image: 'https://ipfs.io/ipfs/bafybeigftj67vis7l2zk67mlhyhtxpmur4cdsjn75whcwsgc3vwyyqqxhm/'
  },
  {
    id: 'creepz-4641',
    name: 'Creepz #4641',
    image: 'https://ipfs.io/ipfs/bafybeie33swu4tflca577zulrtficvwqn3degqnq22mwd7inkkxqzj5454/'
  },
  {
    id: 'creepz-10448',
    name: 'Creepz #10448',
    image: 'https://ipfs.io/ipfs/bafybeiexl3uhcxvutjr6twtuav5hpsojew2plojuwfoq4xejcz7mjs57ba/'
  },
            
  {
    id: 'creepz-5085',
    name: 'Creepz #5085',
    image: 'https://ipfs.io/ipfs/bafybeibezo46wsykyry2tvg4adhsf5bfunh26wgde3ywvfig2juh6n433u/'
  },
  {
    id: 'creepz-3274',
    name: 'Creepz #3274',
    image: 'https://ipfs.io/ipfs/bafybeigmvd4d5u5puhl5djnaiemlk7ixomrzluliof3qpkwgslzwoqzfdm/'
  },
  {
    id: 'creepz-2892',
    name: 'Creepz #2892',
    image: 'https://ipfs.io/ipfs/bafybeib2aqci5je6qulrcfwtxa7hiahaoqlhrxh4il3c7htzkf3ieshnoq/'
  },
  {
    id: 'creepz-7004',
    name: 'Creepz #7004',
    image: 'https://ipfs.io/ipfs/bafybeignonjleekcxaqy34b6ugkvsfut77mkzbkcdvuimyxvwpk3xw72i4/'
  },
  {
    id: 'creepz-6686',
    name: 'Creepz #6686',
    image: 'https://ipfs.io/ipfs/bafybeifr5kneroc4uathz4pjpcuwq4e46kkf2krgpakzrld75lw37ogpjq/'
  },
  {
    id: 'creepz-6871',
    name: 'Creepz #6871',
    image: 'https://ipfs.io/ipfs/bafybeic72ckxzuwyhkiidx7efcesukutgzihvd5dkzvllnssaqkbb4d6ha/'
  },
  {
    id: 'creepz-9217',
    name: 'Creepz #9217',
    image: 'https://ipfs.io/ipfs/bafybeid67dmqattov7vhyftmqbu6jcuy57zbtn4uvwofhdjsvvgzu5pds4/'
  },
  {
    id: 'creepz-9222',
    name: 'Creepz #9222',
    image: 'https://ipfs.io/ipfs/bafybeiharlads76exq6fg3c46ro6npkyh4fod5mpv4btmljdts56seuusm/'
  },
  {
    id: 'creepz-9218',
    name: 'Creepz #9218',
    image: 'https://ipfs.io/ipfs/bafybeifexdktaffygecrb6no7pcrtpycqb5aycey3pdxhzurfbkyvnxh7e/'
  },
  {
    id: 'creepz-9219',
    name: 'Creepz #9219',
    image: 'https://ipfs.io/ipfs/bafybeihap774uv7n27bzv6f4244ftjq6k5skfplumq6hntwfcdyyifgrzm/'
  },
  {
    id: 'creepz-6475',
    name: 'Creepz #6475',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F6475.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F6475.png&w=1080&q=75'
  },
  {
    id: 'creepz-9220',
    name: 'Creepz #9220',
    image: 'https://ipfs.io/ipfs/bafybeicql2cufitclp4eolsyxjgkdfeisvss72wroj2lfumbnycht6eh3a/'
  },
  {
    id: 'creepz-9216',
    name: 'Creepz #9216',
    image: 'https://ipfs.io/ipfs/bafybeid7ksfyilueeaicavz5hrzjphn5r2vg4w42ytbpxslzhfqokrkk2e/'
  },
  {
    id: 'creepz-3810',
    name: 'Creepz #3810',
    image: 'https://ipfs.io/ipfs/bafybeifvsmwsjws56i7yah4idptnjuhirjeolud2nbb6us2kjoncl33giq/'
  },
  {
    id: 'creepz-2993',
    name: 'Creepz #2993',
    image: 'https://ipfs.io/ipfs/bafybeie726o4vggbwjax3fbepwimumskmeut4wdkidlxukrcwmqmmnfnta/'
  },
  {
    id: 'creepz-4251',
    name: 'Creepz #4251',
    image: 'https://ipfs.io/ipfs/bafybeiflushaarxrw3ujwxrpe3abqm3y2eqnbcegynjjjyyf7u3pqy42oy/'
  },
  {
    id: 'creepz-2681',
    name: 'Creepz #2681',
    image: 'https://ipfs.io/ipfs/bafybeib26ymch6adk7fga4smgmwulllr7s6oafmwexevx5ngkpkpefzw4q/'
  },
  {
    id: 'creepz-7493',
    name: 'Creepz #7493',
    image: 'https://ipfs.overlord.xyz/_ipx/w_1080,q_75/https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F7493.png?url=https%3A%2F%2Fcreepz-public.nyc3.digitaloceanspaces.com%2Fcreepz%2F7493.png&w=1080&q=75'
  },
  {
    id: 'creepz-4205',
    name: 'Creepz #4205',
    image: 'https://ipfs.io/ipfs/bafybeiatezoryhoenn7gypd4pukyuw5mdkbdgpl45rc6snstbvacwv7ok4/'
  },
  {
    id: 'creepz-9129',
    name: 'Creepz #9129',
    image: 'https://ipfs.io/ipfs/bafybeidgjpyzov3lqovjolypgchelxu3yodyn3opefiy4gttd6qdvm6ffa/'
  },
  {
    id: 'creepz-7538',
    name: 'Creepz #7538',
    image: 'https://ipfs.io/ipfs/bafybeibzirna3rgksmcfs34jpwv54fy3glsrfphlsthweob2az2pzoua3e/'
  }

              ];
        actualNftData = CreepzbyOVERLORDNFTs.find(nft => nft.id.toString() === itemId);
            
                 } else if (collectionId === 'infinigods') {
        const InfiniGodsNFTs = [
    
  {
    id: 'infinigods-community-pass-4260',
    name: 'InfiniGods Community Pass #4260',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4259',
    name: 'InfiniGods Community Pass #4259',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4258',
    name: 'InfiniGods Community Pass #4258',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4255',
    name: 'InfiniGods Community Pass #4255',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4257',
    name: 'InfiniGods Community Pass #4257',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4256',
    name: 'InfiniGods Community Pass #4256',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4254',
    name: 'InfiniGods Community Pass #4254',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4253',
    name: 'InfiniGods Community Pass #4253',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4252',
    name: 'InfiniGods Community Pass #4252',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
 {
    id: 'infinigods-community-pass-4251',
    name: 'InfiniGods Community Pass #4251',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4250',
    name: 'InfiniGods Community Pass #4250',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4249',
    name: 'InfiniGods Community Pass #4249',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4248',
    name: 'InfiniGods Community Pass #4248',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4246',
    name: 'InfiniGods Community Pass #4246',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4247',
    name: 'InfiniGods Community Pass #4247',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4245',
    name: 'InfiniGods Community Pass #4245',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4244',
    name: 'InfiniGods Community Pass #4244',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4243',
    name: 'InfiniGods Community Pass #4243',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4242',
    name: 'InfiniGods Community Pass #4242',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4241',
    name: 'InfiniGods Community Pass #4241',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4240',
    name: 'InfiniGods Community Pass #4240',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4239',
    name: 'InfiniGods Community Pass #4239',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4238',
    name: 'InfiniGods Community Pass #4238',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4237',
    name: 'InfiniGods Community Pass #4237',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4236',
    name: 'InfiniGods Community Pass #4236',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4234',
    name: 'InfiniGods Community Pass #4234',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4235',
    name: 'InfiniGods Community Pass #4235',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4233',
    name: 'InfiniGods Community Pass #4233',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4230',
    name: 'InfiniGods Community Pass #4230',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4232',
    name: 'InfiniGods Community Pass #4232',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4231',
    name: 'InfiniGods Community Pass #4231',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4229',
    name: 'InfiniGods Community Pass #4229',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4228',
    name: 'InfiniGods Community Pass #4228',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4227',
    name: 'InfiniGods Community Pass #4227',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4226',
    name: 'InfiniGods Community Pass #4226',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4225',
    name: 'InfiniGods Community Pass #4225',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4224',
    name: 'InfiniGods Community Pass #4224',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4223',
    name: 'InfiniGods Community Pass #4223',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4221',
    name: 'InfiniGods Community Pass #4221',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4219',
    name: 'InfiniGods Community Pass #4219',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4220',
    name: 'InfiniGods Community Pass #4220',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4222',
    name: 'InfiniGods Community Pass #4222',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4218',
    name: 'InfiniGods Community Pass #4218',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4216',
    name: 'InfiniGods Community Pass #4216',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4215',
    name: 'InfiniGods Community Pass #4215',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4214',
    name: 'InfiniGods Community Pass #4214',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4217',
    name: 'InfiniGods Community Pass #4217',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4213',
    name: 'InfiniGods Community Pass #4213',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4211',
    name: 'InfiniGods Community Pass #4211',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4212',
    name: 'InfiniGods Community Pass #4212',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4210',
    name: 'InfiniGods Community Pass #4210',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4209',
    name: 'InfiniGods Community Pass #4209',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4208',
    name: 'InfiniGods Community Pass #4208',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4207',
    name: 'InfiniGods Community Pass #4207',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4204',
    name: 'InfiniGods Community Pass #4204',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4205',
    name: 'InfiniGods Community Pass #4205',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4206',
    name: 'InfiniGods Community Pass #4206',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4203',
    name: 'InfiniGods Community Pass #4203',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4202',
    name: 'InfiniGods Community Pass #4202',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4201',
    name: 'InfiniGods Community Pass #4201',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4199',
    name: 'InfiniGods Community Pass #4199',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4200',
    name: 'InfiniGods Community Pass #4200',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4198',
    name: 'InfiniGods Community Pass #4198',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4196',
    name: 'InfiniGods Community Pass #4196',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4197',
    name: 'InfiniGods Community Pass #4197',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4194',
    name: 'InfiniGods Community Pass #4194',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4195',
    name: 'InfiniGods Community Pass #4195',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4193',
    name: 'InfiniGods Community Pass #4193',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4147',
    name: 'InfiniGods Community Pass #4147',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4146',
    name: 'InfiniGods Community Pass #4146',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4145',
    name: 'InfiniGods Community Pass #4145',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4144',
    name: 'InfiniGods Community Pass #4144',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4143',
    name: 'InfiniGods Community Pass #4143',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4192',
    name: 'InfiniGods Community Pass #4192',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4190',
    name: 'InfiniGods Community Pass #4190',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4189',
    name: 'InfiniGods Community Pass #4189',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4191',
    name: 'InfiniGods Community Pass #4191',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4188',
    name: 'InfiniGods Community Pass #4188',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4185',
    name: 'InfiniGods Community Pass #4185',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4187',
    name: 'InfiniGods Community Pass #4187',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4184',
    name: 'InfiniGods Community Pass #4184',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4186',
    name: 'InfiniGods Community Pass #4186',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4183',
    name: 'InfiniGods Community Pass #4183',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4181',
    name: 'InfiniGods Community Pass #4181',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4180',
    name: 'InfiniGods Community Pass #4180',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4182',
    name: 'InfiniGods Community Pass #4182',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4179',
    name: 'InfiniGods Community Pass #4179',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4178',
    name: 'InfiniGods Community Pass #4178',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4174',
    name: 'InfiniGods Community Pass #4174',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4175',
    name: 'InfiniGods Community Pass #4175',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4177',
    name: 'InfiniGods Community Pass #4177',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4176',
    name: 'InfiniGods Community Pass #4176',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4173',
    name: 'InfiniGods Community Pass #4173',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4172',
    name: 'InfiniGods Community Pass #4172',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4171',
    name: 'InfiniGods Community Pass #4171',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4170',
    name: 'InfiniGods Community Pass #4170',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4169',
    name: 'InfiniGods Community Pass #4169',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4168',
    name: 'InfiniGods Community Pass #4168',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4165',
    name: 'InfiniGods Community Pass #4165',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  },
  {
    id: 'infinigods-community-pass-4166',
    name: 'InfiniGods Community Pass #4166',
    image: 'https://ipfs.io/ipfs/QmQR4aAeK2a4CPnEsDVCxZQLCLUjwskctHEvSTkko6ByrU'
  }

             ];
        actualNftData = InfiniGodsNFTs.find(nft => nft.id.toString() === itemId);
                 
                      } else if (collectionId === 'cool-cats') {
        const coolcatsNFTs = [
            
  {
    id: 'coolcat-9967',
    name: 'Cool Cats #9967',
    image: 'https://ipfs.io/ipfs/QmYFag7t4K6xQARUpdNkdYyBg8n6YGck9MFXQyJZUys5LZ'
  },
  {
    id: 'coolcat-9966',
    name: 'Cool Cats #9966',
    image: 'https://ipfs.io/ipfs/QmfM8XHMtELiQXwqFKTC6JWXKRyjaCESfrMZEbXVofvuUc'
  },
  {
    id: 'coolcat-9965',
    name: 'Cool Cats #9965',
    image: 'https://ipfs.io/ipfs/QmTNwNatMSBU9ArcV1DMUAGMqeAmnY8Akf6vPXmdZA4gqE'
  },
  {
    id: 'coolcat-9964',
    name: 'Cool Cats #9964',
    image: 'https://ipfs.io/ipfs/QmTPnZnQpHeFMWQeAP8UoN6ETCtQ9VtG2PrbnVi9B8KZHg'
  },
  {
    id: 'coolcat-9963',
    name: 'Cool Cats #9963',
    image: 'https://ipfs.io/ipfs/QmeeC1MsoMFiPqwrmRC88MJ3ZBbs5ze4fvVoBKJfL6TgsS'
  },
  {
    id: 'coolcat-9962',
    name: 'Cool Cats #9962',
    image: 'https://ipfs.io/ipfs/QmTEiqyfTAJ3TgvrUWPXejBcMJSCgo6mLgoZSNo1Fzg3kx'
  },
  {
    id: 'coolcat-9961',
    name: 'Cool Cats #9961',
    image: 'https://ipfs.io/ipfs/QmYMim9RhVuWboy9PKaVfKgtGaPJJW46DtcENxtG1hB8wW'
  },
  {
    id: 'coolcat-9959',
    name: 'Cool Cats #9959',
    image: 'https://ipfs.io/ipfs/QmQLAXZd5sjNEzGNnUEPsEXQM6hYXTJv9CMRG5fFvntUhg'
  },
  {
    id: 'coolcat-9958',
    name: 'Cool Cats #9958',
    image: 'https://ipfs.io/ipfs/QmNxARHUe1sJ4TNKeaJgPyBdTVPdpHtg7fg6dmU3UXFgR7'
  },
  {
    id: 'coolcat-9957',
    name: 'Cool Cats #9957',
    image: 'https://ipfs.io/ipfs/QmPoRxdCFZYfCH4vqzzPTQfowS1BAmg47mTP8MLaJvCTUH'
  },
  {
    id: 'coolcat-9956',
    name: 'Cool Cats #9956',
    image: 'https://ipfs.io/ipfs/QmaTeFTiHRExBCWwgdcHZ2QGufY8MSEPiUicmPXJPY8vqc'
  },
  {
    id: 'coolcat-9955',
    name: 'Cool Cats #9955',
    image: 'https://ipfs.io/ipfs/QmcsQg42kNWSn9hqYAVfxwyhm8EmZSVNe7xAYmrHF4HcSb'
  },
  {
    id: 'coolcat-9954',
    name: 'Cool Cats #9954',
    image: 'https://ipfs.io/ipfs/QmaHEtG6QayMCSWKi1kqce7Tb3BPPVFmezbR8woDiVPS9J'
  },
  {
    id: 'coolcat-9953',
    name: 'Cool Cats #9953',
    image: 'https://ipfs.io/ipfs/QmNnWJnrCCJ4SgHuSPSfsW3g6knbULBsV3QKZXXss8P1GN'
  },
  {
    id: 'coolcat-9952',
    name: 'Cool Cats #9952',
    image: 'https://ipfs.io/ipfs/Qmad87dBsu9xMMr6aQhKaWSjdKchrotMJ4WguJbffjEKY6'
  },
  {
    id: 'coolcat-9951',
    name: 'Cool Cats #9951',
    image: 'https://ipfs.io/ipfs/QmUR52Tnb6DZX83He3gwyjgDbKRXdaDPA6DV72GQx6QRJb'
  },
  {
    id: 'coolcat-9950',
    name: 'Cool Cats #9950',
    image: 'https://ipfs.io/ipfs/QmdcKDBVY8xgdZou8vUXxdaZdErgj92bkPEnJFyjzkYNGm'
  },
  {
    id: 'coolcat-9949',
    name: 'Cool Cats #9949',
    image: 'https://ipfs.io/ipfs/QmS9aieLQLsfmNZktw6mLKrx9bshb4up6qrrLTCDPntPe7'
  },
  {
    id: 'coolcat-9948',
    name: 'Cool Cats #9948',
    image: 'https://ipfs.io/ipfs/QmYk7an6K5sZ3iA66xYumM7KU8pdnQc213rq4QCMXd9YGz'
  },
  {
    id: 'coolcat-9947',
    name: 'Cool Cats #9947',
    image: 'https://ipfs.io/ipfs/QmaY6LpcxDvbTcP6Ew5ssJCuEnHnk5x9BMNGrqJXoZMFdB'
  },
  {
    id: 'coolcat-9946',
    name: 'Cool Cats #9946',
    image: 'https://ipfs.io/ipfs/QmSzR3NmT7uZrA32GyJKpNNfxoRmndcS7m3U1vRhA8c3wf'
  },
  {
    id: 'coolcat-9945',
    name: 'Cool Cats #9945',
    image: 'https://ipfs.io/ipfs/QmXEqcPJomFRECJQsYGLciWKgdBNW2PcC43nybdN66vtXt'
  },
  {
    id: 'coolcat-9944',
    name: 'Cool Cats #9944',
    image: 'https://ipfs.io/ipfs/QmbeG7NbqE35ZrexkFMqQMwYcaWZsvaeWnzQx3KVwZk56J'
  },
  {
    id: 'coolcat-9943',
    name: 'Cool Cats #9943',
    image: 'https://ipfs.io/ipfs/QmTT6MqVDNDu1XxLnqQj1w94hpr74EybYB8LDutjQ2Xdv2'
  },
  {
    id: 'coolcat-9942',
    name: 'Cool Cats #9942',
    image: 'https://ipfs.io/ipfs/QmbxUygCQwvSkQHMkJ66D5JrLJ6ZvFmwzFX7xFiP17Lz4y'
  },
  {
    id: 'coolcat-9941',
    name: 'Cool Cats #9941',
    image: 'https://ipfs.io/ipfs/QmWWNL1a3dWu61dqtF2ijcWYFNUsqHajHd7Nf9UUgRfTBn'
  },
  {
    id: 'coolcat-9940',
    name: 'Cool Cats #9940',
    image: 'https://ipfs.io/ipfs/QmQrkcVigNbfjRg4abG4v38mxwiG89oSj9CVmpvWPiKxDK'
  },
  {
    id: 'coolcat-9939',
    name: 'Cool Cats #9939',
    image: 'https://ipfs.io/ipfs/QmfU3W34i8UEJt6gDm5h62CkD69VJKmvT4ipGJLLWJLWsB'
  },
  {
    id: 'coolcat-9938',
    name: 'Cool Cats #9938',
    image: 'https://ipfs.io/ipfs/QmXztCuxcLLiKgNL9Yfsgynte9a4iQV82MBqm7bMcudpzf'
  },
  {
    id: 'coolcat-9937',
    name: 'Cool Cats #9937',
    image: 'https://ipfs.io/ipfs/Qmbrot1gfTKf7Uxi2swPPm6ctAnej9wxGyUWsaqUZ8KjKB'
  },
  {
    id: 'coolcat-9936',
    name: 'Cool Cats #9936',
    image: 'https://ipfs.io/ipfs/QmUDtAWgj6gTRB5vmvGwAfQ3HKU5epRQD1z98ZCqPdN1pH'
  },
  {
    id: 'coolcat-9935',
    name: 'Cool Cats #9935',
    image: 'https://ipfs.io/ipfs/QmctjXwRrbgHYo4fF7EKnhsGCNDeeyo1SWRHhBUZAWpVZq'
  },
  {
    id: 'coolcat-9934',
    name: 'Cool Cats #9934',
    image: 'https://ipfs.io/ipfs/QmXya8aFyDpYmVx9V6HHFzKHXyUozmeMVgQBiArBTmXvRT'
  },
  {
    id: 'coolcat-9933',
    name: 'Cool Cats #9933',
    image: 'https://ipfs.io/ipfs/QmSBphevBkQdAPj1W5Nnr7yzrvSyLckJVPYbvJBqYLR7Kd'
  },
  {
    id: 'coolcat-9932',
    name: 'Cool Cats #9932',
    image: 'https://ipfs.io/ipfs/QmWPt1xN7TPuFKgXCoYbMSuz26uxxBfgLGDAsHYD3V2Ncb'
  },
  {
    id: 'coolcat-9931',
    name: 'Cool Cats #9931',
    image: 'https://ipfs.io/ipfs/QmarPFzo4UGAuHsZqaNASQX3KRmA9ynqdNDPxqLSQvJcnd'
  },
  {
    id: 'coolcat-9930',
    name: 'Cool Cats #9930',
    image: 'https://ipfs.io/ipfs/QmSWZkhETDZUr7Wmf3jpnvmN46pfJmjdGNM4Rfkd9rKLar'
  },
  {
    id: 'coolcat-9929',
    name: 'Cool Cats #9929',
    image: 'https://ipfs.io/ipfs/QmVrYtmxpF5C5VFMz7AKtyMn79NFBgKCLkWoHYc2koP7gx'
  },
  {
    id: 'coolcat-9928',
    name: 'Cool Cats #9928',
    image: 'https://ipfs.io/ipfs/QmUUyhN5FgRJ4HUihnQPMEcynemvXvGkFoDKPypzGxZmDL'
  },
  {
    id: 'coolcat-9927',
    name: 'Cool Cats #9927',
    image: 'https://ipfs.io/ipfs/QmV1q1yvxPFyigfszZt1HbgaHVGnasGQrFFFjG85LDJoAu'
  },
  {
    id: 'coolcat-9926',
    name: 'Cool Cats #9926',
    image: 'https://ipfs.io/ipfs/QmNg1xnn6RkvQAUP8RTZbychW64Z8o89Ejamz2jhXVKikx'
  },
  {
    id: 'coolcat-9925',
    name: 'Cool Cats #9925',
    image: 'https://ipfs.io/ipfs/QmS5E7NJpsA5CSJDDhJ6WpnEWpVyPVTawVaunfcGcPCgkz'
  },
  {
    id: 'coolcat-9924',
    name: 'Cool Cats #9924',
    image: 'https://ipfs.io/ipfs/QmU3vX7fDfYXzd2tVxLNKxk339nuEUEciirwD8TWsmk8hg'
  },
  {
    id: 'coolcat-9923',
    name: 'Cool Cats #9923',
    image: 'https://ipfs.io/ipfs/QmTyBkdG2C7dq7Amx3hUP6MEDtUAWNjibqGxwx23MhVgmf'
  },
  {
    id: 'coolcat-9922',
    name: 'Cool Cats #9922',
    image: 'https://ipfs.io/ipfs/QmR7U8h4oVmpZvKhC1MQfh4aNHD2vXfArSy8rtb76MUycm'
  },
  {
    id: 'coolcat-9921',
    name: 'Cool Cats #9921',
    image: 'https://ipfs.io/ipfs/QmPQDN2ULif8yhHcSp6m5Vfvkzutc1p5hmxmYNSLDteRRH'
  },
  {
    id: 'coolcat-9920',
    name: 'Cool Cats #9920',
    image: 'https://ipfs.io/ipfs/QmeG3tc2wjcrs42Ewr3GXgomQ1gX4NveVZzUEwbZrfEzSi'
  },
  {
    id: 'coolcat-9919',
    name: 'Cool Cats #9919',
    image: 'https://ipfs.io/ipfs/QmXo3jbr6jyDAziqPCge1L6dUVuQbajo9rmAw3Hw3ebjsX'
  },
  {
    id: 'coolcat-9918',
    name: 'Cool Cats #9918',
    image: 'https://ipfs.io/ipfs/QmYyEYzD1M9TFJ1NUnmuBHB89g5v4fDfCoBGVSzYvbX3NZ'
  },
  {
    id: 'coolcat-9917',
    name: 'Cool Cats #9917',
    image: 'https://ipfs.io/ipfs/QmQE8DQJrPegrfzaFyMRugkmbC8fCFzrrD49Mmb9KN8cFE'
  },
  {
    id: 'coolcat-9916',
    name: 'Cool Cats #9916',
    image: 'https://ipfs.io/ipfs/QmUgSucqJvExJzXBzkUHyHSZK18a1qrB7MH5mepaA65c9A'
  },
  {
    id: 'coolcat-9915',
    name: 'Cool Cats #9915',
    image: 'https://ipfs.io/ipfs/QmaL3NQnXWYWfhPNBhn1eFEUsrbrhYfdtvQXck2dTyWsEb'
  },
  {
    id: 'coolcat-9914',
    name: 'Cool Cats #9914',
    image: 'https://ipfs.io/ipfs/QmRq1DkEQRCkLzZe6jwutNpWEmRvAvjyphxzJLcoZAHdhs'
  },
  {
    id: 'coolcat-9913',
    name: 'Cool Cats #9913',
    image: 'https://ipfs.io/ipfs/QmUDUFZZwZxXkGNKbqr2Yhyw91RKo8vkMTYFGn3jtMhMdk'
  },
  {
    id: 'coolcat-9912',
    name: 'Cool Cats #9912',
    image: 'https://ipfs.io/ipfs/QmYjHXZjpPTL6DGN29h1eP7FYbrHjmRD9EwwZkm2C38vjU'
  },
  {
    id: 'coolcat-9911',
    name: 'Cool Cats #9911',
    image: 'https://ipfs.io/ipfs/QmdhDxJdSnRJwg31sPTkHEMT1PmMxGc75k4MYgu5yg7sBh'
  },
  {
    id: 'coolcat-9910',
    name: 'Cool Cats #9910',
    image: 'https://ipfs.io/ipfs/QmYdgRGwnRkwPYMareTRjLWSVZzY4LLKRchUpy9TtSTEhS'
  },
  {
    id: 'coolcat-9909',
    name: 'Cool Cats #9909',
    image: 'https://ipfs.io/ipfs/QmYgwQoEipSfaeEQS8k5ny4K8Fh6ddhLuJHfZmnDNtLCRG'
  },
  {
    id: 'coolcat-9908',
    name: 'Cool Cats #9908',
    image: 'https://ipfs.io/ipfs/QmdVbSKthcXH29PViY2hpdZyz4HGJk1JfuVf33pYAquAXu'
  },
  {
    id: 'coolcat-9907',
    name: 'Cool Cats #9907',
    image: 'https://ipfs.io/ipfs/QmZ9K6naSsL3WTQWxWxJiLAMU6VeVHa5XUwPrVQi5VYPea'
  },
  {
    id: 'coolcat-9890',
    name: 'Cool Cats #9890',
    image: 'https://ipfs.io/ipfs/QmZwgM7wZvaE9Yu2o9UaDhCmfFSWZnUdcr6GxbVK1WvqQC'
  },
  {
    id: 'coolcat-9889',
    name: 'Cool Cats #9889',
    image: 'https://ipfs.io/ipfs/QmURSTGgVZu8NXw5bo5Xggfx5T6AtzK9vTsvepK4ESVHyh'
  },
  {
    id: 'coolcat-9888',
    name: 'Cool Cats #9888',
    image: 'https://ipfs.io/ipfs/QmT4XKRAA9V8WZDYUAghyr1E29Fiizcbya8nxxK7xtbRMm'
  },
  {
    id: 'coolcat-9887',
    name: 'Cool Cats #9887',
    image: 'https://ipfs.io/ipfs/QmchRoQbCES99CDHTsVoLZ2zTvjKHeqhtdBwyZoRRADysK'
  },
  {
    id: 'coolcat-9886',
    name: 'Cool Cats #9886',
    image: 'https://ipfs.io/ipfs/QmRvBE1xiV4YcDPkF6GUdfQmvK1b9AmGCzCdNRr9cXKBQE'
  },
  {
    id: 'coolcat-9885',
    name: 'Cool Cats #9885',
    image: 'https://ipfs.io/ipfs/QmZkoTN9zpMW7o6ZuaTfUFTErHAvzvnWp3cHoF78DQmnUb'
  },
  {
    id: 'coolcat-9884',
    name: 'Cool Cats #9884',
    image: 'https://ipfs.io/ipfs/Qmc4cePRYp2exMARdKgAvzmANGHjnpDNbStMxqmL7Guvyb'
  },
  {
    id: 'coolcat-9883',
    name: 'Cool Cats #9883',
    image: 'https://ipfs.io/ipfs/QmaUj7N62dzdSbPC6gqMutHms1MFo7zAwg4W8Fb8Jyy3az'
  },
  {
    id: 'coolcat-9882',
    name: 'Cool Cats #9882',
    image: 'https://ipfs.io/ipfs/QmeJ8ouPthrHr8VxbAq4EbEHgNvbw2n4pyxCA6PM9Jpke4'
  },
  {
    id: 'coolcat-9881',
    name: 'Cool Cats #9881',
    image: 'https://ipfs.io/ipfs/QmQLHtkPua4VnHL55kHpckKxxcMpxBFguQNaGNjJDMXg71'
  },
  {
    id: 'coolcat-9880',
    name: 'Cool Cats #9880',
    image: 'https://ipfs.io/ipfs/QmcTgFetoUfKZPggfDS58QDkwU4Z2yEGLKpqaUczdAJCay'
  },
  {
    id: 'coolcat-9879',
    name: 'Cool Cats #9879',
    image: 'https://ipfs.io/ipfs/QmTy4ofnLvLgTZ9EGLeKLTSYfwC7HoHhQvNbevgrgAsJWB'
  },
  {
    id: 'coolcat-9906',
    name: 'Cool Cats #9906',
    image: 'https://ipfs.io/ipfs/QmYW21SyVe8y4EkhpDMqh9cKRmYtSMvfLsMFztXm6upQnF'
  },
  {
    id: 'coolcat-9858',
    name: 'Cool Cats #9858',
    image: 'https://ipfs.io/ipfs/QmaStCwAq5BFqjePbcxWBRgjvVU2DStddSRFVPcsFLLU58'
  },
  {
    id: 'coolcat-9873',
    name: 'Cool Cats #9873',
    image: 'https://ipfs.io/ipfs/QmZ8G4464rd97bgk6A26ke3M8RjEBkzqF8NPyKmqwysK5c'
  },
  {
    id: 'coolcat-9878',
    name: 'Cool Cats #9878',
    image: 'https://ipfs.io/ipfs/QmT1XtRUYMbRXKfF8emWcUkKU9YFX56CabBrFFaXja6azC'
  },
  {
    id: 'coolcat-9905',
    name: 'Cool Cats #9905',
    image: 'https://ipfs.io/ipfs/QmfRhfAdDJNuLaUVdnsq1MzreGuB8sSBS8qTJeHn6TwddM'
  },
  {
    id: 'coolcat-9894',
    name: 'Cool Cats #9894',
    image: 'https://ipfs.io/ipfs/QmQFJsWBiQkCZe8UPSzg2NwQi3JxixSf727Hv7Veouzp8r'
  },
  {
    id: 'coolcat-9857',
    name: 'Cool Cats #9857',
    image: 'https://ipfs.io/ipfs/QmRjqS7iavUDW5NwSYKRcw6BE9zq3MyZAvQXZ3pTafY3sw'
  },
  {
    id: 'coolcat-9868',
    name: 'Cool Cats #9868',
    image: 'https://ipfs.io/ipfs/QmZ6ZfZjG15N6UF7eY3CFaZj5Np4FuCBnbpEaQa1RPZNMj'
  },
  {
    id: 'coolcat-9877',
    name: 'Cool Cats #9877',
    image: 'https://ipfs.io/ipfs/QmSw3vjMn39ENgeBqGgmryjVCJ9u8e7Ca6VBZ2pDjMnVdr'
  },
  {
    id: 'coolcat-9872',
    name: 'Cool Cats #9872',
    image: 'https://ipfs.io/ipfs/QmeySjBidrpkvYYWBDcYzbmhqXQkey3dkJNc6GKvyqjrEB'
  },
  {
    id: 'coolcat-9902',
    name: 'Cool Cats #9902',
    image: 'https://ipfs.io/ipfs/QmWSitYjf1t4MTRA5iwqwVQXx1wW5fzUKRcCAfvpaFWU2s'
  },
  {
    id: 'coolcat-9898',
    name: 'Cool Cats #9898',
    image: 'https://ipfs.io/ipfs/QmXMzbkQZLawMPQ8BP8bCiG1Er5Gxg5tWMghZh9obNuzWU'
  },
  {
    id: 'coolcat-9904',
    name: 'Cool Cats #9904',
    image: 'https://ipfs.io/ipfs/QmerD9vVVKPV6XsU7h2N7qcx1oF4TZ1qghie5eJAjTf8vQ'
  },
  {
    id: 'coolcat-9903',
    name: 'Cool Cats #9903',
    image: 'https://ipfs.io/ipfs/QmdedcwJw2mep8BnFEY5uuCoFA57MyxH8cHbX45dAaPdRW'
  },
  {
    id: 'coolcat-9856',
    name: 'Cool Cats #9856',
    image: 'https://ipfs.io/ipfs/QmZyCHq2y5qdRATtzRGjAoJqJpEWTz33MqDABs43DLjWAR'
  },
  {
    id: 'coolcat-9867',
    name: 'Cool Cats #9867',
    image: 'https://ipfs.io/ipfs/Qma34zAkXxVCdoiTgWTw6vVKrhRm6JVzhShJ2zsQnTkN5g'
  },
  {
    id: 'coolcat-9893',
    name: 'Cool Cats #9893',
    image: 'https://ipfs.io/ipfs/QmecYdKvg6pWK6PPemUYUnJ7uAdGSNUHbu3Xfog7zQbwkD'
  },
  {
    id: 'coolcat-9876',
    name: 'Cool Cats #9876',
    image: 'https://ipfs.io/ipfs/QmX86ZFXwz1NR8NvaPDg3rcc8iuYpN1kpYB1Q29PaYWKmM'
  },
  {
    id: 'coolcat-9871',
    name: 'Cool Cats #9871',
    image: 'https://ipfs.io/ipfs/QmRULgdBh9G3T46uuR2AtVxZKmTmDXeUazYMNcKyaWHWJM'
  },
  {
    id: 'coolcat-9899',
    name: 'Cool Cats #9899',
    image: 'https://ipfs.io/ipfs/QmQuygDVypQ8dnuRayBGVxnB4b1EUDESZaugxido2QNKKk'
  },
  {
    id: 'coolcat-9901',
    name: 'Cool Cats #9901',
    image: 'https://ipfs.io/ipfs/QmWESPYdg8cST3vXVZ9SWCe7nnbkThfrMaG5Yf6fvwDPEs'
  },
  {
    id: 'coolcat-9900',
    name: 'Cool Cats #9900',
    image: 'https://ipfs.io/ipfs/QmVejEABRh6DECHFV6eT8AMZ9p8brsnd5ATD8mWLerPUYT'
  },
  {
    id: 'coolcat-9897',
    name: 'Cool Cats #9897',
    image: 'https://ipfs.io/ipfs/QmX7bgawnjZwnB6ZbGNGeUFMHoCjyhqE5wHbtsSQgXmRnF'
  },
  {
    id: 'coolcat-9896',
    name: 'Cool Cats #9896',
    image: 'https://ipfs.io/ipfs/QmdN47n4yPc6SAmDV5y5fxafB2KApXBehnt1yhzMM9HxPJ'
  },
  {
    id: 'coolcat-9855',
    name: 'Cool Cats #9855',
    image: 'https://ipfs.io/ipfs/QmPtSaXgaCaz3aWMPTZtTKowkZcHZHGh3Jm7qYxtMxCRxr'
  },
  {
    id: 'coolcat-9866',
    name: 'Cool Cats #9866',
    image: 'https://ipfs.io/ipfs/QmZ7oEk1n8KMBv54cJRq48iFDwLLDt9EDVkEV7VNC1LzPh'
  },
  {
    id: 'coolcat-9892',
    name: 'Cool Cats #9892',
    image: 'https://ipfs.io/ipfs/QmZwSnZLAsq62KZz5gYfHS9ZVrdYeEFmNQxiq92vsrV8KQ'
  }
            
            ];
        actualNftData = coolcatsNFTs.find(nft => nft.id.toString() === itemId);
                     
                      } else if (collectionId === 'potatoz') {
        const potatozNFTs = [
            
  {
    "id": "potatoz-9998",
    "name": "Potatoz #9998",
    "image": "https://grow-api.memeland.com/token/images/9998.gif"
  },
  {
    "id": "potatoz-9997",
    "name": "Potatoz #9997",
    "image": "https://grow-api.memeland.com/token/images/9997.gif"
  },
  {
    "id": "potatoz-9996",
    "name": "Potatoz #9996",
    "image": "https://grow-api.memeland.com/token/images/9996.gif"
  },
  {
    "id": "potatoz-9995",
    "name": "Potatoz #9995",
    "image": "https://grow-api.memeland.com/token/images/9995.gif"
  },
  {
    "id": "potatoz-9994",
    "name": "Potatoz #9994",
    "image": "https://grow-api.memeland.com/token/images/9994.gif"
  },
  {
    "id": "potatoz-9993",
    "name": "Potatoz #9993",
    "image": "https://grow-api.memeland.com/token/images/9993.gif"
  },
  {
    "id": "potatoz-9992",
    "name": "Potatoz #9992",
    "image": "https://grow-api.memeland.com/token/images/9992.gif"
  },
  {
    "id": "potatoz-9991",
    "name": "Potatoz #9991",
    "image": "https://grow-api.memeland.com/token/images/9991.gif"
  },
  {
    "id": "potatoz-9990",
    "name": "Potatoz #9990",
    "image": "https://grow-api.memeland.com/token/images/9990.gif"
  },
  {
    "id": "potatoz-9989",
    "name": "Potatoz #9989",
    "image": "https://grow-api.memeland.com/token/images/9989.gif"
  },
  {
    "id": "potatoz-9988",
    "name": "Potatoz #9988",
    "image": "https://grow-api.memeland.com/token/images/9988.gif"
  },
  {
    "id": "potatoz-9987",
    "name": "Potatoz #9987",
    "image": "https://grow-api.memeland.com/token/images/9987.gif"
  },
  {
    "id": "potatoz-9986",
    "name": "Potatoz #9986",
    "image": "https://grow-api.memeland.com/token/images/9986.gif"
  },
  {
    "id": "potatoz-9985",
    "name": "Potatoz #9985",
    "image": "https://grow-api.memeland.com/token/images/9985.gif"
  },
  {
    "id": "potatoz-9984",
    "name": "Potatoz #9984",
    "image": "https://grow-api.memeland.com/token/images/9984.gif"
  },
  {
    "id": "potatoz-9983",
    "name": "Potatoz #9983",
    "image": "https://grow-api.memeland.com/token/images/9983.gif"
  },
  {
    "id": "potatoz-9982",
    "name": "Potatoz #9982",
    "image": "https://grow-api.memeland.com/token/images/9982.gif"
  },
  {
    "id": "potatoz-9981",
    "name": "Potatoz #9981",
    "image": "https://grow-api.memeland.com/token/images/9981.gif"
  },
  {
    "id": "potatoz-9980",
    "name": "Potatoz #9980",
    "image": "https://grow-api.memeland.com/token/images/9980.gif"
  },
  {
    "id": "potatoz-9979",
    "name": "Potatoz #9979",
    "image": "https://grow-api.memeland.com/token/images/9979.gif"
  },
  {
    "id": "potatoz-9978",
    "name": "Potatoz #9978",
    "image": "https://grow-api.memeland.com/token/images/9978.gif"
  },
  {
    "id": "potatoz-9977",
    "name": "Potatoz #9977",
    "image": "https://grow-api.memeland.com/token/images/9977.gif"
  },
  {
    "id": "potatoz-9976",
    "name": "Potatoz #9976",
    "image": "https://grow-api.memeland.com/token/images/9976.gif"
  },
  {
    "id": "potatoz-9975",
    "name": "Potatoz #9975",
    "image": "https://grow-api.memeland.com/token/images/9975.gif"
  },
  {
    "id": "potatoz-9974",
    "name": "Potatoz #9974",
    "image": "https://grow-api.memeland.com/token/images/9974.gif"
  },
  {
    "id": "potatoz-9973",
    "name": "Potatoz #9973",
    "image": "https://grow-api.memeland.com/token/images/9973.gif"
  },
  {
    "id": "potatoz-9972",
    "name": "Potatoz #9972",
    "image": "https://grow-api.memeland.com/token/images/9972.gif"
  },
  {
    "id": "potatoz-9971",
    "name": "Potatoz #9971",
    "image": "https://grow-api.memeland.com/token/images/9971.gif"
  },
  {
    "id": "potatoz-9970",
    "name": "Potatoz #9970",
    "image": "https://grow-api.memeland.com/token/images/9970.gif"
  },
  {
    "id": "potatoz-9969",
    "name": "Potatoz #9969",
    "image": "https://grow-api.memeland.com/token/images/9969.gif"
  },
  {
    "id": "potatoz-9968",
    "name": "Potatoz #9968",
    "image": "https://grow-api.memeland.com/token/images/9968.gif"
  },
  {
    "id": "potatoz-9967",
    "name": "Potatoz #9967",
    "image": "https://grow-api.memeland.com/token/images/9967.gif"
  },
  {
    "id": "potatoz-9966",
    "name": "Potatoz #9966",
    "image": "https://grow-api.memeland.com/token/images/9966.gif"
  },
  {
    "id": "potatoz-9965",
    "name": "Potatoz #9965",
    "image": "https://grow-api.memeland.com/token/images/9965.gif"
  },
  {
    "id": "potatoz-9964",
    "name": "Potatoz #9964",
    "image": "https://grow-api.memeland.com/token/images/9964.gif"
  },
  {
    "id": "potatoz-9963",
    "name": "Potatoz #9963",
    "image": "https://grow-api.memeland.com/token/images/9963.gif"
  },
  {
    "id": "potatoz-9962",
    "name": "Potatoz #9962",
    "image": "https://grow-api.memeland.com/token/images/9962.gif"
  },
  {
    "id": "potatoz-9961",
    "name": "Potatoz #9961",
    "image": "https://grow-api.memeland.com/token/images/9961.gif"
  },
  {
    "id": "potatoz-9960",
    "name": "Potatoz #9960",
    "image": "https://grow-api.memeland.com/token/images/9960.gif"
  },
  {
    "id": "potatoz-9959",
    "name": "Potatoz #9959",
    "image": "https://grow-api.memeland.com/token/images/9959.gif"
  },
  {
    "id": "potatoz-9958",
    "name": "Potatoz #9958",
    "image": "https://grow-api.memeland.com/token/images/9958.gif"
  },
  {
    "id": "potatoz-9957",
    "name": "Potatoz #9957",
    "image": "https://grow-api.memeland.com/token/images/9957.gif"
  },
  {
    "id": "potatoz-9956",
    "name": "Potatoz #9956",
    "image": "https://grow-api.memeland.com/token/images/9956.gif"
  },
  {
    "id": "potatoz-9955",
    "name": "Potatoz #9955",
    "image": "https://grow-api.memeland.com/token/images/9955.gif"
  },
  {
    "id": "potatoz-9954",
    "name": "Potatoz #9954",
    "image": "https://grow-api.memeland.com/token/images/9954.gif"
  },
  {
    "id": "potatoz-9953",
    "name": "Potatoz #9953",
    "image": "https://grow-api.memeland.com/token/images/9953.gif"
  },
  {
    "id": "potatoz-9952",
    "name": "Potatoz #9952",
    "image": "https://grow-api.memeland.com/token/images/9952.gif"
  },
  {
    "id": "potatoz-9951",
    "name": "Potatoz #9951",
    "image": "https://grow-api.memeland.com/token/images/9951.gif"
  },
  {
    "id": "potatoz-9950",
    "name": "Potatoz #9950",
    "image": "https://grow-api.memeland.com/token/images/9950.gif"
  },
  {
    "id": "potatoz-9949",
    "name": "Potatoz #9949",
    "image": "https://grow-api.memeland.com/token/images/9949.gif"
  },
  {
    "id": "potatoz-9948",
    "name": "Potatoz #9948",
    "image": "https://grow-api.memeland.com/token/images/9948.gif"
  },
  {
    "id": "potatoz-9947",
    "name": "Potatoz #9947",
    "image": "https://grow-api.memeland.com/token/images/9947.gif"
  },
  {
    "id": "potatoz-9946",
    "name": "Potatoz #9946",
    "image": "https://grow-api.memeland.com/token/images/9946.gif"
  },
  {
    "id": "potatoz-9945",
    "name": "Potatoz #9945",
    "image": "https://grow-api.memeland.com/token/images/9945.gif"
  },
  {
    "id": "potatoz-9944",
    "name": "Potatoz #9944",
    "image": "https://grow-api.memeland.com/token/images/9944.gif"
  },
  {
    "id": "potatoz-9943",
    "name": "Potatoz #9943",
    "image": "https://grow-api.memeland.com/token/images/9943.gif"
  },
  {
    "id": "potatoz-9942",
    "name": "Potatoz #9942",
    "image": "https://grow-api.memeland.com/token/images/9942.gif"
  },
  {
    "id": "potatoz-9941",
    "name": "Potatoz #9941",
    "image": "https://grow-api.memeland.com/token/images/9941.gif"
  },
  {
    "id": "potatoz-9940",
    "name": "Potatoz #9940",
    "image": "https://grow-api.memeland.com/token/images/9940.gif"
  },
  {
    "id": "potatoz-9939",
    "name": "Potatoz #9939",
    "image": "https://grow-api.memeland.com/token/images/9939.gif"
  },
  {
    "id": "potatoz-9938",
    "name": "Potatoz #9938",
    "image": "https://grow-api.memeland.com/token/images/9938.gif"
  },
  {
    "id": "potatoz-9937",
    "name": "Potatoz #9937",
    "image": "https://grow-api.memeland.com/token/images/9937.gif"
  },
  {
    "id": "potatoz-9936",
    "name": "Potatoz #9936",
    "image": "https://grow-api.memeland.com/token/images/9936.gif"
  },
  {
    "id": "potatoz-9935",
    "name": "Potatoz #9935",
    "image": "https://grow-api.memeland.com/token/images/9935.gif"
  },
  {
    "id": "potatoz-9934",
    "name": "Potatoz #9934",
    "image": "https://grow-api.memeland.com/token/images/9934.gif"
  },
  {
    "id": "potatoz-9933",
    "name": "Potatoz #9933",
    "image": "https://grow-api.memeland.com/token/images/9933.gif"
  },
  {
    "id": "potatoz-9932",
    "name": "Potatoz #9932",
    "image": "https://grow-api.memeland.com/token/images/9932.gif"
  },
  {
    "id": "potatoz-9931",
    "name": "Potatoz #9931",
    "image": "https://grow-api.memeland.com/token/images/9931.gif"
  },
  {
    "id": "potatoz-9930",
    "name": "Potatoz #9930",
    "image": "https://grow-api.memeland.com/token/images/9930.gif"
  },
  {
    "id": "potatoz-9929",
    "name": "Potatoz #9929",
    "image": "https://grow-api.memeland.com/token/images/9929.gif"
  },
  {
    "id": "potatoz-9928",
    "name": "Potatoz #9928",
    "image": "https://grow-api.memeland.com/token/images/9928.gif"
  },
  {
    "id": "potatoz-9927",
    "name": "Potatoz #9927",
    "image": "https://grow-api.memeland.com/token/images/9927.gif"
  },
  {
    "id": "potatoz-9926",
    "name": "Potatoz #9926",
    "image": "https://grow-api.memeland.com/token/images/9926.gif"
  },
  {
    "id": "potatoz-9925",
    "name": "Potatoz #9925",
    "image": "https://grow-api.memeland.com/token/images/9925.gif"
  },
  {
    "id": "potatoz-9924",
    "name": "Potatoz #9924",
    "image": "https://grow-api.memeland.com/token/images/9924.gif"
  },
  {
    "id": "potatoz-9923",
    "name": "Potatoz #9923",
    "image": "https://grow-api.memeland.com/token/images/9923.gif"
  },
  {
    "id": "potatoz-9922",
    "name": "Potatoz #9922",
    "image": "https://grow-api.memeland.com/token/images/9922.gif"
  },
  {
    "id": "potatoz-9921",
    "name": "Potatoz #9921",
    "image": "https://grow-api.memeland.com/token/images/9921.gif"
  },
  {
    "id": "potatoz-9920",
    "name": "Potatoz #9920",
    "image": "https://grow-api.memeland.com/token/images/9920.gif"
  },
  {
    "id": "potatoz-9919",
    "name": "Potatoz #9919",
    "image": "https://grow-api.memeland.com/token/images/9919.gif"
  },
  {
    "id": "potatoz-9918",
    "name": "Potatoz #9918",
    "image": "https://grow-api.memeland.com/token/images/9918.gif"
  },
  {
    "id": "potatoz-9917",
    "name": "Potatoz #9917",
    "image": "https://grow-api.memeland.com/token/images/9917.gif"
  },
  {
    "id": "potatoz-9916",
    "name": "Potatoz #9916",
    "image": "https://grow-api.memeland.com/token/images/9916.gif"
  },
  {
    "id": "potatoz-9915",
    "name": "Potatoz #9915",
    "image": "https://grow-api.memeland.com/token/images/9915.gif"
  },
  {
    "id": "potatoz-9914",
    "name": "Potatoz #9914",
    "image": "https://grow-api.memeland.com/token/images/9914.gif"
  },
  {
    "id": "potatoz-9913",
    "name": "Potatoz #9913",
    "image": "https://grow-api.memeland.com/token/images/9913.gif"
  },
  {
    "id": "potatoz-9912",
    "name": "Potatoz #9912",
    "image": "https://grow-api.memeland.com/token/images/9912.gif"
  },
  {
    "id": "potatoz-9911",
    "name": "Potatoz #9911",
    "image": "https://grow-api.memeland.com/token/images/9911.gif"
  },
  {
    "id": "potatoz-9910",
    "name": "Potatoz #9910",
    "image": "https://grow-api.memeland.com/token/images/9910.gif"
  },
  {
    "id": "potatoz-9909",
    "name": "Potatoz #9909",
    "image": "https://grow-api.memeland.com/token/images/9909.gif"
  },
  {
    "id": "potatoz-9908",
    "name": "Potatoz #9908",
    "image": "https://grow-api.memeland.com/token/images/9908.gif"
  },
  {
    "id": "potatoz-9907",
    "name": "Potatoz #9907",
    "image": "https://grow-api.memeland.com/token/images/9907.gif"
  },
  {
    "id": "potatoz-9906",
    "name": "Potatoz #9906",
    "image": "https://grow-api.memeland.com/token/images/9906.gif"
  },
  {
    "id": "potatoz-9905",
    "name": "Potatoz #9905",
    "image": "https://grow-api.memeland.com/token/images/9905.gif"
  },
  {
    "id": "potatoz-9904",
    "name": "Potatoz #9904",
    "image": "https://grow-api.memeland.com/token/images/9904.gif"
  },
  {
    "id": "potatoz-9903",
    "name": "Potatoz #9903",
    "image": "https://grow-api.memeland.com/token/images/9903.gif"
  },
  {
    "id": "potatoz-9902",
    "name": "Potatoz #9902",
    "image": "https://grow-api.memeland.com/token/images/9902.gif"
  },
  {
    "id": "potatoz-9901",
    "name": "Potatoz #9901",
    "image": "https://grow-api.memeland.com/token/images/9901.gif"
  },
  {
    "id": "potatoz-9900",
    "name": "Potatoz #9900",
    "image": "https://grow-api.memeland.com/token/images/9900.gif"
  },
  {
    "id": "potatoz-9899",
    "name": "Potatoz #9899",
    "image": "https://grow-api.memeland.com/token/images/9899.gif"
  }
            ];
        actualNftData = potatozNFTs.find(nft => nft.id.toString() === itemId);
                      
                          } else if (collectionId === 'xpsr-24') {
        const xpsrNFTs = [
            
  {
    "id": "xpsr-4888",
    "name": "XPSR #4888",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3e10b8168f6ad432a2959defc5a942e5.png"
  },
  {
    "id": "xpsr-4887",
    "name": "XPSR #4887",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/44c6a79323ae89479276b0f53bb0fb32.png"
  },
  {
    "id": "xpsr-4885",
    "name": "XPSR #4885",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/cfaa12a77605628bc85cc7ff0a0ff471.png"
  },
  {
    "id": "xpsr-4886",
    "name": "XPSR #4886",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b9102ba1af1f210d2334d8c262d9bd24.png"
  },
  {
    "id": "xpsr-4884",
    "name": "XPSR #4884",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/c889a9519c8eb0936530a0cd567b8bdf.png"
  },
  {
    "id": "xpsr-4883",
    "name": "XPSR #4883",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3b95335cd88c19ae82dbd4329dbad755.png"
  },
  {
    "id": "xpsr-4882",
    "name": "XPSR #4882",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/293c3de8c464f2a3fed03b2841531e95.png"
  },
  {
    "id": "xpsr-4879",
    "name": "XPSR #4879",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b66c7d00c333fd6a92240e243314651d.png"
  },
  {
    "id": "xpsr-4880",
    "name": "XPSR #4880",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/481ce9cfd2c564902510e232751b42f8.png"
  },
  {
    "id": "xpsr-4881",
    "name": "XPSR #4881",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/f0845713fd05f4f0829fb8cb35cd0314.png"
  },
  {
    "id": "xpsr-4878",
    "name": "XPSR #4878",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3cb94a62c10a271541cd7d03949071ac.png"
  },
  {
    "id": "xpsr-4877",
    "name": "XPSR #4877",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/47199a9cbe498b16910cba5123a99d5d.png"
  },
  {
    "id": "xpsr-4876",
    "name": "XPSR #4876",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/34c40209bb3bc024addf9926a1c918b4.png"
  },
  {
    "id": "xpsr-4875",
    "name": "XPSR #4875",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/51177831f1b1d70a1c0d303024e0a8ef.png"
  },
  {
    "id": "xpsr-4874",
    "name": "XPSR #4874",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/451e4c1e4980701305a5fee07a79f775.png"
  },
  {
    "id": "xpsr-4872",
    "name": "XPSR #4872",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/388b0189d618ad389043d24f1207515a.png"
  },
  {
    "id": "xpsr-4871",
    "name": "XPSR #4871",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/23c3d8bbfb24254ba5d3037393670d19.png"
  },
  {
    "id": "xpsr-4873",
    "name": "XPSR #4873",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3c7a4af3f494b2a61305c5d1c392d500.png"
  },
  {
    "id": "xpsr-4870",
    "name": "XPSR #4870",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/cfa0bcacae3eb40ca58a5c1ed5112414.png"
  },
  {
    "id": "xpsr-4869",
    "name": "XPSR #4869",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/f17ad127098e18c266ef98f4880225db.png"
  },
  {
    "id": "xpsr-4868",
    "name": "XPSR #4868",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/e76161c70d784fbb0fafe795f158ec69.png"
  },
  {
    "id": "xpsr-4865",
    "name": "XPSR #4865",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/1248db64527ba1eadfddb770ca8f9013.png"
  },
  {
    "id": "xpsr-4867",
    "name": "XPSR #4867",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/62ca919d3f55c085606684104b2a399e.png"
  },
  {
    "id": "xpsr-4864",
    "name": "XPSR #4864",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/19877e43ba88ffdb2eab0fa9ae95c906.png"
  },
  {
    "id": "xpsr-4866",
    "name": "XPSR #4866",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/080e88afdfe3c45c02744774de92ba9a.png"
  },
  {
    "id": "xpsr-4863",
    "name": "XPSR #4863",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/0ef5f82faf179ef8a95452b7ab15cc51.png"
  },
  {
    "id": "xpsr-4860",
    "name": "XPSR #4860",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/aa9dabb85dd5811f7985af94033b74b2.png"
  },
  {
    "id": "xpsr-4862",
    "name": "XPSR #4862",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/4d21f86168a9815035cd34105789715d.png"
  },
  {
    "id": "xpsr-4861",
    "name": "XPSR #4861",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/02ec4b2e0e689ec125c08641f8d5b477.png"
  },
  {
    "id": "xpsr-4859",
    "name": "XPSR #4859",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/7bc903e3d29a41789fd2e3354d2ca72d.png"
  },
  {
    "id": "xpsr-4857",
    "name": "XPSR #4857",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/2bc6f41801fba3f7fd229ab8eafdccb8.png"
  },
  {
    "id": "xpsr-4858",
    "name": "XPSR #4858",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/fe31077207ffeff2fecd7900303e27f4.png"
  },
  {
    "id": "xpsr-4856",
    "name": "XPSR #4856",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/47a756552a71383526bb78a495c6b5bb.png"
  },
  {
    "id": "xpsr-4854",
    "name": "XPSR #4854",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/a3fefe6514941cb12d5555084679048f.png"
  },
  {
    "id": "xpsr-4855",
    "name": "XPSR #4855",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/8460c276e0966d15c5c569d35f43a221.png"
  },
  {
    "id": "xpsr-4853",
    "name": "XPSR #4853",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/74ef248a0bfb44a2a166f4cd3322d037.png"
  },
  {
    "id": "xpsr-4850",
    "name": "XPSR #4850",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/f1605133ba421e74cdb6697f3534629d.png"
  },
  {
    "id": "xpsr-4852",
    "name": "XPSR #4852",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/79b871d49295375fb0f98266b848707b.png"
  },
  {
    "id": "xpsr-4849",
    "name": "XPSR #4849",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/68febd9a0587166a3bde3628611c4d12.png"
  },
  {
    "id": "xpsr-4848",
    "name": "XPSR #4848",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/050858f1b31ef71fe2dff30d982ccbaa.png"
  },
  {
    "id": "xpsr-4847",
    "name": "XPSR #4847",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/8138f9f58d7f2aa0696a34e5e02e4d8a.png"
  },
  {
    "id": "xpsr-4846",
    "name": "XPSR #4846",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/269e9f1f0e677e8b9ae8d4b74ce82c20.png"
  },
  {
    "id": "xpsr-4845",
    "name": "XPSR #4845",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b579fcc9719e8113dfa1e105a045004c.png"
  },
  {
    "id": "xpsr-4844",
    "name": "XPSR #4844",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/bd9b0964253e3bd9de7e1182a48d7e23.png"
  },
  {
    "id": "xpsr-4843",
    "name": "XPSR #4843",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/a904d001c11e485f8aa03d79b151e28d.png"
  },
  {
    "id": "xpsr-4842",
    "name": "XPSR #4842",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/99d6f1ddfd66a9cceb36d0ccac8dcada.png"
  },
  {
    "id": "xpsr-4841",
    "name": "XPSR #4841",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/4cc946b12416be104ff23f02e33e1d65.png"
  },
  {
    "id": "xpsr-4840",
    "name": "XPSR #4840",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b0d12e796d8e8ecc954385f27e8ff5d5.png"
  },
  {
    "id": "xpsr-4839",
    "name": "XPSR #4839",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/43f58743b62cbbe7ae12f0b2c3dbb2d0.png"
  },
  {
    "id": "xpsr-4838",
    "name": "XPSR #4838",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/e04206753426e471f8748eb4138bad6b.png"
  },
  {
    "id": "xpsr-4837",
    "name": "XPSR #4837",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/6de5cde224ffe8be4102e4bc25872932.png"
  },
  {
    "id": "xpsr-4836",
    "name": "XPSR #4836",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/68b23f1b31405d62fdde3e37144a1b84.png"
  },
  {
    "id": "xpsr-4835",
    "name": "XPSR #4835",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/582d02b1f5b5763e86e4ff1eb0cbc74c.png"
  },
  {
    "id": "xpsr-4834",
    "name": "XPSR #4834",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3eca98b9ca977fc60adad304539880d0.png"
  },
  {
    "id": "xpsr-4833",
    "name": "XPSR #4833",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/7f7f276f7adaa607b96b4f24c98bca30.png"
  },
  {
    "id": "xpsr-4832",
    "name": "XPSR #4832",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b757f868646eb9465df8941fc687dffd.png"
  },
  {
    "id": "xpsr-4831",
    "name": "XPSR #4831",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/89200b81916d71a79558531196f8613b.png"
  },
  {
    "id": "xpsr-4830",
    "name": "XPSR #4830",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/03e84b83609f7aeb7355fa497bbe8250.png"
  },
  {
    "id": "xpsr-4829",
    "name": "XPSR #4829",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/0455f100c6acc4a65aa5827115069884.png"
  },
  {
    "id": "xpsr-4828",
    "name": "XPSR #4828",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/6e0bf1b8c83e6eda572616e032b3af2a.png"
  },
  {
    "id": "xpsr-4826",
    "name": "XPSR #4826",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/bffbe47ed41030675cc8f90429fcac34.png"
  },
  {
    "id": "xpsr-4827",
    "name": "XPSR #4827",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/a3b6bb4fa7f10f3ac03f93d34bf85d4b.png"
  },
  {
    "id": "xpsr-4825",
    "name": "XPSR #4825",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/70e18680ed60ad2cc049702a9d71b3c4.png"
  },
  {
    "id": "xpsr-4824",
    "name": "XPSR #4824",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/07d76eb5b6bae00edbd108240157e47c.png"
  },
  {
    "id": "xpsr-4823",
    "name": "XPSR #4823",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3bddcb4892aa6864f7f3ca7135898176.png"
  },
  {
    "id": "xpsr-4821",
    "name": "XPSR #4821",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/a12907216fbefa41b79345d374c1d87e.png"
  },
  {
    "id": "xpsr-4822",
    "name": "XPSR #4822",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/14520b1cf3759ff98a1353863c6608f0.png"
  },
  {
    "id": "xpsr-4820",
    "name": "XPSR #4820",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/338e9a8d357df3352799036279a36d50.png"
  },
  {
    "id": "xpsr-4818",
    "name": "XPSR #4818",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/36208fe9af2930c0457519debdb95c3d.png"
  },
  {
    "id": "xpsr-4819",
    "name": "XPSR #4819",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/32768dba1d31d90b822a77ce36b2a586.png"
  },
  {
    "id": "xpsr-4817",
    "name": "XPSR #4817",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/fe4b89aebf700700ad0ae964a122ccf3.png"
  },
  {
    "id": "xpsr-4816",
    "name": "XPSR #4816",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/f48ccd305c956efffa34c0f493b1b1f3.png"
  },
  {
    "id": "xpsr-4815",
    "name": "XPSR #4815",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/3d8b320177824ffe5bb56a4ad652e7b7.png"
  },
  {
    "id": "xpsr-4814",
    "name": "XPSR #4814",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/0cf9f98eb76516b73d22fe006e87b012.png"
  },
  {
    "id": "xpsr-4813",
    "name": "XPSR #4813",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/cd7812170731052fa2b6084ee1b3c6e6.png"
  },
  {
    "id": "xpsr-4812",
    "name": "XPSR #4812",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/ca9aa78ff4ada01a1990431c2ca0110f.png"
  },
  {
    "id": "xpsr-4811",
    "name": "XPSR #4811",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/a75985b712594f806af4522a3ae22294.png"
  },
  {
    "id": "xpsr-4809",
    "name": "XPSR #4809",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/1e5330ac64d0dd67f44250e222ff3f4d.png"
  },
  {
    "id": "xpsr-4810",
    "name": "XPSR #4810",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b7efb92ce99cd80db29f26468ba228a0.png"
  },
  {
    "id": "xpsr-4807",
    "name": "XPSR #4807",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/cd8cd882f91934936ab23c4e773c44b1.png"
  },
  {
    "id": "xpsr-4808",
    "name": "XPSR #4808",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/6b7ef6573a9adaeec32de4b2b48e0dcc.png"
  },
  {
    "id": "xpsr-4806",
    "name": "XPSR #4806",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/47a0e6d81433fbd6117a1d08433e7f7e.png"
  },
  {
    "id": "xpsr-4805",
    "name": "XPSR #4805",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/5b28afc09a5d4f9e2c3c0d5208b87c5f.png"
  },
  {
    "id": "xpsr-4804",
    "name": "XPSR #4804",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/39ece1a15c331f1b5d3b9ab07b7bfa11.png"
  },
  {
    "id": "xpsr-4803",
    "name": "XPSR #4803",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/8d9c8e2db2241bc13906146472e6b691.png"
  },
  {
    "id": "xpsr-4802",
    "name": "XPSR #4802",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/1be33142eb50778ce7d7a3f3ecdfed41.png"
  },
  {
    "id": "xpsr-4799",
    "name": "XPSR #4799",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/72b42dd7ad2d2e51a331d21f7b4ad276.png"
  },
  {
    "id": "xpsr-4801",
    "name": "XPSR #4801",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/204e69033d4120efc9dcc22f7a102570.png"
  },
  {
    "id": "xpsr-4798",
    "name": "XPSR #4798",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/c985977ead73eb6a68eb7b2baa2d51f0.png"
  },
  {
    "id": "xpsr-4800",
    "name": "XPSR #4800",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/e7e99744fed78ec6525216ce9d0a36d0.png"
  },
  {
    "id": "xpsr-4797",
    "name": "XPSR #4797",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/107daaa8290fb6fee1d47f98b5f67505.png"
  },
  {
    "id": "xpsr-4796",
    "name": "XPSR #4796",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/4092433f05f0f6644772b6d8ad10e1ff.png"
  },
  {
    "id": "xpsr-4794",
    "name": "XPSR #4794",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/1b43f4e11b749ab87b74c2847700d86a.png"
  },
  {
    "id": "xpsr-4795",
    "name": "XPSR #4795",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/b44e1a39dc6585195561098f9f4af802.png"
  },
  {
    "id": "xpsr-4793",
    "name": "XPSR #4793",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/f4f7a9453d1d58a3ba361cb470f8913c.png"
  },
  {
    "id": "xpsr-4792",
    "name": "XPSR #4792",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/e17cf21cb31df478ab53d4268de57259.png"
  },
  {
    "id": "xpsr-4791",
    "name": "XPSR #4791",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/0818626a03c6c83c4e63dba5d5c34632.png"
  },
  {
    "id": "xpsr-4789",
    "name": "XPSR #4789",
    "image": "https://staticassets.confiction.com/web/nft-assets/xpsr/62fe3f1ee5273b33f580af24b73d6091.png"
  }

            ];
        actualNftData = xpsrNFTs.find(nft => nft.id.toString() === itemId);
                          
                 } else if (collectionId === 'kanpai-pandas-infinity') {
        const kanpaiinfinityNFTs = [
            
  {
    "id": "kanpaipanda-250",
    "name": "Infinity Panda #250",
    "image": "https://ipfs.io/ipfs/bafybeibxmuishdxqifn4qqklicbkba4r7ws2axdwwfvdo4fpgxunjcchva"
  },
  {
    "id": "kanpaipanda-249",
    "name": "Infinity Panda #249",
    "image": "https://ipfs.io/ipfs/bafybeif57ojnz3yiar4htogru7l6qxzqz2gllhdpzt3ndhvhsvg6zvwhiq"
  },
  {
    "id": "kanpaipanda-248",
    "name": "Infinity Panda #248",
    "image": "https://ipfs.io/ipfs/bafybeiaaa4izmn7kn6eo5kgntyam6jgm3ajyxl7he3s7oj2u6wbva4mobu"
  },
  {
    "id": "kanpaipanda-247",
    "name": "Infinity Panda #247",
    "image": "https://ipfs.io/ipfs/bafybeibc37nlf5eufdpccrhavn63lxgavuci77gk2bt26ylorw2pwwdcte"
  },
  {
    "id": "kanpaipanda-246",
    "name": "Infinity Panda #246",
    "image": "https://ipfs.io/ipfs/bafybeier4b55suggaxirndzilil5krwxpm53skfxjvsnjehp4q6co423ya"
  },
  {
    "id": "kanpaipanda-245",
    "name": "Infinity Panda #245",
    "image": "https://ipfs.io/ipfs/bafybeicuydnhavgowsyew5tvehhthgmwrggtxzmv7o6pxqw3yxq3rkegr4"
  },
  {
    "id": "kanpaipanda-244",
    "name": "Infinity Panda #244",
    "image": "https://ipfs.io/ipfs/bafybeieajz4vlcqm7hvpvmtmhtvhmdw3r7drlbmkmctfcwrr4tkbjymmre"
  },
  {
    "id": "kanpaipanda-243",
    "name": "Infinity Panda #243",
    "image": "https://ipfs.io/ipfs/bafybeigjh52u243iikeogbbypg6axsqikbtrcyjr6w6s6bs7nt3jux6iue"
  },
  {
    "id": "kanpaipanda-242",
    "name": "Infinity Panda #242",
    "image": "https://ipfs.io/ipfs/bafybeiesphnka6chzult6rd7p6riukb7ny4zeegnd5cw3mv4mytbdq5lj4"
  },
  {
    "id": "kanpaipanda-241",
    "name": "Infinity Panda #241",
    "image": "https://ipfs.io/ipfs/bafybeibts44azmja6bveyzasn64lxrpt3fmptepvvum4k7qbzcsvuszkca"
  },
  {
    "id": "kanpaipanda-240",
    "name": "Infinity Panda #240",
    "image": "https://ipfs.io/ipfs/bafybeigncohpnmjiu6mr6ymgg4cop4fnlvt3zzrzwedyz77royfbtwhe2a"
  },
  {
    "id": "kanpaipanda-239",
    "name": "Infinity Panda #239",
    "image": "https://ipfs.io/ipfs/bafybeidoymhogzfvjjxuma56vjn5cwnvn4xy7qqqxvncq73gytmzzcncja"
  },
  {
    "id": "kanpaipanda-238",
    "name": "Infinity Panda #238",
    "image": "https://ipfs.io/ipfs/bafybeihhlobznjhanrkinhyute7fqn6qfxgz6ah2inqv5v4aus5njblti4"
  },
  {
    "id": "kanpaipanda-237",
    "name": "Infinity Panda #237",
    "image": "https://ipfs.io/ipfs/bafybeihgpsvqwbxgmjyvsnwx4rjq2cr6zt3cskv2qf3eqdfwuxmzt4h5tm"
  },
  {
    "id": "kanpaipanda-236",
    "name": "Infinity Panda #236",
    "image": "https://ipfs.io/ipfs/bafybeidly5ioutt6axonovc6duo7x2qnkiodvm2eujgt6cmyi4gq4qmqvm"
  },
  {
    "id": "kanpaipanda-235",
    "name": "Infinity Panda #235",
    "image": "https://ipfs.io/ipfs/bafybeieir7w4qij5tjxsc53tyfggw2ry7oq2h7x3eqygzdopirlmoaehmq"
  },
  {
    "id": "kanpaipanda-234",
    "name": "Infinity Panda #234",
    "image": "https://ipfs.io/ipfs/bafybeieuehdr5bpxreasw77dqssbgvnekhnhrzt4rkmodzyjkazjs42cfe"
  },
  {
    "id": "kanpaipanda-233",
    "name": "Infinity Panda #233",
    "image": "https://ipfs.io/ipfs/bafybeig3yupyprwykf7ggqap5wvq2iq2s6bsn4beykvqzleb7itaieb4p4"
  },
  {
    "id": "kanpaipanda-232",
    "name": "Infinity Panda #232",
    "image": "https://ipfs.io/ipfs/bafybeib4x6ynky42bbiy4tariavhag5ve7aaekppqgaytfoj23u2ojfiqq"
  },
  {
    "id": "kanpaipanda-231",
    "name": "Infinity Panda #231",
    "image": "https://ipfs.io/ipfs/bafybeidflsb4wykjv7domu5rkhzbt27r2qqpyjsjc3eo3bcami7bkqbjoe"
  },
  {
    "id": "kanpaipanda-230",
    "name": "Infinity Panda #230",
    "image": "https://ipfs.io/ipfs/bafybeic2efyhmg2zx72ik52skf44u4i7o5l2zgdhtnk5z5xjwxnbnticbu"
  },
  {
    "id": "kanpaipanda-229",
    "name": "Infinity Panda #229",
    "image": "https://ipfs.io/ipfs/bafybeignpdcy4cwbh2veyybwdy2xjltcjgajdauumoz2p5z2qermwpuf4q"
  },
  {
    "id": "kanpaipanda-228",
    "name": "Infinity Panda #228",
    "image": "https://ipfs.io/ipfs/bafybeifqbsj4rmnnrnb25jljvpno2emvbsnutzj2jxs6wh77qdx6swpmce"
  },
  {
    "id": "kanpaipanda-227",
    "name": "Infinity Panda #227",
    "image": "https://ipfs.io/ipfs/bafybeia6d6ysvestknymyctswsn244vwbz5ciurcyrgufp35fysuu7hnhy"
  },
  {
    "id": "kanpaipanda-226",
    "name": "Infinity Panda #226",
    "image": "https://ipfs.io/ipfs/bafybeiaa24ch4gldnuiwlkdmx5s7xzrerhdkmmtowilpvsh4tggtadevd4"
  },
  {
    "id": "kanpaipanda-225",
    "name": "Infinity Panda #225",
    "image": "https://ipfs.io/ipfs/bafybeigg7eib7cwfqpja7dzv56cii6vas26hh4splapelzwgkkir6rigky"
  },
  {
    "id": "kanpaipanda-224",
    "name": "Infinity Panda #224",
    "image": "https://ipfs.io/ipfs/bafybeiepla5ucr666ktpnclxhjfvrzyatwq4bedkzjd26hqehnpynbbbi4"
  },
  {
    "id": "kanpaipanda-223",
    "name": "Infinity Panda #223",
    "image": "https://ipfs.io/ipfs/bafybeigihej34ggoprthpxfzmsouu5r7dktd7q7xaltfwpx2th7rn77vrq"
  },
  {
    "id": "kanpaipanda-222",
    "name": "Infinity Panda #222",
    "image": "https://ipfs.io/ipfs/bafybeifr6yp4q76ygmhqpgil4pg4yl737gxiocqpwdpifcniq6qvz757hi"
  },
  {
    "id": "kanpaipanda-221",
    "name": "Infinity Panda #221",
    "image": "https://ipfs.io/ipfs/bafybeicn73detezyfhuysbv4pnnwzlxidjibktbkomrxdybcevsmbul3ce"
  },
  {
    "id": "kanpaipanda-220",
    "name": "Infinity Panda #220",
    "image": "https://ipfs.io/ipfs/bafybeid2fiawtkqtaq6stzukw5r4u7xvrv2znraw27sow7hwk2u72ajkla"
  },
  {
    "id": "kanpaipanda-219",
    "name": "Infinity Panda #219",
    "image": "https://ipfs.io/ipfs/bafybeigbxvo6jvpyi6zt6fsumi3ca63vwoqt3vgom7tnxvxn6wetg32pj4"
  },
  {
    "id": "kanpaipanda-218",
    "name": "Infinity Panda #218",
    "image": "https://ipfs.io/ipfs/bafybeidcubj2oqsuegqtlff2blwklus434dsgwbq7bs5zebtm2jqczpf2q"
  },
  {
    "id": "kanpaipanda-217",
    "name": "Infinity Panda #217",
    "image": "https://ipfs.io/ipfs/bafybeibmvsgjwptc6nwfwbpquj7karw5ebu7wxb2zal4xqyh4g3frbppqq"
  },
  {
    "id": "kanpaipanda-216",
    "name": "Infinity Panda #216",
    "image": "https://ipfs.io/ipfs/bafybeibjf6b67dcq5cwsezmgccywnr4kf25jpm6ghgmhro7k345jfclgoq"
  },
  {
    "id": "kanpaipanda-215",
    "name": "Infinity Panda #215",
    "image": "https://ipfs.io/ipfs/bafybeiaxc6xvc5phkmeekv6sros4awyjgalvb2kj6e4mcibo4jcyq2cslq"
  },
  {
    "id": "kanpaipanda-214",
    "name": "Infinity Panda #214",
    "image": "https://ipfs.io/ipfs/bafybeifqpnrfmk3wmlrm6n7gcdhwe262ubyz3gklqb2uusfosvuvoo76ii"
  },
  {
    "id": "kanpaipanda-213",
    "name": "Infinity Panda #213",
    "image": "https://ipfs.io/ipfs/bafybeihvgaxmci233eyhdsu34xpyjrept6zhufbtzdw6j4eyiywumgw7fy"
  },
  {
    "id": "kanpaipanda-212",
    "name": "Infinity Panda #212",
    "image": "https://ipfs.io/ipfs/bafybeigtpj3eurmt3drmgj7kwrqtnhsiab2jk3uhnchi2nk6xs55ec7dpm"
  },
  {
    "id": "kanpaipanda-211",
    "name": "Infinity Panda #211",
    "image": "https://ipfs.io/ipfs/bafybeignqix7rkchnds74gul2cfmfsfvuis3gj37g5v3pxymmumgo23ema"
  },
  {
    "id": "kanpaipanda-210",
    "name": "Infinity Panda #210",
    "image": "https://ipfs.io/ipfs/bafybeicqjmd73c2xupe3bsox6zmmgcdhn6tmbx4dmawzbhcw4fpscmhiu4"
  },
  {
    "id": "kanpaipanda-209",
    "name": "Infinity Panda #209",
    "image": "https://ipfs.io/ipfs/bafybeidheutgzdaha7ecxkddgs7s4rjpykk4rcgvyusnyzvy7jwx564gvm"
  },
  {
    "id": "kanpaipanda-208",
    "name": "Infinity Panda #208",
    "image": "https://ipfs.io/ipfs/bafybeiazmvq4dhkjxhdosauvbb36rl7wxcnoxuiy4d3avragksel5l2abq"
  },
  {
    "id": "kanpaipanda-207",
    "name": "Infinity Panda #207",
    "image": "https://ipfs.io/ipfs/bafybeig2oahcx34mgnp3ktm4x5ykulyb6wlc5zbftyuawzpgt2sjc5l2ui"
  },
  {
    "id": "kanpaipanda-206",
    "name": "Infinity Panda #206",
    "image": "https://ipfs.io/ipfs/bafybeicnhbtudiervqstoaz6fg6jydklookuajmrjoeoaxvlfffmyddr6i"
  },
  {
    "id": "kanpaipanda-205",
    "name": "Infinity Panda #205",
    "image": "https://ipfs.io/ipfs/bafybeihfo26ukd5jaqude2fne3xotxhvlkg7wtjxmji44e6vyqd2no7hza"
  },
  {
    "id": "kanpaipanda-204",
    "name": "Infinity Panda #204",
    "image": "https://ipfs.io/ipfs/bafybeidaqbrkumapk2capuitk6qshw5jdaqtzux4o65oxnq75lnbegy6he"
  },
  {
    "id": "kanpaipanda-203",
    "name": "Infinity Panda #203",
    "image": "https://ipfs.io/ipfs/bafybeie4uzqvyh57ruimyzwl6lix4suw6txn7ce5tbhflyicaomxwtxaqe"
  },
  {
    "id": "kanpaipanda-202",
    "name": "Infinity Panda #202",
    "image": "https://ipfs.io/ipfs/bafybeie5f5c3se3d2xxyo4jkhivchn6i6fxrtv2nqrslh6wvkkoxsb52z4"
  },
  {
    "id": "kanpaipanda-201",
    "name": "Infinity Panda #201",
    "image": "https://ipfs.io/ipfs/bafybeiguscjdm4cz7gzyu66ai2fohoqhxjgvqt37covcax3fouudoe7kii"
  },
  {
    "id": "kanpaipanda-200",
    "name": "Infinity Panda #200",
    "image": "https://ipfs.io/ipfs/bafybeiesgelth4w5quvf3u4yq6ckfwmf4ou2v4cskn442gbn3ucoovmnd4"
  },
  {
    "id": "kanpaipanda-199",
    "name": "Infinity Panda #199",
    "image": "https://ipfs.io/ipfs/bafybeifd5sgogoegmq6loqzzwvcxuuzy2atg64n2gwn3wb7fty5qib6t5q"
  },
  {
    "id": "kanpaipanda-198",
    "name": "Infinity Panda #198",
    "image": "https://ipfs.io/ipfs/bafybeibop4ssycxjj7ldwn5fqln4ztauxv266wtszmify62mynyjnbhsni"
  },
  {
    "id": "kanpaipanda-197",
    "name": "Infinity Panda #197",
    "image": "https://ipfs.io/ipfs/bafybeidycprmpmfwo7vsnauhzj5nlgerfzp5duxme54pjtovj4j3p2mopy"
  },
  {
    "id": "kanpaipanda-196",
    "name": "Infinity Panda #196",
    "image": "https://ipfs.io/ipfs/bafybeigi4yuvid2q334c7xsixmpk64vekk3lngznubh3mtgtunsuaowf6m"
  },
  {
    "id": "kanpaipanda-195",
    "name": "Infinity Panda #195",
    "image": "https://ipfs.io/ipfs/bafybeiezlwcqadxnjcuf4vlnalej2fmjnec76poduyenjmiqcbxn7hy3te"
  },
  {
    "id": "kanpaipanda-194",
    "name": "Infinity Panda #194",
    "image": "https://ipfs.io/ipfs/bafybeig6lru35gyboonctdcnt6qpqnbpww4woohrodym5fjokndfqjmp54"
  },
  {
    "id": "kanpaipanda-193",
    "name": "Infinity Panda #193",
    "image": "https://ipfs.io/ipfs/bafybeic725a5gebcbqcyk2lneudynrvk3eqqkaw2rzgjdhyjsqssgrkni4"
  },
  {
    "id": "kanpaipanda-192",
    "name": "Infinity Panda #192",
    "image": "https://ipfs.io/ipfs/bafybeibxm6tqsqnlltx6lyal2dxlsmrnlk64ulluxx2zbfireixzm3eiuq"
  },
  {
    "id": "kanpaipanda-191",
    "name": "Infinity Panda #191",
    "image": "https://ipfs.io/ipfs/bafybeidf6x7yb24q3s6opb77b4h7ty6xxvl3bvqpd277mf6nvrtdvxozyu"
  },
  {
    "id": "kanpaipanda-190",
    "name": "Infinity Panda #190",
    "image": "https://ipfs.io/ipfs/bafybeicq34ohr56ugbuotp2hvthj644hucb2usgerwywabz3az5qox7e4u"
  },
  {
    "id": "kanpaipanda-189",
    "name": "Infinity Panda #189",
    "image": "https://ipfs.io/ipfs/bafybeih7l4rt2sihnu2nlotcedtqeg534xlmq3rwrymbiih7evhflmtqim"
  },
  {
    "id": "kanpaipanda-188",
    "name": "Infinity Panda #188",
    "image": "https://ipfs.io/ipfs/bafybeiekfje3lsh37silfmbtbrdnqp2tbmkqvixy34y4tvmq5aldf5kb3u"
  },
  {
    "id": "kanpaipanda-187",
    "name": "Infinity Panda #187",
    "image": "https://ipfs.io/ipfs/bafybeiafozlqgkoqury4cxcu36nmyxhwjpqij2oguldeygaxunwdntomiy"
  },
  {
    "id": "kanpaipanda-186",
    "name": "Infinity Panda #186",
    "image": "https://ipfs.io/ipfs/bafybeibwmyolj6nx34mj3ymh6bolm6kvukq6ldmdgrg6ttfmrjmhux2bzu"
  },
  {
    "id": "kanpaipanda-185",
    "name": "Infinity Panda #185",
    "image": "https://ipfs.io/ipfs/bafybeidpnltg6tp3reyf65ukmxn42ll7m3s2qnyzstfjszg2355frb7z3a"
  },
  {
    "id": "kanpaipanda-184",
    "name": "Infinity Panda #184",
    "image": "https://ipfs.io/ipfs/bafybeifzkfmzxzeq7rgzy623sebsqtww45annc4xypmuhtvikbv635adue"
  },
  {
    "id": "kanpaipanda-183",
    "name": "Infinity Panda #183",
    "image": "https://ipfs.io/ipfs/bafybeidoaavjiulr6dm76kftdbd76incp24jbk4bxfznsrjzrdadifbb7e"
  },
  {
    "id": "kanpaipanda-182",
    "name": "Infinity Panda #182",
    "image": "https://ipfs.io/ipfs/bafybeihmg4fkrn6pzelftid7s6svi2odny3smdiyf5aidoq4prrseu6dym"
  },
  {
    "id": "kanpaipanda-181",
    "name": "Infinity Panda #181",
    "image": "https://ipfs.io/ipfs/bafybeifdnxwiknh5rem5kx644mam5qzbhbulpiep2uehvrxchsevwddqui"
  },
  {
    "id": "kanpaipanda-180",
    "name": "Infinity Panda #180",
    "image": "https://ipfs.io/ipfs/bafybeie77pck5m4ota7kit3od2un4jhuwpovmknlq7b4ciuvrokxdgi4qu"
  },
  {
    "id": "kanpaipanda-179",
    "name": "Infinity Panda #179",
    "image": "https://ipfs.io/ipfs/bafybeigdqj23nottgqmn2ipqtnwcnvk5s6j7ljbh6c3bkyqsfuoygx6ahe"
  },
  {
    "id": "kanpaipanda-178",
    "name": "Infinity Panda #178",
    "image": "https://ipfs.io/ipfs/bafybeib3u4uskdzrv2wn7ojo7gy2el4jxjmop3ydwnzflzhufv24nzapy4"
  },
  {
    "id": "kanpaipanda-177",
    "name": "Infinity Panda #177",
    "image": "https://ipfs.io/ipfs/bafybeiapcucfipginl3yvojc2i56fcrrn6tsm7lhkkdzc52rgv6vkspyve"
  },
  {
    "id": "kanpaipanda-176",
    "name": "Infinity Panda #176",
    "image": "https://ipfs.io/ipfs/bafybeibm5b5l4ojniqsl5drtliuuhnrlekuz3b33lmuzf2ph5kp2ivuvbq"
  },
  {
    "id": "kanpaipanda-175",
    "name": "Infinity Panda #175",
    "image": "https://ipfs.io/ipfs/bafybeigwspb5gzwbjkz754frobrs6yruvmsaahtpg7wdnv7a23ldeztxwu"
  },
  {
    "id": "kanpaipanda-174",
    "name": "Infinity Panda #174",
    "image": "https://ipfs.io/ipfs/bafybeibhjemgysntg6orgol7qpybljuyn5pfwvwsaepi7frofpulybocxm"
  },
  {
    "id": "kanpaipanda-173",
    "name": "Infinity Panda #173",
    "image": "https://ipfs.io/ipfs/bafybeigdm5t7aghbe4lodtdpxroz6u3r56r324ectfexg7jgul3qkicvua"
  },
  {
    "id": "kanpaipanda-172",
    "name": "Infinity Panda #172",
    "image": "https://ipfs.io/ipfs/bafybeib2dm7t6hl3gvyygr3r6wsiodth3xapodsehmqcov6rtgcdj752ui"
  },
  {
    "id": "kanpaipanda-171",
    "name": "Infinity Panda #171",
    "image": "https://ipfs.io/ipfs/bafybeic62qjqmg53luojfpmfu3653sg3ufr5ycjmgbqgxicekzst7zomwu"
  },
  {
    "id": "kanpaipanda-170",
    "name": "Infinity Panda #170",
    "image": "https://ipfs.io/ipfs/bafybeiddbz3qikccfgyhwf437kyengozt26hjs5ooaen6gnd74d2x2cp24"
  },
  {
    "id": "kanpaipanda-169",
    "name": "Infinity Panda #169",
    "image": "https://ipfs.io/ipfs/bafybeiemvifvbyq37gdy7j4vnsthgu5qhxobgnrfz2mefwceodj2z56u4q"
  },
  {
    "id": "kanpaipanda-168",
    "name": "Infinity Panda #168",
    "image": "https://ipfs.io/ipfs/bafybeiemqjat5fn7tk4vz5jmh3kuh7xropxkm22rcafxbausdvzns3se7y"
  },
  {
    "id": "kanpaipanda-167",
    "name": "Infinity Panda #167",
    "image": "https://ipfs.io/ipfs/bafybeiha3cjrt2egqcdzzvq4prcgiyuxkp6jfaqldq2kdbikjtq4ggl2tu"
  },
  {
    "id": "kanpaipanda-166",
    "name": "Infinity Panda #166",
    "image": "https://ipfs.io/ipfs/bafybeig3q3esfzl4atcrjnj4lolo6fkkitqwo5er2yijmlcqr2bucasbym"
  },
  {
    "id": "kanpaipanda-165",
    "name": "Infinity Panda #165",
    "image": "https://ipfs.io/ipfs/bafybeibw5dgdo3jw7whc4fn6yaooimf3lbeayzylswyoixqz3cat32kkuy"
  },
  {
    "id": "kanpaipanda-164",
    "name": "Infinity Panda #164",
    "image": "https://ipfs.io/ipfs/bafybeicptvfsb7daws6lz2cua63x4pjkr2aeuhulhjiwy2and53bfnj2x4"
  },
  {
    "id": "kanpaipanda-163",
    "name": "Infinity Panda #163",
    "image": "https://ipfs.io/ipfs/bafybeihv72pjcraum4ieloc3wefjgpsclnfb35rx6erufohg2ocmhtuvhu"
  },
  {
    "id": "kanpaipanda-162",
    "name": "Infinity Panda #162",
    "image": "https://ipfs.io/ipfs/bafybeiearfpygop7q22z4k4rl6rkbp2alywp4zy6fp3jcea3ene5vgrxba"
  },
  {
    "id": "kanpaipanda-161",
    "name": "Infinity Panda #161",
    "image": "https://ipfs.io/ipfs/bafybeihfil72bhw6x2vblfm4pisbsqc7h2hm7txnfn2kruow3pgv44frz4"
  },
  {
    "id": "kanpaipanda-160",
    "name": "Infinity Panda #160",
    "image": "https://ipfs.io/ipfs/bafybeig4exekgdajzhtlnrq7c3tew7bu2a2nz4xi2xaufg64a7ahrz7vse"
  },
  {
    "id": "kanpaipanda-159",
    "name": "Infinity Panda #159",
    "image": "https://ipfs.io/ipfs/bafybeicm5dqrxs3wnson74j6oodvqgdtu6aud3oph43sh3pmc6etjphl3q"
  },
  {
    "id": "kanpaipanda-158",
    "name": "Infinity Panda #158",
    "image": "https://ipfs.io/ipfs/bafybeieh3mesehrbbrdqisiovpsymqmi4ejvyqcvm5altuhyybqph45y7m"
  },
  {
    "id": "kanpaipanda-157",
    "name": "Infinity Panda #157",
    "image": "https://ipfs.io/ipfs/bafybeif7vdvubacrhupb6qesqeomp7vpiqcm3mhuycz2abwqntlvrf4poe"
  },
  {
    "id": "kanpaipanda-156",
    "name": "Infinity Panda #156",
    "image": "https://ipfs.io/ipfs/bafybeif42dofc3k4buv2p3bnybnkiisxfqd7buljsiuyeezjmisnwghb2m"
  },
  {
    "id": "kanpaipanda-155",
    "name": "Infinity Panda #155",
    "image": "https://ipfs.io/ipfs/bafybeic7kss5zu2ysexxmo5d6kkmqc4oko2syjmtigtkwr6bkifq6wruba"
  },
  {
    "id": "kanpaipanda-154",
    "name": "Infinity Panda #154",
    "image": "https://ipfs.io/ipfs/bafybeic3b4yvntjnptomv4dkin4bw2m54oww6rbu7mxadqftcnf6n4kgda"
  },
  {
    "id": "kanpaipanda-153",
    "name": "Infinity Panda #153",
    "image": "https://ipfs.io/ipfs/bafybeicsmjaed2ctspnuh422eqcla3sutkxvdb7fac6is4v2v4z54whfva"
  },
  {
    "id": "kanpaipanda-152",
    "name": "Infinity Panda #152",
    "image": "https://ipfs.io/ipfs/bafybeifta7dn2fso4y4vgu4wipwrh7x2gq4blzbbcolgxthwbcoux4i75y"
  },
  {
    "id": "kanpaipanda-151",
    "name": "Infinity Panda #151",
    "image": "https://ipfs.io/ipfs/bafybeigxhm6eregtms6r4fjsoqpjo5xz4zahtibsmpdird2zzpe5rltp7a"
  }
            
             ];
        actualNftData = kanpaiinfinityNFTs.find(nft => nft.id.toString() === itemId);
                               
                      } else if (collectionId === 'cyberkongz') {
        const cyberkongzNFTs = [
            
  {
    "id": "cyberkongz-11",
    "name": "CyberKongz #11",
    "image": "https://ipfs.io/ipfs/QmbVXhyh9fSHCEE5E7cnF4K867dtTKGkrYxJRXwVCQ8K9D"
  },
  {
    "id": "cyberkongz-862",
    "name": "CyberKongz #862",
    "image": "https://ipfs.io/ipfs/QmTBrrqLqE5GnHRPAmGWmLBYKUjKZLJE4yfuLbqdPumgzt"
  },
  {
    "id": "cyberkongz-971",
    "name": "CyberKongz #971",
    "image": "https://ipfs.io/ipfs/QmZG9twKsCeZzGrfQs75s5XcaKUDoicMcCvoFZVUfKxHcS"
  },
  {
    "id": "cyberkongz-536",
    "name": "CyberKongz #536",
    "image": "https://ipfs.io/ipfs/QmetCy1hewuR92o577kZ525rt7AZBj9XDGRSw4EGgBadz8"
  },
  {
    "id": "cyberkongz-645",
    "name": "CyberKongz #645",
    "image": "https://ipfs.io/ipfs/QmagiC96ABAwTsNZevEp2zMuY2cuvQBpJKgbJHNGPAU4hb"
  },
  {
    "id": "cyberkongz-392",
    "name": "CyberKongz #392",
    "image": "https://ipfs.io/ipfs/QmUd38ZMjMJLskvWLctgCR97JCRDyapeysJ82E7tyGCedU"
  },
  {
    "id": "cyberkongz-276",
    "name": "CyberKongz #276",
    "image": "https://ipfs.io/ipfs/Qma6VqqjV76x8Fi24EVkbm4E4NktVtbhDnLir7SijgFM2b"
  },
  {
    "id": "cyberkongz-374",
    "name": "CyberKongz #374",
    "image": "https://ipfs.io/ipfs/QmStSMaJ5QdirajReNBsji6FZsM8UAf5f3MPM64V8jMn26"
  },
  {
    "id": "cyberkongz-307",
    "name": "CyberKongz #307",
    "image": "https://ipfs.io/ipfs/QmfKmnXA1HTvquwF13FkLu36s6CrmNW4JWHgrvanCJ8sin"
  },
  {
    "id": "cyberkongz-157",
    "name": "CyberKongz #157",
    "image": "https://ipfs.io/ipfs/QmYZLK7dqe2oKkrcu9KHpdqwhV9RsomkVFWCpSSWP6Nz7r"
  },
  {
    "id": "cyberkongz-552",
    "name": "CyberKongz #552",
    "image": "https://ipfs.io/ipfs/Qmaf6zB7zZ65QzeeUwvFSLpYGkFoNvimPrEuVkD7Uw8sy1"
  },
  {
    "id": "cyberkongz-871",
    "name": "CyberKongz #871",
    "image": "https://ipfs.io/ipfs/QmShMw3GUSETTqfsSGMgNXxpnnHehT2GE5dqh1uaKF5gRH"
  },
  {
    "id": "cyberkongz-830",
    "name": "CyberKongz #830",
    "image": "https://ipfs.io/ipfs/QmQfGFh5hTwd6UnNUPfQV4Y1HPZFV2X3LyELicLjCpdq9g"
  },
  {
    "id": "cyberkongz-919",
    "name": "CyberKongz #919",
    "image": "https://ipfs.io/ipfs/QmQzHa84rvwm5DUpwnNNTygqWnmCPur9mSDimdVDcPpH6L"
  },
  {
    "id": "cyberkongz-465",
    "name": "CyberKongz #465",
    "image": "https://ipfs.io/ipfs/Qma4gCCrt7U8ox1ktzBsMLNvjNPyY7XZ5RBFirRMBdn8JZ"
  },
  {
    "id": "cyberkongz-771",
    "name": "CyberKongz #771",
    "image": "https://ipfs.io/ipfs/QmUVYLYFbiGL87hajuNdmQY8fRuu5ooDwM8rKARHk975Pr"
  },
  {
    "id": "cyberkongz-990",
    "name": "CyberKongz #990",
    "image": "https://ipfs.io/ipfs/Qmd83UzSAGL5icoCUrF1amBborb1hov8szWmN5BxJV8Y3U"
  },
  {
    "id": "cyberkongz-927",
    "name": "CyberKongz #927",
    "image": "https://ipfs.io/ipfs/QmUbFsJi86UFfgKEGzFUMHgCUv4jXRPJQAJ6UymyeoBfmc"
  },
  {
    "id": "cyberkongz-587",
    "name": "CyberKongz #587",
    "image": "https://ipfs.io/ipfs/QmdYTGSUjYxQvPCxYyEtm8R2RmxXZXPXNgdGNSgSmUGgkY"
  },
  {
    "id": "cyberkongz-447",
    "name": "CyberKongz #447",
    "image": "https://ipfs.io/ipfs/QmNqyZw6pNjUN8RWbMNQBNR2Fc3vDCz9gb8kuDYm9sC7gx"
  },
  {
    "id": "cyberkongz-630",
    "name": "CyberKongz #630",
    "image": "https://ipfs.io/ipfs/QmPjNm6FZHyNh8yzxwuKmfNW9itaLCCTEbVeZeBpEERx37"
  },
  {
    "id": "cyberkongz-974",
    "name": "CyberKongz #974",
    "image": "https://ipfs.io/ipfs/QmU1cHihpvdaFLLDvWjsZp9sKyk3zz8Sj8WkJHAhfJa2vm"
  },
  {
    "id": "cyberkongz-680",
    "name": "CyberKongz #680",
    "image": "https://ipfs.io/ipfs/QmTT465PtJALFCDmHnXga19ebbun2i4r2zM9nbYtLR8dRL"
  },
  {
    "id": "cyberkongz-149",
    "name": "CyberKongz #149",
    "image": "https://ipfs.io/ipfs/QmPfDKr3DPmChrpFjWtCokw71oinuSa3RiK2wsZNYUMoJT"
  },
  {
    "id": "cyberkongz-969",
    "name": "CyberKongz #969",
    "image": "https://ipfs.io/ipfs/QmQGA11xCZKQfRH2qCs9zD9Stu9i6ZUMWZBFzVdMRLJ1BH"
  },
  {
    "id": "cyberkongz-316",
    "name": "CyberKongz #316",
    "image": "https://ipfs.io/ipfs/QmZikYtag8gyMAWxm9pSM6nvTXzaVtgnT2fwox8da1NrR7"
  },
  {
    "id": "cyberkongz-167",
    "name": "CyberKongz #167",
    "image": "https://ipfs.io/ipfs/QmPEEWQK8sWngZbMrr35wReZ2RypxoF46Xyabc7PZZRCGt"
  },
  {
    "id": "cyberkongz-171",
    "name": "CyberKongz #171",
    "image": "https://ipfs.io/ipfs/QmTXg4d2UyyTkmivifehD9FXw5gB97TCnWt78XFKUWCCzS"
  },
  {
    "id": "cyberkongz-979",
    "name": "CyberKongz #979",
    "image": "https://ipfs.io/ipfs/QmQ16venNrsStbb6zdJVSyCiYJ41A9ReiGPgy5K1UcGt6X"
  },
  {
    "id": "cyberkongz-172",
    "name": "CyberKongz #172",
    "image": "https://ipfs.io/ipfs/QmZjSkYfc6EXhZniYmPKB7xVChjW73ywHcpMJeUy6kNwso"
  },
  {
    "id": "cyberkongz-562",
    "name": "CyberKongz #562",
    "image": "https://ipfs.io/ipfs/QmYAByaVHu6qaZDDbQjY1yd7r83w8kmyqsH6U4K7Gf1aP6"
  },
  {
    "id": "cyberkongz-737",
    "name": "CyberKongz #737",
    "image": "https://ipfs.io/ipfs/QmdmpPoUnbZSf26HWAHyvFPk8Eh6SXb5n63uhCFwSUzpfr"
  },
  {
    "id": "cyberkongz-485",
    "name": "CyberKongz #485",
    "image": "https://ipfs.io/ipfs/QmagxDNgDjpsMDdAvYTzbrv3CuFPjGpjcEWMTR8RPEMXmt"
  },
  {
    "id": "cyberkongz-516",
    "name": "CyberKongz #516",
    "image": "https://ipfs.io/ipfs/QmXVwBN8rQsTyWRMqNAUmMPWSXeK4LE5R6gUXbBkffmzuT"
  },
  {
    "id": "cyberkongz-243",
    "name": "CyberKongz #243",
    "image": "https://ipfs.io/ipfs/QmdY3TJG5DPpN31g4XV2f39i28cygFQC5XwaEt5Bg8GNhg"
  },
  {
    "id": "cyberkongz-269",
    "name": "CyberKongz #269",
    "image": "https://ipfs.io/ipfs/QmRSmRyEUMYHWxsemhaS4y8k64DxVRBJ6McPSv7PE7xbEK"
  },
  {
    "id": "cyberkongz-523",
    "name": "CyberKongz #523",
    "image": "https://ipfs.io/ipfs/QmPXzZNyxjs2f8p3Cpf5z6WEEdCVnJhrB6pUE3UaCXZdB6"
  },
  {
    "id": "cyberkongz-622",
    "name": "CyberKongz #622",
    "image": "https://ipfs.io/ipfs/QmSEhmpxWMY1TEZT1G3s5sHWpvs8uc3Rz137RBbx566xrp"
  },
  {
    "id": "cyberkongz-882",
    "name": "CyberKongz #882",
    "image": "https://ipfs.io/ipfs/QmbNzRSe3PwpTxUXJUGr2uSAQQN6XY5yyQtNpiyoAKM21W"
  },
  {
    "id": "cyberkongz-323",
    "name": "CyberKongz #323",
    "image": "https://ipfs.io/ipfs/QmRWxcKabQw314PqQ4S15TNkUinDYjHnKZwB1hyPTC3pgj"
  },
  {
    "id": "cyberkongz-412",
    "name": "CyberKongz #412",
    "image": "https://ipfs.io/ipfs/QmSadQkZdYwfQN6sGTdZi3S945CQ43yrGUeS5CPFoaRPvG"
  },
  {
    "id": "cyberkongz-498",
    "name": "CyberKongz #498",
    "image": "https://ipfs.io/ipfs/QmUrrLJHa72TbFL3UQd9nzYhVXMJZ4S78dJdVu3ejXPKiu"
  },
  {
    "id": "cyberkongz-305",
    "name": "CyberKongz #305",
    "image": "https://ipfs.io/ipfs/QmWkDyPwvq7hREWtdVeAxLkNog8iz1MJ3eyhXWjR24hUCi"
  },
  {
    "id": "cyberkongz-403",
    "name": "CyberKongz #403",
    "image": "https://ipfs.io/ipfs/QmYuaBj3jYaRoyTSTstpNd1L8De5dgiXjFz9b3Gbs4ZxTy"
  },
  {
    "id": "cyberkongz-475",
    "name": "CyberKongz #475",
    "image": "https://ipfs.io/ipfs/QmdwsnfEZTWNeRuDLVpswNASqf6ySTKu2xBwAEGjZjwxJS"
  },
  {
    "id": "cyberkongz-289",
    "name": "CyberKongz #289",
    "image": "https://ipfs.io/ipfs/QmeKEvjUe55fK6UvDt1grSfKDuJB4BBwAQyHm31AKEQqDf"
  },
  {
    "id": "cyberkongz-812",
    "name": "CyberKongz #812",
    "image": "https://ipfs.io/ipfs/QmY27E2royZCBjwyn6ukYAiafr6hUvpGHyqctRvHqDybB1"
  },
  {
    "id": "cyberkongz-637",
    "name": "CyberKongz #637",
    "image": "https://ipfs.io/ipfs/QmWHnUJ71YxC4B5qM6fV1oP3vPQMyF7iDAUPgQQc1PMWu1"
  },
  {
    "id": "cyberkongz-140",
    "name": "CyberKongz #140",
    "image": "https://ipfs.io/ipfs/Qmf9KEVYc7Jih55iJk4tU4vfWT5bQ3sLYLHxWC8HmD8ERc"
  },
  {
    "id": "cyberkongz-361",
    "name": "CyberKongz #361",
    "image": "https://ipfs.io/ipfs/QmQudDb59T5ZTXM3LdCxfhJuuXwHZLBgDuquSKaKimAz1X"
  },
  {
    "id": "cyberkongz-558",
    "name": "CyberKongz #558",
    "image": "https://ipfs.io/ipfs/QmdJicu2fgWyCwKthJaDMT5Jfi6Vya38vsh5Z5efsZhVid"
  },
  {
    "id": "cyberkongz-561",
    "name": "CyberKongz #561",
    "image": "https://ipfs.io/ipfs/QmT5MwnpcktQbzK3fVKTsTSBZuuHigCniKmKM5pAoTkpy7"
  },
  {
    "id": "cyberkongz-350",
    "name": "CyberKongz #350",
    "image": "https://ipfs.io/ipfs/QmUUjyVWgTckJsvTVW9TQmNoxUMWRmExrRzaZXauGnv7ih"
  },
  {
    "id": "cyberkongz-866",
    "name": "CyberKongz #866",
    "image": "https://ipfs.io/ipfs/QmYGE3RiaLeki35vpQnrF46ScdK9MmgvpeDFUU2hwQbBkz"
  },
  {
    "id": "cyberkongz-949",
    "name": "CyberKongz #949",
    "image": "https://ipfs.io/ipfs/QmWZ9Pg5Fq8AV1aZ4C6v3Lo2C5xYDBAVeN3729orSYjh3h"
  },
  {
    "id": "cyberkongz-335",
    "name": "CyberKongz #335",
    "image": "https://ipfs.io/ipfs/QmUdXXamMZjLdq9a9VumzRD1wjUHEA9ULs11gxQNHMFG87"
  },
  {
    "id": "cyberkongz-978",
    "name": "CyberKongz #978",
    "image": "https://ipfs.io/ipfs/QmQv9Nqix2saxy4KWvVvbPHwvh8BjFWvy5iht4GiDmhmrw"
  }
            
            ];
        actualNftData = cyberkongzNFTs.find(nft => nft.id.toString() === itemId);
                      
                          } else if (collectionId === 'a-kid-called-beast') {
        const akidcalledbeastNFTs = [
            
  {
    "id": "beast-9899",
    "name": "BEAST #9899",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9899.png"
  },
  {
    "id": "beast-9896",
    "name": "BEAST #9896",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9896.png"
  },
  {
    "id": "beast-9893",
    "name": "BEAST #9893",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9893.png"
  },
  {
    "id": "beast-9895",
    "name": "BEAST #9895",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9895.png"
  },
  {
    "id": "beast-9898",
    "name": "BEAST #9898",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9898.png"
  },
  {
    "id": "beast-9900",
    "name": "BEAST #9900",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9900.png"
  },
  {
    "id": "beast-9894",
    "name": "BEAST #9894",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9894.png"
  },
  {
    "id": "beast-9897",
    "name": "BEAST #9897",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9897.png"
  },
  {
    "id": "beast-9892",
    "name": "BEAST #9892",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9892.png"
  },
  {
    "id": "beast-9890",
    "name": "BEAST #9890",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9890.png"
  },
  {
    "id": "beast-9887",
    "name": "BEAST #9887",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9887.png"
  },
  {
    "id": "beast-9881",
    "name": "BEAST #9881",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9881.png"
  },
  {
    "id": "beast-9889",
    "name": "BEAST #9889",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9889.png"
  },
  {
    "id": "beast-9885",
    "name": "BEAST #9885",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9885.png"
  },
  {
    "id": "beast-9879",
    "name": "BEAST #9879",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9879.png"
  },
  {
    "id": "beast-9876",
    "name": "BEAST #9876",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9876.png"
  },
  {
    "id": "beast-9875",
    "name": "BEAST #9875",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9875.png"
  },
  {
    "id": "beast-9859",
    "name": "BEAST #9859",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9859.png"
  },
  {
    "id": "beast-9860",
    "name": "BEAST #9860",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9860.png"
  },
  {
    "id": "beast-9852",
    "name": "BEAST #9852",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9852.png"
  },
  {
    "id": "beast-9855",
    "name": "BEAST #9855",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9855.png"
  },
  {
    "id": "beast-9853",
    "name": "BEAST #9853",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9853.png"
  },
  {
    "id": "beast-9848",
    "name": "BEAST #9848",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9848.png"
  },
  {
    "id": "beast-9846",
    "name": "BEAST #9846",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9846.png"
  },
  {
    "id": "beast-9845",
    "name": "BEAST #9845",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9845.png"
  },
  {
    "id": "beast-9844",
    "name": "BEAST #9844",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9844.png"
  },
  {
    "id": "beast-9886",
    "name": "BEAST #9886",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9886.png"
  },
  {
    "id": "beast-9883",
    "name": "BEAST #9883",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9883.png"
  },
  {
    "id": "beast-9882",
    "name": "BEAST #9882",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9882.png"
  },
  {
    "id": "beast-9880",
    "name": "BEAST #9880",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9880.png"
  },
  {
    "id": "beast-9878",
    "name": "BEAST #9878",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9878.png"
  },
  {
    "id": "beast-9877",
    "name": "BEAST #9877",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9877.png"
  },
  {
    "id": "beast-9874",
    "name": "BEAST #9874",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9874.png"
  },
  {
    "id": "beast-9873",
    "name": "BEAST #9873",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9873.png"
  },
  {
    "id": "beast-9871",
    "name": "BEAST #9871",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9871.png"
  },
  {
    "id": "beast-9870",
    "name": "BEAST #9870",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9870.png"
  },
  {
    "id": "beast-9869",
    "name": "BEAST #9869",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9869.png"
  },
  {
    "id": "beast-9867",
    "name": "BEAST #9867",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9867.png"
  },
  {
    "id": "beast-9868",
    "name": "BEAST #9868",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9868.png"
  },
  {
    "id": "beast-9866",
    "name": "BEAST #9866",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9866.png"
  },
  {
    "id": "beast-9865",
    "name": "BEAST #9865",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9865.png"
  },
  {
    "id": "beast-9864",
    "name": "BEAST #9864",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9864.png"
  },
  {
    "id": "beast-9862",
    "name": "BEAST #9862",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9862.png"
  },
  {
    "id": "beast-9863",
    "name": "BEAST #9863",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9863.png"
  },
  {
    "id": "beast-9861",
    "name": "BEAST #9861",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9861.png"
  },
  {
    "id": "beast-9858",
    "name": "BEAST #9858",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9858.png"
  },
  {
    "id": "beast-9856",
    "name": "BEAST #9856",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9856.png"
  },
  {
    "id": "beast-9854",
    "name": "BEAST #9854",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9854.png"
  },
  {
    "id": "beast-9851",
    "name": "BEAST #9851",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9851.png"
  },
  {
    "id": "beast-9849",
    "name": "BEAST #9849",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9849.png"
  },
  {
    "id": "beast-9850",
    "name": "BEAST #9850",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9850.png"
  },
  {
    "id": "beast-9847",
    "name": "BEAST #9847",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9847.png"
  },
  {
    "id": "beast-9843",
    "name": "BEAST #9843",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9843.png"
  },
  {
    "id": "beast-9891",
    "name": "BEAST #9891",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9891.png"
  },
  {
    "id": "beast-9884",
    "name": "BEAST #9884",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9884.png"
  },
  {
    "id": "beast-9872",
    "name": "BEAST #9872",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9872.png"
  },
  {
    "id": "beast-9857",
    "name": "BEAST #9857",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9857.png"
  },
  {
    "id": "beast-9888",
    "name": "BEAST #9888",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9888.png"
  },
  {
    "id": "beast-9842",
    "name": "BEAST #9842",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9842.png"
  },
  {
    "id": "beast-9841",
    "name": "BEAST #9841",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9841.png"
  },
  {
    "id": "beast-9839",
    "name": "BEAST #9839",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9839.png"
  },
  {
    "id": "beast-9838",
    "name": "BEAST #9838",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9838.png"
  },
  {
    "id": "beast-9840",
    "name": "BEAST #9840",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9840.png"
  },
  {
    "id": "beast-9835",
    "name": "BEAST #9835",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9835.png"
  },
  {
    "id": "beast-9834",
    "name": "BEAST #9834",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9834.png"
  },
  {
    "id": "beast-9837",
    "name": "BEAST #9837",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9837.png"
  },
  {
    "id": "beast-9836",
    "name": "BEAST #9836",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9836.png"
  },
  {
    "id": "beast-9833",
    "name": "BEAST #9833",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9833.png"
  },
  {
    "id": "beast-9832",
    "name": "BEAST #9832",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9832.png"
  },
  {
    "id": "beast-9831",
    "name": "BEAST #9831",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9831.png"
  },
  {
    "id": "beast-9830",
    "name": "BEAST #9830",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9830.png"
  },
  {
    "id": "beast-9829",
    "name": "BEAST #9829",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9829.png"
  },
  {
    "id": "beast-9828",
    "name": "BEAST #9828",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9828.png"
  },
  {
    "id": "beast-9827",
    "name": "BEAST #9827",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9827.png"
  },
  {
    "id": "beast-9826",
    "name": "BEAST #9826",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9826.png"
  },
  {
    "id": "beast-9823",
    "name": "BEAST #9823",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9823.png"
  },
  {
    "id": "beast-9824",
    "name": "BEAST #9824",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9824.png"
  },
  {
    "id": "beast-9825",
    "name": "BEAST #9825",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9825.png"
  },
  {
    "id": "beast-9822",
    "name": "BEAST #9822",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9822.png"
  },
  {
    "id": "beast-9821",
    "name": "BEAST #9821",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9821.png"
  },
  {
    "id": "beast-9818",
    "name": "BEAST #9818",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9818.png"
  },
  {
    "id": "beast-9817",
    "name": "BEAST #9817",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9817.png"
  },
  {
    "id": "beast-9815",
    "name": "BEAST #9815",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9815.png"
  },
  {
    "id": "beast-9814",
    "name": "BEAST #9814",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9814.png"
  },
  {
    "id": "beast-9813",
    "name": "BEAST #9813",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9813.png"
  },
  {
    "id": "beast-9812",
    "name": "BEAST #9812",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9812.png"
  },
  {
    "id": "beast-9811",
    "name": "BEAST #9811",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9811.png"
  },
  {
    "id": "beast-9808",
    "name": "BEAST #9808",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9808.png"
  },
  {
    "id": "beast-9809",
    "name": "BEAST #9809",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9809.png"
  },
  {
    "id": "beast-9810",
    "name": "BEAST #9810",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9810.png"
  },
  {
    "id": "beast-9807",
    "name": "BEAST #9807",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9807.png"
  },
  {
    "id": "beast-9805",
    "name": "BEAST #9805",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9805.png"
  },
  {
    "id": "beast-9804",
    "name": "BEAST #9804",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9804.png"
  },
  {
    "id": "beast-9803",
    "name": "BEAST #9803",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9803.png"
  },
  {
    "id": "beast-9802",
    "name": "BEAST #9802",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9802.png"
  },
  {
    "id": "beast-9801",
    "name": "BEAST #9801",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9801.png"
  },
  {
    "id": "beast-9800",
    "name": "BEAST #9800",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9800.png"
  },
  {
    "id": "beast-9798",
    "name": "BEAST #9798",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9798.png"
  },
  {
    "id": "beast-9796",
    "name": "BEAST #9796",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9796.png"
  },
  {
    "id": "beast-9797",
    "name": "BEAST #9797",
    "image": "https://ipfs.io/ipfs/QmQYeDSnhqUbNJ7XeMGSUV5KdHHMk5Hkjk2BpShZhKZ7gU/9797.png"
  }
             ];
        actualNftData = akidcalledbeastNFTs.find(nft => nft.id.toString() === itemId);
                          
                              } else if (collectionId === 'chimpers') {
        const chimpersNFTs = [
            
  {
    "id": "chimpers-5555",
    "name": "Chimpers #5555",
    "image": "https://arweave.net/sbEPC2PWVB_vWI_UsNaKvBOc_6HHUpbre9PhQubI6GQ/"
  },
  {
    "id": "chimpers-5554",
    "name": "Chimpers #5554",
    "image": "https://arweave.net/kSJC5aPtoNdJ7ch_mMw7-FoHFwtz7Ff5PwfU_X5mp5U/"
  },
  {
    "id": "chimpers-5553",
    "name": "Chimpers #5553",
    "image": "https://arweave.net/LA2p41v-aG2lc6xcCe8RIx2cASXYEX-HEiYc9Tw_CQI/"
  },
  {
    "id": "chimpers-5551",
    "name": "Chimpers #5551",
    "image": "https://arweave.net/y2Ct7SumaD5iQ8juMjgYOy8hibVYJqE4Q57-tpu1L4s/"
  },
  {
    "id": "chimpers-5552",
    "name": "Chimpers #5552",
    "image": "https://arweave.net/Xr0dmk9iHq2tyMTF0tFuqfHlfKPhJsmJ6cEr6aBm-eo/"
  },
  {
    "id": "chimpers-5550",
    "name": "Chimpers #5550",
    "image": "https://arweave.net/Tkxx6v8EF4NYNPa0sdBcK_fIjrdarlF6uO8xaMFeu8I/"
  },
  {
    "id": "chimpers-5549",
    "name": "Chimpers #5549",
    "image": "https://arweave.net/E9R02WW7heVKrOycFsYxaMzJ895GgSeMPy62G7Zx-Gs/"
  },
  {
    "id": "chimpers-5548",
    "name": "Chimpers #5548",
    "image": "https://qktbvijx2nzdmrfdefyoi4zmmld5cbxt6rg26nxozcmxa6y3jfka.arweave.net/gqYaoTfTcjZEoyFw5HMsYsfRBvP0Ta827siZcHsbSVQ/"
  },
  {
    "id": "chimpers-5547",
    "name": "Chimpers #5547",
    "image": "https://arweave.net/6SPGnzkzEsPRJtk286Vd9QPy_m6uhwC7witHYShFAws/"
  },
  {
    "id": "chimpers-5546",
    "name": "Chimpers #5546",
    "image": "https://arweave.net/0M-noAULahBGzfaDqgl3sS3v2dXtGZ1XPjuY6bByI08/"
  },
  {
    "id": "chimpers-5545",
    "name": "Chimpers #5545",
    "image": "https://mjemsshh2k5nahalfm453allc6cnjuyumtbhmiyrz22slus33xtq.arweave.net/YkjJSOfSutAcCys53YFrF4TU0xRkwnYjEc61JdJb3ec/"
  },
  {
    "id": "chimpers-5544",
    "name": "Chimpers #5544",
    "image": "https://mjscefvrlz4e5z3rtq5se47vsdajw7nrj5nre7mbzee3tzs3ivza.arweave.net/YmQiFrFeeE7ncZw7InP1kMCbfbFPWxJ9gckJueZbRXI/"
  },
  {
    "id": "chimpers-5543",
    "name": "Chimpers #5543",
    "image": "https://ffnii32vguikhchxer4mprregaofchy3gv6yvkr2yjysi7jyc2vq.arweave.net/KVqEb1U1EKOI9yR4x8YkMBxRHxs1fYqqOsJxJH04Fqs/"
  },
  {
    "id": "chimpers-5542",
    "name": "Chimpers #5542",
    "image": "https://arweave.net/FrGVuLhGVXf4_I2NivXeqUMmg8WNQQULM_F8gpxWADc/"
  },
  {
    "id": "chimpers-5541",
    "name": "Chimpers #5541",
    "image": "https://ypewigdlm43qh4655qszrw5e6jkvpzx52q5hnxtgbessxobotz3a.arweave.net/w8lkGGtnNwPz3ewlmNuk8lVX5v3UOnbeZgklK7gunnY/"
  },
  {
    "id": "chimpers-5536",
    "name": "Chimpers #5536",
    "image": "https://arweave.net/3Fe0ECuu8lAOAujLCjc-oB3c8bXyYDaW7YjDcJOXsak/"
  },
  {
    "id": "chimpers-5535",
    "name": "Chimpers #5535",
    "image": "https://arweave.net/Qw6Rg17BWu7O3xCykTWTRaVW1zMuwKGmdXWW4-EeJc0/"
  },
  {
    "id": "chimpers-5540",
    "name": "Chimpers #5540",
    "image": "https://arweave.net/M0nIYph8-h8O4otgyGDKwBNcXvcZovBoKo757o7mI9Q/"
  },
  {
    "id": "chimpers-5537",
    "name": "Chimpers #5537",
    "image": "https://2jtnutn4vpcniysaa2lncyely5pnrxricuubxzgv3ps7n6ughtsa.arweave.net/0mbaTbyrxNRiQAaW0WCLx17Y3igVKBvk1dvl9vqGPOQ/"
  },
  {
    "id": "chimpers-5539",
    "name": "Chimpers #5539",
    "image": "https://arweave.net/9Hq_BHfWgOnPEGqUwUqMx7fOoZI4RAy2eh2b-2Z5Uv8/"
  },
  {
    "id": "chimpers-5538",
    "name": "Chimpers #5538",
    "image": "https://arweave.net/ixvx1i-xYu7pS-fe0D6peMNUVkr38yDpsP4Zjl57vCA/"
  },
  {
    "id": "chimpers-5534",
    "name": "Chimpers #5534",
    "image": "https://arweave.net/BS-uQ6lwbrzF_ac2zO4Ga_QtK1383Z2ald1HRnMb6q0/"
  },
  {
    "id": "chimpers-5533",
    "name": "Chimpers #5533",
    "image": "https://arweave.net/--ykb_rD6Egw2W3n_8Iq7SmLRPFFYuydG27fu6s73Tc/"
  },
  {
    "id": "chimpers-5532",
    "name": "Chimpers #5532",
    "image": "https://arweave.net/eGTpyJXLD44MYddp8W2b45DpgDPjlPg4lUGs_RJ2nMQ/"
  },
  {
    "id": "chimpers-5530",
    "name": "Chimpers #5530",
    "image": "https://arweave.net/NEe3sH4S7w99Iqm6wMNQHC4z10dEfEG_0JmQ69V7qLY/"
  },
  {
    "id": "chimpers-5531",
    "name": "Chimpers #5531",
    "image": "https://arweave.net/oGiNfOruqOR4o9bGiNWxuIqJX_6PbGAHsWMdkTCdmBY/"
  },
  {
    "id": "chimpers-5529",
    "name": "Chimpers #5529",
    "image": "https://yjnxbefiirib53vikjxgncwlls5pnmgwgzr56bzu4uzo4oxdrbja.arweave.net/wltwkKhEUB7uqFJuZorLXLr2sNY2Y98HNOUy7jrjiFI/"
  },
  {
    "id": "chimpers-5527",
    "name": "Chimpers #5527",
    "image": "https://arweave.net/SJsy3RT0p6Quo_NKGs1FrPCqlMn5DUyGBKauyxTwhw0/"
  },
  {
    "id": "chimpers-5528",
    "name": "Chimpers #5528",
    "image": "https://arweave.net/tNI_lxlKJ1Qn1PEn_10ieiKT0zNxfNhAJH7Y9K_mmOE/"
  },
  {
    "id": "chimpers-5518",
    "name": "Chimpers #5518",
    "image": "https://arweave.net/qPjvR9PjDoXdZPjmFHKi87oIK_FyZ0lX0khLDHQEXeU/"
  },
  {
    "id": "chimpers-5526",
    "name": "Chimpers #5526",
    "image": "https://arweave.net/dMSi6VCeEUYOgdS8xHgLR75fStb8z3oKo6AeH_f9wGM/"
  },
  {
    "id": "chimpers-5525",
    "name": "Chimpers #5525",
    "image": "https://nxkofa43mo5iiihxjolnklgonunwy7ivs7uy65bcn256qrvznnza.arweave.net/bdTig5tjuoQg90uW1SzObRtsfRWX6Y90Im676Ea5a3I/"
  },
  {
    "id": "chimpers-5523",
    "name": "Chimpers #5523",
    "image": "https://arweave.net/sA-j9MO8dVfScu552D0CdiHyrf2qiNgIEocsNAGf6eg/"
  },
  {
    "id": "chimpers-5524",
    "name": "Chimpers #5524",
    "image": "https://arweave.net/vZlbMuu1pFd-MdRUVGW-pcOie-Arw7pDjDSvlEQgotY/"
  },
  {
    "id": "chimpers-5521",
    "name": "Chimpers #5521",
    "image": "https://ze2m7gpuapatvhbyzoofbgfunwnem34zn6k4t24rjqhfuyk5mnzq.arweave.net/yTTPmfQDwTqcOMucUJi0bZpGb5lvlcnrkUwOWmFdY3M/"
  },
  {
    "id": "chimpers-5519",
    "name": "Chimpers #5519",
    "image": "https://6qttwpld4xtrrn3ow2eo35ieubceo45onrljiltwmnvnqxe5f5ya.arweave.net/9Cc7PWPl5xi3braI7fUEoERHc65sVpQudmNq2FydL3A/"
  },
  {
    "id": "chimpers-5517",
    "name": "Chimpers #5517",
    "image": "https://arweave.net/Glr-Pe94cBw_0apUfrPfvJYwrNu2m4USWTWBYKfOluM/"
  },
  {
    "id": "chimpers-5516",
    "name": "Chimpers #5516",
    "image": "https://arweave.net/d6AQcWeRXifEhegAQ9Cq3YW1W_7kME1rshqcd0MVmzk/"
  },
  {
    "id": "chimpers-5522",
    "name": "Chimpers #5522",
    "image": "https://evrq2go32ataqsubqjypmunbt3jsxwfcwdocjuidujik672haybq.arweave.net/JWMNGdvQJghKgYJw9lGhntMr2KKw3CTRA6JQr39HBgM/"
  },
  {
    "id": "chimpers-5512",
    "name": "Chimpers #5512",
    "image": "https://arweave.net/v2q0UfWSO6X9FKsbZ2TGF_ONARM7WA6Pc4jw0zZDPsU/"
  },
  {
    "id": "chimpers-5511",
    "name": "Chimpers #5511",
    "image": "https://arweave.net/4VnnxipTm-ta2wuO2Ofz4Q8Ahbc0oKN1ourc81HoKBQ/"
  },
  {
    "id": "chimpers-5513",
    "name": "Chimpers #5513",
    "image": "https://arweave.net/Lw1Tr8eK-_4ctmDvPsBmFtluLSPdQkpq2p7_PSracoQ/"
  },
  {
    "id": "chimpers-5514",
    "name": "Chimpers #5514",
    "image": "https://arweave.net/36It65B0o7H7D7I8ZGGvaI2_sclLEpBQNAxoA-7_lEg/"
  },
  {
    "id": "chimpers-5520",
    "name": "Chimpers #5520",
    "image": "https://arweave.net/CObgsa1ZqQyl1yPu8OCpt4ZNZmwzA_lyxoo8rj6zv6c/"
  },
  {
    "id": "chimpers-5510",
    "name": "Chimpers #5510",
    "image": "https://arweave.net/RSelSBP-nXl1mV7nZc5L5luL57QbyU3JtHVKuw3oI_M/"
  },
  {
    "id": "chimpers-5515",
    "name": "Chimpers #5515",
    "image": "https://5m53zk4nmnwatxq4aaghzqvgyhlnevbduwf5zuwuiknva4yw5anq.arweave.net/6zu8q41jbAneHAAMfMKmwdbSVCOli9zS1EKbUHMW6Bs/"
  },
  {
    "id": "chimpers-5508",
    "name": "Chimpers #5508",
    "image": "https://arweave.net/UkR146sno_1D6--dvFie-eEsjujL1kbW57qYrE0DKag/"
  },
  {
    "id": "chimpers-5509",
    "name": "Chimpers #5509",
    "image": "https://vhde3tcqllugdfpvdq6qb7wrduowydepavqlhkpad43rxvno34iq.arweave.net/qcZNzFBa6GGV9Rw9AP7RHR1sDI8FYLOp4B83G9Wu3xE/"
  },
  {
    "id": "chimpers-5507",
    "name": "Chimpers #5507",
    "image": "https://isy466bspdeglnc2jppqadwb45vop3pfcbxdw4uwpufyetpefhrq.arweave.net/RLHPeDJ4yGW0WkvfAA7B52rn7eUQbjtyln0Lgk3kKeM/"
  },
  {
    "id": "chimpers-5506",
    "name": "Chimpers #5506",
    "image": "https://2lzaoghyonbf6xauneoisgvn3pcz5rhwybvctunuyyrn2tuzen3q.arweave.net/0vIHGPhzQl9cFGkciRqt28WexPbAainRtMYi3U6ZI3c/"
  },
  {
    "id": "chimpers-5505",
    "name": "Chimpers #5505",
    "image": "https://arweave.net/FIooOXywKqF9la8OL-hA3__WpkRFw0cFffC8u5RpKyM/"
  },
  {
    "id": "chimpers-5500",
    "name": "Chimpers #5500",
    "image": "https://ub6ltkkewu2zkl64roiez4q4eyvxxbhzkjtkcnokslse4afppk5a.arweave.net/oHy5qUS1NZUv3IuQTPIcJit7hPlSZqE1ypLkTgCvero/"
  },
  {
    "id": "chimpers-5501",
    "name": "Chimpers #5501",
    "image": "https://arweave.net/BP_t3535MntO52ozucCOVUYlIQ-lHjkYnT8eLczGhTI/"
  },
  {
    "id": "chimpers-5504",
    "name": "Chimpers #5504",
    "image": "https://ohvobkhu25cg2qpjmh5tjvckldpooqqu3hvuwzlludbwihqygkpq.arweave.net/cergqPTXRG1B6WH7NNRKWN7nQhTZ60tla6DDZB4YMp8/"
  },
  {
    "id": "chimpers-5503",
    "name": "Chimpers #5503",
    "image": "https://arweave.net/u0jjAvbe9ITPKNSuFIwLUWv4zS47eLQfH82J-pEG6qs/"
  },
  {
    "id": "chimpers-5496",
    "name": "Chimpers #5496",
    "image": "https://arweave.net/ODQ6G2ThU-SfUypaiFHGD7KF5Ij_Dm6mv1S6rv1zkfA/"
  },
  {
    "id": "chimpers-5502",
    "name": "Chimpers #5502",
    "image": "https://arweave.net/uQbU2wIic3Ska8IWfhDWcupMMjI_dK2v2nH4EAa4X3g/"
  },
  {
    "id": "chimpers-5498",
    "name": "Chimpers #5498",
    "image": "https://arweave.net/fbbA4yf6M-u0n-kgkUpcBwhsiyxvg1XaPqKnsPKS4GI/"
  },
  {
    "id": "chimpers-5497",
    "name": "Chimpers #5497",
    "image": "https://arweave.net/S09tYRrWTuLhd9k1BBBaTU1j-dmapNVBhC-osHvC7CQ/"
  },
  {
    "id": "chimpers-5499",
    "name": "Chimpers #5499",
    "image": "https://sojgrl2rdcdxqtdmbrck2pbbjq6whyl2lglctipcrgfhzifscqqa.arweave.net/k5Jor1EYh3hMbAxErTwhTD1j4XpZlimh4omKfKCyFCA/"
  },
  {
    "id": "chimpers-5495",
    "name": "Chimpers #5495",
    "image": "https://arweave.net/gGjQVGWVm7cwjkyj9N67ZC07D7F3LoB0FlMCV_j0fMo/"
  },
  {
    "id": "chimpers-5494",
    "name": "Chimpers #5494",
    "image": "https://mccpdn3id4xfdnc2rhomzsh4hsggcy6hpg4lkln2oq4m3j2flz5q.arweave.net/YITxt2gfLlG0WonczMj8PIxhY8d5uLUtunQ4zadFXns/"
  },
  {
    "id": "chimpers-5492",
    "name": "Chimpers #5492",
    "image": "https://s6kq2l3xyagq5fg6xjl252ozib7ekx4sudrdkormvgnj62c7ikea.arweave.net/l5UNL3fADQ6U3rpXrunZQH5FX5Kg4jU6LKman2hfQog/"
  },
  {
    "id": "chimpers-5491",
    "name": "Chimpers #5491",
    "image": "https://4ocsq4nk3cdxmoorjosdj3ue3atflfvsanizirzfl5hlws7xr3ma.arweave.net/44UocarYh3Y50UukNO6E2CZVlrIDUZRHJV9Ou0v3jtg/"
  },
  {
    "id": "chimpers-5493",
    "name": "Chimpers #5493",
    "image": "https://jstkdjaelh27kaynnohgdcgyrsig5ezgnsgkrza5hujonsilcvna.arweave.net/TKahpARZ9fUDDWuOYYjYjJBukyZsjKjkHT0S5skLFVo/"
  },
  {
    "id": "chimpers-5489",
    "name": "Chimpers #5489",
    "image": "https://c4guyvoykfb2ju7hgtgu2c3sgzg7cnkoohiz62lezem7hutwyzla.arweave.net/Fw1MVdhRQ6TT5zTNTQtyNk3xNU5x0Z9pZMkZ89J2xlY/"
  },
  {
    "id": "chimpers-5487",
    "name": "Chimpers #5487",
    "image": "https://mwrbsdw6f3wkakmmcyxf6ibmjmjpciltnspju6lloksjn5yvzo5a.arweave.net/ZaIZDt4u7KApjBYuXyAsSxLxIXNsnpp5a3KklvcVy7o/"
  },
  {
    "id": "chimpers-5488",
    "name": "Chimpers #5488",
    "image": "https://xaqmu2d2fb3bfh32hztn2kocidzigy4qsw4bd2iuds4qamagk4xq.arweave.net/uCDKaHoodhKfej5m3SnCQPKDY5CVuBHpFBy5ADAGVy8/"
  },
  {
    "id": "chimpers-5490",
    "name": "Chimpers #5490",
    "image": "https://arweave.net/9iYc9s8Kz-OO8xYJMlmkYZlOQbogIKUfb-DQtesTmjY/"
  },
  {
    "id": "chimpers-5484",
    "name": "Chimpers #5484",
    "image": "https://arweave.net/IX27biTE3mUrWeazMDhps_bH1gr_qYDrLjv3ItjjEZY/"
  },
  {
    "id": "chimpers-5483",
    "name": "Chimpers #5483",
    "image": "https://arweave.net/mo-1H2S30l6uTJY1hraTLpEHmyPrmj3NSo7IWawHFHE/"
  },
  {
    "id": "chimpers-5485",
    "name": "Chimpers #5485",
    "image": "https://arweave.net/4PD1Gguh8gwGsKBxHKj11y7flEX3MnYLXfQZ91H--90/"
  },
  {
    "id": "chimpers-5486",
    "name": "Chimpers #5486",
    "image": "https://arweave.net/JvqeGq_aYQ1WQdQRTL-inhmu4RbrigFJmT8VoKgTp-M/"
  },
  {
    "id": "chimpers-5482",
    "name": "Chimpers #5482",
    "image": "https://j53dhi5v2m24kn2pwimdbblcke67vlgopkaqqcxwiwgadmebxf5q.arweave.net/T3Yzo7XTNcU3T7IYMIViUT36rM56gQgK9kWMAbCBuXs/"
  },
  {
    "id": "chimpers-5478",
    "name": "Chimpers #5478",
    "image": "https://arweave.net/7psOqsXP01goqEZzstgNrKA9k6UB1-7QfhV6V8wluhQ/"
  },
  {
    "id": "chimpers-5479",
    "name": "Chimpers #5479",
    "image": "https://arweave.net/7oXmqKd9eJO1ISaH-ZgErAxDFSHytkVHZDGHjR1pok8/"
  },
  {
    "id": "chimpers-5481",
    "name": "Chimpers #5481",
    "image": "https://arweave.net/LTFckjX4LnLe3-pvpH0TqBeCIDqszZRDW8S3F4b6mLo/"
  },
  {
    "id": "chimpers-5477",
    "name": "Chimpers #5477",
    "image": "https://arweave.net/9UH1Lue81FO4y_GqbJ2w30qmgHgRRH0DODVcTd3wuXQ/"
  },
  {
    "id": "chimpers-5476",
    "name": "Chimpers #5476",
    "image": "https://arweave.net/64emY3IzEnQVopOVbD37tQUiBZ8Q0Sm-129a9G6z_ik/"
  },
  {
    "id": "chimpers-5480",
    "name": "Chimpers #5480",
    "image": "https://cwdpskbilpk3wk7ppjaq6eaum42ogitupeitzl2j6qisqounvc3a.arweave.net/FYb5KChb1bsr73pBDxAUZzTjInR5ETyvSfQRKDqNqLY/"
  },
  {
    "id": "chimpers-5475",
    "name": "Chimpers #5475",
    "image": "https://arweave.net/0647wJpMNwSNjUUGpJLaj70xycduREUeDwdEL-tvJeA/"
  },
  {
    "id": "chimpers-5474",
    "name": "Chimpers #5474",
    "image": "https://arweave.net/cxtT-aSZ9XxvlG7lEsw1cyg79UkaSHY6xzr9dbC2z8A/"
  },
  {
    "id": "chimpers-5473",
    "name": "Chimpers #5473",
    "image": "https://arweave.net/-ehOKCPSRgLWez4Ty1I9lHqomzO9fpXCFRrVMnwyGvA/"
  },
  {
    "id": "chimpers-5472",
    "name": "Chimpers #5472",
    "image": "https://arweave.net/-iiqduYxQRzi0VgU-Xn4odkOs4mA7GcBokhPZrFNYCU/"
  },
  {
    "id": "chimpers-5471",
    "name": "Chimpers #5471",
    "image": "https://arweave.net/61RlYLohwia-jrO70zL37R1kWApucZP88StYhKOLoIk/"
  },
  {
    "id": "chimpers-5470",
    "name": "Chimpers #5470",
    "image": "https://5e6v3ynzrzub4kvaeojqfahcozea5lbyin4ivzcz23bztfihinma.arweave.net/6T1d4bmOaB4qoCOTAoDidkgOrDhDeIrkWdbDmZUHQ1g/"
  },
  {
    "id": "chimpers-5469",
    "name": "Chimpers #5469",
    "image": "https://rwk3ahughm5svb3nstresaemhjebx2yhzztxiyyoympa5rdtljta.arweave.net/jZWwHoY7OyqHbZTiSQCMOkgb6wfOZ3RjDsMeDsRzWmY/"
  },
  {
    "id": "chimpers-5468",
    "name": "Chimpers #5468",
    "image": "https://arweave.net/bWWIbHbx5OTgG3QZRmj8-JKwMeO7Ar59rloNIxw6VE0/"
  },
  {
    "id": "chimpers-5467",
    "name": "Chimpers #5467",
    "image": "https://6onxvregrpfcpkcrfwhbewdx43huvjugjwcgqqyprpthnb5wjnsq.arweave.net/85t6xIaLyieoUS2OElh35s9KpoZNhGhDD4vmdoe2S2U/"
  },
  {
    "id": "chimpers-5464",
    "name": "Chimpers #5464",
    "image": "https://arweave.net/u4R_kU6SLNP6VoatFVBYVBRUX5-fRhRY6Zk1Vp8_c6A/"
  },
  {
    "id": "chimpers-5466",
    "name": "Chimpers #5466",
    "image": "https://arweave.net/Q9SBUx8k2KbI3tv9bULo2CfwQ9bnqNpgE_66toxsOmI/"
  },
  {
    "id": "chimpers-5465",
    "name": "Chimpers #5465",
    "image": "https://arweave.net/QVG9Kctq2cyfeWwYrRNirv3RkXVFtvBgdN-m_UOzu7o/"
  },
  {
    "id": "chimpers-5463",
    "name": "Chimpers #5463",
    "image": "https://arweave.net/0Ksw2X7YLKo9guNyeKUW5vAm3bjW2J-LKZyhiyce_ac/"
  },
  {
    "id": "chimpers-5462",
    "name": "Chimpers #5462",
    "image": "https://is3xsbd55wngl3jf6ph5irjymtzhjapkr4tmgfqjvrwzksw3bb7q.arweave.net/RLd5BH3tmmXtJfPP1EU4ZPJ0geqPJsMWCaxtlUrbCH8/"
  },
  {
    "id": "chimpers-5461",
    "name": "Chimpers #5461",
    "image": "https://arweave.net/Vfyw_idLlmGdXney32oVnm7kbunhgt0U9Ru03EpMOpY/"
  },
  {
    "id": "chimpers-5460",
    "name": "Chimpers #5460",
    "image": "https://arweave.net/uE8DI_ePG_mPLytYbDmF0KZ2YgHpH-dHPByW9IeUNzU/"
  },
  {
    "id": "chimpers-5459",
    "name": "Chimpers #5459",
    "image": "https://m5cqwv2jqm44jezroa4hvpxxpbeqxbi3aijfd623n4jq2ktk3kza.arweave.net/Z0ULV0mDOcSTMXA4er73eEkLhRsCElH7W28TDSpq2rI/"
  },
  {
    "id": "chimpers-5458",
    "name": "Chimpers #5458",
    "image": "https://arweave.net/9zWLtfFFJK32gPvHKwGATleuMpKYnDNLs-7TerCnaP0/"
  },
  {
    "id": "chimpers-5457",
    "name": "Chimpers #5457",
    "image": "https://t65vydokceoze4gg6hhncjbfnijr2q2y5f7yjjvjr3ptppftpcma.arweave.net/n7tcDcoRHZJwxvHO0SQlahMdQ1jpf4SmqY7fN7yzeJg/"
  },
  {
    "id": "chimpers-5456",
    "name": "Chimpers #5456",
    "image": "https://v6gf66ed5dfwxol3pk6khomzkhchi4boibnqavlcexxrtrmptzeq.arweave.net/r4xfeIPoy2u5e3q8o7mZUcR0cC5AWwBVYiXvGcWPnkk/"
  }
             ];
        actualNftData = chimpersNFTs.find(nft => nft.id.toString() === itemId);
                              
                                  
              } else if (collectionId === 'proof-of-narnian-nft') {
        const proofofnarnianNFTs = [
            
  {
    "id": "narnian-3333",
    "name": "Proof of Narnian #3333",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3333?s=b34a79d54c309a6f54b5380ee2400c8a"
  },
  {
    "id": "narnian-3332",
    "name": "Proof of Narnian #3332",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3332?s=78aab1c71574912c8bdefa104727efe8"
  },
  {
    "id": "narnian-3331",
    "name": "Proof of Narnian #3331",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3331?s=77b2bc77365781510b18fe61b7bc0bee"
  },
  {
    "id": "narnian-3330",
    "name": "Proof of Narnian #3330",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3330?s=d5f8aa8bd7dd1b7066a1bfcccd0e4ef8"
  },
  {
    "id": "narnian-3329",
    "name": "Proof of Narnian #3329",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3329?s=caa1b43e84fd52943befc25e29e33c8a"
  },
  {
    "id": "narnian-3328",
    "name": "Proof of Narnian #3328",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3328?s=b5bc01f3ff981bd52b04677a798225f8"
  },
  {
    "id": "narnian-3327",
    "name": "Proof of Narnian #3327",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3327?s=cfa2105d3c25ecfec6c31df70141c414"
  },
  {
    "id": "narnian-3326",
    "name": "Proof of Narnian #3326",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3326?s=0c010670d77f356f9c6ca1e9897d6669"
  },
  {
    "id": "narnian-3325",
    "name": "Proof of Narnian #3325",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3325?s=0bc2abe12cb2087c1909f050ed8bd962"
  },
  {
    "id": "narnian-3324",
    "name": "Proof of Narnian #3324",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3324?s=b3ef907b1fbbeea749557d8af8f58778"
  },
  {
    "id": "narnian-3323",
    "name": "Proof of Narnian #3323",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3323?s=0396fe4c6e13711f80165b4e48e82294"
  },
  {
    "id": "narnian-3322",
    "name": "Proof of Narnian #3322",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3322?s=c551f34f5f7a24b997b0a1bdfc78bf44"
  },
  {
    "id": "narnian-3321",
    "name": "Proof of Narnian #3321",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3321?s=4a68dc69c17aaca03cf38f11e24b6469"
  },
  {
    "id": "narnian-3320",
    "name": "Proof of Narnian #3320",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3320?s=d00c4824142f246f13dad5d6e956ae1c"
  },
  {
    "id": "narnian-3319",
    "name": "Proof of Narnian #3319",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3319?s=1d806c3bff3b028dbaacecda854dc31c"
  },
  {
    "id": "narnian-3318",
    "name": "Proof of Narnian #3318",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3318?s=499b850179c3eea61abeb827731b5aff"
  },
  {
    "id": "narnian-3317",
    "name": "Proof of Narnian #3317",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3317?s=d28497dbd8516ad06f40189ecce9585f"
  },
  {
    "id": "narnian-3316",
    "name": "Proof of Narnian #3316",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3316?s=df3def21cfe104d5f904fe2e4a27fd04"
  },
  {
    "id": "narnian-3315",
    "name": "Proof of Narnian #3315",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3315?s=e68399b71629f6c1a83a775a44ccb054"
  },
  {
    "id": "narnian-3314",
    "name": "Proof of Narnian #3314",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3314?s=d98feb992692762c9b0326b7ed85a571"
  },
  {
    "id": "narnian-3313",
    "name": "Proof of Narnian #3313",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3313?s=e2ded95ded5615cc629c85fe761b96db"
  },
  {
    "id": "narnian-3312",
    "name": "Proof of Narnian #3312",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3312?s=bc68a69223abeb185b15fe11c512add1"
  },
  {
    "id": "narnian-3311",
    "name": "Proof of Narnian #3311",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3311?s=6022ea336492d676b0e4baa2a5c752b2"
  },
  {
    "id": "narnian-3310",
    "name": "Proof of Narnian #3310",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3310?s=99fcbc76c036456b5010b8e8e69025f3"
  },
  {
    "id": "narnian-3309",
    "name": "Proof of Narnian #3309",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3309?s=d936276e7a53080f78468237a6cc767b"
  },
  {
    "id": "narnian-3308",
    "name": "Proof of Narnian #3308",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3308?s=19f2cc8000a99c1b8698157b6451abf6"
  },
  {
    "id": "narnian-3307",
    "name": "Proof of Narnian #3307",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3307?s=2858d3e413ccd59fbcdd4db911e19472"
  },
  {
    "id": "narnian-3306",
    "name": "Proof of Narnian #3306",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3306?s=507e817dbc0b66d6a12c62176f250255"
  },
  {
    "id": "narnian-3305",
    "name": "Proof of Narnian #3305",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3305?s=1c6f0c8390d18e5faf955431e74f5b5f"
  },
  {
    "id": "narnian-3304",
    "name": "Proof of Narnian #3304",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3304?s=f3100c916568ab2eb67de31ed67b7a4f"
  },
  {
    "id": "narnian-3303",
    "name": "Proof of Narnian #3303",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3303?s=9101efa1b7c784c01673eb472d0778e0"
  },
  {
    "id": "narnian-3302",
    "name": "Proof of Narnian #3302",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3302?s=e0b8bbbacf9955dcd3fcd674fb51f65d"
  },
  {
    "id": "narnian-3301",
    "name": "Proof of Narnian #3301",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3301?s=31938f7de6b88ad093008abd38fec6b6"
  },
  {
    "id": "narnian-3300",
    "name": "Proof of Narnian #3300",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3300?s=e989220618b691180795749b25f836f0"
  },
  {
    "id": "narnian-3299",
    "name": "Proof of Narnian #3299",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3299?s=6e47bb30da7217145e67da45264e1913"
  },
  {
    "id": "narnian-3298",
    "name": "Proof of Narnian #3298",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3298?s=494bff8c35046ba514d15bddbb4d9323"
  },
  {
    "id": "narnian-3297",
    "name": "Proof of Narnian #3297",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3297?s=0b1427fd8dd62e1b4f1ad3a5a3129871"
  },
  {
    "id": "narnian-3296",
    "name": "Proof of Narnian #3296",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3296?s=7e22cfcf7a673845e131357a1fab2fe6"
  },
  {
    "id": "narnian-3295",
    "name": "Proof of Narnian #3295",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3295?s=379e3721ad5a54fea0050af3cd8962ea"
  },
  {
    "id": "narnian-3294",
    "name": "Proof of Narnian #3294",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3294?s=8fbed4c21384030276714ea96af3a0d0"
  },
  {
    "id": "narnian-3293",
    "name": "Proof of Narnian #3293",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3293?s=be537d29bddfbad870646db35fef16ea"
  },
  {
    "id": "narnian-3292",
    "name": "Proof of Narnian #3292",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3292?s=72494ca0a4875d25117ace8fa572fe08"
  },
  {
    "id": "narnian-3291",
    "name": "Proof of Narnian #3291",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3291?s=d39b1c88cafe37f9507c2bf1bc7a1419"
  },
  {
    "id": "narnian-3290",
    "name": "Proof of Narnian #3290",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3290?s=2f270e592bddaa8c1bee4078ada5df11"
  },
  {
    "id": "narnian-3289",
    "name": "Proof of Narnian #3289",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3289?s=694109aac4a5c2bfb099d306918910c5"
  },
  {
    "id": "narnian-3288",
    "name": "Proof of Narnian #3288",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3288?s=ac73f59a860731c33780114cd0f142a2"
  },
  {
    "id": "narnian-3287",
    "name": "Proof of Narnian #3287",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3287?s=be93d0aa0b73376ac894e94c51b6a268"
  },
  {
    "id": "narnian-3286",
    "name": "Proof of Narnian #3286",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3286?s=8db46a6e065584fb4030148d20611c6c"
  },
  {
    "id": "narnian-3285",
    "name": "Proof of Narnian #3285",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3285?s=a9c9a019c07552576cde0d4709a6143f"
  },
  {
    "id": "narnian-3284",
    "name": "Proof of Narnian #3284",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3284?s=8fe4534a14ee50cd07022183919a5899"
  },
  {
    "id": "narnian-3283",
    "name": "Proof of Narnian #3283",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3283?s=265058f2965aebeeb4e3e6160dd4263d"
  },
  {
    "id": "narnian-3282",
    "name": "Proof of Narnian #3282",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3282?s=12e14ecb47c35559dd1ba698fb22c614"
  },
  {
    "id": "narnian-3281",
    "name": "Proof of Narnian #3281",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3281?s=6d5749f74bf1b4dd6c3e2cf21dd2b7d2"
  },
  {
    "id": "narnian-3280",
    "name": "Proof of Narnian #3280",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3280?s=097bd90df69c35411683c0bf26b87616"
  },
  {
    "id": "narnian-3279",
    "name": "Proof of Narnian #3279",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3279?s=98d7a74e3c32d308b4d12e9f29677276"
  },
  {
    "id": "narnian-3278",
    "name": "Proof of Narnian #3278",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3278?s=fc03f8749114d3594cec722f98bdb756"
  },
  {
    "id": "narnian-3277",
    "name": "Proof of Narnian #3277",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3277?s=97375cf6e8952e11e1a9620817c51d37"
  },
  {
    "id": "narnian-3276",
    "name": "Proof of Narnian #3276",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3276?s=fd648066573a33aac2105f1312883bb0"
  },
  {
    "id": "narnian-3275",
    "name": "Proof of Narnian #3275",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3275?s=10bcfb447a4532ecbe0508d880ccceb3"
  },
  {
    "id": "narnian-3274",
    "name": "Proof of Narnian #3274",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3274?s=613e4e51521a5b162cc878e72f3c6417"
  },
  {
    "id": "narnian-3273",
    "name": "Proof of Narnian #3273",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3273?s=2149f3961622c93da1cd77901a1e3156"
  },
  {
    "id": "narnian-3272",
    "name": "Proof of Narnian #3272",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3272?s=b22424e76205f7903b221f918ae0d291"
  },
  {
    "id": "narnian-3271",
    "name": "Proof of Narnian #3271",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3271?s=e058d3e1b8c7f1914d734e176b40e8d0"
  },
  {
    "id": "narnian-3270",
    "name": "Proof of Narnian #3270",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3270?s=da41c2b0a2ab88e61f9b51512eefa62c"
  },
  {
    "id": "narnian-3269",
    "name": "Proof of Narnian #3269",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3269?s=622c0bcc64cfe82249ab587284a0971b"
  },
  {
    "id": "narnian-3268",
    "name": "Proof of Narnian #3268",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3268?s=754446622986b3d6a1f3fc93e59d1415"
  },
  {
    "id": "narnian-3267",
    "name": "Proof of Narnian #3267",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3267?s=fd69bf8ca34ae2c7e4556b6a64426d21"
  },
  {
    "id": "narnian-3266",
    "name": "Proof of Narnian #3266",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3266?s=20963667d848e57e840d11666e5fe51a"
  },
  {
    "id": "narnian-3265",
    "name": "Proof of Narnian #3265",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3265?s=7ef33fb387ff65bf747374959af19ec6"
  },
  {
    "id": "narnian-3264",
    "name": "Proof of Narnian #3264",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3264?s=2a517538714f4b326213b43779f28db1"
  },
  {
    "id": "narnian-3263",
    "name": "Proof of Narnian #3263",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3263?s=fee4b6f407b7cfe10e0b7ceb369127ce"
  },
  {
    "id": "narnian-3262",
    "name": "Proof of Narnian #3262",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3262?s=f224fcc112b95c151eac4f05985305dd"
  },
  {
    "id": "narnian-3261",
    "name": "Proof of Narnian #3261",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3261?s=54c1e18083fc8507df1c1d4a6f3de86b"
  },
  {
    "id": "narnian-3260",
    "name": "Proof of Narnian #3260",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3260?s=e125462464e2fdf4c3cffdb2384544df"
  },
  {
    "id": "narnian-3259",
    "name": "Proof of Narnian #3259",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3259?s=a82c408f9a613ed187e9b34c256eb794"
  },
  {
    "id": "narnian-3258",
    "name": "Proof of Narnian #3258",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3258?s=6cc891d7bc62a803b62ce44be9cadd98"
  },
  {
    "id": "narnian-3257",
    "name": "Proof of Narnian #3257",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3257?s=4ad13a074e70c540d06c2351fe6b85e4"
  },
  {
    "id": "narnian-3256",
    "name": "Proof of Narnian #3256",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3256?s=4c9dad0381de3be0474176218d132bfb"
  },
  {
    "id": "narnian-3255",
    "name": "Proof of Narnian #3255",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3255?s=fddc5c40b1b82dacee7f41ef0fa44589"
  },
  {
    "id": "narnian-3254",
    "name": "Proof of Narnian #3254",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3254?s=bb002c006dbee56930fa69743e5d95aa"
  },
  {
    "id": "narnian-3253",
    "name": "Proof of Narnian #3253",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3253?s=e9c1d84610e4a62896089a8ac9e6c2b8"
  },
  {
    "id": "narnian-3252",
    "name": "Proof of Narnian #3252",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3252?s=37a9e5c801f26a17696aa0e053e811f2"
  },
  {
    "id": "narnian-3251",
    "name": "Proof of Narnian #3251",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3251?s=f887f69ff63a9343662b3f9136c19239"
  },
  {
    "id": "narnian-3250",
    "name": "Proof of Narnian #3250",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3250?s=4e1adf947f89a3848f2d45e18ce42d1a"
  },
  {
    "id": "narnian-3249",
    "name": "Proof of Narnian #3249",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3249?s=158f8476c5309bdde76884f037f03f64"
  },
  {
    "id": "narnian-3248",
    "name": "Proof of Narnian #3248",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3248?s=2e1291bb01ff5bdf3be27dc17225593e"
  },
  {
    "id": "narnian-3247",
    "name": "Proof of Narnian #3247",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3247?s=162de94c15dcef60470dbfacb3d20fc6"
  },
  {
    "id": "narnian-3246",
    "name": "Proof of Narnian #3246",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3246?s=4a7f56d0b71ae6f1bba6512978946e78"
  },
  {
    "id": "narnian-3245",
    "name": "Proof of Narnian #3245",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3245?s=fc154950a602492b1973f517a061a837"
  },
  {
    "id": "narnian-3244",
    "name": "Proof of Narnian #3244",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3244?s=da6f766fad7c4583b1981c9da27d4fe7"
  },
  {
    "id": "narnian-3243",
    "name": "Proof of Narnian #3243",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3243?s=6c5c85b33ec22383545717b02b9daea3"
  },
  {
    "id": "narnian-3242",
    "name": "Proof of Narnian #3242",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3242?s=e6d7309734386f9fc3f8dce6d56a64a7"
  },
  {
    "id": "narnian-3241",
    "name": "Proof of Narnian #3241",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3241?s=c1e482b84e342f2da6f798bd1e267499"
  },
  {
    "id": "narnian-3240",
    "name": "Proof of Narnian #3240",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3240?s=6d66aa81f5a39b5a3a05b2906cb6f481"
  },
  {
    "id": "narnian-3239",
    "name": "Proof of Narnian #3239",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3239?s=226ddfa78f0edf1e02c9571f0113edb0"
  },
  {
    "id": "narnian-3238",
    "name": "Proof of Narnian #3238",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3238?s=00078ec63f6dd33706513280ef478ff7"
  },
  {
    "id": "narnian-3237",
    "name": "Proof of Narnian #3237",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3237?s=74012eff1a61072ba245f5e10592432b"
  },
  {
    "id": "narnian-3236",
    "name": "Proof of Narnian #3236",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3236?s=4f84d7eee8eba5a7f071b02affa4c523"
  },
  {
    "id": "narnian-3235",
    "name": "Proof of Narnian #3235",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3235?s=2028742cb915578cf481818149e3d2ca"
  },
  {
    "id": "narnian-3234",
    "name": "Proof of Narnian #3234",
    "image": "https://assets.bueno.art/images/8ed96917-8f03-4d32-aa1f-78de8d7d05ab/default/3234?s=3655548746fa4c0aaf5d26f069bc51da"
  }

            ];
        actualNftData = proofofnarnianNFTs.find(nft => nft.id.toString() === itemId);
             
              } else if (collectionId === 'project-aeon') {
        const projectaeonNFTs = [
    
  {
    "id": "aeon-3333",
    "name": "Project AEON #3333",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3333.png"
  },
  {
    "id": "aeon-3332",
    "name": "Project AEON #3332",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3332.png"
  },
  {
    "id": "aeon-3331",
    "name": "Project AEON #3331",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3331.png"
  },
  {
    "id": "aeon-3330",
    "name": "Project AEON #3330",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3330.png"
  },
  {
    "id": "aeon-3329",
    "name": "Project AEON #3329",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3329.png"
  },
  {
    "id": "aeon-3326",
    "name": "Project AEON #3326",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3326.png"
  },
  {
    "id": "aeon-3327",
    "name": "Project AEON #3327",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3327.png"
  },
  {
    "id": "aeon-3328",
    "name": "Project AEON #3328",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3328.png"
  },
  {
    "id": "aeon-3325",
    "name": "Project AEON #3325",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3325.png"
  },
  {
    "id": "aeon-3322",
    "name": "Project AEON #3322",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3322.png"
  },
  {
    "id": "aeon-3323",
    "name": "Project AEON #3323",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3323.png"
  },
  {
    "id": "aeon-3324",
    "name": "Project AEON #3324",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3324.png"
  },
  {
    "id": "aeon-3320",
    "name": "Project AEON #3320",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3320.png"
  },
  {
    "id": "aeon-3321",
    "name": "Project AEON #3321",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3321.png"
  },
  {
    "id": "aeon-3319",
    "name": "Project AEON #3319",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3319.png"
  },
  {
    "id": "aeon-3318",
    "name": "Project AEON #3318",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3318.png"
  },
  {
    "id": "aeon-3316",
    "name": "Project AEON #3316",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3316.png"
  },
  {
    "id": "aeon-3315",
    "name": "Project AEON #3315",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3315.png"
  },
  {
    "id": "aeon-3317",
    "name": "Project AEON #3317",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3317.png"
  },
  {
    "id": "aeon-3314",
    "name": "Project AEON #3314",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3314.png"
  },
  {
    "id": "aeon-3313",
    "name": "Project AEON #3313",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3313.png"
  },
  {
    "id": "aeon-3312",
    "name": "Project AEON #3312",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3312.png"
  },
  {
    "id": "aeon-3311",
    "name": "Project AEON #3311",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3311.png"
  },
  {
    "id": "aeon-3310",
    "name": "Project AEON #3310",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3310.png"
  },
  {
    "id": "aeon-3308",
    "name": "Project AEON #3308",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3308.png"
  },
  {
    "id": "aeon-3309",
    "name": "Project AEON #3309",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3309.png"
  },
  {
    "id": "aeon-3307",
    "name": "Project AEON #3307",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3307.png"
  },
  {
    "id": "aeon-3306",
    "name": "Project AEON #3306",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3306.png"
  },
  {
    "id": "aeon-3305",
    "name": "Project AEON #3305",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3305.png"
  },
  {
    "id": "aeon-3303",
    "name": "Project AEON #3303",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3303.png"
  },
  {
    "id": "aeon-3302",
    "name": "Project AEON #3302",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3302.png"
  },
  {
    "id": "aeon-3304",
    "name": "Project AEON #3304",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3304.png"
  },
  {
    "id": "aeon-3299",
    "name": "Project AEON #3299",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3299.png"
  },
  {
    "id": "aeon-3301",
    "name": "Project AEON #3301",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3301.png"
  },
  {
    "id": "aeon-3300",
    "name": "Project AEON #3300",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3300.png"
  },
  {
    "id": "aeon-3298",
    "name": "Project AEON #3298",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3298.png"
  },
  {
    "id": "aeon-3297",
    "name": "Project AEON #3297",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3297.png"
  },
  {
    "id": "aeon-3296",
    "name": "Project AEON #3296",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3296.png"
  },
  {
    "id": "aeon-3294",
    "name": "Project AEON #3294",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3294.png"
  },
  {
    "id": "aeon-3292",
    "name": "Project AEON #3292",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3292.png"
  },
  {
    "id": "aeon-3295",
    "name": "Project AEON #3295",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3295.png"
  },
  {
    "id": "aeon-3293",
    "name": "Project AEON #3293",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3293.png"
  },
  {
    "id": "aeon-3289",
    "name": "Project AEON #3289",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3289.png"
  },
  {
    "id": "aeon-3290",
    "name": "Project AEON #3290",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3290.png"
  },
  {
    "id": "aeon-3288",
    "name": "Project AEON #3288",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3288.png"
  },
  {
    "id": "aeon-3291",
    "name": "Project AEON #3291",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3291.png"
  },
  {
    "id": "aeon-3287",
    "name": "Project AEON #3287",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3287.png"
  },
  {
    "id": "aeon-3284",
    "name": "Project AEON #3284",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3284.png"
  },
  {
    "id": "aeon-3286",
    "name": "Project AEON #3286",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3286.png"
  },
  {
    "id": "aeon-3285",
    "name": "Project AEON #3285",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3285.png"
  },
  {
    "id": "aeon-3282",
    "name": "Project AEON #3282",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3282.png"
  },

  {
    "id": "aeon-3283",
    "name": "Project AEON #3283",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3283.png"
  },
  {
    "id": "aeon-3281",
    "name": "Project AEON #3281",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3281.png"
  },
  {
    "id": "aeon-3280",
    "name": "Project AEON #3280",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3280.png"
  },
  {
    "id": "aeon-3279",
    "name": "Project AEON #3279",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3279.png"
  },
  {
    "id": "aeon-3278",
    "name": "Project AEON #3278",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3278.png"
  },
  {
    "id": "aeon-3275",
    "name": "Project AEON #3275",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3275.png"
  },
  {
    "id": "aeon-3271",
    "name": "Project AEON #3271",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3271.png"
  },
  {
    "id": "aeon-3274",
    "name": "Project AEON #3274",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3274.png"
  },
  {
    "id": "aeon-3272",
    "name": "Project AEON #3272",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3272.png"
  },
  {
    "id": "aeon-3276",
    "name": "Project AEON #3276",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3276.png"
  },
  {
    "id": "aeon-3277",
    "name": "Project AEON #3277",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3277.png"
  },
  {
    "id": "aeon-3273",
    "name": "Project AEON #3273",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3273.png"
  },
  {
    "id": "aeon-3270",
    "name": "Project AEON #3270",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3270.png"
  },
  {
    "id": "aeon-3269",
    "name": "Project AEON #3269",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3269.png"
  },
  {
    "id": "aeon-3268",
    "name": "Project AEON #3268",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3268.png"
  },
  {
    "id": "aeon-3266",
    "name": "Project AEON #3266",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3266.png"
  },
  {
    "id": "aeon-3267",
    "name": "Project AEON #3267",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3267.png"
  },
  {
    "id": "aeon-3265",
    "name": "Project AEON #3265",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3265.png"
  },
  {
    "id": "aeon-3264",
    "name": "Project AEON #3264",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3264.png"
  },
  {
    "id": "aeon-3263",
    "name": "Project AEON #3263",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3263.png"
  },
  {
    "id": "aeon-3262",
    "name": "Project AEON #3262",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3262.png"
  },
  {
    "id": "aeon-3258",
    "name": "Project AEON #3258",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3258.png"
  },
  {
    "id": "aeon-3260",
    "name": "Project AEON #3260",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3260.png"
  },
  {
    "id": "aeon-3261",
    "name": "Project AEON #3261",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3261.png"
  },
  {
    "id": "aeon-3259",
    "name": "Project AEON #3259",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3259.png"
  },
  {
    "id": "aeon-3256",
    "name": "Project AEON #3256",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3256.png"
  },
  {
    "id": "aeon-3257",
    "name": "Project AEON #3257",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3257.png"
  },
  {
    "id": "aeon-3255",
    "name": "Project AEON #3255",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3255.png"
  },
  {
    "id": "aeon-3254",
    "name": "Project AEON #3254",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3254.png"
  },
  {
    "id": "aeon-3252",
    "name": "Project AEON #3252",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3252.png"
  },
  {
    "id": "aeon-3253",
    "name": "Project AEON #3253",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3253.png"
  },
  {
    "id": "aeon-3250",
    "name": "Project AEON #3250",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3250.png"
  },
  {
    "id": "aeon-3246",
    "name": "Project AEON #3246",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3246.png"
  },
  {
    "id": "aeon-3251",
    "name": "Project AEON #3251",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3251.png"
  },
  {
    "id": "aeon-3244",
    "name": "Project AEON #3244",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3244.png"
  },
  {
    "id": "aeon-3248",
    "name": "Project AEON #3248",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3248.png"
  },
  {
    "id": "aeon-3249",
    "name": "Project AEON #3249",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3249.png"
  },
  {
    "id": "aeon-3247",
    "name": "Project AEON #3247",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3247.png"
  },
  {
    "id": "aeon-3245",
    "name": "Project AEON #3245",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3245.png"
  },
  {
    "id": "aeon-3236",
    "name": "Project AEON #3236",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3236.png"
  },
  {
    "id": "aeon-3240",
    "name": "Project AEON #3240",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3240.png"
  },
  {
    "id": "aeon-3235",
    "name": "Project AEON #3235",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3235.png"
  },
  {
    "id": "aeon-3241",
    "name": "Project AEON #3241",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3241.png"
  },
  {
    "id": "aeon-3243",
    "name": "Project AEON #3243",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3243.png"
  },
  {
    "id": "aeon-3239",
    "name": "Project AEON #3239",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3239.png"
  },
  {
    "id": "aeon-3237",
    "name": "Project AEON #3237",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3237.png"
  },
  {
    "id": "aeon-3242",
    "name": "Project AEON #3242",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3242.png"
  },
  {
    "id": "aeon-3238",
    "name": "Project AEON #3238",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3238.png"
  },
  {
    "id": "aeon-3234",
    "name": "Project AEON #3234",
    "image": "https://ipfs.io/ipfs/bafybeie7t5ehlwnhs5jh3r5pbqx7q2gvjpvzgm3zsftxxwrupzsaiemlpq/3234.png"
  }
            
             ];
        actualNftData = projectaeonNFTs.find(nft => nft.id.toString() === itemId);
               
              } else if (collectionId === 'boredapekennelclub') {
        const boredapekennelNFTs = [
            
  {
    "id": "bakc-8009",
    "name": "BoredApeKennelClub #8009",
    "image": "https://ipfs.io/ipfs/QmdQdX37uS8ZA4YKNg3sCo3vRfq1oXt2Rk7RMQ41cAw4vU"
  },
  {
    "id": "bakc-655",
    "name": "BoredApeKennelClub #655",
    "image": "https://ipfs.io/ipfs/QmTGC3YxjLAgZV8BYWiqL15vzYxxnDDRNuzaStGptZ1VUu"
  },
  {
    "id": "bakc-998",
    "name": "BoredApeKennelClub #998",
    "image": "https://ipfs.io/ipfs/QmYHjPtHzBSyVDZwK5Vqt4GHsB4nJv3DpHpkXKRkyAzJ1j"
  },
  {
    "id": "bakc-7456",
    "name": "BoredApeKennelClub #7456",
    "image": "https://ipfs.io/ipfs/QmaFiwig1Z8bfntdTJhapvRDhrNdiPnxCtx7k7EU72bGhz"
  },
  {
    "id": "bakc-835",
    "name": "BoredApeKennelClub #835",
    "image": "https://ipfs.io/ipfs/QmXkE2aip19CqYN7v9dYnjCgD8jrKNooGacokBost64NZJ"
  },
  {
    "id": "bakc-4883",
    "name": "BoredApeKennelClub #4883",
    "image": "https://ipfs.io/ipfs/QmdSg4FNErHjC7mZjQpRgLJVVWPDgmvM5zschLusb5T57c"
  },
  {
    "id": "bakc-96",
    "name": "BoredApeKennelClub #96",
    "image": "https://ipfs.io/ipfs/QmTSxbT8Ddh6Fen1T9axPa8J4JTnhPPwgno6jGTJ4Vhzq2"
  },
  {
    "id": "bakc-1066",
    "name": "BoredApeKennelClub #1066",
    "image": "https://ipfs.io/ipfs/Qmf1vvh6eL4pNvUkE3csWZS2kDyQy5hEgLiNMiSzU4Efoq"
  },
  {
    "id": "bakc-8292",
    "name": "BoredApeKennelClub #8292",
    "image": "https://ipfs.io/ipfs/QmZh5raZdMwVdSxFUvtY3KwvodpWzQHmNMoA5Nr4GsWHN7"
  },
  {
    "id": "bakc-8291",
    "name": "BoredApeKennelClub #8291",
    "image": "https://ipfs.io/ipfs/Qmcbw6uRUaRhuAc68pmNuooTffqwisREwDfjeTRisEF92b"
  },
  {
    "id": "bakc-8290",
    "name": "BoredApeKennelClub #8290",
    "image": "https://ipfs.io/ipfs/QmbaCLLsuAvjMstWWGRCnwmjFXgMvc9NCTtyvpmnayzJxq"
  },
  {
    "id": "bakc-8289",
    "name": "BoredApeKennelClub #8289",
    "image": "https://ipfs.io/ipfs/QmNsbGFP4DgDDL9uQi6nYU7MLfQofQ9cH2ktwD77qgcAGS"
  },
  {
    "id": "bakc-8288",
    "name": "BoredApeKennelClub #8288",
    "image": "https://ipfs.io/ipfs/QmTvZ6PVySWtgzhT9iitLRZrBebbKJ9d2UjajdUj48eZux"
  },
  {
    "id": "bakc-7563",
    "name": "BoredApeKennelClub #7563",
    "image": "https://ipfs.io/ipfs/QmdgbMKGb2LZBJfeq2Wu5XZVTLL8WmtDV3Y3kkPUh8GP4j"
  },
  {
    "id": "bakc-7281",
    "name": "BoredApeKennelClub #7281",
    "image": "https://ipfs.io/ipfs/QmcHMHa3VxbXGUqdqXZ6eaEtn1bcAWGR6c8zYaHkNcpszD"
  },
  {
    "id": "bakc-4078",
    "name": "BoredApeKennelClub #4078",
    "image": "https://ipfs.io/ipfs/QmWghdkRypT4h1i97kRVNkyk1KQvN1XRMRTV4hZWzyqZUn"
  },
  {
    "id": "bakc-5269",
    "name": "BoredApeKennelClub #5269",
    "image": "https://ipfs.io/ipfs/QmV4n7ESRak3kDCR5gV6RjfppHLbsHeiTvDZp8apeehu7v"
  },
  {
    "id": "bakc-838",
    "name": "BoredApeKennelClub #838",
    "image": "https://ipfs.io/ipfs/QmYwgL6Gj99BeBHSSe2LGFwyhGw625yjjzh5hqRzKfned4"
  },
  {
    "id": "bakc-3212",
    "name": "BoredApeKennelClub #3212",
    "image": "https://ipfs.io/ipfs/QmVghhVVvEnRgP2KwrEPeiEmsQEUmCceKDSgznxni8YiFW"
  },
  {
    "id": "bakc-3635",
    "name": "BoredApeKennelClub #3635",
    "image": "https://ipfs.io/ipfs/QmR3tcA1jJVdabyjWCLkbpCE8wjRqkxRJ7kwZwjukf1Pwm"
  },
  {
    "id": "bakc-6751",
    "name": "BoredApeKennelClub #6751",
    "image": "https://ipfs.io/ipfs/QmYaV7PXcvkjcS4PX3gyfQRX4yqjqXnH3B6GysNdUfGmFK"
  },
  {
    "id": "bakc-7037",
    "name": "BoredApeKennelClub #7037",
    "image": "https://ipfs.io/ipfs/QmZKVwq8cNt3aiCuLMpNnzfh995XgFCYzJfEQN8v77VsoY"
  },
  {
    "id": "bakc-5892",
    "name": "BoredApeKennelClub #5892",
    "image": "https://ipfs.io/ipfs/QmRKsemLuYBjxysSoHqEy13om7tn9jP9pdDDqkwzfqTjyf"
  },
  {
    "id": "bakc-2710",
    "name": "BoredApeKennelClub #2710",
    "image": "https://ipfs.io/ipfs/QmXrXgcmRdCCrAecMs3G2orK711n8ogiuMa2khX1mB1tmn"
  },
  {
    "id": "bakc-2704",
    "name": "BoredApeKennelClub #2704",
    "image": "https://ipfs.io/ipfs/QmSAut8AGY69oJC35Vxe8TN8pDV3XE4b7sxA4pVfjE3Ht4"
  },
  {
    "id": "bakc-2699",
    "name": "BoredApeKennelClub #2699",
    "image": "https://ipfs.io/ipfs/QmVrt6sq5ghFVCCmHzUjPghWLqQ1nqkD86MfWgGEaVeQDK"
  },
  {
    "id": "bakc-1004",
    "name": "BoredApeKennelClub #1004",
    "image": "https://ipfs.io/ipfs/QmTooqaTUMqj5nXEJ4UNNrhzhmbKcNARHC1UJYg8GJaNRp"
  },
  {
    "id": "bakc-744",
    "name": "BoredApeKennelClub #744",
    "image": "https://ipfs.io/ipfs/QmNjiXk6aKierayr4jQT6vT7TMEvCUFqYEoPcdeBvncEHV"
  },
  {
    "id": "bakc-951",
    "name": "BoredApeKennelClub #951",
    "image": "https://ipfs.io/ipfs/QmbSZzDTHk6HhZXc2okbr22Pj2G55wWrrVrHdTg1Kr7GKX"
  },
  {
    "id": "bakc-525",
    "name": "BoredApeKennelClub #525",
    "image": "https://ipfs.io/ipfs/QmPus6P41xX4bdAUaJYiTRSznod9qEukwh9mSHz7GEsPft"
  },
  {
    "id": "bakc-1150",
    "name": "BoredApeKennelClub #1150",
    "image": "https://ipfs.io/ipfs/QmVUNGghYK7ach48CxK1V565FnxoGb5Yh7wneMieVsHM4c"
  },
  {
    "id": "bakc-696",
    "name": "BoredApeKennelClub #696",
    "image": "https://ipfs.io/ipfs/QmdWfFxp6nsKYXPSACiyVUAVVcno5h37NYRSfrLUtnTTjb"
  },
  {
    "id": "bakc-1083",
    "name": "BoredApeKennelClub #1083",
    "image": "https://ipfs.io/ipfs/QmUXrGsTwzXVJLEpmeucP7Z4scELhf1ki6qhMjEHhc5GF8"
  },
  {
    "id": "bakc-682",
    "name": "BoredApeKennelClub #682",
    "image": "https://ipfs.io/ipfs/Qmbx9aQKnytAtbYeKbaTQH5ahqNuBbvEGaotZcaZL4bGjY"
  },
  {
    "id": "bakc-2863",
    "name": "BoredApeKennelClub #2863",
    "image": "https://ipfs.io/ipfs/QmZgXV6SvXCX88Z3z7n7DbiQUXJFa3NvtX2wxRTMNXucUD"
  },
  {
    "id": "bakc-121",
    "name": "BoredApeKennelClub #121",
    "image": "https://ipfs.io/ipfs/QmbL8UZ8iRYT2BNTMUofRRVt6mtQbPvbxGXw1ddNn2MLRS"
  },
  {
    "id": "bakc-9516",
    "name": "BoredApeKennelClub #9516",
    "image": "https://ipfs.io/ipfs/QmNLpidzSbSpKC1DP6jqC6iRXYutfnAsvTuSvyzNovCrrJ"
  },
  {
    "id": "bakc-2484",
    "name": "BoredApeKennelClub #2484",
    "image": "https://ipfs.io/ipfs/QmXUfZMckbAnU26pUBJYXxd3Veru47WraEmZcejadbp2we"
  },
  {
    "id": "bakc-7901",
    "name": "BoredApeKennelClub #7901",
    "image": "https://ipfs.io/ipfs/QmQvx22xpnrMDeTKJzRAB5jAqgPSBHbYduyHzF9Vn8dbbU"
  },
  {
    "id": "bakc-8013",
    "name": "BoredApeKennelClub #8013",
    "image": "https://ipfs.io/ipfs/QmUNw1VL7ZL2dMT4hhmj1A1sFERrtM9vV4bRgcD5zeZvVm"
  },
  {
    "id": "bakc-9530",
    "name": "BoredApeKennelClub #9530",
    "image": "https://ipfs.io/ipfs/QmTsSgLLzSRaCDm724SPAyJmzaceLDeYp4uBmH6ygBFPjF"
  },
  {
    "id": "bakc-2372",
    "name": "BoredApeKennelClub #2372",
    "image": "https://ipfs.io/ipfs/Qmb9uGPfs7w6iY9uUoahURYMh8kZJ58tWR6rfczHMqz4P6"
  },
  {
    "id": "bakc-1703",
    "name": "BoredApeKennelClub #1703",
    "image": "https://ipfs.io/ipfs/QmckfW8qNWpvFpuUVg4ZP6s91pNRSLP8ftkcJKpzWTiKsR"
  },
  {
    "id": "bakc-8371",
    "name": "BoredApeKennelClub #8371",
    "image": "https://ipfs.io/ipfs/QmSTgLbFbJKMPDpoZxdf4Xf1GM9wnEvG2BNASzr84BDkDc"
  },
  {
    "id": "bakc-7119",
    "name": "BoredApeKennelClub #7119",
    "image": "https://ipfs.io/ipfs/QmZeFAnMVdvy1fev18qk8wCbRR4rT8sAsVPZgcjy6mev4a"
  },
  {
    "id": "bakc-6192",
    "name": "BoredApeKennelClub #6192",
    "image": "https://ipfs.io/ipfs/Qmd1otNbU3nGrN7YcqGaDoCzuG6tE6he7XeGpVCpZmqVet"
  },
  {
    "id": "bakc-1568",
    "name": "BoredApeKennelClub #1568",
    "image": "https://ipfs.io/ipfs/QmerMYJ6kFPS6s3S4NETD1K3yzqZ5iDG6RScCVWeXwev6M"
  },
  {
    "id": "bakc-1729",
    "name": "BoredApeKennelClub #1729",
    "image": "https://ipfs.io/ipfs/QmUPjYyG2ZbcgRixUpeBudParDFPkzjYKw8mNaoAqdADk4"
  },
  {
    "id": "bakc-8021",
    "name": "BoredApeKennelClub #8021",
    "image": "https://ipfs.io/ipfs/QmcJA3NK1RoMzWbUMoBKztR5siTTH9NfM6Z9dYvaHX45u5"
  },
  {
    "id": "bakc-8940",
    "name": "BoredApeKennelClub #8940",
    "image": "https://ipfs.io/ipfs/QmNcCcT4AVtF4uqSYY741fXKdULi2tU4f64TodFR5jD47G"
  },
  {
    "id": "bakc-3478",
    "name": "BoredApeKennelClub #3478",
    "image": "https://ipfs.io/ipfs/QmUg9V1xvEYFQQQam36tZyKPBVqYR8qSPtMNE1TytxQ4Tt"
  },
  {
    "id": "bakc-8871",
    "name": "BoredApeKennelClub #8871",
    "image": "https://ipfs.io/ipfs/QmQNrbiALbpTQkzgybsRGC1qCGzH2suwKHs6pFXPS7XJaH"
  },
  {
    "id": "bakc-4298",
    "name": "BoredApeKennelClub #4298",
    "image": "https://ipfs.io/ipfs/QmTpmEHaVWztXHUn3gVhLQ9bHyaBv6yivxEEXPaeaXajVd"
  },
  {
    "id": "bakc-3065",
    "name": "BoredApeKennelClub #3065",
    "image": "https://ipfs.io/ipfs/QmdsNFaLtNRNYPRvn1jwZUD7qbUvDJEARGfCWBLU7PC4EL"
  },
  {
    "id": "bakc-5860",
    "name": "BoredApeKennelClub #5860",
    "image": "https://ipfs.io/ipfs/QmXwguyM5nGwEPmfNmKE737WPxX4pWCT5k5mjPpykUZZQZ"
  },
  {
    "id": "bakc-8948",
    "name": "BoredApeKennelClub #8948",
    "image": "https://ipfs.io/ipfs/QmW2scP8wtVFAS7YgrG47PvmcHwJLEefTECWRiv8eV2Cno"
  },
  {
    "id": "bakc-8086",
    "name": "BoredApeKennelClub #8086",
    "image": "https://ipfs.io/ipfs/QmVzazgfJVqv8waLZYnzj9pgt3BHja8BDicGyNesVLD3Fe"
  },
  {
    "id": "bakc-2817",
    "name": "BoredApeKennelClub #2817",
    "image": "https://ipfs.io/ipfs/QmPmmZXUBCQZ1PdwCwL3maSvBFV1KsKENph3eNgn7NvDL1"
  },
  {
    "id": "bakc-4120",
    "name": "BoredApeKennelClub #4120",
    "image": "https://ipfs.io/ipfs/QmSEUsFggJrdQd6H9GaAP36KdG3u4qmjegZKZ1YxWFHHHR"
  },
  {
    "id": "bakc-7509",
    "name": "BoredApeKennelClub #7509",
    "image": "https://ipfs.io/ipfs/QmSNLx91d2hUAGBKfxEuF9KBYNMSWfi69ncD2Y7GwFsxGt"
  },
  {
    "id": "bakc-7156",
    "name": "BoredApeKennelClub #7156",
    "image": "https://ipfs.io/ipfs/QmW1SLhkydU1TLYzUFcL2BUyJ13TZqBGWgTWX3C1dWTGq7"
  },
  {
    "id": "bakc-3787",
    "name": "BoredApeKennelClub #3787",
    "image": "https://ipfs.io/ipfs/QmQtoMK772XxyFTCgQiv8RfGMb3tpe9pWhDZ5jq3qttoNn"
  },
  {
    "id": "bakc-9735",
    "name": "BoredApeKennelClub #9735",
    "image": "https://ipfs.io/ipfs/Qmb317r2rDrRGWcZBJ8SgqJRBshMTTEL4nx6uaLWLDX7ZY"
  },
  {
    "id": "bakc-9522",
    "name": "BoredApeKennelClub #9522",
    "image": "https://ipfs.io/ipfs/Qmc6XDn4TPsVCfxUUuDamdv4Yi7KzEXLQwVL6SmweUDTHG"
  },
  {
    "id": "bakc-1397",
    "name": "BoredApeKennelClub #1397",
    "image": "https://ipfs.io/ipfs/QmXUiYJGmDqtZ7KW1RTYZxgcGS9f2BsdHHbcwF4JK2mj2b"
  },
  {
    "id": "bakc-6309",
    "name": "BoredApeKennelClub #6309",
    "image": "https://ipfs.io/ipfs/QmeF8ex42h73n7hE2kV2K3qNrMdmwxGqVaQ4EqmZ8K2NG8"
  },
  {
    "id": "bakc-9513",
    "name": "BoredApeKennelClub #9513",
    "image": "https://ipfs.io/ipfs/QmTfYNYdt6EXuqCvxAaavgRxPdHHJLi3uEL6P9v4w3F6uA"
  },
  {
    "id": "bakc-5098",
    "name": "BoredApeKennelClub #5098",
    "image": "https://ipfs.io/ipfs/QmZSmZPis63GBPz27sX4auRft4UNqd3DQYhHJpwCgozapU"
  },
  {
    "id": "bakc-9653",
    "name": "BoredApeKennelClub #9653",
    "image": "https://ipfs.io/ipfs/QmX7jWUDW4zkiAF2n8hYkvp7EJAGStNFKua9PYwq7sRJSr"
  },
  {
    "id": "bakc-8541",
    "name": "BoredApeKennelClub #8541",
    "image": "https://ipfs.io/ipfs/QmZ2am9ACEDVR5NxEMBULurUm6HWAYzwsToYEWhCCsdzuE"
  },
  {
    "id": "bakc-7702",
    "name": "BoredApeKennelClub #7702",
    "image": "https://ipfs.io/ipfs/QmZ8QWiGWDzfHti4KeNyaRe9tC5WAYr4fpu2YaK3eyqGaf"
  },
  {
    "id": "bakc-83",
    "name": "BoredApeKennelClub #83",
    "image": "https://ipfs.io/ipfs/QmdEgHizJcG2nvmPnKvc51B6qRxHjctzUCwN2MiRDr6gNq"
  },
  {
    "id": "bakc-4000",
    "name": "BoredApeKennelClub #4000",
    "image": "https://ipfs.io/ipfs/QmWzcN7kUEeC53u8jnNbemgMBmDB3pxHHDxfZmRzaW3BvB"
  },
  {
    "id": "bakc-9525",
    "name": "BoredApeKennelClub #9525",
    "image": "https://ipfs.io/ipfs/Qmeuy74rcREUcqWPtbg46Z9wgUYe91p3i95KHogkna2nCf"
  },
  {
    "id": "bakc-9523",
    "name": "BoredApeKennelClub #9523",
    "image": "https://ipfs.io/ipfs/QmSpgY9gDGecuWSCv89e92Y5UE9GsNH3LqSeX37vK63pAj"
  },
  {
    "id": "bakc-9529",
    "name": "BoredApeKennelClub #9529",
    "image": "https://ipfs.io/ipfs/QmPpBYE15BqyTGZwRL6TtiBGZuHuP6VMd3ofgBfVfA5vym"
  },
  {
    "id": "bakc-9520",
    "name": "BoredApeKennelClub #9520",
    "image": "https://ipfs.io/ipfs/QmP64UdXpoo96wJktV2FeEDBw9qzp5KGNAoYaALgEQukmB"
  },
  {
    "id": "bakc-9528",
    "name": "BoredApeKennelClub #9528",
    "image": "https://ipfs.io/ipfs/QmS3qJXa5b1B74qBhpyYJv1B2766kEPHrQwTBZjEaiUN8X"
  },
  {
    "id": "bakc-6099",
    "name": "BoredApeKennelClub #6099",
    "image": "https://ipfs.io/ipfs/QmcUth1tfKPRMuD8WGWMzCBamEP5G7qDyWkFpZtDAg4aKp"
  },
  {
    "id": "bakc-4204",
    "name": "BoredApeKennelClub #4204",
    "image": "https://ipfs.io/ipfs/QmXofC4qBEGQNzGJTwXDF6HRFaPTGVUV4YF5bxq3sXFqKi"
  },
  {
    "id": "bakc-3684",
    "name": "BoredApeKennelClub #3684",
    "image": "https://ipfs.io/ipfs/QmQov1qi4jeMCSSwngvYEJL4jjEXmiZmfutPjWJ7HEHkDY"
  },
  {
    "id": "bakc-3333",
    "name": "BoredApeKennelClub #3333",
    "image": "https://ipfs.io/ipfs/QmbZD6MctZgxmkDixGxjSA5cNNMjyynyCPxjhCgQhoARTj"
  },
  {
    "id": "bakc-8079",
    "name": "BoredApeKennelClub #8079",
    "image": "https://ipfs.io/ipfs/Qme8vdKa3CK9nZB7sDYXUyLqaeNGFJcARqfvSbSNfJzjec"
  },
  {
    "id": "bakc-3818",
    "name": "BoredApeKennelClub #3818",
    "image": "https://ipfs.io/ipfs/QmUh2AhT9s1FjMLrNvXYLbu8zNLmT252d5yYPWNJpNyaq9"
  },
  {
    "id": "bakc-7857",
    "name": "BoredApeKennelClub #7857",
    "image": "https://ipfs.io/ipfs/QmafWgHLmEVofrcB1b8X6aMbP88Wx1HEhrfAvVfSKG6LzS"
  },
  {
    "id": "bakc-9969",
    "name": "BoredApeKennelClub #9969",
    "image": "https://ipfs.io/ipfs/QmQs4DLLZkboXYu52r2cWb6YzjxNC76YSwQx5M6MKuxocU"
  },
  {
    "id": "bakc-9944",
    "name": "BoredApeKennelClub #9944",
    "image": "https://ipfs.io/ipfs/QmVPUK6fvDYCYTaitxKqCAWdtP94WJLuF6siEVqAfTWMpj"
  },
  {
    "id": "bakc-9975",
    "name": "BoredApeKennelClub #9975",
    "image": "https://ipfs.io/ipfs/QmQHPFMuhBBAa2mybYeAKunvXztkYSBk76MbkjfLoHvAUR"
  },
  {
    "id": "bakc-8106",
    "name": "BoredApeKennelClub #8106",
    "image": "https://ipfs.io/ipfs/QmWFneCWq732hcbbMvMXpPHMckvqcBpnNE1XMsTMgJUW8X"
  },
  {
    "id": "bakc-7912",
    "name": "BoredApeKennelClub #7912",
    "image": "https://ipfs.io/ipfs/QmSU8ngSdPtk36tdoGZxAu3US1BJrue6ZA3qZGk336kF8Y"
  },
  {
    "id": "bakc-1452",
    "name": "BoredApeKennelClub #1452",
    "image": "https://ipfs.io/ipfs/QmaGcN1tuW2MHgFmbVqxCYqVvswYaPTg4rwbb1AWYD3EtM"
  },
  {
    "id": "bakc-8040",
    "name": "BoredApeKennelClub #8040",
    "image": "https://ipfs.io/ipfs/QmZAHEijvU57FeVr3b6pzviDRxpFYykB6XUQRaS6Q16nhM"
  },
  {
    "id": "bakc-3925",
    "name": "BoredApeKennelClub #3925",
    "image": "https://ipfs.io/ipfs/QmaEFJTeSnproKHWM9cZnMrAqSSWvjgPGPMXEWMCKyduJB"
  },
  {
    "id": "bakc-2401",
    "name": "BoredApeKennelClub #2401",
    "image": "https://ipfs.io/ipfs/QmPSV2qa2k3dCJeCnKEF4EdjQRYkGrLPCHBskdXwT1BVjD"
  },
  {
    "id": "bakc-2360",
    "name": "BoredApeKennelClub #2360",
    "image": "https://ipfs.io/ipfs/Qmd1KBXtofbDvZuf3h8nuscra7FoMUYwkwQ48R7Ck8hi6N"
  },
  {
    "id": "bakc-7580",
    "name": "BoredApeKennelClub #7580",
    "image": "https://ipfs.io/ipfs/QmVmbDFmKk5UfVaViFFzGD5qrRdaTd11N71XxQKn1ZmhxT"
  },
  {
    "id": "bakc-1816",
    "name": "BoredApeKennelClub #1816",
    "image": "https://ipfs.io/ipfs/QmXDZx9Dv9quMZasqujsTpYhooFb9dQUjf38GQB7dtj1yr"
  },
  {
    "id": "bakc-5236",
    "name": "BoredApeKennelClub #5236",
    "image": "https://ipfs.io/ipfs/QmXHTddazxfKQ6p3NGh56XCqZdUReHy5SXzw92pyTQJLC2"
  },
  {
    "id": "bakc-9463",
    "name": "BoredApeKennelClub #9463",
    "image": "https://ipfs.io/ipfs/QmRQry5QBZVuGueq1MeHp6gc3VTyfL3c5cuovfAw1cvEht"
  },
  {
    "id": "bakc-6408",
    "name": "BoredApeKennelClub #6408",
    "image": "https://ipfs.io/ipfs/Qmc4uhE9jgpRSSCbwxesU8hLsWYRXhrtUiAJB2eYvrjh59"
  }
            ];
        actualNftData = boredapekennelNFTs.find(nft => nft.id.toString() === itemId);
              
                   } else if (collectionId === 'degods') {
        const DeGodsNFTs = [
            
  {
    "id": "degods-4247",
    "name": "DeGods #4247",
    "image": "https://metadata.degods.com/g/4247-dead-rm.png"
  },
  {
    "id": "degods-3379",
    "name": "DeGods #3379",
    "image": "https://metadata.degods.com/g/3379-dead-rm.png"
  },
  {
    "id": "degods-4866",
    "name": "DeGods #4866",
    "image": "https://metadata.degods.com/g/4866-dead-rm.png"
  },
  {
    "id": "degods-2876",
    "name": "DeGods #2876",
    "image": "https://metadata.degods.com/g/2876-dead-rm.png"
  },
  {
    "id": "degods-5006",
    "name": "DeGods #5006",
    "image": "https://metadata.degods.com/g/5006-dead-rm.png"
  },
  {
    "id": "degods-9195",
    "name": "DeGods #9195",
    "image": "https://metadata.degods.com/g/9195-dead-rm.png"
  },
  {
    "id": "degods-3148",
    "name": "DeGods #3148",
    "image": "https://metadata.degods.com/g/3148-dead-rm.png"
  },
  {
    "id": "degods-3647",
    "name": "DeGods #3647",
    "image": "https://metadata.degods.com/g/3647-dead-rm.png"
  },
  {
    "id": "degods-2891",
    "name": "DeGods #2891",
    "image": "https://metadata.degods.com/g/2891-dead-rm.png"
  },
  {
    "id": "degods-5641",
    "name": "DeGods #5641",
    "image": "https://metadata.degods.com/g/5641-dead-rm.png"
  },
  {
    "id": "degods-4876",
    "name": "DeGods #4876",
    "image": "https://metadata.degods.com/g/4876-dead-rm.png"
  },
  {
    "id": "degods-2300",
    "name": "DeGods #2300",
    "image": "https://metadata.degods.com/g/2300-dead-rm.png"
  },
  {
    "id": "degods-84",
    "name": "DeGods #84",
    "image": "https://metadata.degods.com/g/84-dead-rm.png"
  },
  {
    "id": "degods-7418",
    "name": "DeGods #7418",
    "image": "https://metadata.degods.com/g/7418-dead.png"
  },
  {
    "id": "degods-6485",
    "name": "DeGods #6485",
    "image": "https://metadata.degods.com/g/6485-dead-rm.png"
  },
  {
    "id": "degods-721",
    "name": "DeGods #721",
    "image": "https://metadata.degods.com/g/721-dead.png"
  },
  {
    "id": "degods-7195",
    "name": "DeGods #7195",
    "image": "https://metadata.degods.com/g/7195-s3-male.png"
  },
  {
    "id": "degods-2468",
    "name": "DeGods #2468",
    "image": "https://metadata.degods.com/g/2468-dead.png"
  },
  {
    "id": "degods-4511",
    "name": "DeGods #4511",
    "image": "https://metadata.degods.com/g/4511-dead-rm.png"
  },
  {
    "id": "degods-9599",
    "name": "DeGods #9599",
    "image": "https://metadata.degods.com/g/9599.png"
  },
  {
    "id": "degods-1181",
    "name": "DeGods #1181",
    "image": "https://metadata.degods.com/g/1181-dead-rm.png"
  },
  {
    "id": "degods-8592",
    "name": "DeGods #8592",
    "image": "https://metadata.degods.com/g/8592-dead-rm.png"
  },
  {
    "id": "degods-7514",
    "name": "DeGods #7514",
    "image": "https://metadata.degods.com/g/7514.png"
  },
  {
    "id": "degods-5288",
    "name": "DeGods #5288",
    "image": "https://metadata.degods.com/g/5288-dead.png"
  },
  {
    "id": "degods-2546",
    "name": "DeGods #2546",
    "image": "https://metadata.degods.com/g/2546-dead-rm.png"
  },
  {
    "id": "degods-3128",
    "name": "DeGods #3128",
    "image": "https://metadata.degods.com/g/3128-dead-rm.png"
  },
  {
    "id": "degods-4597",
    "name": "DeGods #4597",
    "image": "https://metadata.degods.com/g/4597-dead-rm.png"
  },
  {
    "id": "degods-3116",
    "name": "DeGods #3116",
    "image": "https://metadata.degods.com/g/3116-dead-rm.png"
  },
  {
    "id": "degods-3098",
    "name": "DeGods #3098",
    "image": "https://metadata.degods.com/g/3098-dead-rm.png"
  },
  {
    "id": "degods-9679",
    "name": "DeGods #9679",
    "image": "https://metadata.degods.com/g/9679-dead-rm.png"
  },
  {
    "id": "degods-7673",
    "name": "DeGods #7673",
    "image": "https://metadata.degods.com/g/7673-dead-rm.png"
  },
  {
    "id": "degods-6144",
    "name": "DeGods #6144",
    "image": "https://metadata.degods.com/g/6144-dead-rm.png"
  },
  {
    "id": "degods-8794",
    "name": "DeGods #8794",
    "image": "https://metadata.degods.com/g/8794-dead-rm.png"
  },
  {
    "id": "degods-3028",
    "name": "DeGods #3028",
    "image": "https://metadata.degods.com/g/3028-dead-rm.png"
  },
  {
    "id": "degods-7812",
    "name": "DeGods #7812",
    "image": "https://metadata.degods.com/g/7812-dead-rm.png"
  },
  {
    "id": "degods-7380",
    "name": "DeGods #7380",
    "image": "https://metadata.degods.com/g/7380-dead-rm.png"
  },
  {
    "id": "degods-9397",
    "name": "DeGods #9397",
    "image": "https://metadata.degods.com/g/9397-dead-rm.png"
  },
  {
    "id": "degods-5852",
    "name": "DeGods #5852",
    "image": "https://metadata.degods.com/g/5852-dead-rm.png"
  },
  {
    "id": "degods-1357",
    "name": "DeGods #1357",
    "image": "https://metadata.degods.com/g/1357-dead-rm.png"
  },
  {
    "id": "degods-8207",
    "name": "DeGods #8207",
    "image": "https://metadata.degods.com/g/8207-dead-rm.png"
  },
  {
    "id": "degods-2009",
    "name": "DeGods #2009",
    "image": "https://metadata.degods.com/g/2009-dead-rm.png"
  },
  {
    "id": "degods-9425",
    "name": "DeGods #9425",
    "image": "https://metadata.degods.com/g/9425-dead-rm.png"
  },
  {
    "id": "degods-3876",
    "name": "DeGods #3876",
    "image": "https://metadata.degods.com/g/3876-dead-rm.png"
  },
  {
    "id": "degods-8787",
    "name": "DeGods #8787",
    "image": "https://metadata.degods.com/g/8787-dead-rm.png"
  },
  {
    "id": "degods-4370",
    "name": "DeGods #4370",
    "image": "https://metadata.degods.com/g/4370-dead-rm.png"
  },
  {
    "id": "degods-5945",
    "name": "DeGods #5945",
    "image": "https://metadata.degods.com/g/5945-dead-rm.png"
  },
  {
    "id": "degods-9017",
    "name": "DeGods #9017",
    "image": "https://metadata.degods.com/g/9017-s3-male.png"
  },
  {
    "id": "degods-8684",
    "name": "DeGods #8684",
    "image": "https://metadata.degods.com/g/8684-dead-rm.png"
  },
  {
    "id": "degods-2890",
    "name": "DeGods #2890",
    "image": "https://metadata.degods.com/g/2890-dead-rm.png"
  },
  {
    "id": "degods-3014",
    "name": "DeGods #3014",
    "image": "https://metadata.degods.com/g/3014-dead-rm.png"
  },
  {
    "id": "degods-8438",
    "name": "DeGods #8438",
    "image": "https://metadata.degods.com/g/8438-dead-rm.png"
  },
  {
    "id": "degods-8237",
    "name": "DeGods #8237",
    "image": "https://metadata.degods.com/g/8237-dead-rm.png"
  },
  {
    "id": "degods-4761",
    "name": "DeGods #4761",
    "image": "https://metadata.degods.com/g/4761-dead-rm.png"
  },
  {
    "id": "degods-83",
    "name": "DeGods #83",
    "image": "https://metadata.degods.com/g/83-dead-rm.png"
  },
  {
    "id": "degods-5735",
    "name": "DeGods #5735",
    "image": "https://metadata.degods.com/g/5735-dead-rm.png"
  },
  {
    "id": "degods-610",
    "name": "DeGods #610",
    "image": "https://metadata.degods.com/g/610-dead-rm.png"
  },
  {
    "id": "degods-6876",
    "name": "DeGods #6876",
    "image": "https://metadata.degods.com/g/6876-dead-rm.png"
  },
  {
    "id": "degods-217",
    "name": "DeGods #217",
    "image": "https://metadata.degods.com/g/217-s3-male.png"
  },
  {
    "id": "degods-4691",
    "name": "DeGods #4691",
    "image": "https://metadata.degods.com/g/4691-dead-rm.png"
  },
  {
    "id": "degods-6238",
    "name": "DeGods #6238",
    "image": "https://metadata.degods.com/g/6238-dead-rm.png"
  },
  {
    "id": "degods-3588",
    "name": "DeGods #3588",
    "image": "https://metadata.degods.com/g/3588-dead-rm.png"
  },
  {
    "id": "degods-9426",
    "name": "DeGods #9426",
    "image": "https://metadata.degods.com/g/9426-dead-rm.png"
  },
  {
    "id": "degods-3493",
    "name": "DeGods #3493",
    "image": "https://metadata.degods.com/g/3493-dead-rm.png"
  },
  {
    "id": "degods-8190",
    "name": "DeGods #8190",
    "image": "https://metadata.degods.com/g/8190-dead-rm.png"
  },
  {
    "id": "degods-7068",
    "name": "DeGods #7068",
    "image": "https://metadata.degods.com/g/7068-dead-rm.png"
  },
  {
    "id": "degods-9422",
    "name": "DeGods #9422",
    "image": "https://metadata.degods.com/g/9422-dead-rm.png"
  },
  {
    "id": "degods-6595",
    "name": "DeGods #6595",
    "image": "https://metadata.degods.com/g/6595-dead-rm.png"
  },
  {
    "id": "degods-2198",
    "name": "DeGods #2198",
    "image": "https://metadata.degods.com/g/2198-dead-rm.png"
  },
  {
    "id": "degods-2678",
    "name": "DeGods #2678",
    "image": "https://metadata.degods.com/g/2678-dead.png"
  },
  {
    "id": "degods-2742",
    "name": "DeGods #2742",
    "image": "https://metadata.degods.com/g/2742-dead-rm.png"
  },
  {
    "id": "degods-8035",
    "name": "DeGods #8035",
    "image": "https://metadata.degods.com/g/8035-dead.png"
  },
  {
    "id": "degods-5309",
    "name": "DeGods #5309",
    "image": "https://metadata.degods.com/g/5309-dead-rm.png"
  },
  {
    "id": "degods-1473",
    "name": "DeGods #1473",
    "image": "https://metadata.degods.com/g/1473-dead-rm.png"
  },
  {
    "id": "degods-5260",
    "name": "DeGods #5260",
    "image": "https://metadata.degods.com/g/5260-dead.png"
  },
  {
    "id": "degods-9594",
    "name": "DeGods #9594",
    "image": "https://metadata.degods.com/g/9594-dead-rm.png"
  },
  {
    "id": "degods-8983",
    "name": "DeGods #8983",
    "image": "https://metadata.degods.com/g/8983-dead.png"
  },
  {
    "id": "degods-6609",
    "name": "DeGods #6609",
    "image": "https://metadata.degods.com/g/6609-dead-rm.png"
  },
  {
    "id": "degods-8469",
    "name": "DeGods #8469",
    "image": "https://metadata.degods.com/g/8469-dead-rm.png"
  },
  {
    "id": "degods-2729",
    "name": "DeGods #2729",
    "image": "https://metadata.degods.com/g/2729-dead-rm.png"
  },
  {
    "id": "degods-6580",
    "name": "DeGods #6580",
    "image": "https://metadata.degods.com/g/6580-dead-rm.png"
  },
  {
    "id": "degods-4071",
    "name": "DeGods #4071",
    "image": "https://metadata.degods.com/g/4071-dead-rm.png"
  },
  {
    "id": "degods-8702",
    "name": "DeGods #8702",
    "image": "https://metadata.degods.com/g/8702-dead-rm.png"
  },
  {
    "id": "degods-3085",
    "name": "DeGods #3085",
    "image": "https://metadata.degods.com/g/3085-dead-rm.png"
  },
  {
    "id": "degods-8607",
    "name": "DeGods #8607",
    "image": "https://metadata.degods.com/g/8607-dead-rm.png"
  },
  {
    "id": "degods-4527",
    "name": "DeGods #4527",
    "image": "https://metadata.degods.com/g/4527-dead-rm.png"
  },
  {
    "id": "degods-4007",
    "name": "DeGods #4007",
    "image": "https://metadata.degods.com/g/4007-dead-rm.png"
  },
  {
    "id": "degods-6169",
    "name": "DeGods #6169",
    "image": "https://metadata.degods.com/g/6169-dead.png"
  },
  {
    "id": "degods-4989",
    "name": "DeGods #4989",
    "image": "https://metadata.degods.com/g/4989-dead.png"
  },
  {
    "id": "degods-794",
    "name": "DeGods #794",
    "image": "https://metadata.degods.com/g/794-dead-rm.png"
  },
  {
    "id": "degods-9458",
    "name": "DeGods #9458",
    "image": "https://metadata.degods.com/g/9458-dead-rm.png"
  },
  {
    "id": "degods-113",
    "name": "DeGods #113",
    "image": "https://metadata.degods.com/g/113-dead-rm.png"
  },
  {
    "id": "degods-1392",
    "name": "DeGods #1392",
    "image": "https://metadata.degods.com/g/1392-dead-rm.png"
  },
  {
    "id": "degods-2806",
    "name": "DeGods #2806",
    "image": "https://metadata.degods.com/g/2806-dead.png"
  },
  {
    "id": "degods-9747",
    "name": "DeGods #9747",
    "image": "https://metadata.degods.com/g/9747-dead-rm.png"
  },
  {
    "id": "degods-7061",
    "name": "DeGods #7061",
    "image": "https://metadata.degods.com/g/7061-dead-rm.png"
  },
  {
    "id": "degods-6277",
    "name": "DeGods #6277",
    "image": "https://metadata.degods.com/g/6277-dead-rm.png"
  },
  {
    "id": "degods-1118",
    "name": "DeGods #1118",
    "image": "https://metadata.degods.com/g/1118-dead-rm.png"
  },
  {
    "id": "degods-5157",
    "name": "DeGods #5157",
    "image": "https://metadata.degods.com/g/5157-dead.png"
  },
  {
    "id": "degods-1519",
    "name": "DeGods #1519",
    "image": "https://metadata.degods.com/g/1519-dead.png"
  },
  {
    "id": "degods-6451",
    "name": "DeGods #6451",
    "image": "https://metadata.degods.com/g/6451-dead.png"
  }

             ];
        actualNftData = DeGodsNFTs.find(nft => nft.id.toString() === itemId);
                   
                        } else if (collectionId === 'clonex') {
        const cloneXNFTs = [
            
  {
    "id": "clonex-19761",
    "name": "CloneX #19761",
    "image": "https://clonex-assets.rtfkt.com/images/19761.png"
  },
  {
    "id": "clonex-19760",
    "name": "CloneX #19760",
    "image": "https://clonex-assets.rtfkt.com/images/19760.png"
  },
  {
    "id": "clonex-19759",
    "name": "CloneX #19759",
    "image": "https://clonex-assets.rtfkt.com/images/19759.png"
  },
  {
    "id": "clonex-19758",
    "name": "CloneX #19758",
    "image": "https://clonex-assets.rtfkt.com/images/19758.png"
  },
  {
    "id": "clonex-19757",
    "name": "CloneX #19757",
    "image": "https://clonex-assets.rtfkt.com/images/19757.png"
  },
  {
    "id": "clonex-19756",
    "name": "CloneX #19756",
    "image": "https://clonex-assets.rtfkt.com/images/19756.png"
  },
  {
    "id": "clonex-19755",
    "name": "CloneX #19755",
    "image": "https://clonex-assets.rtfkt.com/images/19755.png"
  },
  {
    "id": "clonex-19754",
    "name": "CloneX #19754",
    "image": "https://clonex-assets.rtfkt.com/images/19754.png"
  },
  {
    "id": "clonex-19753",
    "name": "CloneX #19753",
    "image": "https://clonex-assets.rtfkt.com/images/19753.png"
  },
  {
    "id": "clonex-19752",
    "name": "CloneX #19752",
    "image": "https://clonex-assets.rtfkt.com/images/19752.png"
  },
  {
    "id": "clonex-19751",
    "name": "CloneX #19751",
    "image": "https://clonex-assets.rtfkt.com/images/19751.png"
  },
  {
    "id": "clonex-19750",
    "name": "CloneX #19750",
    "image": "https://clonex-assets.rtfkt.com/images/19750.png"
  },
  {
    "id": "clonex-19749",
    "name": "CloneX #19749",
    "image": "https://clonex-assets.rtfkt.com/images/19749.png"
  },
  {
    "id": "clonex-19748",
    "name": "CloneX #19748",
    "image": "https://clonex-assets.rtfkt.com/images/19748.png"
  },
  {
    "id": "clonex-19747",
    "name": "CloneX #19747",
    "image": "https://clonex-assets.rtfkt.com/images/19747.png"
  },
  {
    "id": "clonex-19746",
    "name": "CloneX #19746",
    "image": "https://clonex-assets.rtfkt.com/images/19746.png"
  },
  {
    "id": "clonex-19745",
    "name": "CloneX #19745",
    "image": "https://clonex-assets.rtfkt.com/images/19745.png"
  },
  {
    "id": "clonex-19744",
    "name": "CloneX #19744",
    "image": "https://clonex-assets.rtfkt.com/images/19744.png"
  },
  {
    "id": "clonex-19743",
    "name": "CloneX #19743",
    "image": "https://clonex-assets.rtfkt.com/images/19743.png"
  },
  {
    "id": "clonex-19742",
    "name": "CloneX #19742",
    "image": "https://clonex-assets.rtfkt.com/images/19742.png"
  },
  {
    "id": "clonex-19741",
    "name": "CloneX #19741",
    "image": "https://clonex-assets.rtfkt.com/images/19741.png"
  },
  {
    "id": "clonex-19740",
    "name": "CloneX #19740",
    "image": "https://clonex-assets.rtfkt.com/images/19740.png"
  },
  {
    "id": "clonex-19739",
    "name": "CloneX #19739",
    "image": "https://clonex-assets.rtfkt.com/images/19739.png"
  },
  {
    "id": "clonex-19738",
    "name": "CloneX #19738",
    "image": "https://clonex-assets.rtfkt.com/images/19738.png"
  },
  {
    "id": "clonex-19737",
    "name": "CloneX #19737",
    "image": "https://clonex-assets.rtfkt.com/images/19737.png"
  },
  {
    "id": "clonex-19736",
    "name": "CloneX #19736",
    "image": "https://clonex-assets.rtfkt.com/images/19736.png"
  },
  {
    "id": "clonex-19735",
    "name": "CloneX #19735",
    "image": "https://clonex-assets.rtfkt.com/images/19735.png"
  },
  {
    "id": "clonex-19734",
    "name": "CloneX #19734",
    "image": "https://clonex-assets.rtfkt.com/images/19734.png"
  },
  {
    "id": "clonex-19733",
    "name": "CloneX #19733",
    "image": "https://clonex-assets.rtfkt.com/images/19733.png"
  },
  {
    "id": "clonex-19732",
    "name": "CloneX #19732",
    "image": "https://clonex-assets.rtfkt.com/images/19732.png"
  },
  {
    "id": "clonex-19731",
    "name": "CloneX #19731",
    "image": "https://clonex-assets.rtfkt.com/images/19731.png"
  },
  {
    "id": "clonex-19730",
    "name": "CloneX #19730",
    "image": "https://clonex-assets.rtfkt.com/images/19730.png"
  },
  {
    "id": "clonex-19729",
    "name": "CloneX #19729",
    "image": "https://clonex-assets.rtfkt.com/images/19729.png"
  },
  {
    "id": "clonex-19728",
    "name": "CloneX #19728",
    "image": "https://clonex-assets.rtfkt.com/images/19728.png"
  },
  {
    "id": "clonex-19727",
    "name": "CloneX #19727",
    "image": "https://clonex-assets.rtfkt.com/images/19727.png"
  },
  {
    "id": "clonex-19726",
    "name": "CloneX #19726",
    "image": "https://clonex-assets.rtfkt.com/images/19726.png"
  },
  {
    "id": "clonex-19725",
    "name": "CloneX #19725",
    "image": "https://clonex-assets.rtfkt.com/images/19725.png"
  },
  {
    "id": "clonex-19724",
    "name": "CloneX #19724",
    "image": "https://clonex-assets.rtfkt.com/images/19724.png"
  },
  {
    "id": "clonex-19723",
    "name": "CloneX #19723",
    "image": "https://clonex-assets.rtfkt.com/images/19723.png"
  },
  {
    "id": "clonex-19722",
    "name": "CloneX #19722",
    "image": "https://clonex-assets.rtfkt.com/images/19722.png"
  },
  {
    "id": "clonex-19721",
    "name": "CloneX #19721",
    "image": "https://clonex-assets.rtfkt.com/images/19721.png"
  },
  {
    "id": "clonex-19720",
    "name": "CloneX #19720",
    "image": "https://clonex-assets.rtfkt.com/images/19720.png"
  },
  {
    "id": "clonex-19719",
    "name": "CloneX #19719",
    "image": "https://clonex-assets.rtfkt.com/images/19719.png"
  },
  {
    "id": "clonex-19718",
    "name": "CloneX #19718",
    "image": "https://clonex-assets.rtfkt.com/images/19718.png"
  },
  {
    "id": "clonex-19717",
    "name": "CloneX #19717",
    "image": "https://clonex-assets.rtfkt.com/images/19717.png"
  },
  {
    "id": "clonex-19716",
    "name": "CloneX #19716",
    "image": "https://clonex-assets.rtfkt.com/images/19716.png"
  },
  {
    "id": "clonex-19715",
    "name": "CloneX #19715",
    "image": "https://clonex-assets.rtfkt.com/images/19715.png"
  },
  {
    "id": "clonex-19714",
    "name": "CloneX #19714",
    "image": "https://clonex-assets.rtfkt.com/images/19714.png"
  },
  {
    "id": "clonex-19713",
    "name": "CloneX #19713",
    "image": "https://clonex-assets.rtfkt.com/images/19713.png"
  },
  {
    "id": "clonex-19712",
    "name": "CloneX #19712",
    "image": "https://clonex-assets.rtfkt.com/images/19712.png"
  },
  {
    "id": "clonex-19711",
    "name": "CloneX #19711",
    "image": "https://clonex-assets.rtfkt.com/images/19711.png"
  },
  {
    "id": "clonex-19710",
    "name": "CloneX #19710",
    "image": "https://clonex-assets.rtfkt.com/images/19710.png"
  },
  {
    "id": "clonex-19709",
    "name": "CloneX #19709",
    "image": "https://clonex-assets.rtfkt.com/images/19709.png"
  },
  {
    "id": "clonex-19708",
    "name": "CloneX #19708",
    "image": "https://clonex-assets.rtfkt.com/images/19708.png"
  },
  {
    "id": "clonex-19707",
    "name": "CloneX #19707",
    "image": "https://clonex-assets.rtfkt.com/images/19707.png"
  },
  {
    "id": "clonex-19706",
    "name": "CloneX #19706",
    "image": "https://clonex-assets.rtfkt.com/images/19706.png"
  },
  {
    "id": "clonex-19705",
    "name": "CloneX #19705",
    "image": "https://clonex-assets.rtfkt.com/images/19705.png"
  },
  {
    "id": "clonex-19704",
    "name": "CloneX #19704",
    "image": "https://clonex-assets.rtfkt.com/images/19704.png"
  },
  {
    "id": "clonex-19703",
    "name": "CloneX #19703",
    "image": "https://clonex-assets.rtfkt.com/images/19703.png"
  },
  {
    "id": "clonex-19702",
    "name": "CloneX #19702",
    "image": "https://clonex-assets.rtfkt.com/images/19702.png"
  },
  {
    "id": "clonex-19701",
    "name": "CloneX #19701",
    "image": "https://clonex-assets.rtfkt.com/images/19701.png"
  },
  {
    "id": "clonex-19700",
    "name": "CloneX #19700",
    "image": "https://clonex-assets.rtfkt.com/images/19700.png"
  },
  {
    "id": "clonex-19699",
    "name": "CloneX #19699",
    "image": "https://clonex-assets.rtfkt.com/images/19699.png"
  },
  {
    "id": "clonex-19698",
    "name": "CloneX #19698",
    "image": "https://clonex-assets.rtfkt.com/images/19698.png"
  },
  {
    "id": "clonex-19697",
    "name": "CloneX #19697",
    "image": "https://clonex-assets.rtfkt.com/images/19697.png"
  },
  {
    "id": "clonex-19696",
    "name": "CloneX #19696",
    "image": "https://clonex-assets.rtfkt.com/images/19696.png"
  },
  {
    "id": "clonex-19695",
    "name": "CloneX #19695",
    "image": "https://clonex-assets.rtfkt.com/images/19695.png"
  },
  {
    "id": "clonex-19694",
    "name": "CloneX #19694",
    "image": "https://clonex-assets.rtfkt.com/images/19694.png"
  },
  {
    "id": "clonex-19693",
    "name": "CloneX #19693",
    "image": "https://clonex-assets.rtfkt.com/images/19693.png"
  },
  {
    "id": "clonex-19692",
    "name": "CloneX #19692",
    "image": "https://clonex-assets.rtfkt.com/images/19692.png"
  },
  {
    "id": "clonex-19691",
    "name": "CloneX #19691",
    "image": "https://clonex-assets.rtfkt.com/images/19691.png"
  },
  {
    "id": "clonex-19690",
    "name": "CloneX #19690",
    "image": "https://clonex-assets.rtfkt.com/images/19690.png"
  },
  {
    "id": "clonex-19689",
    "name": "CloneX #19689",
    "image": "https://clonex-assets.rtfkt.com/images/19689.png"
  },
  {
    "id": "clonex-19688",
    "name": "CloneX #19688",
    "image": "https://clonex-assets.rtfkt.com/images/19688.png"
  },
  {
    "id": "clonex-19687",
    "name": "CloneX #19687",
    "image": "https://clonex-assets.rtfkt.com/images/19687.png"
  },
  {
    "id": "clonex-19686",
    "name": "CloneX #19686",
    "image": "https://clonex-assets.rtfkt.com/images/19686.png"
  },
  {
    "id": "clonex-19685",
    "name": "CloneX #19685",
    "image": "https://clonex-assets.rtfkt.com/images/19685.png"
  },
  {
    "id": "clonex-19684",
    "name": "CloneX #19684",
    "image": "https://clonex-assets.rtfkt.com/images/19684.png"
  },
  {
    "id": "clonex-19683",
    "name": "CloneX #19683",
    "image": "https://clonex-assets.rtfkt.com/images/19683.png"
  },
  {
    "id": "clonex-19682",
    "name": "CloneX #19682",
    "image": "https://clonex-assets.rtfkt.com/images/19682.png"
  },
  {
    "id": "clonex-19681",
    "name": "CloneX #19681",
    "image": "https://clonex-assets.rtfkt.com/images/19681.png"
  },
  {
    "id": "clonex-19680",
    "name": "CloneX #19680",
    "image": "https://clonex-assets.rtfkt.com/images/19680.png"
  },
  {
    "id": "clonex-19679",
    "name": "CloneX #19679",
    "image": "https://clonex-assets.rtfkt.com/images/19679.png"
  },
  {
    "id": "clonex-19678",
    "name": "CloneX #19678",
    "image": "https://clonex-assets.rtfkt.com/images/19678.png"
  },
  {
    "id": "clonex-19677",
    "name": "CloneX #19677",
    "image": "https://clonex-assets.rtfkt.com/images/19677.png"
  },
  {
    "id": "clonex-19676",
    "name": "CloneX #19676",
    "image": "https://clonex-assets.rtfkt.com/images/19676.png"
  },
  {
    "id": "clonex-19675",
    "name": "CloneX #19675",
    "image": "https://clonex-assets.rtfkt.com/images/19675.png"
  },
  {
    "id": "clonex-19674",
    "name": "CloneX #19674",
    "image": "https://clonex-assets.rtfkt.com/images/19674.png"
  },
  {
    "id": "clonex-19673",
    "name": "CloneX #19673",
    "image": "https://clonex-assets.rtfkt.com/images/19673.png"
  },
  {
    "id": "clonex-19672",
    "name": "CloneX #19672",
    "image": "https://clonex-assets.rtfkt.com/images/19672.png"
  },
  {
    "id": "clonex-19671",
    "name": "CloneX #19671",
    "image": "https://clonex-assets.rtfkt.com/images/19671.png"
  },
  {
    "id": "clonex-19670",
    "name": "CloneX #19670",
    "image": "https://clonex-assets.rtfkt.com/images/19670.png"
  },
  {
    "id": "clonex-19669",
    "name": "CloneX #19669",
    "image": "https://clonex-assets.rtfkt.com/images/19669.png"
  },
  {
    "id": "clonex-19668",
    "name": "CloneX #19668",
    "image": "https://clonex-assets.rtfkt.com/images/19668.png"
  },
  {
    "id": "clonex-19667",
    "name": "CloneX #19667",
    "image": "https://clonex-assets.rtfkt.com/images/19667.png"
  },
  {
    "id": "clonex-19666",
    "name": "CloneX #19666",
    "image": "https://clonex-assets.rtfkt.com/images/19666.png"
  },
  {
    "id": "clonex-19665",
    "name": "CloneX #19665",
    "image": "https://clonex-assets.rtfkt.com/images/19665.png"
  },
  {
    "id": "clonex-19664",
    "name": "CloneX #19664",
    "image": "https://clonex-assets.rtfkt.com/images/19664.png"
  },
  {
    "id": "clonex-19663",
    "name": "CloneX #19663",
    "image": "https://clonex-assets.rtfkt.com/images/19663.png"
  },
  {
    "id": "clonex-19662",
    "name": "CloneX #19662",
    "image": "https://clonex-assets.rtfkt.com/images/19662.png"
  }

             ];
        actualNftData = cloneXNFTs.find(nft => nft.id.toString() === itemId);
                        
                             } else if (collectionId === 'quirkies-originals') {
        const quirkiesNFTs = [
            
  {
    "id": "quirkies-4999",
    "name": "Quirkies Originals #4999",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4999.png"
  },
  {
    "id": "quirkies-4998",
    "name": "Quirkies Originals #4998",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4998.png"
  },
  {
    "id": "quirkies-4997",
    "name": "Quirkies Originals #4997",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4997.png"
  },
  {
    "id": "quirkies-4996",
    "name": "Quirkies Originals #4996",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4996.png"
  },
  {
    "id": "quirkies-4995",
    "name": "Quirkies Originals #4995",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4995.png"
  },
  {
    "id": "quirkies-4994",
    "name": "Quirkies Originals #4994",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4994.png"
  },
  {
    "id": "quirkies-4993",
    "name": "Quirkies Originals #4993",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4993.png"
  },
  {
    "id": "quirkies-4992",
    "name": "Quirkies Originals #4992",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4992.png"
  },
  {
    "id": "quirkies-4991",
    "name": "Quirkies Originals #4991",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4991.png"
  },
  {
    "id": "quirkies-4989",
    "name": "Quirkies Originals #4989",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4989.png"
  },
  {
    "id": "quirkies-4986",
    "name": "Quirkies Originals #4986",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4986.png"
  },
  {
    "id": "quirkies-4988",
    "name": "Quirkies Originals #4988",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4988.png"
  },
  {
    "id": "quirkies-4985",
    "name": "Quirkies Originals #4985",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4985.png"
  },
  {
    "id": "quirkies-4990",
    "name": "Quirkies Originals #4990",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4990.png"
  },
  {
    "id": "quirkies-4984",
    "name": "Quirkies Originals #4984",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4984.png"
  },
  {
    "id": "quirkies-4983",
    "name": "Quirkies Originals #4983",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4983.png"
  },
  {
    "id": "quirkies-4987",
    "name": "Quirkies Originals #4987",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4987.png"
  },
  {
    "id": "quirkies-4982",
    "name": "Quirkies Originals #4982",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4982.png"
  },
  {
    "id": "quirkies-4981",
    "name": "Quirkies Originals #4981",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4981.png"
  },
  {
    "id": "quirkies-4980",
    "name": "Quirkies Originals #4980",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4980.png"
  },
  {
    "id": "quirkies-4979",
    "name": "Quirkies Originals #4979",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4979.png"
  },
  {
    "id": "quirkies-4978",
    "name": "Quirkies Originals #4978",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4978.png"
  },
  {
    "id": "quirkies-4977",
    "name": "Quirkies Originals #4977",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4977.png"
  },
  {
    "id": "quirkies-4976",
    "name": "Quirkies Originals #4976",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4976.png"
  },
  {
    "id": "quirkies-4974",
    "name": "Quirkies Originals #4974",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4974.png"
  },
  {
    "id": "quirkies-4975",
    "name": "Quirkies Originals #4975",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4975.png"
  },
  {
    "id": "quirkies-4973",
    "name": "Quirkies Originals #4973",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4973.png"
  },
  {
    "id": "quirkies-4972",
    "name": "Quirkies Originals #4972",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4972.png"
  },
  {
    "id": "quirkies-4971",
    "name": "Quirkies Originals #4971",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4971.png"
  },
  {
    "id": "quirkies-4970",
    "name": "Quirkies Originals #4970",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4970.png"
  },
  {
    "id": "quirkies-4969",
    "name": "Quirkies Originals #4969",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4969.png"
  },
  {
    "id": "quirkies-4966",
    "name": "Quirkies Originals #4966",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4966.png"
  },
  {
    "id": "quirkies-4968",
    "name": "Quirkies Originals #4968",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4968.png"
  },
  {
    "id": "quirkies-4965",
    "name": "Quirkies Originals #4965",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4965.png"
  },
  {
    "id": "quirkies-4967",
    "name": "Quirkies Originals #4967",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4967.png"
  },
  {
    "id": "quirkies-4963",
    "name": "Quirkies Originals #4963",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4963.png"
  },
  {
    "id": "quirkies-4964",
    "name": "Quirkies Originals #4964",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4964.png"
  },
  {
    "id": "quirkies-4962",
    "name": "Quirkies Originals #4962",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4962.png"
  },
  {
    "id": "quirkies-4960",
    "name": "Quirkies Originals #4960",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4960.png"
  },
  {
    "id": "quirkies-4961",
    "name": "Quirkies Originals #4961",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4961.png"
  },
  {
    "id": "quirkies-4959",
    "name": "Quirkies Originals #4959",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4959.png"
  },
  {
    "id": "quirkies-4956",
    "name": "Quirkies Originals #4956",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4956.png"
  },
  {
    "id": "quirkies-4958",
    "name": "Quirkies Originals #4958",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4958.png"
  },
  {
    "id": "quirkies-4957",
    "name": "Quirkies Originals #4957",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4957.png"
  },
  {
    "id": "quirkies-4954",
    "name": "Quirkies Originals #4954",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4954.png"
  },
  {
    "id": "quirkies-4953",
    "name": "Quirkies Originals #4953",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4953.png"
  },
  {
    "id": "quirkies-4952",
    "name": "Quirkies Originals #4952",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4952.png"
  },
  {
    "id": "quirkies-4955",
    "name": "Quirkies Originals #4955",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4955.png"
  },
  {
    "id": "quirkies-4951",
    "name": "Quirkies Originals #4951",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4951.png"
  },
  {
    "id": "quirkies-4950",
    "name": "Quirkies Originals #4950",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4950.png"
  },
  {
    "id": "quirkies-4948",
    "name": "Quirkies Originals #4948",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4948.png"
  },
  {
    "id": "quirkies-4949",
    "name": "Quirkies Originals #4949",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4949.png"
  },
  {
    "id": "quirkies-4947",
    "name": "Quirkies Originals #4947",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4947.png"
  },
  {
    "id": "quirkies-4944",
    "name": "Quirkies Originals #4944",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4944.png"
  },
  {
    "id": "quirkies-4946",
    "name": "Quirkies Originals #4946",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4946.png"
  },
  {
    "id": "quirkies-4945",
    "name": "Quirkies Originals #4945",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4945.png"
  },
  {
    "id": "quirkies-4943",
    "name": "Quirkies Originals #4943",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4943.png"
  },
  {
    "id": "quirkies-4942",
    "name": "Quirkies Originals #4942",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4942.png"
  },
  {
    "id": "quirkies-4941",
    "name": "Quirkies Originals #4941",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4941.png"
  },
  {
    "id": "quirkies-4940",
    "name": "Quirkies Originals #4940",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4940.png"
  },
  {
    "id": "quirkies-4939",
    "name": "Quirkies Originals #4939",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4939.png"
  },
  {
    "id": "quirkies-4938",
    "name": "Quirkies Originals #4938",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4938.png"
  },
  {
    "id": "quirkies-4937",
    "name": "Quirkies Originals #4937",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4937.png"
  },
  {
    "id": "quirkies-4936",
    "name": "Quirkies Originals #4936",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4936.png"
  },
  {
    "id": "quirkies-4935",
    "name": "Quirkies Originals #4935",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4935.png"
  },
  {
    "id": "quirkies-4934",
    "name": "Quirkies Originals #4934",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4934.png"
  },
  {
    "id": "quirkies-4933",
    "name": "Quirkies Originals #4933",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4933.png"
  },
  {
    "id": "quirkies-4932",
    "name": "Quirkies Originals #4932",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4932.png"
  },
  {
    "id": "quirkies-4931",
    "name": "Quirkies Originals #4931",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4931.png"
  },
  {
    "id": "quirkies-4930",
    "name": "Quirkies Originals #4930",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4930.png"
  },
  {
    "id": "quirkies-4929",
    "name": "Quirkies Originals #4929",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4929.png"
  },
  {
    "id": "quirkies-4928",
    "name": "Quirkies Originals #4928",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4928.png"
  },
  {
    "id": "quirkies-4926",
    "name": "Quirkies Originals #4926",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4926.png"
  },
  {
    "id": "quirkies-4923",
    "name": "Quirkies Originals #4923",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4923.png"
  },
  {
    "id": "quirkies-4925",
    "name": "Quirkies Originals #4925",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4925.png"
  },
  {
    "id": "quirkies-4924",
    "name": "Quirkies Originals #4924",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4924.png"
  },
  {
    "id": "quirkies-4922",
    "name": "Quirkies Originals #4922",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4922.png"
  },
  {
    "id": "quirkies-4920",
    "name": "Quirkies Originals #4920",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4920.png"
  },
  {
    "id": "quirkies-4921",
    "name": "Quirkies Originals #4921",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4921.png"
  },
  {
    "id": "quirkies-4919",
    "name": "Quirkies Originals #4919",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4919.png"
  },
  {
    "id": "quirkies-4918",
    "name": "Quirkies Originals #4918",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4918.png"
  },
  {
    "id": "quirkies-4916",
    "name": "Quirkies Originals #4916",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4916.png"
  },
  {
    "id": "quirkies-4917",
    "name": "Quirkies Originals #4917",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4917.png"
  },
  {
    "id": "quirkies-4915",
    "name": "Quirkies Originals #4915",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4915.png"
  },
  {
    "id": "quirkies-4914",
    "name": "Quirkies Originals #4914",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4914.png"
  },
  {
    "id": "quirkies-4913",
    "name": "Quirkies Originals #4913",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4913.png"
  },
  {
    "id": "quirkies-4912",
    "name": "Quirkies Originals #4912",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4912.png"
  },
  {
    "id": "quirkies-4911",
    "name": "Quirkies Originals #4911",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4911.png"
  },
  {
    "id": "quirkies-4910",
    "name": "Quirkies Originals #4910",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4910.png"
  },
  {
    "id": "quirkies-4909",
    "name": "Quirkies Originals #4909",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4909.png"
  },
  {
    "id": "quirkies-4908",
    "name": "Quirkies Originals #4908",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4908.png"
  },
  {
    "id": "quirkies-4907",
    "name": "Quirkies Originals #4907",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4907.png"
  },
  {
    "id": "quirkies-4906",
    "name": "Quirkies Originals #4906",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4906.png"
  },
  {
    "id": "quirkies-4905",
    "name": "Quirkies Originals #4905",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4905.png"
  },
  {
    "id": "quirkies-4904",
    "name": "Quirkies Originals #4904",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4904.png"
  },
  {
    "id": "quirkies-4902",
    "name": "Quirkies Originals #4902",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4902.png"
  },
  {
    "id": "quirkies-4903",
    "name": "Quirkies Originals #4903",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4903.png"
  },
  {
    "id": "quirkies-4901",
    "name": "Quirkies Originals #4901",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4901.png"
  },
  {
    "id": "quirkies-4900",
    "name": "Quirkies Originals #4900",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4900.png"
  },
  {
    "id": "quirkies-4898",
    "name": "Quirkies Originals #4898",
    "image": "https://ipfs.io/ipfs/bafybeifm3xckuzmsfpj5qxo6w67h2w2m5wl5bdc7fdmqnfh54bmvgpq3cq/4898.png"
  }
            
             ];
        actualNftData = quirkiesNFTs.find(nft => nft.id.toString() === itemId);
                             
                    } else if (collectionId === 'mfer') {
        const mfersNFTs = [
            
  {
    "id": "mfers-10002",
    "name": "mfers #10002",
    "image": "https://ipfs.io/ipfs/QmY5ZGBbsbtapwAHpzZ8dn3picFbVtYbDRKZioivoJpmwx"
  },
  {
    "id": "mfers-10001",
    "name": "mfers #10001",
    "image": "https://ipfs.io/ipfs/QmfVHu7wBHRxL4mTe45hWkeshwM5hhw7g5xUmJgmCgGx79"
  },
  {
    "id": "mfers-10013",
    "name": "mfers #10013",
    "image": "https://ipfs.io/ipfs/QmRpoHW8LaSszMFX871c9WnZy3A4iB4FNpq9kT1uyFCwYE"
  },
  {
    "id": "mfers-10012",
    "name": "mfers #10012",
    "image": "https://ipfs.io/ipfs/QmXP94Q7sMSAaNs7to8o4CdsroEkXh7izLuGx6tHu36kwy"
  },
  {
    "id": "mfers-9974",
    "name": "mfers #9974",
    "image": "https://ipfs.io/ipfs/QmV719FZBXhN1S1NBXy7PrCeLoQ1PgeRSX5hy5MQGzd4fd"
  },
  {
    "id": "mfers-9991",
    "name": "mfers #9991",
    "image": "https://ipfs.io/ipfs/Qmd7Lqzdd5RsPX7FzvNpbZSHws4AqMzQhXRxzksvH3unpc"
  },
  {
    "id": "mfers-10000",
    "name": "mfers #10000",
    "image": "https://ipfs.io/ipfs/QmTsjb8m8u4xaxm3CDz1HeLWYGnJ4hCWYq2sQ1j45i42Cw"
  },
  {
    "id": "mfers-10011",
    "name": "mfers #10011",
    "image": "https://ipfs.io/ipfs/QmZMkCaK7uwBBWoWazCez1fp655gLxykRpPPqRG6wLU3K7"
  },
  {
    "id": "mfers-9973",
    "name": "mfers #9973",
    "image": "https://ipfs.io/ipfs/QmbzFL2Ja3eDMWyuVpYodWM9E88GiFdHq9HpwNAXWUwJon"
  },
  {
    "id": "mfers-9990",
    "name": "mfers #9990",
    "image": "https://ipfs.io/ipfs/QmR52SinECMKFXrw2XkxkahzrpLxTkERRMuiaYnwkUBULn"
  },
  {
    "id": "mfers-9972",
    "name": "mfers #9972",
    "image": "https://ipfs.io/ipfs/QmZPqzmRwc5eREoiTD2WgfFJuPJoCzd8Nr6WkC9zBay35o"
  },
  {
    "id": "mfers-10010",
    "name": "mfers #10010",
    "image": "https://ipfs.io/ipfs/QmckDWMX3831TuCehtC4E2Uig9VXP41tcsmpsVyCHkjpyY"
  },
  {
    "id": "mfers-9999",
    "name": "mfers #9999",
    "image": "https://ipfs.io/ipfs/QmaSwX5KWwBrfKjPiSEmnricgCMqp1cg7YqEG3et9aP9eA"
  },
  {
    "id": "mfers-9989",
    "name": "mfers #9989",
    "image": "https://ipfs.io/ipfs/QmbuFTAr6MH92JViNLuDxhkYZK7fz55S4jNCEwTJeXBqVc"
  },
  {
    "id": "mfers-9971",
    "name": "mfers #9971",
    "image": "https://ipfs.io/ipfs/QmYDXrQDYibmJU2dnJfwuiUMsT8vF8n6GoTP32E4Shms5o"
  },
  {
    "id": "mfers-9963",
    "name": "mfers #9963",
    "image": "https://ipfs.io/ipfs/Qman9XTGf5hESjFUY3gi1vzgYjFP8Hi63p245C117zoMWt"
  },
  {
    "id": "mfers-9988",
    "name": "mfers #9988",
    "image": "https://ipfs.io/ipfs/QmPvqkie95aam1kCbTQ2SRGV8wwwh3kbxPh1jpykma7nq5"
  },
  {
    "id": "mfers-10009",
    "name": "mfers #10009",
    "image": "https://ipfs.io/ipfs/QmNVMAkpXKEtweaa25SfeJ9Sf4HBexLVfGiaqP37XXi99q"
  },
  {
    "id": "mfers-9998",
    "name": "mfers #9998",
    "image": "https://ipfs.io/ipfs/Qmdg2ZyCB7WrBYFNuAkURNiM9uRudVgTeSUHfg4raqHX3o"
  },
  {
    "id": "mfers-9956",
    "name": "mfers #9956",
    "image": "https://ipfs.io/ipfs/Qmahv6hGDZJC3kSwpBn3BiePvKfEGDmyoGa5Y9HYt4fGnc"
  },
  {
    "id": "mfers-9970",
    "name": "mfers #9970",
    "image": "https://ipfs.io/ipfs/QmNSeZFZ71Pouxs97CPptKnWz6RLT7XtPobKQ1oHntSX87"
  },
  {
    "id": "mfers-9962",
    "name": "mfers #9962",
    "image": "https://ipfs.io/ipfs/QmYEGS83RyDbgG5qEPoCGydTtpDYqbVWFcQHpVh1wMBEGB"
  },
  {
    "id": "mfers-10008",
    "name": "mfers #10008",
    "image": "https://ipfs.io/ipfs/QmbtbuVoj89FnRjUY5jDg2ALBaeiBTaJ56MpsbCsPKHgFq"
  },
  {
    "id": "mfers-9987",
    "name": "mfers #9987",
    "image": "https://ipfs.io/ipfs/QmcwhLd5mcRoJ5ZSgxDudayKWsvzYNTT7wuDu8dq2111sp"
  },
  {
    "id": "mfers-9997",
    "name": "mfers #9997",
    "image": "https://ipfs.io/ipfs/QmeCUcwKh4QH5hXV7isPcwei8ZBVWsdxes68KxpLvMVXp3"
  },
  {
    "id": "mfers-9981",
    "name": "mfers #9981",
    "image": "https://ipfs.io/ipfs/QmQ9xJtieSxE71U9wbKfeWi5xGec4qTszHMfeEcuMyWzUW"
  },
  {
    "id": "mfers-9955",
    "name": "mfers #9955",
    "image": "https://ipfs.io/ipfs/QmPkFmpZjayzkMpm3sYZSsZ1Bug8jWRdeDT3NGC7oBmjp5"
  },
  {
    "id": "mfers-9969",
    "name": "mfers #9969",
    "image": "https://ipfs.io/ipfs/QmYi4sBxW9pvmedF1SBPg6BsE4oaPqnJpbj1aHwnFnCZ2v"
  },
  {
    "id": "mfers-9961",
    "name": "mfers #9961",
    "image": "https://ipfs.io/ipfs/QmW2Y3EUKR1D8QcjS9qdxypnoLfZaTgfNaVi4mzYuSvHtH"
  },
  {
    "id": "mfers-10007",
    "name": "mfers #10007",
    "image": "https://ipfs.io/ipfs/QmNfdN3PUitXi8eEtMRkA28ydziaahWPZEM2P7RBCKpy5U"
  },
  {
    "id": "mfers-9986",
    "name": "mfers #9986",
    "image": "https://ipfs.io/ipfs/QmeCKCYgY7i3RtTtRajXpDM5nMs2pvhhgW3zQQ866gyCaQ"
  },
  {
    "id": "mfers-9996",
    "name": "mfers #9996",
    "image": "https://ipfs.io/ipfs/QmVoWjomCtvZ8zntikP6R4YVvXQEHoVg2wF7RLwEqw3xgd"
  },
  {
    "id": "mfers-9980",
    "name": "mfers #9980",
    "image": "https://ipfs.io/ipfs/QmWLdoaNHyszKQ9Z1jfVfHJU9YADXzzhQgbMbUXEwW7kwB"
  },
  {
    "id": "mfers-9968",
    "name": "mfers #9968",
    "image": "https://ipfs.io/ipfs/QmSv7beh3vH5pUyjt6fUv8H5Vw5YzyZvrxY94sMD7Qi2hd"
  },
  {
    "id": "mfers-9954",
    "name": "mfers #9954",
    "image": "https://ipfs.io/ipfs/QmbhBb9fjf2dBHG1smRNXMr7gKSeoN6J9MgAp7qZqvnVpH"
  },
  {
    "id": "mfers-9960",
    "name": "mfers #9960",
    "image": "https://ipfs.io/ipfs/QmRcRrVmWo41tVrDzfaHDqswXtGrB4EM2wUFdC4MbnZYEe"
  },
  {
    "id": "mfers-10006",
    "name": "mfers #10006",
    "image": "https://ipfs.io/ipfs/QmegjcVv1SK6S7od8ou9c9sZ94r1tVFCQ7TN8BaBpq5mVb"
  },
  {
    "id": "mfers-10018",
    "name": "mfers #10018",
    "image": "https://ipfs.io/ipfs/QmX7AWruXKt2xrbiPxYJrYCqYYVim72kf56g2KH4sLCdfG"
  },
  {
    "id": "mfers-9985",
    "name": "mfers #9985",
    "image": "https://ipfs.io/ipfs/QmTosPjTriDV7a7N2Py7BYzZpgdKUeFmLeTQyyYLPB9pNC"
  },
  {
    "id": "mfers-9995",
    "name": "mfers #9995",
    "image": "https://ipfs.io/ipfs/QmR7a6G7seAGYEBGTyVbbPfNwbe2RF1DWzjpfRvXmdFLV4"
  },
  {
    "id": "mfers-9979",
    "name": "mfers #9979",
    "image": "https://ipfs.io/ipfs/Qmash4v3EiVrJQ3sXHmFF6dRPnioJ6yMuQahDHxi9rwRyd"
  },
  {
    "id": "mfers-9967",
    "name": "mfers #9967",
    "image": "https://ipfs.io/ipfs/QmXmLrPt954MMkXu5yf8XP47ScpgRN6B3XE57jFN6nirrK"
  },
  {
    "id": "mfers-9953",
    "name": "mfers #9953",
    "image": "https://ipfs.io/ipfs/QmQSCqXTpn1zM34KAPjnbXnwGc1pWUfjxKhLFCwhbeToYY"
  },
  {
    "id": "mfers-10017",
    "name": "mfers #10017",
    "image": "https://ipfs.io/ipfs/QmemUr1ZmvoE6aNsRJ9feLkftACzkD4NFQbUPJx6JyNYPW"
  },
  {
    "id": "mfers-9984",
    "name": "mfers #9984",
    "image": "https://ipfs.io/ipfs/QmYuPyCiQxtDVsFmEssXTDNKD1avC3Y6CgwFQDMT4qd69D"
  },
  {
    "id": "mfers-9959",
    "name": "mfers #9959",
    "image": "https://ipfs.io/ipfs/QmbvxbY1EdMbYgzprE3EwqZjcCMa5dmShQ62fcGPtzfDWX"
  },
  {
    "id": "mfers-10005",
    "name": "mfers #10005",
    "image": "https://ipfs.io/ipfs/QmQp7RcDqgqqdi4QZgytforvyRXipBULGPX56o9fpwcxY1"
  },
  {
    "id": "mfers-9978",
    "name": "mfers #9978",
    "image": "https://ipfs.io/ipfs/QmQRZKc98TQhSXyw2JETh6gyDME3kTiqFnxe8CMfvXSJGj"
  },
  {
    "id": "mfers-9994",
    "name": "mfers #9994",
    "image": "https://ipfs.io/ipfs/QmcJNrTLVNLaaAAp87f6SwzTSvxXsYDmMcuvBCWrP3dQjH"
  },
  {
    "id": "mfers-9966",
    "name": "mfers #9966",
    "image": "https://ipfs.io/ipfs/QmPhrmiRZF5n64oqM2kciZ8rHcG7VHcq6mBcRzY6jFxppy"
  },
  {
    "id": "mfers-10016",
    "name": "mfers #10016",
    "image": "https://ipfs.io/ipfs/QmRPBz9kHpnNd2dBsU3aqN8rUh2JxaEwcPz9xqniMyZkjN"
  },
  {
    "id": "mfers-9952",
    "name": "mfers #9952",
    "image": "https://ipfs.io/ipfs/QmSrfcyUefrR5uEs2eTy3BXpVnA6x473ASmxTWKB8X5Yyb"
  },
  {
    "id": "mfers-9983",
    "name": "mfers #9983",
    "image": "https://ipfs.io/ipfs/QmeuLkWfLnVSxxeSwzVyi36fbGj2J3nJtY4C7zLaLW9gJE"
  },
  {
    "id": "mfers-10014",
    "name": "mfers #10014",
    "image": "https://ipfs.io/ipfs/QmV3sK557QQVL1Smsc7obvvurcoXSgrBx2sKvzUFnGRiwo"
  },
  {
    "id": "mfers-9958",
    "name": "mfers #9958",
    "image": "https://ipfs.io/ipfs/QmUGYbdQDbJ5qubcviN19NnxJ4JHULh5ohM7dj2sZDjN8F"
  },
  {
    "id": "mfers-10004",
    "name": "mfers #10004",
    "image": "https://ipfs.io/ipfs/QmW4frLbCeBWLeAHQzBm5NbuZQ7qyhjijuVDAHMfuCTjnh"
  },
  {
    "id": "mfers-10019",
    "name": "mfers #10019",
    "image": "https://ipfs.io/ipfs/QmaD3XMmT9KqPWCuytxS1f5UhNxkfgZUEeDdF1jSpqGXio"
  },
  {
    "id": "mfers-10020",
    "name": "mfers #10020",
    "image": "https://ipfs.io/ipfs/QmT6ryBSiqurdaBo3pC6HaRwhgAPwc16a5zPBANw9FYNeY"
  },
  {
    "id": "mfers-9976",
    "name": "mfers #9976",
    "image": "https://ipfs.io/ipfs/QmZRucR2dViai1VmrhLEpuWLXn8qqudfLZNvGo5EAm8PPB"
  },
  {
    "id": "mfers-9964",
    "name": "mfers #9964",
    "image": "https://ipfs.io/ipfs/QmW6RSZ6a6V6xecAsBtXbvSogrBeZng8AJ3SBSzVsPzBBi"
  },
  {
    "id": "mfers-9993",
    "name": "mfers #9993",
    "image": "https://ipfs.io/ipfs/QmexteNBfceTJge3w4mFfqobyCsW9nAfKpnAQJ1s5wpYSn"
  },
  {
    "id": "mfers-9965",
    "name": "mfers #9965",
    "image": "https://ipfs.io/ipfs/QmRsMx4k1ueaRtW3wqCMrBzb8FWqgUvS3if7Xhg2mcX8as"
  },
  {
    "id": "mfers-9977",
    "name": "mfers #9977",
    "image": "https://ipfs.io/ipfs/QmbcJLBZ9DxqtjSeHUPj5tgLbTiqb7iYxwg7G9Ghm6poRe"
  },
  {
    "id": "mfers-10015",
    "name": "mfers #10015",
    "image": "https://ipfs.io/ipfs/QmTyT56se1tAyfJk8dK2AvGejsVDfpwy9An4ebUFXW9Cc6"
  },
  {
    "id": "mfers-9992",
    "name": "mfers #9992",
    "image": "https://ipfs.io/ipfs/QmU37rmkiQDns6Fv1QZDsBoJCXDdqYBcBCdiRB3YM6iuUK"
  },
  {
    "id": "mfers-9982",
    "name": "mfers #9982",
    "image": "https://ipfs.io/ipfs/QmVY34KQ1KVngJcqqPCs8UkkYQHoHwsvTHvWJWKR7Pm1ZU"
  },
  {
    "id": "mfers-10003",
    "name": "mfers #10003",
    "image": "https://ipfs.io/ipfs/QmNUmqXTFbUSBbYskkV1LC4z1gqg9a9PWFcaCqPmr5sPuf"
  },
  {
    "id": "mfers-9957",
    "name": "mfers #9957",
    "image": "https://ipfs.io/ipfs/QmUEvisyJBzDGJZwPMTYvuhho9K3WQxVTuAZ5gYpCz4nJz"
  },
  {
    "id": "mfers-9975",
    "name": "mfers #9975",
    "image": "https://ipfs.io/ipfs/QmeTiLP6vRf37FMrZUo3EjtnHCh8GZibRNzrBvPFBYHGn4"
  },
  {
    "id": "mfers-9912",
    "name": "mfers #9912",
    "image": "https://ipfs.io/ipfs/QmbjdHwEj1CN8eUjq3xSsijKv7ddGc8pFvHnmNwMMH3kY4"
  },
  {
    "id": "mfers-9911",
    "name": "mfers #9911",
    "image": "https://ipfs.io/ipfs/QmRcMe6kvRBxZ3Pb1HhxnaDEj43Cw8Q9SNinAhh1ABo1z4"
  },
  {
    "id": "mfers-9922",
    "name": "mfers #9922",
    "image": "https://ipfs.io/ipfs/QmYWw4ychXCjnW8hdTTGWoKTET9K3UcKX7boBE5eqEUtC4"
  },
  {
    "id": "mfers-9932",
    "name": "mfers #9932",
    "image": "https://ipfs.io/ipfs/QmQ28UqxcTxKMff5Y2PGeX1siysQCXVJ32vAf4H28sKSTV"
  },
  {
    "id": "mfers-9902",
    "name": "mfers #9902",
    "image": "https://ipfs.io/ipfs/QmPehK6AqJoJCuygerqVNhe72rbvg5RUVGgaUu8E4kafuZ"
  },
  {
    "id": "mfers-9910",
    "name": "mfers #9910",
    "image": "https://ipfs.io/ipfs/QmfUE1PW7vmFmsS5kqUB8gtBxFQCrbWHsiBmC6RHUgaHDM"
  },
  {
    "id": "mfers-9931",
    "name": "mfers #9931",
    "image": "https://ipfs.io/ipfs/QmUWGjvFuKHoq7zQKHX712MQCtQPjiiuJtme76beCcdm51"
  },
  {
    "id": "mfers-9901",
    "name": "mfers #9901",
    "image": "https://ipfs.io/ipfs/QmPpRngMoiVxKPrErGVfZu6KGzVXQhcaqfxA7aUyDovgo1"
  },
  {
    "id": "mfers-9909",
    "name": "mfers #9909",
    "image": "https://ipfs.io/ipfs/QmdYXW2R1ZtKvtgZB9rcNy2UFnzB9ybDmoQqVfdhR9R8nT"
  },
  {
    "id": "mfers-9930",
    "name": "mfers #9930",
    "image": "https://ipfs.io/ipfs/QmcUAtjynwjwXmMvTVYyz37jz4Uu8ePuSpxcYhnVgFxcfm"
  },
  {
    "id": "mfers-9921",
    "name": "mfers #9921",
    "image": "https://ipfs.io/ipfs/QmXHCKi1N6hrrNBAWxiD28HGkaJcgbsLBdRfnD5zZzbhcm"
  },
  {
    "id": "mfers-9908",
    "name": "mfers #9908",
    "image": "https://ipfs.io/ipfs/QmS4AW7QthPvcNsBwkrXfkCFjjRkxPWqEngM7FY1qC65RT"
  },
  {
    "id": "mfers-9900",
    "name": "mfers #9900",
    "image": "https://ipfs.io/ipfs/QmYpA12Xg15W7TVLYGCQVMwzDas1cmQaxKvypkVLPBgYXZ"
  },
  {
    "id": "mfers-9929",
    "name": "mfers #9929",
    "image": "https://ipfs.io/ipfs/QmeyVX5tJJuhy6QXbFjGJc7MQsFFqkZkq7yadhpUrN9Qu9"
  },
  {
    "id": "mfers-9920",
    "name": "mfers #9920",
    "image": "https://ipfs.io/ipfs/Qmf1KGJ2GCL3H3BCSCf4sjgbnbqQnvCjKxfEhMEHxsb7vz"
  },
  {
    "id": "mfers-9907",
    "name": "mfers #9907",
    "image": "https://ipfs.io/ipfs/QmchH8oGZsDHc1a3UtCG2QHFC1VK4z9otwi6TpC6FQwaQi"
  },
  {
    "id": "mfers-9944",
    "name": "mfers #9944",
    "image": "https://ipfs.io/ipfs/QmP1kEGZUtmARbvDQLv3iyv6ZLcBq7Ahkhuo5VuV2n8ccF"
  },
  {
    "id": "mfers-9790",
    "name": "mfers #9790",
    "image": "https://ipfs.io/ipfs/QmRdZX6hHLvFG6mWmKVLQ19QN9PxdchwkgNzMH5kfJiHxr"
  },
  {
    "id": "mfers-9899",
    "name": "mfers #9899",
    "image": "https://ipfs.io/ipfs/QmTmZYAWeHZJizrfLqw7VwrbSC5axAUkoDjTedRQWCKqfN"
  },
  {
    "id": "mfers-9928",
    "name": "mfers #9928",
    "image": "https://ipfs.io/ipfs/Qmb8Hu1FhMfqMPUpdtc5kTBCSCenhrFphhuyBGHnHMkheD"
  },
  {
    "id": "mfers-9919",
    "name": "mfers #9919",
    "image": "https://ipfs.io/ipfs/QmVRBkTGWXQxMNygUKusdNHA7gsE1TDpByLrZwE1hzJfCi"
  },
  {
    "id": "mfers-9758",
    "name": "mfers #9758",
    "image": "https://ipfs.io/ipfs/QmaQYqgXmZ8vZGU1YZxx6en23cCyBcucpoMAkijg71ohSF"
  },
  {
    "id": "mfers-9845",
    "name": "mfers #9845",
    "image": "https://ipfs.io/ipfs/QmfGRDHaVkMjmsFZpj4TtQb1Sn87GSej9EVeVWotZFKTyF"
  },
  {
    "id": "mfers-9805",
    "name": "mfers #9805",
    "image": "https://ipfs.io/ipfs/QmaQGJTQ33bpeYFarme3XL97avxf5XVxddDKadZDxqfkDS"
  },
  {
    "id": "mfers-9906",
    "name": "mfers #9906",
    "image": "https://ipfs.io/ipfs/QmbHaqNuhXydnthuTZsrZZWRHhHYZpgDbUxGaooLNWdMti"
  },
  {
    "id": "mfers-9943",
    "name": "mfers #9943",
    "image": "https://ipfs.io/ipfs/Qmbg2bLNGop6F123J2woEXvSfqaPmjxEfjdwZ5xam6T6s9"
  },
  {
    "id": "mfers-9789",
    "name": "mfers #9789",
    "image": "https://ipfs.io/ipfs/Qme6CiU9bSKcK4QYgiX2Lo53ToKXpPkwvewGWZWUXLBkYK"
  },
  {
    "id": "mfers-9898",
    "name": "mfers #9898",
    "image": "https://ipfs.io/ipfs/QmWowjU9vUecvetyXxN2CsNH2tSJu1Mfi11TbHBS4WhbuM"
  },
  {
    "id": "mfers-9927",
    "name": "mfers #9927",
    "image": "https://ipfs.io/ipfs/QmXtFSyFUzvsvvFvyFsygsLjeHmKfbepcBRsVoDZCco7Fi"
  },
  {
    "id": "mfers-9892",
    "name": "mfers #9892",
    "image": "https://ipfs.io/ipfs/QmRrWXY9WUuo4PL6g1e8qtEJg54awUzjjnx6G4knF3nGdX"
  },
  {
    "id": "mfers-9825",
    "name": "mfers #9825",
    "image": "https://ipfs.io/ipfs/Qmdmi5iGUBBpY7fQVkEtNzk4Urfku1eKkYQrHdXyvJvvd4"
  }
                
             ];
        actualNftData = mfersNFTs.find(nft => nft.id.toString() === itemId);
                             
                           } else if (collectionId === 'goblintown') {
        const goblintownNFTs = [
            
  {
    "id": "goblintown-9997",
    "name": "goblintown.wtf #9997",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9997.png"
  },
  {
    "id": "goblintown-9998",
    "name": "goblintown.wtf #9998",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9998.png"
  },
  {
    "id": "goblintown-9996",
    "name": "goblintown.wtf #9996",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9996.png"
  },
  {
    "id": "goblintown-9995",
    "name": "goblintown.wtf #9995",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9995.png"
  },
  {
    "id": "goblintown-9994",
    "name": "goblintown.wtf #9994",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9994.png"
  },
  {
    "id": "goblintown-9993",
    "name": "goblintown.wtf #9993",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9993.png"
  },
  {
    "id": "goblintown-9992",
    "name": "goblintown.wtf #9992",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9992.png"
  },
  {
    "id": "goblintown-9991",
    "name": "goblintown.wtf #9991",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9991.png"
  },
  {
    "id": "goblintown-9990",
    "name": "goblintown.wtf #9990",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9990.png"
  },
  {
    "id": "goblintown-9986",
    "name": "goblintown.wtf #9986",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9986.png"
  },
  {
    "id": "goblintown-9989",
    "name": "goblintown.wtf #9989",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9989.png"
  },
  {
    "id": "goblintown-9988",
    "name": "goblintown.wtf #9988",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9988.png"
  },
  {
    "id": "goblintown-9985",
    "name": "goblintown.wtf #9985",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9985.png"
  },
  {
    "id": "goblintown-9987",
    "name": "goblintown.wtf #9987",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9987.png"
  },
  {
    "id": "goblintown-9983",
    "name": "goblintown.wtf #9983",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9983.png"
  },
  {
    "id": "goblintown-9984",
    "name": "goblintown.wtf #9984",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9984.png"
  },
  {
    "id": "goblintown-9982",
    "name": "goblintown.wtf #9982",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9982.png"
  },
  {
    "id": "goblintown-9980",
    "name": "goblintown.wtf #9980",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9980.png"
  },
  {
    "id": "goblintown-9981",
    "name": "goblintown.wtf #9981",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9981.png"
  },
  {
    "id": "goblintown-9978",
    "name": "goblintown.wtf #9978",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9978.png"
  },
  {
    "id": "goblintown-9979",
    "name": "goblintown.wtf #9979",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9979.png"
  },
  {
    "id": "goblintown-9977",
    "name": "goblintown.wtf #9977",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9977.png"
  },
  {
    "id": "goblintown-9976",
    "name": "goblintown.wtf #9976",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9976.png"
  },
  {
    "id": "goblintown-9973",
    "name": "goblintown.wtf #9973",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9973.png"
  },
  {
    "id": "goblintown-9975",
    "name": "goblintown.wtf #9975",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9975.png"
  },
  {
    "id": "goblintown-9972",
    "name": "goblintown.wtf #9972",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9972.png"
  },
  {
    "id": "goblintown-9970",
    "name": "goblintown.wtf #9970",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9970.png"
  },
  {
    "id": "goblintown-9974",
    "name": "goblintown.wtf #9974",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9974.png"
  },
  {
    "id": "goblintown-9971",
    "name": "goblintown.wtf #9971",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9971.png"
  },
  {
    "id": "goblintown-9969",
    "name": "goblintown.wtf #9969",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9969.png"
  },
  {
    "id": "goblintown-9968",
    "name": "goblintown.wtf #9968",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9968.png"
  },
  {
    "id": "goblintown-9967",
    "name": "goblintown.wtf #9967",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9967.png"
  },
  {
    "id": "goblintown-9966",
    "name": "goblintown.wtf #9966",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9966.png"
  },
  {
    "id": "goblintown-9964",
    "name": "goblintown.wtf #9964",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9964.png"
  },
  {
    "id": "goblintown-9965",
    "name": "goblintown.wtf #9965",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9965.png"
  },
  {
    "id": "goblintown-9962",
    "name": "goblintown.wtf #9962",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9962.png"
  },
  {
    "id": "goblintown-9961",
    "name": "goblintown.wtf #9961",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9961.png"
  },
  {
    "id": "goblintown-9959",
    "name": "goblintown.wtf #9959",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9959.png"
  },
  {
    "id": "goblintown-9960",
    "name": "goblintown.wtf #9960",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9960.png"
  },
  {
    "id": "goblintown-9958",
    "name": "goblintown.wtf #9958",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9958.png"
  },
  {
    "id": "goblintown-9957",
    "name": "goblintown.wtf #9957",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9957.png"
  },
  {
    "id": "goblintown-9956",
    "name": "goblintown.wtf #9956",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9956.png"
  },
  {
    "id": "goblintown-9953",
    "name": "goblintown.wtf #9953",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9953.png"
  },
  {
    "id": "goblintown-9954",
    "name": "goblintown.wtf #9954",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9954.png"
  },
  {
    "id": "goblintown-9955",
    "name": "goblintown.wtf #9955",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9955.png"
  },
  {
    "id": "goblintown-9952",
    "name": "goblintown.wtf #9952",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9952.png"
  },
  {
    "id": "goblintown-9951",
    "name": "goblintown.wtf #9951",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9951.png"
  },
  {
    "id": "goblintown-9950",
    "name": "goblintown.wtf #9950",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9950.png"
  },
  {
    "id": "goblintown-9949",
    "name": "goblintown.wtf #9949",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9949.png"
  },
  {
    "id": "goblintown-9946",
    "name": "goblintown.wtf #9946",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9946.png"
  },
  {
    "id": "goblintown-9948",
    "name": "goblintown.wtf #9948",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9948.png"
  },
  {
    "id": "goblintown-9947",
    "name": "goblintown.wtf #9947",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9947.png"
  },
  {
    "id": "goblintown-9945",
    "name": "goblintown.wtf #9945",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9945.png"
  },
  {
    "id": "goblintown-9944",
    "name": "goblintown.wtf #9944",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9944.png"
  },
  {
    "id": "goblintown-9943",
    "name": "goblintown.wtf #9943",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9943.png"
  },
  {
    "id": "goblintown-9941",
    "name": "goblintown.wtf #9941",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9941.png"
  },
  {
    "id": "goblintown-9942",
    "name": "goblintown.wtf #9942",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9942.png"
  },
  {
    "id": "goblintown-9940",
    "name": "goblintown.wtf #9940",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9940.png"
  },
  {
    "id": "goblintown-9939",
    "name": "goblintown.wtf #9939",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9939.png"
  },
  {
    "id": "goblintown-9938",
    "name": "goblintown.wtf #9938",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9938.png"
  },
  {
    "id": "goblintown-9937",
    "name": "goblintown.wtf #9937",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9937.png"
  },
  {
    "id": "goblintown-9936",
    "name": "goblintown.wtf #9936",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9936.png"
  },
  {
    "id": "goblintown-9935",
    "name": "goblintown.wtf #9935",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9935.png"
  },
  {
    "id": "goblintown-9933",
    "name": "goblintown.wtf #9933",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9933.png"
  },
  {
    "id": "goblintown-9934",
    "name": "goblintown.wtf #9934",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9934.png"
  },
  {
    "id": "goblintown-9932",
    "name": "goblintown.wtf #9932",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9932.png"
  },
  {
    "id": "goblintown-9931",
    "name": "goblintown.wtf #9931",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9931.png"
  },
  {
    "id": "goblintown-9930",
    "name": "goblintown.wtf #9930",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9930.png"
  },
  {
    "id": "goblintown-9929",
    "name": "goblintown.wtf #9929",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9929.png"
  },
  {
    "id": "goblintown-9927",
    "name": "goblintown.wtf #9927",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9927.png"
  },
  {
    "id": "goblintown-9926",
    "name": "goblintown.wtf #9926",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9926.png"
  },
  {
    "id": "goblintown-9928",
    "name": "goblintown.wtf #9928",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9928.png"
  },
  {
    "id": "goblintown-9924",
    "name": "goblintown.wtf #9924",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9924.png"
  },
  {
    "id": "goblintown-9923",
    "name": "goblintown.wtf #9923",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9923.png"
  },
  {
    "id": "goblintown-9925",
    "name": "goblintown.wtf #9925",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9925.png"
  },
  {
    "id": "goblintown-9922",
    "name": "goblintown.wtf #9922",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9922.png"
  },
  {
    "id": "goblintown-9921",
    "name": "goblintown.wtf #9921",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9921.png"
  },
  {
    "id": "goblintown-9920",
    "name": "goblintown.wtf #9920",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9920.png"
  },
  {
    "id": "goblintown-9919",
    "name": "goblintown.wtf #9919",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9919.png"
  },
  {
    "id": "goblintown-9918",
    "name": "goblintown.wtf #9918",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9918.png"
  },
  {
    "id": "goblintown-9917",
    "name": "goblintown.wtf #9917",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9917.png"
  },
  {
    "id": "goblintown-9916",
    "name": "goblintown.wtf #9916",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9916.png"
  },
  {
    "id": "goblintown-9915",
    "name": "goblintown.wtf #9915",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9915.png"
  },
  {
    "id": "goblintown-9914",
    "name": "goblintown.wtf #9914",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9914.png"
  },
  {
    "id": "goblintown-9913",
    "name": "goblintown.wtf #9913",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9913.png"
  },
  {
    "id": "goblintown-9911",
    "name": "goblintown.wtf #9911",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9911.png"
  },
  {
    "id": "goblintown-9912",
    "name": "goblintown.wtf #9912",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9912.png"
  },
  {
    "id": "goblintown-9908",
    "name": "goblintown.wtf #9908",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9908.png"
  },
  {
    "id": "goblintown-9910",
    "name": "goblintown.wtf #9910",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9910.png"
  },
  {
    "id": "goblintown-9909",
    "name": "goblintown.wtf #9909",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9909.png"
  },
  {
    "id": "goblintown-9907",
    "name": "goblintown.wtf #9907",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9907.png"
  },
  {
    "id": "goblintown-9906",
    "name": "goblintown.wtf #9906",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9906.png"
  },
  {
    "id": "goblintown-9902",
    "name": "goblintown.wtf #9902",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9902.png"
  },
  {
    "id": "goblintown-9903",
    "name": "goblintown.wtf #9903",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9903.png"
  },
  {
    "id": "goblintown-9905",
    "name": "goblintown.wtf #9905",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9905.png"
  },
  {
    "id": "goblintown-9901",
    "name": "goblintown.wtf #9901",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9901.png"
  },
  {
    "id": "goblintown-9904",
    "name": "goblintown.wtf #9904",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9904.png"
  },
  {
    "id": "goblintown-9899",
    "name": "goblintown.wtf #9899",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9899.png"
  },
  {
    "id": "goblintown-9900",
    "name": "goblintown.wtf #9900",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9900.png"
  },
  {
    "id": "goblintown-9898",
    "name": "goblintown.wtf #9898",
    "image": "https://ipfs.io/ipfs/QmSifFzarzzen5Vv4TWWhpN56VksqZrF3Bmuuc4gdGTEv1/9898.png"
  }
                           ];
        actualNftData = goblintownNFTs.find(nft => nft.id.toString() === itemId);
                           
                } else if (collectionId === 'checks-vv-originals') {
        const vvchecksNFTs = [
            
  {
    "id": "checks-14879",
    "name": "Checks #14879",
    "image": "https://raw.seadn.io/files/070f287ed84167b6ff0df8d86248ddea.svg"
  },
  {
    "id": "checks-5340",
    "name": "Checks #5340",
    "image": "https://raw.seadn.io/files/3b54dc257751f91510bcd1ced865c33c.svg"
  },
  {
    "id": "checks-11908",
    "name": "Checks #11908",
    "image": "https://raw.seadn.io/files/4f92382d554eeb09231fc02e50fffb61.svg"
  },
  {
    "id": "checks-13318",
    "name": "Checks #13318",
    "image": "https://raw.seadn.io/files/0371cafa6c270fc7cec3191d4156109a.svg"
  },
  {
    "id": "checks-15416",
    "name": "Checks #15416",
    "image": "https://raw.seadn.io/files/c9eec428cb67b4866a4ee41ba7b1ea38.svg"
  },
  {
    "id": "checks-9204",
    "name": "Checks #9204",
    "image": "https://raw.seadn.io/files/24bd5e67dba728afd50ddb086393f4df.svg"
  },
  {
    "id": "checks-3917",
    "name": "Checks #3917",
    "image": "https://raw.seadn.io/files/025d3bb2b4f2a9c3f573fbc9fac0c656.svg"
  },
  {
    "id": "checks-3190",
    "name": "Checks #3190",
    "image": "https://raw.seadn.io/files/5894331fef770a739c7d306e3f9d7f0a.svg"
  },
  {
    "id": "checks-5546",
    "name": "Checks #5546",
    "image": "https://raw.seadn.io/files/fc47bb282a871948e2dc2ea0cd72a4c7.svg"
  },
  {
    "id": "checks-11455",
    "name": "Checks #11455",
    "image": "https://raw.seadn.io/files/2f3cf49e7df63fc887938cc1259eeec8.svg"
  },
  {
    "id": "checks-2757",
    "name": "Checks #2757",
    "image": "https://raw.seadn.io/files/99e7a7db568d3dbb0bb15264719085be.svg"
  },
  {
    "id": "checks-14595",
    "name": "Checks #14595",
    "image": "https://raw.seadn.io/files/6b56f963b1ae16a2ce9a97efcd69c32c.svg"
  },
  {
    "id": "checks-3089",
    "name": "Checks #3089",
    "image": "https://raw.seadn.io/files/8a26394d65c7c7bbff777334b468cb3e.svg"
  },
  {
    "id": "checks-13233",
    "name": "Checks #13233",
    "image": "https://raw.seadn.io/files/c755c00b00297f505ecd1a2a5e52a373.svg"
  },
  {
    "id": "checks-8750",
    "name": "Checks #8750",
    "image": "https://raw.seadn.io/files/2a8636641c7e8aa6e3b336d0a7447b4e.svg"
  },
  {
    "id": "checks-7321",
    "name": "Checks #7321",
    "image": "https://raw.seadn.io/files/ecca300e54ce154b7e7103e5fdf8fe83.svg"
  },
  {
    "id": "checks-6587",
    "name": "Checks #6587",
    "image": "https://raw.seadn.io/files/4c9255ebc011ece86401c9b99255ad61.svg"
  },
  {
    "id": "checks-6120",
    "name": "Checks #6120",
    "image": "https://raw.seadn.io/files/51232eb01f2e9160a6cf6bd87664cee3.svg"
  },
  {
    "id": "checks-3990",
    "name": "Checks #3990",
    "image": "https://raw.seadn.io/files/b6b3740ce8d0f36364a842729bd5df33.svg"
  },
  {
    "id": "checks-3695",
    "name": "Checks #3695",
    "image": "https://raw.seadn.io/files/d77c1526fc1a8d05f65ed2b37749606f.svg"
  },
  {
    "id": "checks-3523",
    "name": "Checks #3523",
    "image": "https://raw.seadn.io/files/9b4af2bb1ebd488c8f6231b741804d65.svg"
  },
  {
    "id": "checks-2609",
    "name": "Checks #2609",
    "image": "https://raw.seadn.io/files/e4b48b6fd4a25d89d36889d01272d032.svg"
  },
  {
    "id": "checks-1184",
    "name": "Checks #1184",
    "image": "https://raw.seadn.io/files/a23469e203c184c4aca42e5962ab5f41.svg"
  },
  {
    "id": "checks-15945",
    "name": "Checks #15945",
    "image": "https://raw.seadn.io/files/7281c3ff34d0e34aa5b40a8c12a04d9b.svg"
  },
  {
    "id": "checks-15100",
    "name": "Checks #15100",
    "image": "https://raw.seadn.io/files/f251e99c4af79f4edb306384dcb30778.svg"
  },
  {
    "id": "checks-14288",
    "name": "Checks #14288",
    "image": "https://raw.seadn.io/files/724367caa1a0a824b51c12c904160961.svg"
  },
  {
    "id": "checks-13082",
    "name": "Checks #13082",
    "image": "https://raw.seadn.io/files/e312d1a6f37dae40d906c9dc68354ddb.svg"
  },
  {
    "id": "checks-10943",
    "name": "Checks #10943",
    "image": "https://raw.seadn.io/files/a8b59fd8594947d6e8cc9c2b4d99af1e.svg"
  },
  {
    "id": "checks-6331",
    "name": "Checks #6331",
    "image": "https://raw.seadn.io/files/9661cbd337fe4a38fc998d6b84896c29.svg"
  },
  {
    "id": "checks-2779",
    "name": "Checks #2779",
    "image": "https://raw.seadn.io/files/dd4f4384f8b86ba8cc6f19dc8d522011.svg"
  },
  {
    "id": "checks-8812",
    "name": "Checks #8812",
    "image": "https://raw.seadn.io/files/1250f27b797e746337589a6fd5db171f.svg"
  },
  {
    "id": "checks-4124",
    "name": "Checks #4124",
    "image": "https://raw.seadn.io/files/fee5f102b466f6d3042d2392e2c6fa4e.svg"
  },
  {
    "id": "checks-3680",
    "name": "Checks #3680",
    "image": "https://raw.seadn.io/files/b30557b4fd15f63f9c9dd1af7869164a.svg"
  },
  {
    "id": "checks-3169",
    "name": "Checks #3169",
    "image": "https://raw.seadn.io/files/8473721e2b61ef8db34d5e7c5dc24fe6.svg"
  },
  {
    "id": "checks-2576",
    "name": "Checks #2576",
    "image": "https://raw.seadn.io/files/8fed2963635b80df3a8a9edb4be2cb38.svg"
  },
  {
    "id": "checks-1583",
    "name": "Checks #1583",
    "image": "https://raw.seadn.io/files/c6f8e5830e02ba71a0d1314f0585b2d4.svg"
  },
  {
    "id": "checks-1955",
    "name": "Checks #1955",
    "image": "https://raw.seadn.io/files/13c64350d6f5b44df0ae9a877fc92381.svg"
  },
  {
    "id": "checks-8625",
    "name": "Checks #8625",
    "image": "https://raw.seadn.io/files/1490dae63af0ef0443415a71fe72472c.svg"
  },
  {
    "id": "checks-9623",
    "name": "Checks #9623",
    "image": "https://raw.seadn.io/files/21718685e7082c9b58177e07886b6242.svg"
  },
  {
    "id": "checks-11847",
    "name": "Checks #11847",
    "image": "https://raw.seadn.io/files/8676e8d7cc9d005753d57f95e817cb14.svg"
  },
  {
    "id": "checks-9154",
    "name": "Checks #9154",
    "image": "https://raw.seadn.io/files/ed83d09fda98db39c52a1c9afc177b81.svg"
  },
  {
    "id": "checks-6042",
    "name": "Checks #6042",
    "image": "https://raw.seadn.io/files/8ff09ca6baaf101e78cf44f6fc6f927b.svg"
  },
  {
    "id": "checks-3265",
    "name": "Checks #3265",
    "image": "https://raw.seadn.io/files/21f93f6b3a970d5448c33911630769dc.svg"
  },
  {
    "id": "checks-5679",
    "name": "Checks #5679",
    "image": "https://raw.seadn.io/files/8e00e9ded0983d16072a82de9a884977.svg"
  },
  {
    "id": "checks-11802",
    "name": "Checks #11802",
    "image": "https://raw.seadn.io/files/43cb1564278d7abec738dee4ffd60942.svg"
  },
  {
    "id": "checks-4160",
    "name": "Checks #4160",
    "image": "https://raw.seadn.io/files/5bca02fb29c72604d3441ca13b198c9b.svg"
  },
  {
    "id": "checks-9096",
    "name": "Checks #9096",
    "image": "https://raw.seadn.io/files/aae4744d595d96215d5281e09e35aa15.svg"
  },
  {
    "id": "checks-15726",
    "name": "Checks #15726",
    "image": "https://raw.seadn.io/files/07e9a722ac437d833bceccc8745e4253.svg"
  },
  {
    "id": "checks-3507",
    "name": "Checks #3507",
    "image": "https://raw.seadn.io/files/debfca53d25c24bce118a29b28ca5caf.svg"
  },
  {
    "id": "checks-14254",
    "name": "Checks #14254",
    "image": "https://raw.seadn.io/files/cabef87d66c8b80b34bbce61383a66d5.svg"
  },
  {
    "id": "checks-12706",
    "name": "Checks #12706",
    "image": "https://raw.seadn.io/files/a2e299e4c0fea9e794221fe038b54214.svg"
  },
  {
    "id": "checks-2194",
    "name": "Checks #2194",
    "image": "https://raw.seadn.io/files/a131d226bd07333832039204d7d20398.svg"
  },
  {
    "id": "checks-13655",
    "name": "Checks #13655",
    "image": "https://raw.seadn.io/files/4066e6d9107cfdc6640c1faa514b294b.svg"
  },
  {
    "id": "checks-5815",
    "name": "Checks #5815",
    "image": "https://raw.seadn.io/files/3a864c6efaf7768ebf0eabf5c06b6c45.svg"
  },
  {
    "id": "checks-9956",
    "name": "Checks #9956",
    "image": "https://raw.seadn.io/files/63a8550ff4a4c0036b3100097d2375af.svg"
  },
  {
    "id": "checks-5297",
    "name": "Checks #5297",
    "image": "https://raw.seadn.io/files/2121ad2dd8ee60f08083af140f6e7b53.svg"
  },
  {
    "id": "checks-13426",
    "name": "Checks #13426",
    "image": "https://raw.seadn.io/files/4724615c3d0bc01ec2fefa6b8aa5ad90.svg"
  },
  {
    "id": "checks-4381",
    "name": "Checks #4381",
    "image": "https://raw.seadn.io/files/cd2cccd037fcd416be130b66fad14f0f.svg"
  },
  {
    "id": "checks-2103",
    "name": "Checks #2103",
    "image": "https://raw.seadn.io/files/000554238ddb909c0c1e40cee431e064.svg"
  },
  {
    "id": "checks-14093",
    "name": "Checks #14093",
    "image": "https://raw.seadn.io/files/37269ce1c62173e63964a1c7d3fbff65.svg"
  },
  {
    "id": "checks-15233",
    "name": "Checks #15233",
    "image": "https://raw.seadn.io/files/4291cbef968fb3a4338bea30e9a7d5d1.svg"
  },
  {
    "id": "checks-4306",
    "name": "Checks #4306",
    "image": "https://raw.seadn.io/files/fea7d734474734d59a608765f7aa6901.svg"
  },
  {
    "id": "checks-12936",
    "name": "Checks #12936",
    "image": "https://raw.seadn.io/files/667ab36a4cea7e589a665da5c35750c4.svg"
  },
  {
    "id": "checks-12312",
    "name": "Checks #12312",
    "image": "https://raw.seadn.io/files/68b686205aaf4162d54e4e6287e46e9d.svg"
  },
  {
    "id": "checks-13928",
    "name": "Checks #13928",
    "image": "https://raw.seadn.io/files/cb9761148b51930ae2348d234df6026d.svg"
  },
  {
    "id": "checks-14966",
    "name": "Checks #14966",
    "image": "https://raw.seadn.io/files/55b4ba69ac844617fa817b84f2117666.svg"
  },
  {
    "id": "checks-3894",
    "name": "Checks #3894",
    "image": "https://raw.seadn.io/files/d737fe5adc36dbfdd93c0fc21a271b29.svg"
  },
  {
    "id": "checks-10129",
    "name": "Checks #10129",
    "image": "https://raw.seadn.io/files/7fbc71f4384ad41705b843ea80d6d876.svg"
  },
  {
    "id": "checks-14721",
    "name": "Checks #14721",
    "image": "https://raw.seadn.io/files/9bdf0445987c9944472f0ff79829d249.svg"
  },
  {
    "id": "checks-14467",
    "name": "Checks #14467",
    "image": "https://raw.seadn.io/files/6bca887139809848e192828f6b7d7f19.svg"
  },
  {
    "id": "checks-6061",
    "name": "Checks #6061",
    "image": "https://raw.seadn.io/files/5fde3b681cfb250d0ee728539369d278.svg"
  },
  {
    "id": "checks-14258",
    "name": "Checks #14258",
    "image": "https://raw.seadn.io/files/2ab1a307f762f0c1660076a7d48fecf7.svg"
  },
  {
    "id": "checks-1508",
    "name": "Checks #1508",
    "image": "https://raw.seadn.io/files/60e3ca7961a6e7ce2d21880a0462936b.svg"
  },
  {
    "id": "checks-11931",
    "name": "Checks #11931",
    "image": "https://raw.seadn.io/files/844177e3f005bc12fb1fece599b5fc3e.svg"
  },
  {
    "id": "checks-7537",
    "name": "Checks #7537",
    "image": "https://raw.seadn.io/files/b8b24427d7a9358a00245fe2b56e65bc.svg"
  },
  {
    "id": "checks-10874",
    "name": "Checks #10874",
    "image": "https://raw.seadn.io/files/80b2ab7a8e90d72b6e7baa990ae26b6b.svg"
  },
  {
    "id": "checks-12630",
    "name": "Checks #12630",
    "image": "https://raw.seadn.io/files/4ce339a470f43db9e1e2530a6a3a823f.svg"
  },
  {
    "id": "checks-14480",
    "name": "Checks #14480",
    "image": "https://raw.seadn.io/files/f32c7f83625304ec2301256df1bbf6c7.svg"
  },
  {
    "id": "checks-12358",
    "name": "Checks #12358",
    "image": "https://raw.seadn.io/files/f1d6194328f314985598ba6364f097fa.svg"
  },
  {
    "id": "checks-1216",
    "name": "Checks #1216",
    "image": "https://raw.seadn.io/files/c672f01f6cba7a704278ffc91ac7365e.svg"
  },
  {
    "id": "checks-10175",
    "name": "Checks #10175",
    "image": "https://raw.seadn.io/files/0ca297e2bffe44b9f1fdef973fa8be3d.svg"
  },
  {
    "id": "checks-15241",
    "name": "Checks #15241",
    "image": "https://raw.seadn.io/files/eacbbbce10f29e299210bd59928ecef4.svg"
  },
  {
    "id": "checks-735",
    "name": "Checks #735",
    "image": "https://raw.seadn.io/files/d48dd5ec6b4b79eca8e6b06bc9c62629.svg"
  },
  {
    "id": "checks-8139",
    "name": "Checks #8139",
    "image": "https://raw.seadn.io/files/17efac417b7bd5afa49b33bbdc1f20e5.svg"
  },
  {
    "id": "checks-14470",
    "name": "Checks #14470",
    "image": "https://raw.seadn.io/files/7f3766b1682a83a12ac6763b17649253.svg"
  },
  {
    "id": "checks-15741",
    "name": "Checks #15741",
    "image": "https://raw.seadn.io/files/d755048e478deade748a0927c08ddd19.svg"
  },
  {
    "id": "checks-3854",
    "name": "Checks #3854",
    "image": "https://raw.seadn.io/files/bb55d10d5a49096c995e1fe57a1cd0a9.svg"
  },
  {
    "id": "checks-3848",
    "name": "Checks #3848",
    "image": "https://raw.seadn.io/files/dcb0601340e37ff39ff893f95269f6f2.svg"
  },
  {
    "id": "checks-12859",
    "name": "Checks #12859",
    "image": "https://raw.seadn.io/files/8f703f7f33a4729fc49ed8e45b0d14e8.svg"
  },
  {
    "id": "checks-2814",
    "name": "Checks #2814",
    "image": "https://raw.seadn.io/files/20a2de11ed98a1737b8483cd65e131dc.svg"
  },
  {
    "id": "checks-11893",
    "name": "Checks #11893",
    "image": "https://raw.seadn.io/files/833956b90993be26178ed8b715d151ed.svg"
  },
  {
    "id": "checks-11620",
    "name": "Checks #11620",
    "image": "https://raw.seadn.io/files/fdf95f26f487e7e425fcf0f2e11b8107.svg"
  },
  {
    "id": "checks-9225",
    "name": "Checks #9225",
    "image": "https://raw.seadn.io/files/888d0f37fc7ed80480ca123f5670cef0.svg"
  },
  {
    "id": "checks-13126",
    "name": "Checks #13126",
    "image": "https://raw.seadn.io/files/295e6d6af537fc43c2e5c6b3ef621e86.svg"
  },
  {
    "id": "checks-15537",
    "name": "Checks #15537",
    "image": "https://raw.seadn.io/files/1d5d47d3a57788f8c0002986efdfb9ef.svg"
  },
  {
    "id": "checks-5404",
    "name": "Checks #5404",
    "image": "https://raw.seadn.io/files/30da74462b7e10e71a69b8794e1c00b0.svg"
  },
  {
    "id": "checks-9569",
    "name": "Checks #9569",
    "image": "https://raw.seadn.io/files/ac19e350f005a94881831a66f483af96.svg"
  },
  {
    "id": "checks-1994",
    "name": "Checks #1994",
    "image": "https://raw.seadn.io/files/6da0e0b1db9294f186451a0d0468e348.svg"
  },
  {
    "id": "checks-15647",
    "name": "Checks #15647",
    "image": "https://raw.seadn.io/files/c0222a46509be4fb9e06bb140381c2d1.svg"
  },
  {
    "id": "checks-12128",
    "name": "Checks #12128",
    "image": "https://raw.seadn.io/files/3eb929348254d5d255031da6156d2999.svg"
  }
            
              ];
        actualNftData = vvchecksNFTs.find(nft => nft.id.toString() === itemId);
                
                     } else if (collectionId === 'snackgang') {
        const snackgangNFTs = [
            
  {
    "id": "snackgang-5555",
    "name": "SNACKGANG #5555",
    "image": "https://ipfs.io/ipfs/QmUYr9EGux3jDR7VTkELdsLgj5tzqXRXxAM1GtVf3wg1ra"
  },
  {
    "id": "snackgang-5551",
    "name": "SNACKGANG #5551",
    "image": "https://ipfs.io/ipfs/QmahEyBPsXGmZEnAKSAjks7QJzYMNYY27wrejJ2ofoshxn"
  },
  {
    "id": "snackgang-5549",
    "name": "SNACKGANG #5549",
    "image": "https://ipfs.io/ipfs/QmbPTnSts2xtzJsFzxG2BWS7WTzx5NcDn7x5SkdBnkgxBm"
  },
  {
    "id": "snackgang-5553",
    "name": "SNACKGANG #5553",
    "image": "https://ipfs.io/ipfs/QmNUq8gRqeVa3JjSRMEBDRprUgfzf2eAn8GfPoNcamSwng"
  },
  {
    "id": "snackgang-5547",
    "name": "SNACKGANG #5547",
    "image": "https://ipfs.io/ipfs/QmPxunYpm6v3DuPc2HUkvYSrDEpKoeoiCCSduGasrpoFqG"
  },
  {
    "id": "snackgang-5552",
    "name": "SNACKGANG #5552",
    "image": "https://ipfs.io/ipfs/QmRH9SXnkHbeB1z5Ftatt55puCSjjgjmauRpYD39RNW9BN"
  },
  {
    "id": "snackgang-5548",
    "name": "SNACKGANG #5548",
    "image": "https://ipfs.io/ipfs/Qmda18nQeS3wEo4cvR2Pk94vt8G6rpRWPsn5WeSG7dA8ss"
  },
  {
    "id": "snackgang-5554",
    "name": "SNACKGANG #5554",
    "image": "https://ipfs.io/ipfs/Qmf377cP1MadBdH7NjvyHwT4YiNaUNvfpJSugjmQ1KMtZv"
  },
  {
    "id": "snackgang-5550",
    "name": "SNACKGANG #5550",
    "image": "https://ipfs.io/ipfs/QmboS5MraFZJwrrMNEe7rdQs6tzFYat3mB7TftsF9TjBYP"
  },
  {
    "id": "snackgang-5544",
    "name": "SNACKGANG #5544",
    "image": "https://ipfs.io/ipfs/QmWqiysQRDnhvzGTN2BwiHLej4G3cX7dLzuAngqFrFswV8"
  },
  {
    "id": "snackgang-5545",
    "name": "SNACKGANG #5545",
    "image": "https://ipfs.io/ipfs/QmXmYd2QiEoFeHKMNbGfxRdgcY3B1LhRDenMowQtUodJd3"
  },
  {
    "id": "snackgang-5541",
    "name": "SNACKGANG #5541",
    "image": "https://ipfs.io/ipfs/QmSMyCXhJMty2dQLrK2wgvAr2XgJe5QLNNsVRLefVX9ohM"
  },
  {
    "id": "snackgang-5546",
    "name": "SNACKGANG #5546",
    "image": "https://ipfs.io/ipfs/QmPTDinANWP2cuifpVeMyfE1Fp4Dbt64mmG7AKimpRUw3L"
  },
  {
    "id": "snackgang-5538",
    "name": "SNACKGANG #5538",
    "image": "https://ipfs.io/ipfs/QmdGUFHhDnL2cJnNG7iVQqTWYLmxHPRTGc9D3o8iouzENe"
  },
  {
    "id": "snackgang-5540",
    "name": "SNACKGANG #5540",
    "image": "https://ipfs.io/ipfs/QmPAsn9EoQTCJMr4AJmVqcQSFinQDR7msD6RsQegxixXnV"
  },
  {
    "id": "snackgang-5542",
    "name": "SNACKGANG #5542",
    "image": "https://ipfs.io/ipfs/QmWPNVqjGR1X6ymN3qgnCuP6BWbWeB7SkNQVtYL7iPnjgP"
  },
  {
    "id": "snackgang-5533",
    "name": "SNACKGANG #5533",
    "image": "https://ipfs.io/ipfs/QmVMHrJpNjSyFkQh1igWwLpz7nCsD2RLDk92iSUdsjvi1u"
  },
  {
    "id": "snackgang-5539",
    "name": "SNACKGANG #5539",
    "image": "https://ipfs.io/ipfs/QmXcgKSrw2PSZASBA8yU3gPcHVUFEgTge2NSR2cWUaWxxD"
  },
  {
    "id": "snackgang-5536",
    "name": "SNACKGANG #5536",
    "image": "https://ipfs.io/ipfs/QmQXAjnJo4Hmhr7NJuLu9LJyeHzQZeNx5whJAyUSkSZP8s"
  },
  {
    "id": "snackgang-5537",
    "name": "SNACKGANG #5537",
    "image": "https://ipfs.io/ipfs/QmZeGdbcP7ZdqaW3x5mnzVs8a4HmtcAfZbA6n2nZQHt7BX"
  },
  {
    "id": "snackgang-5530",
    "name": "SNACKGANG #5530",
    "image": "https://ipfs.io/ipfs/QmPCXF3a9vEZyCRtUSrSExmrbbGc2WjbednAWXFEwogp2g"
  },
  {
    "id": "snackgang-5535",
    "name": "SNACKGANG #5535",
    "image": "https://ipfs.io/ipfs/QmY1M22dvA6fbngXcodEHpLbfE7CAWmBThyWHerVhEr17w"
  },
  {
    "id": "snackgang-5531",
    "name": "SNACKGANG #5531",
    "image": "https://ipfs.io/ipfs/QmTxvutzJ3iy9aKfNPc3zZVBe7YHjTZn5TxZu3oz8siqAz"
  },
  {
    "id": "snackgang-5534",
    "name": "SNACKGANG #5534",
    "image": "https://ipfs.io/ipfs/QmNPRDADGjeEyi1e6gpDP6rGM6WcxzE5ibHwP2tJZ4p7va"
  },
  {
    "id": "snackgang-5543",
    "name": "SNACKGANG #5543",
    "image": "https://ipfs.io/ipfs/Qmct2xf5dqGp7SoiYgmsumEzPxZ2nYa3MTkEYqkCvVaZNT"
  },
  {
    "id": "snackgang-5532",
    "name": "SNACKGANG #5532",
    "image": "https://ipfs.io/ipfs/QmYXpqh3MAp2YJsPnBtkirZPjj4oX3svn3npaJ7STya4mZ"
  },
  {
    "id": "snackgang-5529",
    "name": "SNACKGANG #5529",
    "image": "https://ipfs.io/ipfs/QmS1boWsFfDeN7ownP9Pe7eaT5YdNub8odoftE7TLHeT3y"
  },
  {
    "id": "snackgang-5528",
    "name": "SNACKGANG #5528",
    "image": "https://ipfs.io/ipfs/Qmb31eQquDqskpFKHvaQLQaYT3C16uJadMwAEPio9N7o9M"
  },
  {
    "id": "snackgang-5521",
    "name": "SNACKGANG #5521",
    "image": "https://ipfs.io/ipfs/QmYwiYKmFWDcW5qnFPPa2my7bY61fSxfNugZruNj6gQfWw"
  },
  {
    "id": "snackgang-5523",
    "name": "SNACKGANG #5523",
    "image": "https://ipfs.io/ipfs/QmU1J62GDuGFTtWk7PAXkG5LKT9Dadk2TDoboNCcvsRnwe"
  },
  {
    "id": "snackgang-5525",
    "name": "SNACKGANG #5525",
    "image": "https://ipfs.io/ipfs/QmeG38NYXdEVpc1HvUyUjzgLZvGGmkx61ogdvF8tzZWnHQ"
  },
  {
    "id": "snackgang-5522",
    "name": "SNACKGANG #5522",
    "image": "https://ipfs.io/ipfs/QmNxynwzAYeJopPnuLWKLVfZuxhcR4UCHty2unG3jsF9rC"
  },
  {
    "id": "snackgang-5519",
    "name": "SNACKGANG #5519",
    "image": "https://ipfs.io/ipfs/QmZVGHjA1CTwjdQj7kC6z4jPWEvQrVDqnjDQtShG9x7szP"
  },
  {
    "id": "snackgang-5517",
    "name": "SNACKGANG #5517",
    "image": "https://ipfs.io/ipfs/QmZfqpP8n97HmfKLyYhbZAW7W29npLHfgoD2oxocFRb39t"
  },
  {
    "id": "snackgang-5514",
    "name": "SNACKGANG #5514",
    "image": "https://ipfs.io/ipfs/QmWBx7efdMke4zqryp4oTzy41nPux59nKgexZSZA4XWK9L"
  },
  {
    "id": "snackgang-5516",
    "name": "SNACKGANG #5516",
    "image": "https://ipfs.io/ipfs/QmXeeZpM9L11tderxANSBC1G64T5zDHs968yS9Ybfx5Nut"
  },
  {
    "id": "snackgang-5515",
    "name": "SNACKGANG #5515",
    "image": "https://ipfs.io/ipfs/QmRL9nFKTE5TF7fTEyh78SW8VRGa5Jy9cvRRDf5TdEsJ3Z"
  },
  {
    "id": "snackgang-5513",
    "name": "SNACKGANG #5513",
    "image": "https://ipfs.io/ipfs/QmUiw5oW3kJ6vrsMRbxWes4SMgiBY3hmxw5wb7DhAuniBq"
  },
  {
    "id": "snackgang-5518",
    "name": "SNACKGANG #5518",
    "image": "https://ipfs.io/ipfs/Qmeii7zxmFBMTsjHZHHABLPLvqf8C9GHe8pGj3uDYHwWbT"
  },
  {
    "id": "snackgang-5527",
    "name": "SNACKGANG #5527",
    "image": "https://ipfs.io/ipfs/QmQqypuuJtjknKn3vqEj6KqHdaJYRMwNq3on9mcgqTDcLc"
  },
  {
    "id": "snackgang-5520",
    "name": "SNACKGANG #5520",
    "image": "https://ipfs.io/ipfs/QmPSNhz4V5uCAKcPjXSu1cbLdrrxPfJUCQCqzfEHpm2w68"
  },
  {
    "id": "snackgang-5526",
    "name": "SNACKGANG #5526",
    "image": "https://ipfs.io/ipfs/QmPuMbuUWN4kgXTkcLWruN4sDLMK6pJxY1L4HtNMHEzTBx"
  },
  {
    "id": "snackgang-5524",
    "name": "SNACKGANG #5524",
    "image": "https://ipfs.io/ipfs/Qmdbiuc53wCCCPCTzgvR8z7pey5Lvm3b9nkCxm8ikaAqps"
  },
  {
    "id": "snackgang-5512",
    "name": "SNACKGANG #5512",
    "image": "https://ipfs.io/ipfs/QmXhUijU4AWK4PwWSLD27iShANsgD8oyKM6duaR4xYS3Zr"
  },
  {
    "id": "snackgang-5511",
    "name": "SNACKGANG #5511",
    "image": "https://ipfs.io/ipfs/QmWfVfBFYFF2AQEnPX8vB6FFKZLnLer9XGEfkQjFCCHrBE"
  },
  {
    "id": "snackgang-5509",
    "name": "SNACKGANG #5509",
    "image": "https://ipfs.io/ipfs/QmaR6WRt3bq8wua89WoQbVfDt6nE8gDi42KPaGCweUBzxf"
  },
  {
    "id": "snackgang-5507",
    "name": "SNACKGANG #5507",
    "image": "https://ipfs.io/ipfs/QmdGYADrsRrHuHKLyY6rHPvT9VECYUDhA2o8fTPoGuNmbQ"
  },
  {
    "id": "snackgang-5508",
    "name": "SNACKGANG #5508",
    "image": "https://ipfs.io/ipfs/QmdkssjWc645w3ocZvZnDkLpBEHuqqHTrNN95bL41r1ZHi"
  },
  {
    "id": "snackgang-5505",
    "name": "SNACKGANG #5505",
    "image": "https://ipfs.io/ipfs/QmTsPAJsCDi3Y4LtLyhAHWnc4y6cmPwH7rUa7Lee4QYzKr"
  },
  {
    "id": "snackgang-5503",
    "name": "SNACKGANG #5503",
    "image": "https://ipfs.io/ipfs/QmbAnCxYZEjsc1mgMcDpGNbf3dMqCLzWGiS2CaBYtqweta"
  },
  {
    "id": "snackgang-5502",
    "name": "SNACKGANG #5502",
    "image": "https://ipfs.io/ipfs/QmbYbC6r7gpzWY7p6tYxayfBsR2vLKNWJR8jNoPBS9uXkR"
  },
  {
    "id": "snackgang-5506",
    "name": "SNACKGANG #5506",
    "image": "https://ipfs.io/ipfs/QmXk44pFx1tdK7c7ZdbNVx1CS6fo5EaVwt2PUCYi2FdUrB"
  },
  {
    "id": "snackgang-5504",
    "name": "SNACKGANG #5504",
    "image": "https://ipfs.io/ipfs/QmXAGyPsgNJXmkUmDeQd5gYyVvMMMsrCc1dQfb5fUtJjHV"
  },
  {
    "id": "snackgang-5500",
    "name": "SNACKGANG #5500",
    "image": "https://ipfs.io/ipfs/QmdxwsSFy4zMfCSwPJ2GLFnm5kRzdMncLXLQEtCzdzaRqV"
  },
  {
    "id": "snackgang-5510",
    "name": "SNACKGANG #5510",
    "image": "https://ipfs.io/ipfs/QmZcGvUkGJeed9LL6dC7EYAbByAjPDo2PJYPoykVY6hht6"
  },
  {
    "id": "snackgang-5501",
    "name": "SNACKGANG #5501",
    "image": "https://ipfs.io/ipfs/QmXt6AysYeKLwVYEFiUgzRKAesgLebiJZSKLrriW8zTgB9"
  },
  {
    "id": "snackgang-5494",
    "name": "SNACKGANG #5494",
    "image": "https://ipfs.io/ipfs/QmQUYCSXbRExpZnKTwwkGshwgeR2pH4K6FxX4B21Hs6cTE"
  },
  {
    "id": "snackgang-5499",
    "name": "SNACKGANG #5499",
    "image": "https://ipfs.io/ipfs/QmcctLAXewhmRgE1MGNYetFv9pJrfuXDVZe2rKdBUzbFks"
  },
  {
    "id": "snackgang-5497",
    "name": "SNACKGANG #5497",
    "image": "https://ipfs.io/ipfs/QmX3DfmgBdWL2eRCXiMe2Mgd4iRLk54r4Xt9j8w8psKeaz"
  },
  {
    "id": "snackgang-5496",
    "name": "SNACKGANG #5496",
    "image": "https://ipfs.io/ipfs/QmQV16RZ132kit9EEJMBNTBuic7TnksxxxbLMmm5uRLYmH"
  },
  {
    "id": "snackgang-5491",
    "name": "SNACKGANG #5491",
    "image": "https://ipfs.io/ipfs/QmbSqVCbJS7B5JXpkZ2WZsPScwk5hhJ9mrAotqCEGonNPN"
  },
  {
    "id": "snackgang-5493",
    "name": "SNACKGANG #5493",
    "image": "https://ipfs.io/ipfs/QmbCH9cuoXdNBC8AwWhncevJ39ic6FATbPFjtj3nxRDfmb"
  },
  {
    "id": "snackgang-5495",
    "name": "SNACKGANG #5495",
    "image": "https://ipfs.io/ipfs/Qmch4Fudn8tDtfXPRXzErhxCVLRMqo7a4K2cGQNBSjP8FV"
  },
  {
    "id": "snackgang-5490",
    "name": "SNACKGANG #5490",
    "image": "https://ipfs.io/ipfs/QmZk5ERXioupzjrDv38NJ9zGbBpoQfGTGssjLjgknxyXev"
  },
  {
    "id": "snackgang-5489",
    "name": "SNACKGANG #5489",
    "image": "https://ipfs.io/ipfs/QmbxyH32UDYCtswKFXdooiXvN33gmwsYptRoJynQoZNyKf"
  },
  {
    "id": "snackgang-5488",
    "name": "SNACKGANG #5488",
    "image": "https://ipfs.io/ipfs/QmTogNfZFziViKV4yP3sDpf9thxDSo8GRFTAPGHARoSL7s"
  },
  {
    "id": "snackgang-5484",
    "name": "SNACKGANG #5484",
    "image": "https://ipfs.io/ipfs/QmUM87scGhwoXcPt1trYCV83tVLjy6Ln8uB9yjr8B1y3rk"
  },
  {
    "id": "snackgang-5486",
    "name": "SNACKGANG #5486",
    "image": "https://ipfs.io/ipfs/QmXsmk7NznqB3nLpBCxJyAw9rv3jK7QPLQm4eZbvGqRHuX"
  },
  {
    "id": "snackgang-5485",
    "name": "SNACKGANG #5485",
    "image": "https://ipfs.io/ipfs/QmVF7Evebft6sXrYgm1WXiC5rMGJXFyxsbQy8q86wvaADP"
  },
  {
    "id": "snackgang-5498",
    "name": "SNACKGANG #5498",
    "image": "https://ipfs.io/ipfs/Qmcuxv8uZNKwq42DMRkSznRTddVGSMgS3mj31qw9f7QsK2"
  },
  {
    "id": "snackgang-5492",
    "name": "SNACKGANG #5492",
    "image": "https://ipfs.io/ipfs/QmXn5pbNDHTP824Go28ztKm7geuwuxNSJCX33Df7NoRxwR"
  },
  {
    "id": "snackgang-5487",
    "name": "SNACKGANG #5487",
    "image": "https://ipfs.io/ipfs/QmVEcNvY12Ja5Fs3v69EgBRBJfkxVnh6P9hTRWSGghtdHn"
  },
  {
    "id": "snackgang-5483",
    "name": "SNACKGANG #5483",
    "image": "https://ipfs.io/ipfs/QmPrwTi8uKuKzmkCq3ugamWGtHy2SUMSKwSPBzNY6fDyna"
  },
  {
    "id": "snackgang-5481",
    "name": "SNACKGANG #5481",
    "image": "https://ipfs.io/ipfs/QmPvraZnMrjXNtUpUbutpfCyWBREoov6egmzVxQqZzH9xE"
  },
  {
    "id": "snackgang-5482",
    "name": "SNACKGANG #5482",
    "image": "https://ipfs.io/ipfs/QmXKQRkXdTYQgQHzSyqJSPbhJRYzkoBgZRFBcDx6DCQd5o"
  },
  {
    "id": "snackgang-5478",
    "name": "SNACKGANG #5478",
    "image": "https://ipfs.io/ipfs/Qmb4rCgQvmxAP1DCAyK4jib42Ls7QiFoFRfVF7aGku8wTg"
  },
  {
    "id": "snackgang-5476",
    "name": "SNACKGANG #5476",
    "image": "https://ipfs.io/ipfs/QmY8DYV99Ahc2wU6Hj8CTg1Cq16XsB8acpFg35XuH8EQCx"
  },
  {
    "id": "snackgang-5474",
    "name": "SNACKGANG #5474",
    "image": "https://ipfs.io/ipfs/QmdmX4J5aYh1hVo3PEVPRhAbe946m3AJ7kKKZhXjnbHtXi"
  },
  {
    "id": "snackgang-5475",
    "name": "SNACKGANG #5475",
    "image": "https://ipfs.io/ipfs/QmZikSyjDxspAMLxQ9qZeezPfNEZq3tCY31UQmjGZhKCA9"
  },
  {
    "id": "snackgang-5472",
    "name": "SNACKGANG #5472",
    "image": "https://ipfs.io/ipfs/QmaxwR9R6ZwNSuv1om9UHcepgEkugkQ5wphVCPxrsLo5QB"
  },
  {
    "id": "snackgang-5473",
    "name": "SNACKGANG #5473",
    "image": "https://ipfs.io/ipfs/Qme6ysr7dE5Yfayz4SP5p6pB2ttWZfK46ccB22UhibLbPd"
  },
  {
    "id": "snackgang-5466",
    "name": "SNACKGANG #5466",
    "image": "https://ipfs.io/ipfs/QmcVgV4CQA4KZLS2dvjrb87eeioGquwwYR3FxHLtaVqc1B"
  },
  {
    "id": "snackgang-5471",
    "name": "SNACKGANG #5471",
    "image": "https://ipfs.io/ipfs/QmT9jR3gzfZoCUv3DyWyLGGsgdgdPtNpkVbcxQDFv9vVw6"
  },
  {
    "id": "snackgang-5467",
    "name": "SNACKGANG #5467",
    "image": "https://ipfs.io/ipfs/QmRFqPpXSzgLqdPT2yEnoKX2wsYRRi4ckw8oTYnyeFo1T7"
  },
  {
    "id": "snackgang-5469",
    "name": "SNACKGANG #5469",
    "image": "https://ipfs.io/ipfs/QmRiAMKU86CpvujXvW3nFynSXvY37wsB4fGUVgHcXLuQYj"
  },
  {
    "id": "snackgang-5468",
    "name": "SNACKGANG #5468",
    "image": "https://ipfs.io/ipfs/QmYArNKCj1CWxQv9XFANjroc9MxoqLD1ZbKnpg7Eywh6t7"
  },
  {
    "id": "snackgang-5477",
    "name": "SNACKGANG #5477",
    "image": "https://ipfs.io/ipfs/QmZerahtZbN1eURwWskUBD76RfAJLziy6vshp6mE9rTb4v"
  },
  {
    "id": "snackgang-5479",
    "name": "SNACKGANG #5479",
    "image": "https://ipfs.io/ipfs/QmXkryu1bRRddUdb4AkjqCoM2GXKNkxem73B8sYXGYdtJR"
  },
  {
    "id": "snackgang-5480",
    "name": "SNACKGANG #5480",
    "image": "https://ipfs.io/ipfs/QmcLpQL5psdp9gPuQm2MDy6C7xiTVD9iE1RANGNTXXadkn"
  },
  {
    "id": "snackgang-5470",
    "name": "SNACKGANG #5470",
    "image": "https://ipfs.io/ipfs/QmVgfo6VoA4jzqgZxLtKEu39xusmzomrxqzQtcMoQTgJ7g"
  },
  {
    "id": "snackgang-5463",
    "name": "SNACKGANG #5463",
    "image": "https://ipfs.io/ipfs/QmPshvPNRDB1GoLDcUGqBSy5GKoMGkizZvWYe3P4ZF7baj"
  },
  {
    "id": "snackgang-5462",
    "name": "SNACKGANG #5462",
    "image": "https://ipfs.io/ipfs/Qma2f4RxoL9AS3GvTBYKjJLjeSKMw6ehQwPjjg234yGzpm"
  },
  {
    "id": "snackgang-5460",
    "name": "SNACKGANG #5460",
    "image": "https://ipfs.io/ipfs/QmNWYGRCe3TeZnox9XmDij2qytCovtheyzpcNEisyL4bzE"
  },
  {
    "id": "snackgang-5465",
    "name": "SNACKGANG #5465",
    "image": "https://ipfs.io/ipfs/Qmd2Uu3R6zw8DkfVNn98qRjkgBK97YpQFwmRQydujHg9Qg"
  },
  {
    "id": "snackgang-5461",
    "name": "SNACKGANG #5461",
    "image": "https://ipfs.io/ipfs/QmRhJMZC1NWrRbfCmY8z6bYU35mrrr8gr6EhsA29SsQd3t"
  },
  {
    "id": "snackgang-5459",
    "name": "SNACKGANG #5459",
    "image": "https://ipfs.io/ipfs/QmXfuk5QtnQbqfUShhTjxMRQ9iAqFUnEHPHfCBWnd9H4BF"
  },
  {
    "id": "snackgang-5459",
    "name": "SNACKGANG #5459",
    "image": "https://ipfs.io/ipfs/QmXfuk5QtnQbqfUShhTjxMRQ9iAqFUnEHPHfCBWnd9H4BF"
  },
  {
    "id": "snackgang-5452",
    "name": "SNACKGANG #5452",
    "image": "https://ipfs.io/ipfs/QmPSU2tjC454Xtyw6AE7BbCPoY47cjfysYrc7T65Lxs2oM"
  },
  {
    "id": "snackgang-5456",
    "name": "SNACKGANG #5456",
    "image": "https://ipfs.io/ipfs/QmdDzQowtF7idvSgTu2yQzkLiX43By84imfKxpAX6xQ4PH"
  },
  {
    "id": "snackgang-5453",
    "name": "SNACKGANG #5453",
    "image": "https://ipfs.io/ipfs/Qmdjg3p1XNwY34njNoRFze8Ub9sB6rYVuhoRoMFYyPbo4h"
  },
  {
    "id": "snackgang-5451",
    "name": "SNACKGANG #5451",
    "image": "https://ipfs.io/ipfs/QmYMnpYa7wgifXT5hmU2iXZ2sCywxKNH4zAhw6wwG7P8du"
  }

             ];
        actualNftData = snackgangNFTs.find(nft => nft.id.toString() === itemId);
                     
                          } else if (collectionId === 'metawinners') {
        const metawinnersNFTs = [
        
  {
    "id": "metawinners-9998",
    "name": "MetaWinners #9998",
    "image": "https://ipfs.io/ipfs/QmZMBuZdouumMeATWHNFbeFB6Ko3UqLxL9CyY8DoDqmRag"
  },
  {
    "id": "metawinners-10000",
    "name": "MetaWinners #10000",
    "image": "https://ipfs.io/ipfs/QmfZp1aNs967RhTfqZnQBaiETnrJhQEcBfv99bUtsRHz1C"
  },
  {
    "id": "metawinners-9999",
    "name": "MetaWinners #9999",
    "image": "https://ipfs.io/ipfs/QmPHnoKfXpKquzqsS6qAHQw83itvrkH33ugJRW7gEuSH9x"
  },
  {
    "id": "metawinners-9997",
    "name": "MetaWinners #9997",
    "image": "https://ipfs.io/ipfs/QmZCLG6bWsRhLowqN1zLFUyTAyVLcRQ7P1PUHb1BnYECLL"
  },
  {
    "id": "metawinners-9996",
    "name": "MetaWinners #9996",
    "image": "https://ipfs.io/ipfs/QmVCQ78wZ9wRzDtZRonG1NcGDChYaaba8LXepSbacshMLW"
  },
  {
    "id": "metawinners-9995",
    "name": "MetaWinners #9995",
    "image": "https://ipfs.io/ipfs/QmQ1kU7RowqL3DVC92YFk5pyPFAHyPB67NGbAXd93TqfMV"
  },
  {
    "id": "metawinners-9994",
    "name": "MetaWinners #9994",
    "image": "https://ipfs.io/ipfs/QmazTx1tCsy3A24WwujCKFuTfUV9gccoMzxs4eWsGZRny2"
  },
  {
    "id": "metawinners-9992",
    "name": "MetaWinners #9992",
    "image": "https://ipfs.io/ipfs/QmXtpLCuJ3bQjW2b53zwDBLFYsLqjLKFzzNAE7jSsgSUWc"
  },
  {
    "id": "metawinners-9990",
    "name": "MetaWinners #9990",
    "image": "https://ipfs.io/ipfs/QmTrYQNxHbSbEkpezauU5cZac9BGDqDWyumKVz2PT3CXW8"
  },
  {
    "id": "metawinners-9991",
    "name": "MetaWinners #9991",
    "image": "https://ipfs.io/ipfs/QmcSS4M3yCx4KtJcYVNED35Eohhr8oscaVTqmmkqr762wn"
  },
  {
    "id": "metawinners-9987",
    "name": "MetaWinners #9987",
    "image": "https://ipfs.io/ipfs/QmQMteTSDLPPXLsxucgnN5E4a6rtpASKqjErgnxibX3pUv"
  },
  {
    "id": "metawinners-9993",
    "name": "MetaWinners #9993",
    "image": "https://ipfs.io/ipfs/QmbEeqzETEbpB8K9JgNepd8HCtwXhxzWYv34JZVtCfHZYz"
  },
  {
    "id": "metawinners-9985",
    "name": "MetaWinners #9985",
    "image": "https://ipfs.io/ipfs/QmTJct14eGmVm8Uxgzbj2acq8ti8vJVGX95bEuHHFPUd7h"
  },
  {
    "id": "metawinners-9989",
    "name": "MetaWinners #9989",
    "image": "https://ipfs.io/ipfs/QmVsaaWbXiDPUeprBMjrLzgYKUBhNZhxXdZyM9WbB72uXH"
  },
  {
    "id": "metawinners-9988",
    "name": "MetaWinners #9988",
    "image": "https://ipfs.io/ipfs/QmPTC5zczY5mTcQH5fU9srsuvamMxVwpAD2WsCuBibhSBC"
  },
  {
    "id": "metawinners-9984",
    "name": "MetaWinners #9984",
    "image": "https://ipfs.io/ipfs/QmTifLBdNY2abAumPWJUK67JhjPbMitNVtmNnoaTg6jxxj"
  },
  {
    "id": "metawinners-9986",
    "name": "MetaWinners #9986",
    "image": "https://ipfs.io/ipfs/QmbJoXGqR84An3TsNBipxCP2dhyDvvTvxV9eizMzocnf9p"
  },
  {
    "id": "metawinners-9981",
    "name": "MetaWinners #9981",
    "image": "https://ipfs.io/ipfs/QmcFG8rn7aaVt5mLzrGiCy56cTe4r1MVtPMtXnsQwK4RSu"
  },
  {
    "id": "metawinners-9983",
    "name": "MetaWinners #9983",
    "image": "https://ipfs.io/ipfs/QmNwm6RTSKykFxpoRXLVyP3cfs5HL65eGkobSk3VyuonsZ"
  },
  {
    "id": "metawinners-9982",
    "name": "MetaWinners #9982",
    "image": "https://ipfs.io/ipfs/QmaTVov9jrSVzQ33ogcQJbXWL9Vb4vWDBzryKPaacUQQEb"
  },
  {
    "id": "metawinners-9977",
    "name": "MetaWinners #9977",
    "image": "https://ipfs.io/ipfs/QmYuJHnTRh7b1uDLeU4etwRkn9PAFQNsMQSx1HNZ9ozMwT"
  },
  {
    "id": "metawinners-9975",
    "name": "MetaWinners #9975",
    "image": "https://ipfs.io/ipfs/QmaKcodAP7bDdJPYJU3nh24d9iU9sCHUPjgbffSzy76VTu"
  },
  {
    "id": "metawinners-9970",
    "name": "MetaWinners #9970",
    "image": "https://ipfs.io/ipfs/QmQYDUwM1W55bj4hYJYaZKsbSgh32bYGqeKS1HmXX1zFZf"
  },
  {
    "id": "metawinners-9972",
    "name": "MetaWinners #9972",
    "image": "https://ipfs.io/ipfs/QmXwRJsQ9y71iZRYXNK4bWePA9RReKe9vENpvXVwTg6ioC"
  },
  {
    "id": "metawinners-9968",
    "name": "MetaWinners #9968",
    "image": "https://ipfs.io/ipfs/QmUtcTQWxAS5d8CrHD2PJLiJjmya96Bz9FJ4FUMWEWbqq3"
  },
  {
    "id": "metawinners-9971",
    "name": "MetaWinners #9971",
    "image": "https://ipfs.io/ipfs/QmcGfBXwSWqaxVStBVteGBPDFCaR2ivto81XWipp345D19"
  },
  {
    "id": "metawinners-9969",
    "name": "MetaWinners #9969",
    "image": "https://ipfs.io/ipfs/QmSay5jA8FgZutk7mXnyGjFnAzPDN5jWav3pbwAzzsUtGv"
  },
  {
    "id": "metawinners-9974",
    "name": "MetaWinners #9974",
    "image": "https://ipfs.io/ipfs/QmT7YAr9D5g5eyYi8ch3rKEGS9QfGRLvnn3oncFp6eP6pU"
  },
  {
    "id": "metawinners-9979",
    "name": "MetaWinners #9979",
    "image": "https://ipfs.io/ipfs/QmZyzxMXXxRkVNbrQZwJzPkH4Z1Xpkt9BFSVXiogorvi7b"
  },
  {
    "id": "metawinners-9980",
    "name": "MetaWinners #9980",
    "image": "https://ipfs.io/ipfs/QmViQ5M1pbzQYTDgpce7KmejJ4SgT4wQ4dwMmuZDRJWBVH"
  },
  {
    "id": "metawinners-9976",
    "name": "MetaWinners #9976",
    "image": "https://ipfs.io/ipfs/QmdNhJ3WueAohoBjtYBv9hB24x3YPSuBtfgovat78MfLKM"
  },
  {
    "id": "metawinners-9978",
    "name": "MetaWinners #9978",
    "image": "https://ipfs.io/ipfs/QmXT8Sc3b7q638EtbyuwWfoBhw8JNkP9CaFwTwwDjVFvpq"
  },
  {
    "id": "metawinners-9973",
    "name": "MetaWinners #9973",
    "image": "https://ipfs.io/ipfs/QmU6t3LWqz2MsUqCYSV5sC7C6p88tdaNT3YcgS46JBYMBT"
  },
  {
    "id": "metawinners-9967",
    "name": "MetaWinners #9967",
    "image": "https://ipfs.io/ipfs/QmTd4Zbn27DQ4uejkhcuE2TWUdzPAF6PY6ihdBuE7Kc9Ep"
  },
  {
    "id": "metawinners-9964",
    "name": "MetaWinners #9964",
    "image": "https://ipfs.io/ipfs/QmeU4WT4oSmFDk6tPhQwXD19EEweKm4pYxqpCaZWnVRN5i"
  },
  {
    "id": "metawinners-9966",
    "name": "MetaWinners #9966",
    "image": "https://ipfs.io/ipfs/QmSM3G7JaFSv5KuXaB6zYWoqvEw6tdKoyxhogz1zb5zUEK"
  },
  {
    "id": "metawinners-9965",
    "name": "MetaWinners #9965",
    "image": "https://ipfs.io/ipfs/QmaARK9S5ndjTssHR166t2hYoHQhFfgN9gqd9pdKL7tf7R"
  },
  {
    "id": "metawinners-9963",
    "name": "MetaWinners #9963",
    "image": "https://ipfs.io/ipfs/QmSdvgxAgzjWFXdkeBxvWf47PBESJ1Mqc9mERrVeuL8TMa"
  },
  {
    "id": "metawinners-9962",
    "name": "MetaWinners #9962",
    "image": "https://ipfs.io/ipfs/QmQa5T7fTUqYvzZ33jWoExKbVbvkE39Us48XhnfsysnroA"
  },
  {
    "id": "metawinners-9961",
    "name": "MetaWinners #9961",
    "image": "https://ipfs.io/ipfs/QmfG8qkAg4ywc4YrzigEfB7bketcmWH42YKVhr7ovgCpwd"
  },
  {
    "id": "metawinners-9960",
    "name": "MetaWinners #9960",
    "image": "https://ipfs.io/ipfs/QmQU4yeQ3uNAmx2kACEhrh5xgFBENNY9SV5gtngaTA7yEC"
  },
  {
    "id": "metawinners-9954",
    "name": "MetaWinners #9954",
    "image": "https://ipfs.io/ipfs/QmVEeuozJYcyKPmY71bgLS8iK2jenotzkxsogyeU96FmTv"
  },
  {
    "id": "metawinners-9950",
    "name": "MetaWinners #9950",
    "image": "https://ipfs.io/ipfs/QmXgvmSr4Epi16AHqvj7yAVrXxatFvozkw4mLsvFd9Hmjt"
  },
  {
    "id": "metawinners-9952",
    "name": "MetaWinners #9952",
    "image": "https://ipfs.io/ipfs/QmUZKGVjCwbigS7ESc22bq3z1f4NHdqgLAeYgYm2nRDk81"
  },
  {
    "id": "metawinners-9959",
    "name": "MetaWinners #9959",
    "image": "https://ipfs.io/ipfs/QmeRMkQZe2PH6nRUpmvWwZDJGwBn3sce9RdtiUyRGnvE6i"
  },
  {
    "id": "metawinners-9955",
    "name": "MetaWinners #9955",
    "image": "https://ipfs.io/ipfs/QmUfQ8dqj85s6gkfyk3oME1pvTv7hqdg32eNKxoYkKZpTz"
  },
  {
    "id": "metawinners-9951",
    "name": "MetaWinners #9951",
    "image": "https://ipfs.io/ipfs/QmfEMjoiA8DVXVmDvepctMeDZP4UWt5r7LG5tjZ3evEW5k"
  },
  {
    "id": "metawinners-9957",
    "name": "MetaWinners #9957",
    "image": "https://ipfs.io/ipfs/QmZYMavmBXMTfXis5yCVa2W3eAVpn14bQ8rUqcvzZn2nY7"
  },
  {
    "id": "metawinners-9953",
    "name": "MetaWinners #9953",
    "image": "https://ipfs.io/ipfs/Qma8gcxsd7MS6XPxta8fXYZjXjjz2fSrZ5exur7byyyazA"
  },
  {
    "id": "metawinners-9958",
    "name": "MetaWinners #9958",
    "image": "https://ipfs.io/ipfs/QmPtu8g7GAxwBDQJtgRyA9dBRim4qnJ6RYfFTNaRcXrqnd/SAMURAI%201.jpg"
  },
  {
    "id": "metawinners-9948",
    "name": "MetaWinners #9948",
    "image": "https://ipfs.io/ipfs/QmPijS4MFQMJwLUn4pxsewdfM8U1rrksbTnvdG18ErAqB7"
  },
  {
    "id": "metawinners-9944",
    "name": "MetaWinners #9944",
    "image": "https://ipfs.io/ipfs/QmSde4wZKd8VuM3df33ZiVUWbw59HoUTxqLR63DoSVWW4b"
  },
  {
    "id": "metawinners-9945",
    "name": "MetaWinners #9945",
    "image": "https://ipfs.io/ipfs/QmVBKbviAMZc33BMfcdCzNpn54uFbBg11jmMVhrx8rhM34"
  },
  {
    "id": "metawinners-9943",
    "name": "MetaWinners #9943",
    "image": "https://ipfs.io/ipfs/QmR9zbaMgndVNh4BH1A6x25koibmEY7XYRx7ZSb6KN2rzu"
  },
  {
    "id": "metawinners-9942",
    "name": "MetaWinners #9942",
    "image": "https://ipfs.io/ipfs/QmQdfhPdtj28Hjs6VFS2oJ911jzWn976yf96cDcVV3Lyju"
  },
  {
    "id": "metawinners-9940",
    "name": "MetaWinners #9940",
    "image": "https://ipfs.io/ipfs/Qme9dL4LMU1K69sXti83hDKa6Mhk9WFB4FHhYquLdQJkTC"
  },
  {
    "id": "metawinners-9941",
    "name": "MetaWinners #9941",
    "image": "https://ipfs.io/ipfs/QmTW9HkD24gUtsd5qEtACF1XYrceDADm97xu5jsEEs4aVj"
  },
  {
    "id": "metawinners-9939",
    "name": "MetaWinners #9939",
    "image": "https://ipfs.io/ipfs/QmQb4PiRu8bdkh2WgJB2qRHxpgQZBrAmbzLmvxg6nVkckP"
  },
  {
    "id": "metawinners-9934",
    "name": "MetaWinners #9934",
    "image": "https://ipfs.io/ipfs/QmcoDfFCCT8x6jasVbnctbpuJAm98SXADQxmM7FrpbFr8Z"
  },
  {
    "id": "metawinners-9933",
    "name": "MetaWinners #9933",
    "image": "https://ipfs.io/ipfs/QmRfAs59Hqf4PCxtR7Mb13XgSFVVQpxUb2o9EQ9crUookN"
  },
  {
    "id": "metawinners-9938",
    "name": "MetaWinners #9938",
    "image": "https://ipfs.io/ipfs/QmNS7XhjAa2dJN9jfVircEzS46fje1WWwoVtHwSScz5viN"
  },
  {
    "id": "metawinners-9932",
    "name": "MetaWinners #9932",
    "image": "https://ipfs.io/ipfs/QmdXEqbMdoUc3n4kfAginfUfAyDhnHB6Q2npNJSFB8m7i3"
  },
  {
    "id": "metawinners-9937",
    "name": "MetaWinners #9937",
    "image": "https://ipfs.io/ipfs/Qmdexa8aerCx9ve5TcpQxAsXYhLWke9Pv39PQyCGvXVmGH"
  },
  {
    "id": "metawinners-9931",
    "name": "MetaWinners #9931",
    "image": "https://ipfs.io/ipfs/QmaiGSYWAYutCpASuMhpfWE6fRLm6PfvSn3VhDWcC1krhv"
  },
  {
    "id": "metawinners-9936",
    "name": "MetaWinners #9936",
    "image": "https://ipfs.io/ipfs/Qmf9AqC9HKwjwezKmfx7G6dY1kkvrp9CMjdXCNwDPRcMLB"
  },
  {
    "id": "metawinners-9930",
    "name": "MetaWinners #9930",
    "image": "https://ipfs.io/ipfs/QmQWZjEUVkgszH21aCsBdE8FPSAGZeMUToXbSQoYmkePig"
  },
  {
    "id": "metawinners-9929",
    "name": "MetaWinners #9929",
    "image": "https://ipfs.io/ipfs/QmZcpuVzKeEfwwhzteV8wJJyDWznLTpp8KD6Jtj4LAg3Ez"
  },
  {
    "id": "metawinners-9935",
    "name": "MetaWinners #9935",
    "image": "https://ipfs.io/ipfs/QmXdyW6p9SxWwuLhH5hLR7EcHr7ZpiRsYCZ7x147EZ51Ky"
  },
  {
    "id": "metawinners-9924",
    "name": "MetaWinners #9924",
    "image": "https://ipfs.io/ipfs/QmR5W2cqDHxJvRPqDbCKzN3A4Dhq6jvMnRKnsncWgeyWY1/9635.jpg"
  },
  {
    "id": "metawinners-9927",
    "name": "MetaWinners #9927",
    "image": "https://ipfs.io/ipfs/QmTbvWrbEZLHTHFjPgchUgfza5NU3tTb191TVdDtuGjpdB"
  },
  {
    "id": "metawinners-9928",
    "name": "MetaWinners #9928",
    "image": "https://ipfs.io/ipfs/QmV3dxmWQvT4kTCXy7hdaNC4yJoNtJ8bDtzkNJyhrTADQ4"
  },
  {
    "id": "metawinners-9926",
    "name": "MetaWinners #9926",
    "image": "https://ipfs.io/ipfs/QmdLnBTws1cDm8Zw9s7MReUPGSiAAoEXSQEDa18nHE5ote"
  },
  {
    "id": "metawinners-9925",
    "name": "MetaWinners #9925",
    "image": "https://ipfs.io/ipfs/QmPZUpuWjuUrHPUXHknGQBDSwr75oYpetJYRkymdTV74nS"
  },
  {
    "id": "metawinners-9915",
    "name": "MetaWinners #9915",
    "image": "https://ipfs.io/ipfs/Qmb5ybWV2e7dxhnCKPg1xDLqgwkZHrrGGDG7fcrKSVU61z"
  },
  {
    "id": "metawinners-9916",
    "name": "MetaWinners #9916",
    "image": "https://ipfs.io/ipfs/QmbXUQPBWFQF71BkAjFi7UPBMH4u4FiGhZsVaAnHWuCWLq"
  },
  {
    "id": "metawinners-9917",
    "name": "MetaWinners #9917",
    "image": "https://ipfs.io/ipfs/QmPJ3KTTQLjDoMUtAE4wh7VUyCQgWJF1Zkow4o3wjsw9Vo"
  },
  {
    "id": "metawinners-9919",
    "name": "MetaWinners #9919",
    "image": "https://ipfs.io/ipfs/QmWWqTsXFGjSdfrwc1f1LgdtC8u4ef7Uyob2KKomzwHeGT"
  },
  {
    "id": "metawinners-9922",
    "name": "MetaWinners #9922",
    "image": "https://ipfs.io/ipfs/QmPTMnsks3R6GGCSP7twdvey4nfZEtT8qKfZpiMnvoJ3Cj"
  },
  {
    "id": "metawinners-9918",
    "name": "MetaWinners #9918",
    "image": "https://ipfs.io/ipfs/QmWM2AqRVC12srC91DwkjaTJf6M9hCfCfKGVkBsDnvWmdu"
  },
  {
    "id": "metawinners-9921",
    "name": "MetaWinners #9921",
    "image": "https://ipfs.io/ipfs/QmSoZTBJJs2atFkEp7mqE65jcNEuwRrMRr8sUzP6xapeek"
  },
  {
    "id": "metawinners-9923",
    "name": "MetaWinners #9923",
    "image": "https://ipfs.io/ipfs/Qmdtt4UznD6cQbZF3eDBefGLFVaiN93QhAdgSm9PWVuCkz"
  },
  {
    "id": "metawinners-9920",
    "name": "MetaWinners #9920",
    "image": "https://ipfs.io/ipfs/QmWnknBbPRdkecSVkYbmoTcdYHqvsaHFQoKd7oZhfErL1d"
  },
  {
    "id": "metawinners-9912",
    "name": "MetaWinners #9912",
    "image": "https://ipfs.io/ipfs/QmUcA1nRu8JkhP2yHurspMfX2snL1cdkc45G3DqfdnyGvk"
  },
  {
    "id": "metawinners-9914",
    "name": "MetaWinners #9914",
    "image": "https://ipfs.io/ipfs/QmW3tqmPA9Q3fkLDfvo8zApEm4DW2b15N8uyyLBKuoPzLU"
  },
  {
    "id": "metawinners-9913",
    "name": "MetaWinners #9913",
    "image": "https://ipfs.io/ipfs/QmQ5zRgW8rwMjPkYR5XGrECJHfHtR6rh52vMj1KTiWRBnX"
  },
  {
    "id": "metawinners-9909",
    "name": "MetaWinners #9909",
    "image": "https://ipfs.io/ipfs/QmR5W2cqDHxJvRPqDbCKzN3A4Dhq6jvMnRKnsncWgeyWY1/9620.jpg"
  },
  {
    "id": "metawinners-9908",
    "name": "MetaWinners #9908",
    "image": "https://ipfs.io/ipfs/QmYDBMfPUbPAUmqoL3pC6uX9PKtMsF4BMnaEymbFsTNGc4"
  },
  {
    "id": "metawinners-9911",
    "name": "MetaWinners #9911",
    "image": "https://ipfs.io/ipfs/QmPHSiChCV92GLr52xggPaHGxcspuZS9JCpvTMnqTGdhHd"
  },
  {
    "id": "metawinners-9907",
    "name": "MetaWinners #9907",
    "image": "https://ipfs.io/ipfs/QmWriygSq6jeYU6gkwwxpWgEvGtQX7Y5PzwQ9wCBZkf7qF"
  },
  {
    "id": "metawinners-9906",
    "name": "MetaWinners #9906",
    "image": "https://ipfs.io/ipfs/QmZJNe9u8DcoZvbamq1hrxJE2guk4uGvtaqFXQbm3rr62X"
  },
  {
    "id": "metawinners-9910",
    "name": "MetaWinners #9910",
    "image": "https://ipfs.io/ipfs/QmRsjGUi2Up3wtgSM7oAFNLiUthWr7md4aX4LqyAf9z5hU"
  },
  {
    "id": "metawinners-9905",
    "name": "MetaWinners #9905",
    "image": "https://ipfs.io/ipfs/QmZiWgg51YMF61KuRK4Vftduw4uiVJMkrntSR4an2nE4ku"
  },
  {
    "id": "metawinners-9904",
    "name": "MetaWinners #9904",
    "image": "https://ipfs.io/ipfs/QmYUL99LfeDk1yoM4TBeG5Yt4rnsyUP955NpKQvUo9Uohi"
  },
  {
    "id": "metawinners-9900",
    "name": "MetaWinners #9900",
    "image": "https://ipfs.io/ipfs/QmckKE5c7SvyAQXQuW45eZLkTqcNJVJqCSN1Hc68mEZCio"
  },
  {
    "id": "metawinners-9901",
    "name": "MetaWinners #9901",
    "image": "https://ipfs.io/ipfs/QmYsSyjWmcb6D2Wb6gHNNcDwDFuTgJj6hhgFakhAYNGJnL"
  },
  {
    "id": "metawinners-9898",
    "name": "MetaWinners #9898",
    "image": "https://ipfs.io/ipfs/QmVKayM69VbahuoiStYGVoHE3yM5LsKyH2unSWEfGkTQBK"
  }
             ];
        actualNftData = metawinnersNFTs.find(nft => nft.id.toString() === itemId);
                           
                          } else if (collectionId === 'pirate-nation-founders') {
        const piratenationNFTs = [
            
  {
    "id": "pirate-nation-8840",
    "name": "Founder's Pirate #8840",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8840"
  },
  {
    "id": "pirate-nation-8692",
    "name": "Founder's Pirate #8692",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8692"
  },
  {
    "id": "pirate-nation-8569",
    "name": "Founder's Pirate #8569",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8569"
  },
  {
    "id": "pirate-nation-8434",
    "name": "Founder's Pirate #8434",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8434"
  },
  {
    "id": "pirate-nation-8564",
    "name": "Founder's Pirate #8564",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8564"
  },
  {
    "id": "pirate-nation-8265",
    "name": "Founder's Pirate #8265",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8265"
  },
  {
    "id": "pirate-nation-8219",
    "name": "Founder's Pirate #8219",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8219"
  },
  {
    "id": "pirate-nation-8143",
    "name": "Founder's Pirate #8143",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8143"
  },
  {
    "id": "pirate-nation-8138",
    "name": "Founder's Pirate #8138",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8138"
  },
  {
    "id": "pirate-nation-8119",
    "name": "Founder's Pirate #8119",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8119"
  },
  {
    "id": "pirate-nation-8100",
    "name": "Founder's Pirate #8100",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8100"
  },
  {
    "id": "pirate-nation-8099",
    "name": "Founder's Pirate #8099",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8099"
  },
  {
    "id": "pirate-nation-8090",
    "name": "Founder's Pirate #8090",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8090"
  },
  {
    "id": "pirate-nation-8072",
    "name": "Founder's Pirate #8072",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8072"
  },
  {
    "id": "pirate-nation-7941",
    "name": "Founder's Pirate #7941",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7941"
  },
  {
    "id": "pirate-nation-7860",
    "name": "Founder's Pirate #7860",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7860"
  },
  {
    "id": "pirate-nation-7701",
    "name": "Founder's Pirate #7701",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7701"
  },
  {
    "id": "pirate-nation-7429",
    "name": "Founder's Pirate #7429",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7429"
  },
  {
    "id": "pirate-nation-7234",
    "name": "Founder's Pirate #7234",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7234"
  },
  {
    "id": "pirate-nation-7143",
    "name": "Founder's Pirate #7143",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7143"
  },
  {
    "id": "pirate-nation-7106",
    "name": "Founder's Pirate #7106",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7106"
  },
  {
    "id": "pirate-nation-7078",
    "name": "Founder's Pirate #7078",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7078"
  },
  {
    "id": "pirate-nation-7075",
    "name": "Founder's Pirate #7075",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7075"
  },
  {
    "id": "pirate-nation-7040",
    "name": "Founder's Pirate #7040",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7040"
  },
  {
    "id": "pirate-nation-7011",
    "name": "Founder's Pirate #7011",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7011"
  },
  {
    "id": "pirate-nation-6882",
    "name": "Founder's Pirate #6882",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6882"
  },
  {
    "id": "pirate-nation-6848",
    "name": "Founder's Pirate #6848",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6848"
  },
  {
    "id": "pirate-nation-6833",
    "name": "Founder's Pirate #6833",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6833"
  },
  {
    "id": "pirate-nation-6832",
    "name": "Founder's Pirate #6832",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6832"
  },
  {
    "id": "pirate-nation-6789",
    "name": "Founder's Pirate #6789",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6789"
  },
  {
    "id": "pirate-nation-6780",
    "name": "Founder's Pirate #6780",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6780"
  },
  {
    "id": "pirate-nation-6769",
    "name": "Founder's Pirate #6769",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6769"
  },
  {
    "id": "pirate-nation-6634",
    "name": "Founder's Pirate #6634",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6634"
  },
  {
    "id": "pirate-nation-6632",
    "name": "Founder's Pirate #6632",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6632"
  },
  {
    "id": "pirate-nation-6336",
    "name": "Founder's Pirate #6336",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6336"
  },
  {
    "id": "pirate-nation-6227",
    "name": "Founder's Pirate #6227",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6227"
  },
  {
    "id": "pirate-nation-6195",
    "name": "Founder's Pirate #6195",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6195"
  },
  {
    "id": "pirate-nation-5989",
    "name": "Founder's Pirate #5989",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5989"
  },
  {
    "id": "pirate-nation-5918",
    "name": "Founder's Pirate #5918",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5918"
  },
  {
    "id": "pirate-nation-5868",
    "name": "Founder's Pirate #5868",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5868"
  },
  {
    "id": "pirate-nation-5522",
    "name": "Founder's Pirate #5522",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5522"
  },
  {
    "id": "pirate-nation-5407",
    "name": "Founder's Pirate #5407",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5407"
  },
  {
    "id": "pirate-nation-5114",
    "name": "Founder's Pirate #5114",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5114"
  },
  {
    "id": "pirate-nation-4904",
    "name": "Founder's Pirate #4904",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4904"
  },
  {
    "id": "pirate-nation-4935",
    "name": "Founder's Pirate #4935",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4935"
  },
  {
    "id": "pirate-nation-4805",
    "name": "Founder's Pirate #4805",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4805"
  },
  {
    "id": "pirate-nation-4797",
    "name": "Founder's Pirate #4797",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4797"
  },
  {
    "id": "pirate-nation-4762",
    "name": "Founder's Pirate #4762",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4762"
  },
  {
    "id": "pirate-nation-4611",
    "name": "Founder's Pirate #4611",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4611"
  },
  {
    "id": "pirate-nation-4477",
    "name": "Founder's Pirate #4477",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4477"
  },
  {
    "id": "pirate-nation-4376",
    "name": "Founder's Pirate #4376",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4376"
  },
  {
    "id": "pirate-nation-4278",
    "name": "Founder's Pirate #4278",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4278"
  },
  {
    "id": "pirate-nation-4277",
    "name": "Founder's Pirate #4277",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4277"
  },
  {
    "id": "pirate-nation-4275",
    "name": "Founder's Pirate #4275",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4275"
  },
  {
    "id": "pirate-nation-4128",
    "name": "Founder's Pirate #4128",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4128"
  },
  {
    "id": "pirate-nation-4109",
    "name": "Founder's Pirate #4109",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4109"
  },
  {
    "id": "pirate-nation-3849",
    "name": "Founder's Pirate #3849",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3849"
  },
  {
    "id": "pirate-nation-3750",
    "name": "Founder's Pirate #3750",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3750"
  },
  {
    "id": "pirate-nation-3667",
    "name": "Founder's Pirate #3667",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3667"
  },
  {
    "id": "pirate-nation-3651",
    "name": "Founder's Pirate #3651",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3651"
  },
  {
    "id": "pirate-nation-3299",
    "name": "Founder's Pirate #3299",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3299"
  },
  {
    "id": "pirate-nation-3565",
    "name": "Founder's Pirate #3565",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3565"
  },
  {
    "id": "pirate-nation-3296",
    "name": "Founder's Pirate #3296",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3296"
  },
  {
    "id": "pirate-nation-3100",
    "name": "Founder's Pirate #3100",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/3100"
  },
  {
    "id": "pirate-nation-2923",
    "name": "Founder's Pirate #2923",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/2923"
  },
  {
    "id": "pirate-nation-2339",
    "name": "Founder's Pirate #2339",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/2339"
  },
  {
    "id": "pirate-nation-1647",
    "name": "Founder's Pirate #1647",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1647"
  },
  {
    "id": "pirate-nation-1380",
    "name": "Founder's Pirate #1380",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1380"
  },
  {
    "id": "pirate-nation-1735",
    "name": "Founder's Pirate #1735",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1735"
  },
  {
    "id": "pirate-nation-1225",
    "name": "Founder's Pirate #1225",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1225"
  },
  {
    "id": "pirate-nation-1223",
    "name": "Founder's Pirate #1223",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1223"
  },
  {
    "id": "pirate-nation-1162",
    "name": "Founder's Pirate #1162",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1162"
  },
  {
    "id": "pirate-nation-1161",
    "name": "Founder's Pirate #1161",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1161"
  },
  {
    "id": "pirate-nation-1005",
    "name": "Founder's Pirate #1005",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1005"
  },
  {
    "id": "pirate-nation-572",
    "name": "Founder's Pirate #572",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/572"
  },
  {
    "id": "pirate-nation-869",
    "name": "Founder's Pirate #869",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/869"
  },
  {
    "id": "pirate-nation-673",
    "name": "Founder's Pirate #673",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/673"
  },
  {
    "id": "pirate-nation-253",
    "name": "Founder's Pirate #253",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/253"
  },
  {
    "id": "pirate-nation-355",
    "name": "Founder's Pirate #355",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/355"
  },
  {
    "id": "pirate-nation-4072",
    "name": "Founder's Pirate #4072",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4072"
  },
  {
    "id": "pirate-nation-5920",
    "name": "Founder's Pirate #5920",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5920"
  },
  {
    "id": "pirate-nation-4837",
    "name": "Founder's Pirate #4837",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4837"
  },
  {
    "id": "pirate-nation-6506",
    "name": "Founder's Pirate #6506",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6506"
  },
  {
    "id": "pirate-nation-138",
    "name": "Founder's Pirate #138",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/138"
  },
  {
    "id": "pirate-nation-5168",
    "name": "Founder's Pirate #5168",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5168"
  },
  {
    "id": "pirate-nation-8539",
    "name": "Founder's Pirate #8539",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8539"
  },
  {
    "id": "pirate-nation-4563",
    "name": "Founder's Pirate #4563",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/4563"
  },
  {
    "id": "pirate-nation-1484",
    "name": "Founder's Pirate #1484",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1484"
  },
  {
    "id": "pirate-nation-2481",
    "name": "Founder's Pirate #2481",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/2481"
  },
  {
    "id": "pirate-nation-743",
    "name": "Founder's Pirate #743",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/743"
  },
  {
    "id": "pirate-nation-6173",
    "name": "Founder's Pirate #6173",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/6173"
  },
  {
    "id": "pirate-nation-8355",
    "name": "Founder's Pirate #8355",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/8355"
  },
  {
    "id": "pirate-nation-2580",
    "name": "Founder's Pirate #2580",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/2580"
  },
  {
    "id": "pirate-nation-1065",
    "name": "Founder's Pirate #1065",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1065"
  },
  {
    "id": "pirate-nation-1343",
    "name": "Founder's Pirate #1343",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1343"
  },
  {
    "id": "pirate-nation-5865",
    "name": "Founder's Pirate #5865",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/5865"
  },
  {
    "id": "pirate-nation-1736",
    "name": "Founder's Pirate #1736",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/1736"
  },
  {
    "id": "pirate-nation-7780",
    "name": "Founder's Pirate #7780",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/7780"
  },
  {
    "id": "pirate-nation-2581",
    "name": "Founder's Pirate #2581",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/2581"
  },
  {
    "id": "pirate-nation-976",
    "name": "Founder's Pirate #976",
    "image": "https://ipfs.io/ipfs/QmRjgEp89ovHdr1M8rkjoC6iEgbNna5kBDjbfubm4zeVDd/976"
  }
             ];
        actualNftData = piratenationNFTs.find(nft => nft.id.toString() === itemId);
      
                  } else if (collectionId === '2049-reflections') {
        const reflectionsNFTs = [
      
  {
    "id": "2049-reflections-2044",
    "name": "2049 // Reflections #2044",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_14_4_3.png"
  },
  {
    "id": "2049-reflections-2037",
    "name": "2049 // Reflections #2037",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_1_8_0.png"
  },
  {
    "id": "2049-reflections-2047",
    "name": "2049 // Reflections #2047",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_0_5_2.png"
  },
  {
    "id": "2049-reflections-2045",
    "name": "2049 // Reflections #2045",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_26_4_2.png"
  },
  {
    "id": "2049-reflections-2039",
    "name": "2049 // Reflections #2039",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_43_2_3.png"
  },
  {
    "id": "2049-reflections-2043",
    "name": "2049 // Reflections #2043",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_44_2_3.png"
  },
  {
    "id": "2049-reflections-2048",
    "name": "2049 // Reflections #2048",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_19_6_3.png"
  },
  {
    "id": "2049-reflections-2046",
    "name": "2049 // Reflections #2046",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_4_2_2.png"
  },
  {
    "id": "2049-reflections-2042",
    "name": "2049 // Reflections #2042",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_12_8_3.png"
  },
  {
    "id": "2049-reflections-2038",
    "name": "2049 // Reflections #2038",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_34_0_3.png"
  },
  {
    "id": "2049-reflections-2040",
    "name": "2049 // Reflections #2040",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_45_5_3.png"
  },
  {
    "id": "2049-reflections-2041",
    "name": "2049 // Reflections #2041",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_32_6_3.png"
  },
  {
    "id": "2049-reflections-2036",
    "name": "2049 // Reflections #2036",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_33_4_1.png"
  },
  {
    "id": "2049-reflections-2035",
    "name": "2049 // Reflections #2035",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_10_1_0.png"
  },
  {
    "id": "2049-reflections-2025",
    "name": "2049 // Reflections #2025",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_3_7_0.png"
  },
  {
    "id": "2049-reflections-2034",
    "name": "2049 // Reflections #2034",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_26_0_2.png"
  },
  {
    "id": "2049-reflections-2029",
    "name": "2049 // Reflections #2029",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_7_1_3.png"
  },
  {
    "id": "2049-reflections-2033",
    "name": "2049 // Reflections #2033",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_28_6_0.png"
  },
  {
    "id": "2049-reflections-2026",
    "name": "2049 // Reflections #2026",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_17_4_0.png"
  },
  {
    "id": "2049-reflections-2032",
    "name": "2049 // Reflections #2032",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_33_4_3.png"
  },
  {
    "id": "2049-reflections-2030",
    "name": "2049 // Reflections #2030",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_33_7_2.png"
  },
  {
    "id": "2049-reflections-2031",
    "name": "2049 // Reflections #2031",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ch_0_1_2.png"
  },
  {
    "id": "2049-reflections-2024",
    "name": "2049 // Reflections #2024",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_8_3_0.png"
  },
  {
    "id": "2049-reflections-2028",
    "name": "2049 // Reflections #2028",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_32_2_0.png"
  },
  {
    "id": "2049-reflections-2023",
    "name": "2049 // Reflections #2023",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_9_4_2.png"
  },
  {
    "id": "2049-reflections-2022",
    "name": "2049 // Reflections #2022",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_45_3_2.png"
  },
  {
    "id": "2049-reflections-2027",
    "name": "2049 // Reflections #2027",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_36_3_2.png"
  },
  {
    "id": "2049-reflections-2020",
    "name": "2049 // Reflections #2020",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ch_0_2_0.png"
  },
  {
    "id": "2049-reflections-2021",
    "name": "2049 // Reflections #2021",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_17_7_1.png"
  },
  {
    "id": "2049-reflections-2019",
    "name": "2049 // Reflections #2019",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_2_1_1.png"
  },
  {
    "id": "2049-reflections-2016",
    "name": "2049 // Reflections #2016",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_14_1_2.png"
  },
  {
    "id": "2049-reflections-2017",
    "name": "2049 // Reflections #2017",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_21_8_2.png"
  },
  {
    "id": "2049-reflections-2018",
    "name": "2049 // Reflections #2018",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_3_4_3.png"
  },
  {
    "id": "2049-reflections-2015",
    "name": "2049 // Reflections #2015",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_34_9_0.png"
  },
  {
    "id": "2049-reflections-2014",
    "name": "2049 // Reflections #2014",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_19_9_2.png"
  },
  {
    "id": "2049-reflections-2013",
    "name": "2049 // Reflections #2013",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_32_4_1.png"
  },
  {
    "id": "2049-reflections-2012",
    "name": "2049 // Reflections #2012",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_2_0_1.png"
  },
  {
    "id": "2049-reflections-2009",
    "name": "2049 // Reflections #2009",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_3_3_3.png"
  },
  {
    "id": "2049-reflections-2010",
    "name": "2049 // Reflections #2010",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_47_2_1.png"
  },
  {
    "id": "2049-reflections-2008",
    "name": "2049 // Reflections #2008",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_37_5_1.png"
  },
  {
    "id": "2049-reflections-2011",
    "name": "2049 // Reflections #2011",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_24_0_0.png"
  },
  {
    "id": "2049-reflections-2006",
    "name": "2049 // Reflections #2006",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_40_7_2.png"
  },
  {
    "id": "2049-reflections-2003",
    "name": "2049 // Reflections #2003",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_28_3_1.png"
  },
  {
    "id": "2049-reflections-1997",
    "name": "2049 // Reflections #1997",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_27_8_2.png"
  },
  {
    "id": "2049-reflections-2007",
    "name": "2049 // Reflections #2007",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_4_3_0.png"
  },
  {
    "id": "2049-reflections-1998",
    "name": "2049 // Reflections #1998",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_30_8_3.png"
  },
  {
    "id": "2049-reflections-2004",
    "name": "2049 // Reflections #2004",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_44_6_1.png"
  },
  {
    "id": "2049-reflections-2000",
    "name": "2049 // Reflections #2000",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_44_4_1.png"
  },
  {
    "id": "2049-reflections-2005",
    "name": "2049 // Reflections #2005",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_44_0_0.png"
  },
  {
    "id": "2049-reflections-2002",
    "name": "2049 // Reflections #2002",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_6_5_2.png"
  },
  {
    "id": "2049-reflections-1995",
    "name": "2049 // Reflections #1995",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_12_6_1.png"
  },
  {
    "id": "2049-reflections-1996",
    "name": "2049 // Reflections #1996",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_36_8_0.png"
  },
  {
    "id": "2049-reflections-1999",
    "name": "2049 // Reflections #1999",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_13_5_3.png"
  },
  {
    "id": "2049-reflections-1994",
    "name": "2049 // Reflections #1994",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_21_7_0.png"
  },
  {
    "id": "2049-reflections-2001",
    "name": "2049 // Reflections #2001",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_25_7_1.png"
  },
  {
    "id": "2049-reflections-1990",
    "name": "2049 // Reflections #1990",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_31_7_3.png"
  },
  {
    "id": "2049-reflections-1992",
    "name": "2049 // Reflections #1992",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_6_6_2.png"
  },
  {
    "id": "2049-reflections-1993",
    "name": "2049 // Reflections #1993",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_2_6_0.png"
  },
  {
    "id": "2049-reflections-1989",
    "name": "2049 // Reflections #1989",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_6_9_0.png"
  },
  {
    "id": "2049-reflections-1987",
    "name": "2049 // Reflections #1987",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_37_1_0.png"
  },
  {
    "id": "2049-reflections-1991",
    "name": "2049 // Reflections #1991",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_20_7_1.png"
  },
  {
    "id": "2049-reflections-1986",
    "name": "2049 // Reflections #1986",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_13_4_0.png"
  },
  {
    "id": "2049-reflections-1980",
    "name": "2049 // Reflections #1980",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_14_9_2.png"
  },
  {
    "id": "2049-reflections-1985",
    "name": "2049 // Reflections #1985",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_27_4_3.png"
  },
  {
    "id": "2049-reflections-1971",
    "name": "2049 // Reflections #1971",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_46_9_3.png"
  },
  {
    "id": "2049-reflections-1976",
    "name": "2049 // Reflections #1976",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_0_5_2.png"
  },
  {
    "id": "2049-reflections-1988",
    "name": "2049 // Reflections #1988",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_33_1_1.png"
  },
  {
    "id": "2049-reflections-1979",
    "name": "2049 // Reflections #1979",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_23_5_2.png"
  },
  {
    "id": "2049-reflections-1973",
    "name": "2049 // Reflections #1973",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_4_6_0.png"
  },
  {
    "id": "2049-reflections-1984",
    "name": "2049 // Reflections #1984",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_29_0_3.png"
  },
  {
    "id": "2049-reflections-1970",
    "name": "2049 // Reflections #1970",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_46_6_0.png"
  },
  {
    "id": "2049-reflections-1983",
    "name": "2049 // Reflections #1983",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_31_9_0.png"
  },
  {
    "id": "2049-reflections-1982",
    "name": "2049 // Reflections #1982",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_46_4_0.png"
  },
  {
    "id": "2049-reflections-1975",
    "name": "2049 // Reflections #1975",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_4_7_2.png"
  },
  {
    "id": "2049-reflections-1978",
    "name": "2049 // Reflections #1978",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_26_1_0.png"
  },
  {
    "id": "2049-reflections-1981",
    "name": "2049 // Reflections #1981",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_37_6_2.png"
  },
  {
    "id": "2049-reflections-1974",
    "name": "2049 // Reflections #1974",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_39_6_3.png"
  },
  {
    "id": "2049-reflections-1972",
    "name": "2049 // Reflections #1972",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_34_5_0.png"
  },
  {
    "id": "2049-reflections-1977",
    "name": "2049 // Reflections #1977",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_22_3_1.png"
  },
  {
    "id": "2049-reflections-1969",
    "name": "2049 // Reflections #1969",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_2_6_2.png"
  },
  {
    "id": "2049-reflections-1968",
    "name": "2049 // Reflections #1968",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_5_5_1.png"
  },
  {
    "id": "2049-reflections-1963",
    "name": "2049 // Reflections #1963",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_49_5_2.png"
  },
  {
    "id": "2049-reflections-1966",
    "name": "2049 // Reflections #1966",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_5_7_3.png"
  },
  {
    "id": "2049-reflections-1965",
    "name": "2049 // Reflections #1965",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_33_1_3.png"
  },
  {
    "id": "2049-reflections-1964",
    "name": "2049 // Reflections #1964",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_5_5_2.png"
  },
  {
    "id": "2049-reflections-1967",
    "name": "2049 // Reflections #1967",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_25_4_2.png"
  },
  {
    "id": "2049-reflections-1962",
    "name": "2049 // Reflections #1962",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_19_7_0.png"
  },
  {
    "id": "2049-reflections-1961",
    "name": "2049 // Reflections #1961",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_17_2_1.png"
  },
  {
    "id": "2049-reflections-1957",
    "name": "2049 // Reflections #1957",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_30_5_2.png"
  },
  {
    "id": "2049-reflections-1960",
    "name": "2049 // Reflections #1960",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_7_2_1.png"
  },
  {
    "id": "2049-reflections-1959",
    "name": "2049 // Reflections #1959",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_18_1_2.png"
  },
  {
    "id": "2049-reflections-1958",
    "name": "2049 // Reflections #1958",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_32_7_0.png"
  },
  {
    "id": "2049-reflections-1956",
    "name": "2049 // Reflections #1956",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_2_7_3.png"
  },
  {
    "id": "2049-reflections-1953",
    "name": "2049 // Reflections #1953",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_9_3_1.png"
  },
  {
    "id": "2049-reflections-1951",
    "name": "2049 // Reflections #1951",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_26_3_1.png"
  },
  {
    "id": "2049-reflections-1955",
    "name": "2049 // Reflections #1955",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_26_5_2.png"
  },
  {
    "id": "2049-reflections-1952",
    "name": "2049 // Reflections #1952",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_35_4_1.png"
  },
  {
    "id": "2049-reflections-1947",
    "name": "2049 // Reflections #1947",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_16_4_2.png"
  },
  {
    "id": "2049-reflections-1954",
    "name": "2049 // Reflections #1954",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/ke_25_1_3.png"
  },
  {
    "id": "2049-reflections-1949",
    "name": "2049 // Reflections #1949",
    "image": "https://ipfs.io/ipfs/bafybeih4tdh4mio7oz5au4pklomtowmsj24nw6gnt7xozx62hexxn37eie/sr_29_1_3.png"
  }
            ];
        actualNftData = reflectionsNFTs.find(nft => nft.id.toString() === itemId);
      
                  } else if (collectionId === 'emby') {
        const embyNFTs = [
            
  {
    id: 'emby-2492',
    name: 'Emby #2492',
    image: 'https://store.ewtd-ipfs.net/images/a8a370d9705b4b19bededb16f955e6aa.png'
  },
  {
    id: 'emby-2499',
    name: 'Emby #2499',
    image: 'https://store.ewtd-ipfs.net/images/4e94828b9f8945069d455287e285281e.png'
  },
  {
    id: 'emby-2496',
    name: 'Emby #2496',
    image: 'https://store.ewtd-ipfs.net/images/45d9b67661544cc7ba0cdf4e45111885.png'
  },
  {
    id: 'emby-2490',
    name: 'Emby #2490',
    image: 'https://store.ewtd-ipfs.net/images/fefe53367b3f4d989d6380b14058281e.png'
  },
  {
    id: 'emby-2498',
    name: 'Emby #2498',
    image: 'https://store.ewtd-ipfs.net/images/77777.png'
  },
  {
    id: 'emby-2491',
    name: 'Emby #2491',
    image: 'https://store.ewtd-ipfs.net/images/d07dfcb4e58444c8b6edd1a371f76b4e.png'
  },
  {
    id: 'emby-2500',
    name: 'Emby #2500',
    image: 'https://store.ewtd-ipfs.net/images/d3d505d96aff492488cc1af67223c2a9.png'
  },
  {
    id: 'emby-2497',
    name: 'Emby #2497',
    image: 'https://store.ewtd-ipfs.net/images/cc758bcf4cca45a481aa18d281cb10c6.png'
  },
  {
    id: 'emby-2494',
    name: 'Emby #2494',
    image: 'https://store.ewtd-ipfs.net/images/ac80e11637a245a58895c035e9329a19.png'
  },
  {
    id: 'emby-2485',
    name: 'Emby #2485',
    image: 'https://store.ewtd-ipfs.net/images/319a790f99ba4bbbbfc91144d74f76b9.png'
  },
  {
    id: 'emby-2487',
    name: 'Emby #2487',
    image: 'https://store.ewtd-ipfs.net/images/1684ea811de6435f84f942a3195ce3c5.png'
  },
  {
    id: 'emby-2489',
    name: 'Emby #2489',
    image: 'https://store.ewtd-ipfs.net/images/11111.png'
  },
  {
    id: 'emby-2488',
    name: 'Emby #2488',
    image: 'https://store.ewtd-ipfs.net/images/43b28b0ec623408abf831b433cd84538.png'
  },
  {
    id: 'emby-2467',
    name: 'Emby #2467',
    image: 'https://store.ewtd-ipfs.net/images/f942ff24f0014627b0f90cb36ed06e31.png'
  },
  {
    id: 'emby-2472',
    name: 'Emby #2472',
    image: 'https://store.ewtd-ipfs.net/images/0a298f447de449b79777258915f775ff.png'
  },
  {
    id: 'emby-2464',
    name: 'Emby #2464',
    image: 'https://store.ewtd-ipfs.net/images/33d49a6fb2f54e13979554a9fc4716e6.png'
  },
  {
    id: 'emby-2480',
    name: 'Emby #2480',
    image: 'https://store.ewtd-ipfs.net/images/fd4c998306f048b5929bb3df0a0d5506.png'
  },
  {
    id: 'emby-2495',
    name: 'Emby #2495',
    image: 'https://store.ewtd-ipfs.net/images/096f73a5d1ea4a928afff34fd1d778d7.png'
  },
  {
    id: 'emby-2493',
    name: 'Emby #2493',
    image: 'https://store.ewtd-ipfs.net/images/7d7179fd950944a78ab11ec84cfeee62.png'
  },
  {
    id: 'emby-2476',
    name: 'Emby #2476',
    image: 'https://store.ewtd-ipfs.net/images/5a05be65254347b0a17c173e58bd0855.png'
  },
  {
    id: 'emby-2460',
    name: 'Emby #2460',
    image: 'https://store.ewtd-ipfs.net/images/b16c8985f06c4eb18b4058b23188600d.png'
  },
  {
    id: 'emby-2474',
    name: 'Emby #2474',
    image: 'https://store.ewtd-ipfs.net/images/9c998da188134abbbc16f6327e3eacdc.png'
  },
  {
    id: 'emby-2475',
    name: 'Emby #2475',
    image: 'https://store.ewtd-ipfs.net/images/6210dac5dd154cdc89cbbd18ae5a3249.png'
  },
  {
    id: 'emby-2469',
    name: 'Emby #2469',
    image: 'https://store.ewtd-ipfs.net/images/5564f995a25a433686ce53110443e25f.png'
  },
  {
    id: 'emby-2459',
    name: 'Emby #2459',
    image: 'https://store.ewtd-ipfs.net/images/7e83e60bcded44f9b815677424cac577.png'
  },
  {
    id: 'emby-2471',
    name: 'Emby #2471',
    image: 'https://store.ewtd-ipfs.net/images/48d35a06520445b28cc6b83e3022f48d.png'
  },
  {
    id: 'emby-2468',
    name: 'Emby #2468',
    image: 'https://store.ewtd-ipfs.net/images/07bcf35251a84202aab466bcf6a0f162.png'
  },
  {
    id: 'emby-2481',
    name: 'Emby #2481',
    image: 'https://store.ewtd-ipfs.net/images/241bb41cb51e4bae923ce07e2694e016.png'
  },
  {
    id: 'emby-2484',
    name: 'Emby #2484',
    image: 'https://store.ewtd-ipfs.net/images/9cdde9aad0fe44d890763a45da920fa9.png'
  },
  {
    id: 'emby-2466',
    name: 'Emby #2466',
    image: 'https://store.ewtd-ipfs.net/images/533c76a9801f4653a7413bea4047b7f9.png'
  },
  {
    id: 'emby-2483',
    name: 'Emby #2483',
    image: 'https://store.ewtd-ipfs.net/images/4033efd977be4ef9ae000391d0dda1d9.png'
  },
  {
    id: 'emby-2473',
    name: 'Emby #2473',
    image: 'https://store.ewtd-ipfs.net/images/e3cecab5d7b044fa9f1e21cd97365645.png'
  },
  {
    id: 'emby-2458',
    name: 'Emby #2458',
    image: 'https://store.ewtd-ipfs.net/images/e16d3d1a4041437fbb1879e4e8a94b9e.png'
  },
  {
    id: 'emby-2477',
    name: 'Emby #2477',
    image: 'https://store.ewtd-ipfs.net/images/3638c05ccc604f45a1394ee88a910ff5.png'
  },
  {
    id: 'emby-2478',
    name: 'Emby #2478',
    image: 'https://store.ewtd-ipfs.net/images/567eae2ba81447f4a9b8c764ddc0b052.png'
  },
  {
    id: 'emby-2470',
    name: 'Emby #2470',
    image: 'https://store.ewtd-ipfs.net/images/0a3b669edd3647d2b7f0273b17d71512.png'
  },
  {
    id: 'emby-2482',
    name: 'Emby #2482',
    image: 'https://store.ewtd-ipfs.net/images/8da11db627864c7a8f2b6c392c397cc4.png'
  },
  {
    id: 'emby-2486',
    name: 'Emby #2486',
    image: 'https://store.ewtd-ipfs.net/images/d5e3f602d1cb42a1a70778c0c6dc340d.png'
  },
  {
    id: 'emby-2479',
    name: 'Emby #2479',
    image: 'https://store.ewtd-ipfs.net/images/da1fd8945c1749b1b026f032bedbc136.png'
  },
  {
    id: 'emby-2463',
    name: 'Emby #2463',
    image: 'https://store.ewtd-ipfs.net/images/511e87334641490aa1c4864589f8e764.png'
  },
  {
    id: 'emby-2461',
    name: 'Emby #2461',
    image: 'https://store.ewtd-ipfs.net/images/a51e405000e7452c880f4b74bca1205e.png'
  },
  {
    id: 'emby-2465',
    name: 'Emby #2465',
    image: 'https://store.ewtd-ipfs.net/images/4aeffb6dc4fa4898b99c12dbf7d5d471.png'
  },
  {
    id: 'emby-2462',
    name: 'Emby #2462',
    image: 'https://store.ewtd-ipfs.net/images/cc7278e3f5914f989f748870cdfcce17.png'
  },
  {
    id: 'emby-2457',
    name: 'Emby #2457',
    image: 'https://resources.xter.io/nft/playember/unrevealed.mp4.jpeg'
  },
  {
    id: 'emby-2456',
    name: 'Emby #2456',
    image: 'https://store.ewtd-ipfs.net/images/3342c5e4637240b2b9b3b8df11826d25.png'
  },
  {
    id: 'emby-2454',
    name: 'Emby #2454',
    image: 'https://store.ewtd-ipfs.net/images/43416f6fda154b2ca94a362cf3505b6d.png'
  },
  {
    id: 'emby-2455',
    name: 'Emby #2455',
    image: 'https://store.ewtd-ipfs.net/images/368c2c379a3344cba10f50c71e8a2ffb.png'
  },
  {
    id: 'emby-2451',
    name: 'Emby #2451',
    image: 'https://store.ewtd-ipfs.net/images/1a0dfce14a13435eb4ea821fbe989821.png'
  },
  {
    id: 'emby-2450',
    name: 'Emby #2450',
    image: 'https://store.ewtd-ipfs.net/images/fbbca7f1eb504cad958755a6c69279cc.png'
  },
  {
    id: 'emby-2453',
    name: 'Emby #2453',
    image: 'https://store.ewtd-ipfs.net/images/7cc25ef7d07346559f3e9f48b0a31dc2.png'
  },
  {
    id: 'emby-2452',
    name: 'Emby #2452',
    image: 'https://store.ewtd-ipfs.net/images/b32e5fd0024342c1a88ba78e062a771a.png'
  },
  {
    id: 'emby-2448',
    name: 'Emby #2448',
    image: 'https://store.ewtd-ipfs.net/images/6916842ec6944663974a2826201857f4.png'
  },
  {
    id: 'emby-2449',
    name: 'Emby #2449',
    image: 'https://store.ewtd-ipfs.net/images/6cd39cbe945d42abb251c5555a08cf73.png'
  },
  {
    id: 'emby-2447',
    name: 'Emby #2447',
    image: 'https://store.ewtd-ipfs.net/images/21454e9cc80f4bed90515a21f799017b.png'
  },
  {
    id: 'emby-2445',
    name: 'Emby #2445',
    image: 'https://store.ewtd-ipfs.net/images/1d708d2538bf439c923972ebd7d35cf1.png'
  },
  {
    id: 'emby-2442',
    name: 'Emby #2442',
    image: 'https://store.ewtd-ipfs.net/images/201eedf088614564b36840759a699074.png'
  },
  {
    id: 'emby-2432',
    name: 'Emby #2432',
    image: 'https://store.ewtd-ipfs.net/images/32ab3693460a4bf0a1770ad1a2f61783.png'
  },
  {
    id: 'emby-2440',
    name: 'Emby #2440',
    image: 'https://resources.xter.io/nft/playember/unrevealed.mp4.jpeg'
  },
  {
    id: 'emby-2446',
    name: 'Emby #2446',
    image: 'https://store.ewtd-ipfs.net/images/f38dafa14c6e46a29bb48144a2b92815.png'
  },
  {
    id: 'emby-2438',
    name: 'Emby #2438',
    image: 'https://store.ewtd-ipfs.net/images/7df7b93f41b144f9bfac60ead93d8550.png'
  },
  {
    id: 'emby-2443',
    name: 'Emby #2443',
    image: 'https://store.ewtd-ipfs.net/images/16df9d22b2cc4a07b47ca18c13c5cec2.png'
  },
  {
    id: 'emby-2444',
    name: 'Emby #2444',
    image: 'https://store.ewtd-ipfs.net/images/6c10df817d50462abf8a30b2cff74dd0.png'
  },
  {
    id: 'emby-2436',
    name: 'Emby #2436',
    image: 'https://store.ewtd-ipfs.net/images/e7dce411ca864588b12dd749539ea655.png'
  },
  {
    id: 'emby-2431',
    name: 'Emby #2431',
    image: 'https://store.ewtd-ipfs.net/images/4da4ca4b11e34bc5ae70c4af5e9a0ef9.png'
  },
  {
    id: 'emby-2428',
    name: 'Emby #2428',
    image: 'https://store.ewtd-ipfs.net/images/5f501b8b7ddc41c586fcf33f68ad2428.png'
  },
  {
    id: 'emby-2434',
    name: 'Emby #2434',
    image: 'https://store.ewtd-ipfs.net/images/34619de31d1f4bfea887b1541484b1e7.png'
  },
  {
    id: 'emby-2427',
    name: 'Emby #2427',
    image: 'https://store.ewtd-ipfs.net/images/16547d6f069a41d4b3edf50cd2aa56f4.png'
  },
  {
    id: 'emby-2439',
    name: 'Emby #2439',
    image: 'https://store.ewtd-ipfs.net/images/0566e90fcbd94ef8a0bd722c28660e76.png'
  },
  {
    id: 'emby-2430',
    name: 'Emby #2430',
    image: 'https://store.ewtd-ipfs.net/images/b753cc5ab708409f86a9d1303b61128d.png'
  },
  {
    id: 'emby-2441',
    name: 'Emby #2441',
    image: 'https://store.ewtd-ipfs.net/images/b08cd70aeaa342e88b404a35c52b2535.png'
  },
  {
    id: 'emby-2421',
    name: 'Emby #2421',
    image: 'https://store.ewtd-ipfs.net/images/896598e528ec4c18b5dcaad212dc20bb.png'
  },
  {
    id: 'emby-2437',
    name: 'Emby #2437',
    image: 'https://store.ewtd-ipfs.net/images/45c7ce0e16f740d5abc189c50939da69.png'
  },
  {
    id: 'emby-2422',
    name: 'Emby #2422',
    image: 'https://store.ewtd-ipfs.net/images/e31bca39478d46ef9e891998cdc34bae.png'
  },
  {
    id: 'emby-2433',
    name: 'Emby #2433',
    image: 'https://store.ewtd-ipfs.net/images/196f8490637842128c65800120020d76.png'
  },
  {
    id: 'emby-2425',
    name: 'Emby #2425',
    image: 'https://store.ewtd-ipfs.net/images/7615d448c6f1486c869b16c4dc698676.png'
  },
  {
    id: 'emby-2426',
    name: 'Emby #2426',
    image: 'https://store.ewtd-ipfs.net/images/466458653f3240eba4dec891e92035ae.png'
  },
  {
    id: 'emby-2435',
    name: 'Emby #2435',
    image: 'https://store.ewtd-ipfs.net/images/9ef205d6c0314a688ce87867c25c83a3.png'
  },
  {
    id: 'emby-2424',
    name: 'Emby #2424',
    image: 'https://resources.xter.io/nft/playember/unrevealed.mp4.jpeg'
  },
  {
    id: 'emby-2429',
    name: 'Emby #2429',
    image: 'https://store.ewtd-ipfs.net/images/e351328230a644ea8eda3efaea6f9d6a.png'
  },
  {
    id: 'emby-2423',
    name: 'Emby #2423',
    image: 'https://store.ewtd-ipfs.net/images/b856e375b0994801a2419e6cc764c715.png'
  },
  {
    id: 'emby-2420',
    name: 'Emby #2420',
    image: 'https://store.ewtd-ipfs.net/images/e23da032a455473ab25998beb612ebd7.png'
  },
  {
    id: 'emby-2419',
    name: 'Emby #2419',
    image: 'https://resources.xter.io/nft/playember/unrevealed.mp4.jpeg'
  },
  {
    id: 'emby-2418',
    name: 'Emby #2418',
    image: 'https://store.ewtd-ipfs.net/images/c16cf9eead0e4f138c0035215801b67b.png'
  },
  {
    id: 'emby-2417',
    name: 'Emby #2417',
    image: 'https://store.ewtd-ipfs.net/images/2a586d2238c8438d9ead4002cd3f7131.png'
  },
  {
    id: 'emby-2395',
    name: 'Emby #2395',
    image: 'https://store.ewtd-ipfs.net/images/696307b539c2478c82ec36de4ea0a745.png'
  },
  {
    id: 'emby-2413',
    name: 'Emby #2413',
    image: 'https://store.ewtd-ipfs.net/images/c08ecccd95de49c08ce10a09a7e8bed2.png'
  },
  {
    id: 'emby-2404',
    name: 'Emby #2404',
    image: 'https://store.ewtd-ipfs.net/images/a234b56cd7a4425aa7e0a290286854f5.png'
  },
  {
    id: 'emby-2397',
    name: 'Emby #2397',
    image: 'https://resources.xter.io/nft/playember/unrevealed.mp4.jpeg'
  },
  {
    id: 'emby-2386',
    name: 'Emby #2386',
    image: 'https://store.ewtd-ipfs.net/images/368fd4a62e5648ad82334a10f675fcf4.png'
  },
  {
    id: 'emby-2393',
    name: 'Emby #2393',
    image: 'https://store.ewtd-ipfs.net/images/6531a28b9bad4f30a949801637a24d7f.png'
  },
  {
    id: 'emby-2405',
    name: 'Emby #2405',
    image: 'https://resources.xter.io/nft/playember/unrevealed.mp4.jpeg'
  },
  {
    id: 'emby-2409',
    name: 'Emby #2409',
    image: 'https://store.ewtd-ipfs.net/images/ae54263a1fef45cead4e37e992d7f1b3.png'
  },
  {
    id: 'emby-2402',
    name: 'Emby #2402',
    image: 'https://store.ewtd-ipfs.net/images/8ea053ba55534b67a539f8de94e63a78.png'
  },
  {
    id: 'emby-2412',
    name: 'Emby #2412',
    image: 'https://store.ewtd-ipfs.net/images/7a0d7b7587524a0f8488dd51030f9173.png'
  },
  {
    id: 'emby-2394',
    name: 'Emby #2394',
    image: 'https://store.ewtd-ipfs.net/images/3421d8a5ffed4cbf9baf90866289e98f.png'
  },
  {
    id: 'emby-2374',
    name: 'Emby #2374',
    image: 'https://store.ewtd-ipfs.net/images/0867711ceadc4849a1dba85e8dd1c340.png'
  },
  {
    id: 'emby-2414',
    name: 'Emby #2414',
    image: 'https://store.ewtd-ipfs.net/images/00182f9164b144759af9ecc3fcc664d5.png'
  },
  {
    id: 'emby-2378',
    name: 'Emby #2378',
    image: 'https://store.ewtd-ipfs.net/images/025003c3205c463e9c544b6f43717ad0.png'
  },
  {
    id: 'emby-2399',
    name: 'Emby #2399',
    image: 'https://store.ewtd-ipfs.net/images/057e9d87b06e4ba89ab06e9ca77cca10.png'
  },
  {
    id: 'emby-2416',
    name: 'Emby #2416',
    image: 'https://store.ewtd-ipfs.net/images/47c78ea6f6ac4abaa5f4b27bafb4f171.png'
  }
                       ];
        actualNftData = embyNFTs.find(nft => nft.id.toString() === itemId);  
                   
                  } else if (collectionId === 'meebits') {
        const meebitsNFTs = [
  {
    id: 'meebits-10175',
    name: 'Meebits #10175',
    image: 'https://files.meebits.app/image/10175.png'
  },
  {
    id: 'meebits-12243',
    name: 'Meebits #12243',
    image: 'https://files.meebits.app/image/12243.png'
  },
  {
    id: 'meebits-4383',
    name: 'Meebits #4383',
    image: 'https://files.meebits.app/image/4383.png'
  },
  {
    id: 'meebits-4545',
    name: 'Meebits #4545',
    image: 'https://files.meebits.app/image/4545.png'
  },
  {
    id: 'meebits-8618',
    name: 'Meebits #8618',
    image: 'https://files.meebits.app/image/8618.png'
  },
  {
    id: 'meebits-11120',
    name: 'Meebits #11120',
    image: 'https://files.meebits.app/image/11120.png'
  },
  {
    id: 'meebits-775',
    name: 'Meebits #775',
    image: 'https://files.meebits.app/image/775.png'
  },
  {
    id: 'meebits-17002',
    name: 'Meebits #17002',
    image: 'https://files.meebits.app/image/17002.png'
  },
  {
    id: 'meebits-3918',
    name: 'Meebits #3918',
    image: 'https://files.meebits.app/image/3918.png'
  },
  {
    id: 'meebits-7887',
    name: 'Meebits #7887',
    image: 'https://files.meebits.app/image/7887.png'
  },
  {
    id: 'meebits-7511',
    name: 'Meebits #7511',
    image: 'https://files.meebits.app/image/7511.png'
  },
  {
    id: 'meebits-829',
    name: 'Meebits #829',
    image: 'https://files.meebits.app/image/829.png'
  },
  {
    id: 'meebits-18960',
    name: 'Meebits #18960',
    image: 'https://files.meebits.app/image/18960.png'
  },
  {
    id: 'meebits-16680',
    name: 'Meebits #16680',
    image: 'https://files.meebits.app/image/16680.png'
  },
  {
    id: 'meebits-11289',
    name: 'Meebits #11289',
    image: 'https://files.meebits.app/image/11289.png'
  },
  {
    id: 'meebits-18174',
    name: 'Meebits #18174',
    image: 'https://files.meebits.app/image/18174.png'
  },
  {
    id: 'meebits-12617',
    name: 'Meebits #12617',
    image: 'https://files.meebits.app/image/12617.png'
  },
  {
    id: 'meebits-3641',
    name: 'Meebits #3641',
    image: 'https://files.meebits.app/image/3641.png'
  },
  {
    id: 'meebits-7891',
    name: 'Meebits #7891',
    image: 'https://files.meebits.app/image/7891.png'
  },
  {
    id: 'meebits-15305',
    name: 'Meebits #15305',
    image: 'https://files.meebits.app/image/15305.png'
  },
  {
    id: 'meebits-3379',
    name: 'Meebits #3379',
    image: 'https://files.meebits.app/image/3379.png'
  },
  {
    id: 'meebits-366',
    name: 'Meebits #366',
    image: 'https://files.meebits.app/image/366.png'
  },
  {
    id: 'meebits-121',
    name: 'Meebits #121',
    image: 'https://files.meebits.app/image/121.png'
  },
  {
    id: 'meebits-11263',
    name: 'Meebits #11263',
    image: 'https://files.meebits.app/image/11263.png'
  },
  {
    id: 'meebits-14998',
    name: 'Meebits #14998',
    image: 'https://files.meebits.app/image/14998.png'
  },
  {
    id: 'meebits-15964',
    name: 'Meebits #15964',
    image: 'https://files.meebits.app/image/15964.png'
  },
  {
    id: 'meebits-15390',
    name: 'Meebits #15390',
    image: 'https://files.meebits.app/image/15390.png'
  },
  {
    id: 'meebits-17023',
    name: 'Meebits #17023',
    image: 'https://files.meebits.app/image/17023.png'
  },
  {
    id: 'meebits-1647',
    name: 'Meebits #1647',
    image: 'https://files.meebits.app/image/1647.png'
  },
  {
    id: 'meebits-4511',
    name: 'Meebits #4511',
    image: 'https://files.meebits.app/image/4511.png'
  },
  {
    id: 'meebits-13179',
    name: 'Meebits #13179',
    image: 'https://files.meebits.app/image/13179.png'
  },
  {
    id: 'meebits-359',
    name: 'Meebits #359',
    image: 'https://files.meebits.app/image/359.png'
  },
  {
    id: 'meebits-15927',
    name: 'Meebits #15927',
    image: 'https://files.meebits.app/image/15927.png'
  },
  {
    id: 'meebits-2423',
    name: 'Meebits #2423',
    image: 'https://files.meebits.app/image/2423.png'
  },
  {
    id: 'meebits-5454',
    name: 'Meebits #5454',
    image: 'https://files.meebits.app/image/5454.png'
  },
  {
    id: 'meebits-11985',
    name: 'Meebits #11985',
    image: 'https://files.meebits.app/image/11985.png'
  },
  {
    id: 'meebits-5299',
    name: 'Meebits #5299',
    image: 'https://files.meebits.app/image/5299.png'
  },
  {
    id: 'meebits-16901',
    name: 'Meebits #16901',
    image: 'https://files.meebits.app/image/16901.png'
  },
  {
    id: 'meebits-11731',
    name: 'Meebits #11731',
    image: 'https://files.meebits.app/image/11731.png'
  },
  {
    id: 'meebits-561',
    name: 'Meebits #561',
    image: 'https://files.meebits.app/image/561.png'
  },
  {
    id: 'meebits-17145',
    name: 'Meebits #17145',
    image: 'https://files.meebits.app/image/17145.png'
  },
  {
    id: 'meebits-7574',
    name: 'Meebits #7574',
    image: 'https://files.meebits.app/image/7574.png'
  },
  {
    id: 'meebits-19552',
    name: 'Meebits #19552',
    image: 'https://files.meebits.app/image/19552.png'
  },
  {
    id: 'meebits-17542',
    name: 'Meebits #17542',
    image: 'https://files.meebits.app/image/17542.png'
  },
  {
    id: 'meebits-12818',
    name: 'Meebits #12818',
    image: 'https://files.meebits.app/image/12818.png'
  },
  {
    id: 'meebits-14195',
    name: 'Meebits #14195',
    image: 'https://files.meebits.app/image/14195.png'
  },
  {
    id: 'meebits-71',
    name: 'Meebits #71',
    image: 'https://files.meebits.app/image/71.png'
  },
  {
    id: 'meebits-2531',
    name: 'Meebits #2531',
    image: 'https://files.meebits.app/image/2531.png'
  },
  {
    id: 'meebits-644',
    name: 'Meebits #644',
    image: 'https://files.meebits.app/image/644.png'
  },
  {
    id: 'meebits-4439',
    name: 'Meebits #4439',
    image: 'https://files.meebits.app/image/4439.png'
  },
  {
    id: 'meebits-812',
    name: 'Meebits #812',
    image: 'https://files.meebits.app/image/812.png'
  },
  {
    id: 'meebits-18092',
    name: 'Meebits #18092',
    image: 'https://files.meebits.app/image/18092.png'
  },
  {
    id: 'meebits-17917',
    name: 'Meebits #17917',
    image: 'https://files.meebits.app/image/17917.png'
  },
  {
    id: 'meebits-14940',
    name: 'Meebits #14940',
    image: 'https://files.meebits.app/image/14940.png'
  },
  {
    id: 'meebits-5061',
    name: 'Meebits #5061',
    image: 'https://files.meebits.app/image/5061.png'
  },
  {
    id: 'meebits-16833',
    name: 'Meebits #16833',
    image: 'https://files.meebits.app/image/16833.png'
  },
  {
    id: 'meebits-13465',
    name: 'Meebits #13465',
    image: 'https://files.meebits.app/image/13465.png'
  },
  {
    id: 'meebits-7739',
    name: 'Meebits #7739',
    image: 'https://files.meebits.app/image/7739.png'
  },
  {
    id: 'meebits-1656',
    name: 'Meebits #1656',
    image: 'https://files.meebits.app/image/1656.png'
  },
  {
    id: 'meebits-6168',
    name: 'Meebits #6168',
    image: 'https://files.meebits.app/image/6168.png'
  },
  {
    id: 'meebits-4037',
    name: 'Meebits #4037',
    image: 'https://files.meebits.app/image/4037.png'
  },
  {
    id: 'meebits-100',
    name: 'Meebits #100',
    image: 'https://files.meebits.app/image/100.png'
  },
  {
    id: 'meebits-1768',
    name: 'Meebits #1768',
    image: 'https://files.meebits.app/image/1768.png'
  },
  {
    id: 'meebits-13437',
    name: 'Meebits #13437',
    image: 'https://files.meebits.app/image/13437.png'
  },
  {
    id: 'meebits-2128',
    name: 'Meebits #2128',
    image: 'https://files.meebits.app/image/2128.png'
  },
  {
    id: 'meebits-7085',
    name: 'Meebits #7085',
    image: 'https://files.meebits.app/image/7085.png'
  },
  {
    id: 'meebits-17336',
    name: 'Meebits #17336',
    image: 'https://files.meebits.app/image/17336.png'
  },
  {
    id: 'meebits-17059',
    name: 'Meebits #17059',
    image: 'https://files.meebits.app/image/17059.png'
  },
  {
    id: 'meebits-7128',
    name: 'Meebits #7128',
    image: 'https://files.meebits.app/image/7128.png'
  },
  {
    id: 'meebits-3173',
    name: 'Meebits #3173',
    image: 'https://files.meebits.app/image/3173.png'
  },
  {
    id: 'meebits-3086',
    name: 'Meebits #3086',
    image: 'https://files.meebits.app/image/3086.png'
  },
  {
    id: 'meebits-8893',
    name: 'Meebits #8893',
    image: 'https://files.meebits.app/image/8893.png'
  },
  {
    id: 'meebits-3582',
    name: 'Meebits #3582',
    image: 'https://files.meebits.app/image/3582.png'
  },
  {
    id: 'meebits-5590',
    name: 'Meebits #5590',
    image: 'https://files.meebits.app/image/5590.png'
  },
  {
    id: 'meebits-2816',
    name: 'Meebits #2816',
    image: 'https://files.meebits.app/image/2816.png'
  },
  {
    id: 'meebits-13936',
    name: 'Meebits #13936',
    image: 'https://files.meebits.app/image/13936.png'
  },
  {
    id: 'meebits-10869',
    name: 'Meebits #10869',
    image: 'https://files.meebits.app/image/10869.png'
  },
  {
    id: 'meebits-14177',
    name: 'Meebits #14177',
    image: 'https://files.meebits.app/image/14177.png'
  },
  {
    id: 'meebits-5919',
    name: 'Meebits #5919',
    image: 'https://files.meebits.app/image/5919.png'
  },
  {
    id: 'meebits-12287',
    name: 'Meebits #12287',
    image: 'https://files.meebits.app/image/12287.png'
  },
  {
    id: 'meebits-8444',
    name: 'Meebits #8444',
    image: 'https://files.meebits.app/image/8444.png'
  },
  {
    id: 'meebits-5837',
    name: 'Meebits #5837',
    image: 'https://files.meebits.app/image/5837.png'
  },
  {
    id: 'meebits-4391',
    name: 'Meebits #4391',
    image: 'https://files.meebits.app/image/4391.png'
  },
  {
    id: 'meebits-11720',
    name: 'Meebits #11720',
    image: 'https://files.meebits.app/image/11720.png'
  },
  {
    id: 'meebits-11705',
    name: 'Meebits #11705',
    image: 'https://files.meebits.app/image/11705.png'
  },
  {
    id: 'meebits-19209',
    name: 'Meebits #19209',
    image: 'https://files.meebits.app/image/19209.png'
  },
  {
    id: 'meebits-4908',
    name: 'Meebits #4908',
    image: 'https://files.meebits.app/image/4908.png'
  },
  {
    id: 'meebits-8473',
    name: 'Meebits #8473',
    image: 'https://files.meebits.app/image/8473.png'
  },
  {
    id: 'meebits-4212',
    name: 'Meebits #4212',
    image: 'https://files.meebits.app/image/4212.png'
  },
  {
    id: 'meebits-16539',
    name: 'Meebits #16539',
    image: 'https://files.meebits.app/image/16539.png'
  },
  {
    id: 'meebits-15558',
    name: 'Meebits #15558',
    image: 'https://files.meebits.app/image/15558.png'
  },
  {
    id: 'meebits-3175',
    name: 'Meebits #3175',
    image: 'https://files.meebits.app/image/3175.png'
  },
  {
    id: 'meebits-1210',
    name: 'Meebits #1210',
    image: 'https://files.meebits.app/image/1210.png'
  },
  {
    id: 'meebits-18329',
    name: 'Meebits #18329',
    image: 'https://files.meebits.app/image/18329.png'
  },
  {
    id: 'meebits-12389',
    name: 'Meebits #12389',
    image: 'https://files.meebits.app/image/12389.png'
  },
  {
    id: 'meebits-1327',
    name: 'Meebits #1327',
    image: 'https://files.meebits.app/image/1327.png'
  },
  {
    id: 'meebits-2958',
    name: 'Meebits #2958',
    image: 'https://files.meebits.app/image/2958.png'
  },
  {
    id: 'meebits-3958',
    name: 'Meebits #3958',
    image: 'https://files.meebits.app/image/3958.png'
  },
  {
    id: 'meebits-9258',
    name: 'Meebits #9258',
    image: 'https://files.meebits.app/image/9258.png'
  },
  {
    id: 'meebits-12688',
    name: 'Meebits #12688',
    image: 'https://files.meebits.app/image/12688.png'
  }
              ];
        actualNftData = meebitsNFTs.find(nft => nft.id.toString() === itemId);  
            
      
       } else if (collectionId === 'mushboomers') {
        const mushboomersNFTs = [
        
  {
    id: 'mushboomers-1239',
    name: 'Mushboomers #1239',
    image: 'https://ipfs.io/ipfs/QmcbnG94y5S8zP3sJWX9wVHWLbFj8c1Dku5J6MVGa82AbU'
  },
  {
    id: 'mushboomers-1238',
    name: 'Mushboomers #1238',
    image: 'https://ipfs.io/ipfs/QmVwUfoHoZQbTJ85iMUxEY1x81HSNJTyeKiMEJQ6KSkpLX'
  },
  {
    id: 'mushboomers-1237',
    name: 'Mushboomers #1237',
    image: 'https://ipfs.io/ipfs/QmZNgbqvxfBajiXhsLfJvL4r9f1VxzmKbcGAKwYQKmXTNE'
  },
  {
    id: 'mushboomers-1236',
    name: 'Mushboomers #1236',
    image: 'https://ipfs.io/ipfs/QmUrMPkYYSwwdJdhjmPa3ExNLRL8uZ94jRCiguPDqrCgLq'
  },
  {
    id: 'mushboomers-1235',
    name: 'Mushboomers #1235',
    image: 'https://ipfs.io/ipfs/QmbaoEmQgcQYbDrHii1iJgpuVoo51jvut8B2V2qPXNAHh3'
  },
  {
    id: 'mushboomers-1234',
    name: 'Mushboomers #1234',
    image: 'https://ipfs.io/ipfs/Qmcv724YQaKmJxYk1scdDWYYbB8LEbwGxE66VZGKWC3E9H'
  },
  {
    id: 'mushboomers-1233',
    name: 'Mushboomers #1233',
    image: 'https://ipfs.io/ipfs/QmRsvSGHjNz69hSD2jKbG5hKHgLcV13R5WjEA4htDGD5EK'
  },
  {
    id: 'mushboomers-1232',
    name: 'Mushboomers #1232',
    image: 'https://ipfs.io/ipfs/Qmb6yfggu9pWfpjaRbbpLa1dd6XvrUY8vvpTqnYZcJFV1a'
  },
  {
    id: 'mushboomers-1231',
    name: 'Mushboomers #1231',
    image: 'https://ipfs.io/ipfs/QmQWXpgBpmi1bnPs4duy7mX3KmLT6GRhXiPh1XFph2HU2v'
  },
  {
    id: 'mushboomers-1230',
    name: 'Mushboomers #1230',
    image: 'https://ipfs.io/ipfs/QmTSzCbFi4jVCJcZD7J1sj2bmZhsfycBjA9BEJnrm6wLJv'
  },
  {
    id: 'mushboomers-1229',
    name: 'Mushboomers #1229',
    image: 'https://ipfs.io/ipfs/QmTcR4s2ksMpZ1g4B4zBWBbN24S9YjJN7ZG3HtCgXKtJyU'
  },
  {
    id: 'mushboomers-1228',
    name: 'Mushboomers #1228',
    image: 'https://ipfs.io/ipfs/QmS6NJHJCU5VymkzQEgoqnASabRTBqL6vfDpeuXjpQzMs2'
  },
  {
    id: 'mushboomers-1227',
    name: 'Mushboomers #1227',
    image: 'https://ipfs.io/ipfs/Qmdx1PcSkNCmhYSpvG3ddXKhmboxzYQxshei1TN4fH8EbJ'
  },
  {
    id: 'mushboomers-1226',
    name: 'Mushboomers #1226',
    image: 'https://ipfs.io/ipfs/Qmd8ZrABgDWhsRXoxJG1m5mCBfZXDfXzjHedybSzLzRarG'
  },
  {
    id: 'mushboomers-1225',
    name: 'Mushboomers #1225',
    image: 'https://ipfs.io/ipfs/QmUzYjbNwNBzPT3QJNhyzcrBUsCnYBu3W4WxaGvktm7i7P'
  },
  {
    id: 'mushboomers-1224',
    name: 'Mushboomers #1224',
    image: 'https://ipfs.io/ipfs/QmaNgdFRrXjrGnsEPGNDZGaiB4LXP1ENuryiJod1jfUbBQ'
  },
  {
    id: 'mushboomers-1223',
    name: 'Mushboomers #1223',
    image: 'https://ipfs.io/ipfs/QmWh4v1yR6pgYyuTT1Ceh8QY8GT3VoeWTMVoRTfujcneUp'
  },
  {
    id: 'mushboomers-1222',
    name: 'Mushboomers #1222',
    image: 'https://ipfs.io/ipfs/QmQB4bmJLS6K3nvhy8Ztiz6N5eiGVNuPt7PhogHVMtFkYb'
  },
  {
    id: 'mushboomers-1221',
    name: 'Mushboomers #1221',
    image: 'https://ipfs.io/ipfs/QmYDh2RQkaVBiU31K688YV6xa3hDDM1xKaVtzZTnWZRaEB'
  },
  {
    id: 'mushboomers-1220',
    name: 'Mushboomers #1220',
    image: 'https://ipfs.io/ipfs/QmXuBunsQJF3SRdkgZB5BhExsPRmU8heyE7UQw8LWJNhyU'
  },
  {
    id: 'mushboomers-1219',
    name: 'Mushboomers #1219',
    image: 'https://ipfs.io/ipfs/QmPAHvFmE1JnuiGMbGMRDP6a4K7trAV2gP5qoDaBwFwtgv'
  },
  {
    id: 'mushboomers-1218',
    name: 'Mushboomers #1218',
    image: 'https://ipfs.io/ipfs/QmWiE3SCeYqDpPawkLAcBuY6Cv9WexongkkWD9BdYARzjk'
  },
  {
    id: 'mushboomers-1217',
    name: 'Mushboomers #1217',
    image: 'https://ipfs.io/ipfs/QmVYUvJFb5RiGMT4WHpsoeJRM85LtKmojyKfyvnXkDZUx2'
  },
  {
    id: 'mushboomers-1216',
    name: 'Mushboomers #1216',
    image: 'https://ipfs.io/ipfs/QmUAZez6fPLVD3VcNQ4wvZxioQRJ3PaGWWFLNK2881hcKj'
  },
  {
    id: 'mushboomers-1215',
    name: 'Mushboomers #1215',
    image: 'https://ipfs.io/ipfs/QmTiQTa6WjWVnvZ5CZ66zUG7H5e8Jo3e5SHqHhmdsrNxvT'
  },
  {
    id: 'mushboomers-1214',
    name: 'Mushboomers #1214',
    image: 'https://ipfs.io/ipfs/QmZYYCkg1AkeUoJ48a3UWzTJJXLg9PAvyQiguKG78xfivV'
  },
  {
    id: 'mushboomers-1213',
    name: 'Mushboomers #1213',
    image: 'https://ipfs.io/ipfs/QmRHhQhA6u3gdBWLtYG8qYkYPCAeZzmXjQV2sqv6eUHDhc'
  },
  {
    id: 'mushboomers-1212',
    name: 'Mushboomers #1212',
    image: 'https://ipfs.io/ipfs/QmT5xvaaMrjYqEFqXP4vjd6y1nfzk4ktzjz1hbbArdmeom'
  },
  {
    id: 'mushboomers-1211',
    name: 'Mushboomers #1211',
    image: 'https://ipfs.io/ipfs/QmYBD1qyGV98yzj2xBNwQJFN4E25Fice1joKZVySBNPw1r'
  },
  {
    id: 'mushboomers-1210',
    name: 'Mushboomers #1210',
    image: 'https://ipfs.io/ipfs/QmSsxXF2v1V3TDgqE8S7tMJjuGy9kCFKBG7fUrvdt57Pid'
  },
  {
    id: 'mushboomers-1209',
    name: 'Mushboomers #1209',
    image: 'https://ipfs.io/ipfs/QmfBFfrQQS5cfDGSPJByHG91ttMKeXpySpgYJaqaovctuE'
  },
  {
    id: 'mushboomers-1208',
    name: 'Mushboomers #1208',
    image: 'https://ipfs.io/ipfs/QmcTP7dvSZTBrhZWy2gjtPbhred5A1ARAudmmCFQ6upN1h'
  },
  {
    id: 'mushboomers-1207',
    name: 'Mushboomers #1207',
    image: 'https://ipfs.io/ipfs/QmbxSXQ9WXnk816EfmECTDGEN4F5UDNFHgdMQNiCpDbV2Z'
  },
  {
    id: 'mushboomers-1206',
    name: 'Mushboomers #1206',
    image: 'https://ipfs.io/ipfs/QmXF4bVhLsfDpBrGsS5pPfC2pAoR89CqGu9o6KdovdkHNh'
  },
  {
    id: 'mushboomers-1205',
    name: 'Mushboomers #1205',
    image: 'https://ipfs.io/ipfs/QmeKnJTDuRw1okLtiptGe7QAEK5vwUWtwBpXjE1yCFeAwc'
  },
  {
    id: 'mushboomers-1204',
    name: 'Mushboomers #1204',
    image: 'https://ipfs.io/ipfs/QmVYCVFdTydK7cmPtTeR1eZiNUiXkdVB5ghXeSaDppQt42'
  },
  {
    id: 'mushboomers-1203',
    name: 'Mushboomers #1203',
    image: 'https://ipfs.io/ipfs/QmcUqPzwy6hEZXSp6fGKfpYhsBjVPeoZgUVd9oLAMYWDWr'
  },
  {
    id: 'mushboomers-1202',
    name: 'Mushboomers #1202',
    image: 'https://ipfs.io/ipfs/QmfQgrwpDr5m7K7ycPbiBGaGLT1hqpY5C2wTX9nJcQ2377'
  },
  {
    id: 'mushboomers-1201',
    name: 'Mushboomers #1201',
    image: 'https://ipfs.io/ipfs/QmeabZr3NVh23FpGLnxYR3YVKgDyeTjpjQa1AwpB1VACVA'
  },
  {
    id: 'mushboomers-1200',
    name: 'Mushboomers #1200',
    image: 'https://ipfs.io/ipfs/QmU3eFKmpdVRj26ZdQ2h3pxq357vyx2MvmaZtFKRLSYy6d'
  },
  {
    id: 'mushboomers-1199',
    name: 'Mushboomers #1199',
    image: 'https://ipfs.io/ipfs/QmPKJkCzHP9W4JUzH2hZKaeWzDVXifgmo77BQJrMS7Sq9W'
  },
  {
    id: 'mushboomers-1198',
    name: 'Mushboomers #1198',
    image: 'https://ipfs.io/ipfs/QmZrjW1cmXaCLqmKXgKyabNgETzi545oAexocgKayMLgfb'
  },
  {
    id: 'mushboomers-1197',
    name: 'Mushboomers #1197',
    image: 'https://ipfs.io/ipfs/QmWZXjJZLtYWDYc6RtEoXXi6QikD6dea5bH5Ya6UBfq69k'
  },
  {
    id: 'mushboomers-1196',
    name: 'Mushboomers #1196',
    image: 'https://ipfs.io/ipfs/QmVrnGB1L4fumJm1onbTipcWvCCrkr9152rTckWp2Xeycg'
  },
  {
    id: 'mushboomers-1195',
    name: 'Mushboomers #1195',
    image: 'https://ipfs.io/ipfs/QmR2z6pEJ5KwPFbeS8uTfkY1FbThV71JaDX2YTrqLbVbsP'
  },
  {
    id: 'mushboomers-1194',
    name: 'Mushboomers #1194',
    image: 'https://ipfs.io/ipfs/QmR52z6Nni524q5szsho2nz2AfrM61Dy9tgsqDyj4qLbWB'
  },
  {
    id: 'mushboomers-1193',
    name: 'Mushboomers #1193',
    image: 'https://ipfs.io/ipfs/Qmew4e18BaxyFgb6UcQYow3ZAojUPSLJkD8anjj6DnQ1sN'
  },
  {
    id: 'mushboomers-1192',
    name: 'Mushboomers #1192',
    image: 'https://ipfs.io/ipfs/QmXf15S48dQ4wsMbFAgPqefYq32SPfuK1mXQHLKzsgHbva'
  },
  {
    id: 'mushboomers-1191',
    name: 'Mushboomers #1191',
    image: 'https://ipfs.io/ipfs/Qmew9xDc8RxSAaNF9TusishQu9fXSvFMcrybeFF4JfVXh6'
  },
  {
    id: 'mushboomers-1190',
    name: 'Mushboomers #1190',
    image: 'https://ipfs.io/ipfs/QmeUTR9jMrQ1vSDeUXGjDufrfwe8i46UukiNZdPGMqSfAS'
  },
  {
    id: 'mushboomers-1180',
    name: 'Mushboomers #1180',
    image: 'https://ipfs.io/ipfs/QmcDuQ5XvG74fuwRctshm4Min5mJMwboVnAmvSYDTVLPxd'
  },
  {
    id: 'mushboomers-1179',
    name: 'Mushboomers #1179',
    image: 'https://ipfs.io/ipfs/QmP7MYJ6Z4kVAuDDvsCqroySiT5ZhKYu7y8f18GY4YFkYQ'
  },
  {
    id: 'mushboomers-1178',
    name: 'Mushboomers #1178',
    image: 'https://ipfs.io/ipfs/QmNUbRPfGo3QfXis3Cue1wqrtiJEZwEQQXEZt4NLi6tsag'
  },
  {
    id: 'mushboomers-1177',
    name: 'Mushboomers #1177',
    image: 'https://ipfs.io/ipfs/Qmc7H22ZRrkzymA5xxoDtpzCQ8xBnNpofTgpzdTc3EacDt'
  },
  {
    id: 'mushboomers-1176',
    name: 'Mushboomers #1176',
    image: 'https://ipfs.io/ipfs/QmRHjg5UxLKMsW4Yc5jyDKDRwkbhSBp8kXpH9soFnNv8Td'
  },
  {
    id: 'mushboomers-1175',
    name: 'Mushboomers #1175',
    image: 'https://ipfs.io/ipfs/QmUpbMvj8dMWSmPAvDgJSNQFZybTni4Hyq2JabPi6bGBFh'
  },
  {
    id: 'mushboomers-1174',
    name: 'Mushboomers #1174',
    image: 'https://ipfs.io/ipfs/QmPzmokRbtvAm7cFJqYGFgbXhLezzZabDs4zXM9gpWH3RJ'
  },
  {
    id: 'mushboomers-1173',
    name: 'Mushboomers #1173',
    image: 'https://ipfs.io/ipfs/QmdSCd67CzoJDhabNQeu8CQzMTbTpSfpp2YvSNh6CJVbEV'
  },
  {
    id: 'mushboomers-1172',
    name: 'Mushboomers #1172',
    image: 'https://ipfs.io/ipfs/QmaNuMnm13rgZNAinAp6PUiT8pz7ex4r4KdHxxSH1wJdUP'
  },
  {
    id: 'mushboomers-1171',
    name: 'Mushboomers #1171',
    image: 'https://ipfs.io/ipfs/QmZXTLrCZx59o1rJbXb4vtvGYbZcQiKA3A5oPzn23Rx2fZ'
  },
  {
    id: 'mushboomers-1170',
    name: 'Mushboomers #1170',
    image: 'https://ipfs.io/ipfs/QmNdxrcgbBW4rshGjp8dhhGojiJ1aXMPbzGUJTj7Voy2Z1'
  },
  {
    id: 'mushboomers-1169',
    name: 'Mushboomers #1169',
    image: 'https://ipfs.io/ipfs/QmXKCX2QxnbPY8xs7HZxVXqRdRfq8qRrVA2svCT4JpCxA2'
  },
  {
    id: 'mushboomers-1168',
    name: 'Mushboomers #1168',
    image: 'https://ipfs.io/ipfs/QmVdmZ2C9W5FQ6FmR5xC4CoK1vqm8eM24vH9CKZHCNkYFh'
  },
  {
    id: 'mushboomers-1167',
    name: 'Mushboomers #1167',
    image: 'https://ipfs.io/ipfs/QmdLLcngq19nBH2icj66Fsa5tUhmKhsJW8YpE3JixkqtX5'
  },
  {
    id: 'mushboomers-1166',
    name: 'Mushboomers #1166',
    image: 'https://ipfs.io/ipfs/QmZucoiSbubQGhMdXQvGUjdV8VFJXsRvf4GvfURfojgcEp'
  },
  {
    id: 'mushboomers-1165',
    name: 'Mushboomers #1165',
    image: 'https://ipfs.io/ipfs/QmZVwVoSKT44VGFyZ7dgV1nDNBDBEcP8hFNsnPVCr1JXjT'
  },
  {
    id: 'mushboomers-1164',
    name: 'Mushboomers #1164',
    image: 'https://ipfs.io/ipfs/QmdA6DRsAokTMp1rKC6ULZCZn81dtsCq7i9EcYk92nKcD7'
  },
  {
    id: 'mushboomers-1163',
    name: 'Mushboomers #1163',
    image: 'https://ipfs.io/ipfs/QmP4LPLBs3JzVG3q5GsuuBYafyfuEtE5xET3EpKLpPRkD9'
  },
  {
    id: 'mushboomers-1162',
    name: 'Mushboomers #1162',
    image: 'https://ipfs.io/ipfs/QmNYospt4gHXx6ohf78rDz6NZt1bVUMDuGBQGrARAwuCHH'
  },
  {
    id: 'mushboomers-1161',
    name: 'Mushboomers #1161',
    image: 'https://ipfs.io/ipfs/QmVfLZhLfVhtPJC9jAVvsVA7wtrRzKjDBCAP4mCd4mxJcb'
  },
  {
    id: 'mushboomers-1160',
    name: 'Mushboomers #1160',
    image: 'https://ipfs.io/ipfs/QmYHHf2YyDDwuqSkahC5b3zizRFtLQ8h25Z4gspwvZFdcz'
  },
  {
    id: 'mushboomers-1159',
    name: 'Mushboomers #1159',
    image: 'https://ipfs.io/ipfs/QmewiwJTdDEvtcH2qSfhnRtK8yWrDfXkNcxgMG9YZZKWDq'
  },
  {
    id: 'mushboomers-1158',
    name: 'Mushboomers #1158',
    image: 'https://ipfs.io/ipfs/Qmccp6CWNRp7wbDbsMb44RCjUYuTgkgAx4gKo2GhF9WXap'
  },
  {
    id: 'mushboomers-1157',
    name: 'Mushboomers #1157',
    image: 'https://ipfs.io/ipfs/QmbRaM1paYpFPUEXYd63UXZLC4UHtGjYPsWUkUyFSLgbcd'
  },
  {
    id: 'mushboomers-1156',
    name: 'Mushboomers #1156',
    image: 'https://ipfs.io/ipfs/QmcXfrkV56qtMjhTjqXYv7U4BK59AetXVUnY2LX75Dzu2D'
  },
  {
    id: 'mushboomers-1155',
    name: 'Mushboomers #1155',
    image: 'https://ipfs.io/ipfs/Qmb715jYNj6R8zZg5hgQGQFHVEQgsnq1CvTGN6nUbnhNGw'
  },
  {
    id: 'mushboomers-1154',
    name: 'Mushboomers #1154',
    image: 'https://ipfs.io/ipfs/Qmet2WGGpXhnr5h92AvTdK7ixZFymhDcBgjyFKSuiGkFmR'
  },
  {
    id: 'mushboomers-1153',
    name: 'Mushboomers #1153',
    image: 'https://ipfs.io/ipfs/QmVy8jEjEGdUrA4Ud2nLjjukxKpgZYuqAtyt5G5m9L1N2m'
  },
  {
    id: 'mushboomers-1152',
    name: 'Mushboomers #1152',
    image: 'https://ipfs.io/ipfs/QmeqaTM47VfP6KchyAjQAHMQtBT36jv7dpjRRBWfEVwgAk'
  },
  {
    id: 'mushboomers-1151',
    name: 'Mushboomers #1151',
    image: 'https://ipfs.io/ipfs/QmSsTMroZCRuSuMmJ2WLaPokwGQQcZ4f4NVu5WRzKXqxKV'
  },
  {
    id: 'mushboomers-1150',
    name: 'Mushboomers #1150',
    image: 'https://ipfs.io/ipfs/QmQNNT3qdNQpky4QZD741iPbKupPxkzLCaKCiYAvo91zqK'
  },
  {
    id: 'mushboomers-1149',
    name: 'Mushboomers #1149',
    image: 'https://ipfs.io/ipfs/QmZ3CBNchzYsrTcGVySiTj5PwwT4AxR3WkaEDQhk1z8zH9'
  },
  {
    id: 'mushboomers-1148',
    name: 'Mushboomers #1148',
    image: 'https://ipfs.io/ipfs/QmTxohtMnLzDzxQSLh5Ab8DVZRZ6X8Awz1grrPzygxwm9H'
  },
  {
    id: 'mushboomers-1147',
    name: 'Mushboomers #1147',
    image: 'https://ipfs.io/ipfs/QmbD4WCGJGjcKhpiyF1QVePeC9VFtRxRWBr1WCyjEA34uP'
  },
  {
    id: 'mushboomers-1146',
    name: 'Mushboomers #1146',
    image: 'https://ipfs.io/ipfs/QmVgHk9dm29VrGkBhL8YRzrEGfL31hVGfPKiqEw6JLNMa1'
  },
  {
    id: 'mushboomers-1145',
    name: 'Mushboomers #1145',
    image: 'https://ipfs.io/ipfs/QmZZr3zMMdc2hW7bt6nJxieRZmpx7r6xkLM1qYKxtwE8B2'
  },
  {
    id: 'mushboomers-1144',
    name: 'Mushboomers #1144',
    image: 'https://ipfs.io/ipfs/QmRmKtEFTS1AvpvRxeDa2FYLo6vZ7hzheZjN3xpkSQvLTE'
  },
  {
    id: 'mushboomers-1143',
    name: 'Mushboomers #1143',
    image: 'https://ipfs.io/ipfs/QmZmouvm1iLWLR1EMjbe5RgY8wSYNLt5BgvRBnBfd9Ke6j'
  },
  {
    id: 'mushboomers-1142',
    name: 'Mushboomers #1142',
    image: 'https://ipfs.io/ipfs/QmYYPN4m1upA11mpxHqp3J4J6hPTTzue5q43swXPMyozBH'
  },
  {
    id: 'mushboomers-1141',
    name: 'Mushboomers #1141',
    image: 'https://ipfs.io/ipfs/QmQLMtnVrPQX5Qp3u2QtvL8oNHzUFrix3KJstV5TVSabgn'
  },
  {
    id: 'mushboomers-1140',
    name: 'Mushboomers #1140',
    image: 'https://ipfs.io/ipfs/QmPnZo9ak5TrtycnajKaHpajiodcRzS7QuLtSo9gw7iCGb'
  }
         ];
        actualNftData = mushboomersNFTs.find(nft => nft.id.toString() === itemId);  
       
            } else if (collectionId === 'digidaigaku') {
        const digidaigakuNFTs = [
        
  {
    id: 'digidaigaku-genesis-1909',
    name: 'DigiDaigaku Genesis #1909',
    image: 'https://digidaigaku.com/image/1909.png'
  },
  {
    id: 'digidaigaku-genesis-712',
    name: 'DigiDaigaku Genesis #712',
    image: 'https://digidaigaku.com/image/712.png'
  },
  {
    id: 'digidaigaku-genesis-710',
    name: 'DigiDaigaku Genesis #710',
    image: 'https://digidaigaku.com/image/710.png'
  },
  {
    id: 'digidaigaku-genesis-709',
    name: 'DigiDaigaku Genesis #709',
    image: 'https://digidaigaku.com/image/709.png'
  },
  {
    id: 'digidaigaku-genesis-711',
    name: 'DigiDaigaku Genesis #711',
    image: 'https://digidaigaku.com/image/711.png'
  },
  {
    id: 'digidaigaku-genesis-708',
    name: 'DigiDaigaku Genesis #708',
    image: 'https://digidaigaku.com/image/708.png'
  },
  {
    id: 'digidaigaku-genesis-706',
    name: 'DigiDaigaku Genesis #706',
    image: 'https://digidaigaku.com/image/706.png'
  },
  {
    id: 'digidaigaku-genesis-2014',
    name: 'DigiDaigaku Genesis #2014',
    image: 'https://digidaigaku.com/image/2014.png'
  },
  {
    id: 'digidaigaku-genesis-1971',
    name: 'DigiDaigaku Genesis #1971',
    image: 'https://digidaigaku.com/image/1971.png'
  },
  {
    id: 'digidaigaku-genesis-2020',
    name: 'DigiDaigaku Genesis #2020',
    image: 'https://digidaigaku.com/image/2020.png'
  },
  {
    id: 'digidaigaku-genesis-2002',
    name: 'DigiDaigaku Genesis #2002',
    image: 'https://digidaigaku.com/image/2002.png'
  },
  {
    id: 'digidaigaku-genesis-2018',
    name: 'DigiDaigaku Genesis #2018',
    image: 'https://digidaigaku.com/image/2018.png'
  },
  {
    id: 'digidaigaku-genesis-2010',
    name: 'DigiDaigaku Genesis #2010',
    image: 'https://digidaigaku.com/image/2010.png'
  },
  {
    id: 'digidaigaku-genesis-1995',
    name: 'DigiDaigaku Genesis #1995',
    image: 'https://digidaigaku.com/image/1995.png'
  },
  {
    id: 'digidaigaku-genesis-2016',
    name: 'DigiDaigaku Genesis #2016',
    image: 'https://digidaigaku.com/image/2016.png'
  },
  {
    id: 'digidaigaku-genesis-2012',
    name: 'DigiDaigaku Genesis #2012',
    image: 'https://digidaigaku.com/image/2012.png'
  },
  {
    id: 'digidaigaku-genesis-2022',
    name: 'DigiDaigaku Genesis #2022',
    image: 'https://digidaigaku.com/image/2022.png'
  },
  {
    id: 'digidaigaku-genesis-2021',
    name: 'DigiDaigaku Genesis #2021',
    image: 'https://digidaigaku.com/image/2021.png'
  },
  {
    id: 'digidaigaku-genesis-2015',
    name: 'DigiDaigaku Genesis #2015',
    image: 'https://digidaigaku.com/image/2015.png'
  },
  {
    id: 'digidaigaku-genesis-2019',
    name: 'DigiDaigaku Genesis #2019',
    image: 'https://digidaigaku.com/image/2019.png'
  },
  {
    id: 'digidaigaku-genesis-2009',
    name: 'DigiDaigaku Genesis #2009',
    image: 'https://digidaigaku.com/image/2009.png'
  },
  {
    id: 'digidaigaku-genesis-2006',
    name: 'DigiDaigaku Genesis #2006',
    image: 'https://digidaigaku.com/image/2006.png'
  },
  {
    id: 'digidaigaku-genesis-2017',
    name: 'DigiDaigaku Genesis #2017',
    image: 'https://digidaigaku.com/image/2017.png'
  },
  {
    id: 'digidaigaku-genesis-2013',
    name: 'DigiDaigaku Genesis #2013',
    image: 'https://digidaigaku.com/image/2013.png'
  },
  {
    id: 'digidaigaku-genesis-2011',
    name: 'DigiDaigaku Genesis #2011',
    image: 'https://digidaigaku.com/image/2011.png'
  },
  {
    id: 'digidaigaku-genesis-2007',
    name: 'DigiDaigaku Genesis #2007',
    image: 'https://digidaigaku.com/image/2007.png'
  },
  {
    id: 'digidaigaku-genesis-1985',
    name: 'DigiDaigaku Genesis #1985',
    image: 'https://digidaigaku.com/image/1985.png'
  },
  {
    id: 'digidaigaku-genesis-1994',
    name: 'DigiDaigaku Genesis #1994',
    image: 'https://digidaigaku.com/image/1994.png'
  },
  {
    id: 'digidaigaku-genesis-2001',
    name: 'DigiDaigaku Genesis #2001',
    image: 'https://digidaigaku.com/image/2001.png'
  },
  {
    id: 'digidaigaku-genesis-1978',
    name: 'DigiDaigaku Genesis #1978',
    image: 'https://digidaigaku.com/image/1978.png'
  },
  {
    id: 'digidaigaku-genesis-1998',
    name: 'DigiDaigaku Genesis #1998',
    image: 'https://digidaigaku.com/image/1998.png'
  },
  {
    id: 'digidaigaku-genesis-1984',
    name: 'DigiDaigaku Genesis #1984',
    image: 'https://digidaigaku.com/image/1984.png'
  },
  {
    id: 'digidaigaku-genesis-2003',
    name: 'DigiDaigaku Genesis #2003',
    image: 'https://digidaigaku.com/image/2003.png'
  },
  {
    id: 'digidaigaku-genesis-1992',
    name: 'DigiDaigaku Genesis #1992',
    image: 'https://digidaigaku.com/image/1992.png'
  },
  {
    id: 'digidaigaku-genesis-2000',
    name: 'DigiDaigaku Genesis #2000',
    image: 'https://digidaigaku.com/image/2000.png'
  },
  {
    id: 'digidaigaku-genesis-2008',
    name: 'DigiDaigaku Genesis #2008',
    image: 'https://digidaigaku.com/image/2008.png'
  },
  {
    id: 'digidaigaku-genesis-1965',
    name: 'DigiDaigaku Genesis #1965',
    image: 'https://digidaigaku.com/image/1965.png'
  },
  {
    id: 'digidaigaku-genesis-1997',
    name: 'DigiDaigaku Genesis #1997',
    image: 'https://digidaigaku.com/image/1997.png'
  },
  {
    id: 'digidaigaku-genesis-1990',
    name: 'DigiDaigaku Genesis #1990',
    image: 'https://digidaigaku.com/image/1990.png'
  },
  {
    id: 'digidaigaku-genesis-1999',
    name: 'DigiDaigaku Genesis #1999',
    image: 'https://digidaigaku.com/image/1999.png'
  },
  {
    id: 'digidaigaku-genesis-2005',
    name: 'DigiDaigaku Genesis #2005',
    image: 'https://digidaigaku.com/image/2005.png'
  },
  {
    id: 'digidaigaku-genesis-1970',
    name: 'DigiDaigaku Genesis #1970',
    image: 'https://digidaigaku.com/image/1970.png'
  },
  {
    id: 'digidaigaku-genesis-1979',
    name: 'DigiDaigaku Genesis #1979',
    image: 'https://digidaigaku.com/image/1979.png'
  },
  {
    id: 'digidaigaku-genesis-1991',
    name: 'DigiDaigaku Genesis #1991',
    image: 'https://digidaigaku.com/image/1991.png'
  },
  {
    id: 'digidaigaku-genesis-2004',
    name: 'DigiDaigaku Genesis #2004',
    image: 'https://digidaigaku.com/image/2004.png'
  },
  {
    id: 'digidaigaku-genesis-1987',
    name: 'DigiDaigaku Genesis #1987',
    image: 'https://digidaigaku.com/image/1987.png'
  },
  {
    id: 'digidaigaku-genesis-1974',
    name: 'DigiDaigaku Genesis #1974',
    image: 'https://digidaigaku.com/image/1974.png'
  },
  {
    id: 'digidaigaku-genesis-1980',
    name: 'DigiDaigaku Genesis #1980',
    image: 'https://digidaigaku.com/image/1980.png'
  },
  {
    id: 'digidaigaku-genesis-1989',
    name: 'DigiDaigaku Genesis #1989',
    image: 'https://digidaigaku.com/image/1989.png'
  },
  {
    id: 'digidaigaku-genesis-1986',
    name: 'DigiDaigaku Genesis #1986',
    image: 'https://digidaigaku.com/image/1986.png'
  },
  {
    id: 'digidaigaku-genesis-1996',
    name: 'DigiDaigaku Genesis #1996',
    image: 'https://digidaigaku.com/image/1996.png'
  },
  {
    id: 'digidaigaku-genesis-1993',
    name: 'DigiDaigaku Genesis #1993',
    image: 'https://digidaigaku.com/image/1993.png'
  },
  {
    id: 'digidaigaku-genesis-1988',
    name: 'DigiDaigaku Genesis #1988',
    image: 'https://digidaigaku.com/image/1988.png'
  },
  {
    id: 'digidaigaku-genesis-1982',
    name: 'DigiDaigaku Genesis #1982',
    image: 'https://digidaigaku.com/image/1982.png'
  },
  {
    id: 'digidaigaku-genesis-1969',
    name: 'DigiDaigaku Genesis #1969',
    image: 'https://digidaigaku.com/image/1969.png'
  },
  {
    id: 'digidaigaku-genesis-1983',
    name: 'DigiDaigaku Genesis #1983',
    image: 'https://digidaigaku.com/image/1983.png'
  },
  {
    id: 'digidaigaku-genesis-1972',
    name: 'DigiDaigaku Genesis #1972',
    image: 'https://digidaigaku.com/image/1972.png'
  },
  {
    id: 'digidaigaku-genesis-1981',
    name: 'DigiDaigaku Genesis #1981',
    image: 'https://digidaigaku.com/image/1981.png'
  },
  {
    id: 'digidaigaku-genesis-1976',
    name: 'DigiDaigaku Genesis #1976',
    image: 'https://digidaigaku.com/image/1976.png'
  },
  {
    id: 'digidaigaku-genesis-1968',
    name: 'DigiDaigaku Genesis #1968',
    image: 'https://digidaigaku.com/image/1968.png'
  },
  {
    id: 'digidaigaku-genesis-1973',
    name: 'DigiDaigaku Genesis #1973',
    image: 'https://digidaigaku.com/image/1973.png'
  },
  {
    id: 'digidaigaku-genesis-1960',
    name: 'DigiDaigaku Genesis #1960',
    image: 'https://digidaigaku.com/image/1960.png'
  },
  {
    id: 'digidaigaku-genesis-1975',
    name: 'DigiDaigaku Genesis #1975',
    image: 'https://digidaigaku.com/image/1975.png'
  },
  {
    id: 'digidaigaku-genesis-1962',
    name: 'DigiDaigaku Genesis #1962',
    image: 'https://digidaigaku.com/image/1962.png'
  },
  {
    id: 'digidaigaku-genesis-1963',
    name: 'DigiDaigaku Genesis #1963',
    image: 'https://digidaigaku.com/image/1963.png'
  },
  {
    id: 'digidaigaku-genesis-1977',
    name: 'DigiDaigaku Genesis #1977',
    image: 'https://digidaigaku.com/image/1977.png'
  },
  {
    id: 'digidaigaku-genesis-1959',
    name: 'DigiDaigaku Genesis #1959',
    image: 'https://digidaigaku.com/image/1959.png'
  },
  {
    id: 'digidaigaku-genesis-1961',
    name: 'DigiDaigaku Genesis #1961',
    image: 'https://digidaigaku.com/image/1961.png'
  },
  {
    id: 'digidaigaku-genesis-1958',
    name: 'DigiDaigaku Genesis #1958',
    image: 'https://digidaigaku.com/image/1958.png'
  },
  {
    id: 'digidaigaku-genesis-1966',
    name: 'DigiDaigaku Genesis #1966',
    image: 'https://digidaigaku.com/image/1966.png'
  },
  {
    id: 'digidaigaku-genesis-1967',
    name: 'DigiDaigaku Genesis #1967',
    image: 'https://digidaigaku.com/image/1967.png'
  },
  {
    id: 'digidaigaku-genesis-1964',
    name: 'DigiDaigaku Genesis #1964',
    image: 'https://digidaigaku.com/image/1964.png'
  },
  {
    id: 'digidaigaku-genesis-1957',
    name: 'DigiDaigaku Genesis #1957',
    image: 'https://digidaigaku.com/image/1957.png'
  },
  {
    id: 'digidaigaku-genesis-1954',
    name: 'DigiDaigaku Genesis #1954',
    image: 'https://digidaigaku.com/image/1954.png'
  },
  {
    id: 'digidaigaku-genesis-1956',
    name: 'DigiDaigaku Genesis #1956',
    image: 'https://digidaigaku.com/image/1956.png'
  },
  {
    id: 'digidaigaku-genesis-1955',
    name: 'DigiDaigaku Genesis #1955',
    image: 'https://digidaigaku.com/image/1955.png'
  },
  {
    id: 'digidaigaku-genesis-1953',
    name: 'DigiDaigaku Genesis #1953',
    image: 'https://digidaigaku.com/image/1953.png'
  },
  {
    id: 'digidaigaku-genesis-1951',
    name: 'DigiDaigaku Genesis #1951',
    image: 'https://digidaigaku.com/image/1951.png'
  },
  {
    id: 'digidaigaku-genesis-1952',
    name: 'DigiDaigaku Genesis #1952',
    image: 'https://digidaigaku.com/image/1952.png'
  },
  {
    id: 'digidaigaku-genesis-1950',
    name: 'DigiDaigaku Genesis #1950',
    image: 'https://digidaigaku.com/image/1950.png'
  },
  {
    id: 'digidaigaku-genesis-1949',
    name: 'DigiDaigaku Genesis #1949',
    image: 'https://digidaigaku.com/image/1949.png'
  },
  {
    id: 'digidaigaku-genesis-1945',
    name: 'DigiDaigaku Genesis #1945',
    image: 'https://digidaigaku.com/image/1945.png'
  },
  {
    id: 'digidaigaku-genesis-1946',
    name: 'DigiDaigaku Genesis #1946',
    image: 'https://digidaigaku.com/image/1946.png'
  },
  {
    id: 'digidaigaku-genesis-1948',
    name: 'DigiDaigaku Genesis #1948',
    image: 'https://digidaigaku.com/image/1948.png'
  },
  {
    id: 'digidaigaku-genesis-1942',
    name: 'DigiDaigaku Genesis #1942',
    image: 'https://digidaigaku.com/image/1942.png'
  },
  {
    id: 'digidaigaku-genesis-1944',
    name: 'DigiDaigaku Genesis #1944',
    image: 'https://digidaigaku.com/image/1944.png'
  },
  {
    id: 'digidaigaku-genesis-1943',
    name: 'DigiDaigaku Genesis #1943',
    image: 'https://digidaigaku.com/image/1943.png'
  },
  {
    id: 'digidaigaku-genesis-1947',
    name: 'DigiDaigaku Genesis #1947',
    image: 'https://digidaigaku.com/image/1947.png'
  },
  {
    id: 'digidaigaku-genesis-1899',
    name: 'DigiDaigaku Genesis #1899',
    image: 'https://digidaigaku.com/image/1899.png'
  },
  {
    id: 'digidaigaku-genesis-1938',
    name: 'DigiDaigaku Genesis #1938',
    image: 'https://digidaigaku.com/image/1938.png'
  },
  {
    id: 'digidaigaku-genesis-1933',
    name: 'DigiDaigaku Genesis #1933',
    image: 'https://digidaigaku.com/image/1933.png'
  },
  {
    id: 'digidaigaku-genesis-1941',
    name: 'DigiDaigaku Genesis #1941',
    image: 'https://digidaigaku.com/image/1941.png'
  },
  {
    id: 'digidaigaku-genesis-1920',
    name: 'DigiDaigaku Genesis #1920',
    image: 'https://digidaigaku.com/image/1920.png'
  },
  {
    id: 'digidaigaku-genesis-1927',
    name: 'DigiDaigaku Genesis #1927',
    image: 'https://digidaigaku.com/image/1927.png'
  },
  {
    id: 'digidaigaku-genesis-1925',
    name: 'DigiDaigaku Genesis #1925',
    image: 'https://digidaigaku.com/image/1925.png'
  },
  {
    id: 'digidaigaku-genesis-1940',
    name: 'DigiDaigaku Genesis #1940',
    image: 'https://digidaigaku.com/image/1940.png'
  },
  {
    id: 'digidaigaku-genesis-1934',
    name: 'DigiDaigaku Genesis #1934',
    image: 'https://digidaigaku.com/image/1934.png'
  },
  {
    id: 'digidaigaku-genesis-1936',
    name: 'DigiDaigaku Genesis #1936',
    image: 'https://digidaigaku.com/image/1936.png'
  },
  {
    id: 'digidaigaku-genesis-1935',
    name: 'DigiDaigaku Genesis #1935',
    image: 'https://digidaigaku.com/image/1935.png'
  },
  {
    id: 'digidaigaku-genesis-1924',
    name: 'DigiDaigaku Genesis #1924',
    image: 'https://digidaigaku.com/image/1924.png'
  }
             ];
        actualNftData = digidaigakuNFTs.find(nft => nft.id.toString() === itemId);  
            
            } else if (collectionId === 'my-pet-hooligan') {
        const mypethooliganNFTs = [
            
  {
    id: 'my-pet-hooligan-8731',
    name: 'Hooligan #8731',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8731.png'
  },
  {
    id: 'my-pet-hooligan-8730',
    name: 'Hooligan #8730',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8730.png'
  },
  {
    id: 'my-pet-hooligan-8729',
    name: 'Hooligan #8729',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8729.png'
  },
  {
    id: 'my-pet-hooligan-8728',
    name: 'Hooligan #8728',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8728.png'
  },
  {
    id: 'my-pet-hooligan-8727',
    name: 'Hooligan #8727',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8727.png'
  },
  {
    id: 'my-pet-hooligan-8726',
    name: 'Hooligan #8726',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8726.png'
  },
  {
    id: 'my-pet-hooligan-8725',
    name: 'Hooligan #8725',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8725.png'
  },
  {
    id: 'my-pet-hooligan-8724',
    name: 'Hooligan #8724',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8724.png'
  },
  {
    id: 'my-pet-hooligan-8723',
    name: 'Hooligan #8723',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8723.png'
  },
  {
    id: 'my-pet-hooligan-8722',
    name: 'Hooligan #8722',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8722.png'
  },
  {
    id: 'my-pet-hooligan-8721',
    name: 'Hooligan #8721',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8721.png'
  },
  {
    id: 'my-pet-hooligan-8720',
    name: 'Hooligan #8720',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8720.png'
  },
  {
    id: 'my-pet-hooligan-8719',
    name: 'Hooligan #8719',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8719.png'
  },
  {
    id: 'my-pet-hooligan-8718',
    name: 'Hooligan #8718',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8718.png'
  },
  {
    id: 'my-pet-hooligan-8717',
    name: 'Hooligan #8717',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8717.png'
  },
  {
    id: 'my-pet-hooligan-8716',
    name: 'Hooligan #8716',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8716.png'
  },
  {
    id: 'my-pet-hooligan-8715',
    name: 'Hooligan #8715',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8715.png'
  },
  {
    id: 'my-pet-hooligan-8714',
    name: 'Hooligan #8714',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8714.png'
  },
  {
    id: 'my-pet-hooligan-8713',
    name: 'Hooligan #8713',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8713.png'
  },
  {
    id: 'my-pet-hooligan-8712',
    name: 'Hooligan #8712',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8712.png'
  },
  {
    id: 'my-pet-hooligan-8711',
    name: 'Hooligan #8711',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8711.png'
  },
  {
    id: 'my-pet-hooligan-8710',
    name: 'Hooligan #8710',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8710.png'
  },
  {
    id: 'my-pet-hooligan-8709',
    name: 'Hooligan #8709',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8709.png'
  },
  {
    id: 'my-pet-hooligan-8708',
    name: 'Hooligan #8708',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8708.png'
  },
  {
    id: 'my-pet-hooligan-8707',
    name: 'Hooligan #8707',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8707.png'
  },
  {
    id: 'my-pet-hooligan-8706',
    name: 'Hooligan #8706',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8706.png'
  },
  {
    id: 'my-pet-hooligan-8705',
    name: 'Hooligan #8705',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8705.png'
  },
  {
    id: 'my-pet-hooligan-8704',
    name: 'Hooligan #8704',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8704.png'
  },
  {
    id: 'my-pet-hooligan-8703',
    name: 'Hooligan #8703',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8703.png'
  },
  {
    id: 'my-pet-hooligan-8702',
    name: 'Hooligan #8702',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8702.png'
  },
  {
    id: 'my-pet-hooligan-8701',
    name: 'Hooligan #8701',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8701.png'
  },
  {
    id: 'my-pet-hooligan-8700',
    name: 'Hooligan #8700',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8700.png'
  },
  {
    id: 'my-pet-hooligan-8669',
    name: 'Hooligan #8669',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8669.png'
  },
  {
    id: 'my-pet-hooligan-8699',
    name: 'Hooligan #8699',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8699.png'
  },
  {
    id: 'my-pet-hooligan-8668',
    name: 'Hooligan #8668',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8668.png'
  },
  {
    id: 'my-pet-hooligan-8698',
    name: 'Hooligan #8698',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8698.png'
  },
  {
    id: 'my-pet-hooligan-8624',
    name: 'Hooligan #8624',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8624.png'
  },
  {
    id: 'my-pet-hooligan-8667',
    name: 'Hooligan #8667',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8667.png'
  },
  {
    id: 'my-pet-hooligan-8697',
    name: 'Hooligan #8697',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8697.png'
  },
  {
    id: 'my-pet-hooligan-8623',
    name: 'Hooligan #8623',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8623.png'
  },
  {
    id: 'my-pet-hooligan-8666',
    name: 'Hooligan #8666',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8666.png'
  },
  {
    id: 'my-pet-hooligan-8696',
    name: 'Hooligan #8696',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8696.png'
  },
  {
    id: 'my-pet-hooligan-8622',
    name: 'Hooligan #8622',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8622.png'
  },
  {
    id: 'my-pet-hooligan-8665',
    name: 'Hooligan #8665',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8665.png'
  },
  {
    id: 'my-pet-hooligan-8695',
    name: 'Hooligan #8695',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8695.png'
  },
  {
    id: 'my-pet-hooligan-8664',
    name: 'Hooligan #8664',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8664.png'
  },
  {
    id: 'my-pet-hooligan-8621',
    name: 'Hooligan #8621',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8621.png'
  },
  {
    id: 'my-pet-hooligan-8694',
    name: 'Hooligan #8694',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8694.png'
  },
  {
    id: 'my-pet-hooligan-8881',
    name: 'Hooligan #8881',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8881.png'
  },
  {
    id: 'my-pet-hooligan-8841',
    name: 'Hooligan #8841',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8841.png'
  },
  {
    id: 'my-pet-hooligan-8663',
    name: 'Hooligan #8663',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8663.png'
  },
  {
    id: 'my-pet-hooligan-8620',
    name: 'Hooligan #8620',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8620.png'
  },
  {
    id: 'my-pet-hooligan-8856',
    name: 'Hooligan #8856',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8856.png'
  },
  {
    id: 'my-pet-hooligan-8693',
    name: 'Hooligan #8693',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8693.png'
  },
  {
    id: 'my-pet-hooligan-8866',
    name: 'Hooligan #8866',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8866.png'
  },
  {
    id: 'my-pet-hooligan-8806',
    name: 'Hooligan #8806',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8806.png'
  },
  {
    id: 'my-pet-hooligan-8831',
    name: 'Hooligan #8831',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8831.png'
  },
  {
    id: 'my-pet-hooligan-8871',
    name: 'Hooligan #8871',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8871.png'
  },
  {
    id: 'my-pet-hooligan-8826',
    name: 'Hooligan #8826',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8826.png'
  },
  {
    id: 'my-pet-hooligan-8766',
    name: 'Hooligan #8766',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8766.png'
  },
  {
    id: 'my-pet-hooligan-8851',
    name: 'Hooligan #8851',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8851.png'
  },
  {
    id: 'my-pet-hooligan-8816',
    name: 'Hooligan #8816',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8816.png'
  },
  {
    id: 'my-pet-hooligan-8811',
    name: 'Hooligan #8811',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8811.png'
  },
  {
    id: 'my-pet-hooligan-8876',
    name: 'Hooligan #8876',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8876.png'
  },
  {
    id: 'my-pet-hooligan-8880',
    name: 'Hooligan #8880',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8880.png'
  },
  {
    id: 'my-pet-hooligan-8776',
    name: 'Hooligan #8776',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8776.png'
  },
  {
    id: 'my-pet-hooligan-8861',
    name: 'Hooligan #8861',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8861.png'
  },
  {
    id: 'my-pet-hooligan-8886',
    name: 'Hooligan #8886',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8886.png'
  },
  {
    id: 'my-pet-hooligan-8791',
    name: 'Hooligan #8791',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8791.png'
  },
  {
    id: 'my-pet-hooligan-8771',
    name: 'Hooligan #8771',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8771.png'
  },
  {
    id: 'my-pet-hooligan-8846',
    name: 'Hooligan #8846',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8846.png'
  },
  {
    id: 'my-pet-hooligan-8840',
    name: 'Hooligan #8840',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8840.png'
  },
  {
    id: 'my-pet-hooligan-8821',
    name: 'Hooligan #8821',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8821.png'
  },
  {
    id: 'my-pet-hooligan-8662',
    name: 'Hooligan #8662',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8662.png'
  },
  {
    id: 'my-pet-hooligan-8761',
    name: 'Hooligan #8761',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8761.png'
  },
  {
    id: 'my-pet-hooligan-8619',
    name: 'Hooligan #8619',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8619.png'
  },
  {
    id: 'my-pet-hooligan-8692',
    name: 'Hooligan #8692',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8692.png'
  },
  {
    id: 'my-pet-hooligan-8855',
    name: 'Hooligan #8855',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8855.png'
  },
  {
    id: 'my-pet-hooligan-8781',
    name: 'Hooligan #8781',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8781.png'
  },
  {
    id: 'my-pet-hooligan-8786',
    name: 'Hooligan #8786',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8786.png'
  },
  {
    id: 'my-pet-hooligan-8836',
    name: 'Hooligan #8836',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8836.png'
  },
  {
    id: 'my-pet-hooligan-8865',
    name: 'Hooligan #8865',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8865.png'
  },
  {
    id: 'my-pet-hooligan-8805',
    name: 'Hooligan #8805',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8805.png'
  },
  {
    id: 'my-pet-hooligan-8746',
    name: 'Hooligan #8746',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8746.png'
  },
  {
    id: 'my-pet-hooligan-8796',
    name: 'Hooligan #8796',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8796.png'
  },
  {
    id: 'my-pet-hooligan-8756',
    name: 'Hooligan #8756',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8756.png'
  },
  {
    id: 'my-pet-hooligan-8830',
    name: 'Hooligan #8830',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8830.png'
  },
  {
    id: 'my-pet-hooligan-8801',
    name: 'Hooligan #8801',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8801.png'
  },
  {
    id: 'my-pet-hooligan-8736',
    name: 'Hooligan #8736',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8736.png'
  },
  {
    id: 'my-pet-hooligan-8741',
    name: 'Hooligan #8741',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8741.png'
  },
  {
    id: 'my-pet-hooligan-8765',
    name: 'Hooligan #8765',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8765.png'
  },
  {
    id: 'my-pet-hooligan-8850',
    name: 'Hooligan #8850',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8850.png'
  },
  {
    id: 'my-pet-hooligan-8751',
    name: 'Hooligan #8751',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8751.png'
  },
  {
    id: 'my-pet-hooligan-8875',
    name: 'Hooligan #8875',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8875.png'
  },
  {
    id: 'my-pet-hooligan-8815',
    name: 'Hooligan #8815',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8815.png'
  },
  {
    id: 'my-pet-hooligan-8810',
    name: 'Hooligan #8810',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8810.png'
  },
  {
    id: 'my-pet-hooligan-8885',
    name: 'Hooligan #8885',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8885.png'
  },
  {
    id: 'my-pet-hooligan-8825',
    name: 'Hooligan #8825',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8825.png'
  },
  {
    id: 'my-pet-hooligan-8860',
    name: 'Hooligan #8860',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8860.png'
  },
  {
    id: 'my-pet-hooligan-8775',
    name: 'Hooligan #8775',
    image: 'https://ipfs.io/ipfs/QmZqqLdyuQPvG9P3cr3AccSGJ1ivp5SRx3Ni7JmXr7CZE4/8775.png'
  }
            
             ];
        actualNftData = mypethooliganNFTs.find(nft => nft.id.toString() === itemId);  
            
                 } else if (collectionId === 'moonbirds') {
        const moonbirdsNFTs = [
            
  {
    id: 'moonbirds-9999',
    name: 'Moonbirds #9999',
    image: 'https://raw.seadn.io/files/01ee5e9b1522577c8dccfb8ee98b68ba.bin'
  },
  {
    id: 'moonbirds-9998',
    name: 'Moonbirds #9998',
    image: 'https://raw.seadn.io/files/fd30322e37d3ecba7c241051e7cd7655.bin'
  },
  {
    id: 'moonbirds-9997',
    name: 'Moonbirds #9997',
    image: 'https://raw.seadn.io/files/d5156c3d2505980aab0817519c67bc79.bin'
  },
  {
    id: 'moonbirds-9996',
    name: 'Moonbirds #9996',
    image: 'https://raw.seadn.io/files/8eb4567c936f493bd0483ef31e1265c0.bin'
  },
  {
    id: 'moonbirds-9995',
    name: 'Moonbirds #9995',
    image: 'https://raw.seadn.io/files/d1b7190a24697bd1c32d7b0bcb5d2670.bin'
  },
  {
    id: 'moonbirds-9994',
    name: 'Moonbirds #9994',
    image: 'https://raw.seadn.io/files/ec3bdd436a2548836e574496cc34dbb5.bin'
  },
  {
    id: 'moonbirds-9993',
    name: 'Moonbirds #9993',
    image: 'https://raw.seadn.io/files/7d3cc122906283267c896b0091c66a36.bin'
  },
  {
    id: 'moonbirds-9992',
    name: 'Moonbirds #9992',
    image: 'https://raw.seadn.io/files/2d2a497c68330be3a8608d69189385eb.bin'
  },
  {
    id: 'moonbirds-9991',
    name: 'Moonbirds #9991',
    image: 'https://raw.seadn.io/files/8e454a7cb4d88279ac8d1d18df9d190d.bin'
  },
  {
    id: 'moonbirds-9990',
    name: 'Moonbirds #9990',
    image: 'https://raw.seadn.io/files/aff357ba79e6dda6e48cc5744bd00bfa.bin'
  },
  {
    id: 'moonbirds-9989',
    name: 'Moonbirds #9989',
    image: 'https://raw.seadn.io/files/222eb625506d9c25f864e02d50f6bb5e.bin'
  },
  {
    id: 'moonbirds-9988',
    name: 'Moonbirds #9988',
    image: 'https://raw.seadn.io/files/11a7b9b9f395876b7b87421d552abe7c.bin'
  },
  {
    id: 'moonbirds-9987',
    name: 'Moonbirds #9987',
    image: 'https://raw.seadn.io/files/3231b4f92361d0a56128d5433e5e6147.bin'
  },
  {
    id: 'moonbirds-9986',
    name: 'Moonbirds #9986',
    image: 'https://raw.seadn.io/files/d413397780c6bca062cb707aa94d1d50.bin'
  },
  {
    id: 'moonbirds-9985',
    name: 'Moonbirds #9985',
    image: 'https://raw.seadn.io/files/4bbe837ba74e546a1959589a5e7324c9.bin'
  },
  {
    id: 'moonbirds-9984',
    name: 'Moonbirds #9984',
    image: 'https://raw.seadn.io/files/48d04b5760cc409b0fd8f67baa1932ac.bin'
  },
  {
    id: 'moonbirds-9983',
    name: 'Moonbirds #9983',
    image: 'https://raw.seadn.io/files/28e7e97a2d28f9e53a445e6ef490e9ec.bin'
  },
  {
    id: 'moonbirds-9982',
    name: 'Moonbirds #9982',
    image: 'https://raw.seadn.io/files/856c6bddcc115954bd433f5784664dba.bin'
  },
  {
    id: 'moonbirds-9981',
    name: 'Moonbirds #9981',
    image: 'https://raw.seadn.io/files/e4a543b2ab5e125351d08103ccbc7123.bin'
  },
  {
    id: 'moonbirds-9980',
    name: 'Moonbirds #9980',
    image: 'https://raw.seadn.io/files/3aff96386d80a33fda670312bd9d51bf.bin'
  },
  {
    id: 'moonbirds-9979',
    name: 'Moonbirds #9979',
    image: 'https://raw.seadn.io/files/e4d0c3b47016ff6809fb9839610cfb64.bin'
  },
  {
    id: 'moonbirds-9978',
    name: 'Moonbirds #9978',
    image: 'https://raw.seadn.io/files/3b3f14dcd45c99faa71d68650be00ab0.bin'
  },
  {
    id: 'moonbirds-9977',
    name: 'Moonbirds #9977',
    image: 'https://raw.seadn.io/files/2c42f016e7aa30c689473f606e81f770.bin'
  },
  {
    id: 'moonbirds-9976',
    name: 'Moonbirds #9976',
    image: 'https://raw.seadn.io/files/7821451455784f55842eb776c4b8ebb8.bin'
  },
  {
    id: 'moonbirds-9975',
    name: 'Moonbirds #9975',
    image: 'https://raw.seadn.io/files/ee6206c8344a236001f6c56a68f1641f.bin'
  },
  {
    id: 'moonbirds-9974',
    name: 'Moonbirds #9974',
    image: 'https://raw.seadn.io/files/9237026b45a2031634245ce1842bed64.bin'
  },
  {
    id: 'moonbirds-9973',
    name: 'Moonbirds #9973',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9973.png'
  },
  {
    id: 'moonbirds-9972',
    name: 'Moonbirds #9972',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9972.png'
  },
  {
    id: 'moonbirds-9971',
    name: 'Moonbirds #9971',
    image: 'https://raw.seadn.io/files/4fb2790580f006a8c2bdb701d8b52e3b.bin'
  },
  {
    id: 'moonbirds-9970',
    name: 'Moonbirds #9970',
    image: 'https://raw.seadn.io/files/80a34004b7884c0085b5aad0793073c9.bin'
  },
  {
    id: 'moonbirds-9969',
    name: 'Moonbirds #9969',
    image: 'https://raw.seadn.io/files/d4506a9e4cb93b241fb8176b1cac2384.bin'
  },
  {
    id: 'moonbirds-9968',
    name: 'Moonbirds #9968',
    image: 'https://raw.seadn.io/files/672674419cafcb86d658b715abac17bd.bin'
  },
  {
    id: 'moonbirds-9967',
    name: 'Moonbirds #9967',
    image: 'https://raw.seadn.io/files/86f2098435f1a99634775f40e8401a86.bin'
  },
  {
    id: 'moonbirds-9966',
    name: 'Moonbirds #9966',
    image: 'https://raw.seadn.io/files/92bcd5a9eef2fc2a0f107264ad085c3d.bin'
  },
  {
    id: 'moonbirds-9965',
    name: 'Moonbirds #9965',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9965.png'
  },
  {
    id: 'moonbirds-9964',
    name: 'Moonbirds #9964',
    image: 'https://raw.seadn.io/files/429c9d13f357adf956a6b47286d9b773.bin'
  },
  {
    id: 'moonbirds-9963',
    name: 'Moonbirds #9963',
    image: 'https://raw.seadn.io/files/0c1efbe18354955c4b7c0e70b6d9129b.bin'
  },
  {
    id: 'moonbirds-9962',
    name: 'Moonbirds #9962',
    image: 'https://raw.seadn.io/files/d2f4484a83571e5eb3591813005725bf.bin'
  },
  {
    id: 'moonbirds-9961',
    name: 'Moonbirds #9961',
    image: 'https://raw.seadn.io/files/f6b4403748d8a81a59c0fbedca29cd45.bin'
  },
  {
    id: 'moonbirds-9960',
    name: 'Moonbirds #9960',
    image: 'https://raw.seadn.io/files/81d5e2cdebca23e6e88ce1869b21c066.bin'
  },
  {
    id: 'moonbirds-9959',
    name: 'Moonbirds #9959',
    image: 'https://raw.seadn.io/files/6eb2130b3394f73de73a98ec7e855d00.bin'
  },
  {
    id: 'moonbirds-9958',
    name: 'Moonbirds #9958',
    image: 'https://raw.seadn.io/files/e96e953b506f89e4c7354a2e2107afaf.bin'
  },
  {
    id: 'moonbirds-9957',
    name: 'Moonbirds #9957',
    image: 'https://raw.seadn.io/files/801364cfd990a2f61c518625ff5c2b8e.bin'
  },
  {
    id: 'moonbirds-9956',
    name: 'Moonbirds #9956',
    image: 'https://raw.seadn.io/files/26653fd3bef061456e4e202a95760590.bin'
  },
  {
    id: 'moonbirds-9955',
    name: 'Moonbirds #9955',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9955.png'
  },
  {
    id: 'moonbirds-9954',
    name: 'Moonbirds #9954',
    image: 'https://raw.seadn.io/files/43dd92e1f1857dd1ad1701a9658a95c8.bin'
  },
  {
    id: 'moonbirds-9953',
    name: 'Moonbirds #9953',
    image: 'https://raw.seadn.io/files/5716fef916eb08d10fbb6ad33eb64176.bin'
  },
  {
    id: 'moonbirds-9952',
    name: 'Moonbirds #9952',
    image: 'https://raw.seadn.io/files/76fc12ac06a9a8673819598b750430fb.bin'
  },
  {
    id: 'moonbirds-9951',
    name: 'Moonbirds #9951',
    image: 'https://raw.seadn.io/files/0e4afdebc1ce3ef2f7cfa77532ba9dc1.bin'
  },
  {
    id: 'moonbirds-9950',
    name: 'Moonbirds #9950',
    image: 'https://raw.seadn.io/files/538988e61786a64ea2fab40bbb65ab2a.bin'
  },
  {
    id: 'moonbirds-9949',
    name: 'Moonbirds #9949',
    image: 'https://raw.seadn.io/files/b27ba807974df605454f760b29e67772.bin'
  },
  {
    id: 'moonbirds-9948',
    name: 'Moonbirds #9948',
    image: 'https://raw.seadn.io/files/620d159747ee2200942cc3fd3658d7b4.bin'
  },
  {
    id: 'moonbirds-9947',
    name: 'Moonbirds #9947',
    image: 'https://raw.seadn.io/files/bfa8aa29ce4c7656c4194d5d499a9982.bin'
  },
  {
    id: 'moonbirds-9946',
    name: 'Moonbirds #9946',
    image: 'https://raw.seadn.io/files/b778df0cd96928809d4de4c56abb8ea0.bin'
  },
  {
    id: 'moonbirds-9945',
    name: 'Moonbirds #9945',
    image: 'https://raw.seadn.io/files/6c8a0f85e156343aa28624f50f01eef0.bin'
  },
  {
    id: 'moonbirds-9944',
    name: 'Moonbirds #9944',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9944.png'
  },
  {
    id: 'moonbirds-9943',
    name: 'Moonbirds #9943',
    image: 'https://raw.seadn.io/files/e5f274f19da036c566899d833ba2d989.bin'
  },
  {
    id: 'moonbirds-9942',
    name: 'Moonbirds #9942',
    image: 'https://raw.seadn.io/files/d97bc98d2298ebdac6da8dbbaacc36b6.bin'
  },
  {
    id: 'moonbirds-9941',
    name: 'Moonbirds #9941',
    image: 'https://raw.seadn.io/files/e9fd480292151928b0178d6217e006d5.bin'
  },
  {
    id: 'moonbirds-9940',
    name: 'Moonbirds #9940',
    image: 'https://raw.seadn.io/files/7037a1260fa8b062793da83db3b29a75.bin'
  },
  {
    id: 'moonbirds-9939',
    name: 'Moonbirds #9939',
    image: 'https://raw.seadn.io/files/fad86e31c3c374eaf9236d77d0783dc8.bin'
  },
  {
    id: 'moonbirds-9938',
    name: 'Moonbirds #9938',
    image: 'https://raw.seadn.io/files/beabcbabb469ffdb27bda9c1c490deb6.bin'
  },
  {
    id: 'moonbirds-9937',
    name: 'Moonbirds #9937',
    image: 'https://raw.seadn.io/files/51b318a362e1389b729fa311db8fed95.bin'
  },
  {
    id: 'moonbirds-9936',
    name: 'Moonbirds #9936',
    image: 'https://raw.seadn.io/files/fe9afb7b83ef33876fcf5fc104a76c07.bin'
  },
  {
    id: 'moonbirds-9935',
    name: 'Moonbirds #9935',
    image: 'https://raw.seadn.io/files/95d09546ef89c807a96af39dbb95a88b.bin'
  },
  {
    id: 'moonbirds-9934',
    name: 'Moonbirds #9934',
    image: 'https://raw.seadn.io/files/3974974127c6c8d8cc4d9b181b78a5dd.bin'
  },
  {
    id: 'moonbirds-9933',
    name: 'Moonbirds #9933',
    image: 'https://raw.seadn.io/files/cf47da8aa897a7e40e7d4b27a61696b7.bin'
  },
  {
    id: 'moonbirds-9932',
    name: 'Moonbirds #9932',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9932.png'
  },
  {
    id: 'moonbirds-9931',
    name: 'Moonbirds #9931',
    image: 'https://raw.seadn.io/files/f019cbaab21e8a3aecad677a3c26b64c.bin'
  },
  {
    id: 'moonbirds-9930',
    name: 'Moonbirds #9930',
    image: 'https://raw.seadn.io/files/fcc534aba8f41d0dad8f24c3703751f3.bin'
  },
  {
    id: 'moonbirds-9929',
    name: 'Moonbirds #9929',
    image: 'https://raw.seadn.io/files/694761adef64115f5f4b702cecbec798.bin'
  },
  {
    id: 'moonbirds-9928',
    name: 'Moonbirds #9928',
    image: 'https://raw.seadn.io/files/81e7f56e596806922d195e5c808ab18a.bin'
  },
  {
    id: 'moonbirds-9927',
    name: 'Moonbirds #9927',
    image: 'https://raw.seadn.io/files/780e3398c3f2dbd0aef4d9fb08872f57.bin'
  },
  {
    id: 'moonbirds-9926',
    name: 'Moonbirds #9926',
    image: 'https://raw.seadn.io/files/1981f55de072bb7df25fedc0c264e8d0.bin'
  },
  {
    id: 'moonbirds-9925',
    name: 'Moonbirds #9925',
    image: 'https://raw.seadn.io/files/13b0dd97017aa4413e774395de288781.bin'
  },
  {
    id: 'moonbirds-9924',
    name: 'Moonbirds #9924',
    image: 'https://raw.seadn.io/files/f3f302128dc8ffca2d3105b7c74386ee.bin'
  },
  {
    id: 'moonbirds-9923',
    name: 'Moonbirds #9923',
    image: 'https://raw.seadn.io/files/23e49ef8e3383cff64c4a6782aef48e8.bin'
  },
  {
    id: 'moonbirds-9922',
    name: 'Moonbirds #9922',
    image: 'https://raw.seadn.io/files/c8670ade95ed74ddaec4644e7d1e678a.bin'
  },
  {
    id: 'moonbirds-9921',
    name: 'Moonbirds #9921',
    image: 'https://raw.seadn.io/files/ce4f2e48945431802ce32ea3fd8d4892.bin'
  },
  {
    id: 'moonbirds-9920',
    name: 'Moonbirds #9920',
    image: 'https://raw.seadn.io/files/48586381fb377c0a719efbb954134827.bin'
  },
  {
    id: 'moonbirds-9919',
    name: 'Moonbirds #9919',
    image: 'https://raw.seadn.io/files/350d7aee2fe59f4d10041142b197d992.bin'
  },
  {
    id: 'moonbirds-9918',
    name: 'Moonbirds #9918',
    image: 'https://raw.seadn.io/files/41be95c611a4da6ce952cf30d8eb5eb2.bin'
  },
  {
    id: 'moonbirds-9917',
    name: 'Moonbirds #9917',
    image: 'https://raw.seadn.io/files/eb246da716f4d1f5d5a53e37a256dcfc.bin'
  },
  {
    id: 'moonbirds-9916',
    name: 'Moonbirds #9916',
    image: 'https://raw.seadn.io/files/0837c9abc7fd63b605aa62941b7a1175.bin'
  },
  {
    id: 'moonbirds-9915',
    name: 'Moonbirds #9915',
    image: 'https://raw.seadn.io/files/be72e689cd466300fc3e9cd33508f3e7.bin'
  },
  {
    id: 'moonbirds-9914',
    name: 'Moonbirds #9914',
    image: 'https://raw.seadn.io/files/fca09ea371d3e6a26dcf2ecab72a4697.bin'
  },
  {
    id: 'moonbirds-9913',
    name: 'Moonbirds #9913',
    image: 'https://raw.seadn.io/files/706d7d2f70f4c1100f84f90dac2f74f2.bin'
  },
  {
    id: 'moonbirds-9912',
    name: 'Moonbirds #9912',
    image: 'https://raw.seadn.io/files/05f751eca338844582471da3481b215d.bin'
  },
  {
    id: 'moonbirds-9911',
    name: 'Moonbirds #9911',
    image: 'https://raw.seadn.io/files/9e994b2b9d7d0afa2d7eb932190e28de.bin'
  },
  {
    id: 'moonbirds-9910',
    name: 'Moonbirds #9910',
    image: 'https://raw.seadn.io/files/bfdb2728197d3b1f33bdc2ca4f293285.bin'
  },
  {
    id: 'moonbirds-9909',
    name: 'Moonbirds #9909',
    image: 'https://raw.seadn.io/files/5c04d601749ffb75a31ec5e3c743c5da.bin'
  },
  {
    id: 'moonbirds-9908',
    name: 'Moonbirds #9908',
    image: 'https://raw.seadn.io/files/a5134ba1344112ac954fed1d8e8d25ec.bin'
  },
  {
    id: 'moonbirds-9907',
    name: 'Moonbirds #9907',
    image: 'https://raw.seadn.io/files/79a50e4a901e7c0408d98079821412de.bin'
  },
  {
    id: 'moonbirds-9906',
    name: 'Moonbirds #9906',
    image: 'https://raw.seadn.io/files/51f391118b495317c03dbd2029d34b3b.bin'
  },
  {
    id: 'moonbirds-9905',
    name: 'Moonbirds #9905',
    image: 'https://collection-assets.proof.xyz/moonbirds/images/9905.png'
  },
  {
    id: 'moonbirds-9904',
    name: 'Moonbirds #9904',
    image: 'https://raw.seadn.io/files/36513eda4bca40204410ffba340afcb1.bin'
  },
  {
    id: 'moonbirds-9903',
    name: 'Moonbirds #9903',
    image: 'https://raw.seadn.io/files/e8f76bac97e2c06cde7d29d9ef73404d.bin'
  },
  {
    id: 'moonbirds-9902',
    name: 'Moonbirds #9902',
    image: 'https://raw.seadn.io/files/c86ed5931956b7e181596adb11938a48.bin'
  },
  {
    id: 'moonbirds-9901',
    name: 'Moonbirds #9901',
    image: 'https://raw.seadn.io/files/0031e0ffecadee0be368fd6cdd7fa922.bin'
  },
  {
    id: 'moonbirds-9900',
    name: 'Moonbirds #9900',
    image: 'https://raw.seadn.io/files/48e980396f386429447b3a7e4248969e.bin'
  }

                  ];
        actualNftData = moonbirdsNFTs.find(nft => nft.id.toString() === itemId);  
                 
                      } else if (collectionId === 'overworld-incarna') {
        const overworldincarnaNFTs = [
            
  {
    id: 'overworld-incarna-2098',
    name: 'OVERWORLD INCARNA #2098',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-4046723150797086849.jpeg'
  },
  {
    id: 'overworld-incarna-2099',
    name: 'OVERWORLD INCARNA #2099',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3871538168120086444.jpeg'
  },
  {
    id: 'overworld-incarna-2097',
    name: 'OVERWORLD INCARNA #2097',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-6042258135305765788.jpeg'
  },
  {
    id: 'overworld-incarna-2096',
    name: 'OVERWORLD INCARNA #2096',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-711673907956533839.jpeg'
  },
  {
    id: 'overworld-incarna-2100',
    name: 'OVERWORLD INCARNA #2100',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-2007755029558431847.jpeg'
  },
  {
    id: 'overworld-incarna-2094',
    name: 'OVERWORLD INCARNA #2094',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-4160327372879252705.jpeg'
  },
  {
    id: 'overworld-incarna-2092',
    name: 'OVERWORLD INCARNA #2092',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3076391485315190151.jpeg'
  },
  {
    id: 'overworld-incarna-2095',
    name: 'OVERWORLD INCARNA #2095',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-1634825683742162010.jpeg'
  },
  {
    id: 'overworld-incarna-2093',
    name: 'OVERWORLD INCARNA #2093',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3929688343403748232.jpeg'
  },
  {
    id: 'overworld-incarna-2089',
    name: 'OVERWORLD INCARNA #2089',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-4665346629897715491.jpeg'
  },
  {
    id: 'overworld-incarna-2086',
    name: 'OVERWORLD INCARNA #2086',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-6150243227721760598.jpeg'
  },
  {
    id: 'overworld-incarna-2083',
    name: 'OVERWORLD INCARNA #2083',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-198851983534077080.jpeg'
  },
  {
    id: 'overworld-incarna-2091',
    name: 'OVERWORLD INCARNA #2091',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3051913466298655846.jpeg'
  },
  {
    id: 'overworld-incarna-2090',
    name: 'OVERWORLD INCARNA #2090',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-4361216934312725434.jpeg'
  },
  {
    id: 'overworld-incarna-2088',
    name: 'OVERWORLD INCARNA #2088',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-2458784706129276245.jpeg'
  },
  {
    id: 'overworld-incarna-2087',
    name: 'OVERWORLD INCARNA #2087',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-2093221157314222037.jpeg'
  },
  {
    id: 'overworld-incarna-2084',
    name: 'OVERWORLD INCARNA #2084',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-6065715410835604039.jpeg'
  },
  {
    id: 'overworld-incarna-2082',
    name: 'OVERWORLD INCARNA #2082',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-1423190906205277782.jpeg'
  },
  {
    id: 'overworld-incarna-2085',
    name: 'OVERWORLD INCARNA #2085',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-95417756330493850.jpeg'
  },
  {
    id: 'overworld-incarna-2063',
    name: 'OVERWORLD INCARNA #2063',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-1398057596767168661.jpeg'
  },
  {
    id: 'overworld-incarna-2081',
    name: 'OVERWORLD INCARNA #2081',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-2815566775163775206.jpeg'
  },
  {
    id: 'overworld-incarna-2072',
    name: 'OVERWORLD INCARNA #2072',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3478763289347517947.jpeg'
  },
  {
    id: 'overworld-incarna-2075',
    name: 'OVERWORLD INCARNA #2075',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-6912800161626818478.jpeg'
  },
  {
    id: 'overworld-incarna-2079',
    name: 'OVERWORLD INCARNA #2079',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-8088229531886969928.jpeg'
  },
  {
    id: 'overworld-incarna-2077',
    name: 'OVERWORLD INCARNA #2077',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-2538130384304753247.jpeg'
  },
  {
    id: 'overworld-incarna-2071',
    name: 'OVERWORLD INCARNA #2071',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-7269035929329304982.jpeg'
  },
  {
    id: 'overworld-incarna-2076',
    name: 'OVERWORLD INCARNA #2076',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3969046645873517594.jpeg'
  },
  {
    id: 'overworld-incarna-2069',
    name: 'OVERWORLD INCARNA #2069',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3829202277780719860.jpeg'
  },
  {
    id: 'overworld-incarna-2073',
    name: 'OVERWORLD INCARNA #2073',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-4010391894963493370.jpeg'
  },
  {
    id: 'overworld-incarna-2080',
    name: 'OVERWORLD INCARNA #2080',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-3635572655812987494.jpeg'
  },
  {
    id: 'overworld-incarna-2078',
    name: 'OVERWORLD INCARNA #2078',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-8157419694712536610.jpeg'
  },
  {
    id: 'overworld-incarna-2074',
    name: 'OVERWORLD INCARNA #2074',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-6489336130408459113.jpeg'
  },
  {
    id: 'overworld-incarna-2068',
    name: 'OVERWORLD INCARNA #2068',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-5251428147986429242.jpeg'
  },
  {
    id: 'overworld-incarna-2066',
    name: 'OVERWORLD INCARNA #2066',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7088360189582950210.jpeg'
  },
  {
    id: 'overworld-incarna-2064',
    name: 'OVERWORLD INCARNA #2064',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-6256045995166864820.jpeg'
  },
  {
    id: 'overworld-incarna-2070',
    name: 'OVERWORLD INCARNA #2070',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519584-5421185077781688438.jpeg'
  },
  {
    id: 'overworld-incarna-2065',
    name: 'OVERWORLD INCARNA #2065',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7517007213959937355.jpeg'
  },
  {
    id: 'overworld-incarna-2067',
    name: 'OVERWORLD INCARNA #2067',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-3534355308742311862.jpeg'
  },
  {
    id: 'overworld-incarna-2062',
    name: 'OVERWORLD INCARNA #2062',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-386951861377188062.jpeg'
  },
  {
    id: 'overworld-incarna-2061',
    name: 'OVERWORLD INCARNA #2061',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4859611903135375474.jpeg'
  },
  {
    id: 'overworld-incarna-2060',
    name: 'OVERWORLD INCARNA #2060',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-2333042169032360061.jpeg'
  },
  {
    id: 'overworld-incarna-2059',
    name: 'OVERWORLD INCARNA #2059',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-2279652149630966928.jpeg'
  },
  {
    id: 'overworld-incarna-2058',
    name: 'OVERWORLD INCARNA #2058',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7560476449572051722.jpeg'
  },
  {
    id: 'overworld-incarna-2047',
    name: 'OVERWORLD INCARNA #2047',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4343852535077714986.jpeg'
  },
  {
    id: 'overworld-incarna-2049',
    name: 'OVERWORLD INCARNA #2049',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-3611343583236795741.jpeg'
  },
  {
    id: 'overworld-incarna-2054',
    name: 'OVERWORLD INCARNA #2054',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-8604451795832100289.jpeg'
  },
  {
    id: 'overworld-incarna-2048',
    name: 'OVERWORLD INCARNA #2048',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-8869003361059894006.jpeg'
  },
  {
    id: 'overworld-incarna-2051',
    name: 'OVERWORLD INCARNA #2051',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-417102033047043356.jpeg'
  },
  {
    id: 'overworld-incarna-2045',
    name: 'OVERWORLD INCARNA #2045',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-3021398021655570071.jpeg'
  },
  {
    id: 'overworld-incarna-2057',
    name: 'OVERWORLD INCARNA #2057',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-5561802071400988775.jpeg'
  },
  {
    id: 'overworld-incarna-2056',
    name: 'OVERWORLD INCARNA #2056',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7457748803139842732.jpeg'
  },
  {
    id: 'overworld-incarna-2046',
    name: 'OVERWORLD INCARNA #2046',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4846048792719250936.jpeg'
  },
  {
    id: 'overworld-incarna-2053',
    name: 'OVERWORLD INCARNA #2053',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-151076417597537098.jpeg'
  },
  {
    id: 'overworld-incarna-2043',
    name: 'OVERWORLD INCARNA #2043',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7484737013242284795.jpeg'
  },
  {
    id: 'overworld-incarna-2052',
    name: 'OVERWORLD INCARNA #2052',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-5345616237835108815.jpeg'
  },
  {
    id: 'overworld-incarna-2042',
    name: 'OVERWORLD INCARNA #2042',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4390457918314145906.jpeg'
  },
  {
    id: 'overworld-incarna-2050',
    name: 'OVERWORLD INCARNA #2050',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-8185682028430109162.jpeg'
  },
  {
    id: 'overworld-incarna-2055',
    name: 'OVERWORLD INCARNA #2055',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-5931058688885643641.jpeg'
  },
  {
    id: 'overworld-incarna-2044',
    name: 'OVERWORLD INCARNA #2044',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7218144186342017659.jpeg'
  },
  {
    id: 'overworld-incarna-2035',
    name: 'OVERWORLD INCARNA #2035',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-2641195169528316508.jpeg'
  },
  {
    id: 'overworld-incarna-2037',
    name: 'OVERWORLD INCARNA #2037',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7528036860944853636.jpeg'
  },
  {
    id: 'overworld-incarna-2041',
    name: 'OVERWORLD INCARNA #2041',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-5322472855539796401.jpeg'
  },
  {
    id: 'overworld-incarna-2038',
    name: 'OVERWORLD INCARNA #2038',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-57306700091128669.jpeg'
  },
  {
    id: 'overworld-incarna-2039',
    name: 'OVERWORLD INCARNA #2039',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-6270808768706381298.jpeg'
  },
  {
    id: 'overworld-incarna-2040',
    name: 'OVERWORLD INCARNA #2040',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-3601402311345690575.jpeg'
  },
  {
    id: 'overworld-incarna-2036',
    name: 'OVERWORLD INCARNA #2036',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-2958047863587608958.jpeg'
  },
  {
    id: 'overworld-incarna-2033',
    name: 'OVERWORLD INCARNA #2033',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4801186526377817194.jpeg'
  },
  {
    id: 'overworld-incarna-2034',
    name: 'OVERWORLD INCARNA #2034',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-1995344842633645718.jpeg'
  },
  {
    id: 'overworld-incarna-2030',
    name: 'OVERWORLD INCARNA #2030',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-1482187546014885039.jpeg'
  },
  {
    id: 'overworld-incarna-2032',
    name: 'OVERWORLD INCARNA #2032',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-7016348350585238508.jpeg'
  },
  {
    id: 'overworld-incarna-2031',
    name: 'OVERWORLD INCARNA #2031',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4421227044579286985.jpeg'
  },
  {
    id: 'overworld-incarna-2028',
    name: 'OVERWORLD INCARNA #2028',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-862987702623703430.jpeg'
  },
  {
    id: 'overworld-incarna-2022',
    name: 'OVERWORLD INCARNA #2022',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-263849504388759410.jpeg'
  },
  {
    id: 'overworld-incarna-2029',
    name: 'OVERWORLD INCARNA #2029',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-1908574047039819874.jpeg'
  },
  {
    id: 'overworld-incarna-2027',
    name: 'OVERWORLD INCARNA #2027',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-2008055269893079893.jpeg'
  },
  {
    id: 'overworld-incarna-2025',
    name: 'OVERWORLD INCARNA #2025',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-4701716440911081176.jpeg'
  },
  {
    id: 'overworld-incarna-2023',
    name: 'OVERWORLD INCARNA #2023',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-2924783701910225759.jpeg'
  },
  {
    id: 'overworld-incarna-2026',
    name: 'OVERWORLD INCARNA #2026',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519583-3106040782701723446.jpeg'
  },
  {
    id: 'overworld-incarna-2020',
    name: 'OVERWORLD INCARNA #2020',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-632497799418204750.jpeg'
  },
  {
    id: 'overworld-incarna-2021',
    name: 'OVERWORLD INCARNA #2021',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-2069574030060637994.jpeg'
  },
  {
    id: 'overworld-incarna-2024',
    name: 'OVERWORLD INCARNA #2024',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-7075117467521257536.jpeg'
  },
  {
    id: 'overworld-incarna-2018',
    name: 'OVERWORLD INCARNA #2018',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-2189331014722467600.jpeg'
  },
  {
    id: 'overworld-incarna-2019',
    name: 'OVERWORLD INCARNA #2019',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-3559090010457513483.jpeg'
  },
  {
    id: 'overworld-incarna-2017',
    name: 'OVERWORLD INCARNA #2017',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-6605422298974859142.jpeg'
  },
  {
    id: 'overworld-incarna-2013',
    name: 'OVERWORLD INCARNA #2013',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-5896079501631806386.jpeg'
  },
  {
    id: 'overworld-incarna-2014',
    name: 'OVERWORLD INCARNA #2014',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-4105510456048133106.jpeg'
  },
  {
    id: 'overworld-incarna-1993',
    name: 'OVERWORLD INCARNA #1993',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-3201512424333309674.jpeg'
  },
  {
    id: 'overworld-incarna-2015',
    name: 'OVERWORLD INCARNA #2015',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-1345282625571607596.jpeg'
  },
  {
    id: 'overworld-incarna-2016',
    name: 'OVERWORLD INCARNA #2016',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-5472436335758734031.jpeg'
  },
  {
    id: 'overworld-incarna-2011',
    name: 'OVERWORLD INCARNA #2011',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-7090578803760518465.jpeg'
  },
  {
    id: 'overworld-incarna-2012',
    name: 'OVERWORLD INCARNA #2012',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-5533237748179773604.jpeg'
  },
  {
    id: 'overworld-incarna-1976',
    name: 'OVERWORLD INCARNA #1976',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519581-8806910946508630918.jpeg'
  },
  {
    id: 'overworld-incarna-1998',
    name: 'OVERWORLD INCARNA #1998',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-5791876905401069668.jpeg'
  },
  {
    id: 'overworld-incarna-1987',
    name: 'OVERWORLD INCARNA #1987',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-2548508216596176152.jpeg'
  },
  {
    id: 'overworld-incarna-2000',
    name: 'OVERWORLD INCARNA #2000',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-8726835252647159586.jpeg'
  },
  {
    id: 'overworld-incarna-2010',
    name: 'OVERWORLD INCARNA #2010',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-693802722789274640.jpeg'
  },
  {
    id: 'overworld-incarna-1975',
    name: 'OVERWORLD INCARNA #1975',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519581-6077367752811052255.jpeg'
  },
  {
    id: 'overworld-incarna-2006',
    name: 'OVERWORLD INCARNA #2006',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-900650234696327850.jpeg'
  },
  {
    id: 'overworld-incarna-2003',
    name: 'OVERWORLD INCARNA #2003',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-2721246467864814024.jpeg'
  },
  {
    id: 'overworld-incarna-2005',
    name: 'OVERWORLD INCARNA #2005',
    image: 'https://resources.xter.io/funfog_upload/f66404351a91/nft/1702519582-8287061358002785835.jpeg'
  }
            
             ];
        actualNftData = overworldincarnaNFTs.find(nft => nft.id.toString() === itemId);  
                     
                            } else if (collectionId === 'nakamigos') {
        const nakamigosNFTs = [
            
  {
    id: 'nakamigos-19995',
    name: 'Nakamigos #19995',
    image: 'https://ipfs.io/ipfs/QmbJpQiRwJmCk9T1j14st1NKdeyp2tvynbArnLmKRm556G'
  },
  {
    id: 'nakamigos-19994',
    name: 'Nakamigos #19994',
    image: 'https://ipfs.io/ipfs/Qmd97nfbFu3AkosMSNytHAXHBdu4khtXcxz16Gjx5YufZ9'
  },
  {
    id: 'nakamigos-19991',
    name: 'Nakamigos #19991',
    image: 'https://ipfs.io/ipfs/QmR7TZhVtP1ez7A7MN7kpycC5MwbEQqW7VbHKabXbbbnb1'
  },
  {
    id: 'nakamigos-19992',
    name: 'Nakamigos #19992',
    image: 'https://ipfs.io/ipfs/QmXpgCGQh8CjLkR6vQKFjC8Toy7dD3yLfWUhYj4hm1cAUU'
  },
  {
    id: 'nakamigos-19987',
    name: 'Nakamigos #19987',
    image: 'https://ipfs.io/ipfs/QmXLENu2SXEVJLNJMJcBfJ4bzG53KvtKAR4RDTgRj6DJV2'
  },
  {
    id: 'nakamigos-19983',
    name: 'Nakamigos #19983',
    image: 'https://ipfs.io/ipfs/QmW5ZX8P8cv9Fv38xdd64rhAKs67pJ4D1MyeLEYDHC4niZ'
  },
  {
    id: 'nakamigos-19980',
    name: 'Nakamigos #19980',
    image: 'https://ipfs.io/ipfs/QmR9z7hvVkVXBRVwvZXvmEGDqHFmNeGYpMWX2WeBGrta37'
  },
  {
    id: 'nakamigos-19979',
    name: 'Nakamigos #19979',
    image: 'https://ipfs.io/ipfs/QmRifEmwjUCypdio1mNnPuar5BpEEWoE2BLs4oCfSVr6KH'
  },
  {
    id: 'nakamigos-19978',
    name: 'Nakamigos #19978',
    image: 'https://ipfs.io/ipfs/QmVcuCnYiHLTv8zRhiJNwke4ucWToz4CGdC8V67axtZJST'
  },
  {
    id: 'nakamigos-19976',
    name: 'Nakamigos #19976',
    image: 'https://ipfs.io/ipfs/QmYkCsUX18HgbguyocagLqRDJEdG8EMzju8nztMshgGvwc'
  },
  {
    id: 'nakamigos-19953',
    name: 'Nakamigos #19953',
    image: 'https://ipfs.io/ipfs/QmRoMq8ZpCoSVteeMQizstdtXjfFujujCQvsp31BXQuyXp'
  },
  {
    id: 'nakamigos-19972',
    name: 'Nakamigos #19972',
    image: 'https://ipfs.io/ipfs/QmayNRJ2tr66Mb4u3Wth4xHHZWAZDQeyWpxSHibTdJFMNj'
  },
  {
    id: 'nakamigos-19964',
    name: 'Nakamigos #19964',
    image: 'https://ipfs.io/ipfs/QmetSebn1FQmN78djbCAns9xPBnbQHB2cLydBRfd1spwB4'
  },
  {
    id: 'nakamigos-19950',
    name: 'Nakamigos #19950',
    image: 'https://ipfs.io/ipfs/Qmdx12oogWKYH4qDqRPSGTNyQs9X7iAG78qHKG5hDmE44m'
  },
  {
    id: 'nakamigos-19971',
    name: 'Nakamigos #19971',
    image: 'https://ipfs.io/ipfs/QmXxJbLfApg3F39onaZcWbWGrsx1hJSPBzQoXQHau1bS5f'
  },
  {
    id: 'nakamigos-19949',
    name: 'Nakamigos #19949',
    image: 'https://ipfs.io/ipfs/QmdtE77MSx8Pbmkn6RY83kNa3eu8qdswdU2a6DxNkZMhVZ'
  },
  {
    id: 'nakamigos-19970',
    name: 'Nakamigos #19970',
    image: 'https://ipfs.io/ipfs/Qme5zJKVW5whhV7b8sSuDLcSDMZEVc3hjU3YFeQcQyGEwi'
  },
  {
    id: 'nakamigos-19948',
    name: 'Nakamigos #19948',
    image: 'https://ipfs.io/ipfs/QmSDmuazj61tPgWX2p25y2Y7EH3VQvCjtNbca4QHG6T8SY'
  },
  {
    id: 'nakamigos-19961',
    name: 'Nakamigos #19961',
    image: 'https://ipfs.io/ipfs/QmQyiyhcepdU3s7npj34odcHuRKGNJuGgpJFY525n2jsYv'
  },
  {
    id: 'nakamigos-19968',
    name: 'Nakamigos #19968',
    image: 'https://ipfs.io/ipfs/QmZMf3SY1UcRcSdR9MeexVWTopikPyJxbRZYJsq3gssRxz'
  },
  {
    id: 'nakamigos-19960',
    name: 'Nakamigos #19960',
    image: 'https://ipfs.io/ipfs/QmTLvxdoYAcgsqZxrNHLeQV4UG3R4KmVzfF29jt9v5Nq5V'
  },
  {
    id: 'nakamigos-19946',
    name: 'Nakamigos #19946',
    image: 'https://ipfs.io/ipfs/QmammMuwqmC81fBTWms1CpuypRnE61HYEk3hqSH5NbtjD2'
  },
  {
    id: 'nakamigos-19967',
    name: 'Nakamigos #19967',
    image: 'https://ipfs.io/ipfs/Qmb4WhXPSD7WqkGBsYqQm8Nj7NHDcgReBECDRRJ3vivyXH'
  },
  {
    id: 'nakamigos-19958',
    name: 'Nakamigos #19958',
    image: 'https://ipfs.io/ipfs/QmVRwVwnTiZuwHhpFj2m25PBpEdsRBhFVo8LdA5KBa3iko'
  },
  {
    id: 'nakamigos-19956',
    name: 'Nakamigos #19956',
    image: 'https://ipfs.io/ipfs/QmehiR2xtc6rtN3j1Bn9VAGgPXa3aPrECDvNS4UQsh3jBH'
  },
  {
    id: 'nakamigos-19935',
    name: 'Nakamigos #19935',
    image: 'https://ipfs.io/ipfs/QmY8dwqJ4nGWnyB1iap9oZEh9apY3CfAy4KLzfttCdPyJj'
  },
  {
    id: 'nakamigos-19934',
    name: 'Nakamigos #19934',
    image: 'https://ipfs.io/ipfs/QmeRjSSQF3jq5USWNri3ZwPhTpNxT6QQ5s4qKgvE7g7HCU'
  },
  {
    id: 'nakamigos-19905',
    name: 'Nakamigos #19905',
    image: 'https://ipfs.io/ipfs/QmYU34CPCagucig3hcJKxtDa2R9EupcXvZMoKFvVUGb2tN'
  },
  {
    id: 'nakamigos-19904',
    name: 'Nakamigos #19904',
    image: 'https://ipfs.io/ipfs/Qmb8nZNLQikXSEoCwpvenm8vXay4P5nbAKNX4fnxYrnR9x'
  },
  {
    id: 'nakamigos-19930',
    name: 'Nakamigos #19930',
    image: 'https://ipfs.io/ipfs/QmXGGt7y1LmQVFSDzeRrXGp5QXfVSFsVMEuvD5hSariWr4'
  },
  {
    id: 'nakamigos-19922',
    name: 'Nakamigos #19922',
    image: 'https://ipfs.io/ipfs/QmahWJxPco7Ao9MhYBC58ztwE6cHsjmLskHZoH2Uhyws62'
  },
  {
    id: 'nakamigos-19915',
    name: 'Nakamigos #19915',
    image: 'https://ipfs.io/ipfs/QmdhqGMZuRM3h1ubEwierWm9DmctHgtC4wjYdcdTPi6AZU'
  },
  {
    id: 'nakamigos-19921',
    name: 'Nakamigos #19921',
    image: 'https://ipfs.io/ipfs/QmV7srsSDWCaDM4VrAPJDtVCzgY4HYGxwjNNC6HU7gWriS'
  },
  {
    id: 'nakamigos-19920',
    name: 'Nakamigos #19920',
    image: 'https://ipfs.io/ipfs/QmS65XMSRLzbqHSkFv5aRWTjuuLfYdECVtKWNqTkiACPo1'
  },
  {
    id: 'nakamigos-19927',
    name: 'Nakamigos #19927',
    image: 'https://ipfs.io/ipfs/QmZyPQuRpVYAJe9HYTMnUQAmet5qPHCM4SuTP9anjSjzrF'
  },
  {
    id: 'nakamigos-19901',
    name: 'Nakamigos #19901',
    image: 'https://ipfs.io/ipfs/QmRu7EiXgerrV7anjLxrZUTX6j16E4WM3pPqyYVPUUHdzL'
  },
  {
    id: 'nakamigos-19919',
    name: 'Nakamigos #19919',
    image: 'https://ipfs.io/ipfs/QmRAyTHwK7wC7UXH7PMyByjg1XXnmcRDzAeJp5ST5ANxzq'
  },
  {
    id: 'nakamigos-19926',
    name: 'Nakamigos #19926',
    image: 'https://ipfs.io/ipfs/QmeQkbp9uHvU8V6WgEdLg65vtmhiwz8E5ahjK1HV3Va58a'
  },
  {
    id: 'nakamigos-19899',
    name: 'Nakamigos #19899',
    image: 'https://ipfs.io/ipfs/QmQ9icFw9YXmCLcSaAwMTEBgTFwCcj9yct2zacn2kuunqQ'
  },
  {
    id: 'nakamigos-19912',
    name: 'Nakamigos #19912',
    image: 'https://ipfs.io/ipfs/QmfQBnPvUqkVc5igiY6uSXM5TdFKSTQ1LxEA5F1Uycik5P'
  },
  {
    id: 'nakamigos-19917',
    name: 'Nakamigos #19917',
    image: 'https://ipfs.io/ipfs/QmZzP55XhrkDTb5GuPxoYU7NjYAmxBkFeaGmEHZeH6BHdt'
  },
  {
    id: 'nakamigos-19896',
    name: 'Nakamigos #19896',
    image: 'https://ipfs.io/ipfs/QmdsnmyAqLYZSPtVkUiQs15911md79XVhKbovp9wAEQzNA'
  },
  {
    id: 'nakamigos-19909',
    name: 'Nakamigos #19909',
    image: 'https://ipfs.io/ipfs/QmRiY9gKcaJho3sHUq33r7KLboYyNbHBkMXbc2hbw2Fom8'
  },
  {
    id: 'nakamigos-19908',
    name: 'Nakamigos #19908',
    image: 'https://ipfs.io/ipfs/QmYMeiiwggAihASE6KbXTZCP2bqsTMk9yhSwQVT3XcEyju'
  },
  {
    id: 'nakamigos-19906',
    name: 'Nakamigos #19906',
    image: 'https://ipfs.io/ipfs/QmeeAvyQApGHNgKAdjwsigbTfMo8cjiUnJzK2qdKLbRfhg'
  },
  {
    id: 'nakamigos-19875',
    name: 'Nakamigos #19875',
    image: 'https://ipfs.io/ipfs/QmfXqwur3vhsG6xXQJbfvFT7SNr5L2DTro35M6FxXfw9rR'
  },
  {
    id: 'nakamigos-19874',
    name: 'Nakamigos #19874',
    image: 'https://ipfs.io/ipfs/QmWuXL5rEuMxQAss1CisDTfPWoyJHgQkLhXc7tSm6g5ueu'
  },
  {
    id: 'nakamigos-19872',
    name: 'Nakamigos #19872',
    image: 'https://ipfs.io/ipfs/QmYpECaXmbpvLiuRVC3QecFfoMmcgXpW1ggozmBpW4Up6X'
  },
  {
    id: 'nakamigos-19855',
    name: 'Nakamigos #19855',
    image: 'https://ipfs.io/ipfs/QmXns85hEmWMHBGFoMyUBAFXRsAKvgv6szvAwRGXvYzGsE'
  },
  {
    id: 'nakamigos-19844',
    name: 'Nakamigos #19844',
    image: 'https://ipfs.io/ipfs/QmXznEh1s3Tmncd2rQCS2URpVunVro8c8BUHLdm4GMxPpP'
  },
  {
    id: 'nakamigos-19870',
    name: 'Nakamigos #19870',
    image: 'https://ipfs.io/ipfs/QmR3Mn9dFP4zD8YN7oLLekySoow8K9spbimo9rT9H8uWaw'
  },
  {
    id: 'nakamigos-19854',
    name: 'Nakamigos #19854',
    image: 'https://ipfs.io/ipfs/QmUioRkckXM89NRV5usVJfb5MMU24YTWoEUJtJqPNFA8Mt'
  },
  {
    id: 'nakamigos-19839',
    name: 'Nakamigos #19839',
    image: 'https://ipfs.io/ipfs/QmNxudfGUDTaCvtE1BsnVnxVK77QLiBJrACpk3T9f3rMP5'
  },
  {
    id: 'nakamigos-19851',
    name: 'Nakamigos #19851',
    image: 'https://ipfs.io/ipfs/QmcPYctK6HEhsHVQsHyzwMjYFeixesgBM5D9Y8kpjNTAa5'
  },
  {
    id: 'nakamigos-19866',
    name: 'Nakamigos #19866',
    image: 'https://ipfs.io/ipfs/QmbYGPzy8WG1auQ2mVx9YbnPiKvGVoP118MB1YCFLDJ1US'
  },
  {
    id: 'nakamigos-19838',
    name: 'Nakamigos #19838',
    image: 'https://ipfs.io/ipfs/Qmc3e1jqqLUN1foRZsLDgArskY1vZuS3bQFfbvpiWB8pdG'
  },
  {
    id: 'nakamigos-19837',
    name: 'Nakamigos #19837',
    image: 'https://ipfs.io/ipfs/QmPWkJvf5fqiX6hvd7qSR7er3KxqQ9MgXeRtUHe8kXTQTe'
  },
  {
    id: 'nakamigos-19849',
    name: 'Nakamigos #19849',
    image: 'https://ipfs.io/ipfs/QmfXob5pUdx2wz15iNxZQKDwneRzDnYNxv5L917VaEkasT'
  },
  {
    id: 'nakamigos-19836',
    name: 'Nakamigos #19836',
    image: 'https://ipfs.io/ipfs/QmWDkbC9Vq7kh4FxMPsFbcZyHxLiXwMbycCBcU1zfesmVm'
  },
  {
    id: 'nakamigos-19847',
    name: 'Nakamigos #19847',
    image: 'https://ipfs.io/ipfs/QmcaUwu3YQPKpy7DEXvKfVkkR3EzWNJ78P7GdyipK15reN'
  },
  {
    id: 'nakamigos-19834',
    name: 'Nakamigos #19834',
    image: 'https://ipfs.io/ipfs/QmS8b6gWVg7cPEwiL9TMFQX7zLBf1QVXeJ5cYj5t7uadqG'
  },
  {
    id: 'nakamigos-19831',
    name: 'Nakamigos #19831',
    image: 'https://ipfs.io/ipfs/QmaZgayxsQzkSPbEUB2q2cQDSxvpVujgWGajVFmGkGAuuo'
  },
  {
    id: 'nakamigos-19830',
    name: 'Nakamigos #19830',
    image: 'https://ipfs.io/ipfs/QmUiyHrgk3DYn9CkVCQRVGH1n2k7TdLTkqyjWbmCdtNfX9'
  },
  {
    id: 'nakamigos-19829',
    name: 'Nakamigos #19829',
    image: 'https://ipfs.io/ipfs/QmVFYScyKu7B2DLKxTtzCu28jqC3ggvuJChQmc95NTSbnr'
  },
  {
    id: 'nakamigos-19827',
    name: 'Nakamigos #19827',
    image: 'https://ipfs.io/ipfs/Qmd3q4ENG2mji5ZuTp2B6qi1v8EDLE6eDK3jSpdvcV1w3U'
  },
  {
    id: 'nakamigos-19944',
    name: 'Nakamigos #19944',
    image: 'https://ipfs.io/ipfs/QmeQEDbiBwDueAyx4SgWE6y7VJE425XShRzQ2cBh8VHRaT'
  },
  {
    id: 'nakamigos-19943',
    name: 'Nakamigos #19943',
    image: 'https://ipfs.io/ipfs/QmZLtdKKQ8pUb4F7Cvx4cx6dZusmKr579n42kp6BLrSJgs'
  },
  {
    id: 'nakamigos-19940',
    name: 'Nakamigos #19940',
    image: 'https://ipfs.io/ipfs/QmPHoQm9THbhWPxWLWuDp9Q84qv4kSjQea8sCAjrtur5Dw'
  },
  {
    id: 'nakamigos-19938',
    name: 'Nakamigos #19938',
    image: 'https://ipfs.io/ipfs/QmeMj1gkKd6LciPJmGWFe72mKXtZYvYaUwKRRUDDokJpS7'
  },
  {
    id: 'nakamigos-19937',
    name: 'Nakamigos #19937',
    image: 'https://ipfs.io/ipfs/QmPSeYm5sHnpxiHiBZhgp4Hw95byy4Fz9RE2vezvspzPfZ'
  },
  {
    id: 'nakamigos-19794',
    name: 'Nakamigos #19794',
    image: 'https://ipfs.io/ipfs/QmQjGqZGny5Ko5A4tzcG5ZZKc4CEyaywv26VhM8Q6tgzWf'
  },
  {
    id: 'nakamigos-19793',
    name: 'Nakamigos #19793',
    image: 'https://ipfs.io/ipfs/QmQEPPNE9kxqVcYY1KMmCTMKPTVcTA6vKNXd2QMxTshpJR'
  },
  {
    id: 'nakamigos-19790',
    name: 'Nakamigos #19790',
    image: 'https://ipfs.io/ipfs/QmazurtKihwd2SS7Y2K5JbqrDMj1Mx3sDrUdgAQRuo9C25'
  },
  {
    id: 'nakamigos-19884',
    name: 'Nakamigos #19884',
    image: 'https://ipfs.io/ipfs/QmSdxsSnQpUvWVwSFJCQQX3DprHVnbwKq9dsr6V1LjMjjE'
  },
  {
    id: 'nakamigos-19788',
    name: 'Nakamigos #19788',
    image: 'https://ipfs.io/ipfs/QmNUpxtHss8qFv8qasZS5y6f9RDguisDcHsCddhabdDrDX'
  },
  {
    id: 'nakamigos-19881',
    name: 'Nakamigos #19881',
    image: 'https://ipfs.io/ipfs/QmRKujyoZNks2vnFrfMQQATRb4F45etA8Esqo2LQwi57er'
  },
  {
    id: 'nakamigos-19895',
    name: 'Nakamigos #19895',
    image: 'https://ipfs.io/ipfs/QmdVH3r4Jx8T7BA4qzLGhXfDPfTS7uEdHiCc6GQNGUxKC4'
  },
  {
    id: 'nakamigos-19786',
    name: 'Nakamigos #19786',
    image: 'https://ipfs.io/ipfs/QmSggK44h6Rjt8bzZEo2iT38rhdyXqqtAupJhSWSFBibJM'
  },
  {
    id: 'nakamigos-19893',
    name: 'Nakamigos #19893',
    image: 'https://ipfs.io/ipfs/QmVJppVSeVt4VdjoyLCNUN95CPwsSmvdWW7QyBjeBYvixP'
  },
  {
    id: 'nakamigos-19878',
    name: 'Nakamigos #19878',
    image: 'https://ipfs.io/ipfs/QmeZ6MTmBpT1oiy6RezgYifZg979RfT7PPE3tb5WQ6TCVY'
  },
  {
    id: 'nakamigos-19877',
    name: 'Nakamigos #19877',
    image: 'https://ipfs.io/ipfs/QmYxXpcMwZYgvJ79Lvs5Qm1VZxqBZUkMBwrrZdWSZT85vS'
  },
  {
    id: 'nakamigos-19891',
    name: 'Nakamigos #19891',
    image: 'https://ipfs.io/ipfs/QmbapRiU6wHhm4vU63shcrioUzubu6rBM4QvfhtcFxxKzG'
  },
  {
    id: 'nakamigos-19876',
    name: 'Nakamigos #19876',
    image: 'https://ipfs.io/ipfs/QmWUjHoiWXNpwDWNWyQPuFcDkh121wM61SkW4mrirvSUZ9'
  },
  {
    id: 'nakamigos-19889',
    name: 'Nakamigos #19889',
    image: 'https://ipfs.io/ipfs/QmT3wgtJpnictC1R5jmGfxrqXxP9ZMGJT3YiK7y2T9AtxX'
  },
  {
    id: 'nakamigos-19865',
    name: 'Nakamigos #19865',
    image: 'https://ipfs.io/ipfs/QmUxbpw1pjzrYYAyfuGpvw9eiJg2wQSbdRmfiKhD2SCNqn'
  },
  {
    id: 'nakamigos-19888',
    name: 'Nakamigos #19888',
    image: 'https://ipfs.io/ipfs/Qmbn8us8rBLYkhwjDj3PBC7bDQemAjWqKVY2psop8jYgjf'
  },
  {
    id: 'nakamigos-19863',
    name: 'Nakamigos #19863',
    image: 'https://ipfs.io/ipfs/QmTjqErgtF1PQGSNaxbCQFjmZpspQ9WC4rxMzsq5K3RGyE'
  },
  {
    id: 'nakamigos-19862',
    name: 'Nakamigos #19862',
    image: 'https://ipfs.io/ipfs/QmYsVp2x3JeaBzFFRohNchrcJ1bQUA9bteQBro38qtx3Dw'
  },
  {
    id: 'nakamigos-19724',
    name: 'Nakamigos #19724',
    image: 'https://ipfs.io/ipfs/Qmbn5HGG3Re7woBaacZG8H2L64YSFvzbm3Lnug3Nu2dvYV'
  },
  {
    id: 'nakamigos-19860',
    name: 'Nakamigos #19860',
    image: 'https://ipfs.io/ipfs/QmVqsJqXRUzK3u7NXRdjX9y1VotkNFTorJTGgxwEAaCJWA'
  },
  {
    id: 'nakamigos-19856',
    name: 'Nakamigos #19856',
    image: 'https://ipfs.io/ipfs/QmStsxm8c1nPh6miQYNVu9sYBBPzc8GfAs3Lg7bstKoiCc'
  },
  {
    id: 'nakamigos-19815',
    name: 'Nakamigos #19815',
    image: 'https://ipfs.io/ipfs/Qmcn8RTrVWdRUT7SHcg1YfEUT5Xs1TxCZ7iXQhtRTwef8R'
  },
  {
    id: 'nakamigos-19812',
    name: 'Nakamigos #19812',
    image: 'https://ipfs.io/ipfs/QmUrSfYgTCYB4fhvB2SjpqG5Cbat7FmdpNkh2BdaTiuztM'
  },
  {
    id: 'nakamigos-19825',
    name: 'Nakamigos #19825',
    image: 'https://ipfs.io/ipfs/QmbPuucYqU4oS5nVLnNSFtMkLfkimM58An5LvrMogipfN8'
  },
  {
    id: 'nakamigos-19809',
    name: 'Nakamigos #19809',
    image: 'https://ipfs.io/ipfs/QmStMS3Y6LZDWW6b95vymv3mFzizD2JHGN8gS2gnthtcfv'
  },
  {
    id: 'nakamigos-19824',
    name: 'Nakamigos #19824',
    image: 'https://ipfs.io/ipfs/QmcMZ9AoNKgxFUAwRpeELDKUb4pDxAW2Y6xK23iscJ6NwT'
  },
  {
    id: 'nakamigos-19721',
    name: 'Nakamigos #19721',
    image: 'https://ipfs.io/ipfs/QmWQmjxqGWWh2YdgAnAc1Vbpr29hHxiry6ASNkfTRKRZ5i'
  },
  {
    id: 'nakamigos-19807',
    name: 'Nakamigos #19807',
    image: 'https://ipfs.io/ipfs/QmQFqYNcLcF1eZLZng4Nni75E6enKmJ51qzaS9pj57ev59'
  },
  {
    id: 'nakamigos-19735',
    name: 'Nakamigos #19735',
    image: 'https://ipfs.io/ipfs/QmRjpCqSv3yG6RH6r97YmfiXVt4htBYvN7iebzNYocJdT2'
  },
  {
    id: 'nakamigos-19822',
    name: 'Nakamigos #19822',
    image: 'https://ipfs.io/ipfs/QmRbwq5AAzMUze8gCpbJo8tdHmWPxxYrSXa2QFdMaietzx'
  }
             ];
        actualNftData = nakamigosNFTs.find(nft => nft.id.toString() === itemId);  
                                
                        } else if (collectionId === 'pixelady-maker') {
        const pixelladymakerNFTs = [
        
  {
    id: 'pixelady-maker-10000',
    name: 'Pixelady Maker #10000',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/10000.png'
  },
  {
    id: 'pixelady-maker-9997',
    name: 'Pixelady Maker #9997',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9997.png'
  },
  {
    id: 'pixelady-maker-9999',
    name: 'Pixelady Maker #9999',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9999.png'
  },
  {
    id: 'pixelady-maker-9998',
    name: 'Pixelady Maker #9998',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9998.png'
  },
  {
    id: 'pixelady-maker-9996',
    name: 'Pixelady Maker #9996',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9996.png'
  },
  {
    id: 'pixelady-maker-9995',
    name: 'Pixelady Maker #9995',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9995.png'
  },
  {
    id: 'pixelady-maker-9992',
    name: 'Pixelady Maker #9992',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9992.png'
  },
  {
    id: 'pixelady-maker-9989',
    name: 'Pixelady Maker #9989',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9989.png'
  },
  {
    id: 'pixelady-maker-9988',
    name: 'Pixelady Maker #9988',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9988.png'
  },
  {
    id: 'pixelady-maker-9986',
    name: 'Pixelady Maker #9986',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9986.png'
  },
  {
    id: 'pixelady-maker-9991',
    name: 'Pixelady Maker #9991',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9991.png'
  },
  {
    id: 'pixelady-maker-9987',
    name: 'Pixelady Maker #9987',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9987.png'
  },
  {
    id: 'pixelady-maker-9994',
    name: 'Pixelady Maker #9994',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9994.png'
  },
  {
    id: 'pixelady-maker-9985',
    name: 'Pixelady Maker #9985',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9985.png'
  },
  {
    id: 'pixelady-maker-9990',
    name: 'Pixelady Maker #9990',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9990.png'
  },
  {
    id: 'pixelady-maker-9983',
    name: 'Pixelady Maker #9983',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9983.png'
  },
  {
    id: 'pixelady-maker-9993',
    name: 'Pixelady Maker #9993',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9993.png'
  },
  {
    id: 'pixelady-maker-9984',
    name: 'Pixelady Maker #9984',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9984.png'
  },
  {
    id: 'pixelady-maker-9977',
    name: 'Pixelady Maker #9977',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9977.png'
  },
  {
    id: 'pixelady-maker-9979',
    name: 'Pixelady Maker #9979',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9979.png'
  },
  {
    id: 'pixelady-maker-9980',
    name: 'Pixelady Maker #9980',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9980.png'
  },
  {
    id: 'pixelady-maker-9978',
    name: 'Pixelady Maker #9978',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9978.png'
  },
  {
    id: 'pixelady-maker-9976',
    name: 'Pixelady Maker #9976',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9976.png'
  },
  {
    id: 'pixelady-maker-9981',
    name: 'Pixelady Maker #9981',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9981.png'
  },
  {
    id: 'pixelady-maker-9982',
    name: 'Pixelady Maker #9982',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9982.png'
  },
  {
    id: 'pixelady-maker-9970',
    name: 'Pixelady Maker #9970',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9970.png'
  },
  {
    id: 'pixelady-maker-9967',
    name: 'Pixelady Maker #9967',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9967.png'
  },
  {
    id: 'pixelady-maker-9966',
    name: 'Pixelady Maker #9966',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9966.png'
  },
  {
    id: 'pixelady-maker-9964',
    name: 'Pixelady Maker #9964',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9964.png'
  },
  {
    id: 'pixelady-maker-9965',
    name: 'Pixelady Maker #9965',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9965.png'
  },
  {
    id: 'pixelady-maker-9962',
    name: 'Pixelady Maker #9962',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9962.png'
  },
  {
    id: 'pixelady-maker-9969',
    name: 'Pixelady Maker #9969',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9969.png'
  },
  {
    id: 'pixelady-maker-9963',
    name: 'Pixelady Maker #9963',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9963.png'
  },
  {
    id: 'pixelady-maker-9973',
    name: 'Pixelady Maker #9973',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9973.png'
  },
  {
    id: 'pixelady-maker-9968',
    name: 'Pixelady Maker #9968',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9968.png'
  },
  {
    id: 'pixelady-maker-9974',
    name: 'Pixelady Maker #9974',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9974.png'
  },
  {
    id: 'pixelady-maker-9971',
    name: 'Pixelady Maker #9971',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9971.png'
  },
  {
    id: 'pixelady-maker-9975',
    name: 'Pixelady Maker #9975',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9975.png'
  },
  {
    id: 'pixelady-maker-9972',
    name: 'Pixelady Maker #9972',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9972.png'
  },
  {
    id: 'pixelady-maker-9961',
    name: 'Pixelady Maker #9961',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9961.png'
  },
  {
    id: 'pixelady-maker-9960',
    name: 'Pixelady Maker #9960',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9960.png'
  },
  {
    id: 'pixelady-maker-9958',
    name: 'Pixelady Maker #9958',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9958.png'
  },
  {
    id: 'pixelady-maker-9957',
    name: 'Pixelady Maker #9957',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9957.png'
  },
  {
    id: 'pixelady-maker-9954',
    name: 'Pixelady Maker #9954',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9954.png'
  },
  {
    id: 'pixelady-maker-9955',
    name: 'Pixelady Maker #9955',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9955.png'
  },
  {
    id: 'pixelady-maker-9956',
    name: 'Pixelady Maker #9956',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9956.png'
  },
  {
    id: 'pixelady-maker-9945',
    name: 'Pixelady Maker #9945',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9945.png'
  },
  {
    id: 'pixelady-maker-9953',
    name: 'Pixelady Maker #9953',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9953.png'
  },
  {
    id: 'pixelady-maker-9952',
    name: 'Pixelady Maker #9952',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9952.png'
  },
  {
    id: 'pixelady-maker-9951',
    name: 'Pixelady Maker #9951',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9951.png'
  },
  {
    id: 'pixelady-maker-9950',
    name: 'Pixelady Maker #9950',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9950.png'
  },
  {
    id: 'pixelady-maker-9949',
    name: 'Pixelady Maker #9949',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9949.png'
  },
  {
    id: 'pixelady-maker-9946',
    name: 'Pixelady Maker #9946',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9946.png'
  },
  {
    id: 'pixelady-maker-9947',
    name: 'Pixelady Maker #9947',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9947.png'
  },
  {
    id: 'pixelady-maker-9944',
    name: 'Pixelady Maker #9944',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9944.png'
  },
  {
    id: 'pixelady-maker-9941',
    name: 'Pixelady Maker #9941',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9941.png'
  },
  {
    id: 'pixelady-maker-9940',
    name: 'Pixelady Maker #9940',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9940.png'
  },
  {
    id: 'pixelady-maker-9939',
    name: 'Pixelady Maker #9939',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9939.png'
  },
  {
    id: 'pixelady-maker-9937',
    name: 'Pixelady Maker #9937',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9937.png'
  },
  {
    id: 'pixelady-maker-9936',
    name: 'Pixelady Maker #9936',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9936.png'
  },
  {
    id: 'pixelady-maker-9935',
    name: 'Pixelady Maker #9935',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9935.png'
  },
  {
    id: 'pixelady-maker-9934',
    name: 'Pixelady Maker #9934',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9934.png'
  },
  {
    id: 'pixelady-maker-9933',
    name: 'Pixelady Maker #9933',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9933.png'
  },
  {
    id: 'pixelady-maker-9932',
    name: 'Pixelady Maker #9932',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9932.png'
  },
  {
    id: 'pixelady-maker-9942',
    name: 'Pixelady Maker #9942',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9942.png'
  },
  {
    id: 'pixelady-maker-9938',
    name: 'Pixelady Maker #9938',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9938.png'
  },
  {
    id: 'pixelady-maker-9943',
    name: 'Pixelady Maker #9943',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9943.png'
  },
  {
    id: 'pixelady-maker-9959',
    name: 'Pixelady Maker #9959',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9959.png'
  },
  {
    id: 'pixelady-maker-9948',
    name: 'Pixelady Maker #9948',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9948.png'
  },
  {
    id: 'pixelady-maker-9931',
    name: 'Pixelady Maker #9931',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9931.png'
  },
  {
    id: 'pixelady-maker-9930',
    name: 'Pixelady Maker #9930',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9930.png'
  },
  {
    id: 'pixelady-maker-9929',
    name: 'Pixelady Maker #9929',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9929.png'
  },
  {
    id: 'pixelady-maker-9928',
    name: 'Pixelady Maker #9928',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9928.png'
  },
  {
    id: 'pixelady-maker-9927',
    name: 'Pixelady Maker #9927',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9927.png'
  },
  {
    id: 'pixelady-maker-9925',
    name: 'Pixelady Maker #9925',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9925.png'
  },
  {
    id: 'pixelady-maker-9926',
    name: 'Pixelady Maker #9926',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9926.png'
  },
  {
    id: 'pixelady-maker-9922',
    name: 'Pixelady Maker #9922',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9922.png'
  },
  {
    id: 'pixelady-maker-9923',
    name: 'Pixelady Maker #9923',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9923.png'
  },
  {
    id: 'pixelady-maker-9924',
    name: 'Pixelady Maker #9924',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9924.png'
  },
  {
    id: 'pixelady-maker-9920',
    name: 'Pixelady Maker #9920',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9920.png'
  },
  {
    id: 'pixelady-maker-9921',
    name: 'Pixelady Maker #9921',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9921.png'
  },
  {
    id: 'pixelady-maker-9919',
    name: 'Pixelady Maker #9919',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9919.png'
  },
  {
    id: 'pixelady-maker-9917',
    name: 'Pixelady Maker #9917',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9917.png'
  },
  {
    id: 'pixelady-maker-9918',
    name: 'Pixelady Maker #9918',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9918.png'
  },
  {
    id: 'pixelady-maker-9915',
    name: 'Pixelady Maker #9915',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9915.png'
  },
  {
    id: 'pixelady-maker-9913',
    name: 'Pixelady Maker #9913',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9913.png'
  },
  {
    id: 'pixelady-maker-9916',
    name: 'Pixelady Maker #9916',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9916.png'
  },
  {
    id: 'pixelady-maker-9914',
    name: 'Pixelady Maker #9914',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9914.png'
  },
  {
    id: 'pixelady-maker-9909',
    name: 'Pixelady Maker #9909',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9909.png'
  },
  {
    id: 'pixelady-maker-9906',
    name: 'Pixelady Maker #9906',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9906.png'
  },
  {
    id: 'pixelady-maker-9903',
    name: 'Pixelady Maker #9903',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9903.png'
  },
  {
    id: 'pixelady-maker-9912',
    name: 'Pixelady Maker #9912',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9912.png'
  },
  {
    id: 'pixelady-maker-9907',
    name: 'Pixelady Maker #9907',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9907.png'
  },
  {
    id: 'pixelady-maker-9904',
    name: 'Pixelady Maker #9904',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9904.png'
  },
  {
    id: 'pixelady-maker-9905',
    name: 'Pixelady Maker #9905',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9905.png'
  },
  {
    id: 'pixelady-maker-9908',
    name: 'Pixelady Maker #9908',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9908.png'
  },
  {
    id: 'pixelady-maker-9911',
    name: 'Pixelady Maker #9911',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9911.png'
  },
  {
    id: 'pixelady-maker-9902',
    name: 'Pixelady Maker #9902',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9902.png'
  },
  {
    id: 'pixelady-maker-9901',
    name: 'Pixelady Maker #9901',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9901.png'
  },
  {
    id: 'pixelady-maker-9900',
    name: 'Pixelady Maker #9900',
    image: 'https://ipfs.io/ipfs/bafybeih5mqafo34424swmfdboww3s2tvfmzoojbip4jmcjbg5n3fl7edee/9900.png'
  }
                                ];
        actualNftData = pixelladymakerNFTs.find(nft => nft.id.toString() === itemId);  
                         
                          } else if (collectionId === 'terraforms') {
        const terraformsNFTs = [
            
  {
    "id": "terraforms-9911",
    "name": "Terraforms #9911",
    "image": "https://raw.seadn.io/files/b76d0ba97f0b8d0d4a0a927ccfde7808.svg"
  },
  {
    "id": "terraforms-9910",
    "name": "Terraforms #9910",
    "image": "https://raw.seadn.io/files/014a9a61631168e8aa597f0cfae2941b.svg"
  },
  {
    "id": "terraforms-9909",
    "name": "Terraforms #9909",
    "image": "https://raw.seadn.io/files/047e5e3052423433f9d99bb324753950.svg"
  },
  {
    "id": "terraforms-9908",
    "name": "Terraforms #9908",
    "image": "https://raw.seadn.io/files/ec692d6a1a3bc68a2c61b8cfd8d84c0c.svg"
  },
  {
    "id": "terraforms-9907",
    "name": "Terraforms #9907",
    "image": "https://raw.seadn.io/files/8ef760989d275e7d6d020a1439634cb6.svg"
  },
  {
    "id": "terraforms-9906",
    "name": "Terraforms #9906",
    "image": "https://raw.seadn.io/files/12b9d5076ecfbb77783d404086b91af2.svg"
  },
  {
    "id": "terraforms-9905",
    "name": "Terraforms #9905",
    "image": "https://raw.seadn.io/files/f6bf386bb1fe8bfc588080886bd7a3db.svg"
  },
  {
    "id": "terraforms-9890",
    "name": "Terraforms #9890",
    "image": "https://raw.seadn.io/files/f4907229de64426fd5173edecd9466d2.svg"
  },
  {
    "id": "terraforms-9885",
    "name": "Terraforms #9885",
    "image": "https://raw.seadn.io/files/4588cb32fbba03a5e60883bf0bda3a9f.svg"
  },
  {
    "id": "terraforms-9889",
    "name": "Terraforms #9889",
    "image": "https://raw.seadn.io/files/50fbe61a0428e8db9ba74da5adde1bbd.svg"
  },
  {
    "id": "terraforms-9884",
    "name": "Terraforms #9884",
    "image": "https://raw.seadn.io/files/2dd2e75546f87ecc1e973cce2f307722.svg"
  },
  {
    "id": "terraforms-9888",
    "name": "Terraforms #9888",
    "image": "https://raw.seadn.io/files/4f18dc7566cef33e632da5edc33ff1e9.svg"
  },
  {
    "id": "terraforms-9904",
    "name": "Terraforms #9904",
    "image": "https://raw.seadn.io/files/4e00cba9483437c522cc745bd078ad3f.svg"
  },
  {
    "id": "terraforms-9883",
    "name": "Terraforms #9883",
    "image": "https://raw.seadn.io/files/54fb232f794e57cd44506dbfd418e83f.svg"
  },
  {
    "id": "terraforms-9887",
    "name": "Terraforms #9887",
    "image": "https://raw.seadn.io/files/0319b42cc6bacec1b84af77abe41f445.svg"
  },
  {
    "id": "terraforms-9903",
    "name": "Terraforms #9903",
    "image": "https://raw.seadn.io/files/456067f99145cb08b09f75c97503f2f2.svg"
  },
  {
    "id": "terraforms-9882",
    "name": "Terraforms #9882",
    "image": "https://raw.seadn.io/files/19730cbfa54acdac33208dba7a030b7e.svg"
  },
  {
    "id": "terraforms-9892",
    "name": "Terraforms #9892",
    "image": "https://raw.seadn.io/files/be10292dffe56ba3e773334c1118a058.svg"
  },
  {
    "id": "terraforms-9897",
    "name": "Terraforms #9897",
    "image": "https://raw.seadn.io/files/28b739d458f9a2aa9b6bd26cfaed14a7.svg"
  },
  {
    "id": "terraforms-9886",
    "name": "Terraforms #9886",
    "image": "https://raw.seadn.io/files/31ca96b5a22a332e6247e9445bc73d9e.svg"
  },
  {
    "id": "terraforms-9902",
    "name": "Terraforms #9902",
    "image": "https://raw.seadn.io/files/8ca268989d4e83425be942b96d0ff3df.svg"
  },
  {
    "id": "terraforms-9896",
    "name": "Terraforms #9896",
    "image": "https://raw.seadn.io/files/8729b97d61e4ed34ab5c1fff11a98b98.svg"
  },
  {
    "id": "terraforms-9881",
    "name": "Terraforms #9881",
    "image": "https://raw.seadn.io/files/ca5ca540b016f6117a24232f4f07ddf8.svg"
  },
  {
    "id": "terraforms-9874",
    "name": "Terraforms #9874",
    "image": "https://raw.seadn.io/files/786166cf681e0379668a6731d9727586.svg"
  },
  {
    "id": "terraforms-9895",
    "name": "Terraforms #9895",
    "image": "https://raw.seadn.io/files/7580b129fe4d5386218842e1d0279867.svg"
  },
  {
    "id": "terraforms-9901",
    "name": "Terraforms #9901",
    "image": "https://raw.seadn.io/files/06ce5adeec3f36f16c1ba27fd09746ae.svg"
  },
  {
    "id": "terraforms-9880",
    "name": "Terraforms #9880",
    "image": "https://raw.seadn.io/files/c16976cf00ce50c4965099f71e57e89d.svg"
  },
  {
    "id": "terraforms-9899",
    "name": "Terraforms #9899",
    "image": "https://raw.seadn.io/files/0c20aee8c69084adf48d6b326e5eb355.svg"
  },
  {
    "id": "terraforms-9873",
    "name": "Terraforms #9873",
    "image": "https://raw.seadn.io/files/23ce16947e45b8fec267b74159b2e446.svg"
  },
  {
    "id": "terraforms-9894",
    "name": "Terraforms #9894",
    "image": "https://raw.seadn.io/files/4339d5cb8c2d1a13cc6bfc76721215b6.svg"
  },
  {
    "id": "terraforms-9898",
    "name": "Terraforms #9898",
    "image": "https://raw.seadn.io/files/219255dda4ac8b729fac8b59e9ebcc67.svg"
  },
  {
    "id": "terraforms-9879",
    "name": "Terraforms #9879",
    "image": "https://raw.seadn.io/files/fbdf8f714d964b51a930bc70f5060c89.svg"
  },
  {
    "id": "terraforms-9900",
    "name": "Terraforms #9900",
    "image": "https://raw.seadn.io/files/9abc292eff51dee1177a98225196b08f.svg"
  },
  {
    "id": "terraforms-9872",
    "name": "Terraforms #9872",
    "image": "https://raw.seadn.io/files/699c695fbfb68a5a93c0f650adb46796.svg"
  },
  {
    "id": "terraforms-9893",
    "name": "Terraforms #9893",
    "image": "https://raw.seadn.io/files/41b45a40c0b818f3fa1c8e81227f6683.svg"
  },
  {
    "id": "terraforms-9878",
    "name": "Terraforms #9878",
    "image": "https://raw.seadn.io/files/1dec92511b682b3a8431619b9531fd1a.svg"
  },
  {
    "id": "terraforms-9891",
    "name": "Terraforms #9891",
    "image": "https://raw.seadn.io/files/22b4b45efc6bcf922be7beb324ea546e.svg"
  },
  {
    "id": "terraforms-9875",
    "name": "Terraforms #9875",
    "image": "https://raw.seadn.io/files/2e924f54afa5b5c7c8398e2a823118e7.svg"
  },
  {
    "id": "terraforms-9871",
    "name": "Terraforms #9871",
    "image": "https://raw.seadn.io/files/b0fb24075d9ef4f3d7904aa91c4660cf.svg"
  },
  {
    "id": "terraforms-9877",
    "name": "Terraforms #9877",
    "image": "https://raw.seadn.io/files/d5d1710bdb72550d3cf198a057a99a98.svg"
  },
  {
    "id": "terraforms-9862",
    "name": "Terraforms #9862",
    "image": "https://raw.seadn.io/files/7af89d35df1c2965170a8ed596980f75.svg"
  },
  {
    "id": "terraforms-9869",
    "name": "Terraforms #9869",
    "image": "https://raw.seadn.io/files/6836c3c11c9291f6dcdde5ea19fddf43.svg"
  },
  {
    "id": "terraforms-9870",
    "name": "Terraforms #9870",
    "image": "https://raw.seadn.io/files/a6c09bbcca8d9e4ee104cb90eb06e373.svg"
  },
  {
    "id": "terraforms-9876",
    "name": "Terraforms #9876",
    "image": "https://raw.seadn.io/files/a5501f92d2b66e1315a3c6cf119e5297.svg"
  },
  {
    "id": "terraforms-9868",
    "name": "Terraforms #9868",
    "image": "https://raw.seadn.io/files/73940c564f979776b8f7239f4d263360.svg"
  },
  {
    "id": "terraforms-9861",
    "name": "Terraforms #9861",
    "image": "https://raw.seadn.io/files/ca3a44aec5924e6ae2f458ec9c4c887c.svg"
  },
  {
    "id": "terraforms-9860",
    "name": "Terraforms #9860",
    "image": "https://raw.seadn.io/files/cc9ec8fb5a7024fcef1b55e7a8925fa6.svg"
  },
  {
    "id": "terraforms-9867",
    "name": "Terraforms #9867",
    "image": "https://raw.seadn.io/files/a625ffe8028ac7f1ebdecf75a1c37a05.svg"
  },
  {
    "id": "terraforms-9859",
    "name": "Terraforms #9859",
    "image": "https://raw.seadn.io/files/3419b31f3fb09473f3b559f1b2cab27d.svg"
  },
  {
    "id": "terraforms-9858",
    "name": "Terraforms #9858",
    "image": "https://raw.seadn.io/files/78e9ab9adee073b57bb86801d8b5f9a8.svg"
  },
  {
    "id": "terraforms-9857",
    "name": "Terraforms #9857",
    "image": "https://raw.seadn.io/files/47cf5933722d3671234ceaf73ebcbbad.svg"
  },
  {
    "id": "terraforms-9856",
    "name": "Terraforms #9856",
    "image": "https://raw.seadn.io/files/6a4fdbac6f92569e17af306bb401e1df.svg"
  },
  {
    "id": "terraforms-9865",
    "name": "Terraforms #9865",
    "image": "https://raw.seadn.io/files/8937ac38e956e0017a689915fefe43a4.svg"
  },
  {
    "id": "terraforms-9855",
    "name": "Terraforms #9855",
    "image": "https://raw.seadn.io/files/800376eb58787d64531195194c520ab1.svg"
  },
  {
    "id": "terraforms-9864",
    "name": "Terraforms #9864",
    "image": "https://raw.seadn.io/files/98ee11c3acbf71f6ac082bea720343dc.svg"
  },
  {
    "id": "terraforms-9866",
    "name": "Terraforms #9866",
    "image": "https://raw.seadn.io/files/18e463f03e795f5f9c625992e42e13eb.svg"
  },
  {
    "id": "terraforms-9854",
    "name": "Terraforms #9854",
    "image": "https://raw.seadn.io/files/21f5bd00b299e34903385f02cb1c2326.svg"
  },
  {
    "id": "terraforms-9863",
    "name": "Terraforms #9863",
    "image": "https://raw.seadn.io/files/aa97ea5d98dd93a772914dfd44fcef80.svg"
  },
  {
    "id": "terraforms-9852",
    "name": "Terraforms #9852",
    "image": "https://raw.seadn.io/files/3c87add55da6b3bfc19d774c205ddb2e.svg"
  },
  {
    "id": "terraforms-9853",
    "name": "Terraforms #9853",
    "image": "https://raw.seadn.io/files/5098c0a0cced16a7356dba7f168448df.svg"
  },
  {
    "id": "terraforms-9851",
    "name": "Terraforms #9851",
    "image": "https://raw.seadn.io/files/435d7a8dc18bf422f38b6d2c2e1a4a50.svg"
  },
  {
    "id": "terraforms-9828",
    "name": "Terraforms #9828",
    "image": "https://raw.seadn.io/files/c8e87cc6e70aa9ac9625d66c3228aea2.svg"
  },
  {
    "id": "terraforms-9838",
    "name": "Terraforms #9838",
    "image": "https://raw.seadn.io/files/06d0df05d13e4781c5e466d9b0106ab4.svg"
  },
  {
    "id": "terraforms-9796",
    "name": "Terraforms #9796",
    "image": "https://raw.seadn.io/files/5fc0af5cad69de8dacadfd1c92ab872f.svg"
  },
  {
    "id": "terraforms-9827",
    "name": "Terraforms #9827",
    "image": "https://raw.seadn.io/files/9f39d3e814ff5f00a73ec7042d6bfcde.svg"
  },
  {
    "id": "terraforms-9818",
    "name": "Terraforms #9818",
    "image": "https://raw.seadn.io/files/0ae814b0c099b7895f990b2bd040e78a.svg"
  },
  {
    "id": "terraforms-9837",
    "name": "Terraforms #9837",
    "image": "https://raw.seadn.io/files/a51e197034d7c32da3fa7265d47dd11c.svg"
  },
  {
    "id": "terraforms-9808",
    "name": "Terraforms #9808",
    "image": "https://raw.seadn.io/files/c4486bd38c2044d58ebb70b32e37731e.svg"
  },
  {
    "id": "terraforms-9826",
    "name": "Terraforms #9826",
    "image": "https://raw.seadn.io/files/400b8d23d7cd5fd05fae73a529823b5a.svg"
  },
  {
    "id": "terraforms-9817",
    "name": "Terraforms #9817",
    "image": "https://raw.seadn.io/files/1ebb197dbbae6024fb5042ace89eba94.svg"
  },
  {
    "id": "terraforms-9795",
    "name": "Terraforms #9795",
    "image": "https://raw.seadn.io/files/085cb3d5cb7bb324c12d3811672523d4.svg"
  },
  {
    "id": "terraforms-9836",
    "name": "Terraforms #9836",
    "image": "https://raw.seadn.io/files/4a00958196cc0cc0f9b7357feece8b50.svg"
  },
  {
    "id": "terraforms-9807",
    "name": "Terraforms #9807",
    "image": "https://raw.seadn.io/files/14253882df55b6adc53f9cdc90e8cf73.svg"
  },
  {
    "id": "terraforms-9825",
    "name": "Terraforms #9825",
    "image": "https://raw.seadn.io/files/da3e261061da85b13dacf22577e3d340.svg"
  },
  {
    "id": "terraforms-9816",
    "name": "Terraforms #9816",
    "image": "https://raw.seadn.io/files/4760193c9e430275b47ed4e2f5716795.svg"
  },
  {
    "id": "terraforms-9794",
    "name": "Terraforms #9794",
    "image": "https://raw.seadn.io/files/8ef760989d275e7d6d020a1439634cb6.svg"
  },
  {
    "id": "terraforms-9835",
    "name": "Terraforms #9835",
    "image": "https://raw.seadn.io/files/3ef8ac19d49fb7b894690cd9c2e01521.svg"
  },
  {
    "id": "terraforms-9752",
    "name": "Terraforms #9752",
    "image": "https://raw.seadn.io/files/470a53b61dbb1acfbec699acf8fae4f5.svg"
  },
  {
    "id": "terraforms-9785",
    "name": "Terraforms #9785",
    "image": "https://raw.seadn.io/files/94fd3d4b9ecab7128aa07652b2c15444.svg"
  },
  {
    "id": "terraforms-9806",
    "name": "Terraforms #9806",
    "image": "https://raw.seadn.io/files/a956adb01daa277c93b9365a7bf26038.svg"
  },
  {
    "id": "terraforms-9824",
    "name": "Terraforms #9824",
    "image": "https://raw.seadn.io/files/5c63c7b0dbeaaebf9a5b8330f82d0aad.svg"
  },
  {
    "id": "terraforms-9815",
    "name": "Terraforms #9815",
    "image": "https://raw.seadn.io/files/22ba9cb2967617da8ef39c9e82cbad88.svg"
  },
  {
    "id": "terraforms-9775",
    "name": "Terraforms #9775",
    "image": "https://raw.seadn.io/files/eff933e1e7caa0b94016e26abaf64f24.svg"
  },
  {
    "id": "terraforms-9762",
    "name": "Terraforms #9762",
    "image": "https://raw.seadn.io/files/a3e5d500fbb0f9a89dfc4523b42eb0a8.svg"
  },
  {
    "id": "terraforms-9850",
    "name": "Terraforms #9850",
    "image": "https://raw.seadn.io/files/5ddbe931e04629489d82d19a1e405884.svg"
  },
  {
    "id": "terraforms-9814",
    "name": "Terraforms #9814",
    "image": "https://raw.seadn.io/files/9b831f31e385dd9740143be1bd061aca.svg"
  },
  {
    "id": "terraforms-9805",
    "name": "Terraforms #9805",
    "image": "https://raw.seadn.io/files/a1dce77069d8e7f1cb9849540aeb3e99.svg"
  },
  {
    "id": "terraforms-9751",
    "name": "Terraforms #9751",
    "image": "https://raw.seadn.io/files/24ff7e0f76263165ed48fafd615e7d06.svg"
  },
  {
    "id": "terraforms-9834",
    "name": "Terraforms #9834",
    "image": "https://raw.seadn.io/files/ed0ea000c12fc702c2bcdff4ab368107.svg"
  },
  {
    "id": "terraforms-9784",
    "name": "Terraforms #9784",
    "image": "https://raw.seadn.io/files/9478ccd3541597851cda34254d90f436.svg"
  },
  {
    "id": "terraforms-9774",
    "name": "Terraforms #9774",
    "image": "https://raw.seadn.io/files/474aa47eec96f067968e7f61629235f4.svg"
  },
  {
    "id": "terraforms-9849",
    "name": "Terraforms #9849",
    "image": "https://raw.seadn.io/files/342aba3938c2740707c59287353b9ca4.svg"
  },
  {
    "id": "terraforms-9761",
    "name": "Terraforms #9761",
    "image": "https://raw.seadn.io/files/541ea485dc7852c2b8a776fb20c2acd0.svg"
  },
  {
    "id": "terraforms-9813",
    "name": "Terraforms #9813",
    "image": "https://raw.seadn.io/files/e3c1a43c2233a7fc6df3061ec84b0942.svg"
  },
  {
    "id": "terraforms-9750",
    "name": "Terraforms #9750",
    "image": "https://raw.seadn.io/files/64a169992ec027ea631c153ec24bf18e.svg"
  },
  {
    "id": "terraforms-9804",
    "name": "Terraforms #9804",
    "image": "https://raw.seadn.io/files/8b5522fac08a32fb005a5da379555027.svg"
  },
  {
    "id": "terraforms-9833",
    "name": "Terraforms #9833",
    "image": "https://raw.seadn.io/files/3a4b6202c75a5541b20781bc87ff427b.svg"
  },
  {
    "id": "terraforms-9783",
    "name": "Terraforms #9783",
    "image": "https://raw.seadn.io/files/01300db07288af61bd8aa71ae4897b65.svg"
  },
  {
    "id": "terraforms-9793",
    "name": "Terraforms #9793",
    "image": "https://raw.seadn.io/files/da49176830dfe4e5edbee3c80476c1f5.svg"
  },
  {
    "id": "terraforms-9823",
    "name": "Terraforms #9823",
    "image": "https://raw.seadn.io/files/c64d492b2cb148ceccff7697239e8839.svg"
  }

                        ];
        actualNftData = terraformsNFTs.find(nft => nft.id.toString() === itemId);      
                            
                            
                            
                            
                              } else if (collectionId === 'the-plooshies') {
        const theplooshiesNFTs = [
            
  {
    id: 'the-plooshies-3312',
    name: 'Plooshy #3312',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3312/3312.png'
  },
  {
    id: 'the-plooshies-3301',
    name: 'Plooshy #3301',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3301/3301.png'
  },
  {
    id: 'the-plooshies-3329',
    name: 'Plooshy #3329',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3329/3329.png'
  },
  {
    id: 'the-plooshies-3306',
    name: 'Plooshy #3306',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3306/3306.png'
  },
  {
    id: 'the-plooshies-3318',
    name: 'Plooshy #3318',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3318/3318.png'
  },
  {
    id: 'the-plooshies-3313',
    name: 'Plooshy #3313',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3313/3313.png'
  },
  {
    id: 'the-plooshies-3300',
    name: 'Plooshy #3300',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3300/3300.png'
  },
  {
    id: 'the-plooshies-3326',
    name: 'Plooshy #3326',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3326/3326.png'
  },
  {
    id: 'the-plooshies-3307',
    name: 'Plooshy #3307',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3307/3307.png'
  },
  {
    id: 'the-plooshies-3319',
    name: 'Plooshy #3319',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3319/3319.png'
  },
  {
    id: 'the-plooshies-3311',
    name: 'Plooshy #3311',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3311/3311.png'
  },
  {
    id: 'the-plooshies-3303',
    name: 'Plooshy #3303',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3303/3303.png'
  },
  {
    id: 'the-plooshies-3327',
    name: 'Plooshy #3327',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3327/3327.png'
  },
  {
    id: 'the-plooshies-3308',
    name: 'Plooshy #3308',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3308/3308.png'
  },
  {
    id: 'the-plooshies-3316',
    name: 'Plooshy #3316',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3316/3316.png'
  },
  {
    id: 'the-plooshies-3321',
    name: 'Plooshy #3321',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3321/3321.png'
  },
  {
    id: 'the-plooshies-3302',
    name: 'Plooshy #3302',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3302/3302.png'
  },
  {
    id: 'the-plooshies-3323',
    name: 'Plooshy #3323',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3323/3323.png'
  },
  {
    id: 'the-plooshies-3310',
    name: 'Plooshy #3310',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3310/3310.png'
  },
  {
    id: 'the-plooshies-3332',
    name: 'Plooshy #3332',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3332/3332.png'
  },
  {
    id: 'the-plooshies-3305',
    name: 'Plooshy #3305',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3305/3305.png'
  },
  {
    id: 'the-plooshies-3325',
    name: 'Plooshy #3325',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3325/3325.png'
  },
  {
    id: 'the-plooshies-3315',
    name: 'Plooshy #3315',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3315/3315.png'
  },
  {
    id: 'the-plooshies-3320',
    name: 'Plooshy #3320',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3320/3320.png'
  },
  {
    id: 'the-plooshies-3304',
    name: 'Plooshy #3304',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3304/3304.png'
  },
  {
    id: 'the-plooshies-3314',
    name: 'Plooshy #3314',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3314/3314.png'
  },
  {
    id: 'the-plooshies-3333',
    name: 'Plooshy #3333',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3333/3333.png'
  },
  {
    id: 'the-plooshies-3324',
    name: 'Plooshy #3324',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3324/3324.png'
  },
  {
    id: 'the-plooshies-3309',
    name: 'Plooshy #3309',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3309/3309.png'
  },
  {
    id: 'the-plooshies-3317',
    name: 'Plooshy #3317',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3317/3317.png'
  },
  {
    id: 'the-plooshies-3322',
    name: 'Plooshy #3322',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3322/3322.png'
  },
  {
    id: 'the-plooshies-3328',
    name: 'Plooshy #3328',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3328/3328.png'
  },
  {
    id: 'the-plooshies-3330',
    name: 'Plooshy #3330',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3330/3330.png'
  },
  {
    id: 'the-plooshies-3331',
    name: 'Plooshy #3331',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3331/3331.png'
  },
  {
    id: 'the-plooshies-3289',
    name: 'Plooshy #3289',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3289/3289.png'
  },
  {
    id: 'the-plooshies-3220',
    name: 'Plooshy #3220',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3220/3220.png'
  },
  {
    id: 'the-plooshies-3246',
    name: 'Plooshy #3246',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3246/3246.png'
  },
  {
    id: 'the-plooshies-3290',
    name: 'Plooshy #3290',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3290/3290.png'
  },
  {
    id: 'the-plooshies-3271',
    name: 'Plooshy #3271',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3271/3271.png'
  },
  {
    id: 'the-plooshies-3283',
    name: 'Plooshy #3283',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3283/3283.png'
  },
  {
    id: 'the-plooshies-3280',
    name: 'Plooshy #3280',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3280/3280.png'
  },
  {
    id: 'the-plooshies-3287',
    name: 'Plooshy #3287',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3287/3287.png'
  },
  {
    id: 'the-plooshies-3296',
    name: 'Plooshy #3296',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3296/3296.png'
  },
  {
    id: 'the-plooshies-3248',
    name: 'Plooshy #3248',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3248/3248.png'
  },
  {
    id: 'the-plooshies-3222',
    name: 'Plooshy #3222',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3222/3222.png'
  },
  {
    id: 'the-plooshies-3269',
    name: 'Plooshy #3269',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3269/3269.png'
  },
  {
    id: 'the-plooshies-3255',
    name: 'Plooshy #3255',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3255/3255.png'
  },
  {
    id: 'the-plooshies-3299',
    name: 'Plooshy #3299',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3299/3299.png'
  },
  {
    id: 'the-plooshies-3278',
    name: 'Plooshy #3278',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3278/3278.png'
  },
  {
    id: 'the-plooshies-3288',
    name: 'Plooshy #3288',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3288/3288.png'
  },
  {
    id: 'the-plooshies-3292',
    name: 'Plooshy #3292',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3292/3292.png'
  },
  {
    id: 'the-plooshies-3295',
    name: 'Plooshy #3295',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3295/3295.png'
  },
  {
    id: 'the-plooshies-3297',
    name: 'Plooshy #3297',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3297/3297.png'
  },
  {
    id: 'the-plooshies-3272',
    name: 'Plooshy #3272',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3272/3272.png'
  },
  {
    id: 'the-plooshies-3282',
    name: 'Plooshy #3282',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3282/3282.png'
  },
  {
    id: 'the-plooshies-3254',
    name: 'Plooshy #3254',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3254/3254.png'
  },
  {
    id: 'the-plooshies-3277',
    name: 'Plooshy #3277',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3277/3277.png'
  },
  {
    id: 'the-plooshies-3197',
    name: 'Plooshy #3197',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3197/3197.png'
  },
  {
    id: 'the-plooshies-3275',
    name: 'Plooshy #3275',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3275/3275.png'
  },
  {
    id: 'the-plooshies-3286',
    name: 'Plooshy #3286',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3286/3286.png'
  },
  {
    id: 'the-plooshies-3284',
    name: 'Plooshy #3284',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3284/3284.png'
  },
  {
    id: 'the-plooshies-3298',
    name: 'Plooshy #3298',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3298/3298.png'
  },
  {
    id: 'the-plooshies-3242',
    name: 'Plooshy #3242',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3242/3242.png'
  },
  {
    id: 'the-plooshies-3249',
    name: 'Plooshy #3249',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3249/3249.png'
  },
  {
    id: 'the-plooshies-3281',
    name: 'Plooshy #3281',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3281/3281.png'
  },
  {
    id: 'the-plooshies-3291',
    name: 'Plooshy #3291',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3291/3291.png'
  },
  {
    id: 'the-plooshies-3293',
    name: 'Plooshy #3293',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3293/3293.png'
  },
  {
    id: 'the-plooshies-3285',
    name: 'Plooshy #3285',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3285/3285.png'
  },
  {
    id: 'the-plooshies-3294',
    name: 'Plooshy #3294',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3294/3294.png'
  },
  {
    id: 'the-plooshies-3253',
    name: 'Plooshy #3253',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3253/3253.png'
  },
  {
    id: 'the-plooshies-3268',
    name: 'Plooshy #3268',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3268/3268.png'
  },
  {
    id: 'the-plooshies-3264',
    name: 'Plooshy #3264',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3264/3264.png'
  },
  {
    id: 'the-plooshies-3237',
    name: 'Plooshy #3237',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3237/3237.png'
  },
  {
    id: 'the-plooshies-3204',
    name: 'Plooshy #3204',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3204/3204.png'
  },
  {
    id: 'the-plooshies-3232',
    name: 'Plooshy #3232',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3232/3232.png'
  },
  {
    id: 'the-plooshies-3245',
    name: 'Plooshy #3245',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3245/3245.png'
  },
  {
    id: 'the-plooshies-3279',
    name: 'Plooshy #3279',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3279/3279.png'
  },
  {
    id: 'the-plooshies-3195',
    name: 'Plooshy #3195',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3195/3195.png'
  },
  {
    id: 'the-plooshies-3276',
    name: 'Plooshy #3276',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3276/3276.png'
  },
  {
    id: 'the-plooshies-3247',
    name: 'Plooshy #3247',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3247/3247.png'
  },
  {
    id: 'the-plooshies-3273',
    name: 'Plooshy #3273',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3273/3273.png'
  },
  {
    id: 'the-plooshies-3221',
    name: 'Plooshy #3221',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3221/3221.png'
  },
  {
    id: 'the-plooshies-3267',
    name: 'Plooshy #3267',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3267/3267.png'
  },
  {
    id: 'the-plooshies-3274',
    name: 'Plooshy #3274',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3274/3274.png'
  },
  {
    id: 'the-plooshies-3252',
    name: 'Plooshy #3252',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3252/3252.png'
  },
  {
    id: 'the-plooshies-3270',
    name: 'Plooshy #3270',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3270/3270.png'
  },
  {
    id: 'the-plooshies-3263',
    name: 'Plooshy #3263',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3263/3263.png'
  },
  {
    id: 'the-plooshies-3240',
    name: 'Plooshy #3240',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3240/3240.png'
  },
  {
    id: 'the-plooshies-3266',
    name: 'Plooshy #3266',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3266/3266.png'
  },
  {
    id: 'the-plooshies-3207',
    name: 'Plooshy #3207',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3207/3207.png'
  },
  {
    id: 'the-plooshies-3184',
    name: 'Plooshy #3184',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3184/3184.png'
  },
  {
    id: 'the-plooshies-3265',
    name: 'Plooshy #3265',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3265/3265.png'
  },
  {
    id: 'the-plooshies-3243',
    name: 'Plooshy #3243',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3243/3243.png'
  },
  {
    id: 'the-plooshies-3262',
    name: 'Plooshy #3262',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3262/3262.png'
  },
  {
    id: 'the-plooshies-3260',
    name: 'Plooshy #3260',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3260/3260.png'
  },
  {
    id: 'the-plooshies-3244',
    name: 'Plooshy #3244',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3244/3244.png'
  },
  {
    id: 'the-plooshies-3238',
    name: 'Plooshy #3238',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3238/3238.png'
  },
  {
    id: 'the-plooshies-3234',
    name: 'Plooshy #3234',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3234/3234.png'
  },
  {
    id: 'the-plooshies-3257',
    name: 'Plooshy #3257',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3257/3257.png'
  },
  {
    id: 'the-plooshies-3261',
    name: 'Plooshy #3261',
    image: 'https://ipfs.io/ipfs/QmYjxqvV4xByAzM1qnKULXCrd3T4jRrJDdLgKN8Rz7s245/3261/3261.png'
  }   
            
                                ];
        actualNftData = theplooshiesNFTs.find(nft => nft.id.toString() === itemId);  
                                  
                             } else if (collectionId === 'killabears') {
        const killabearsNFTs = [
            
  {
    id: 'killabears-3287',
    name: 'KILLABEARS #3287',
    image: 'https://assets.killabears.com/content/killabears/img/3287-d89312fe2889debe8b14f83843d3868824f90106ab694b8249df7ed4da9a5db1.png'
  },
  {
    id: 'killabears-3227',
    name: 'KILLABEARS #3227',
    image: 'https://assets.killabears.com/content/killabears/img/3227-bdfa4bc0c372079c0cb36331865e9a9a78cf0501441a0db1a8f259806cb8d05b.png'
  },
  {
    id: 'killabears-3207',
    name: 'KILLABEARS #3207',
    image: 'https://assets.killabears.com/content/killabears/img/3207-486e160ad5df51fc876e64a56f9f8f6652beefaf4b0667eca2b7dcaaa385a33c.png'
  },
  {
    id: 'killabears-3252',
    name: 'KILLABEARS #3252',
    image: 'https://assets.killabears.com/content/killabears/img/3252-82628f29af3e7e1d52452b7d795d8d1032c06d8757662e17f91836c28e3aeed5.png'
  },
  {
    id: 'killabears-3276',
    name: 'KILLABEARS #3276',
    image: 'https://assets.killabears.com/content/killabears/img/3276-f3b4dfbb21335328421268259b96132d1a070891cf067b049ec5b26b0ba65106.png'
  },
  {
    id: 'killabears-3320',
    name: 'KILLABEARS #3320',
    image: 'https://assets.killabears.com/content/killabears/img/3320-fe54a6f06bd394345b17c5a2c39bcfde9242b835a1ae7f6e2ebe2566cb8154b1.png'
  },
  {
    id: 'killabears-3286',
    name: 'KILLABEARS #3286',
    image: 'https://assets.killabears.com/content/killabears/img/3286-429c28123c08b8a858d7c86965281e707d3030f760e6f8ecc79b9201157052c2.png'
  },
  {
    id: 'killabears-3226',
    name: 'KILLABEARS #3226',
    image: 'https://assets.killabears.com/content/killabears/img/3226-f6bcf7b833bae93f1d2215bde68a8852b2fc4ffe58a965eb2a904189de550a75.png'
  },
  {
    id: 'killabears-3206',
    name: 'KILLABEARS #3206',
    image: 'https://assets.killabears.com/content/killabears/img/3206-8331dab3733be2ef03e535f9699e78f98740703a550248b5f744aa6fe8d891dc.png'
  },
  {
    id: 'killabears-3251',
    name: 'KILLABEARS #3251',
    image: 'https://assets.killabears.com/content/killabears/img/3251-e265105b1b90b5d7d34e0546240e4b53ea16b4086f3fd303602bd92d7b3ad4f0.png'
  },
  {
    id: 'killabears-3275',
    name: 'KILLABEARS #3275',
    image: 'https://assets.killabears.com/content/killabears/img/3275-d5d2c75683d87e320e45ec0c14e7a6228f7a0028b5d67c62a20a81c54a8099ac.png'
  },
  {
    id: 'killabears-3319',
    name: 'KILLABEARS #3319',
    image: 'https://assets.killabears.com/content/killabears/img/3319-065bd6b18bd00d5b83d518207fc7b708f59a485aa1a873ffd20040c87fa3c548.png'
  },
  {
    id: 'killabears-3285',
    name: 'KILLABEARS #3285',
    image: 'https://assets.killabears.com/content/killabears/img/3285-e0da4a52886343010eda8bfe1ed4b23125b1884750989e0370f194d0d365c5f7.png'
  },
  {
    id: 'killabears-3311',
    name: 'KILLABEARS #3311',
    image: 'https://assets.killabears.com/content/killabears/img/3311-7e2670552c77e747b7490df5dcb0b660269eea11f887f870a24717a366b36843.png'
  },
  {
    id: 'killabears-3225',
    name: 'KILLABEARS #3225',
    image: 'https://assets.killabears.com/content/killabears/img/3225-ce34d346d3940642afa9141249e8d32f62a8f53815893210e00c5d527b8b8048.png'
  },
  {
    id: 'killabears-3205',
    name: 'KILLABEARS #3205',
    image: 'https://assets.killabears.com/content/killabears/img/3205-772b71d3c2d33db1e0169246caca866d0f5e204705052a497c8483f421c665c0.png'
  },
  {
    id: 'killabears-3250',
    name: 'KILLABEARS #3250',
    image: 'https://assets.killabears.com/content/killabears/img/3250-5aad6166700a709056e8b0304a548ef6d6f10532089a776a4c2e3eddc66e394e.png'
  },
  {
    id: 'killabears-3318',
    name: 'KILLABEARS #3318',
    image: 'https://assets.killabears.com/content/killabears/img/3318-7b86a3aef0e4e372b5cef024cc144514dd14bc5011bb6e01c6389d2788854e5d.png'
  },
  {
    id: 'killabears-3284',
    name: 'KILLABEARS #3284',
    image: 'https://assets.killabears.com/content/killabears/img/3284-4f132c5798c4f65741316b513bfec34d64762289b5f3ac333cfdc8af6648a94b.png'
  },
  {
    id: 'killabears-3274',
    name: 'KILLABEARS #3274',
    image: 'https://assets.killabears.com/content/killabears/img/3274-3e8b61627341ad4e60b4f11031f3efd27528ad20cf575d5ff26faf6505083bf2.png'
  },
  {
    id: 'killabears-3310',
    name: 'KILLABEARS #3310',
    image: 'https://assets.killabears.com/content/killabears/img/3310-62430e610198cdc43a974efed5f1f1a1e9c6d7d579c47b6ad2075cb71b7f24ae.png'
  },
  {
    id: 'killabears-3204',
    name: 'KILLABEARS #3204',
    image: 'https://assets.killabears.com/content/killabears/img/3204-b64f1d078212d2af3776493c1f0d9898f2ff030f896760d83f360e694989ec5a.png'
  },
  {
    id: 'killabears-3224',
    name: 'KILLABEARS #3224',
    image: 'https://assets.killabears.com/content/killabears/img/3224-5488070e155d7fd4408f31faa108646930fe32305e55818f821d7efc12e345f3.png'
  },
  {
    id: 'killabears-3249',
    name: 'KILLABEARS #3249',
    image: 'https://assets.killabears.com/content/killabears/img/3249-f8da33b69bbdb6ce7870fb6b83f9ed341c04eab5e91fa2e30ee244879132590b.png'
  },
  {
    id: 'killabears-3283',
    name: 'KILLABEARS #3283',
    image: 'https://assets.killabears.com/content/killabears/img/3283-299dc21b07e189398f5afd79af0e9261a66ab66978d88fa680d610a23de784c4.png'
  },
  {
    id: 'killabears-3309',
    name: 'KILLABEARS #3309',
    image: 'https://assets.killabears.com/content/killabears/img/3309-8663689f6b251b35c7129b2eb15f95c80d80094ba5dd7ee47fbdf50045a25aa2.png'
  },
  {
    id: 'killabears-3203',
    name: 'KILLABEARS #3203',
    image: 'https://assets.killabears.com/content/killabears/img/3203-e2080aa5bbd89bdf8aaefce2dbde25a4fa5b3f12ab9c47fa92eec7f1feb63739.png'
  },
  {
    id: 'killabears-3273',
    name: 'KILLABEARS #3273',
    image: 'https://assets.killabears.com/content/killabears/img/3273-3d79219f5b34d6f0cb66e130b1aa0a6430cfdc62b43440edfde2baf6d02a573a.png'
  },
  {
    id: 'killabears-3317',
    name: 'KILLABEARS #3317',
    image: 'https://assets.killabears.com/content/killabears/img/3317-6de8cedd9a50d713893fd36aed05fcfa8f8fb915b845243c5ea2b59bfc1309b9.png'
  },
  {
    id: 'killabears-3223',
    name: 'KILLABEARS #3223',
    image: 'https://assets.killabears.com/content/killabears/img/3223-59d21b0df01217be7c45010a0f4f9e4bb1f385d7c0d45437b83f8c1ceebee04a.png'
  },
  {
    id: 'killabears-3303',
    name: 'KILLABEARS #3303',
    image: 'https://assets.killabears.com/content/killabears/img/3303-74b0b21ffd4fd2ea1aa6a0eff7bcae8afa1e76a50e3538992f946d0b1e3f2dbf.png'
  },
  {
    id: 'killabears-3248',
    name: 'KILLABEARS #3248',
    image: 'https://assets.killabears.com/content/killabears/img/3248-1913a487361aa04e032c45cfc2eef216ff0df59c2b4a3f1683a32f205c205640.png'
  },
  {
    id: 'killabears-3182',
    name: 'KILLABEARS #3182',
    image: 'https://assets.killabears.com/content/killabears/img/3182-57b6865ea2df3f901bb526d01a46579f72895066969905cd50cffae08abc95db.png'
  },
  {
    id: 'killabears-3282',
    name: 'KILLABEARS #3282',
    image: 'https://assets.killabears.com/content/killabears/img/3282-333029e58868b1cfe845f5f4ec5302109b4a344f055c1e6936025bb023ac4688.png'
  },
  {
    id: 'killabears-3308',
    name: 'KILLABEARS #3308',
    image: 'https://assets.killabears.com/content/killabears/img/3308-8e59f4a201b004406be81aa60eb9ff8b65ef73c840c5298d0a0e04873c61ca5d.png'
  },
  {
    id: 'killabears-3267',
    name: 'KILLABEARS #3267',
    image: 'https://assets.killabears.com/content/killabears/img/3267-8534c4cc24ffeb27ab808a97d51ad77fdb19901d920d5148c4b8923092346422.png'
  },
  {
    id: 'killabears-3316',
    name: 'KILLABEARS #3316',
    image: 'https://assets.killabears.com/content/killabears/img/3316-1dcab246f5c21224455aeaf138b48323d0aa0e49d7b3bf8975abca5cf2181a74.png'
  },
  {
    id: 'killabears-3272',
    name: 'KILLABEARS #3272',
    image: 'https://assets.killabears.com/content/killabears/img/3272-b0455ba25e876aa6bb3d3dc214935bf1ccf1323a9c042c843c200c602b6b3a87.png'
  },
  {
    id: 'killabears-3222',
    name: 'KILLABEARS #3222',
    image: 'https://assets.killabears.com/content/killabears/img/3222-5077948817f650d78c483b344e2e1411bbca57995d8b195b793aa568d5b9c52d.png'
  },
  {
    id: 'killabears-3242',
    name: 'KILLABEARS #3242',
    image: 'https://assets.killabears.com/content/killabears/img/3242-19db5c43413ac576d99779507330dffa3be1a951f07e02bef21ed139c289eb39.png'
  },
  {
    id: 'killabears-3202',
    name: 'KILLABEARS #3202',
    image: 'https://assets.killabears.com/content/killabears/img/3202-c4764d1d07ef0f91f878489584552cd17994d844032c0dd753a80a7dac9d1bdb.png'
  },
  {
    id: 'killabears-3307',
    name: 'KILLABEARS #3307',
    image: 'https://assets.killabears.com/content/killabears/img/3307-b35908ffdfddab72f8a457d0cc6cf39d20c92b02f891f571b881458f63ebe0b0.png'
  },
  {
    id: 'killabears-3237',
    name: 'KILLABEARS #3237',
    image: 'https://assets.killabears.com/content/killabears/img/3237-71448b9dfa6b84d1db197945f1ad9cbdab693a55347371803e21531d63f3d9bc.png'
  },
  {
    id: 'killabears-3331',
    name: 'KILLABEARS #3331',
    image: 'https://assets.killabears.com/content/killabears/img/3331-6e3ee546282ceaa35c10b79aa21ead395a8eb39e8a5b1c6b9cb25cbcf4078bef.png'
  },
  {
    id: 'killabears-3217',
    name: 'KILLABEARS #3217',
    image: 'https://assets.killabears.com/content/killabears/img/3217-2feb034241e1b9dc12d7e9a44bc55caf57a0eb335bf9b3eb0be8607e385d2ff0.png'
  },
  {
    id: 'killabears-3236',
    name: 'KILLABEARS #3236',
    image: 'https://assets.killabears.com/content/killabears/img/3236-d679e40333e66b8cea3187021efffd19bc86deb4132d6355d104c9dca4115b3f.png'
  },
  {
    id: 'killabears-3297',
    name: 'KILLABEARS #3297',
    image: 'https://assets.killabears.com/content/killabears/img/3297-e35129977b4d3bd524530fcd31fd732c85f00f04539a47eaea53094d14c4960d.png'
  },
  {
    id: 'killabears-3330',
    name: 'KILLABEARS #3330',
    image: 'https://assets.killabears.com/content/killabears/img/3330-e47208ee8381b6c089be7af294803330783ca890d951bd8f1fa69c1ca3852be6.png'
  },
  {
    id: 'killabears-3262',
    name: 'KILLABEARS #3262',
    image: 'https://assets.killabears.com/content/killabears/img/3262-fa853bc2aea9e107b2438f70167762e8e16745bba883763573afe0cae0c51d1b.png'
  },
  {
    id: 'killabears-3216',
    name: 'KILLABEARS #3216',
    image: 'https://assets.killabears.com/content/killabears/img/3216-a78452db29078f26d4cd75114fc99db39e3b83ce7939dbb6245308585d652288.png'
  },
  {
    id: 'killabears-3296',
    name: 'KILLABEARS #3296',
    image: 'https://assets.killabears.com/content/killabears/img/3296-6c7d5ec3ca9b25c7a2f4a79835171161fc0ae904a08269bf36c99cd1d8b33a9f.png'
  },
  {
    id: 'killabears-3235',
    name: 'KILLABEARS #3235',
    image: 'https://assets.killabears.com/content/killabears/img/3235-bfb4b8ba045254f37b1a4e35bccd5dff8fb8058a2f4af9d5a5914f9148d57bcb.png'
  },
  {
    id: 'killabears-3329',
    name: 'KILLABEARS #3329',
    image: 'https://assets.killabears.com/content/killabears/img/3329-fe04875db3a44d388dc95331d8e88f7736646796d09b27c68102bda317b97b55.png'
  },
  {
    id: 'killabears-3261',
    name: 'KILLABEARS #3261',
    image: 'https://assets.killabears.com/content/killabears/img/3261-62e65ca37e03fc1565e78c5cb0a69ad6b46d6bebd9136f7ccd4977773e618b05.png'
  },
  {
    id: 'killabears-3215',
    name: 'KILLABEARS #3215',
    image: 'https://assets.killabears.com/content/killabears/img/3215-7a3e8673f377606fe53ee4c790ca1e52bcd4f1dba4808f58e77b33488205ae59.png'
  },
  {
    id: 'killabears-3295',
    name: 'KILLABEARS #3295',
    image: 'https://assets.killabears.com/content/killabears/img/3295-9fa90f42ae8001fc75024358c5c655649a18425b3bb64d10fc8846b08fc7aaf8.png'
  },
  {
    id: 'killabears-3328',
    name: 'KILLABEARS #3328',
    image: 'https://assets.killabears.com/content/killabears/img/3328-66a8998ce321f664843bc3362800cd76c18b537b2e2d3732441e1323aee34180.png'
  },
  {
    id: 'killabears-3234',
    name: 'KILLABEARS #3234',
    image: 'https://assets.killabears.com/content/killabears/img/3234-8da0d8ccc77f804a887a626f5fd49e9ec48821a3470a87f05ce7088d8c6774bd.png'
  },
  {
    id: 'killabears-3260',
    name: 'KILLABEARS #3260',
    image: 'https://assets.killabears.com/content/killabears/img/3260-a9587a2bcb8e0c81b5ef12cfd37fc5509f150c4e7c57fc80cecfb5ac903325bb.png'
  },
  {
    id: 'killabears-3214',
    name: 'KILLABEARS #3214',
    image: 'https://assets.killabears.com/content/killabears/img/3214-01965ac7bbc9ab0aa1ff258333fde1978b0a31f3c687f990079d2144e65b0121.png'
  },
  {
    id: 'killabears-3294',
    name: 'KILLABEARS #3294',
    image: 'https://assets.killabears.com/content/killabears/img/3294-3c8764de1f12a74b750a87733484a1b28fe92c8de2777e5b4ad48960ed7ffe66.png'
  },
  {
    id: 'killabears-3233',
    name: 'KILLABEARS #3233',
    image: 'https://assets.killabears.com/content/killabears/img/3233-2704e1db616f56bb5d59a58957f75be0e0075f6cb78f813c877e2938b04812b4.png'
  },
  {
    id: 'killabears-3259',
    name: 'KILLABEARS #3259',
    image: 'https://assets.killabears.com/content/killabears/img/3259-2f4bf6c9a1c9d51834a23234ee6816f63c4539597b6dab5c7483bc48a988e606.png'
  },
  {
    id: 'killabears-3327',
    name: 'KILLABEARS #3327',
    image: 'https://assets.killabears.com/content/killabears/img/3327-5b6a8b8d9c9d436e7cad759f248afa1d1377b254de4f5f3a3fb59fa46ca1bda5.png'
  },
  {
    id: 'killabears-3213',
    name: 'KILLABEARS #3213',
    image: 'https://assets.killabears.com/content/killabears/img/3213-d0f55716e0193577b6e10ea291d56bbf0812b0ca11128233d565da778ba272d5.png'
  },
  {
    id: 'killabears-3293',
    name: 'KILLABEARS #3293',
    image: 'https://assets.killabears.com/content/killabears/img/3293-52394be22d42c618bb3dba3013b457373024319dd50a7f40d1b7edae60d2ea9b.png'
  },
  {
    id: 'killabears-3258',
    name: 'KILLABEARS #3258',
    image: 'https://assets.killabears.com/content/killabears/img/3258-eed4794b5d099cfbc59f7f9681d9b4a04afcfa66ee52079f7e4099b45897afcb.png'
  },
  {
    id: 'killabears-3326',
    name: 'KILLABEARS #3326',
    image: 'https://assets.killabears.com/content/killabears/img/3326-fde11301e91b810f330b9658e7474d8df2c0df038e9659bbf04f12ae99fa8cf7.png'
  },
  {
    id: 'killabears-3232',
    name: 'KILLABEARS #3232',
    image: 'https://assets.killabears.com/content/killabears/img/3232-d92e2249d33d2380422450e53f7ae708ac5f6fc181018f22a5622f34f9f22609.png'
  },
  {
    id: 'killabears-3212',
    name: 'KILLABEARS #3212',
    image: 'https://assets.killabears.com/content/killabears/img/3212-035981f244a78bddc549001f5a42ae939081210c09b311009b007e5239e1d54f.png'
  },
  {
    id: 'killabears-3292',
    name: 'KILLABEARS #3292',
    image: 'https://assets.killabears.com/content/killabears/img/3292-677843126a62d31b4c4afb51d6d53b1e4a7ac4464aba84cf706643ba211a36a6.png'
  },
  {
    id: 'killabears-3257',
    name: 'KILLABEARS #3257',
    image: 'https://assets.killabears.com/content/killabears/img/3257-442b0a79e96462ea8a4a3b194fc6adc3dd45e424ebd21c7bdbf133d17c46be11.png'
  },
  {
    id: 'killabears-3325',
    name: 'KILLABEARS #3325',
    image: 'https://assets.killabears.com/content/killabears/img/3325-29e9503e8a01737e4d6c7c0cbfe5c058240bfa1b4a8bdf1edd41b310b4d92cb4.png'
  },
  {
    id: 'killabears-3231',
    name: 'KILLABEARS #3231',
    image: 'https://assets.killabears.com/content/killabears/img/3231-f05e667c40dc99978d37694d169771abb1290138219d39710bb40c87478a78c3.png'
  },
  {
    id: 'killabears-3211',
    name: 'KILLABEARS #3211',
    image: 'https://assets.killabears.com/content/killabears/img/3211-4830e04b66e635da864f4440ca01772bc799d06f383f2102f88ad0835965fbc5.png'
  },
  {
    id: 'killabears-3291',
    name: 'KILLABEARS #3291',
    image: 'https://assets.killabears.com/content/killabears/img/3291-e468b3c31d26e1f9f39ed74a9a761d40ce6f4d59a892fb12975131902a8df6ff.png'
  },
  {
    id: 'killabears-3256',
    name: 'KILLABEARS #3256',
    image: 'https://assets.killabears.com/content/killabears/img/3256-217bf41a0845276e8e374ee73813b4237d8a9de349f5873cb015423073d96899.png'
  },
  {
    id: 'killabears-3324',
    name: 'KILLABEARS #3324',
    image: 'https://assets.killabears.com/content/killabears/img/3324-4dacb0546976e4051a2896539208b17c9cb048fdfe0a645cf3eb962489d166e5.png'
  },
  {
    id: 'killabears-3290',
    name: 'KILLABEARS #3290',
    image: 'https://assets.killabears.com/content/killabears/img/3290-81644938aad10472928a04710eae6d9a3647b67a875985d405bbaddc8b4d8b6b.png'
  },
  {
    id: 'killabears-3230',
    name: 'KILLABEARS #3230',
    image: 'https://assets.killabears.com/content/killabears/img/3230-f236cd1a5254a8f767b652a4d097c00d750d58e0ea05e7f1c74d83a7ac385c6d.png'
  },
  {
    id: 'killabears-3210',
    name: 'KILLABEARS #3210',
    image: 'https://assets.killabears.com/content/killabears/img/3210-8b4bd5fc1dd635e8d272a1f83163e64794aa4b82863d8d451672722910a22f8c.png'
  },
  {
    id: 'killabears-3255',
    name: 'KILLABEARS #3255',
    image: 'https://assets.killabears.com/content/killabears/img/3255-f3f1a5d982a6d08e4bd1392e5a903377a339bd18d6ae7e5051b1061a703a9b2a.png'
  },
  {
    id: 'killabears-3323',
    name: 'KILLABEARS #3323',
    image: 'https://assets.killabears.com/content/killabears/img/3323-bcb53fe5601b993bf95494ff9f3449988dc3ef28df2e30a226937c8804f9764d.png'
  },
  {
    id: 'killabears-3289',
    name: 'KILLABEARS #3289',
    image: 'https://assets.killabears.com/content/killabears/img/3289-31091b1984549d28850305016cef5e6bd1c345d05b1c2536e23ac80ee76288e5.png'
  },
  {
    id: 'killabears-3229',
    name: 'KILLABEARS #3229',
    image: 'https://assets.killabears.com/content/killabears/img/3229-454aa67a3cc6fffe19ae765d941db249726b5e9e21ac768d7028a8fa797232ba.png'
  },
  {
    id: 'killabears-3209',
    name: 'KILLABEARS #3209',
    image: 'https://assets.killabears.com/content/killabears/img/3209-1e820c168ac11172e6e3fc97f7bca5fe70e669425a9084d902f6b361c9f53c9d.png'
  },
  {
    id: 'killabears-3254',
    name: 'KILLABEARS #3254',
    image: 'https://assets.killabears.com/content/killabears/img/3254-6d3cfa010f4a5870812054421efc097b78010beaf99b1b04c23dc0206bb1dd4a.png'
  },
  {
    id: 'killabears-3322',
    name: 'KILLABEARS #3322',
    image: 'https://assets.killabears.com/content/killabears/img/3322-0853bbd2f382d0ced766dc96c3ef42f3bee97e158ecf82b68d8d03350f184c20.png'
  },
  {
    id: 'killabears-3288',
    name: 'KILLABEARS #3288',
    image: 'https://assets.killabears.com/content/killabears/img/3288-fdcc1becaae9a9b0bedea9966f3e9520189b8089c1e6be759d6e05e0a593bfc1.png'
  },
  {
    id: 'killabears-3228',
    name: 'KILLABEARS #3228',
    image: 'https://assets.killabears.com/content/killabears/img/3228-53a7e01d1ec18038611240a3f0263702c456f09708dac22f64c5f4c627d69da0.png'
  },
  {
    id: 'killabears-3208',
    name: 'KILLABEARS #3208',
    image: 'https://assets.killabears.com/content/killabears/img/3208-11635d4d35f6d917f7ff1550df56f3f564daae8e2955c5bdd7ad9be572434865.png'
  },
  {
    id: 'killabears-3253',
    name: 'KILLABEARS #3253',
    image: 'https://assets.killabears.com/content/killabears/img/3253-0ad948da68c990e3cac1d6378c321370c03b5f7a61a399a424d9a93d16f4dc3b.png'
  },
  {
    id: 'killabears-3277',
    name: 'KILLABEARS #3277',
    image: 'https://assets.killabears.com/content/killabears/img/3277-ed842cc3d88b24b9c1e0c6ced5a3ece18f7d0b2be2cb618d16c1cb1b930ca81b.png'
  },
  {
    id: 'killabears-3321',
    name: 'KILLABEARS #3321',
    image: 'https://assets.killabears.com/content/killabears/img/3321-c2c4bbf21283d4f29835f8c4b8b5bb144ff8a07d0be9aeb6440c06ff9c74b353.png'
  }
                                ];
        actualNftData = killabearsNFTs.find(nft => nft.id.toString() === itemId);  
                                        
                                        
                             } else if (collectionId === 'radbro-webring') {
        const radrowebringNFTs = [
            
  {
    id: 'radbro-webring-4999',
    name: 'Radbro Webring #4999',
    image: 'https://ipfs.io/ipfs/bafybeia6vlo3itthawxl4oyfc6kbu333lsljz3dzaayhohlpvjrhk4j2le'
  },
  {
    id: 'radbro-webring-4998',
    name: 'Radbro Webring #4998',
    image: 'https://ipfs.io/ipfs/bafybeihhmyduglzvieeutveycyg5slsa3ij5dtg4quxqe766epuc2rfnzy'
  },
  {
    id: 'radbro-webring-4994',
    name: 'Radbro Webring #4994',
    image: 'https://ipfs.io/ipfs/bafybeialm2sgd4uzfoszlufyzpcivwo4rwtbsodovthtwgupi2shxocgva'
  },
  {
    id: 'radbro-webring-4993',
    name: 'Radbro Webring #4993',
    image: 'https://ipfs.io/ipfs/bafybeibtruqyv7uni2a5s3qaboxno7lfme32k3xe4gqwgzxsfag4pdsjs4'
  },
  {
    id: 'radbro-webring-4992',
    name: 'Radbro Webring #4992',
    image: 'https://ipfs.io/ipfs/bafybeicl2mau73sxtzyhx4rls57v4nandlx5h4xqrfmrhrkgk72rirnqje'
  },
  {
    id: 'radbro-webring-4991',
    name: 'Radbro Webring #4991',
    image: 'https://ipfs.io/ipfs/bafybeidev5qw7iqfex2moymzalcxwz6iy5yi2hauurhkwhrr6hgbeds6mu'
  },
  {
    id: 'radbro-webring-4990',
    name: 'Radbro Webring #4990',
    image: 'https://ipfs.io/ipfs/bafybeicnp4jc5qs7zhx77njxjz6k3cqouao4jmnfwvws4wfwjhtv4h7j2i'
  },
  {
    id: 'radbro-webring-4989',
    name: 'Radbro Webring #4989',
    image: 'https://ipfs.io/ipfs/bafybeiawi3i2yieut5txduv6oasbp56spxanej4a326636ugdcxils3jve'
  },
  {
    id: 'radbro-webring-4987',
    name: 'Radbro Webring #4987',
    image: 'https://ipfs.io/ipfs/bafybeicophgz2mkf45lvlg7pcu56vt7aykp2spudddgcp4qx6xgo2uhkru'
  },
  {
    id: 'radbro-webring-4988',
    name: 'Radbro Webring #4988',
    image: 'https://ipfs.io/ipfs/bafybeibrfo4uewrbqd3qmfjcm5eslkue4a7yl725v4iklfqhppg3gq2num'
  },
  {
    id: 'radbro-webring-4995',
    name: 'Radbro Webring #4995',
    image: 'https://ipfs.io/ipfs/bafybeiba4bykeaa3difeedczi334g64af5m7xzrdy6q5xkrdhaqhixedum'
  },
  {
    id: 'radbro-webring-5000',
    name: 'Radbro Webring #5000',
    image: 'https://ipfs.io/ipfs/bafybeicdzjmqmi7xvd33w5diir7jaz6up676vtzofa56iba2mpk34n3h6e'
  },
  {
    id: 'radbro-webring-4997',
    name: 'Radbro Webring #4997',
    image: 'https://ipfs.io/ipfs/bafybeice2jyeamntjebswdqarzwf6kmrrqsmsdke4m4poyi5buz2kzbqeq'
  },
  {
    id: 'radbro-webring-4996',
    name: 'Radbro Webring #4996',
    image: 'https://ipfs.io/ipfs/bafybeich5iv5tjgly65ashy663usq2jyj3kginqo5fpddy5nmo5xmlfjlq'
  },
  {
    id: 'radbro-webring-4986',
    name: 'Radbro Webring #4986',
    image: 'https://ipfs.io/ipfs/bafybeifajo52k7z6gwnrmfblid6l3p4zhxeoh76pizurbpfcvtbczhprc4'
  },
  {
    id: 'radbro-webring-4984',
    name: 'Radbro Webring #4984',
    image: 'https://ipfs.io/ipfs/bafybeidojwslxde3bpaeb2v5txpnalfp6367ozr5obvtfvhep3cqhtrqgi'
  },
  {
    id: 'radbro-webring-4985',
    name: 'Radbro Webring #4985',
    image: 'https://ipfs.io/ipfs/bafybeihqewazikccdtvj4xtrkjypkd72osw4xoboubbqn6sanpbao3nf3y'
  },
  {
    id: 'radbro-webring-4978',
    name: 'Radbro Webring #4978',
    image: 'https://ipfs.io/ipfs/bafybeibyvs3jbwevttvnqq3ptc53qnkgrfxuj2hznow57kqev7uyn7j53a'
  },
  {
    id: 'radbro-webring-4977',
    name: 'Radbro Webring #4977',
    image: 'https://ipfs.io/ipfs/bafybeielcc2msqlwhef4idvhbuxsmvx7hmy6qlfrpgsxfsc4i7daqdjgce'
  },
  {
    id: 'radbro-webring-4976',
    name: 'Radbro Webring #4976',
    image: 'https://ipfs.io/ipfs/bafybeiez7mdvwiu7o37fylsrl6xg3g2dhzahht6mpiexljq5hqp7k5ovai'
  },
  {
    id: 'radbro-webring-4975',
    name: 'Radbro Webring #4975',
    image: 'https://ipfs.io/ipfs/bafybeicydedcpreyhpuog43tlgd3id2elljoltwy4mzoeeua2dpdwjxwoe'
  },
  {
    id: 'radbro-webring-4973',
    name: 'Radbro Webring #4973',
    image: 'https://ipfs.io/ipfs/bafybeihb43bnglnsk7pm3l3du2dc5vmwuu45ivqjsg3525zxhdntiwr2uu'
  },
  {
    id: 'radbro-webring-4972',
    name: 'Radbro Webring #4972',
    image: 'https://ipfs.io/ipfs/bafybeif24ycdhskwx3j75lhhhmedyikceisf4qt54subx2tldwwtlhhnte'
  },
  {
    id: 'radbro-webring-4971',
    name: 'Radbro Webring #4971',
    image: 'https://ipfs.io/ipfs/bafybeigk4wlum5ie3scom3ehy2e64mludvf3l7fagdncc4pcpllrzzbui4'
  },
  {
    id: 'radbro-webring-4970',
    name: 'Radbro Webring #4970',
    image: 'https://ipfs.io/ipfs/bafybeibgjodqm4qbhmq2k2zumcvyyxjeztm7pumkw66ivt6w6yxyh7k7uq'
  },
  {
    id: 'radbro-webring-4969',
    name: 'Radbro Webring #4969',
    image: 'https://ipfs.io/ipfs/bafybeibdrv4iav3mh24lvvyadtodquvso55xu3c2okhrphwspnctvnl7ma'
  },
  {
    id: 'radbro-webring-4968',
    name: 'Radbro Webring #4968',
    image: 'https://ipfs.io/ipfs/bafybeif4niotec43nk55ub2ultlhzhbe7pykxnxh26ame5ddhunpqjxbmq'
  },
  {
    id: 'radbro-webring-4966',
    name: 'Radbro Webring #4966',
    image: 'https://ipfs.io/ipfs/bafybeiezndwtonyshku56gfpefg777c7jhnudlcs7iwpcdfr6uu34dymoa'
  },
  {
    id: 'radbro-webring-4962',
    name: 'Radbro Webring #4962',
    image: 'https://ipfs.io/ipfs/bafybeig6k2r5ixhvgyn5glhty6btwdpoj2kkznr7tjkaoqzypglejgymfi'
  },
  {
    id: 'radbro-webring-4963',
    name: 'Radbro Webring #4963',
    image: 'https://ipfs.io/ipfs/bafybeieggmouuuz3hfup5gzyfkuveemopjkm375ykv3pcjz447ypfzuf3u'
  },
  {
    id: 'radbro-webring-4961',
    name: 'Radbro Webring #4961',
    image: 'https://ipfs.io/ipfs/bafybeidcwbecjfcl6vrbbdkwdqgquvbou66zrgeyz7zla335d3qvcbcp2a'
  },
  {
    id: 'radbro-webring-4979',
    name: 'Radbro Webring #4979',
    image: 'https://ipfs.io/ipfs/bafybeibqfwjdmluahh6scpvvwudq5xcnihrtvprf2siovbaklhh4yab3la'
  },
  {
    id: 'radbro-webring-4967',
    name: 'Radbro Webring #4967',
    image: 'https://ipfs.io/ipfs/bafybeia6neijtrw2t226gy5w3zskeyftyfksblww72ah53nw5cv5qyhyse'
  },
  {
    id: 'radbro-webring-4982',
    name: 'Radbro Webring #4982',
    image: 'https://ipfs.io/ipfs/bafybeihppplz655qw5fnreaggl6e4nvzq2qbuznjfmtc3gxxrtcbjydqsu'
  },
  {
    id: 'radbro-webring-4965',
    name: 'Radbro Webring #4965',
    image: 'https://ipfs.io/ipfs/bafybeib7kt2ttnr6htwh5g4xphapfdtfrtvmh7fcmx2o5trum2virra5xm'
  },
  {
    id: 'radbro-webring-4983',
    name: 'Radbro Webring #4983',
    image: 'https://ipfs.io/ipfs/bafybeifkrbwkghtcq2f52uu27xw6i67e7c2hq3e3w7vwlpxjyjp3bdjur4'
  },
  {
    id: 'radbro-webring-4964',
    name: 'Radbro Webring #4964',
    image: 'https://ipfs.io/ipfs/bafybeihjyfsruj5feesemntkffmtjadmapelrlz5ehijnidipgs4ovea7a'
  },
  {
    id: 'radbro-webring-4980',
    name: 'Radbro Webring #4980',
    image: 'https://ipfs.io/ipfs/bafybeihquels6z6yn3gg3utloezk2votbmdppegyeor6gsxxhms2s5yb7q'
  },
  {
    id: 'radbro-webring-4981',
    name: 'Radbro Webring #4981',
    image: 'https://ipfs.io/ipfs/bafybeicmnkqvlizapgl5uc7uarwfzzjk4lpgb2sehfcicyb564bggyo47y'
  },
  {
    id: 'radbro-webring-4974',
    name: 'Radbro Webring #4974',
    image: 'https://ipfs.io/ipfs/bafybeigorxztvbnjmzdink3um6nvcgtzd6ps2fhijjbuviffq2l47vacom'
  },
  {
    id: 'radbro-webring-4960',
    name: 'Radbro Webring #4960',
    image: 'https://ipfs.io/ipfs/bafybeicfedvthf6b2gbi6ydjtc5fg273bc3y3eekhdg7u5a433xfll5lam'
  },
  {
    id: 'radbro-webring-4959',
    name: 'Radbro Webring #4959',
    image: 'https://ipfs.io/ipfs/bafybeiboknpbf57s5helfhyyizzmyz5khsjwzf4i4hf7xam6mahr6hvh74'
  },
  {
    id: 'radbro-webring-4958',
    name: 'Radbro Webring #4958',
    image: 'https://ipfs.io/ipfs/bafybeidjqpcihl7p4tvvjo3ve6d6tgy2skynbvk4yy4qwz3jpzq6mor74u'
  },
  {
    id: 'radbro-webring-4957',
    name: 'Radbro Webring #4957',
    image: 'https://ipfs.io/ipfs/bafybeieeli62utothcl64ncvsd55fncnq4mgfkvej42e3ka34jyj5hzltu'
  },
  {
    id: 'radbro-webring-4955',
    name: 'Radbro Webring #4955',
    image: 'https://ipfs.io/ipfs/bafybeic6oo6yy735awaco5jdme4wm7zwfcmjail6f5pot4spzyir54naye'
  },
  {
    id: 'radbro-webring-4956',
    name: 'Radbro Webring #4956',
    image: 'https://ipfs.io/ipfs/bafybeigr3ilbxjx626uk2ehwkvnq7skdf5hbqey2seel76e6x3jq75quzy'
  },
  {
    id: 'radbro-webring-4954',
    name: 'Radbro Webring #4954',
    image: 'https://ipfs.io/ipfs/bafybeif3i7ly7xjganjzkhen7jmmpybor732wffhdjw4nl2uglzxy6vtye'
  },
  {
    id: 'radbro-webring-4951',
    name: 'Radbro Webring #4951',
    image: 'https://ipfs.io/ipfs/bafybeiecogsuq3cshjiffhdrfswxuube5xlfazrybkl6gkk6w7wpzwi5ka'
  },
  {
    id: 'radbro-webring-4952',
    name: 'Radbro Webring #4952',
    image: 'https://ipfs.io/ipfs/bafybeiaeq3roeey5a5jmlttwnysxup3htapomynba6ky32tprsnlh7giq4'
  },
  {
    id: 'radbro-webring-4948',
    name: 'Radbro Webring #4948',
    image: 'https://ipfs.io/ipfs/bafybeia7bdadwvexqirzruw4z3a7afuz6tkicifyrfing6vmkaktjbtgp4'
  },
  {
    id: 'radbro-webring-4947',
    name: 'Radbro Webring #4947',
    image: 'https://ipfs.io/ipfs/bafybeiamw3pkjdowyqnlueoykp35fjyoce37oouwsmxlptoll7qewq7vvi'
  },
  {
    id: 'radbro-webring-4946',
    name: 'Radbro Webring #4946',
    image: 'https://ipfs.io/ipfs/bafybeie6zss46vnnwabl52dlfu3bbmvghupf22yxdbljaryfjqr3rf3jbu'
  },
  {
    id: 'radbro-webring-4945',
    name: 'Radbro Webring #4945',
    image: 'https://ipfs.io/ipfs/bafybeid2hog47wlfhjwfnrhzwpcyhbl5mq3ngykx632xde6x4iny357vk4'
  },
  {
    id: 'radbro-webring-4944',
    name: 'Radbro Webring #4944',
    image: 'https://ipfs.io/ipfs/bafybeidxyj6fithfivgecnyspglcjwralhvkhws44p5tzweg72avuelp64'
  },
  {
    id: 'radbro-webring-4942',
    name: 'Radbro Webring #4942',
    image: 'https://ipfs.io/ipfs/bafybeidrcnwchkyehjplz6qn5ngefybmlszxpao7yueazynhcpl4mws5aa'
  },
  {
    id: 'radbro-webring-4943',
    name: 'Radbro Webring #4943',
    image: 'https://ipfs.io/ipfs/bafybeid7pirz2j5jxwuvar4jqwzkiup6e2v6k4oknqkxithmeqqwj2bvoa'
  },
  {
    id: 'radbro-webring-4953',
    name: 'Radbro Webring #4953',
    image: 'https://ipfs.io/ipfs/bafybeigfrf7vofujtuxu6kbsmqxj73vpn77lhg3eryrppvqiuw3shbzqnm'
  },
  {
    id: 'radbro-webring-4949',
    name: 'Radbro Webring #4949',
    image: 'https://ipfs.io/ipfs/bafybeigictfhac6reyakiihstpjhlbmer5qkbr6qyejhlhosgqk4enizvu'
  },
  {
    id: 'radbro-webring-4950',
    name: 'Radbro Webring #4950',
    image: 'https://ipfs.io/ipfs/bafybeiftdcdy3brrp6yf3fnkrwc6be6iotdtsnjqc6tio6wvdxqjio7csy'
  },
  {
    id: 'radbro-webring-4941',
    name: 'Radbro Webring #4941',
    image: 'https://ipfs.io/ipfs/bafybeihm2atrdww2x7y2ryxozddilv2gp3qwrot3jcicizc7vipoldbxgi'
  },
  {
    id: 'radbro-webring-4940',
    name: 'Radbro Webring #4940',
    image: 'https://ipfs.io/ipfs/bafybeiehghkvjslxga6rgodzee6aceig3zomdgau7z6sk2gsj2sp6hrswu'
  },
  {
    id: 'radbro-webring-4939',
    name: 'Radbro Webring #4939',
    image: 'https://ipfs.io/ipfs/bafybeihs43k3prsk4xexnjlthkexgvzhuczktd4tpkm4cfxql4nverzyhi'
  },
  {
    id: 'radbro-webring-4936',
    name: 'Radbro Webring #4936',
    image: 'https://ipfs.io/ipfs/bafybeiauc47xbbue4gtolr3x4dgapxwbal3ja3kuwfbpeflrjcjx2ztfvm'
  },
  {
    id: 'radbro-webring-4937',
    name: 'Radbro Webring #4937',
    image: 'https://ipfs.io/ipfs/bafybeiggwpjyowbdh4aoprnheximpxgersx4e43dy6x6iwvbdl7c5fx3yu'
  },
  {
    id: 'radbro-webring-4938',
    name: 'Radbro Webring #4938',
    image: 'https://ipfs.io/ipfs/bafybeidpddcxogjv3rmts62xcczcgydacfxnpgty336olto37zqgmgy4ci'
  },
  {
    id: 'radbro-webring-4935',
    name: 'Radbro Webring #4935',
    image: 'https://ipfs.io/ipfs/bafybeiacc25fdwspbwv6xdsvmmdav3tizq2bxyefhpemujco5b2y73sl2a'
  },
  {
    id: 'radbro-webring-4934',
    name: 'Radbro Webring #4934',
    image: 'https://ipfs.io/ipfs/bafybeig52fez6cy4uv5tuelkaeadrkzw7vpiwqqzkrkkx7g4vxilfzzn7y'
  },
  {
    id: 'radbro-webring-4933',
    name: 'Radbro Webring #4933',
    image: 'https://ipfs.io/ipfs/bafybeibokhcbca4n3ok7sl4n4bv73xgskx5mnqj7z6ebiqjxk5fz4sb5pi'
  },
  {
    id: 'radbro-webring-4930',
    name: 'Radbro Webring #4930',
    image: 'https://ipfs.io/ipfs/bafybeie34vp2dlo255fof4vf6ts2righxvqnvugwsxxyfn7i2qywpdhhmm'
  },
  {
    id: 'radbro-webring-4931',
    name: 'Radbro Webring #4931',
    image: 'https://ipfs.io/ipfs/bafybeib4mf6pw7hpqooni4kb4fx3qtivfuocwabc3z4dpklsraumaceyhy'
  },
  {
    id: 'radbro-webring-4932',
    name: 'Radbro Webring #4932',
    image: 'https://ipfs.io/ipfs/bafybeifbbigroeq5uvsbwkn3la75jufh6sukuzwujdsuznozswihjjjzoi'
  },
  {
    id: 'radbro-webring-4928',
    name: 'Radbro Webring #4928',
    image: 'https://ipfs.io/ipfs/bafybeihu76ffd2gtb5vbjfde2v7n537xepd35ltp3wrpeswsgruys7xtvy'
  },
  {
    id: 'radbro-webring-4929',
    name: 'Radbro Webring #4929',
    image: 'https://ipfs.io/ipfs/bafybeiene5qkedq4t5vjylfson7en26vgm5nrehyms7hxk54ol2rxsilcu'
  },
  {
    id: 'radbro-webring-4927',
    name: 'Radbro Webring #4927',
    image: 'https://ipfs.io/ipfs/bafybeie4jdkxnof3y5i3dwybcixxvpauc74bbde3b552rzqcuyirq7p5ia'
  },
  {
    id: 'radbro-webring-4926',
    name: 'Radbro Webring #4926',
    image: 'https://ipfs.io/ipfs/bafybeicshwv65lkaklaqum44boguepgr2rsggfbhakgsdp7aheap32wlxi'
  },
  {
    id: 'radbro-webring-4925',
    name: 'Radbro Webring #4925',
    image: 'https://ipfs.io/ipfs/bafybeigq6u362lloefirsrqnq6zjfmjakyqk7xfxykivegcvkvpnx3nm5a'
  },
  {
    id: 'radbro-webring-4924',
    name: 'Radbro Webring #4924',
    image: 'https://ipfs.io/ipfs/bafybeiehxbg5aqkb2ty5amnsxpl5w37xdtope5amf56j6sg7i23xchjelu'
  },
  {
    id: 'radbro-webring-4922',
    name: 'Radbro Webring #4922',
    image: 'https://ipfs.io/ipfs/bafybeih3quaxnyitkwqawxmlpbvzohugxn3j4bsyyk6exbdnhotadireq4'
  },
  {
    id: 'radbro-webring-4923',
    name: 'Radbro Webring #4923',
    image: 'https://ipfs.io/ipfs/bafybeibv2twipkuhrifa2qtopmvjdjo3y3yjm757k7bdsgvuzinvrlg2uy'
  },
  {
    id: 'radbro-webring-4921',
    name: 'Radbro Webring #4921',
    image: 'https://ipfs.io/ipfs/bafybeifjasgzpw3vgcfx6vgebt23w42np2fuh6poqevwvgnwa3rlis5av4'
  },
  {
    id: 'radbro-webring-4919',
    name: 'Radbro Webring #4919',
    image: 'https://ipfs.io/ipfs/bafybeiaygq4pkuhgd5lb44cvyi2rduenlrqeevuq5bccqx45kfjijf7gy4'
  },
  {
    id: 'radbro-webring-4920',
    name: 'Radbro Webring #4920',
    image: 'https://ipfs.io/ipfs/bafybeih24nrfzfzegbm7y7spfcecqbexjepwwwe2lrtswluzeus7rt27pq'
  },
  {
    id: 'radbro-webring-4915',
    name: 'Radbro Webring #4915',
    image: 'https://ipfs.io/ipfs/bafybeihiz5ailjtjef2p7jprg7wgym7hko7a26ci7lsksk3rshorjd2ix4'
  },
  {
    id: 'radbro-webring-4916',
    name: 'Radbro Webring #4916',
    image: 'https://ipfs.io/ipfs/bafybeihelvqi3swrt2tlh3iiwwmhzy6oihqhq7wewdi36ujxix5lwycz4y'
  },
  {
    id: 'radbro-webring-4914',
    name: 'Radbro Webring #4914',
    image: 'https://ipfs.io/ipfs/bafybeiaodfs4y7uhnquhle2o7yxqr4kcdodc3avrnyr33wxglmcxj2agqa'
  },
  {
    id: 'radbro-webring-4917',
    name: 'Radbro Webring #4917',
    image: 'https://ipfs.io/ipfs/bafybeib4psfzhksexb3awpsnxcbwnws44764co3dbje7qoyksh6z453axe'
  },
  {
    id: 'radbro-webring-4918',
    name: 'Radbro Webring #4918',
    image: 'https://ipfs.io/ipfs/bafybeibzenottumax6iabumgxryhrepgsx3sbjmo3irfchpzxd7x65rinq'
  },
  {
    id: 'radbro-webring-4911',
    name: 'Radbro Webring #4911',
    image: 'https://ipfs.io/ipfs/bafybeiglswcxy5max4ouev5fyu5mzcv2rvftsdcdyqrrvh3ekvjyjomm7a'
  },
  {
    id: 'radbro-webring-4913',
    name: 'Radbro Webring #4913',
    image: 'https://ipfs.io/ipfs/bafybeigzvd7soma7awfjjn2tucqsmhdsycp4srcyjd4ahbsdwis374pnma'
  },
  {
    id: 'radbro-webring-4907',
    name: 'Radbro Webring #4907',
    image: 'https://ipfs.io/ipfs/bafybeihoe7sudmmz7mj5pf7z6lzcvxarqpj2yhawxv3aparjuqu76cpypu'
  },
  {
    id: 'radbro-webring-4912',
    name: 'Radbro Webring #4912',
    image: 'https://ipfs.io/ipfs/bafybeifjtvpnqm7caooqooqsot3lazizmh7jpeqom3n3gqyyntshgkhdgm'
  },
  {
    id: 'radbro-webring-4905',
    name: 'Radbro Webring #4905',
    image: 'https://ipfs.io/ipfs/bafybeiaia5pxx7iouazvri5tvropbor4447bq5iq3pw4eexw2rjyilcpda'
  },
  {
    id: 'radbro-webring-4909',
    name: 'Radbro Webring #4909',
    image: 'https://ipfs.io/ipfs/bafybeigisa33oedvmabnwupvuxazz2v2f4askztgvnsxzte3rpgf7fjx7a'
  },
  {
    id: 'radbro-webring-4908',
    name: 'Radbro Webring #4908',
    image: 'https://ipfs.io/ipfs/bafybeifp76p7o3ctw3htdsyd3dq3df7nbw2fcfqr4vbruzvjre7v6plvfm'
  },
  {
    id: 'radbro-webring-4906',
    name: 'Radbro Webring #4906',
    image: 'https://ipfs.io/ipfs/bafybeibm2c2gncmunuiz3czrw3sje2yoallnc7oikcztwujz2nmian3voe'
  },
  {
    id: 'radbro-webring-4910',
    name: 'Radbro Webring #4910',
    image: 'https://ipfs.io/ipfs/bafybeid44dfcpjg62iugylfpssbghjbixqlvwxipvp6gncday6b74delfi'
  },
  {
    id: 'radbro-webring-4904',
    name: 'Radbro Webring #4904',
    image: 'https://ipfs.io/ipfs/bafybeiduizb5j3gdkhdqn7lkxwrkwgpn36anrm2flpno54qzh3twp66jg4'
  },
  {
    id: 'radbro-webring-4900',
    name: 'Radbro Webring #4900',
    image: 'https://ipfs.io/ipfs/bafybeigv62wlwzmrqqxc2crqb2osftkrkq2ebiso3wx7ijl5tahr6nwv6i'
  },
  {
    id: 'radbro-webring-4903',
    name: 'Radbro Webring #4903',
    image: 'https://ipfs.io/ipfs/bafybeicvxhhcqddjb3l6dd4pe3qzvowlqwzstejcuzfaeofomvwr6hnl3i'
  },
  {
    id: 'radbro-webring-4902',
    name: 'Radbro Webring #4902',
    image: 'https://ipfs.io/ipfs/bafybeici2goqzqztrfezyjrjghtb7atm4styuftjxngdeuh3v3nbhkdja4'
  }

                                ];
        actualNftData = radrowebringNFTs.find(nft => nft.id.toString() === itemId);  
                                              
                         } else if (collectionId === 'v1-cryptopunks-wrapped') {
        const v1punksNFTs = [
        
  {
    id: 'v1-cryptopunks-wrapped-9865',
    name: 'V1-PUNK #9865',
    image: 'https://ipfs.io/ipfs/Qmaw7ESSXVcyVxCosgEoSuxmgzcCT2c1hUBbyXeif2wtbC'
  },
  {
    id: 'v1-cryptopunks-wrapped-4833',
    name: 'V1-PUNK #4833',
    image: 'https://ipfs.io/ipfs/QmQPw7hJUbjoNjhNxSs88ijCgSmL5ZmpkeQZR8ukhDk2WZ'
  },
  {
    id: 'v1-cryptopunks-wrapped-9236',
    name: 'V1-PUNK #9236',
    image: 'https://ipfs.io/ipfs/QmUHNqFZvAUiXr8JHwEgnm6S8Uedzz4FZrBeMVe4kW6gz5'
  },
  {
    id: 'v1-cryptopunks-wrapped-1282',
    name: 'V1-PUNK #1282',
    image: 'https://ipfs.io/ipfs/QmfRBxLTVwZgbrERd2xR9PmG14vAUL6ZGefNuscq565LJ7'
  },
  {
    id: 'v1-cryptopunks-wrapped-9570',
    name: 'V1-PUNK #9570',
    image: 'https://ipfs.io/ipfs/QmTn89zFaGLagVDwMy1trzREgLXqV7HJYYUvBD7bWVQjuh'
  },
  {
    id: 'v1-cryptopunks-wrapped-9834',
    name: 'V1-PUNK #9834',
    image: 'https://ipfs.io/ipfs/QmXJV133ArEfXPZS5JxgszGQuuLqTKi7eV9g1YmJCcCve7'
  },
  {
    id: 'v1-cryptopunks-wrapped-8747',
    name: 'V1-PUNK #8747',
    image: 'https://ipfs.io/ipfs/QmSLmgbVs2ue75a7kA6GR3tEYAQ7QNw7qyfhNzTLq2nE2S'
  },
  {
    id: 'v1-cryptopunks-wrapped-8631',
    name: 'V1-PUNK #8631',
    image: 'https://ipfs.io/ipfs/QmUGYhfxpMLwSV4NAHD9AVPZ4CzGRtiFpzCkQcxo7y2R6V'
  },
  {
    id: 'v1-cryptopunks-wrapped-9443',
    name: 'V1-PUNK #9443',
    image: 'https://ipfs.io/ipfs/QmZAvr31asEyJxPRGYvKzv2PbSLX4Ph47SN9E4r4ZKtGMq'
  },
  {
    id: 'v1-cryptopunks-wrapped-9524',
    name: 'V1-PUNK #9524',
    image: 'https://ipfs.io/ipfs/QmWv2UR6BT1afeuHW7K6H2Cz97q1iadS8uzSpyJAcuKxyT'
  },
  {
    id: 'v1-cryptopunks-wrapped-8908',
    name: 'V1-PUNK #8908',
    image: 'https://ipfs.io/ipfs/QmYP3JCZ9LbSg6eW77LLaGdinTESH1xGohk2mnSRr9wjLi'
  },
  {
    id: 'v1-cryptopunks-wrapped-8090',
    name: 'V1-PUNK #8090',
    image: 'https://ipfs.io/ipfs/QmanxkjodfC7SHk5FxdzXMwN2f5rZjXZLFzXR9gjGWV9Ym'
  },
  {
    id: 'v1-cryptopunks-wrapped-7838',
    name: 'V1-PUNK #7838',
    image: 'https://ipfs.io/ipfs/QmYmxxLhMxHw6TAc9hgQ3bszkLkuCuMZX2QZ2F7zfhDHuJ'
  },
  {
    id: 'v1-cryptopunks-wrapped-5009',
    name: 'V1-PUNK #5009',
    image: 'https://ipfs.io/ipfs/QmRVpuFNwD55VSEjLYswH2MxguPBoL77zFmgBoZrM9JNA3'
  },
  {
    id: 'v1-cryptopunks-wrapped-5203',
    name: 'V1-PUNK #5203',
    image: 'https://ipfs.io/ipfs/QmeNWprRQQhDLoA89fgeqewcDK2ymNDwf6C1EALyDjnkvK'
  },
  {
    id: 'v1-cryptopunks-wrapped-5609',
    name: 'V1-PUNK #5609',
    image: 'https://ipfs.io/ipfs/QmSxspF4Dbfc8Cm5wkiyfEbpkFYmV86ZSADvoX5gzW19oG'
  },
  {
    id: 'v1-cryptopunks-wrapped-3484',
    name: 'V1-PUNK #3484',
    image: 'https://ipfs.io/ipfs/QmSPyd7UjX9MJrim9WpYqradQakqCPb3mwfJbF9nRhonUe'
  },
  {
    id: 'v1-cryptopunks-wrapped-7315',
    name: 'V1-PUNK #7315',
    image: 'https://ipfs.io/ipfs/QmUndMBDVYmziKdxY34tH3Ng2oDKs7RFJru3AwhKWmnWpK'
  },
  {
    id: 'v1-cryptopunks-wrapped-8858',
    name: 'V1-PUNK #8858',
    image: 'https://ipfs.io/ipfs/Qmf9fM81Fe8dPXAYj6zrRn8x83FLiVikRwYhCBmrmXjoNa'
  },
  {
    id: 'v1-cryptopunks-wrapped-5940',
    name: 'V1-PUNK #5940',
    image: 'https://ipfs.io/ipfs/QmfJ7VztFjhzFVd2fVHcGidYJBYo6cQCugdTzELUQ6pocY'
  },
  {
    id: 'v1-cryptopunks-wrapped-4524',
    name: 'V1-PUNK #4524',
    image: 'https://ipfs.io/ipfs/QmXbYSuq4uzuK8VW9T7AxMgYtYnSguynnLqP71695dJm1A'
  },
  {
    id: 'v1-cryptopunks-wrapped-3290',
    name: 'V1-PUNK #3290',
    image: 'https://ipfs.io/ipfs/QmNfKBxzdtNffgn982HtV4jxfpHQgqdLjpTGyXC8hFBbgV'
  },
  {
    id: 'v1-cryptopunks-wrapped-5123',
    name: 'V1-PUNK #5123',
    image: 'https://ipfs.io/ipfs/QmX58Tp6UYUqthSnxp9N1EZRm5WzFCxR3m6UAkRc4gJb12'
  },
  {
    id: 'v1-cryptopunks-wrapped-7382',
    name: 'V1-PUNK #7382',
    image: 'https://ipfs.io/ipfs/Qmc1cpRnpakt9pPQU34QkzkENVNh2tyJjhnguCdvQdLMwF'
  },
  {
    id: 'v1-cryptopunks-wrapped-8637',
    name: 'V1-PUNK #8637',
    image: 'https://ipfs.io/ipfs/QmZZxVRdMJCxSzNS9G6iqv6ebWTKLkMaYGSiKphEL9mvJm'
  },
  {
    id: 'v1-cryptopunks-wrapped-9037',
    name: 'V1-PUNK #9037',
    image: 'https://ipfs.io/ipfs/QmcXv1c7LLPJTu3GkgBST4rUYqbLzpfQnsSVUJ647BsPFN'
  },
  {
    id: 'v1-cryptopunks-wrapped-6185',
    name: 'V1-PUNK #6185',
    image: 'https://ipfs.io/ipfs/QmPsjyhmquTi3CwpC3LQ2763qHhoV3pphr5cdd1pzCBzFe'
  },
  {
    id: 'v1-cryptopunks-wrapped-6938',
    name: 'V1-PUNK #6938',
    image: 'https://ipfs.io/ipfs/QmbrwgeTYkTRo64FkmNVXceTemGA4QKw1R28HrDem1vPoz'
  },
  {
    id: 'v1-cryptopunks-wrapped-7041',
    name: 'V1-PUNK #7041',
    image: 'https://ipfs.io/ipfs/QmULDj2Rn8ww4xLuhP6xbrJ1gJgAjBhZrdKuTh2oTTsFPC'
  },
  {
    id: 'v1-cryptopunks-wrapped-8245',
    name: 'V1-PUNK #8245',
    image: 'https://ipfs.io/ipfs/Qmbu2JKASzBCL6J6X3TQCXMdETKqCiaSb54LMLrbi2CjTE'
  },
  {
    id: 'v1-cryptopunks-wrapped-7878',
    name: 'V1-PUNK #7878',
    image: 'https://ipfs.io/ipfs/QmaGKFrSRgWPFR57D1ZDBscnxq3p6VvidueKJf9crvf1yk'
  },
  {
    id: 'v1-cryptopunks-wrapped-8366',
    name: 'V1-PUNK #8366',
    image: 'https://ipfs.io/ipfs/QmatqGbU8oreQcVdygDgVAVVxm6wZ6YeQapVQJjyLmvVYY'
  },
  {
    id: 'v1-cryptopunks-wrapped-5808',
    name: 'V1-PUNK #5808',
    image: 'https://ipfs.io/ipfs/QmUC1mcwv5rAugg1iY7VZMkrZzU2YU3LhcGnNShBjTzGqY'
  },
  {
    id: 'v1-cryptopunks-wrapped-3796',
    name: 'V1-PUNK #3796',
    image: 'https://ipfs.io/ipfs/QmTNAxESWFSevTpJucsb9F6wFoxVWQwXkeWEUctXYPggy6'
  },
  {
    id: 'v1-cryptopunks-wrapped-3693',
    name: 'V1-PUNK #3693',
    image: 'https://ipfs.io/ipfs/QmRgyybw4FKWQHw7pcXmzwK9VULbxR7hSMVRATcYJXaX94'
  },
  {
    id: 'v1-cryptopunks-wrapped-2691',
    name: 'V1-PUNK #2691',
    image: 'https://ipfs.io/ipfs/QmWuBhccEuftLjJAiPa6KHZiv7Nvq8TcPAbXaSNH4QP1im'
  },
  {
    id: 'v1-cryptopunks-wrapped-2498',
    name: 'V1-PUNK #2498',
    image: 'https://ipfs.io/ipfs/QmNtneDxFY4JLWq8Lw3izSbbohqHtPfNt7f2t6MSKWB1oM'
  },
  {
    id: 'v1-cryptopunks-wrapped-2193',
    name: 'V1-PUNK #2193',
    image: 'https://ipfs.io/ipfs/QmdSUupWhSmAs6d8Vm7kJD5k184hBcZkrMoVzcCTtxovAG'
  },
  {
    id: 'v1-cryptopunks-wrapped-1698',
    name: 'V1-PUNK #1698',
    image: 'https://ipfs.io/ipfs/QmXYTQA2msxyHEquEe3bGTNyLSZDKPkKsY4cNKD29kpjfY'
  },
  {
    id: 'v1-cryptopunks-wrapped-1639',
    name: 'V1-PUNK #1639',
    image: 'https://ipfs.io/ipfs/QmNQgLRJCgNBy1rgiCyfUiuAQ7otm3DkHfAVx8Mmj1SMAD'
  },
  {
    id: 'v1-cryptopunks-wrapped-1286',
    name: 'V1-PUNK #1286',
    image: 'https://ipfs.io/ipfs/QmNfMpN25wNHfX6ep8G6b1VcFRX7AibKu4VyV4XycXroG1'
  },
  {
    id: 'v1-cryptopunks-wrapped-1281',
    name: 'V1-PUNK #1281',
    image: 'https://ipfs.io/ipfs/QmUyKv2VCk1DGenpSyoyfPVVebZy1RnamoCj1b1XFnSSC2'
  },
  {
    id: 'v1-cryptopunks-wrapped-1181',
    name: 'V1-PUNK #1181',
    image: 'https://ipfs.io/ipfs/QmbUGfDrwHvh8uGbMbE1mETQ4GPHZqnygwqw5W2injA3zn'
  },
  {
    id: 'v1-cryptopunks-wrapped-2880',
    name: 'V1-PUNK #2880',
    image: 'https://ipfs.io/ipfs/QmXZYEVHvRetAjZqeSJtiUKk9CwJx6qPzZanixnpU1Lgxh'
  },
  {
    id: 'v1-cryptopunks-wrapped-3187',
    name: 'V1-PUNK #3187',
    image: 'https://ipfs.io/ipfs/QmNqr2BQA6uJGt8KNZURepUBWN7Fo5rVc18h9Cjdoq1icb'
  },
  {
    id: 'v1-cryptopunks-wrapped-2687',
    name: 'V1-PUNK #2687',
    image: 'https://ipfs.io/ipfs/QmTVfNdVcNvmnETUREpfagg2x8DivYktG8eiBvPPzczuUp'
  },
  {
    id: 'v1-cryptopunks-wrapped-2323',
    name: 'V1-PUNK #2323',
    image: 'https://ipfs.io/ipfs/QmcnCj9REHe2f7Wj4yR22gXWwmbHiHcJFHceBgVzzdMhm1'
  },
  {
    id: 'v1-cryptopunks-wrapped-2403',
    name: 'V1-PUNK #2403',
    image: 'https://ipfs.io/ipfs/QmbjVbqE2zJRctEnxqFGnqYEqGdhyvE8Pu9TZNEMw1Vc87'
  },
  {
    id: 'v1-cryptopunks-wrapped-2316',
    name: 'V1-PUNK #2316',
    image: 'https://ipfs.io/ipfs/QmTQ11mJdNJX3kSYGzLvUUJbxdgvpzDGG1WXVuvY5yUjju'
  },
  {
    id: 'v1-cryptopunks-wrapped-2295',
    name: 'V1-PUNK #2295',
    image: 'https://ipfs.io/ipfs/QmRE9TE9ucDnTQhwizpYTg1rzveJkJsswWFoFJdVrDRczc'
  },
  {
    id: 'v1-cryptopunks-wrapped-2164',
    name: 'V1-PUNK #2164',
    image: 'https://ipfs.io/ipfs/QmWC5RCykKqNxMBBfb7KjeWgiCwFucEjnmbABorLCeFCAD'
  },
  {
    id: 'v1-cryptopunks-wrapped-2298',
    name: 'V1-PUNK #2298',
    image: 'https://ipfs.io/ipfs/QmaHv1bTpMgvQEyeZx12BXcmxAJc8roZTDCwRbeSVTH6A4'
  },
  {
    id: 'v1-cryptopunks-wrapped-1858',
    name: 'V1-PUNK #1858',
    image: 'https://ipfs.io/ipfs/QmQqnLjoVnBJfDuhFjPAmZGbVTyQvCSBmzThwY7Ev4tMYr'
  },
  {
    id: 'v1-cryptopunks-wrapped-1814',
    name: 'V1-PUNK #1814',
    image: 'https://ipfs.io/ipfs/QmUmodhKdTgA9SnCQt83C2r57qxzXsXytQoFxXzuB28kcF'
  },
  {
    id: 'v1-cryptopunks-wrapped-1731',
    name: 'V1-PUNK #1731',
    image: 'https://ipfs.io/ipfs/QmULXsVHvjLzB1vV2QPa22rhpg83tugQsctwBLjNSCsw1T'
  },
  {
    id: 'v1-cryptopunks-wrapped-1622',
    name: 'V1-PUNK #1622',
    image: 'https://ipfs.io/ipfs/QmXb4LWygrSgLAm3cHWqEhoPV1oP38Fcg43MnWYU1HqCtB'
  },
  {
    id: 'v1-cryptopunks-wrapped-1673',
    name: 'V1-PUNK #1673',
    image: 'https://ipfs.io/ipfs/QmQ1t6BcTdFGBaMCJ3NpDELrSijmC7yk2HyBW26xRcgf1G'
  },
  {
    id: 'v1-cryptopunks-wrapped-1570',
    name: 'V1-PUNK #1570',
    image: 'https://ipfs.io/ipfs/QmajXU7jxSgZX8tfSWaNEKW2SKrD8QXYcm5KN6jwq3ZSqv'
  },
  {
    id: 'v1-cryptopunks-wrapped-1533',
    name: 'V1-PUNK #1533',
    image: 'https://ipfs.io/ipfs/QmQeAZwpncJGE5yTHmXGfWpVt4LkkBoKN2pwYvagzjFfWt'
  },
  {
    id: 'v1-cryptopunks-wrapped-1551',
    name: 'V1-PUNK #1551',
    image: 'https://ipfs.io/ipfs/QmR53uWzYfiLUMooUKWcTvFD5QbmSM2RXFmeYACbiuuRSR'
  },
  {
    id: 'v1-cryptopunks-wrapped-1516',
    name: 'V1-PUNK #1516',
    image: 'https://ipfs.io/ipfs/QmYpb1HKX9RLmNdepCk6Pxve9Lp7vrUfRw9CijpncRtYJ9'
  },
  {
    id: 'v1-cryptopunks-wrapped-1525',
    name: 'V1-PUNK #1525',
    image: 'https://ipfs.io/ipfs/QmVxzPJoNpDbbnZsUBhn5XohdfsNBNXgNB21op4TKRUee8'
  },
  {
    id: 'v1-cryptopunks-wrapped-1502',
    name: 'V1-PUNK #1502',
    image: 'https://ipfs.io/ipfs/QmdWUSKckBi9TqmYuH3uDnFQX4oo34jtEBGvoLbdCssfFc'
  },
  {
    id: 'v1-cryptopunks-wrapped-1334',
    name: 'V1-PUNK #1334',
    image: 'https://ipfs.io/ipfs/QmQVwfG8QuTy3AAGEhpMzxqd7cak3scSkKnwfrN88UsHXd'
  },
  {
    id: 'v1-cryptopunks-wrapped-1312',
    name: 'V1-PUNK #1312',
    image: 'https://ipfs.io/ipfs/QmPnvpYDhZ1wHWjwGyvfg8M4bGFQwdA3MKZqFuhTAWajDg'
  },
  {
    id: 'v1-cryptopunks-wrapped-1241',
    name: 'V1-PUNK #1241',
    image: 'https://ipfs.io/ipfs/Qmdbb7h5wUXqrPUTzMBYafS6Au7vwSCU31myrrid4qiXXH'
  },
  {
    id: 'v1-cryptopunks-wrapped-7218',
    name: 'V1-PUNK #7218',
    image: 'https://ipfs.io/ipfs/QmaMBEHai5rMfH65drREetRKkGmcX4nQMtaTtuG6ffyNUu'
  },
  {
    id: 'v1-cryptopunks-wrapped-7233',
    name: 'V1-PUNK #7233',
    image: 'https://ipfs.io/ipfs/Qme7SHXdQRCdS7xSQtzb77Awxzm71c3rmthUjrYFQ9EK1i'
  },
  {
    id: 'v1-cryptopunks-wrapped-7188',
    name: 'V1-PUNK #7188',
    image: 'https://ipfs.io/ipfs/QmWSF2vmzg5CUoUnmmc1uYFdiMrR65u16CYoxU9tquqE3n'
  },
  {
    id: 'v1-cryptopunks-wrapped-7840',
    name: 'V1-PUNK #7840',
    image: 'https://ipfs.io/ipfs/Qmd4xvCyuZMQ6TuU2BtXzKJVqHPW4RSjijBjRcY3fYkdzi'
  },
  {
    id: 'v1-cryptopunks-wrapped-8292',
    name: 'V1-PUNK #8292',
    image: 'https://ipfs.io/ipfs/QmfA6QVqDnimJ2QWgtvWRU73om1NmUrHVyneNnwzB8vUcU'
  },
  {
    id: 'v1-cryptopunks-wrapped-1344',
    name: 'V1-PUNK #1344',
    image: 'https://ipfs.io/ipfs/QmXGKawxXpTRbnXQzziLgemL225ZX7UAgmJ38HCtMq83MY'
  },
  {
    id: 'v1-cryptopunks-wrapped-9593',
    name: 'V1-PUNK #9593',
    image: 'https://ipfs.io/ipfs/QmUwqGFvqVxzaYwbxWZaQmJDAwcjemUpMz9B3qEGZR9FEo'
  },
  {
    id: 'v1-cryptopunks-wrapped-8094',
    name: 'V1-PUNK #8094',
    image: 'https://ipfs.io/ipfs/QmWct8h8xLKUK6H2jEZXF2paQUaz6T5W1HE2zu2zKZa8xf'
  },
  {
    id: 'v1-cryptopunks-wrapped-7445',
    name: 'V1-PUNK #7445',
    image: 'https://ipfs.io/ipfs/QmZ577Uor8uTY6dnVGEe4CPDjFj5FgwQxLJRJvRtFjqwB2'
  },
  {
    id: 'v1-cryptopunks-wrapped-7367',
    name: 'V1-PUNK #7367',
    image: 'https://ipfs.io/ipfs/QmQpieruGUR3kz42JdwnnDXrLHp13FeWgcqp1ZPLEuPbjc'
  },
  {
    id: 'v1-cryptopunks-wrapped-7063',
    name: 'V1-PUNK #7063',
    image: 'https://ipfs.io/ipfs/QmZnwK6vFheQFYLmwvZqAmbt6HEKoLDrApu7HQ4WJ4HH5a'
  },
  {
    id: 'v1-cryptopunks-wrapped-6184',
    name: 'V1-PUNK #6184',
    image: 'https://ipfs.io/ipfs/QmZMUFyUczkr9DZmQVCmnQq4NAhNjiPBnTb6eHYuG8T1p9'
  },
  {
    id: 'v1-cryptopunks-wrapped-6104',
    name: 'V1-PUNK #6104',
    image: 'https://ipfs.io/ipfs/QmWJvW3ZLx5rnyYoRH4B7eogSNN1TmqUA2pWbywvWc5LLj'
  },
  {
    id: 'v1-cryptopunks-wrapped-5858',
    name: 'V1-PUNK #5858',
    image: 'https://ipfs.io/ipfs/QmaVdtczFG6TqKgz98AAacZdnPXha4m2Zer8YWfvTxGUuc'
  },
  {
    id: 'v1-cryptopunks-wrapped-5673',
    name: 'V1-PUNK #5673',
    image: 'https://ipfs.io/ipfs/QmdbyPaTBDvWBLrEGQQcZQkiRE1thc9ENB4YPoTteJDGtn'
  },
  {
    id: 'v1-cryptopunks-wrapped-5563',
    name: 'V1-PUNK #5563',
    image: 'https://ipfs.io/ipfs/QmQ5ikscSakTAxtxZSk3ovpVhZx6C7vi4mtiSYCzKskg5V'
  },
  {
    id: 'v1-cryptopunks-wrapped-5256',
    name: 'V1-PUNK #5256',
    image: 'https://ipfs.io/ipfs/QmUWxYF92oFa85viMLriQ8NrweP7wzkyVtCcErVnXPps2J'
  },
  {
    id: 'v1-cryptopunks-wrapped-5076',
    name: 'V1-PUNK #5076',
    image: 'https://ipfs.io/ipfs/QmeJtfjGZZrGjeYzLeVcceRwHbbBvbkVryxHadHvGK34Yf'
  },
  {
    id: 'v1-cryptopunks-wrapped-4849',
    name: 'V1-PUNK #4849',
    image: 'https://ipfs.io/ipfs/QmPK3zUR7hhKsvVXWT3kmVT7sHHxorYv6aQBGvE7mAivFz'
  },
  {
    id: 'v1-cryptopunks-wrapped-3747',
    name: 'V1-PUNK #3747',
    image: 'https://ipfs.io/ipfs/QmW7CZBBLpN4d8DhPm2ekfQ8nvtiwHW8zkh6yu6ytbjTz9'
  },
  {
    id: 'v1-cryptopunks-wrapped-3725',
    name: 'V1-PUNK #3725',
    image: 'https://ipfs.io/ipfs/QmQhGpqtTmZx6J1gjreU5LJMmvWf9Uxj74hXEfG7PbrUVa'
  },
  {
    id: 'v1-cryptopunks-wrapped-5551',
    name: 'V1-PUNK #5551',
    image: 'https://ipfs.io/ipfs/QmbLBqeWBkswrq8zJvxTiiWdyrytwkhoPHi4aPASyLQjCs'
  },
  {
    id: 'v1-cryptopunks-wrapped-3457',
    name: 'V1-PUNK #3457',
    image: 'https://ipfs.io/ipfs/Qmch96L8yMArSzqec27WEZt6WrncijzH5nnjV2pUn2d8it'
  },
  {
    id: 'v1-cryptopunks-wrapped-3401',
    name: 'V1-PUNK #3401',
    image: 'https://ipfs.io/ipfs/QmV6GnmiqY21NMUx7A7SU9Niix4EqvV1XWqviksTaxsgby'
  },
  {
    id: 'v1-cryptopunks-wrapped-3252',
    name: 'V1-PUNK #3252',
    image: 'https://ipfs.io/ipfs/QmUiMd7gdoPwzVkN59Rim3M7e881bzDVrMhdME2V9xgoU9'
  },
  {
    id: 'v1-cryptopunks-wrapped-3251',
    name: 'V1-PUNK #3251',
    image: 'https://ipfs.io/ipfs/QmR3Km9VcrHxqodmsPvQfQhQa7gyqaQ3MGPZ5xCHsuhnp8'
  },
  {
    id: 'v1-cryptopunks-wrapped-2736',
    name: 'V1-PUNK #2736',
    image: 'https://ipfs.io/ipfs/QmcZtuSksPQsTzxmur12NKGXUr88max8V49CEWdKMUzwAx'
  },
  {
    id: 'v1-cryptopunks-wrapped-1959',
    name: 'V1-PUNK #1959',
    image: 'https://ipfs.io/ipfs/QmQNuYy7UC3ANUvLqV1dqCF7Xn12ZeyAp6GXsZzjofB6tG'
  },
  {
    id: 'v1-cryptopunks-wrapped-1341',
    name: 'V1-PUNK #1341',
    image: 'https://ipfs.io/ipfs/QmTzzFDDNcrWcNdMhRy11fenqmy14HTQ1LEJr2vWLTCNJc'
  },
  {
    id: 'v1-cryptopunks-wrapped-5386',
    name: 'V1-PUNK #5386',
    image: 'https://ipfs.io/ipfs/QmekakF7T1KnTTxfRGBkyDTxJ4f1ejQyUbJEGy6s6QbJJ6'
  },
  {
    id: 'v1-cryptopunks-wrapped-1455',
    name: 'V1-PUNK #1455',
    image: 'https://ipfs.io/ipfs/QmdTx13LSqj3w23rXjUGSPAuzjXPf28WNx3bHS6Q8ibvwU'
  },
  {
    id: 'v1-cryptopunks-wrapped-8592',
    name: 'V1-PUNK #8592',
    image: 'https://ipfs.io/ipfs/QmeXv3HkmC45TMbWQRgJKws7raQ3Z8nzBW7FacEDxq4fph'
  },
  {
    id: 'v1-cryptopunks-wrapped-4888',
    name: 'V1-PUNK #4888',
    image: 'https://ipfs.io/ipfs/QmViDxQxPmw98qXm9WxddDhmzgkmP1NuNjXJfp5ZdhhG37'
  },
  {
    id: 'v1-cryptopunks-wrapped-2617',
    name: 'V1-PUNK #2617',
    image: 'https://ipfs.io/ipfs/QmTxsT5RBFmBPXdscAYV4amKuWx8UKtETuSEsAkvwoLjkd'
  }
            
                                ];
        actualNftData = v1punksNFTs.find(nft => nft.id.toString() === itemId);  
                                                
                          } else if (collectionId === 'khuga-by-khuga-labs') {
        const khugaNFTs = [
            
  {
    id: 'Khuga by Khuga Labs-5533',
    name: 'Khuga #5533',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5533.png'
  },
  {
    id: 'Khuga by Khuga Labs-5554',
    name: 'Khuga #5554',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5554.png'
  },
  {
    id: 'Khuga by Khuga Labs-5543',
    name: 'Khuga #5543',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5543.png'
  },
  {
    id: 'Khuga by Khuga Labs-5536',
    name: 'Khuga #5536',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5536.png'
  },
  {
    id: 'Khuga by Khuga Labs-5531',
    name: 'Khuga #5531',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5531.png'
  },
  {
    id: 'Khuga by Khuga Labs-5521',
    name: 'Khuga #5521',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5521.png'
  },
  {
    id: 'Khuga by Khuga Labs-5534',
    name: 'Khuga #5534',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5534.png'
  },
  {
    id: 'Khuga by Khuga Labs-5523',
    name: 'Khuga #5523',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5523.png'
  },
  {
    id: 'Khuga by Khuga Labs-5516',
    name: 'Khuga #5516',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5516.png'
  },
  {
    id: 'Khuga by Khuga Labs-5552',
    name: 'Khuga #5552',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5552.png'
  },
  {
    id: 'Khuga by Khuga Labs-5518',
    name: 'Khuga #5518',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5518.png'
  },
  {
    id: 'Khuga by Khuga Labs-5538',
    name: 'Khuga #5538',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5538.png'
  },
  {
    id: 'Khuga by Khuga Labs-5548',
    name: 'Khuga #5548',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5548.png'
  },
  {
    id: 'Khuga by Khuga Labs-5508',
    name: 'Khuga #5508',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5508.png'
  },
  {
    id: 'Khuga by Khuga Labs-5551',
    name: 'Khuga #5551',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5551.png'
  },
  {
    id: 'Khuga by Khuga Labs-5546',
    name: 'Khuga #5546',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5546.png'
  },
  {
    id: 'Khuga by Khuga Labs-5527',
    name: 'Khuga #5527',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5527.png'
  },
  {
    id: 'Khuga by Khuga Labs-5540',
    name: 'Khuga #5540',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5540.png'
  },
  {
    id: 'Khuga by Khuga Labs-5525',
    name: 'Khuga #5525',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5525.png'
  },
  {
    id: 'Khuga by Khuga Labs-5544',
    name: 'Khuga #5544',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5544.png'
  },
  {
    id: 'Khuga by Khuga Labs-5512',
    name: 'Khuga #5512',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5512.png'
  },
  {
    id: 'Khuga by Khuga Labs-5510',
    name: 'Khuga #5510',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5510.png'
  },
  {
    id: 'Khuga by Khuga Labs-5507',
    name: 'Khuga #5507',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5507.png'
  },
  {
    id: 'Khuga by Khuga Labs-5514',
    name: 'Khuga #5514',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5514.png'
  },
  {
    id: 'Khuga by Khuga Labs-5529',
    name: 'Khuga #5529',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5529.png'
  },
  {
    id: 'Khuga by Khuga Labs-5532',
    name: 'Khuga #5532',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5532.png'
  },
  {
    id: 'Khuga by Khuga Labs-5555',
    name: 'Khuga #5555',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5555.png'
  },
  {
    id: 'Khuga by Khuga Labs-5542',
    name: 'Khuga #5542',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5542.png'
  },
  {
    id: 'Khuga by Khuga Labs-5535',
    name: 'Khuga #5535',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5535.png'
  },
  {
    id: 'Khuga by Khuga Labs-5537',
    name: 'Khuga #5537',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5537.png'
  },
  {
    id: 'Khuga by Khuga Labs-5553',
    name: 'Khuga #5553',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5553.png'
  },
  {
    id: 'Khuga by Khuga Labs-5539',
    name: 'Khuga #5539',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5539.png'
  },
  {
    id: 'Khuga by Khuga Labs-5520',
    name: 'Khuga #5520',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5520.png'
  },
  {
    id: 'Khuga by Khuga Labs-5530',
    name: 'Khuga #5530',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5530.png'
  },
  {
    id: 'Khuga by Khuga Labs-5549',
    name: 'Khuga #5549',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5549.png'
  },
  {
    id: 'Khuga by Khuga Labs-5550',
    name: 'Khuga #5550',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5550.png'
  },
  {
    id: 'Khuga by Khuga Labs-5545',
    name: 'Khuga #5545',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5545.png'
  },
  {
    id: 'Khuga by Khuga Labs-5541',
    name: 'Khuga #5541',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5541.png'
  },
  {
    id: 'Khuga by Khuga Labs-5519',
    name: 'Khuga #5519',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5519.png'
  },
  {
    id: 'Khuga by Khuga Labs-5547',
    name: 'Khuga #5547',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5547.png'
  },
  {
    id: 'Khuga by Khuga Labs-5522',
    name: 'Khuga #5522',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5522.png'
  },
  {
    id: 'Khuga by Khuga Labs-5524',
    name: 'Khuga #5524',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5524.png'
  },
  {
    id: 'Khuga by Khuga Labs-5517',
    name: 'Khuga #5517',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5517.png'
  },
  {
    id: 'Khuga by Khuga Labs-5526',
    name: 'Khuga #5526',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5526.png'
  },
  {
    id: 'Khuga by Khuga Labs-5528',
    name: 'Khuga #5528',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5528.png'
  },
  {
    id: 'Khuga by Khuga Labs-5511',
    name: 'Khuga #5511',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5511.png'
  },
  {
    id: 'Khuga by Khuga Labs-5515',
    name: 'Khuga #5515',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5515.png'
  },
  {
    id: 'Khuga by Khuga Labs-5506',
    name: 'Khuga #5506',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5506.png'
  },
  {
    id: 'Khuga by Khuga Labs-5509',
    name: 'Khuga #5509',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5509.png'
  },
  {
    id: 'Khuga by Khuga Labs-5513',
    name: 'Khuga #5513',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5513.png'
  },
  {
    id: 'Khuga by Khuga Labs-5494',
    name: 'Khuga #5494',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5494.png'
  },
  {
    id: 'Khuga by Khuga Labs-5043',
    name: 'Khuga #5043',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5043.png'
  },
  {
    id: 'Khuga by Khuga Labs-5495',
    name: 'Khuga #5495',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5495.png'
  },
  {
    id: 'Khuga by Khuga Labs-5477',
    name: 'Khuga #5477',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5477.png'
  },
  {
    id: 'Khuga by Khuga Labs-5044',
    name: 'Khuga #5044',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5044.png'
  },
  {
    id: 'Khuga by Khuga Labs-5484',
    name: 'Khuga #5484',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5484.png'
  },
  {
    id: 'Khuga by Khuga Labs-5504',
    name: 'Khuga #5504',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5504.png'
  },
  {
    id: 'Khuga by Khuga Labs-5499',
    name: 'Khuga #5499',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5499.png'
  },
  {
    id: 'Khuga by Khuga Labs-5465',
    name: 'Khuga #5465',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5465.png'
  },
  {
    id: 'Khuga by Khuga Labs-5500',
    name: 'Khuga #5500',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5500.png'
  },
  {
    id: 'Khuga by Khuga Labs-5467',
    name: 'Khuga #5467',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5467.png'
  },
  {
    id: 'Khuga by Khuga Labs-5483',
    name: 'Khuga #5483',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5483.png'
  },
  {
    id: 'Khuga by Khuga Labs-5473',
    name: 'Khuga #5473',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5473.png'
  },
  {
    id: 'Khuga by Khuga Labs-5497',
    name: 'Khuga #5497',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5497.png'
  },
  {
    id: 'Khuga by Khuga Labs-5460',
    name: 'Khuga #5460',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5460.png'
  },
  {
    id: 'Khuga by Khuga Labs-5505',
    name: 'Khuga #5505',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5505.png'
  },
  {
    id: 'Khuga by Khuga Labs-5485',
    name: 'Khuga #5485',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5485.png'
  },
  {
    id: 'Khuga by Khuga Labs-5498',
    name: 'Khuga #5498',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5498.png'
  },
  {
    id: 'Khuga by Khuga Labs-5492',
    name: 'Khuga #5492',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5492.png'
  },
  {
    id: 'Khuga by Khuga Labs-5490',
    name: 'Khuga #5490',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5490.png'
  },
  {
    id: 'Khuga by Khuga Labs-5481',
    name: 'Khuga #5481',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5481.png'
  },
  {
    id: 'Khuga by Khuga Labs-5476',
    name: 'Khuga #5476',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5476.png'
  },
  {
    id: 'Khuga by Khuga Labs-5464',
    name: 'Khuga #5464',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5464.png'
  },
  {
    id: 'Khuga by Khuga Labs-5475',
    name: 'Khuga #5475',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5475.png'
  },
  {
    id: 'Khuga by Khuga Labs-5470',
    name: 'Khuga #5470',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5470.png'
  },
  {
    id: 'Khuga by Khuga Labs-5501',
    name: 'Khuga #5501',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5501.png'
  },
  {
    id: 'Khuga by Khuga Labs-5489',
    name: 'Khuga #5489',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5489.png'
  },
  {
    id: 'Khuga by Khuga Labs-5482',
    name: 'Khuga #5482',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5482.png'
  },
  {
    id: 'Khuga by Khuga Labs-5472',
    name: 'Khuga #5472',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5472.png'
  },
  {
    id: 'Khuga by Khuga Labs-5248',
    name: 'Khuga #5248',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5248.png'
  },
  {
    id: 'Khuga by Khuga Labs-5461',
    name: 'Khuga #5461',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5461.png'
  },
  {
    id: 'Khuga by Khuga Labs-5466',
    name: 'Khuga #5466',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5466.png'
  },
  {
    id: 'Khuga by Khuga Labs-5496',
    name: 'Khuga #5496',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5496.png'
  },
  {
    id: 'Khuga by Khuga Labs-5486',
    name: 'Khuga #5486',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5486.png'
  },
  {
    id: 'Khuga by Khuga Labs-5469',
    name: 'Khuga #5469',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5469.png'
  },
  {
    id: 'Khuga by Khuga Labs-5479',
    name: 'Khuga #5479',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5479.png'
  },
  {
    id: 'Khuga by Khuga Labs-5431',
    name: 'Khuga #5431',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5431.png'
  },
  {
    id: 'Khuga by Khuga Labs-5429',
    name: 'Khuga #5429',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5429.png'
  },
  {
    id: 'Khuga by Khuga Labs-5480',
    name: 'Khuga #5480',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5480.png'
  },
  {
    id: 'Khuga by Khuga Labs-5493',
    name: 'Khuga #5493',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5493.png'
  },
  {
    id: 'Khuga by Khuga Labs-5471',
    name: 'Khuga #5471',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5471.png'
  },
  {
    id: 'Khuga by Khuga Labs-5491',
    name: 'Khuga #5491',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5491.png'
  },
  {
    id: 'Khuga by Khuga Labs-5438',
    name: 'Khuga #5438',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5438.png'
  },
  {
    id: 'Khuga by Khuga Labs-5488',
    name: 'Khuga #5488',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5488.png'
  },
  {
    id: 'Khuga by Khuga Labs-5474',
    name: 'Khuga #5474',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5474.png'
  },
  {
    id: 'Khuga by Khuga Labs-5440',
    name: 'Khuga #5440',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5440.png'
  },
  {
    id: 'Khuga by Khuga Labs-5457',
    name: 'Khuga #5457',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5457.png'
  },
  {
    id: 'Khuga by Khuga Labs-5463',
    name: 'Khuga #5463',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5463.png'
  },
  {
    id: 'Khuga by Khuga Labs-5436',
    name: 'Khuga #5436',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5436.png'
  },
  {
    id: 'Khuga by Khuga Labs-5459',
    name: 'Khuga #5459',
    image: 'https://ipfs.io/ipfs/QmabZm7rqjr3dc7mmPHzXZRLMDWnu9iBAYjtiq3koBjjwU/5459.png'
  }
                                ];
        actualNftData = khugaNFTs.find(nft => nft.id.toString() === itemId);  
                                                
                          } else if (collectionId === 'sugartown-oras') {
        const sugartownNFTs = [
            
           {
    id: 'Sugartown Oras-6909',
    name: 'Sugartown Oras #6909',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6909.png'
},
{
    id: 'Sugartown Oras-6907',
    name: 'Sugartown Oras #6907',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6907.png'
},
{
    id: 'Sugartown Oras-6908',
    name: 'Sugartown Oras #6908',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6908.png'
},
{
    id: 'Sugartown Oras-6906',
    name: 'Sugartown Oras #6906',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6906.png'
},
{
    id: 'Sugartown Oras-6902',
    name: 'Sugartown Oras #6902',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6902.png'
},
{
    id: 'Sugartown Oras-6904',
    name: 'Sugartown Oras #6904',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6904.png'
},
{
    id: 'Sugartown Oras-6905',
    name: 'Sugartown Oras #6905',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6905.png'
},
{
    id: 'Sugartown Oras-6903',
    name: 'Sugartown Oras #6903',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6903.png'
},
{
    id: 'Sugartown Oras-6901',
    name: 'Sugartown Oras #6901',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6901.png'
},
{
    id: 'Sugartown Oras-6900',
    name: 'Sugartown Oras #6900',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6900.png'
},
{
    id: 'Sugartown Oras-6899',
    name: 'Sugartown Oras #6899',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6899.png'
},
{
    id: 'Sugartown Oras-6898',
    name: 'Sugartown Oras #6898',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6898.png'
},
{
    id: 'Sugartown Oras-6896',
    name: 'Sugartown Oras #6896',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6896.png'
},
{
    id: 'Sugartown Oras-6895',
    name: 'Sugartown Oras #6895',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6895.png'
},
{
    id: 'Sugartown Oras-6897',
    name: 'Sugartown Oras #6897',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6897.png'
},
{
    id: 'Sugartown Oras-6894',
    name: 'Sugartown Oras #6894',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6894.png'
},
{
    id: 'Sugartown Oras-6892',
    name: 'Sugartown Oras #6892',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6892.png'
},
{
    id: 'Sugartown Oras-6893',
    name: 'Sugartown Oras #6893',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6893.png'
},
{
    id: 'Sugartown Oras-6891',
    name: 'Sugartown Oras #6891',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6891.png'
},
{
    id: 'Sugartown Oras-6890',
    name: 'Sugartown Oras #6890',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6890.png'
},
{
    id: 'Sugartown Oras-6889',
    name: 'Sugartown Oras #6889',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6889.png'
},
{
    id: 'Sugartown Oras-6888',
    name: 'Sugartown Oras #6888',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6888.png'
},
{
    id: 'Sugartown Oras-6887',
    name: 'Sugartown Oras #6887',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6887.png'
},
{
    id: 'Sugartown Oras-6886',
    name: 'Sugartown Oras #6886',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6886.png'
},
{
    id: 'Sugartown Oras-6883',
    name: 'Sugartown Oras #6883',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6883.png'
},
{
    id: 'Sugartown Oras-6882',
    name: 'Sugartown Oras #6882',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6882.png'
},
{
    id: 'Sugartown Oras-6881',
    name: 'Sugartown Oras #6881',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6881.png'
},
{
    id: 'Sugartown Oras-6880',
    name: 'Sugartown Oras #6880',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6880.png'
},
{
    id: 'Sugartown Oras-6879',
    name: 'Sugartown Oras #6879',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6879.png'
},
{
    id: 'Sugartown Oras-6877',
    name: 'Sugartown Oras #6877',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6877.png'
},
{
    id: 'Sugartown Oras-6876',
    name: 'Sugartown Oras #6876',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6876.png'
},
{
    id: 'Sugartown Oras-6875',
    name: 'Sugartown Oras #6875',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6875.png'
},
{
    id: 'Sugartown Oras-6874',
    name: 'Sugartown Oras #6874',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6874.png'
},
{
    id: 'Sugartown Oras-6873',
    name: 'Sugartown Oras #6873',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6873.png'
},
{
    id: 'Sugartown Oras-6872',
    name: 'Sugartown Oras #6872',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6872.png'
},
{
    id: 'Sugartown Oras-6869',
    name: 'Sugartown Oras #6869',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6869.png'
},
{
    id: 'Sugartown Oras-6871',
    name: 'Sugartown Oras #6871',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6871.png'
},
{
    id: 'Sugartown Oras-6870',
    name: 'Sugartown Oras #6870',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6870.png'
},
{
    id: 'Sugartown Oras-6868',
    name: 'Sugartown Oras #6868',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6868.png'
},
{
    id: 'Sugartown Oras-6867',
    name: 'Sugartown Oras #6867',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6867.png'
},
{
    id: 'Sugartown Oras-6866',
    name: 'Sugartown Oras #6866',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6866.png'
},
{
    id: 'Sugartown Oras-6864',
    name: 'Sugartown Oras #6864',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6864.png'
},
{
    id: 'Sugartown Oras-6863',
    name: 'Sugartown Oras #6863',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6863.png'
},
{
    id: 'Sugartown Oras-6860',
    name: 'Sugartown Oras #6860',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6860.png'
},
{
    id: 'Sugartown Oras-6861',
    name: 'Sugartown Oras #6861',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6861.png'
},
{
    id: 'Sugartown Oras-6862',
    name: 'Sugartown Oras #6862',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6862.png'
},
{
    id: 'Sugartown Oras-6856',
    name: 'Sugartown Oras #6856',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6856.png'
},
{
    id: 'Sugartown Oras-6858',
    name: 'Sugartown Oras #6858',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6858.png'
},
{
    id: 'Sugartown Oras-6859',
    name: 'Sugartown Oras #6859',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6859.png'
},
{
    id: 'Sugartown Oras-6854',
    name: 'Sugartown Oras #6854',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6854.png'
},
{
    id: 'Sugartown Oras-6857',
    name: 'Sugartown Oras #6857',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6857.png'
},
{
    id: 'Sugartown Oras-6855',
    name: 'Sugartown Oras #6855',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6855.png'
},
            {
    id: 'Sugartown Oras-6853',
    name: 'Sugartown Oras #6853',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6853.png'
},
{
    id: 'Sugartown Oras-6852',
    name: 'Sugartown Oras #6852',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6852.png'
},
{
    id: 'Sugartown Oras-6850',
    name: 'Sugartown Oras #6850',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6850.png'
},
{
    id: 'Sugartown Oras-6851',
    name: 'Sugartown Oras #6851',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6851.png'
},
{
    id: 'Sugartown Oras-6849',
    name: 'Sugartown Oras #6849',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6849.png'
},
{
    id: 'Sugartown Oras-6848',
    name: 'Sugartown Oras #6848',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6848.png'
},
{
    id: 'Sugartown Oras-6847',
    name: 'Sugartown Oras #6847',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6847.png'
},
{
    id: 'Sugartown Oras-6846',
    name: 'Sugartown Oras #6846',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6846.png'
},
{
    id: 'Sugartown Oras-6865',
    name: 'Sugartown Oras #6865',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6865.png'
},
{
    id: 'Sugartown Oras-6878',
    name: 'Sugartown Oras #6878',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6878.png'
},
{
    id: 'Sugartown Oras-6857',
    name: 'Sugartown Oras #6857',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6857.png'
},
{
    id: 'Sugartown Oras-6885',
    name: 'Sugartown Oras #6885',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6885.png'
},
{
    id: 'Sugartown Oras-6884',
    name: 'Sugartown Oras #6884',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6884.png'
},
{
    id: 'Sugartown Oras-6845',
    name: 'Sugartown Oras #6845',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6845.png'
},
{
    id: 'Sugartown Oras-6843',
    name: 'Sugartown Oras #6843',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6843.png'
},
{
    id: 'Sugartown Oras-6844',
    name: 'Sugartown Oras #6844',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6844.png'
},
{
    id: 'Sugartown Oras-6842',
    name: 'Sugartown Oras #6842',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6842.png'
},
{
    id: 'Sugartown Oras-6840',
    name: 'Sugartown Oras #6840',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6840.png'
},
{
    id: 'Sugartown Oras-6839',
    name: 'Sugartown Oras #6839',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6839.png'
},
{
    id: 'Sugartown Oras-6838',
    name: 'Sugartown Oras #6838',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6838.png'
},
{
    id: 'Sugartown Oras-6837',
    name: 'Sugartown Oras #6837',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6837.png'
},
{
    id: 'Sugartown Oras-6836',
    name: 'Sugartown Oras #6836',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6836.png'
},
{
    id: 'Sugartown Oras-6835',
    name: 'Sugartown Oras #6835',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6835.png'
},
{
    id: 'Sugartown Oras-6834',
    name: 'Sugartown Oras #6834',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6834.png'
},
{
    id: 'Sugartown Oras-6833',
    name: 'Sugartown Oras #6833',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6833.png'
},
{
    id: 'Sugartown Oras-6832',
    name: 'Sugartown Oras #6832',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6832.png'
},
{
    id: 'Sugartown Oras-6831',
    name: 'Sugartown Oras #6831',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6831.png'
},
{
    id: 'Sugartown Oras-6830',
    name: 'Sugartown Oras #6830',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6830.png'
},
{
    id: 'Sugartown Oras-6828',
    name: 'Sugartown Oras #6828',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6828.png'
},
{
    id: 'Sugartown Oras-6829',
    name: 'Sugartown Oras #6829',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6829.png'
},
            {
    id: 'Sugartown Oras-6827',
    name: 'Sugartown Oras #6827',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6827.png'
},
{
    id: 'Sugartown Oras-6825',
    name: 'Sugartown Oras #6825',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6825.png'
},
{
    id: 'Sugartown Oras-6824',
    name: 'Sugartown Oras #6824',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6824.png'
},
{
    id: 'Sugartown Oras-6822',
    name: 'Sugartown Oras #6822',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6822.png'
},
{
    id: 'Sugartown Oras-6821',
    name: 'Sugartown Oras #6821',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6821.png'
},
{
    id: 'Sugartown Oras-6820',
    name: 'Sugartown Oras #6820',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6820.png'
},
{
    id: 'Sugartown Oras-6818',
    name: 'Sugartown Oras #6818',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6818.png'
},
{
    id: 'Sugartown Oras-6817',
    name: 'Sugartown Oras #6817',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6817.png'
},
{
    id: 'Sugartown Oras-6816',
    name: 'Sugartown Oras #6816',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6816.png'
},
{
    id: 'Sugartown Oras-6815',
    name: 'Sugartown Oras #6815',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6815.png'
},
{
    id: 'Sugartown Oras-6813',
    name: 'Sugartown Oras #6813',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6813.png'
},
{
    id: 'Sugartown Oras-6812',
    name: 'Sugartown Oras #6812',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6812.png'
},
{
    id: 'Sugartown Oras-6826',
    name: 'Sugartown Oras #6826',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6826.png'
},
{
    id: 'Sugartown Oras-6814',
    name: 'Sugartown Oras #6814',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6814.png'
},
{
    id: 'Sugartown Oras-6841',
    name: 'Sugartown Oras #6841',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6841.png'
},
{
    id: 'Sugartown Oras-6823',
    name: 'Sugartown Oras #6823',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6823.png'
},
{
    id: 'Sugartown Oras-6819',
    name: 'Sugartown Oras #6819',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6819.png'
},
{
    id: 'Sugartown Oras-6811',
    name: 'Sugartown Oras #6811',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6811.png'
},
{
    id: 'Sugartown Oras-6809',
    name: 'Sugartown Oras #6809',
    image: 'https://nfts.visitsugartown.com/nfts/oras/6809.png'
}

                                ];
        actualNftData = sugartownNFTs.find(nft => nft.id.toString() === itemId);  
                                                
                             } else if (collectionId === 'the-plague-nft') {
        const theplagueNFTs = [
            
            {
    id: 'The Plague NFT-10997',
    name: 'The Plague #10997',
    image: 'https://ipfs.io/ipfs/QmV3XwQg4r6tPW7ReBze4iWUcqFeezsY6bMW1aUdPXJPNF'
},
{
    id: 'The Plague NFT-10999',
    name: 'The Plague #10999',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10999.png'
},
{
    id: 'The Plague NFT-10998',
    name: 'The Plague #10998',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10998.png'
},
{
    id: 'The Plague NFT-10995',
    name: 'The Plague #10995',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10995.png'
},
{
    id: 'The Plague NFT-10996',
    name: 'The Plague #10996',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10996.png'
},
{
    id: 'The Plague NFT-10994',
    name: 'The Plague #10994',
    image: 'https://ipfs.io/ipfs/QmS8XFCF4NNbr1ELJ7dArt3L44zVgbiS6qLYBY7GEfbJUz'
},
{
    id: 'The Plague NFT-10993',
    name: 'The Plague #10993',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10993.png'
},
{
    id: 'The Plague NFT-10992',
    name: 'The Plague #10992',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10992.png'
},
{
    id: 'The Plague NFT-10990',
    name: 'The Plague #10990',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10990.png'
},
{
    id: 'The Plague NFT-10991',
    name: 'The Plague #10991',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10991.png'
},
{
    id: 'The Plague NFT-10989',
    name: 'The Plague #10989',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10989.png'
},
{
    id: 'The Plague NFT-10988',
    name: 'The Plague #10988',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10988.png'
},
{
    id: 'The Plague NFT-10987',
    name: 'The Plague #10987',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10987.png'
},
{
    id: 'The Plague NFT-10986',
    name: 'The Plague #10986',
    image: 'https://ipfs.io/ipfs/QmR2K5HLMYAaGHbbF7ij9rQb9wqsH8vUwSfiZD5xRFj6UQ'
},
{
    id: 'The Plague NFT-10985',
    name: 'The Plague #10985',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10985.png'
},
{
    id: 'The Plague NFT-10984',
    name: 'The Plague #10984',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10984.png'
},
{
    id: 'The Plague NFT-10981',
    name: 'The Plague #10981',
    image: 'https://ipfs.io/ipfs/QmYCYVasJxa4eTsNbdTAZQ1BBisLRBb3id7GWp9xfHfm34'
},
{
    id: 'The Plague NFT-10983',
    name: 'The Plague #10983',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10983.png'
},
{
    id: 'The Plague NFT-10982',
    name: 'The Plague #10982',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10982.png'
},
{
    id: 'The Plague NFT-10980',
    name: 'The Plague #10980',
    image: 'https://ipfs.io/ipfs/QmabkmLa9pJ2Ew2YTQjuqSzbQRQwDrdHeRnfwanF16kpxP'
},
{
    id: 'The Plague NFT-10979',
    name: 'The Plague #10979',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10979.png'
},
{
    id: 'The Plague NFT-10978',
    name: 'The Plague #10978',
    image: 'https://ipfs.io/ipfs/QmcMmrZJkbYpxCj4K1PHCF2UVDpZbvHcHF5jGatqC1LUM2'
},
{
    id: 'The Plague NFT-10977',
    name: 'The Plague #10977',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10977.png'
},
{
    id: 'The Plague NFT-10976',
    name: 'The Plague #10976',
    image: 'https://ipfs.io/ipfs/Qmaq5tk1KhbqMLjZb9GiQaz94ofn4yJ3yhko6pAUgB38rM'
},
{
    id: 'The Plague NFT-10975',
    name: 'The Plague #10975',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10975.png'
},
{
    id: 'The Plague NFT-10973',
    name: 'The Plague #10973',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10973.png'
},
{
    id: 'The Plague NFT-10974',
    name: 'The Plague #10974',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10974.png'
},
{
    id: 'The Plague NFT-10972',
    name: 'The Plague #10972',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10972.png'
},
{
    id: 'The Plague NFT-10971',
    name: 'The Plague #10971',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10971.png'
},
{
    id: 'The Plague NFT-10969',
    name: 'The Plague #10969',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10969.png'
},
{
    id: 'The Plague NFT-10970',
    name: 'The Plague #10970',
    image: 'https://ipfs.io/ipfs/QmYyyNE73XQW7qVEAVgj1hsxjEvE5sKn6srx9hsZQdqdu9'
},
{
    id: 'The Plague NFT-10967',
    name: 'The Plague #10967',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10967.png'
},
{
    id: 'The Plague NFT-10968',
    name: 'The Plague #10968',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10968.png'
},
{
    id: 'The Plague NFT-10966',
    name: 'The Plague #10966',
    image: 'https://ipfs.io/ipfs/QmWJ7ayJyqS6wrWWh4pXqkt1M78Sah9f9MEysKeHfUKQB8'
},
{
    id: 'The Plague NFT-10965',
    name: 'The Plague #10965',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10965.png'
},
{
    id: 'The Plague NFT-10964',
    name: 'The Plague #10964',
    image: 'https://ipfs.io/ipfs/QmTc7iP1GtqQmkG5cTK9YRgRaWxZ17W8annjWjXyR6i2y3'
},
{
    id: 'The Plague NFT-10963',
    name: 'The Plague #10963',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10963.png'
},
{
    id: 'The Plague NFT-10962',
    name: 'The Plague #10962',
    image: 'https://ipfs.io/ipfs/QmXxAPsXTcC5m1PznF86oGH36enXG6Ld8GYrJ94BCY1ShE'
},
{
    id: 'The Plague NFT-10961',
    name: 'The Plague #10961',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10961.png'
},
{
    id: 'The Plague NFT-10960',
    name: 'The Plague #10960',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10960.png'
},
{
    id: 'The Plague NFT-10959',
    name: 'The Plague #10959',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10959.png'
},
{
    id: 'The Plague NFT-10958',
    name: 'The Plague #10958',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10958.png'
},
{
    id: 'The Plague NFT-10957',
    name: 'The Plague #10957',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10957.png'
},
{
    id: 'The Plague NFT-10956',
    name: 'The Plague #10956',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10956.png'
},
{
    id: 'The Plague NFT-10955',
    name: 'The Plague #10955',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10955.png'
},
{
    id: 'The Plague NFT-10954',
    name: 'The Plague #10954',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10954.png'
},
{
    id: 'The Plague NFT-10953',
    name: 'The Plague #10953',
    image: 'https://ipfs.io/ipfs/QmZNL83Ma1CDcvE2NEpMJ9M6PZgqH5ZwCuXee6LxvkRdKX'
},
{
    id: 'The Plague NFT-10952',
    name: 'The Plague #10952',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10952.png'
},
{
    id: 'The Plague NFT-10951',
    name: 'The Plague #10951',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10951.png'
},
{
    id: 'The Plague NFT-10950',
    name: 'The Plague #10950',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10950.png'
},
            {
    id: 'The Plague NFT-10949',
    name: 'The Plague #10949',
    image: 'https://ipfs.io/ipfs/QmUxBpdZVLyXmx1kWVEw1Fuy8382AvRZFUMotvDV1UHmQc'
},
{
    id: 'The Plague NFT-10948',
    name: 'The Plague #10948',
    image: 'https://ipfs.io/ipfs/QmQdi8eVth1BdiM9SaktHtNCZ29np1GXr2VxxHRJP4ZBJH'
},
{
    id: 'The Plague NFT-10946',
    name: 'The Plague #10946',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10946.png'
},
{
    id: 'The Plague NFT-10947',
    name: 'The Plague #10947',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10947.png'
},
{
    id: 'The Plague NFT-10945',
    name: 'The Plague #10945',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10945.png'
},
{
    id: 'The Plague NFT-10944',
    name: 'The Plague #10944',
    image: 'https://ipfs.io/ipfs/QmU1ksNfyiy8e835ZSPgTkovWH4fjuHJNaWQfAJFPmU5Ep'
},
{
    id: 'The Plague NFT-10943',
    name: 'The Plague #10943',
    image: 'https://ipfs.io/ipfs/QmWsY89nSXPnpkgtXWi5e3ZGMmsHj63HnrKDxMBYpsJEQz'
},
{
    id: 'The Plague NFT-10941',
    name: 'The Plague #10941',
    image: 'https://ipfs.io/ipfs/QmRxT3bZwksN3yhajGTo5d9oa44e9BDrvfTChY4ZtuqKWY'
},
{
    id: 'The Plague NFT-10942',
    name: 'The Plague #10942',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10942.png'
},
{
    id: 'The Plague NFT-10940',
    name: 'The Plague #10940',
    image: 'https://ipfs.io/ipfs/QmTbbUQAahsbewyYxC6VdWyhke1uKZBGPRiMTqoBfM3gEW'
},
{
    id: 'The Plague NFT-10939',
    name: 'The Plague #10939',
    image: 'https://ipfs.io/ipfs/Qmdm3MLsu74HnLKXTEF5t64ReFRU6qGAXPWpNqePYjyShg'
},
{
    id: 'The Plague NFT-10937',
    name: 'The Plague #10937',
    image: 'https://ipfs.io/ipfs/QmSW3fbGQHpfff4yGosv93hMKSaDQTFAbnEQiEnSB6n5Ld'
},
{
    id: 'The Plague NFT-10938',
    name: 'The Plague #10938',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10938.png'
},
{
    id: 'The Plague NFT-10935',
    name: 'The Plague #10935',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10935.png'
},
{
    id: 'The Plague NFT-10936',
    name: 'The Plague #10936',
    image: 'https://ipfs.io/ipfs/QmRyEdyQQ7BUs77DaYcQnMMKqEJbZ49ALPPEcJtZDuYnYi'
},
{
    id: 'The Plague NFT-10934',
    name: 'The Plague #10934',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10934.png'
},
{
    id: 'The Plague NFT-10933',
    name: 'The Plague #10933',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10933.png'
},
{
    id: 'The Plague NFT-10932',
    name: 'The Plague #10932',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10932.png'
},
{
    id: 'The Plague NFT-10931',
    name: 'The Plague #10931',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10931.png'
},
{
    id: 'The Plague NFT-10930',
    name: 'The Plague #10930',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10930.png'
},
{
    id: 'The Plague NFT-10927',
    name: 'The Plague #10927',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10927.png'
},
{
    id: 'The Plague NFT-10928',
    name: 'The Plague #10928',
    image: 'https://ipfs.io/ipfs/Qmc9zuuSDhPc7G4Xqjwh4TBLo37hMv4LpVd9TXnfwMuAVs'
},
{
    id: 'The Plague NFT-10929',
    name: 'The Plague #10929',
    image: 'https://ipfs.io/ipfs/QmSWrefvRdDQB4Hgq8RMqkEsWZdPSXs7AJPsRcyzxXvFtU'
},
{
    id: 'The Plague NFT-10926',
    name: 'The Plague #10926',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10926.png'
},
{
    id: 'The Plague NFT-10925',
    name: 'The Plague #10925',
    image: 'https://ipfs.io/ipfs/QmZngRduheXjY7B7DTqZCveYSHwJ3VExwjLN1rPZn3phQY'
},
{
    id: 'The Plague NFT-10924',
    name: 'The Plague #10924',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10924.png'
},
{
    id: 'The Plague NFT-10923',
    name: 'The Plague #10923',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10923.png'
},
{
    id: 'The Plague NFT-10922',
    name: 'The Plague #10922',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10922.png'
},
{
    id: 'The Plague NFT-10919',
    name: 'The Plague #10919',
    image: 'https://ipfs.io/ipfs/QmRViK9z6UHLUMncC5ycZb5BeXBLZhL1dSRM4LL9rVdoKX'
},
{
    id: 'The Plague NFT-10921',
    name: 'The Plague #10921',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10921.png'
},
{
    id: 'The Plague NFT-10920',
    name: 'The Plague #10920',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10920.png'
},
{
    id: 'The Plague NFT-10918',
    name: 'The Plague #10918',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10918.png'
},
{
    id: 'The Plague NFT-10917',
    name: 'The Plague #10917',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10917.png'
},
{
    id: 'The Plague NFT-10916',
    name: 'The Plague #10916',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10916.png'
},
{
    id: 'The Plague NFT-10915',
    name: 'The Plague #10915',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10915.png'
},
{
    id: 'The Plague NFT-10913',
    name: 'The Plague #10913',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10913.png'
},
{
    id: 'The Plague NFT-10911',
    name: 'The Plague #10911',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10911.png'
},
{
    id: 'The Plague NFT-10912',
    name: 'The Plague #10912',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10912.png'
},
{
    id: 'The Plague NFT-10914',
    name: 'The Plague #10914',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10914.png'
},
{
    id: 'The Plague NFT-10910',
    name: 'The Plague #10910',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10910.png'
},
{
    id: 'The Plague NFT-10909',
    name: 'The Plague #10909',
    image: 'https://ipfs.io/ipfs/QmcRm7F129d1Kt4wAuL6eeiXzg8sutRKe1SfobENURDZ85'
},
{
    id: 'The Plague NFT-10908',
    name: 'The Plague #10908',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10908.png'
},
{
    id: 'The Plague NFT-10907',
    name: 'The Plague #10907',
    image: 'https://ipfs.io/ipfs/QmbP5jXEveZv3D63UhHArSPZTYf9JBTcokJkehz5rzm7Kv'
},
{
    id: 'The Plague NFT-10905',
    name: 'The Plague #10905',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10905.png'
},
{
    id: 'The Plague NFT-10906',
    name: 'The Plague #10906',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10906.png'
},
{
    id: 'The Plague NFT-10904',
    name: 'The Plague #10904',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10904.png'
},
{
    id: 'The Plague NFT-10903',
    name: 'The Plague #10903',
    image: 'https://ipfs.io/ipfs/QmWAt8W9qjhd96nrRERvZmyLEtLoqWYw4moLuHtyWjVK5s'
},
{
    id: 'The Plague NFT-10902',
    name: 'The Plague #10902',
    image: 'https://ipfs.io/ipfs/QmUummtU2338HS3XCtgGQr7SGNUCFZuphPscpEjKm6vJUt'
},
{
    id: 'The Plague NFT-10901',
    name: 'The Plague #10901',
    image: 'https://ipfs.io/ipfs/QmQ6MDtfjg7LZbPRe7A3ZSPEtvYcVqUTmsuTMM1zhUR8MG/10901.png'
}
            
                                ];
        actualNftData = theplagueNFTs.find(nft => nft.id.toString() === itemId);  
                                                
                         } else if (collectionId === 'rosentica-starfall-travelers') {
        const rosenticaNFTs = [
            
            {
    id: 'Starfall Travelers-5996',
    name: 'Starfall Travelers #5996',
    image: 'https://ipfs.io/ipfs/bafybeibcxik55ohy5drzbjrrp2yvrmvwplnvqd7kd2rbovtpptdtxgcywm'
},
{
    id: 'Starfall Travelers-5994',
    name: 'Starfall Travelers #5994',
    image: 'https://ipfs.io/ipfs/bafybeibyjmbs3kkm6emmtk67dpdpyncyxn3maj65m6nvowh6he3jcisi5u'
},
{
    id: 'Starfall Travelers-5992',
    name: 'Starfall Travelers #5992',
    image: 'https://ipfs.io/ipfs/bafybeiat4dhksiuwcspcehj5hdyumo5kag3u2st2x76ldxmgcafhdykk6q'
},
{
    id: 'Starfall Travelers-5989',
    name: 'Starfall Travelers #5989',
    image: 'https://ipfs.io/ipfs/bafybeibssclvxvltljvseon72mevnc5p6pt5b6jzkom3rsdif42njqakge'
},
{
    id: 'Starfall Travelers-5997',
    name: 'Starfall Travelers #5997',
    image: 'https://ipfs.io/ipfs/bafybeiegpik3tuocfxuz4p44ffoy7t6fq4dkzzsfzacshaxdvbbcpgywmu'
},
{
    id: 'Starfall Travelers-5980',
    name: 'Starfall Travelers #5980',
    image: 'https://ipfs.io/ipfs/bafybeiadnhxy4eytrmvpdwbe2ax4hrjuy35mljxu3yj3wyusdoyx7rtaaq'
},
{
    id: 'Starfall Travelers-5986',
    name: 'Starfall Travelers #5986',
    image: 'https://ipfs.io/ipfs/bafybeih435llyrc7bzzptiumworozfftf7kg5gzzctxft5zwtcmaosgqty'
},
{
    id: 'Starfall Travelers-5978',
    name: 'Starfall Travelers #5978',
    image: 'https://ipfs.io/ipfs/bafybeih2tblrkh6hrcjqnh3w7az5fr75nz7jozwch6ogfxk4jbzik56x2y'
},
{
    id: 'Starfall Travelers-5982',
    name: 'Starfall Travelers #5982',
    image: 'https://ipfs.io/ipfs/bafybeicx5h2hzpb2rajycqp2gw4unwu5bcttxpwpauwxqzumfhos2brufy'
},
{
    id: 'Starfall Travelers-5995',
    name: 'Starfall Travelers #5995',
    image: 'https://ipfs.io/ipfs/bafybeieqrs6xiqlpzv2loguus6oy6y5tq2thn2ubamz3526wpely6cuxgm'
},
{
    id: 'Starfall Travelers-5993',
    name: 'Starfall Travelers #5993',
    image: 'https://ipfs.io/ipfs/bafybeibudm7pcvgqprdy5u342g43a5mab7xgmj4ttm6zo56wqfjpnixouq'
},
{
    id: 'Starfall Travelers-5990',
    name: 'Starfall Travelers #5990',
    image: 'https://ipfs.io/ipfs/bafybeib4e62g3unx5js4ggdqdgtwvc4z5mldlvdte3xzd3wpxoybn3nnvu'
},
{
    id: 'Starfall Travelers-5991',
    name: 'Starfall Travelers #5991',
    image: 'https://ipfs.io/ipfs/bafybeidmq5fja2og6wqvad64khrygyauqqhtrqfkczahrsjpjnbmrl3lgu'
},
{
    id: 'Starfall Travelers-5999',
    name: 'Starfall Travelers #5999',
    image: 'https://ipfs.io/ipfs/bafybeicohw7rfixtcukfgonygmg4lp2muuxyhi5wzxsxux66vllrks7f2u'
},
{
    id: 'Starfall Travelers-6000',
    name: 'Starfall Travelers #6000',
    image: 'https://ipfs.io/ipfs/bafybeifo3wjy3d6nlbadli6mkds6ivix6xs3s42peacuvofawv7e642y44'
},
{
    id: 'Starfall Travelers-5998',
    name: 'Starfall Travelers #5998',
    image: 'https://ipfs.io/ipfs/bafybeig2z52manqadquyuyl7qpp4gybtuwm2m3nacimap3pmc3nureuooa'
},
{
    id: 'Starfall Travelers-5988',
    name: 'Starfall Travelers #5988',
    image: 'https://ipfs.io/ipfs/bafybeiap52nil5c6mxlvyyjofo3xfsbtgwl3j2fsrtvhutgbul7pfp5ss4'
},
{
    id: 'Starfall Travelers-5981',
    name: 'Starfall Travelers #5981',
    image: 'https://ipfs.io/ipfs/bafybeiaqf7ov5lb2etaq4zpcn4li67w4g74iz65fgq2ebumntk6slgei5i'
},
{
    id: 'Starfall Travelers-5987',
    name: 'Starfall Travelers #5987',
    image: 'https://ipfs.io/ipfs/bafybeifsrz37uumgjohvpiejlbwdkgy2udndzcbtdgjal2cdyrp2ens43a'
},
{
    id: 'Starfall Travelers-5984',
    name: 'Starfall Travelers #5984',
    image: 'https://ipfs.io/ipfs/bafybeiba53j46ii4idy3rfh6birehhuvjk4wqiip236p6zigcjxgb2oklm'
},
{
    id: 'Starfall Travelers-5985',
    name: 'Starfall Travelers #5985',
    image: 'https://ipfs.io/ipfs/bafybeiewpciw7zilbomaffqujybqalxa74fgapfbowwly5xaegx7xtjz5m'
},
{
    id: 'Starfall Travelers-5979',
    name: 'Starfall Travelers #5979',
    image: 'https://ipfs.io/ipfs/bafybeidbldnlxubrbtkgwdhhficbdaajkqzci5l7peh52lfad7qdeel7zy'
},
{
    id: 'Starfall Travelers-5983',
    name: 'Starfall Travelers #5983',
    image: 'https://ipfs.io/ipfs/bafybeiaaetgmnkuxff3xntll36o22bitc6zguy3mtw37pn7xknytwbfbte'
},
{
    id: 'Starfall Travelers-5920',
    name: 'Starfall Travelers #5920',
    image: 'https://ipfs.io/ipfs/bafybeie64kbuedqvxhtdiffrwb25oogyzx3pjeel6rv25vvt6h656jzzcy'
},
{
    id: 'Starfall Travelers-5958',
    name: 'Starfall Travelers #5958',
    image: 'https://ipfs.io/ipfs/bafybeibp4thuv52l4acnko43pdz3sr676kqk343mkukgtz4et6h4wijzdu'
},
{
    id: 'Starfall Travelers-5942',
    name: 'Starfall Travelers #5942',
    image: 'https://ipfs.io/ipfs/bafybeibdetzl66phi6gv3ua7xiu6sqfla4zur2ay5ehipiq2ismbr5a6cq'
},
{
    id: 'Starfall Travelers-5977',
    name: 'Starfall Travelers #5977',
    image: 'https://ipfs.io/ipfs/bafybeifssovy2xi733tpp6digauc2h3qi6vwtkitdvfnr3hwhzww7uxogu'
},
{
    id: 'Starfall Travelers-5953',
    name: 'Starfall Travelers #5953',
    image: 'https://ipfs.io/ipfs/bafybeic4xx2p472ufgdesoiddc6pc7wkjydrm2vrupbl3rbwkgomj746am'
},
{
    id: 'Starfall Travelers-5974',
    name: 'Starfall Travelers #5974',
    image: 'https://ipfs.io/ipfs/bafybeigeo6n5dzcxhuv4z6tmqn6onw36xyyc4fws3yke6aev3wxbec47oa'
},
{
    id: 'Starfall Travelers-5940',
    name: 'Starfall Travelers #5940',
    image: 'https://ipfs.io/ipfs/bafybeihkwknd4ezypsgzfxbc4bm2x6vcxkegqj7irsmae6trjv5bw3l3vm'
},
{
    id: 'Starfall Travelers-5951',
    name: 'Starfall Travelers #5951',
    image: 'https://ipfs.io/ipfs/bafybeid5ctksb6esvqxw2cjn3zxcjbjxvj3cd6qrgopjenrio25zmdw2ie'
},
{
    id: 'Starfall Travelers-5949',
    name: 'Starfall Travelers #5949',
    image: 'https://ipfs.io/ipfs/bafybeibegaubmgekevud3acexuompztudzchaopej7wsilwrbn66sqayea'
},
{
    id: 'Starfall Travelers-5923',
    name: 'Starfall Travelers #5923',
    image: 'https://ipfs.io/ipfs/bafybeia7q2a5wzuedcbzckeh7ukclivjcog57cge5zg7sgzcnvo77bmove'
},
{
    id: 'Starfall Travelers-5936',
    name: 'Starfall Travelers #5936',
    image: 'https://ipfs.io/ipfs/bafybeibjx2nfwtat7bdthrzh4e33zid3hqcodjd44ygs3lfu7eqw4daziy'
},
{
    id: 'Starfall Travelers-5966',
    name: 'Starfall Travelers #5966',
    image: 'https://ipfs.io/ipfs/bafybeiga3eunijbet5i3ce2p5i2nps3xolx2ielgxntoyhsyt3b4qq2zc4'
},
{
    id: 'Starfall Travelers-5967',
    name: 'Starfall Travelers #5967',
    image: 'https://ipfs.io/ipfs/bafybeigbm26kf5nnbhruk26tr5gykc5cyrmsly4w4emjeiepfct7us2ah4'
},
{
    id: 'Starfall Travelers-5930',
    name: 'Starfall Travelers #5930',
    image: 'https://ipfs.io/ipfs/bafybeieyunc3n6h6lqbwcm3p7jo2eesw6udb53vbatmnkqgpum4daqwogu'
},
{
    id: 'Starfall Travelers-5929',
    name: 'Starfall Travelers #5929',
    image: 'https://ipfs.io/ipfs/bafybeib5njceqxpo5ltqevifbjditkptur66m7afyrugihva77lvszbjlm'
},
{
    id: 'Starfall Travelers-5956',
    name: 'Starfall Travelers #5956',
    image: 'https://ipfs.io/ipfs/bafybeiaoq4l7avlg72wkba2zqsnfemnrjjbzidtxzh55dexdglv4og6eea'
},
{
    id: 'Starfall Travelers-5925',
    name: 'Starfall Travelers #5925',
    image: 'https://ipfs.io/ipfs/bafybeigr5diiggqfjqy7qjm6jznnzgflyrzbopvc3en6mq3jgiw6oy2nfm'
},
{
    id: 'Starfall Travelers-5962',
    name: 'Starfall Travelers #5962',
    image: 'https://ipfs.io/ipfs/bafybeifgldzil6jnqtqbc4ueiirpthd2t6qnhcen4pte2jkumycwgnipla'
},
{
    id: 'Starfall Travelers-5972',
    name: 'Starfall Travelers #5972',
    image: 'https://ipfs.io/ipfs/bafybeibakbyikuqvy64xthgmoej4bmlbdiit6i2fkz3jtbvuz7tsq77dau'
},
{
    id: 'Starfall Travelers-5970',
    name: 'Starfall Travelers #5970',
    image: 'https://ipfs.io/ipfs/bafybeicobvyew7nsnymmxxirhknjwz6jowt2725i5hr4yrj45z65jeh43u'
},
{
    id: 'Starfall Travelers-5944',
    name: 'Starfall Travelers #5944',
    image: 'https://ipfs.io/ipfs/bafybeigxku3ladt6sr4wrmeahfswmjnj363hke5de34x4f6hwvswyud7la'
},
{
    id: 'Starfall Travelers-5955',
    name: 'Starfall Travelers #5955',
    image: 'https://ipfs.io/ipfs/bafybeieadycujmznqcatvpx5wujllyql4v4ncksjwzywossbof4qonvebu'
},
{
    id: 'Starfall Travelers-5918',
    name: 'Starfall Travelers #5918',
    image: 'https://ipfs.io/ipfs/bafybeibwrs7r2cafzhlfrdmcfoyfrnkspw36ljm7vmorxwsf7zvhbpkhkm'
},
{
    id: 'Starfall Travelers-5933',
    name: 'Starfall Travelers #5933',
    image: 'https://ipfs.io/ipfs/bafybeibwqigzco2bvyplvktd3uovnwtzqbrqaavmx5kq4zmslx2m35smvi'
},
{
    id: 'Starfall Travelers-5961',
    name: 'Starfall Travelers #5961',
    image: 'https://ipfs.io/ipfs/bafybeigy6hrxo5pzkzzcqckkeozq3e7enm7lwklzpyggj6qxfxqascfbie'
},
{
    id: 'Starfall Travelers-5927',
    name: 'Starfall Travelers #5927',
    image: 'https://ipfs.io/ipfs/bafybeiexwyks5w2ka4yslwmspoi5gy3aippbrm6idohahwlsf3dze5zqfm'
},
{
    id: 'Starfall Travelers-5943',
    name: 'Starfall Travelers #5943',
    image: 'https://ipfs.io/ipfs/bafybeicsnbamhjxppoo6kw222x6jcnfvqm7ybq3rmj43fimh7ixsqthi6e'
},
{
    id: 'Starfall Travelers-5947',
    name: 'Starfall Travelers #5947',
    image: 'https://ipfs.io/ipfs/bafybeidjejevht77xxyi2jiabocxjoxxlt4k76tbpho4ir7tynwebxkhza'
},
{
    id: 'Starfall Travelers-5976',
    name: 'Starfall Travelers #5976',
    image: 'https://ipfs.io/ipfs/bafybeidzrqljsrl3byubcsszzzcpshj4gsermin4jb5i6ca36os5nlnc6q'
},
{
    id: 'Starfall Travelers-5975',
    name: 'Starfall Travelers #5975',
    image: 'https://ipfs.io/ipfs/bafybeic6felvfgn47s2sjgwyt33jbxucu465ay6lqcw5htmndo6eki6vpi'
},
         {
    id: 'Starfall Travelers-5971',
    name: 'Starfall Travelers #5971',
    image: 'https://ipfs.io/ipfs/bafybeibcuvzcmph3dpdtf3j6pebp2i7vnvf7sdynro5mauemotoytk5wnu'
},
{
    id: 'Starfall Travelers-5948',
    name: 'Starfall Travelers #5948',
    image: 'https://ipfs.io/ipfs/bafybeibujctllpswdgbrxncovbpswskxpkfj6o2snp4gbeoes33shc2wou'
},
{
    id: 'Starfall Travelers-5959',
    name: 'Starfall Travelers #5959',
    image: 'https://ipfs.io/ipfs/bafybeiby6fpwfftc444ofpnqnl2nh54sm5qobh537jm7wcs3ucgxm32xy4'
},
{
    id: 'Starfall Travelers-5950',
    name: 'Starfall Travelers #5950',
    image: 'https://ipfs.io/ipfs/bafybeih2ocuaa3zpxoq44cqxonx4ecmjkncfotbtz25h4mfmhqfj6aadau'
},
{
    id: 'Starfall Travelers-5939',
    name: 'Starfall Travelers #5939',
    image: 'https://ipfs.io/ipfs/bafybeicg42fny7hfjbhyxtgdl34skxipl2zdxfsaujtdjc55bhoct2fcqq'
},
{
    id: 'Starfall Travelers-5941',
    name: 'Starfall Travelers #5941',
    image: 'https://ipfs.io/ipfs/bafybeigvb7nhlleitzriwfy3wrvqtqbzmt37zgxug2zju2ads6yx4hzbvm'
},
{
    id: 'Starfall Travelers-5973',
    name: 'Starfall Travelers #5973',
    image: 'https://ipfs.io/ipfs/bafybeickq4dlo2bzsw5ynaquhht5peuqlfqrapwikzn53dkim2rxzoxd4u'
},
{
    id: 'Starfall Travelers-5921',
    name: 'Starfall Travelers #5921',
    image: 'https://ipfs.io/ipfs/bafybeifixrkuzvdo4cytcnxl5rzv3k6rz644ekaie6jf27kd534xg2vvdm'
},
{
    id: 'Starfall Travelers-5946',
    name: 'Starfall Travelers #5946',
    image: 'https://ipfs.io/ipfs/bafybeigc75clvmb2qgdjjv55b567hrr745sudbpjgjq3u5e5pwpttfldka'
},
{
    id: 'Starfall Travelers-5952',
    name: 'Starfall Travelers #5952',
    image: 'https://ipfs.io/ipfs/bafybeihy64i6ytb7ayochdup6h3ecfp5aaxseopbnwgcog2u3r3623t6cm'
},
{
    id: 'Starfall Travelers-5965',
    name: 'Starfall Travelers #5965',
    image: 'https://ipfs.io/ipfs/bafybeihc23ba6sgfhhnorsr7wdg54t2hubrg6fsvbce7srxk5bydzkahgy'
},
{
    id: 'Starfall Travelers-5969',
    name: 'Starfall Travelers #5969',
    image: 'https://ipfs.io/ipfs/bafybeic26xgbrcscfz62f7bfdmn4g3b64qcs367qop63gcdozrrk4xxdfm'
},
{
    id: 'Starfall Travelers-5964',
    name: 'Starfall Travelers #5964',
    image: 'https://ipfs.io/ipfs/bafybeicrgpa5bg45n2i7l5e5cenyq4q3c5i3daxrw4enbn3uwkp7iroltm'
},
{
    id: 'Starfall Travelers-5968',
    name: 'Starfall Travelers #5968',
    image: 'https://ipfs.io/ipfs/bafybeicnpzhwsfjwtia5xnaccjm2bu6sxi6s54fuwgvvizcky3estfhrke'
},
{
    id: 'Starfall Travelers-5922',
    name: 'Starfall Travelers #5922',
    image: 'https://ipfs.io/ipfs/bafybeibec3bptwvkvjmgvphdwdq7qzlxog4y3cuomevknu6yfzpnfig3xe'
},
{
    id: 'Starfall Travelers-5935',
    name: 'Starfall Travelers #5935',
    image: 'https://ipfs.io/ipfs/bafybeibqa246p4vdi7epn6sqvfnpeukq5grex4kzbhiqiv2xejbjo26kxi'
},
{
    id: 'Starfall Travelers-5963',
    name: 'Starfall Travelers #5963',
    image: 'https://ipfs.io/ipfs/bafybeid2gg7p2iomkwqmca5bfnkwhsuvdtwsmedeonmorx3izsokl6mwee'
},
{
    id: 'Starfall Travelers-5931',
    name: 'Starfall Travelers #5931',
    image: 'https://ipfs.io/ipfs/bafybeietafnvjrvxt2x4dox4hs3wb5rkxkv3zx2ud55fp5xnauyatyc6ji'
},
{
    id: 'Starfall Travelers-5932',
    name: 'Starfall Travelers #5932',
    image: 'https://ipfs.io/ipfs/bafybeid5kxqnmoj7w7esro5nleism6hswtscsirkdyr36c5rrlnx6iorom'
},
{
    id: 'Starfall Travelers-5957',
    name: 'Starfall Travelers #5957',
    image: 'https://ipfs.io/ipfs/bafybeihzek2eoyufsqt3pvomtps3wtjfx6l3ju7dmeeomke3n7xdesyyri'
},
{
    id: 'Starfall Travelers-5954',
    name: 'Starfall Travelers #5954',
    image: 'https://ipfs.io/ipfs/bafybeibjmhhxvjqrniwhhjrro7knjbqh5z54owp2bo6j3gulygnovfxh44'
},
{
    id: 'Starfall Travelers-5919',
    name: 'Starfall Travelers #5919',
    image: 'https://ipfs.io/ipfs/bafybeiddcznwyzkq77b7s73gvb3v56stnrfioqdvayb43drmck2jwfeoou'
},
{
    id: 'Starfall Travelers-5934',
    name: 'Starfall Travelers #5934',
    image: 'https://ipfs.io/ipfs/bafybeibozecqswxjvcsvac5zsn7jlpl23cfn5wptqlxkrgde6wu7nj3b5e'
},
{
    id: 'Starfall Travelers-5938',
    name: 'Starfall Travelers #5938',
    image: 'https://ipfs.io/ipfs/bafybeifvxatnh3pk6kxvfdf57mjcs6v7d6toerjgsx2jgkv43drmiswjpe'
},
{
    id: 'Starfall Travelers-5937',
    name: 'Starfall Travelers #5937',
    image: 'https://ipfs.io/ipfs/bafybeifk3w2qfrmqhk3cnuvg76pbj6jnxvextwmlp7oafxsp2qsrt4oiy4'
},
{
    id: 'Starfall Travelers-5960',
    name: 'Starfall Travelers #5960',
    image: 'https://ipfs.io/ipfs/bafybeibhcehyn2t7s7q76j5ioajjyjpo4mokymsllec7nfgqzzjoxsrpdq'
},
{
    id: 'Starfall Travelers-5945',
    name: 'Starfall Travelers #5945',
    image: 'https://ipfs.io/ipfs/bafybeihb3b5rw2nyqoxl6jabiphofwxu3zdoisigfpx7u3ryngtiid33ui'
},
{
    id: 'Starfall Travelers-5928',
    name: 'Starfall Travelers #5928',
    image: 'https://ipfs.io/ipfs/bafybeiar2ls4ctuzonjku4hkxtupygjznr3ugkeqz7ricge3v6dhd32wf4'
},
{
    id: 'Starfall Travelers-5924',
    name: 'Starfall Travelers #5924',
    image: 'https://ipfs.io/ipfs/bafybeiajjsdf3nocvk3jdnw5stdgz5wcqo7n7sxpd7kledlihxnu6zbhce'
},
{
    id: 'Starfall Travelers-5926',
    name: 'Starfall Travelers #5926',
    image: 'https://ipfs.io/ipfs/bafybeiazkglilpkg75f4m6ihio37urpiw6g77zdimq32lvkn5bsfbjkty4'
},
{
    id: 'Starfall Travelers-5907',
    name: 'Starfall Travelers #5907',
    image: 'https://ipfs.io/ipfs/bafybeihanye56jh4geupf3jyeibrqqidwr6pjfinrj6ydog54u5rs7l5h4'
},
{
    id: 'Starfall Travelers-5909',
    name: 'Starfall Travelers #5909',
    image: 'https://ipfs.io/ipfs/bafybeibmnyh6xfxaepqkrqlre5mrusjam2vkw4qp5slxvsjz4pdcu7imhi'
},
{
    id: 'Starfall Travelers-5917',
    name: 'Starfall Travelers #5917',
    image: 'https://ipfs.io/ipfs/bafybeidhx3jq7yawwqscismn4vf3ahfbgprbw7lwxx43bmymff33ogcxiq'
},
{
    id: 'Starfall Travelers-5911',
    name: 'Starfall Travelers #5911',
    image: 'https://ipfs.io/ipfs/bafybeicnl7gaom5lcdwzohbmrancliud5ewcks32hrile7ia7fpzq24tsm'
},
{
    id: 'Starfall Travelers-5915',
    name: 'Starfall Travelers #5915',
    image: 'https://ipfs.io/ipfs/bafybeialujkgkrqdaqhuexnult2wjpbweya2mchhxbvxow3qrwvuy5ig5m'
},
{
    id: 'Starfall Travelers-5898',
    name: 'Starfall Travelers #5898',
    image: 'https://ipfs.io/ipfs/bafybeieoidakda2ylqy2hzgxxdwfutlaoddosmcc3q7phh22yms5fmg4rq'
},
{
    id: 'Starfall Travelers-5885',
    name: 'Starfall Travelers #5885',
    image: 'https://ipfs.io/ipfs/bafybeiddjoyngo4qg2lzmipnwwlda4zzeajhvazmq4tq7sbjdoslalrdqu'
},
{
    id: 'Starfall Travelers-5890',
    name: 'Starfall Travelers #5890',
    image: 'https://ipfs.io/ipfs/bafybeig6tg6ogfj7xxhysojtjuz7fj7yx3z3rflsuv3gft6iu4zhqytvhe'
},
{
    id: 'Starfall Travelers-5892',
    name: 'Starfall Travelers #5892',
    image: 'https://ipfs.io/ipfs/bafybeibcskyol7z3rbe2xuot2jhmofbmftctzsapz4gpim4o7275z2fxzm'
},
{
    id: 'Starfall Travelers-5905',
    name: 'Starfall Travelers #5905',
    image: 'https://ipfs.io/ipfs/bafybeifjgllwuvnfujeqyfmjwm7cix54bklf572hkkeyw2yxejr6o2yge4'
},
{
    id: 'Starfall Travelers-5900',
    name: 'Starfall Travelers #5900',
    image: 'https://ipfs.io/ipfs/bafybeigvwjiqe6ja4yjm6ljahlqtduxck7knwe55utytexhfdwmlooj3ci'
},
{
    id: 'Starfall Travelers-5887',
    name: 'Starfall Travelers #5887',
    image: 'https://ipfs.io/ipfs/bafybeidkufbdfh34emp37vynkzkuc3hhcww3pve3czsefsegg6bmvuk2lm'
},
{
    id: 'Starfall Travelers-5904',
    name: 'Starfall Travelers #5904',
    image: 'https://ipfs.io/ipfs/bafybeihunlsrzuqkqsnut7x7rsmi7bog5aspn34afucgiwh3bbqs5gmaiq'
},
{
    id: 'Starfall Travelers-5895',
    name: 'Starfall Travelers #5895',
    image: 'https://ipfs.io/ipfs/bafybeialjwrebw7qnsko4q7b36vuztpw67ioto4ydrmvnaeexiez5lfw5y'
},
{
    id: 'Starfall Travelers-5880',
    name: 'Starfall Travelers #5880',
    image: 'https://ipfs.io/ipfs/bafybeihgwi3fqqbe5f7s3sdfqktae3szggzhalziqa2xtmjjq6dskqveue'
},
{
    id: 'Starfall Travelers-5877',
    name: 'Starfall Travelers #5877',
    image: 'https://ipfs.io/ipfs/bafybeieq6ppmcvuvswlxrong5qvgidw6wprommewmzicpcbqmtvwtuhgja'
},
{
    id: 'Starfall Travelers-5916',
    name: 'Starfall Travelers #5916',
    image: 'https://ipfs.io/ipfs/bafybeide34mt3c2ntyl3t6kgokqjmwj3ofgq2mib3rzs3rqyw23fapze4i'
}   
                                ];
        actualNftData = rosenticaNFTs.find(nft => nft.id.toString() === itemId);  
                                                
                              } else if (collectionId === 'persona') {
        const personaNFTs = [
            
            {
    id: 'Persona-8045',
    name: 'Persona #8045',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-3369695600519266781.jpeg'
},
{
    id: 'Persona-8044',
    name: 'Persona #8044',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-1344410766950876220.jpeg'
},
{
    id: 'Persona-8043',
    name: 'Persona #8043',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-4165120016894645583.jpeg'
},
{
    id: 'Persona-8042',
    name: 'Persona #8042',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-8949387578378184341.jpeg'
},
{
    id: 'Persona-8041',
    name: 'Persona #8041',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2998779401717753726.jpeg'
},
{
    id: 'Persona-8040',
    name: 'Persona #8040',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2953727010370015096.jpeg'
},
{
    id: 'Persona-8039',
    name: 'Persona #8039',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-6400894736701960913.jpeg'
},
{
    id: 'Persona-8038',
    name: 'Persona #8038',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-5645276645583657979.jpeg'
},
{
    id: 'Persona-8037',
    name: 'Persona #8037',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-532328578655598917.jpeg'
},
{
    id: 'Persona-8036',
    name: 'Persona #8036',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-4095069714805185695.jpeg'
},
{
    id: 'Persona-8035',
    name: 'Persona #8035',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-1232062885838698927.jpeg'
},
{
    id: 'Persona-8020',
    name: 'Persona #8020',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-4222938470356683438.jpeg'
},
{
    id: 'Persona-8025',
    name: 'Persona #8025',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-5353698501409135051.jpeg'
},
{
    id: 'Persona-8019',
    name: 'Persona #8019',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-7486012550386369010.jpeg'
},
{
    id: 'Persona-8029',
    name: 'Persona #8029',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-6550549210987200419.jpeg'
},
{
    id: 'Persona-8018',
    name: 'Persona #8018',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-5895054764253360551.jpeg'
},
{
    id: 'Persona-8027',
    name: 'Persona #8027',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2922843394477517143.jpeg'
},
{
    id: 'Persona-8024',
    name: 'Persona #8024',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-5877585277896503984.jpeg'
},
{
    id: 'Persona-8031',
    name: 'Persona #8031',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2229640368010627998.jpeg'
},
{
    id: 'Persona-8033',
    name: 'Persona #8033',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-8567148717916213076.jpeg'
},
{
    id: 'Persona-8021',
    name: 'Persona #8021',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-5614727484743454149.jpeg'
},
{
    id: 'Persona-8026',
    name: 'Persona #8026',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-8023',
    name: 'Persona #8023',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-8841106479725999254.jpeg'
},
{
    id: 'Persona-8032',
    name: 'Persona #8032',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-523531606668918372.jpeg'
},
{
    id: 'Persona-8034',
    name: 'Persona #8034',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-8022',
    name: 'Persona #8022',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-3181296168989139219.jpeg'
},
{
    id: 'Persona-8030',
    name: 'Persona #8030',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-8028',
    name: 'Persona #8028',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-1572435388649048068.jpeg'
},
{
    id: 'Persona-8017',
    name: 'Persona #8017',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2064273219293799214.jpeg'
},
{
    id: 'Persona-8016',
    name: 'Persona #8016',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-4134689301897761298.jpeg'
},
{
    id: 'Persona-8015',
    name: 'Persona #8015',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-8014',
    name: 'Persona #8014',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-797077979406182044.jpeg'
},
{
    id: 'Persona-8013',
    name: 'Persona #8013',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-1276022758399454783.jpeg'
},
{
    id: 'Persona-8012',
    name: 'Persona #8012',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2146800455759999255.jpeg'
},
{
    id: 'Persona-8011',
    name: 'Persona #8011',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-4393139134337967560.jpeg'
},
{
    id: 'Persona-8010',
    name: 'Persona #8010',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-533482504851408792.jpeg'
},
{
    id: 'Persona-8009',
    name: 'Persona #8009',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-2132346856532149432.jpeg'
},
{
    id: 'Persona-8008',
    name: 'Persona #8008',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-5173407491958734173.jpeg'
},
{
    id: 'Persona-8007',
    name: 'Persona #8007',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-6142513677949469391.jpeg'
},
{
    id: 'Persona-8006',
    name: 'Persona #8006',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-3345276101626722522.jpeg'
},
{
    id: 'Persona-8005',
    name: 'Persona #8005',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-1648099861989912068.jpeg'
},
{
    id: 'Persona-8004',
    name: 'Persona #8004',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-6743427611575843820.jpeg'
},
{
    id: 'Persona-8003',
    name: 'Persona #8003',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715439-7912300567397352968.jpeg'
},
{
    id: 'Persona-8002',
    name: 'Persona #8002',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-7289898140321261112.jpeg'
},
{
    id: 'Persona-8001',
    name: 'Persona #8001',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-7237714202358944396.jpeg'
},
{
    id: 'Persona-8000',
    name: 'Persona #8000',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5060259326165204722.jpeg'
},
{
    id: 'Persona-7999',
    name: 'Persona #7999',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-6216352924295547310.jpeg'
},
{
    id: 'Persona-7998',
    name: 'Persona #7998',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-150613119606805118.jpeg'
},
     {
    id: 'Persona-7997',
    name: 'Persona #7997',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-6190921246331834992.jpeg'
},
{
    id: 'Persona-7996',
    name: 'Persona #7996',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-2752404093956784456.jpeg'
},
{
    id: 'Persona-7995',
    name: 'Persona #7995',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-7994',
    name: 'Persona #7994',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-6951851299237749487.jpeg'
},
{
    id: 'Persona-7993',
    name: 'Persona #7993',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-7992',
    name: 'Persona #7992',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-8617531872594830712.jpeg'
},
{
    id: 'Persona-7991',
    name: 'Persona #7991',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-570919986172120191.jpeg'
},
{
    id: 'Persona-7990',
    name: 'Persona #7990',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-22614824324479311.jpeg'
},
{
    id: 'Persona-7989',
    name: 'Persona #7989',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-8329900179684895931.jpeg'
},
{
    id: 'Persona-7988',
    name: 'Persona #7988',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-3684205208671863509.jpeg'
},
{
    id: 'Persona-7987',
    name: 'Persona #7987',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-1338853697458964357.jpeg'
},
{
    id: 'Persona-7986',
    name: 'Persona #7986',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5001610797926715023.jpeg'
},
{
    id: 'Persona-7985',
    name: 'Persona #7985',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5833919086233561846.jpeg'
},
{
    id: 'Persona-7984',
    name: 'Persona #7984',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-3207820841276099382.jpeg'
},
{
    id: 'Persona-7983',
    name: 'Persona #7983',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-3673879406970171142.jpeg'
},
{
    id: 'Persona-7981',
    name: 'Persona #7981',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-2580515537014061786.jpeg'
},
{
    id: 'Persona-7982',
    name: 'Persona #7982',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-784205518016261870.jpeg'
},
{
    id: 'Persona-7979',
    name: 'Persona #7979',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-6132471537277302290.jpeg'
},
{
    id: 'Persona-7980',
    name: 'Persona #7980',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5256134164122790733.jpeg'
},
{
    id: 'Persona-7977',
    name: 'Persona #7977',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5837600618363619676.jpeg'
},
{
    id: 'Persona-7978',
    name: 'Persona #7978',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-527322791259867962.jpeg'
},
{
    id: 'Persona-7976',
    name: 'Persona #7976',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-7632428343217071674.jpeg'
},
{
    id: 'Persona-7975',
    name: 'Persona #7975',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-4084828042735525331.jpeg'
},
{
    id: 'Persona-7974',
    name: 'Persona #7974',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-3470227769587907031.jpeg'
},
{
    id: 'Persona-7972',
    name: 'Persona #7972',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-447250134457934134.jpeg'
},
{
    id: 'Persona-7973',
    name: 'Persona #7973',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5612909555875143720.jpeg'
},
{
    id: 'Persona-7970',
    name: 'Persona #7970',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-485252224953432106.jpeg'
},
{
    id: 'Persona-7971',
    name: 'Persona #7971',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-8199381026660412628.jpeg'
},
{
    id: 'Persona-7969',
    name: 'Persona #7969',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-3853858096586372764.jpeg'
},
{
    id: 'Persona-7968',
    name: 'Persona #7968',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-7611096987109660233.jpeg'
},
{
    id: 'Persona-7967',
    name: 'Persona #7967',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5997399366946168313.jpeg'
},
{
    id: 'Persona-7966',
    name: 'Persona #7966',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-6569500053315002909.jpeg'
},
{
    id: 'Persona-7965',
    name: 'Persona #7965',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-7378222286262828995.jpeg'
},
{
    id: 'Persona-7964',
    name: 'Persona #7964',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-8276565573035858655.jpeg'
},
{
    id: 'Persona-7963',
    name: 'Persona #7963',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-4931240125955631535.jpeg'
},
{
    id: 'Persona-7962',
    name: 'Persona #7962',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-8625648512596629164.jpeg'
},
{
    id: 'Persona-7961',
    name: 'Persona #7961',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-5120565012498149422.jpeg'
},
{
    id: 'Persona-7960',
    name: 'Persona #7960',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-6978523316225124349.jpeg'
},
{
    id: 'Persona-7959',
    name: 'Persona #7959',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1708508308-999939157171544791.gif'
},
{
    id: 'Persona-7958',
    name: 'Persona #7958',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715438-3213247545731912742.jpeg'
},
{
    id: 'Persona-7957',
    name: 'Persona #7957',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-3752831694556275650.jpeg'
},
{
    id: 'Persona-7956',
    name: 'Persona #7956',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-843542047110724712.jpeg'
},
{
    id: 'Persona-7955',
    name: 'Persona #7955',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-4070073050664259159.jpeg'
},
{
    id: 'Persona-7954',
    name: 'Persona #7954',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-4195160562789378184.jpeg'
},
{
    id: 'Persona-7953',
    name: 'Persona #7953',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-4981707297528830895.jpeg'
},
{
    id: 'Persona-7952',
    name: 'Persona #7952',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-6805005265672371537.jpeg'
},
{
    id: 'Persona-7951',
    name: 'Persona #7951',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-8051506066306175944.jpeg'
},
{
    id: 'Persona-7950',
    name: 'Persona #7950',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-6022840513157725324.jpeg'
},
{
    id: 'Persona-7949',
    name: 'Persona #7949',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-1886106657916211840.jpeg'
},
{
    id: 'Persona-7948',
    name: 'Persona #7948',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-3003590404607041275.jpeg'
},
{
    id: 'Persona-7947',
    name: 'Persona #7947',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-2012908098688212376.jpeg'
},
{
    id: 'Persona-7946',
    name: 'Persona #7946',
    image: 'https://resources.xter.io/funfog_upload/612262dd16bd/nft/1711715437-3873074243291856963.jpeg'
}       
            
                                     ];
        actualNftData = personaNFTs.find(nft => nft.id.toString() === itemId);  
                              }
                            
      // Create NFT data with collection-specific information
      const nftData = {
        id: itemId,
        name: actualNftData ? actualNftData.name : `#${itemId}`,
        description: collection.id === "pudgypenguins" 
          ? "Pudgy Penguins is a collection of 8,888 NFT's, accelerating Web3 innovation through IP utilization and community empowerment. Embodying love, empathy, & compassion, the Pudgy Penguins are a beacon of good vibes & positivity for everyone. Each holder receives exclusive access to experiences, events, IP licensing opportunities and more. Let's break through the boundaries of Web3 together."
          : collection.id === "azuki"
          ? "Take the red bean to join the garden. View the collection at azuki.com/gallery.\n\nAzuki starts with a collection of 10,000 avatars that give you membership access to The Garden: a corner of the internet where artists, builders, and web3 enthusiasts meet to create a decentralized future. Azuki holders receive access to exclusive drops, experiences, and more. Visit azuki.com for more details.\n\nWe rise together. We build together. We grow together."
          : collection.id === "lilpudgys"
          ? "LilPudgys is a collection of 10,000 NFT's, accelerating Web3 innovation through IP utilization and community empowerment. Embodying love, empathy, & compassion, the LilPudgys are a beacon of good vibes & positivity for everyone. Each holder receives exclusive access to experiences, events, IP licensing opportunities and more. Let's break through the boundaries of Web3 together."
          : `A unique ${collection.name} NFT living on the Ethereum blockchain.`,
        image: actualNftData ? actualNftData.image : `https://picsum.photos/800/800?random=${collectionId}-${itemId}`,
        owner: {
          address: ownerAddresses?.[collectionId]?.[itemId] || 
                  `0x${Math.random().toString(16).slice(2, 10)}...${Math.random().toString(16).slice(2, 6)}`,
          avatar: `https://picsum.photos/32/32?random=${collectionId}-${itemId}-owner`
        },
        creator: {
          name: collection.name,
          avatar: collection.image,
          verified: true
        },
        collection: {
          name: collection.name,
          image: collection.image,
          verified: true
        },
        price: getPriceForId(itemId),
        lastSale: `${(Math.random() * 5 + collection.floorPrice - 1).toFixed(2)}`,
        attributes: collection.id === "azuki" ? [
          { trait_type: "Type", value: "Human", rarity: "97%" },
          { trait_type: "Hair", value: "Blue Bang", rarity: "3%" },
          { trait_type: "Clothing", value: "Kimono", rarity: "8%" },
          { trait_type: "Eyes", value: "Ruby", rarity: "2%" },
          { trait_type: "Mouth", value: "Smirk", rarity: "15%" },
          { trait_type: "Offhand", value: "Katana", rarity: "5%" },
          { trait_type: "Background", value: "Off White C", rarity: "12%" }
        ] : [
          { trait_type: "Background", value: "Blue", rarity: "12%" },
          { trait_type: "Type", value: "Golden", rarity: "8%" },
          { trait_type: "Eyes", value: "Sleepy", rarity: "15%" },
          { trait_type: "Expression", value: "Happy", rarity: "10%" },
          { trait_type: "Outfit", value: "Casual", rarity: "5%" }
        ],
        history: [
          { 
            event: "Listed", 
            price: `${(Math.random() * 5 + collection.floorPrice).toFixed(2)}`, 
            from: "0x1234", 
            to: null, 
            date: new Date().toISOString().split('T')[0]
          },
          { 
            event: "Sale", 
            price: `${(Math.random() * 5 + collection.floorPrice - 1).toFixed(2)}`, 
            from: "0x5678", 
            to: "0x1234", 
            date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
        ]
      };

      // Format NFT name based on collection
      if (nftData) {
        const collection = trendingCollections.find(c => c.id === collectionId);
        if (collectionId === 'pudgypenguins' && !nftData.name?.startsWith('Pudgy Penguins')) {
          nftData.name = `Pudgy Penguins #${nftData.id}`;
        } else if (collectionId === 'azuki' && !nftData.name?.startsWith('Azuki')) {
          nftData.name = `Azuki #${nftData.id}`;
        } else if (collectionId === 'lilpudgys' && !nftData.name?.startsWith('Lil Pudgy')) {
          nftData.name = `Lil Pudgy #${nftData.id}`;
        } else if (collectionId === 'beanz' && !nftData.name?.startsWith('BEANZ')) {
          nftData.name = `BEANZ #${nftData.id}`;
        } else if (collectionId === 'doodles' && !nftData.name?.startsWith('Doodle')) {
          nftData.name = `Doodle #${nftData.id}`;
        }
        nftData.collection = collection || { name: collectionId };

        // Set offers - either specific or random
        nftData.offers = activeOffers?.[collectionId]?.[itemId] || 
                        generateRandomOffers(collection.floorPrice, Math.floor(Math.random() * 4) + 2);
      }

      setNft(nftData);
      setLoading(false);
    };

    loadNFT();
  }, [collectionId, itemId]);

  if (loading || !nft) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0F1318 0%, #151921 100%)',
        color: '#00F5FF', // Neo-Cyberpunk cyan
      }}>
        <Typography variant="h6" sx={{ color: 'grey.500' }}>Loading...</Typography>
      </Box>
    );
  }

  const handleButtonClick = () => {
    setShowWalletMessage(true);
    // Hide message after 3 seconds
    setTimeout(() => {
      setShowWalletMessage(false);
    }, 3000);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #0F1318 0%, #151921 100%)',
      color: '#00F5FF', // Neo-Cyberpunk cyan
    }}>
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        <Grid container spacing={4}>
          {/* Left Column - Image */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                backgroundColor: 'rgba(26, 29, 36, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(0, 245, 255, 0.2)', // Neo-Cyberpunk cyan border
              }}
            >
              <Box
                component="img"
                src={nft.image}
                alt={nft.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </Paper>
            
            {/* Active Offers */}
            <Paper sx={{ mt: 3, p: 3, borderRadius: 4, bgcolor: 'rgba(26, 29, 36, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 245, 255, 0.2)' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#00F5FF' }}>
                Active Offers
              </Typography>
              <Stack spacing={2}>
                {nft.offers.map((offer, index) => (
                  <Box key={index} sx={{ p: 2, bgcolor: 'rgba(0, 245, 255, 0.1)', borderRadius: 2, border: '1px solid rgba(0, 245, 255, 0.2)' }}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Price</Typography>
                        <Typography variant="subtitle1" sx={{ color: '#00FFA3', fontWeight: 600 }}>{offer.price} ETH</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>From</Typography>
                        <Typography variant="subtitle2" sx={{ color: '#00F5FF' }}>{offer.buyer}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Expires in</Typography>
                        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>{offer.expiration}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>

          {/* Right Column - Details */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Collection Info */}
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle1" sx={{ color: 'rgba(0, 245, 255, 0.7)' }}>
                  {nft.collection.name}
                </Typography>
                {nft.collection.verified && (
                  <VerifiedIcon sx={{ color: '#00F5FF', fontSize: 16 }} />
                )}
              </Stack>

              {/* NFT Name */}
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{ 
                  color: '#00F5FF',
                  textShadow: '0 0 10px rgba(0, 245, 255, 0.5)' // Neo-Cyberpunk glow
                }}
              >
                {nft.name}
              </Typography>

              {/* Owner Info */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 3 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    src={nft.owner.avatar}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Owned by
                  </Typography>
                  <Typography variant="body2" color="primary">
                    {nft.owner.address}
                  </Typography>
                </Stack>
              </Stack>

              {/* Price and Action Buttons */}
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                bgcolor: 'rgba(26, 29, 36, 0.5)', 
                borderRadius: 2,
                border: '1px solid rgba(0, 245, 255, 0.2)', // Neo-Cyberpunk cyan border
              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 245, 255, 0.7)' }}>
                    Floor Price
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#00FFA3', fontWeight: 600 }}>
                    {getCurrentPrice()}
                  </Typography>
                </Box>
                     
                <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: '#FF2E63', // Neon pink
                      color: 'white',
                      fontSize: '18px',
                      py: 1.5,
                      borderRadius: '100px', // Pill shape
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 46, 99, 0.9)', // Slightly transparent on hover
                        boxShadow: '0 0 20px rgba(255, 46, 99, 0.5)' // Neon pink glow
                      }
                    }}
                    onClick={handleButtonClick}
                  >
                    Buy Now
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: '#FF2E63', // Neon pink border
                      color: 'white',
                      fontSize: '18px',
                      py: 1.5,
                      borderRadius: '100px', // Pill shape
                      textTransform: 'none',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: '#FF2E63', // Keep neon pink border on hover
                      }
                    }}
                    onClick={handleButtonClick}
                  >
                    Make Offer
                  </Button>
                </Stack>
                {showWalletMessage && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#FF2E63',
                      mt: 2,
                      p: 1,
                      textAlign: 'center',
                      borderRadius: '8px',
                      animation: 'fadeIn 0.3s ease-in-out',
                      fontWeight: 500,
                      '@keyframes fadeIn': {
                        from: { 
                          opacity: 0,
                          transform: 'translateY(10px)'
                        },
                        to: { 
                          opacity: 1,
                          transform: 'translateY(0)'
                        }
                      }
                    }}
                  >
                    Wallet not connected, connect wallet!
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  <IconButton 
                    sx={{ 
                      color: 'grey.500',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    <FavoriteBorderIcon />
                  </IconButton>
                  <IconButton
                    sx={{ 
                      color: 'grey.500',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    sx={{ 
                      color: 'grey.500',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Box>

              {/* Description */}
              <Accordion
                defaultExpanded
                sx={{
                  backgroundColor: 'rgba(26, 29, 36, 0.95)',
                  backdropFilter: 'blur(20px)',
                  '&:before': { display: 'none' },
                  borderRadius: '16px !important',
                  mb: 2,
                  border: '1px solid rgba(0, 245, 255, 0.2)', // Neo-Cyberpunk cyan border
                  '& .MuiAccordionSummary-root': {
                    '& .MuiTypography-root': {
                      color: '#00F5FF'
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#00F5FF'
                    }
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Description</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">
                    {nft.description}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {/* History */}
              <Accordion
                sx={{
                  backgroundColor: 'rgba(26, 29, 36, 0.95)',
                  backdropFilter: 'blur(20px)',
                  '&:before': { display: 'none' },
                  borderRadius: '16px !important',
                  border: '1px solid rgba(0, 245, 255, 0.2)', // Neo-Cyberpunk cyan border
                  '& .MuiAccordionSummary-root': {
                    '& .MuiTypography-root': {
                      color: '#00F5FF'
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#00F5FF'
                    }
                  },
                  '& .MuiTableCell-root': {
                    color: 'rgba(0, 245, 255, 0.7)',
                    borderColor: 'rgba(0, 245, 255, 0.2)'
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">History</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Event</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>From</TableCell>
                          <TableCell>To</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nft.history.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.event}</TableCell>
                            <TableCell>{item.price || '—'}</TableCell>
                            <TableCell>
                              {item.from ? `${item.from.slice(0, 6)}...` : '—'}
                            </TableCell>
                            <TableCell>
                              {item.to ? `${item.to.slice(0, 6)}...` : '—'}
                            </TableCell>
                            <TableCell>{item.date}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
}

export default ItemPage;
